import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import LandingPage from './LandingPage';
import SessionsPage from './SessionsPage';
import { SocialEmbed, EmbedStyles, preloadSocialEmbeds } from './SocialEmbeds';
import ReferralsPage from './ReferralsPage';
import { useSlotCatalog } from './useSlotCatalog';
import { findSlotArt } from './BattleShared';
import BanListPage from './BanListPage';
import CalculatorsPage from './CalculatorsPage';
import SlotBattlesPage from './SlotBattlesPage';
import BattleWidget from './BattleWidget';
import ClipEarnPage from './ClipEarnPage';
import LegalPage from './LegalPage';
import Fab from './Fab';
import LossbackPage from './LossbackPage';
import { HALLOWEEN_ON, isSpookySlot, HalloweenDecor } from './Halloween';
import { getMonthlyPrizes, monthlyPoolLabel, monthlyPoolShort } from './leaderboardPrizes';

// ============================================
// Resilient fetch: retries + timeout + supersede
// ============================================
// The backend (free Railway tier) can take several seconds to respond under
// load. A failed/slow first request must NOT make features look disabled —
// wrap critical fetches in this instead of raw fetch().
//
// - Initial attempt + up to `retries` retries with exponential backoff (1s, 2s, 4s)
// - Each attempt aborts after `timeout` ms via AbortController
// - 5xx responses are retried (server hiccup); 4xx are returned as-is (real answers)
// - Passing a `key` cancels any still-running call with the same key first, so
//   pollers never stack overlapping request chains. A cancelled call throws an
//   error with `.superseded === true` — callers must ignore those silently.
const _inflightFetches = new Map(); // key -> call record for supersede-cancellation

const fetchWithRetry = async (url, options = {}, { retries = 3, timeout = 10000, key = null } = {}) => {
  const call = { controller: null, superseded: false };
  if (key) {
    const prev = _inflightFetches.get(key);
    if (prev) {
      prev.superseded = true;
      if (prev.controller) prev.controller.abort();
    }
    _inflightFetches.set(key, call);
  }

  const bailIfSuperseded = () => {
    if (call.superseded) {
      const err = new Error('fetchWithRetry: superseded by a newer call');
      err.superseded = true;
      throw err;
    }
  };

  let lastError = null;
  try {
    for (let attempt = 0; attempt <= retries; attempt++) {
      if (attempt > 0) {
        await new Promise(res => setTimeout(res, 1000 * Math.pow(2, attempt - 1))); // 1s, 2s, 4s
      }
      bailIfSuperseded();
      call.controller = new AbortController();
      const timer = setTimeout(() => call.controller.abort(), timeout);
      try {
        const response = await fetch(url, { ...options, signal: call.controller.signal });
        clearTimeout(timer);
        if (response.status >= 500 && attempt < retries) {
          lastError = new Error(`HTTP ${response.status}`);
          continue; // server hiccup — retry
        }
        return response;
      } catch (e) {
        clearTimeout(timer);
        bailIfSuperseded();
        lastError = e; // network error or timeout abort — retry
      }
    }
    throw lastError || new Error('fetchWithRetry: all attempts failed');
  } finally {
    if (key && _inflightFetches.get(key) === call) _inflightFetches.delete(key);
  }
};

// Small pulsing placeholder block for skeleton loading states (styled by .sk-pulse).
const Skel = ({ w = '100%', h = '1rem', r = '6px', style = {} }) => (
  <div className="sk-pulse" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

// ============================================
// FIRST-VISIT WELCOME MODAL (module scope so it doesn't remount)
// Conversion tool for logged-out first-time visitors. Shown once per browser
// (localStorage 'snutzy_visited'), 2s after load, never to logged-in users.
// ============================================
const WELCOME_FEATURES = [
  ['🎯', 'Points for watching & chatting', 'Earn rewards just by being here'],
  ['🏆', 'Monthly leaderboard', `Compete for a share of the ${monthlyPoolShort()} prize pool`],
  ['🎰', 'Chat giveaways with bad luck protection', 'The more you play, the better your odds'],
  ['🛒', 'Points shop', 'Redeem points for cash, bonus buys and exclusive rewards'],
  ['👥', 'Recruit & Earn', 'Bring friends, earn cash for their milestones'],
  ['💰', '10% monthly lossback', 'Loyal guild members get losses covered'],
  ['🔮', 'Bonus hunt predictions', 'Guess the outcome and win prizes']
];

const WelcomeModal = ({ onClose, onLogin }) => (
  <div onClick={onClose} style={{position:'fixed', inset:0, zIndex:10000, background:'rgba(5,5,10,0.75)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem'}}>
    <style>{`
      @keyframes wmBorderSpin { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
      @keyframes wmFadeUp { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      @keyframes wmGlow { 0%, 100% { box-shadow: 0 0 22px rgba(251,191,36,0.35); } 50% { box-shadow: 0 0 42px rgba(251,191,36,0.6); } }
      .wm-card::-webkit-scrollbar { width: 6px; }
      .wm-card::-webkit-scrollbar-thumb { background: rgba(168,85,247,0.4); border-radius: 3px; }
    `}</style>
    {/* Animated gradient border: purple → amber → purple loop */}
    <div onClick={e => e.stopPropagation()} style={{
      padding:'2px', borderRadius:'22px', maxWidth:'540px', width:'100%',
      background:'linear-gradient(120deg, #a855f7, #fbbf24, #a855f7, #fbbf24, #a855f7)',
      backgroundSize:'300% 300%', animation:'wmBorderSpin 6s linear infinite, wmFadeUp 0.45s ease both'
    }}>
      <div className="wm-card" style={{background:'rgba(10,10,16,0.92)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', borderRadius:'20px', padding:'2rem 1.6rem', position:'relative', maxHeight:'88vh', overflowY:'auto'}}>
        <button onClick={onClose} aria-label="Close" style={{position:'absolute', top:'0.9rem', right:'0.9rem', width:'32px', height:'32px', borderRadius:'50%', border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.05)', color:'#a1a1aa', fontSize:'0.95rem', cursor:'pointer', lineHeight:1}}>✕</button>

        <div style={{textAlign:'center', marginBottom:'1.4rem'}}>
          <img src="https://i.postimg.cc/J0xRJ1dH/Photos-MO1q5GQBt-Q.png" alt="SNUTZY" style={{width:'72px', height:'72px', borderRadius:'16px', marginBottom:'0.9rem', boxShadow:'0 0 30px rgba(168,85,247,0.45)'}} />
          <div style={{fontSize:'1.6rem', fontWeight:900, color:'#fff', letterSpacing:'0.5px'}}>
            Join Guild <span style={{background:'linear-gradient(135deg, #a855f7, #fbbf24)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent'}}>SNUTZY</span>
          </div>
          <div style={{fontSize:'0.88rem', color:'#a1a1aa', marginTop:'0.4rem', lineHeight:1.5}}>
            Login with Twitch and connect your Shuffle account to unlock everything
          </div>
        </div>

        <div style={{display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1.5rem'}}>
          {WELCOME_FEATURES.map(([icon, title, desc]) => (
            <div key={title} style={{display:'flex', alignItems:'center', gap:'0.7rem', padding:'0.5rem 0.75rem', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px'}}>
              <span style={{fontSize:'1.15rem', flexShrink:0}}>{icon}</span>
              <div style={{minWidth:0}}>
                <div style={{fontSize:'0.82rem', fontWeight:700, color:'#e2e8f0'}}>{title}</div>
                <div style={{fontSize:'0.72rem', color:'#8b8b96'}}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{display:'flex', flexDirection:'column', gap:'0.6rem'}}>
          <button onClick={onLogin} style={{padding:'0.95rem', borderRadius:'12px', border:'none', cursor:'pointer', fontSize:'1rem', fontWeight:900, color:'#1a1a2e', background:'linear-gradient(135deg, #fbbf24, #f59e0b)', animation:'wmGlow 2.4s ease-in-out infinite'}}>
            Login with Twitch
          </button>
          <button onClick={onClose} style={{padding:'0.7rem', borderRadius:'12px', border:'1px solid rgba(255,255,255,0.15)', background:'transparent', color:'#a1a1aa', fontSize:'0.85rem', fontWeight:600, cursor:'pointer'}}>
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ============================================
// NEWCOMER CHECKLIST (definitions + admin review component)
// ============================================
// verifiedKey maps to the user_checklist column; kind decides the row's action UI.
const CHECKLIST_STEP_DEFS = [
  { key: 'shuffle', verifiedKey: 'shuffle_verified', icon: '🎰', title: 'Connect Shuffle under guild SNUTZY', desc: 'Link your Shuffle username — it must be registered under guild SNUTZY.', kind: 'shuffle' },
  { key: 'discord', verifiedKey: 'discord_verified', icon: '💬', title: 'Connect Discord & join the server', desc: 'Link Discord and join our server.', kind: 'discord', link: 'https://discord.gg/snutzy', linkLabel: 'Join server' },
  { key: 'twitter_snutztv', verifiedKey: 'twitter_snutztv_verified', icon: '🐦', title: 'Follow @snutztv on X', desc: 'Follow, then enter your X username to confirm.', kind: 'manual', link: 'https://x.com/snutztv', linkLabel: 'Follow', placeholder: 'Your X username' },
  { key: 'twitter_snutzygg', verifiedKey: 'twitter_snutzygg_verified', icon: '🐦', title: 'Follow @Snutzygg on X', desc: 'Follow, then enter your X username to confirm.', kind: 'manual', link: 'https://x.com/Snutzygg', linkLabel: 'Follow', placeholder: 'Your X username' },
  { key: 'twitch_follow', verifiedKey: 'twitch_follow_verified', icon: '📺', title: 'Follow snutzy on Twitch', desc: 'Follow the channel, then hit verify.', kind: 'twitch', link: 'https://www.twitch.tv/snutzy', linkLabel: 'Follow' },
  { key: 'instagram', verifiedKey: 'instagram_verified', icon: '📸', title: 'Follow @snutztv on Instagram', desc: 'Follow, then enter your Instagram username to confirm.', kind: 'manual', link: 'https://www.instagram.com/snutztv/', linkLabel: 'Follow', placeholder: 'Your Instagram username' },
  { key: 'youtube', verifiedKey: 'youtube_verified', icon: '▶️', title: 'Subscribe to @snutztv on YouTube', desc: 'Subscribe, then enter your channel name to confirm.', kind: 'manual', link: 'https://www.youtube.com/@snutztv', linkLabel: 'Subscribe', placeholder: 'Your YouTube name' }
];

const CHECKLIST_ADMIN_STEP_LABELS = {
  twitter_snutztv: '🐦 X @snutztv',
  twitter_snutzygg: '🐦 X @Snutzygg',
  twitch_follow: '📺 Twitch follow',
  instagram: '📸 Instagram',
  youtube: '▶️ YouTube'
};

// Quick link so admins can eyeball the submitted account on the platform
const checklistAdminProfileUrl = (step, username) => {
  if (!username) return null;
  if (step === 'twitter_snutztv' || step === 'twitter_snutzygg') return `https://x.com/${username}`;
  if (step === 'instagram') return `https://www.instagram.com/${username}/`;
  if (step === 'youtube') return `https://www.youtube.com/results?search_query=${encodeURIComponent(username)}`;
  if (step === 'twitch_follow') return `https://www.twitch.tv/${username}`;
  return null;
};

// Admin review queue for manual checklist verifications
const AdminChecklist = ({ apiUrl, getToken, onChanged }) => {
  const [pending, setPending] = React.useState(null);
  const [filter, setFilter] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/admin/checklist/pending${filter ? `?step=${encodeURIComponent(filter)}` : ''}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (r.ok) setPending((await r.json()).pending || []);
    } catch (e) { /* refresh button retries */ }
  }, [apiUrl, getToken, filter]);

  React.useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/checklist/${id}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        alert(data.error || 'Action failed');
      }
    } catch (e) { alert('Network error'); }
    setBusy(false);
    load();
    if (onChanged) onChanged();
  };

  const bulkApprove = async () => {
    const ids = (pending || []).map(p => p.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Approve all ${ids.length} shown verification${ids.length === 1 ? '' : 's'}?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/checklist/bulk-approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) alert(data.error || 'Bulk approve failed');
    } catch (e) { alert('Network error'); }
    setBusy(false);
    load();
    if (onChanged) onChanged();
  };

  const filterBtn = (value, label) => (
    <button key={value} onClick={() => setFilter(value)} style={{
      padding:'0.4rem 0.85rem', borderRadius:'8px', fontSize:'0.75rem', fontWeight:700, cursor:'pointer',
      background: filter === value ? 'rgba(168,85,247,0.25)' : 'rgba(255,255,255,0.04)',
      color: filter === value ? '#c084fc' : '#a1a1aa',
      border: filter === value ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.08)'
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{display:'flex', gap:'0.4rem', flexWrap:'wrap', marginBottom:'1rem', alignItems:'center'}}>
        {filterBtn('', 'All')}
        {Object.entries(CHECKLIST_ADMIN_STEP_LABELS).map(([step, label]) => filterBtn(step, label))}
        <div style={{flex:1}} />
        {(pending || []).length > 0 && (
          <button onClick={bulkApprove} disabled={busy} style={{padding:'0.4rem 1rem', borderRadius:'8px', fontSize:'0.75rem', fontWeight:800, cursor:'pointer', background:'rgba(34,197,94,0.15)', color:'#22c55e', border:'1px solid rgba(34,197,94,0.4)'}}>
            ✓ Approve all shown ({(pending || []).length})
          </button>
        )}
      </div>

      {pending === null ? (
        <div style={{display:'flex', flexDirection:'column', gap:'0.5rem'}}>
          {[0, 1, 2].map(i => <Skel key={i} h="56px" r="10px" />)}
        </div>
      ) : pending.length === 0 ? (
        <div style={{textAlign:'center', padding:'2.5rem 1rem', color:'#71717a'}}>
          <div style={{fontSize:'2rem', marginBottom:'0.5rem', opacity:0.5}}>✅</div>
          No pending checklist verifications
        </div>
      ) : (
        <div style={{display:'flex', flexDirection:'column', gap:'0.5rem'}}>
          {pending.map(p => {
            const profileUrl = checklistAdminProfileUrl(p.step, p.submitted_username);
            return (
              <div key={p.id} style={{display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.7rem 0.9rem', background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', flexWrap:'wrap'}}>
                {p.profile_image_url ? (
                  <img src={p.profile_image_url} alt={p.display_name || p.username} style={{width:'34px', height:'34px', borderRadius:'50%', flexShrink:0}} />
                ) : (
                  <div style={{width:'34px', height:'34px', borderRadius:'50%', background:'rgba(168,85,247,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', fontWeight:800, color:'#c084fc', flexShrink:0}}>
                    {(p.display_name || p.username || '?').substring(0, 2).toUpperCase()}
                  </div>
                )}
                <div style={{minWidth:'140px'}}>
                  <div style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0'}}>{p.display_name || p.username}</div>
                  <div style={{fontSize:'0.7rem', color:'#71717a'}}>@{p.username}{p.shuffle_username ? ` · 🎰 ${p.shuffle_username}` : ''}</div>
                </div>
                <span style={{padding:'0.2rem 0.6rem', borderRadius:'6px', fontSize:'0.7rem', fontWeight:700, background:'rgba(168,85,247,0.12)', color:'#c084fc', border:'1px solid rgba(168,85,247,0.25)'}}>
                  {CHECKLIST_ADMIN_STEP_LABELS[p.step] || p.step}
                </span>
                <div style={{flex:1, minWidth:'120px', fontSize:'0.8rem', color:'#fbbf24', fontWeight:600}}>
                  {profileUrl ? (
                    <a href={profileUrl} target="_blank" rel="noopener noreferrer" style={{color:'#fbbf24', textDecoration:'underline'}}>
                      {p.submitted_username}
                    </a>
                  ) : (p.submitted_username || '—')}
                </div>
                <div style={{fontSize:'0.68rem', color:'#71717a'}}>{new Date(p.created_at).toLocaleDateString()}</div>
                <div style={{display:'flex', gap:'0.4rem'}}>
                  <button onClick={() => act(p.id, 'approve')} disabled={busy} style={{padding:'0.4rem 0.9rem', borderRadius:'8px', fontSize:'0.75rem', fontWeight:800, cursor:'pointer', background:'rgba(34,197,94,0.15)', color:'#22c55e', border:'1px solid rgba(34,197,94,0.4)'}}>✓ Approve</button>
                  <button onClick={() => act(p.id, 'deny')} disabled={busy} style={{padding:'0.4rem 0.9rem', borderRadius:'8px', fontSize:'0.75rem', fontWeight:800, cursor:'pointer', background:'rgba(239,68,68,0.12)', color:'#f87171', border:'1px solid rgba(239,68,68,0.35)'}}>✕ Deny</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================
// Chat Giveaway helper components (module scope so they don't remount)
// ============================================

// Kick setup panel for giveaway admins: connection status lights, chatroom id
// (with auto-detect), and the broadcaster connect link. Collapsed by default.
const KickSetupPanel = ({ apiUrl, getToken, onConnectBroadcaster }) => {
  const [open, setOpen] = React.useState(false);
  const [st, setSt] = React.useState(null);
  const [roomInput, setRoomInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const hdrs = React.useCallback(() => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` }), [getToken]);

  const load = React.useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/admin/kick/status`, { headers: hdrs() });
      if (r.ok) { const d = await r.json(); setSt(d); setRoomInput(d.chatroomId || ''); }
    } catch (e) { /* ignore */ }
  }, [apiUrl, hdrs]);
  React.useEffect(() => { if (open) load(); }, [open, load]);

  const saveRoom = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${apiUrl}/api/admin/kick/chatroom`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ chatroomId: roomInput.trim() }) });
      const d = await r.json().catch(() => ({}));
      setMsg(r.ok ? { ok: true, text: '✅ Saved — the listener connects within a few seconds' } : { ok: false, text: d.error || 'Failed to save' });
      load();
    } catch (e) { setMsg({ ok: false, text: 'Failed to save' }); }
    setBusy(false);
  };
  const detect = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${apiUrl}/api/admin/kick/detect`, { method: 'POST', headers: hdrs() });
      const d = await r.json().catch(() => ({}));
      setMsg(r.ok ? { ok: true, text: `✅ Found it — chatroom ${d.chatroomId} (${d.slug})` } : { ok: false, text: d.error || 'Auto-detect failed' });
      load();
    } catch (e) { setMsg({ ok: false, text: 'Auto-detect failed' }); }
    setBusy(false);
  };

  const chip = (ok, labelOk, labelBad) => (
    <span style={{ fontSize: '0.66rem', fontWeight: 800, padding: '0.2rem 0.55rem', borderRadius: '999px', background: ok ? 'rgba(83,252,24,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${ok ? 'rgba(83,252,24,0.4)' : 'rgba(239,68,68,0.35)'}`, color: ok ? '#53fc18' : '#fca5a5' }}>
      {ok ? `✓ ${labelOk}` : `✗ ${labelBad}`}
    </span>
  );

  // Stays invisible on stream: just a tiny neutral "⚙️" text button until opened.
  if (!open) {
    return (
      <div style={{ textAlign: 'right', marginBottom: '0.4rem' }}>
        <button onClick={() => setOpen(true)} title="Admin setup (Kick)" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3f3f46', fontSize: '0.68rem', fontWeight: 700, fontFamily: 'Outfit, sans-serif', padding: '0.15rem 0.3rem' }}>
          ⚙️ setup
        </button>
      </div>
    );
  }
  return (
    <div style={{ background: 'rgba(14,14,18,0.7)', border: '1px solid rgba(83,252,24,0.2)', borderRadius: '14px', padding: '0.8rem 1.1rem', marginBottom: '1rem' }}>
      <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: 0, fontFamily: 'Outfit, sans-serif' }}>
        <span style={{ fontWeight: 900, fontSize: '0.8rem', color: '#53fc18', letterSpacing: '1px' }}>🟢 KICK SETUP</span>
        <span style={{ fontSize: '0.68rem', color: '#71717a' }}>giveaways on Kick chat</span>
        <span style={{ marginLeft: 'auto', color: '#71717a', fontSize: '0.8rem' }}>▴</span>
      </button>
      {open && (
        <div style={{ marginTop: '0.8rem' }}>
          {!st ? <span style={{ fontSize: '0.75rem', color: '#71717a' }}>Loading…</span> : (
            <>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
                {chip(st.oauthConfigured, 'Kick app configured', 'Kick app env vars missing')}
                {chip(st.broadcasterLinked, `Broadcaster: ${st.broadcasterUsername || 'connected'}`, 'Broadcaster not connected')}
                {chip(!!st.chatroomId, `Chatroom ${st.chatroomId}`, 'Chatroom id not set')}
                {chip(st.listenerConnected, 'Chat listener live', 'Listener offline')}
              </div>
              {!st.broadcasterLinked && (
                <button onClick={onConnectBroadcaster} style={{ marginBottom: '0.6rem', padding: '0.45rem 0.9rem', borderRadius: '8px', border: '1px solid rgba(83,252,24,0.4)', background: 'rgba(83,252,24,0.1)', color: '#53fc18', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>
                  🔗 Connect Snutzy's Kick account (announcements + auto-detect)
                </button>
              )}
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={roomInput} onChange={e => setRoomInput(e.target.value)} placeholder="Chatroom id (for reading entries)" inputMode="numeric" style={{ flex: 1, minWidth: '190px', padding: '0.45rem 0.7rem', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.8rem', fontFamily: 'Outfit, sans-serif' }} />
                <button onClick={saveRoom} disabled={busy} style={{ padding: '0.45rem 0.9rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: '#d4d4d8', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>Save</button>
                <button onClick={detect} disabled={busy || !st.broadcasterLinked} title={st.broadcasterLinked ? 'Find the chatroom id automatically' : 'Connect the broadcaster first'} style={{ padding: '0.45rem 0.9rem', borderRadius: '8px', border: '1px solid rgba(83,252,24,0.4)', background: 'rgba(83,252,24,0.1)', color: '#53fc18', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: st.broadcasterLinked ? 1 : 0.5 }}>
                  {busy ? '…' : '🔍 Auto-detect'}
                </button>
              </div>
              {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: msg.ok ? '#53fc18' : '#fca5a5' }}>{msg.text}</div>}
              <div style={{ marginTop: '0.5rem', fontSize: '0.64rem', color: '#71717a', lineHeight: 1.5 }}>
                The chatroom id is only for <strong>reading</strong> entries from Kick chat (anonymous listener). Announcements <strong>write</strong> via Snutzy's connected Kick account, no id needed. Follow age isn't available for Kick winners (Kick's API doesn't expose it) — chat history and Shuffle boosts work.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Form to create a giveaway. Controlled locally, hands final values to onSubmit.
const CgCreateForm = ({ defaults, onSubmit }) => {
  const [vals, setVals] = React.useState(defaults);
  const update = (k, v) => setVals(prev => ({ ...prev, [k]: v }));
  const submit = () => {
    const kw = vals.keyword.trim();
    if (!kw) return alert('Keyword is required');
    if (/\s/.test(kw)) return alert('Keyword cannot contain spaces');
    onSubmit({ ...vals, keyword: kw });
  };
  return (
    <div style={{display:'flex', flexDirection:'column', gap:'0.9rem'}}>
      <label style={{display:'flex', flexDirection:'column', gap:'0.3rem'}}>
        <span style={{fontSize:'0.7rem', color:'#a1a1aa', fontWeight:700, textTransform:'uppercase', letterSpacing:'1px'}}>Keyword *</span>
        <input type="text" placeholder="shuffle" value={vals.keyword} onChange={e => update('keyword', e.target.value)} maxLength={60} style={{padding:'0.6rem 0.8rem', background:'rgba(0,0,0,0.5)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'6px', color:'#fff', fontSize:'0.9rem', outline:'none', fontFamily:'monospace'}} />
        <span style={{fontSize:'0.68rem', color:'#71717a'}}>Viewers type this one word in chat to enter. Must be a single word, no spaces.</span>
      </label>
      {/* Which chat(s) can enter — Twitch, Kick, or both (multi-streaming) */}
      <div style={{display:'flex', flexDirection:'column', gap:'0.3rem'}}>
        <span style={{fontSize:'0.7rem', color:'#a1a1aa', fontWeight:700, textTransform:'uppercase', letterSpacing:'1px'}}>Entries from</span>
        <div style={{display:'flex', gap:'0.4rem'}}>
          {[['twitch', '📺 Twitch only'], ['kick', '🟢 Kick only'], ['both', '🎭 Both chats']].map(([key, lbl]) => (
            <button key={key} onClick={() => update('platforms', key)} style={{
              flex:1, padding:'0.5rem 0.4rem', borderRadius:'8px', cursor:'pointer', fontWeight:800, fontSize:'0.76rem', fontFamily:'Outfit, sans-serif',
              background: (vals.platforms || 'twitch') === key ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${(vals.platforms || 'twitch') === key ? '#a855f7' : 'rgba(255,255,255,0.12)'}`,
              color: (vals.platforms || 'twitch') === key ? '#fff' : '#a1a1aa'
            }}>{lbl}</button>
          ))}
        </div>
        {(vals.platforms === 'kick' || vals.platforms === 'both') && (
          <span style={{fontSize:'0.66rem', color:'#71717a'}}>
            Kick chatters get Shuffle boosts only if they linked Kick on the site. First time? Check ⚙️ setup above.
          </span>
        )}
      </div>
      <div style={{display:'flex', flexDirection:'column', gap:'0.5rem', padding:'0.5rem 0'}}>
        <label style={{display:'flex', alignItems:'center', gap:'0.45rem', cursor:'pointer', fontSize:'0.82rem', color:'#e2e8f0'}}>
          <input type="checkbox" checked={vals.weightedShuffle} onChange={e => update('weightedShuffle', e.target.checked)} style={{width:'16px', height:'16px', cursor:'pointer'}} />
          <span>🎰 Affiliates under guild SNUTZY get <strong style={{color:'#fbbf24'}}>25% more chance</strong></span>
        </label>
        <label style={{display:'flex', alignItems:'center', gap:'0.45rem', cursor:'pointer', fontSize:'0.82rem', color:'#e2e8f0'}}>
          <input type="checkbox" checked={vals.vipBonusEnabled} onChange={e => update('vipBonusEnabled', e.target.checked)} style={{width:'16px', height:'16px', cursor:'pointer'}} />
          <span>👑 VIPs ($50k+ weighted wagered) get an additional <strong style={{color:'#fbbf24'}}>25% more chance</strong> on top</span>
        </label>
        <label style={{display:'flex', alignItems:'center', gap:'0.45rem', cursor:'pointer', fontSize:'0.82rem', color:'#e2e8f0'}}>
          <input type="checkbox" checked={vals.badluckEnabled !== false} onChange={e => update('badluckEnabled', e.target.checked)} style={{width:'16px', height:'16px', cursor:'pointer'}} />
          <span>🍀 Bad luck protection <span style={{color:'#71717a', fontSize:'0.72rem'}}>(off = everyone draws at 1x, streaks untouched)</span></span>
        </label>
        <label style={{display:'flex', alignItems:'center', gap:'0.45rem', cursor:'pointer', fontSize:'0.82rem', color:'#e2e8f0'}}>
          <input type="checkbox" checked={vals.excludePreviousWinners} onChange={e => update('excludePreviousWinners', e.target.checked)} style={{width:'16px', height:'16px', cursor:'pointer'}} />
          <span>🚫 No repeat winners this giveaway</span>
        </label>
        <label style={{display:'flex', alignItems:'center', gap:'0.45rem', cursor:'pointer', fontSize:'0.82rem', color:'#e2e8f0'}}>
          <input type="checkbox" checked={vals.announceInChat} onChange={e => update('announceInChat', e.target.checked)} style={{width:'16px', height:'16px', cursor:'pointer'}} />
          <span>📢 Announce winners in Twitch chat</span>
        </label>
      </div>
      <button onClick={submit} style={{padding:'0.75rem 1.5rem', background:'linear-gradient(135deg, #a855f7, #ec4899)', border:'none', borderRadius:'10px', color:'#fff', fontSize:'0.95rem', fontWeight:800, cursor:'pointer', boxShadow:'0 0 25px rgba(168,85,247,0.3)'}}>🎉 Create & Open Giveaway</button>
    </div>
  );
};

// Slot-reel that cycles entrant avatars/names vertically, decelerating to land on the winner.
// Single-animation design: the reel sits at translateY(0) until `settledWinner` arrives.
// Once it does, we swap the item at LANDING_INDEX for the winner and kick off ONE smooth
// 4-second ease-out scroll from 0 to the winner's center position. No phase transitions,
// no snap — the winner rolls into frame and the reel decelerates to rest on their row.
const CgSlotReel = ({ cgData, settledWinner }) => {
  const ITEM_HEIGHT = 56;
  const STRIP_LENGTH = 80;
  const LANDING_INDEX = 50; // where the winner will be placed
  const VIEWPORT_CENTER_ROW = 2; // 0-indexed: 5 visible rows, index 2 is center

  // Build the stable strip exactly once on mount. Real entrants first; pad with fillers only
  // if there aren't enough for visual variety.
  const [strip] = React.useState(() => {
    const FILLERS = ['Viewer42', 'LuckyChatter', 'SlotFan99', 'BigSpinner', 'ShuffleKing', 'ChatRaider', 'HypeMaster', 'WagerQueen', 'BonusBandit', 'JackpotJane', 'BetBuddy', 'RollMaster', 'TiltLord', 'RainChaser', 'PitBoss'];
    const MIN_POOL = 15;
    const entries = cgData?.entries || [];
    const pool = entries.map(e => ({ id: 'e' + e.id, name: e.twitch_display_name, avatar: e.twitch_avatar }));
    if (pool.length < MIN_POOL) {
      const needed = MIN_POOL - pool.length;
      const shuffled = [...FILLERS].sort(() => Math.random() - 0.5).slice(0, needed);
      shuffled.forEach((n, i) => pool.push({ id: 'f' + i, name: n, avatar: null }));
    }
    if (pool.length === 0) pool.push({ id: 'empty', name: '—', avatar: null });
    const items = [];
    for (let i = 0; i < STRIP_LENGTH; i++) {
      items.push(pool[Math.floor(Math.random() * pool.length)]);
    }
    return items;
  });

  // Once winner is known, swap the item at LANDING_INDEX for the winner record.
  const displayItems = React.useMemo(() => {
    if (!settledWinner) return strip;
    return strip.map((it, i) =>
      i === LANDING_INDEX
        ? { id: 'winner', name: settledWinner.twitch_display_name, avatar: settledWinner.twitch_avatar, isWinner: true }
        : it
    );
  }, [settledWinner, strip]);

  // Animation phase: 'idle' while waiting for winner data, 'animating' once the spin starts.
  // Double rAF ensures the first paint commits translateY(0) before we flip to the landing
  // offset — without that, the CSS transition has no "from" state and won't animate.
  const [phase, setPhase] = React.useState('idle');
  React.useEffect(() => {
    if (!settledWinner || phase !== 'idle') return;
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => setPhase('animating'));
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id1);
  }, [settledWinner, phase]);

  const landingOffset = -(LANDING_INDEX * ITEM_HEIGHT) + (VIEWPORT_CENTER_ROW * ITEM_HEIGHT);
  const targetOffset = phase === 'animating' ? landingOffset : 0;

  return (
    <div style={{position:'relative', width:'320px', height:`${ITEM_HEIGHT * 5}px`, overflow:'hidden', borderRadius:'12px', background:'rgba(0,0,0,0.5)', border:'1px solid rgba(168,85,247,0.3)'}}>
      {/* Center highlight row */}
      <div style={{position:'absolute', left:0, right:0, top:`${VIEWPORT_CENTER_ROW * ITEM_HEIGHT}px`, height:`${ITEM_HEIGHT}px`, background:'rgba(168,85,247,0.12)', borderTop:'2px solid #c084fc', borderBottom:'2px solid #c084fc', pointerEvents:'none', zIndex:2}} />
      {/* Fade gradients top/bottom */}
      <div style={{position:'absolute', top:0, left:0, right:0, height:`${ITEM_HEIGHT * 1.5}px`, background:'linear-gradient(to bottom, rgba(14,14,18,0.95), transparent)', pointerEvents:'none', zIndex:1}} />
      <div style={{position:'absolute', bottom:0, left:0, right:0, height:`${ITEM_HEIGHT * 1.5}px`, background:'linear-gradient(to top, rgba(14,14,18,0.95), transparent)', pointerEvents:'none', zIndex:1}} />
      <div style={{
        transform:`translateY(${targetOffset}px)`,
        // Pure ease-out (no initial acceleration): fast at the start of the scroll, decelerating
        // into a smooth landing. 4s matches the audio tick-and-thunk scheduling.
        transition: phase === 'animating' ? 'transform 4s cubic-bezier(0.15, 0.65, 0.2, 1)' : 'none',
        willChange: 'transform'
      }}>
        {displayItems.map((item, i) => (
          <div key={i} style={{height:`${ITEM_HEIGHT}px`, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.6rem', padding:'0 1rem', color: item.isWinner ? '#fbbf24' : '#e2e8f0', fontSize:'1.05rem', fontWeight: item.isWinner ? 900 : 600, textShadow: item.isWinner ? '0 0 20px rgba(251,191,36,0.6)' : 'none'}}>
            {item.avatar
              ? <img src={item.avatar} alt="" style={{width:'36px', height:'36px', borderRadius:'50%', border: item.isWinner ? '2px solid #fbbf24' : '2px solid rgba(255,255,255,0.1)', flexShrink:0}} />
              : <div style={{width:'36px', height:'36px', borderRadius:'50%', background:'rgba(168,85,247,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0}}>👤</div>}
            <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Elapsed time counter — ticks every second, counts up from `since` (ISO string or timestamp).
// Used on the winner modal to show how long since they were drawn.
// ============ POINTS SHOP ICON ============
// Renders the right SVG icon for a shop item based on its icon_key. Falls back to the
// item.image_url banner when icon_key is 'custom' or unknown — preserves existing items
// untouched while letting admins switch to clean preset icons via the dropdown.
//
// Icons are inline SVG so they scale crisply at any card size and inherit the dark/purple
// brand palette automatically. Each one is a self-contained fragment that gets centered
// in the existing 160px card image slot.
const SHOP_ICON_OPTIONS = [
  { key: 'custom',         label: 'Custom image (use Image URL below)' },
  { key: 'shuffle_token',  label: 'Raw Balance Tip (Shuffle tokens)' },
  { key: 'gift_card',      label: 'Gift Card (wrapped card)' },
  { key: 'bonus_hunt',     label: 'Bonus Hunt Equity (stacked reels)' },
  { key: 'bonus_buy',      label: 'Slot Bonus Buy (BUY BONUS button)' },
  { key: 'discord_role',   label: 'Discord Role (Discord mark + crown)' },
];

const ShopIcon = ({ iconKey, fallbackImageUrl, alt = '' }) => {
  if (!iconKey || iconKey === 'custom') {
    if (!fallbackImageUrl) return null;
    return <img src={fallbackImageUrl} alt={alt} />;
  }
  // Each icon is a 280×180 SVG that fills the card image slot. Same composition as the
  // mockups previewed during design — viewBox kept at 280×180 so all four feel like a set.
  const common = { viewBox: '0 0 280 180', xmlns: 'http://www.w3.org/2000/svg', style: { width: '100%', height: '100%', display: 'block' } };
  const SHUFFLE_LOGO_URL = '/shuffle-tile.png'; // self-hosted in public/ — the old Flickr host deleted the image (410)
  if (iconKey === 'shuffle_token') {
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="si-bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1a1426"/><stop offset="100%" stopColor="#0e0e12"/></linearGradient>
          <linearGradient id="si-edge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#5b21b6"/><stop offset="100%" stopColor="#3b0764"/></linearGradient>
          <radialGradient id="si-shine" cx="0.3" cy="0.3" r="0.6"><stop offset="0%" stopColor="#fff" stopOpacity="0.3"/><stop offset="60%" stopColor="#fff" stopOpacity="0.04"/><stop offset="100%" stopColor="#fff" stopOpacity="0"/></radialGradient>
          <clipPath id="si-tok-big"><circle cx="0" cy="0" r="43"/></clipPath>
          <clipPath id="si-tok-med"><circle cx="0" cy="0" r="34"/></clipPath>
          <clipPath id="si-tok-sm"><circle cx="0" cy="0" r="32"/></clipPath>
        </defs>
        <rect x="0" y="0" width="280" height="180" fill="url(#si-bg)"/>
        <circle cx="140" cy="90" r="100" fill="#a855f7" opacity="0.10"/>
        <circle cx="140" cy="90" r="60" fill="#ec4899" opacity="0.08"/>
        <g transform="translate(92,110)">
          <ellipse cx="3" cy="3" rx="38" ry="38" fill="#000" opacity="0.4"/>
          <circle cx="0" cy="0" r="38" fill="url(#si-edge)"/>
          <g clipPath="url(#si-tok-med)"><image href={SHUFFLE_LOGO_URL} x="-34" y="-34" width="68" height="68" preserveAspectRatio="xMidYMid slice"/></g>
          <circle cx="0" cy="0" r="34" fill="url(#si-shine)"/>
          <circle cx="0" cy="0" r="34" fill="none" stroke="#fff" strokeWidth="0.6" opacity="0.3"/>
        </g>
        <g transform="translate(192,102)">
          <ellipse cx="3" cy="3" rx="36" ry="36" fill="#000" opacity="0.4"/>
          <circle cx="0" cy="0" r="36" fill="url(#si-edge)"/>
          <g clipPath="url(#si-tok-sm)"><image href={SHUFFLE_LOGO_URL} x="-32" y="-32" width="64" height="64" preserveAspectRatio="xMidYMid slice"/></g>
          <circle cx="0" cy="0" r="32" fill="url(#si-shine)"/>
          <circle cx="0" cy="0" r="32" fill="none" stroke="#fff" strokeWidth="0.6" opacity="0.3"/>
        </g>
        <g transform="translate(140,78)">
          <ellipse cx="4" cy="4" rx="48" ry="48" fill="#000" opacity="0.5"/>
          <circle cx="0" cy="0" r="48" fill="url(#si-edge)"/>
          <g clipPath="url(#si-tok-big)"><image href={SHUFFLE_LOGO_URL} x="-43" y="-43" width="86" height="86" preserveAspectRatio="xMidYMid slice"/></g>
          <circle cx="0" cy="0" r="43" fill="url(#si-shine)"/>
          <circle cx="0" cy="0" r="43" fill="none" stroke="#fff" strokeWidth="0.8" opacity="0.4"/>
        </g>
        <circle cx="60" cy="28" r="3" fill="#fbbf24" opacity="0.9"/>
        <circle cx="222" cy="40" r="2.5" fill="#f472b6" opacity="0.9"/>
        <circle cx="212" cy="150" r="2" fill="#a855f7" opacity="0.85"/>
      </svg>
    );
  }
  if (iconKey === 'gift_card') {
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="gi-bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1a1426"/><stop offset="100%" stopColor="#0e0e12"/></linearGradient>
          <linearGradient id="gi-card" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#ec4899"/><stop offset="50%" stopColor="#a855f7"/><stop offset="100%" stopColor="#7c3aed"/></linearGradient>
        </defs>
        <rect x="0" y="0" width="280" height="180" fill="url(#gi-bg)"/>
        <circle cx="140" cy="90" r="100" fill="#ec4899" opacity="0.10"/>
        <g transform="translate(140,90) rotate(-4)">
          <rect x="-86" y="-54" width="172" height="108" rx="11" fill="url(#gi-card)" stroke="#831843" strokeWidth="0.8"/>
          <rect x="-86" y="-38" width="172" height="14" fill="#1a1426" opacity="0.7"/>
          <rect x="-72" y="-2" width="22" height="18" rx="3" fill="#fcd34d" stroke="#92400e" strokeWidth="0.6"/>
          <line x1="-68" y1="2" x2="-54" y2="2" stroke="#92400e" strokeWidth="0.5" opacity="0.6"/>
          <line x1="-68" y1="6" x2="-54" y2="6" stroke="#92400e" strokeWidth="0.5" opacity="0.6"/>
          <line x1="-68" y1="10" x2="-54" y2="10" stroke="#92400e" strokeWidth="0.5" opacity="0.6"/>
          <line x1="-61" y1="-1" x2="-61" y2="15" stroke="#92400e" strokeWidth="0.5" opacity="0.6"/>
          <text x="-40" y="10" fill="#fff" fontFamily="system-ui,sans-serif" fontSize="9" fontWeight="500" letterSpacing="2" opacity="0.95">GIFT CARD</text>
          <g fill="#fff" opacity="0.85">
            {[-66,-62,-58,-54,-44,-40,-36,-32,-22,-18,-14,-10].map((x,i) => <circle key={i} cx={x} cy="32" r="1.6"/>)}
          </g>
          <text x="6" y="36" fill="#fff" fontFamily="system-ui,sans-serif" fontSize="9" fontWeight="500" opacity="0.95">2580</text>
          <rect x="-86" y="-54" width="172" height="14" fill="#fff" opacity="0.15"/>
          <rect x="40" y="-54" width="14" height="108" fill="#fbbf24"/>
          <line x1="42" y1="-54" x2="42" y2="54" stroke="#d97706" strokeWidth="0.8" opacity="0.6"/>
          <line x1="52" y1="-54" x2="52" y2="54" stroke="#d97706" strokeWidth="0.8" opacity="0.6"/>
          <rect x="-86" y="-12" width="172" height="14" fill="#fbbf24"/>
          <line x1="-86" y1="-10" x2="86" y2="-10" stroke="#d97706" strokeWidth="0.8" opacity="0.6"/>
          <line x1="-86" y1="0" x2="86" y2="0" stroke="#d97706" strokeWidth="0.8" opacity="0.6"/>
        </g>
        <g transform="translate(184,80)">
          <ellipse cx="-13" cy="-9" rx="13" ry="9" fill="#fbbf24" stroke="#d97706" strokeWidth="0.6" transform="rotate(-25 -13 -9)"/>
          <ellipse cx="13" cy="-9" rx="13" ry="9" fill="#fbbf24" stroke="#d97706" strokeWidth="0.6" transform="rotate(25 13 -9)"/>
          <ellipse cx="-13" cy="-9" rx="6" ry="4" fill="#d97706" opacity="0.4" transform="rotate(-25 -13 -9)"/>
          <ellipse cx="13" cy="-9" rx="6" ry="4" fill="#d97706" opacity="0.4" transform="rotate(25 13 -9)"/>
          <path d="M -4 0 L -10 16 L -4 14 Z" fill="#fbbf24" stroke="#d97706" strokeWidth="0.5"/>
          <path d="M 4 0 L 10 16 L 4 14 Z" fill="#fbbf24" stroke="#d97706" strokeWidth="0.5"/>
          <rect x="-7" y="-6" width="14" height="12" rx="2" fill="#fbbf24" stroke="#d97706" strokeWidth="0.6"/>
        </g>
      </svg>
    );
  }
  if (iconKey === 'bonus_hunt') {
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="bh-bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1a1426"/><stop offset="100%" stopColor="#0e0e12"/></linearGradient>
          <linearGradient id="bh-acc" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#a855f7"/><stop offset="100%" stopColor="#ec4899"/></linearGradient>
          <linearGradient id="bh-glass" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2a1f3a"/><stop offset="100%" stopColor="#0e0e12"/></linearGradient>
          <linearGradient id="bh-glass2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#231832"/><stop offset="100%" stopColor="#100916"/></linearGradient>
        </defs>
        <rect x="0" y="0" width="280" height="180" fill="url(#bh-bg)"/>
        <circle cx="140" cy="90" r="100" fill="#a855f7" opacity="0.10"/>
        <g transform="translate(180,98) rotate(-6)">
          <rect x="-50" y="-32" width="100" height="64" rx="7" fill="url(#bh-glass2)" stroke="#7c3aed" strokeWidth="1.2"/>
          <text x="-34" y="6" textAnchor="middle" fill="#fbbf24" fontFamily="system-ui,sans-serif" fontSize="18" fontWeight="500" opacity="0.9">★</text>
          <text x="0" y="6" textAnchor="middle" fill="#fbbf24" fontFamily="system-ui,sans-serif" fontSize="18" fontWeight="500" opacity="0.9">★</text>
          <text x="34" y="6" textAnchor="middle" fill="#fbbf24" fontFamily="system-ui,sans-serif" fontSize="18" fontWeight="500" opacity="0.9">★</text>
          <rect x="-50" y="-2" width="100" height="2" fill="#fbbf24" opacity="0.5"/>
        </g>
        <g transform="translate(155,88) rotate(-2)">
          <rect x="-54" y="-34" width="108" height="68" rx="7" fill="url(#bh-glass)" stroke="#a855f7" strokeWidth="1.2"/>
          <text x="-37" y="7" textAnchor="middle" fill="#fbbf24" fontFamily="system-ui,sans-serif" fontSize="22" fontWeight="500">7</text>
          <text x="0" y="7" textAnchor="middle" fill="#fbbf24" fontFamily="system-ui,sans-serif" fontSize="22" fontWeight="500">7</text>
          <text x="37" y="7" textAnchor="middle" fill="#fbbf24" fontFamily="system-ui,sans-serif" fontSize="22" fontWeight="500">7</text>
          <rect x="-54" y="-2" width="108" height="2" fill="#fbbf24" opacity="0.6"/>
        </g>
        <g transform="translate(125,78) rotate(3)">
          <rect x="-58" y="-36" width="116" height="72" rx="8" fill="url(#bh-glass)" stroke="#ec4899" strokeWidth="1.5"/>
          <circle cx="-40" cy="0" r="13" fill="url(#bh-acc)" stroke="#831843" strokeWidth="0.8"/>
          <polygon points="-40,-9 -38,-3 -32,-3 -37,1 -35,7 -40,3 -45,7 -43,1 -48,-3 -42,-3" fill="#fff" opacity="0.95"/>
          <circle cx="0" cy="0" r="13" fill="url(#bh-acc)" stroke="#831843" strokeWidth="0.8"/>
          <polygon points="0,-9 2,-3 8,-3 3,1 5,7 0,3 -5,7 -3,1 -8,-3 -2,-3" fill="#fff" opacity="0.95"/>
          <circle cx="40" cy="0" r="13" fill="url(#bh-acc)" stroke="#831843" strokeWidth="0.8"/>
          <polygon points="40,-9 42,-3 48,-3 43,1 45,7 40,3 35,7 37,1 32,-3 38,-3" fill="#fff" opacity="0.95"/>
          <rect x="-58" y="-2" width="116" height="2" fill="#fbbf24" opacity="0.7"/>
        </g>
        <text x="40" y="56" fill="#22c55e" fontFamily="system-ui,sans-serif" fontSize="13" fontWeight="500">76x</text>
        <text x="222" y="50" fill="#fbbf24" fontFamily="system-ui,sans-serif" fontSize="14" fontWeight="500">204x</text>
        <text x="46" y="152" fill="#f472b6" fontFamily="system-ui,sans-serif" fontSize="12" fontWeight="500">42x</text>
        <text x="218" y="162" fill="#22d3ee" fontFamily="system-ui,sans-serif" fontSize="13" fontWeight="500">101x</text>
        <text x="34" y="106" fill="#a855f7" fontFamily="system-ui,sans-serif" fontSize="11" fontWeight="500" opacity="0.8">12x</text>
      </svg>
    );
  }
  if (iconKey === 'bonus_buy') {
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="bb-bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1a1426"/><stop offset="100%" stopColor="#0e0e12"/></linearGradient>
          <linearGradient id="bb-acc" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#a855f7"/><stop offset="100%" stopColor="#ec4899"/></linearGradient>
          <linearGradient id="bb-glass" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2a1f3a"/><stop offset="100%" stopColor="#0e0e12"/></linearGradient>
        </defs>
        <rect x="0" y="0" width="280" height="180" fill="url(#bb-bg)"/>
        <circle cx="140" cy="90" r="100" fill="#ec4899" opacity="0.10"/>
        <g transform="translate(140,72)">
          <rect x="-72" y="-46" width="144" height="68" rx="9" fill="url(#bb-glass)" stroke="#a855f7" strokeWidth="1.5"/>
          <text x="-46" y="-2" textAnchor="middle" fill="#a855f7" fontFamily="system-ui,sans-serif" fontSize="22" fontWeight="500" opacity="0.5">★</text>
          <text x="46" y="-2" textAnchor="middle" fill="#a855f7" fontFamily="system-ui,sans-serif" fontSize="22" fontWeight="500" opacity="0.5">★</text>
          <circle cx="0" cy="-12" r="18" fill="url(#bb-acc)" stroke="#831843" strokeWidth="1.2"/>
          <polygon points="0,-26 4,-16 14,-16 6,-10 9,0 0,-6 -9,0 -6,-10 -14,-16 -4,-16" fill="#fff" opacity="0.95"/>
          <rect x="-72" y="-14" width="144" height="2" fill="#fbbf24" opacity="0.5"/>
          <line x1="-26" y1="-46" x2="-26" y2="22" stroke="#a855f7" strokeWidth="0.6" opacity="0.4"/>
          <line x1="26" y1="-46" x2="26" y2="22" stroke="#a855f7" strokeWidth="0.6" opacity="0.4"/>
        </g>
        <g transform="translate(140,128)">
          <rect x="-78" y="-2" width="156" height="32" rx="16" fill="#5b21b6" opacity="0.6"/>
          <rect x="-78" y="-6" width="156" height="32" rx="16" fill="url(#bb-acc)" stroke="#831843" strokeWidth="1.2"/>
          <rect x="-72" y="-4" width="144" height="10" rx="5" fill="#fff" opacity="0.25"/>
          <text x="0" y="14" textAnchor="middle" fill="#fff" fontFamily="system-ui,sans-serif" fontSize="13" fontWeight="500" letterSpacing="2">BUY BONUS</text>
        </g>
        <g transform="translate(224,42)">
          <polygon points="0,0 22,0 28,8 22,16 0,16" fill="#a855f7" stroke="#7c3aed" strokeWidth="0.8"/>
          <text x="10" y="11" textAnchor="middle" fill="#fff" fontFamily="system-ui,sans-serif" fontSize="9" fontWeight="500">100x</text>
        </g>
      </svg>
    );
  }
  if (iconKey === 'discord_role') {
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="dr-bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1a1426"/><stop offset="100%" stopColor="#0e0e12"/></linearGradient>
          <linearGradient id="dr-acc" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#a855f7"/><stop offset="100%" stopColor="#ec4899"/></linearGradient>
          <linearGradient id="dr-crown" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fcd34d"/><stop offset="100%" stopColor="#d97706"/></linearGradient>
        </defs>
        <rect x="0" y="0" width="280" height="180" fill="url(#dr-bg)"/>
        <circle cx="140" cy="105" r="100" fill="#a855f7" opacity="0.10"/>
        <circle cx="140" cy="105" r="60" fill="#ec4899" opacity="0.08"/>
        {/* Discord badge — purple/pink gradient circle, centered at (140, 105) */}
        <g transform="translate(140,105)">
          <circle cx="0" cy="0" r="50" fill="url(#dr-acc)" stroke="#831843" strokeWidth="1.2"/>
          <circle cx="0" cy="0" r="45" fill="none" stroke="#fff" strokeWidth="0.6" opacity="0.25"/>
          {/* Discord controller mark — properly centered. Built from a 71x55 viewBox of the
              official mark, centered at its midpoint and scaled to fit ~50px wide inside the badge.
              The path is shifted so its bounding box origin is at (-35.5, -27.5) before scaling. */}
          <g transform="scale(0.62) translate(-35.5,-27.5)" fill="#ffffff">
            <path d="M60.1,4.9C55.5,2.8,50.6,1.2,45.5,0.3c-0.1,0-0.2,0-0.2,0.1c-0.6,1.1-1.3,2.6-1.8,3.7c-5.5-0.8-10.9-0.8-16.3,0 c-0.5-1.2-1.2-2.6-1.8-3.7c0-0.1-0.1-0.1-0.2-0.1c-5.1,0.9-10,2.5-14.6,4.6c0,0-0.1,0-0.1,0.1C1.2,18.7-1.4,32-0.1,45.2 c0,0.1,0.1,0.1,0.1,0.2c6.1,4.5,12,7.2,17.8,9c0.1,0,0.2,0,0.2-0.1c1.4-1.9,2.6-3.9,3.6-6c0.1-0.1,0-0.2-0.1-0.3 c-1.9-0.7-3.8-1.6-5.6-2.7c-0.1-0.1-0.1-0.2,0-0.3c0.4-0.3,0.8-0.6,1.1-0.9c0.1,0,0.1-0.1,0.2,0c11.8,5.4,24.6,5.4,36.3,0 c0.1,0,0.2,0,0.2,0c0.4,0.3,0.8,0.6,1.1,0.9c0.1,0.1,0.1,0.2,0,0.3c-1.8,1.1-3.6,2-5.6,2.7c-0.1,0-0.1,0.2-0.1,0.3 c1.1,2.1,2.3,4.1,3.6,6c0,0.1,0.2,0.1,0.2,0.1c5.8-1.8,11.7-4.5,17.8-9c0.1,0,0.1-0.1,0.1-0.2C73.1,30,69.5,16.7,60.2,5 C60.2,4.9,60.2,4.9,60.1,4.9z M23.7,37.2c-3.5,0-6.5-3.2-6.5-7.2s2.9-7.2,6.5-7.2c3.6,0,6.6,3.3,6.5,7.2 C30.2,34,27.3,37.2,23.7,37.2z M47.7,37.2c-3.5,0-6.5-3.2-6.5-7.2s2.9-7.2,6.5-7.2c3.6,0,6.6,3.3,6.5,7.2 C54.2,34,47.7,37.2z"/>
          </g>
        </g>
        {/* Gold crown floating above the badge — denotes "role" / status reward */}
        <g transform="translate(140,38)">
          <path d="M -22 8 L -22 -8 L -10 4 L 0 -12 L 10 4 L 22 -8 L 22 8 Z" fill="url(#dr-crown)" stroke="#92400e" strokeWidth="0.8" strokeLinejoin="round"/>
          <rect x="-22" y="6" width="44" height="6" rx="1" fill="url(#dr-crown)" stroke="#92400e" strokeWidth="0.8"/>
          <circle cx="-22" cy="-8" r="2.5" fill="#ec4899" stroke="#831843" strokeWidth="0.5"/>
          <circle cx="0" cy="-12" r="3" fill="#ec4899" stroke="#831843" strokeWidth="0.5"/>
          <circle cx="22" cy="-8" r="2.5" fill="#ec4899" stroke="#831843" strokeWidth="0.5"/>
          <line x1="-20" y1="9" x2="20" y2="9" stroke="#92400e" strokeWidth="0.5" opacity="0.6"/>
        </g>
        <circle cx="62" cy="50" r="3" fill="#fbbf24" opacity="0.9"/>
        <circle cx="220" cy="54" r="2.5" fill="#f472b6" opacity="0.9"/>
        <circle cx="58" cy="156" r="2" fill="#a855f7" opacity="0.85"/>
        <circle cx="222" cy="160" r="1.8" fill="#fbbf24" opacity="0.8"/>
      </svg>
    );
  }
  // Unknown key — fall back to image_url if available
  if (fallbackImageUrl) return <img src={fallbackImageUrl} alt={alt} />;
  return null;
};

// ============ SHOP ICON END ============

// Payout status pill for chat giveaway winners. Reads w.paid_at — non-null = paid.
// When `onToggle` is provided (admin), the pill becomes a button that flips state.
// Without onToggle (public/invalidated context), it's a passive label.
// ============ RAFFLE SLOT REEL ============
// Text-only reel (Shuffle usernames, no avatars). Same stable-strip + single-animation
// pattern as CgSlotReel — strip built once on mount, winner swapped in at LANDING_INDEX
// off-screen, single CSS transition scrolls from 0 to the landing position.
const RaffleSlotReel = ({ participants, settledWinnerUsername }) => {
  const ITEM_HEIGHT = 52;
  const STRIP_LENGTH = 80;
  const LANDING_INDEX = 50;
  const VIEWPORT_CENTER_ROW = 2;

  const [strip] = React.useState(() => {
    const pool = (participants || []).map(p => p.username);
    if (pool.length === 0) pool.push('...');
    const items = [];
    for (let i = 0; i < STRIP_LENGTH; i++) {
      items.push(pool[Math.floor(Math.random() * pool.length)]);
    }
    return items;
  });

  const displayItems = React.useMemo(() => {
    if (!settledWinnerUsername) return strip;
    return strip.map((it, i) => i === LANDING_INDEX ? settledWinnerUsername : it);
  }, [settledWinnerUsername, strip]);

  const [phase, setPhase] = React.useState('idle');
  React.useEffect(() => {
    if (!settledWinnerUsername || phase !== 'idle') return;
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => setPhase('animating'));
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id1);
  }, [settledWinnerUsername, phase]);

  const landingOffset = -(LANDING_INDEX * ITEM_HEIGHT) + (VIEWPORT_CENTER_ROW * ITEM_HEIGHT);
  const targetOffset = phase === 'animating' ? landingOffset : 0;

  return (
    <div style={{position:'relative', width:'340px', height:`${ITEM_HEIGHT * 5}px`, overflow:'hidden', borderRadius:'12px', background:'rgba(0,0,0,0.6)', border:'1px solid rgba(245,158,11,0.3)'}}>
      <div style={{position:'absolute', left:0, right:0, top:`${VIEWPORT_CENTER_ROW * ITEM_HEIGHT}px`, height:`${ITEM_HEIGHT}px`, background:'rgba(245,158,11,0.12)', borderTop:'2px solid #fbbf24', borderBottom:'2px solid #fbbf24', pointerEvents:'none', zIndex:2}} />
      <div style={{position:'absolute', top:0, left:0, right:0, height:`${ITEM_HEIGHT * 1.5}px`, background:'linear-gradient(to bottom, rgba(14,14,18,0.95), transparent)', pointerEvents:'none', zIndex:1}} />
      <div style={{position:'absolute', bottom:0, left:0, right:0, height:`${ITEM_HEIGHT * 1.5}px`, background:'linear-gradient(to top, rgba(14,14,18,0.95), transparent)', pointerEvents:'none', zIndex:1}} />
      <div style={{
        transform:`translateY(${targetOffset}px)`,
        transition: phase === 'animating' ? 'transform 4s cubic-bezier(0.15, 0.65, 0.2, 1)' : 'none',
        willChange: 'transform'
      }}>
        {displayItems.map((name, i) => {
          const isWinner = i === LANDING_INDEX && settledWinnerUsername;
          return (
            <div key={i} style={{height:`${ITEM_HEIGHT}px`, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 1rem', color: isWinner ? '#fbbf24' : '#e2e8f0', fontSize: isWinner ? '1.2rem' : '1rem', fontWeight: isWinner ? 900 : 600, textShadow: isWinner ? '0 0 20px rgba(245,158,11,0.6)' : 'none'}}>
              <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
// ============ END RAFFLE SLOT REEL ============

const CgPaidPill = ({ winner, onToggle, compact = false }) => {
  if (!winner || winner.invalidated_at) return null;
  const isPaid = !!winner.paid_at;
  const baseStyle = {
    fontSize: compact ? '0.6rem' : '0.65rem',
    fontWeight: 800,
    padding: compact ? '0.12rem 0.4rem' : '0.18rem 0.55rem',
    borderRadius: '4px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    border: '1px solid',
    cursor: onToggle ? 'pointer' : 'default',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
    background: isPaid ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
    color: isPaid ? '#22c55e' : '#fbbf24',
    borderColor: isPaid ? 'rgba(34,197,94,0.4)' : 'rgba(245,158,11,0.4)',
  };
  const label = isPaid ? '✓ Paid' : '⏳ Pending';
  const title = isPaid
    ? `Paid${winner.paid_by ? ' by ' + winner.paid_by : ''}${winner.paid_at ? ' — ' + new Date(winner.paid_at).toLocaleString() : ''}${onToggle ? ' (click to mark unpaid)' : ''}`
    : (onToggle ? 'Click to mark as paid' : 'Pending payment');
  if (!onToggle) return <span style={baseStyle} title={title}>{label}</span>;
  return (
    <button
      onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); onToggle(winner, isPaid); }}
      title={title}
      style={baseStyle}
    >{label}</button>
  );
};

const CgElapsedTimer = ({ since, style }) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!since) return null;
  const start = typeof since === 'number' ? since : new Date(since).getTime();
  const elapsed = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const display = h > 0
    ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
    : `${m}:${String(s).padStart(2,'0')}`;
  return <span style={style}>⏱ {display}</span>;
};


const CgChatFeed = ({ messages, drawnAt }) => {
  const scrollRef = React.useRef(null);
  const stickToBottomRef = React.useRef(true);
  // Track whether we're "near the bottom"; if user scrolls up, stop auto-scrolling
  // so they can read history without being yanked back down on every poll tick.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 40; // 40px tolerance
  };
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages?.length]);
  // Force-stick to bottom on first paint regardless of cached scroll position
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickToBottomRef.current = true;
  }, []);

  if (!messages || messages.length === 0) {
    return (
      <div style={{background:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'0.75rem', minHeight:'140px', maxHeight:'240px', display:'flex', alignItems:'center', justifyContent:'center'}}>
        <div style={{color:'#71717a', fontSize:'0.8rem', fontStyle:'italic'}}>Waiting for them to speak in chat...</div>
      </div>
    );
  }

  const drawnMs = drawnAt ? new Date(drawnAt).getTime() : null;
  const splitIdx = drawnMs != null ? messages.findIndex(m => m.at >= drawnMs) : -1;
  const hasBoth = splitIdx > 0 && splitIdx < messages.length;
  const out = [];
  messages.forEach((m, i) => {
    if (hasBoth && i === splitIdx) {
      out.push(
        <div key={`sep-${i}`} style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.4rem 0.2rem', margin:'0.15rem 0'}}>
          <div style={{flex:1, height:'1px', background:'linear-gradient(to right, transparent, rgba(251,191,36,0.4), rgba(251,191,36,0.6))'}} />
          <div style={{fontSize:'0.62rem', color:'#fbbf24', fontWeight:800, letterSpacing:'1.5px', textTransform:'uppercase', whiteSpace:'nowrap'}}>🎉 won here</div>
          <div style={{flex:1, height:'1px', background:'linear-gradient(to left, transparent, rgba(251,191,36,0.4), rgba(251,191,36,0.6))'}} />
        </div>
      );
    }
    const isLatest = i === messages.length - 1;
    const isPreWin = hasBoth && i < splitIdx;
    out.push(
      <div key={i} style={{fontSize:'0.82rem', color: isPreWin ? '#a1a1aa' : '#e2e8f0', padding:'0.3rem 0.5rem', background: isLatest ? 'rgba(168,85,247,0.1)' : 'transparent', borderRadius:'5px', borderLeft: isLatest ? '2px solid #c084fc' : '2px solid transparent', opacity: isPreWin ? 0.75 : 1}}>
        <span style={{color:'#71717a', fontSize:'0.65rem', marginRight:'0.5rem', fontFamily:'monospace'}}>{new Date(m.at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit', second:'2-digit'})}</span>
        {m.text}
      </div>
    );
  });

  return (
    <div ref={scrollRef} onScroll={onScroll} style={{background:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'0.75rem', minHeight:'140px', maxHeight:'240px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'0.3rem'}}>
      {out}
    </div>
  );
};

const CgDetailRow = ({ label, value, tone = 'muted' }) => {
  const colors = {
    good: '#22c55e',
    warn: '#fbbf24',
    bad: '#f87171',
    muted: '#a1a1aa'
  };
  return (
    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem', padding:'0.5rem 0.7rem', background:'rgba(0,0,0,0.3)', borderRadius:'6px', fontSize:'0.8rem'}}>
      <span style={{color:'#71717a', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.5px'}}>{label}</span>
      <span style={{color: colors[tone] || colors.muted, fontWeight:700}}>{value}</span>
    </div>
  );
};

// Prize amount input for a winner. Admin types the amount, saves.
const CgPrizeInput = ({ winner, onSave }) => {
  const [amount, setAmount] = React.useState(parseFloat(winner.prize_amount) > 0 ? String(parseFloat(winner.prize_amount)) : '');
  const currentPrize = parseFloat(winner.prize_amount) || 0;
  const parsed = parseFloat(amount);
  const hasChange = Number.isFinite(parsed) && parsed >= 0 && parsed !== currentPrize;
  const save = () => { if (hasChange) onSave(parsed); };
  return (
    <div style={{marginTop:'0.75rem', padding:'0.75rem', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'8px'}}>
      <div style={{fontSize:'0.7rem', fontWeight:700, color:'#86efac', marginBottom:'0.4rem', textTransform:'uppercase', letterSpacing:'0.5px'}}>💰 Prize amount</div>
      <div style={{display:'flex', gap:'0.35rem'}}>
        <div style={{display:'flex', alignItems:'center', padding:'0 0.6rem', background:'rgba(0,0,0,0.4)', borderRadius:'6px 0 0 6px', border:'1px solid rgba(34,197,94,0.3)', borderRight:'none', color:'#86efac', fontWeight:700}}>$</div>
        <input type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') save(); }} style={{flex:1, padding:'0.5rem 0.7rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(34,197,94,0.3)', borderLeft:'none', borderRight:'none', color:'#fff', fontSize:'0.9rem', outline:'none', fontFamily:'monospace'}} />
        <button onClick={save} disabled={!hasChange} style={{padding:'0.5rem 0.9rem', background: hasChange ? '#22c55e' : 'rgba(113,113,122,0.2)', border:'none', borderRadius:'0 6px 6px 0', color: hasChange ? '#052e16' : '#71717a', fontWeight:800, cursor: hasChange ? 'pointer' : 'not-allowed', fontSize:'0.8rem'}}>Save</button>
      </div>
    </div>
  );
};

// Odometer counter — animated count-up display. Defined at MODULE scope so it doesn't
// get recreated on every parent re-render (which would unmount/remount and reset animation).
const OdometerCounter = React.memo(({ value, label, size = 'large' }) => {
  const [displayValue, setDisplayValue] = React.useState(0);
  const animRef = React.useRef(null);
  const prevValueRef = React.useRef(0);
  const currentDisplayRef = React.useRef(0);
  
  React.useEffect(() => {
    if (!value || value <= 0) { setDisplayValue(0); currentDisplayRef.current = 0; prevValueRef.current = 0; return; }
    if (value === prevValueRef.current) return;
    prevValueRef.current = value;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const start = Date.now();
    const startVal = currentDisplayRef.current;
    const duration = 1500;
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = Math.floor(startVal + (value - startVal) * eased);
      currentDisplayRef.current = next;
      setDisplayValue(next);
      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        currentDisplayRef.current = value;
        setDisplayValue(value);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [value]);
  
  const digits = String(Math.floor(displayValue).toLocaleString()).split('');
  const isLarge = size === 'large';
  const tileSize = isLarge ? { w:'30px', h:'40px', fs:'1.6rem', gap:'4px' } : { w:'22px', h:'30px', fs:'1.15rem', gap:'3px' };
  
  return (
    <div style={{display:'inline-flex', flexDirection:'column', alignItems:'center', gap: isLarge ? '0.4rem' : '0.3rem'}}>
      {label && <div style={{fontSize: isLarge ? '0.72rem' : '0.62rem', fontWeight:700, color:'#3b82f6', letterSpacing:'2px', textTransform:'uppercase'}}>{label}</div>}
      <div style={{display:'inline-flex', alignItems:'center', gap: tileSize.gap}}>
        <span style={{color:'#22c55e', fontSize: isLarge ? '1.4rem' : '1rem', fontWeight:900, marginRight:'4px', textShadow:'0 0 12px rgba(34,197,94,0.4)'}}>$</span>
        {digits.map((d, i) => d === ',' ? (
          // Separator sits on the tiles' baseline (bottom-aligned to tile height),
          // not vertically centered — keeps the comma from floating mid-row
          <span key={i} style={{display:'inline-flex', alignItems:'flex-end', height: tileSize.h, lineHeight:1, paddingBottom:'3px', color:'#71717a', fontSize: tileSize.fs, fontWeight:800}}>,</span>
        ) : (
          <span key={i} style={{
            display:'inline-flex', alignItems:'center', justifyContent:'center',
            width: tileSize.w, height: tileSize.h, fontSize: tileSize.fs,
            fontWeight:900, color:'#fff', fontFamily:'monospace',
            background:'linear-gradient(180deg, rgba(20,20,28,0.95), rgba(10,10,16,0.95))',
            border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px',
            boxShadow:'inset 0 1px 0 rgba(255,255,255,0.05), 0 1px 3px rgba(0,0,0,0.4)'
          }}>{d}</span>
        ))}
      </div>
    </div>
  );
});

// Admin manager for landing-page featured social posts (module scope so it doesn't remount)
const AdminFeaturedPosts = ({ apiUrl, getToken }) => {
  const emptyForm = { platform: 'twitter', url: '', title: '', description: '', imageUrl: '', isGiveaway: false };
  const [posts, setPosts] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const load = React.useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/featured-posts`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (e) { console.error('Load featured posts error:', e); }
  }, [apiUrl, getToken]);

  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.url.trim() || !form.title.trim()) return alert('URL and title are required');
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/featured-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setForm(emptyForm);
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to add post');
      }
    } catch (e) { alert('Failed to add post'); }
    setSaving(false);
  };

  const toggle = async (id) => {
    try {
      await fetch(`${apiUrl}/api/admin/featured-posts/${id}/toggle`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${getToken()}` } });
      load();
    } catch (e) { alert('Failed to toggle post'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this featured post?')) return;
    try {
      await fetch(`${apiUrl}/api/admin/featured-posts/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}` } });
      load();
    } catch (e) { alert('Failed to delete post'); }
  };

  const inputStyle = { padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.85rem', fontFamily: 'Outfit, sans-serif', width: '100%' };
  const labelStyle = { fontSize: '0.7rem', color: '#a1a1aa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.3rem', display: 'block' };
  const platformEmoji = { twitter: '𝕏', instagram: '📸', youtube: '▶️', twitch: '📺', tiktok: '🎵', discord: '💬' };

  return (
    <div>
      <div style={{ color: '#a1a1aa', fontSize: '0.85rem', marginBottom: '1.2rem', lineHeight: 1.65, background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '10px', padding: '0.9rem 1.1rem' }}>
        <div style={{ fontWeight: 700, color: '#e4e4e7', marginBottom: '0.4rem' }}>Where posts appear:</div>
        <div>• Posts marked as <strong style={{ color: '#fbbf24' }}>giveaway</strong> → the gold <strong style={{ color: '#fbbf24' }}>Featured Giveaway banner near the top of the LANDING PAGE</strong>, with the real post embedded inside the banner. Only the newest active giveaway post is shown there.</div>
        <div>• All other posts → the <strong style={{ color: '#c4b5fd' }}>HIGHLIGHTS page</strong>, in a "From the Socials" section above the YouTube shorts (max 6 active posts, newest first).</div>
        <div style={{ fontWeight: 700, color: '#e4e4e7', margin: '0.6rem 0 0.4rem' }}>How each platform renders:</div>
        <div>• <strong style={{ color: '#e4e4e7' }}>X / Twitter</strong> — paste the full post URL (https://x.com/user/status/...) and the <strong style={{ color: '#c4b5fd' }}>real post is embedded</strong>, dark themed.</div>
        <div>• <strong style={{ color: '#e4e4e7' }}>YouTube</strong> — paste a video or Shorts URL and a <strong style={{ color: '#c4b5fd' }}>real video player is embedded</strong> (Shorts render vertical). ⚠️ Age-restricted videos refuse to play embedded — test after adding, and avoid posting age-restricted gambling clips here.</div>
        <div>• <strong style={{ color: '#e4e4e7' }}>Instagram</strong> — paste a post/reel URL and we use Instagram's official embed. ⚠️ Instagram often blocks embeds (private account, some browsers/ad-blockers). Check the page after adding — if the embed doesn't load, the site automatically falls back to a styled card, so <strong style={{ color: '#fbbf24' }}>always fill in Title + Image URL as backup for Instagram</strong>.</div>
        <div>• <strong style={{ color: '#e4e4e7' }}>Twitch / TikTok / Discord</strong> — shown as a styled card using your title, description and image.</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.8rem', marginBottom: '0.8rem' }}>
        <div>
          <label style={labelStyle}>Platform *</label>
          <select value={form.platform} onChange={e => update('platform', e.target.value)} style={inputStyle}>
            <option value="twitter">X / Twitter</option>
            <option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option>
            <option value="twitch">Twitch</option>
            <option value="tiktok">TikTok</option>
            <option value="discord">Discord</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Post URL *</label>
          <input value={form.url} onChange={e => update('url', e.target.value)} placeholder="https://x.com/snutztv/status/..." style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Title *</label>
          <input value={form.title} onChange={e => update('title', e.target.value)} placeholder="$500 Giveaway — 48 hours only!" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Image URL (optional)</label>
          <input value={form.imageUrl} onChange={e => update('imageUrl', e.target.value)} placeholder="https://..." style={inputStyle} />
        </div>
      </div>
      <div style={{ marginBottom: '0.8rem' }}>
        <label style={labelStyle}>Description (optional)</label>
        <input value={form.description} onChange={e => update('description', e.target.value)} placeholder="Follow + retweet to enter. Winner drawn Friday." style={inputStyle} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: '#fbbf24', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isGiveaway} onChange={e => update('isGiveaway', e.target.checked)} />
          🎁 This post has an active giveaway (show the Featured Giveaway banner)
        </label>
        <button
          onClick={submit}
          disabled={saving}
          style={{ padding: '0.6rem 1.4rem', background: 'linear-gradient(135deg, #a855f7, #7c3aed)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Adding…' : '+ Add Post'}
        </button>
      </div>

      {posts.length === 0 ? (
        <p className="admin-no-data">No featured posts yet — add one above.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {posts.map(post => (
            <div key={post.id} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.7rem 0.9rem', background: 'rgba(255,255,255,0.03)', border: `1px solid ${post.active ? 'rgba(168,85,247,0.25)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', opacity: post.active ? 1 : 0.5 }}>
              <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{platformEmoji[post.platform] || '🔗'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {post.title}
                  {post.is_giveaway && <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.45rem', borderRadius: '999px', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24' }}>GIVEAWAY</span>}
                  {!post.active && <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.45rem', borderRadius: '999px', background: 'rgba(255,255,255,0.08)', color: '#a1a1aa' }}>HIDDEN</span>}
                </div>
                <a href={post.url} target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', fontSize: '0.72rem', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '420px' }}>{post.url}</a>
              </div>
              <button onClick={() => toggle(post.id)} style={{ padding: '0.35rem 0.7rem', background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '6px', color: '#c4b5fd', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                {post.active ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => remove(post.id)} style={{ padding: '0.35rem 0.7rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Coins a claimable giveaway can be paid out in. Same keys + patterns as CLAIM_CRYPTO_COINS in server.js.
const CLAIM_CRYPTO_COINS = [
  { key: 'BTC',        label: 'Bitcoin (BTC)',        placeholder: 'bc1… / 1… / 3…', pattern: /^(bc1[a-z0-9]{25,87}|[13][1-9A-HJ-NP-Za-km-z]{25,34})$/i },
  { key: 'ETH',        label: 'Ethereum (ETH)',       placeholder: '0x…',            pattern: /^0x[a-fA-F0-9]{40}$/ },
  { key: 'LTC',        label: 'Litecoin (LTC)',       placeholder: 'ltc1… / L… / M…', pattern: /^(ltc1[a-z0-9]{25,87}|[LM3][1-9A-HJ-NP-Za-km-z]{26,33})$/i },
  { key: 'SOL',        label: 'Solana (SOL)',         placeholder: 'Solana address', pattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/ },
  { key: 'USDT-ERC20', label: 'USDT · Ethereum (ERC-20)', placeholder: '0x…',        pattern: /^0x[a-fA-F0-9]{40}$/ },
  { key: 'USDT-TRC20', label: 'USDT · Tron (TRC-20)', placeholder: 'T…',             pattern: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ },
  { key: 'USDC-ERC20', label: 'USDC · Ethereum (ERC-20)', placeholder: '0x…',        pattern: /^0x[a-fA-F0-9]{40}$/ },
];

// ============================================
// Admin: Rewards Ledger — every cash reward one user has received, in one view.
// Built for lossback review (subtract rewards already paid out).
// ============================================
const SOURCE_META = {
  leaderboard_monthly: { icon: '📊', color: '#fbbf24', name: 'Monthly leaderboard' },
  hunt_win:           { icon: '🏆', color: '#fbbf24', name: 'Bonus hunt wins' },
  battle_prize:       { icon: '⚔️', color: '#f87171', name: 'Slot battle prizes' },
  giveaway_admin:     { icon: '🎁', color: '#a855f7', name: 'Admin giveaways' },
  giveaway_claimable: { icon: '🎟️', color: '#a855f7', name: 'Claimable giveaways' },
  giveaway_chat:      { icon: '💬', color: '#c084fc', name: 'Chat giveaways' },
  wager_reward:       { icon: '💰', color: '#22c55e', name: 'Wager rewards' },
  points_shop:        { icon: '💎', color: '#38bdf8', name: 'Points shop' },
  referral:           { icon: '🤝', color: '#f472b6', name: 'Recruit & Earn' },
  clip:               { icon: '✂️', color: '#facc15', name: 'Clip & Earn' },
  challenge:          { icon: '⭐', color: '#fb923c', name: 'Challenges' },
  raffle:             { icon: '🎰', color: '#e879f9', name: 'Raffle / lottery' },
  slot_pick:          { icon: '🎯', color: '#818cf8', name: 'Slot picks' },
  tournament:         { icon: '⚔️', color: '#f87171', name: 'Tournaments' },
  lossback:           { icon: '💸', color: '#4ade80', name: 'Lossback paid' }
};

const AdminRewardsLedger = ({ apiUrl, getToken }) => {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [ledger, setLedger] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [range, setRange] = React.useState('period'); // period | all | custom
  const [custom, setCustom] = React.useState({ from: '', to: '' });
  const [selectedUserId, setSelectedUserId] = React.useState(null);
  const [mode, setMode] = React.useState('user'); // user | site
  const [summary, setSummary] = React.useState(null);
  const [sumRange, setSumRange] = React.useState('period'); // period | all | custom
  const [sumCustom, setSumCustom] = React.useState({ from: '', to: '' });

  const authHeaders = React.useCallback(() => ({ 'Authorization': `Bearer ${getToken()}` }), [getToken]);

  const search = React.useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    try {
      const r = await fetch(`${apiUrl}/api/admin/fraud/search?q=${encodeURIComponent(q.trim())}`, { headers: authHeaders() });
      if (r.ok) setResults((await r.json()).users || []);
    } catch (e) { /* ignore */ }
  }, [apiUrl, authHeaders]);

  React.useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const loadLedger = React.useCallback(async (userId, rangeMode, customRange) => {
    if (!userId) return;
    setLoading(true);
    try {
      let qs = '';
      if (rangeMode === 'custom' && (customRange.from || customRange.to)) {
        const parts = [];
        if (customRange.from) parts.push(`from=${encodeURIComponent(new Date(customRange.from).toISOString())}`);
        if (customRange.to) parts.push(`to=${encodeURIComponent(new Date(customRange.to + 'T23:59:59').toISOString())}`);
        qs = '?' + parts.join('&');
      }
      const r = await fetch(`${apiUrl}/api/admin/user-rewards/${userId}${qs}`, { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        // "Current period" filters client-side against the period the server reports
        if (rangeMode === 'period' && data.currentPeriod?.start) {
          const s = new Date(data.currentPeriod.start), e = new Date(data.currentPeriod.end);
          const inP = (row) => row.date && new Date(row.date) >= s && new Date(row.date) <= e;
          const filtered = data.rewards.filter(inP);
          const bySource = {};
          let total = 0;
          for (const row of filtered) {
            if (!row.counts) continue;
            bySource[row.source] = (bySource[row.source] || 0) + row.amount;
            total += row.amount;
          }
          data.rewards = filtered;
          data.totals = { ...data.totals, window: total, bySource, countedRows: filtered.filter(x => x.counts).length, pendingRows: filtered.filter(x => !x.counts).length };
        }
        setLedger(data);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [apiUrl, authHeaders]);

  React.useEffect(() => { if (selectedUserId) loadLedger(selectedUserId, range, custom); }, [selectedUserId, range, custom, loadLedger]);

  const loadSummary = React.useCallback(async (rangeMode, customRange) => {
    setLoading(true);
    try {
      let qs = '';
      if (rangeMode === 'period') {
        // Server tells us the current period; ask for exactly that window
        const probe = await fetch(`${apiUrl}/api/admin/rewards-summary`, { headers: authHeaders() });
        if (probe.ok) {
          const pd = await probe.json();
          if (pd.currentPeriod?.start) {
            qs = `?from=${encodeURIComponent(pd.currentPeriod.start)}&to=${encodeURIComponent(pd.currentPeriod.end)}`;
          }
        }
      } else if (rangeMode === 'custom' && (customRange.from || customRange.to)) {
        const parts = [];
        if (customRange.from) parts.push(`from=${encodeURIComponent(new Date(customRange.from).toISOString())}`);
        if (customRange.to) parts.push(`to=${encodeURIComponent(new Date(customRange.to + 'T23:59:59').toISOString())}`);
        qs = '?' + parts.join('&');
      }
      const r = await fetch(`${apiUrl}/api/admin/rewards-summary${qs}`, { headers: authHeaders() });
      if (r.ok) setSummary(await r.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [apiUrl, authHeaders]);

  React.useEffect(() => { if (mode === 'site') loadSummary(sumRange, sumCustom); }, [mode, sumRange, sumCustom, loadSummary]);

  const money = (n) => '$' + (Math.round((parseFloat(n) || 0) * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const input = { padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.85rem', fontFamily: 'Outfit, sans-serif' };
  const chip = (active) => ({ padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem', fontFamily: 'Outfit, sans-serif', background: active ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.04)', border: `1px solid ${active ? '#a855f7' : 'rgba(255,255,255,0.1)'}`, color: active ? '#fff' : '#a1a1aa' });

  return (
    <div>
      <p style={{ fontSize: '0.8rem', color: '#a1a1aa', marginBottom: '1rem' }}>
        Every cash reward a user has received, across all systems. Use it when reviewing a <strong style={{ color: '#4ade80' }}>lossback claim</strong> — subtract what they've already been paid.
      </p>

      {/* Mode switch */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[['user', '👤 Per user'], ['site', '🌍 Whole site']].map(([k, lbl]) => (
          <button key={k} onClick={() => setMode(k)} style={{
            padding: '0.45rem 1.1rem', borderRadius: '9px', cursor: 'pointer', fontWeight: 800, fontSize: '0.82rem', fontFamily: 'Outfit, sans-serif',
            background: mode === k ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${mode === k ? 'transparent' : 'rgba(255,255,255,0.1)'}`,
            color: mode === k ? '#fff' : '#a1a1aa'
          }}>{lbl}</button>
        ))}
      </div>

      {mode === 'site' && (
        <div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
            <button style={chip(sumRange === 'period')} onClick={() => setSumRange('period')}>Current period</button>
            <button style={chip(sumRange === 'all')} onClick={() => setSumRange('all')}>All time</button>
            <button style={chip(sumRange === 'custom')} onClick={() => setSumRange('custom')}>Custom</button>
            {sumRange === 'custom' && (
              <>
                <input type="date" value={sumCustom.from} onChange={e => setSumCustom(c => ({ ...c, from: e.target.value }))} style={{ ...input, colorScheme: 'dark' }} />
                <span style={{ color: '#71717a' }}>→</span>
                <input type="date" value={sumCustom.to} onChange={e => setSumCustom(c => ({ ...c, to: e.target.value }))} style={{ ...input, colorScheme: 'dark' }} />
              </>
            )}
          </div>

          {loading && !summary ? (
            <p style={{ color: '#71717a', fontSize: '0.85rem' }}>Adding it all up…</p>
          ) : summary ? (
            <>
              {/* Headline */}
              <div style={{ textAlign: 'center', padding: '1.4rem 1rem', borderRadius: '18px', background: 'linear-gradient(160deg, rgba(34,197,94,0.12), rgba(168,85,247,0.06))', border: '1px solid rgba(34,197,94,0.3)', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '2px', color: '#8b8b96' }}>TOTAL PAID OUT</div>
                <div style={{ fontSize: '2.6rem', fontWeight: 900, color: '#22c55e', lineHeight: 1.1 }}>{money(summary.totals.grand)}</div>
                <div style={{ fontSize: '0.76rem', color: '#a1a1aa', marginTop: '0.3rem' }}>
                  {summary.totals.payouts.toLocaleString()} payouts · {summary.totals.recipients.toLocaleString()} people
                  {summary.totals.pending.amount > 0 && <> · <span style={{ color: '#fbbf24' }}>{money(summary.totals.pending.amount)} pending</span></>}
                </div>
              </div>

              {/* Breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
                {Object.entries(summary.bySource).map(([src, v]) => {
                  const m = SOURCE_META[src] || { icon: '•', color: '#a1a1aa', name: src };
                  const pct = summary.totals.grand > 0 ? (v.amount / summary.totals.grand) * 100 : 0;
                  return (
                    <div key={src} style={{ position: 'relative', padding: '0.6rem 0.9rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '11px', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: `${m.color}1a`, pointerEvents: 'none' }} />
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.95rem' }}>{m.icon}</span>
                        <span style={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff', flex: 1, minWidth: '140px' }}>{m.name}</span>
                        <span style={{ fontSize: '0.68rem', color: '#71717a' }}>{v.count} payout{v.count === 1 ? '' : 's'} · {v.recipients} people</span>
                        <span style={{ fontSize: '0.68rem', color: '#8b8b96', minWidth: '44px', textAlign: 'right' }}>{pct.toFixed(1)}%</span>
                        <span style={{ fontWeight: 900, fontSize: '1rem', color: m.color, minWidth: '96px', textAlign: 'right' }}>{money(v.amount)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Copy for the graphic */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
                <button
                  onClick={() => {
                    const lines = [
                      `SNUTZY payouts${sumRange === 'period' && summary.currentPeriod?.label ? ` — ${summary.currentPeriod.label}` : sumRange === 'all' ? ' — all time' : ''}`,
                      `TOTAL: ${money(summary.totals.grand)} across ${summary.totals.payouts} payouts to ${summary.totals.recipients} people`,
                      '',
                      ...Object.entries(summary.bySource).map(([src, v]) => {
                        const m = SOURCE_META[src] || { icon: '•', name: src };
                        return `${m.icon} ${m.name}: ${money(v.amount)} (${v.count})`;
                      })
                    ];
                    navigator.clipboard?.writeText(lines.join('\n'));
                    alert('📋 Breakdown copied — paste it wherever you build the graphic');
                  }}
                  style={{ padding: '0.5rem 1.1rem', borderRadius: '9px', border: '1px solid rgba(168,85,247,0.4)', background: 'rgba(168,85,247,0.15)', color: '#d8b4fe', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}
                >
                  📋 Copy breakdown
                </button>
                {summary.totals.duplicatesSkipped > 0 && (
                  <span style={{ fontSize: '0.68rem', color: '#71717a' }}>
                    {summary.totals.duplicatesSkipped} duplicate giveaway {summary.totals.duplicatesSkipped === 1 ? 'entry' : 'entries'} excluded
                  </span>
                )}
              </div>

              {/* Top recipients */}
              {summary.topRecipients?.length > 0 && (
                <>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '2px', color: '#8b8b96', marginBottom: '0.5rem' }}>TOP RECIPIENTS</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {summary.topRecipients.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.35rem 0.7rem', background: 'rgba(255,255,255,0.025)', borderRadius: '8px' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#71717a', minWidth: '22px' }}>#{i + 1}</span>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#e4e4e7', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 900, color: '#22c55e' }}>{money(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <p style={{ fontSize: '0.68rem', color: '#71717a', marginTop: '0.9rem' }}>
                Counts money actually paid/approved. Pending and denied rewards are excluded from the total. Points shop items only count if they have a cash value set.
              </p>
            </>
          ) : (
            <p style={{ color: '#71717a', fontSize: '0.85rem' }}>No data.</p>
          )}
        </div>
      )}

      {/* User search */}
      {mode === 'user' && (<>
      <div style={{ position: 'relative', marginBottom: '1.2rem', maxWidth: '460px' }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by Twitch, Shuffle or Discord username…"
          style={{ ...input, width: '100%', boxSizing: 'border-box' }}
        />
        {results.length > 0 && query.trim().length >= 2 && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 40, background: 'rgba(14,12,20,0.98)', border: '1px solid rgba(168,85,247,0.35)', borderRadius: '10px', overflow: 'hidden', maxHeight: '280px', overflowY: 'auto', boxShadow: '0 16px 40px rgba(0,0,0,0.6)' }}>
            {results.map(r => (
              <div key={r.id}
                onClick={() => { setSelectedUserId(r.id); setQuery(''); setResults([]); }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                {r.profile_image_url ? <img src={r.profile_image_url} alt="" style={{ width: '26px', height: '26px', borderRadius: '50%' }} /> : <span>👤</span>}
                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff', flex: 1 }}>{r.display_name}</span>
                {r.shuffle_username && <span style={{ fontSize: '0.7rem', color: '#a78bfa' }}>🎰 {r.shuffle_username}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {loading && <p style={{ color: '#71717a', fontSize: '0.85rem' }}>Building ledger…</p>}

      {ledger && (
        <>
          {/* User header + range picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap', padding: '0.9rem 1.1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', marginBottom: '0.8rem' }}>
            {ledger.user.avatar ? <img src={ledger.user.avatar} alt="" style={{ width: '42px', height: '42px', borderRadius: '50%' }} /> : <span style={{ fontSize: '1.6rem' }}>👤</span>}
            <div style={{ flex: 1, minWidth: '180px' }}>
              <div style={{ fontWeight: 800, color: '#fff' }}>{ledger.user.displayName}</div>
              <div style={{ fontSize: '0.7rem', color: '#8b8b96', display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {ledger.user.shuffleUsername && <span>🎰 {ledger.user.shuffleUsername}</span>}
                {ledger.user.discordUsername && <span>💬 {ledger.user.discordUsername}</span>}
                {ledger.user.kickUsername && <span style={{ color: '#53fc18' }}>🟢 {ledger.user.kickUsername}</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#22c55e', lineHeight: 1 }}>{money(ledger.totals.window)}</div>
              <div style={{ fontSize: '0.62rem', color: '#71717a', fontWeight: 700, letterSpacing: '1px' }}>REWARDS IN RANGE</div>
              <div style={{ fontSize: '0.66rem', color: '#8b8b96', marginTop: '0.15rem' }}>all-time: {money(ledger.totals.allTime)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
            <button style={chip(range === 'period')} onClick={() => setRange('period')}>Current period{ledger.currentPeriod?.label ? ` (${ledger.currentPeriod.label})` : ''}</button>
            <button style={chip(range === 'all')} onClick={() => setRange('all')}>All time</button>
            <button style={chip(range === 'custom')} onClick={() => setRange('custom')}>Custom</button>
            {range === 'custom' && (
              <>
                <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} style={{ ...input, colorScheme: 'dark' }} />
                <span style={{ color: '#71717a' }}>→</span>
                <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} style={{ ...input, colorScheme: 'dark' }} />
              </>
            )}
          </div>

          {/* Per-source summary */}
          {Object.keys(ledger.totals.bySource || {}).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
              {Object.entries(ledger.totals.bySource).sort((a, b) => b[1] - a[1]).map(([src, amt]) => {
                const m = SOURCE_META[src] || { icon: '•', color: '#a1a1aa', name: src };
                return (
                  <div key={src} style={{ padding: '0.55rem 0.75rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px' }}>
                    <div style={{ fontSize: '0.68rem', color: '#8b8b96', fontWeight: 700 }}>{m.icon} {m.name}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 900, color: m.color }}>{money(amt)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Line items */}
          {ledger.rewards.length === 0 ? (
            <p style={{ color: '#71717a', fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' }}>No rewards in this range.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {ledger.rewards.map((r, i) => {
                const m = SOURCE_META[r.source] || { icon: '•', color: '#a1a1aa', name: r.source };
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.5rem 0.8rem', background: r.counts ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.012)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '9px', opacity: r.counts ? 1 : 0.55, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.95rem', flexShrink: 0 }}>{m.icon}</span>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e4e4e7' }}>{r.label}</div>
                      <div style={{ fontSize: '0.68rem', color: '#71717a' }}>{r.detail}{r.date ? ` · ${new Date(r.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</div>
                    </div>
                    <span title={r.duplicateNote || ''} style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.5px', padding: '0.12rem 0.45rem', borderRadius: '999px', flexShrink: 0, background: r.duplicate ? 'rgba(251,191,36,0.12)' : r.counts ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.05)', border: `1px solid ${r.duplicate ? 'rgba(251,191,36,0.35)' : r.counts ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'}`, color: r.duplicate ? '#fbbf24' : r.counts ? '#4ade80' : '#8b8b96' }}>
                      {r.duplicate ? '⧉ DUPLICATE' : (r.status || '').toUpperCase()}
                    </span>
                    <span style={{ fontWeight: 900, color: r.counts ? m.color : '#71717a', minWidth: '80px', textAlign: 'right', fontSize: '0.9rem' }}>{money(r.amount)}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p style={{ fontSize: '0.68rem', color: '#71717a', marginTop: '0.8rem' }}>
            Faded rows are pending/denied and are <strong>not</strong> counted in the totals. Rows tagged <strong style={{ color: '#fbbf24' }}>⧉ DUPLICATE</strong> are manual giveaway entries that match a chat giveaway win (same amount, within a day) — the prize is counted once. Points shop rows only carry a $ value if the item has a cash value set.
          </p>
        </>
      )}
      </>)}
    </div>
  );
};

// Admin manager for the Recruit & Earn referral system (module scope so it doesn't remount)
const AdminReferrals = ({ apiUrl, getToken, onChanged }) => {
  const [view, setView] = React.useState('pending'); // pending | history | network | tools
  const [historyStatus, setHistoryStatus] = React.useState('approved');
  const [rewards, setRewards] = React.useState([]);
  const [network, setNetwork] = React.useState([]);
  const [enabled, setEnabled] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // Tools: manual referral link + window extension
  const [manualReferrer, setManualReferrer] = React.useState('');
  const [manualReferred, setManualReferred] = React.useState('');
  const [extendUser, setExtendUser] = React.useState('');
  const [extendHours, setExtendHours] = React.useState('48');
  const [toolsBusy, setToolsBusy] = React.useState(false);
  const [toolsMsg, setToolsMsg] = React.useState(null); // { type: 'success'|'error', text }

  const authHeaders = React.useCallback(() => ({ 'Authorization': `Bearer ${getToken()}` }), [getToken]);

  const loadRewards = React.useCallback(async (status) => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/referrals/rewards?status=${status}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRewards(data.rewards || []);
      }
    } catch (e) { console.error('Load referral rewards error:', e); }
  }, [apiUrl, authHeaders]);

  const loadNetwork = React.useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/referrals`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setNetwork(data.referrals || []);
      }
    } catch (e) { console.error('Load referral network error:', e); }
  }, [apiUrl, authHeaders]);

  const loadFlag = React.useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/settings/feature_referrals_enabled`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEnabled(data.setting?.value === 'true');
      }
    } catch (e) { console.error('Load referral flag error:', e); }
  }, [apiUrl, authHeaders]);

  React.useEffect(() => { loadFlag(); }, [loadFlag]);
  React.useEffect(() => {
    if (view === 'pending') loadRewards('claimed');
    if (view === 'history') loadRewards(historyStatus);
    if (view === 'network') loadNetwork();
  }, [view, historyStatus, loadRewards, loadNetwork]);

  const refresh = () => {
    if (view === 'pending') loadRewards('claimed');
    if (view === 'history') loadRewards(historyStatus);
    if (view === 'network') loadNetwork();
    if (onChanged) onChanged();
  };

  const toggleFeature = async () => {
    setBusy(true);
    try {
      await fetch(`${apiUrl}/api/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ key: 'feature_referrals_enabled', value: enabled ? 'false' : 'true' })
      });
      setEnabled(!enabled);
    } catch (e) { alert('Failed to toggle feature'); }
    setBusy(false);
  };

  const approve = async (id) => {
    const response = window.prompt('Optional message to the user (leave blank for none):', '');
    if (response === null) return; // cancelled
    const res = await fetch(`${apiUrl}/api/admin/referrals/rewards/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ response })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to approve');
    }
    refresh();
  };

  const deny = async (id) => {
    const response = window.prompt('Reason for denial (shown to the user, required):', '');
    if (response === null) return;
    if (!response.trim()) return alert('A reason is required');
    const res = await fetch(`${apiUrl}/api/admin/referrals/rewards/${id}/deny`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ response })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to deny');
    }
    refresh();
  };

  const flagReferral = async (id) => {
    await fetch(`${apiUrl}/api/admin/referrals/${id}/flag`, { method: 'POST', headers: authHeaders() });
    refresh();
  };

  const removeReferral = async (id) => {
    if (!window.confirm('Remove this referral entirely? All its rewards (including approved history) are deleted.')) return;
    await fetch(`${apiUrl}/api/admin/referrals/${id}`, { method: 'DELETE', headers: authHeaders() });
    refresh();
  };

  const submitManualReferral = async () => {
    if (!manualReferrer.trim() || !manualReferred.trim()) return;
    setToolsBusy(true);
    setToolsMsg(null);
    try {
      const res = await fetch(`${apiUrl}/api/admin/referrals/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ referrer: manualReferrer.trim(), referred: manualReferred.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setToolsMsg({ type: 'success', text: `Linked: ${data.referrer} → ${data.referred}. Milestones start tracking from the next tracker cycle.` });
        setManualReferrer('');
        setManualReferred('');
        if (onChanged) onChanged();
      } else {
        setToolsMsg({ type: 'error', text: data.error || 'Failed to create referral' });
      }
    } catch (e) { setToolsMsg({ type: 'error', text: 'Failed to create referral' }); }
    setToolsBusy(false);
  };

  const submitExtendWindow = async () => {
    if (!extendUser.trim()) return;
    setToolsBusy(true);
    setToolsMsg(null);
    try {
      const res = await fetch(`${apiUrl}/api/admin/referrals/extend-window`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ username: extendUser.trim(), hours: parseInt(extendHours, 10) || 48 })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setToolsMsg({ type: 'success', text: `${data.username} can now set their referrer for the next ${data.hours}h (via the Referrals page).` });
        setExtendUser('');
      } else {
        setToolsMsg({ type: 'error', text: data.error || 'Failed to extend window' });
      }
    } catch (e) { setToolsMsg({ type: 'error', text: 'Failed to extend window' }); }
    setToolsBusy(false);
  };

  const smallBtn = (bg, border, color) => ({ padding: '0.35rem 0.7rem', background: bg, border: `1px solid ${border}`, borderRadius: '6px', color, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'Outfit, sans-serif' });
  const viewBtn = (active) => ({ padding: '0.45rem 1rem', borderRadius: '8px', border: `1px solid ${active ? 'rgba(168,85,247,0.5)' : 'rgba(255,255,255,0.1)'}`, background: active ? 'rgba(168,85,247,0.15)' : 'rgba(255,255,255,0.03)', color: active ? '#d8b4fe' : '#a1a1aa', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' });
  const fmtMoney = (n) => '$' + Math.round(parseFloat(n || 0)).toLocaleString();

  return (
    <div>
      {/* Feature toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.8rem 1rem', borderRadius: '10px', background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.2)', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Recruit &amp; Earn is {enabled === null ? '…' : enabled ? '🟢 LIVE' : '🔴 OFF'}</span>
        <button onClick={toggleFeature} disabled={busy || enabled === null} style={smallBtn(enabled ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)', enabled ? 'rgba(239,68,68,0.35)' : 'rgba(34,197,94,0.35)', enabled ? '#fca5a5' : '#4ade80')}>
          {enabled ? 'Disable' : 'Enable'}
        </button>
        <span style={{ fontSize: '0.75rem', color: '#71717a' }}>Controls the referral prompt, dashboard access and landing page card.</span>
      </div>

      {/* View switcher */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <button style={viewBtn(view === 'pending')} onClick={() => setView('pending')}>⏳ Pending Claims</button>
        <button style={viewBtn(view === 'history')} onClick={() => setView('history')}>📜 History</button>
        <button style={viewBtn(view === 'network')} onClick={() => setView('network')}>🕸️ Referral Network</button>
        <button style={viewBtn(view === 'tools')} onClick={() => setView('tools')}>🛠️ Tools</button>
        {view === 'history' && (
          <select value={historyStatus} onChange={e => setHistoryStatus(e.target.value)} style={{ padding: '0.4rem 0.7rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.8rem', fontFamily: 'Outfit, sans-serif' }}>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="eligible">Eligible (unclaimed)</option>
          </select>
        )}
      </div>

      {(view === 'pending' || view === 'history') && (
        rewards.length === 0 ? (
          <p className="admin-no-data">{view === 'pending' ? 'No claims awaiting review 🎉' : 'Nothing here.'}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {rewards.map(rw => (
              <div key={rw.id} style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.03)', border: `1px solid ${rw.referral_status === 'flagged' ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {rw.referrer_display_name || rw.referrer_username}
                    <span style={{ color: '#71717a', fontWeight: 400 }}> claims </span>
                    <span style={{ color: '#fbbf24' }}>{fmtMoney(rw.reward_amount)}</span>
                    <span style={{ color: '#71717a', fontWeight: 400 }}> ({fmtMoney(rw.milestone_amount)} milestone)</span>
                    {rw.referral_status === 'flagged' && <span style={{ marginLeft: '0.5rem', fontSize: '0.62rem', fontWeight: 700, padding: '0.12rem 0.45rem', borderRadius: '999px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' }}>FLAGGED</span>}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '0.2rem' }}>
                    Recruit: {rw.referred_display_name || rw.referred_username} ({rw.referred_shuffle_username || 'no shuffle'}) · wagered {fmtMoney(rw.last_known_wager)}
                    {rw.referrer_shuffle_username && <> · payout to: <strong style={{ color: '#c4b5fd' }}>{rw.referrer_shuffle_username}</strong></>}
                    {rw.claimed_at && <> · claimed {new Date(rw.claimed_at).toLocaleDateString()}</>}
                  </div>
                  {rw.admin_response && <div style={{ fontSize: '0.75rem', color: '#71717a', marginTop: '0.2rem' }}>Response: {rw.admin_response} {rw.admin_by && `(${rw.admin_by})`}</div>}
                </div>
                {view === 'pending' && (
                  <>
                    <button onClick={() => approve(rw.id)} style={smallBtn('rgba(34,197,94,0.12)', 'rgba(34,197,94,0.35)', '#4ade80')}>✅ Approve</button>
                    <button onClick={() => deny(rw.id)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>❌ Deny</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {view === 'network' && (
        network.length === 0 ? (
          <p className="admin-no-data">No referrals yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {network.map(ref => (
              <div key={ref.id} style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.03)', border: `1px solid ${ref.status === 'flagged' ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', flexWrap: 'wrap', opacity: ref.status === 'flagged' ? 0.85 : 1 }}>
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {ref.referrer_display_name || ref.referrer_username}
                    <span style={{ color: '#a855f7' }}> → </span>
                    {ref.referred_display_name || ref.referred_username}
                    {ref.status === 'flagged' && <span style={{ marginLeft: '0.5rem', fontSize: '0.62rem', fontWeight: 700, padding: '0.12rem 0.45rem', borderRadius: '999px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' }}>FLAGGED</span>}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '0.2rem' }}>
                    Shuffle: {ref.referred_shuffle_username || '—'} · wagered {fmtMoney(ref.last_known_wager)} · {ref.reward_count} rewards ({fmtMoney(ref.approved_total)} approved) · since {new Date(ref.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button onClick={() => flagReferral(ref.id)} style={smallBtn('rgba(251,191,36,0.1)', 'rgba(251,191,36,0.35)', '#fbbf24')}>
                  {ref.status === 'flagged' ? '🟢 Unflag' : '🚩 Flag'}
                </button>
                <button onClick={() => removeReferral(ref.id)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>Delete</button>
              </div>
            ))}
          </div>
        )
      )}

      {view === 'tools' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {toolsMsg && (
            <div style={{
              padding: '0.7rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
              background: toolsMsg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${toolsMsg.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
              color: toolsMsg.type === 'success' ? '#4ade80' : '#fca5a5',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem'
            }}>
              {toolsMsg.text}
              <button onClick={() => setToolsMsg(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1rem' }}>×</button>
            </div>
          )}

          {/* Manually link a referral */}
          <div style={{ padding: '1rem 1.2rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.3rem' }}>🔗 Manually Link a Referral</div>
            <p style={{ fontSize: '0.75rem', color: '#a1a1aa', margin: '0 0 0.8rem' }}>
              Bypasses the 48h window — for recruits who forgot to enter their referrer. Same anti-abuse checks apply (no self/reciprocal referrals, one referrer per user).
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={manualReferrer} onChange={e => setManualReferrer(e.target.value)} placeholder="Referrer (earns rewards)"
                style={{ flex: 1, minWidth: '180px', padding: '0.5rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.82rem', fontFamily: 'Outfit, sans-serif' }} />
              <span style={{ color: '#a855f7', fontWeight: 700 }}>→</span>
              <input value={manualReferred} onChange={e => setManualReferred(e.target.value)} placeholder="Recruit (was referred)"
                onKeyDown={e => { if (e.key === 'Enter') submitManualReferral(); }}
                style={{ flex: 1, minWidth: '180px', padding: '0.5rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.82rem', fontFamily: 'Outfit, sans-serif' }} />
              <button onClick={submitManualReferral} disabled={toolsBusy || !manualReferrer.trim() || !manualReferred.trim()}
                style={{ ...smallBtn('rgba(168,85,247,0.15)', 'rgba(168,85,247,0.4)', '#d8b4fe'), padding: '0.5rem 1rem', opacity: toolsBusy || !manualReferrer.trim() || !manualReferred.trim() ? 0.5 : 1 }}>
                {toolsBusy ? 'Saving…' : 'Link Referral'}
              </button>
            </div>
          </div>

          {/* Reopen a user's set-referrer window */}
          <div style={{ padding: '1rem 1.2rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.3rem' }}>⏰ Reopen Set-Referrer Window</div>
            <p style={{ fontSize: '0.75rem', color: '#a1a1aa', margin: '0 0 0.8rem' }}>
              Lets the user enter their own referrer on the Referrals page even though 48h passed. Tell them to visit the Referrals page — the input will be back.
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={extendUser} onChange={e => setExtendUser(e.target.value)} placeholder="Username (Twitch or Shuffle)"
                onKeyDown={e => { if (e.key === 'Enter') submitExtendWindow(); }}
                style={{ flex: 1, minWidth: '200px', padding: '0.5rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.82rem', fontFamily: 'Outfit, sans-serif' }} />
              <select value={extendHours} onChange={e => setExtendHours(e.target.value)}
                style={{ padding: '0.5rem 0.7rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.82rem', fontFamily: 'Outfit, sans-serif' }}>
                <option value="24">24 hours</option>
                <option value="48">48 hours</option>
                <option value="72">72 hours</option>
                <option value="168">7 days</option>
              </select>
              <button onClick={submitExtendWindow} disabled={toolsBusy || !extendUser.trim()}
                style={{ ...smallBtn('rgba(251,191,36,0.12)', 'rgba(251,191,36,0.35)', '#fbbf24'), padding: '0.5rem 1rem', opacity: toolsBusy || !extendUser.trim() ? 0.5 : 1 }}>
                {toolsBusy ? 'Saving…' : 'Grant Window'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin manager for Clip & Earn (module scope so it doesn't remount)
const AdminClips = ({ apiUrl, getToken, onChanged }) => {
  const [view, setView] = React.useState('pending'); // pending | active | claims | rejected
  const [subs, setSubs] = React.useState([]);
  const [claims, setClaims] = React.useState([]);
  const [enabled, setEnabled] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [viewInputs, setViewInputs] = React.useState({});

  const authHeaders = React.useCallback(() => ({ 'Authorization': `Bearer ${getToken()}` }), [getToken]);

  const loadSubs = React.useCallback(async (status) => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/clips?status=${status}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSubs(data.submissions || []);
      }
    } catch (e) { console.error('Load clips error:', e); }
  }, [apiUrl, authHeaders]);

  const loadClaims = React.useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/clips/claims`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setClaims(data.claims || []);
      }
    } catch (e) { console.error('Load clip claims error:', e); }
  }, [apiUrl, authHeaders]);

  const loadFlag = React.useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/settings/feature_clips_enabled`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEnabled(data.setting?.value === 'true');
      }
    } catch (e) { console.error('Load clips flag error:', e); }
  }, [apiUrl, authHeaders]);

  React.useEffect(() => { loadFlag(); }, [loadFlag]);
  React.useEffect(() => {
    if (view === 'claims') loadClaims();
    else loadSubs(view);
  }, [view, loadSubs, loadClaims]);

  const refresh = () => {
    if (view === 'claims') loadClaims();
    else loadSubs(view);
    if (onChanged) onChanged();
  };

  const toggleFeature = async () => {
    setBusy(true);
    try {
      await fetch(`${apiUrl}/api/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ key: 'feature_clips_enabled', value: enabled ? 'false' : 'true' })
      });
      setEnabled(!enabled);
    } catch (e) { alert('Failed to toggle feature'); }
    setBusy(false);
  };

  const act = async (path, body) => {
    const res = await fetch(`${apiUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body || {})
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Action failed');
    }
    refresh();
  };

  const activate = (id) => act(`/api/admin/clips/${id}/activate`, { note: '' });
  const reject = (id) => {
    const note = window.prompt('Reason for rejection (shown to the user, required):', '');
    if (note === null) return;
    if (!note.trim()) return alert('A reason is required');
    act(`/api/admin/clips/${id}/reject`, { note });
  };
  const updateViews = (id) => {
    const views = parseInt(viewInputs[id], 10);
    if (!Number.isFinite(views) || views < 0) return alert('Enter a valid view count');
    act(`/api/admin/clips/${id}/views`, { views });
    setViewInputs(prev => ({ ...prev, [id]: '' }));
  };
  const approveClaims = (id) => {
    const response = window.prompt('Optional message to the user (leave blank for none):', '');
    if (response === null) return;
    act(`/api/admin/clips/${id}/claims/approve`, { response });
  };
  const denyClaims = (id) => {
    const response = window.prompt('Reason for denial (shown to the user, required):', '');
    if (response === null) return;
    if (!response.trim()) return alert('A reason is required');
    act(`/api/admin/clips/${id}/claims/deny`, { response });
  };

  const smallBtn = (bg, border, color) => ({ padding: '0.35rem 0.7rem', background: bg, border: `1px solid ${border}`, borderRadius: '6px', color, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'Outfit, sans-serif' });
  const viewBtn = (active) => ({ padding: '0.45rem 1rem', borderRadius: '8px', border: `1px solid ${active ? 'rgba(168,85,247,0.5)' : 'rgba(255,255,255,0.1)'}`, background: active ? 'rgba(168,85,247,0.15)' : 'rgba(255,255,255,0.03)', color: active ? '#d8b4fe' : '#a1a1aa', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' });
  const fmtMoney = (n) => '$' + Math.round(parseFloat(n || 0)).toLocaleString();
  const platformEmoji = { youtube: '▶️', twitter: '𝕏', tiktok: '🎵', instagram: '📸' };

  return (
    <div>
      {/* Feature toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.8rem 1rem', borderRadius: '10px', background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.2)', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Clip &amp; Earn is {enabled === null ? '…' : enabled ? '🟢 LIVE' : '🔴 OFF'}</span>
        <button onClick={toggleFeature} disabled={busy || enabled === null} style={smallBtn(enabled ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)', enabled ? 'rgba(239,68,68,0.35)' : 'rgba(34,197,94,0.35)', enabled ? '#fca5a5' : '#4ade80')}>
          {enabled ? 'Disable' : 'Enable'}
        </button>
        <span style={{ fontSize: '0.75rem', color: '#71717a' }}>YouTube views auto-update every 6h; other platforms need manual view updates below.</span>
      </div>

      {/* View switcher */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <button style={viewBtn(view === 'pending')} onClick={() => setView('pending')}>⏳ New Submissions</button>
        <button style={viewBtn(view === 'claims')} onClick={() => setView('claims')}>💰 Payout Claims</button>
        <button style={viewBtn(view === 'active')} onClick={() => setView('active')}>🟢 Active Clips</button>
        <button style={viewBtn(view === 'rejected')} onClick={() => setView('rejected')}>🚫 Rejected</button>
      </div>

      {view === 'claims' ? (
        claims.length === 0 ? (
          <p className="admin-no-data">No payout claims awaiting review 🎉</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {claims.map(c => (
              <div key={c.submission_id} style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {c.display_name || c.username}
                    <span style={{ color: '#71717a', fontWeight: 400 }}> claims </span>
                    <span style={{ color: '#fbbf24' }}>{fmtMoney(c.claim_total)}</span>
                    <span style={{ color: '#71717a', fontWeight: 400 }}> ({c.claim_count} × $10)</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '0.2rem' }}>
                    {platformEmoji[c.platform] || '🔗'} <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>{c.title || c.url}</a>
                    {' '}· {(c.verified_views || 0).toLocaleString()} verified views
                    {c.shuffle_username && <> · payout to: <strong style={{ color: '#c4b5fd' }}>{c.shuffle_username}</strong></>}
                  </div>
                </div>
                <button onClick={() => approveClaims(c.submission_id)} style={smallBtn('rgba(34,197,94,0.12)', 'rgba(34,197,94,0.35)', '#4ade80')}>✅ Approve All</button>
                <button onClick={() => denyClaims(c.submission_id)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>❌ Deny All</button>
              </div>
            ))}
          </div>
        )
      ) : (
        subs.length === 0 ? (
          <p className="admin-no-data">{view === 'pending' ? 'No new submissions 🎉' : 'Nothing here.'}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {subs.map(sub => (
              <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {platformEmoji[sub.platform] || '🔗'} <a href={sub.url} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none' }}>{sub.title || sub.url}</a>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '0.2rem' }}>
                    by {sub.display_name || sub.username} · {(sub.verified_views || 0).toLocaleString()} views
                    {sub.last_view_update && <> (updated {new Date(sub.last_view_update).toLocaleDateString()})</>}
                    {' '}· submitted {new Date(sub.submitted_at).toLocaleDateString()}
                    {parseFloat(sub.approved_total) > 0 && <> · <span style={{ color: '#4ade80' }}>{fmtMoney(sub.approved_total)} paid</span></>}
                    {sub.claimed_count > 0 && <> · <span style={{ color: '#fbbf24' }}>{fmtMoney(sub.claimed_total)} in review</span></>}
                  </div>
                  {sub.admin_notes && <div style={{ fontSize: '0.75rem', color: '#71717a', marginTop: '0.2rem' }}>Note: {sub.admin_notes}</div>}
                </div>
                {view === 'pending' && (
                  <>
                    <button onClick={() => activate(sub.id)} style={smallBtn('rgba(34,197,94,0.12)', 'rgba(34,197,94,0.35)', '#4ade80')}>✅ Activate</button>
                    <button onClick={() => reject(sub.id)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>🚫 Reject</button>
                  </>
                )}
                {view === 'active' && (
                  <>
                    <input
                      value={viewInputs[sub.id] || ''}
                      onChange={e => setViewInputs(prev => ({ ...prev, [sub.id]: e.target.value }))}
                      placeholder="views"
                      style={{ width: '110px', padding: '0.35rem 0.6rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#fff', fontSize: '0.78rem', fontFamily: 'Outfit, sans-serif' }}
                    />
                    <button onClick={() => updateViews(sub.id)} style={smallBtn('rgba(168,85,247,0.12)', 'rgba(168,85,247,0.35)', '#c4b5fd')}>Update Views</button>
                    <button onClick={() => reject(sub.id)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>🚫 Reject</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

// Time-sliced "amount given away" bar shown on the paid predictions, paid
// giveaways and point-shop tabs. The headline number fluctuates with the
// selected slice (driven by /api/admin/payout-summary). Module scope so it
// doesn't remount on every parent render.
const PAYOUT_RANGE_LABELS = { '30d': 'Last 30 days', period: 'This period', all: 'All time' };
const PayoutSummaryBar = ({ metric, range, onRange, summary, loading }) => {
  const fmtMoney = (n) => '$' + Math.round(n || 0).toLocaleString();
  const fmtNum = (n) => Math.round(n || 0).toLocaleString();
  const plural = (n, word) => `${fmtNum(n)} ${word}${n === 1 ? '' : 's'}`;

  let headline = '—', sub = '';
  if (summary) {
    if (metric === 'giveaways') {
      headline = fmtMoney(summary.giveaways.total);
      sub = plural(summary.giveaways.count, 'paid giveaway');
    } else if (metric === 'predictions') {
      headline = fmtMoney(summary.predictions.total);
      sub = `${plural(summary.predictions.count, 'paid reward')} · cash prizes only`;
    } else { // redemptions (point-shop $ auto-parsed from item names)
      headline = fmtMoney(summary.redemptions.total);
      sub = `${plural(summary.redemptions.count, 'fulfilled redemption')} · $ parsed from item names`;
    }
  }

  const rangeBtn = (r) => ({
    padding: '0.4rem 0.85rem', borderRadius: '8px', cursor: 'pointer',
    fontFamily: 'Outfit, sans-serif', fontSize: '0.78rem', fontWeight: 700,
    border: `1px solid ${range === r ? 'rgba(168,85,247,0.55)' : 'rgba(255,255,255,0.12)'}`,
    background: range === r ? 'rgba(168,85,247,0.18)' : 'rgba(255,255,255,0.03)',
    color: range === r ? '#d8b4fe' : '#a1a1aa'
  });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '1rem 1.2rem', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(251,191,36,0.05))', border: '1px solid rgba(168,85,247,0.25)', marginBottom: '1.2rem' }}>
      <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
        {['30d', 'period', 'all'].map(r => (
          <button key={r} onClick={() => onRange(r)} style={rangeBtn(r)}>{PAYOUT_RANGE_LABELS[r]}</button>
        ))}
      </div>
      <div style={{ textAlign: 'right', minWidth: '180px' }}>
        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#fff', lineHeight: 1.1 }}>
          {loading ? '…' : headline}
        </div>
        <div style={{ fontSize: '0.72rem', color: '#a1a1aa', marginTop: '0.15rem' }}>{sub}</div>
      </div>
    </div>
  );
};

// Admin manager for 10% Lossback claims (module scope so it doesn't remount)
// Keep in sync with LOSSBACK_MAX_PAYOUT in backend/server.js — the backend
// rejects anything above this, so a stale value here just blocks valid payouts.
const LOSSBACK_CAP = 2000;

// Collapsible sidebar group. Defaults to open so nothing disappears on first
// load; the user's choice is remembered per-group in localStorage. A group that
// contains the current page force-opens so you can never lose your place.
const SidebarSection = ({ id, title, collapsed, onToggle, containsActive, children }) => {
  const isCollapsed = collapsed && !containsActive;
  const count = React.Children.toArray(children).length;
  return (
    <div className="sidebar-section">
      <button
        className="sidebar-section-title"
        onClick={() => onToggle(id)}
        aria-expanded={!isCollapsed}
        title={isCollapsed ? `Show ${title}` : `Hide ${title}`}
      >
        {title}
        {isCollapsed && count ? <span className="sidebar-section-count">{count}</span> : null}
        <span className={`sidebar-section-chevron ${isCollapsed ? 'collapsed' : ''}`}>▾</span>
      </button>
      {!isCollapsed && <div className="sidebar-section-body">{children}</div>}
    </div>
  );
};

const AdminLossback = ({ apiUrl, getToken, onChanged }) => {
  const [status, setStatus] = React.useState('pending');
  const [claims, setClaims] = React.useState([]);
  const [enabled, setEnabled] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [shot, setShot] = React.useState({ claimId: null, data: null, loading: false });
  const [rewards, setRewards] = React.useState({ claimId: null, data: null, loading: false });

  const authHeaders = React.useCallback(() => ({ 'Authorization': `Bearer ${getToken()}` }), [getToken]);

  // Rewards this user already got inside the claim's period — the amount to
  // subtract before paying lossback. Period comes from the claim's label
  // ("YYYY-MM-DD to YYYY-MM-DD"); falls back to all-time if it can't be parsed.
  const loadRewards = React.useCallback(async (claim) => {
    if (rewards.claimId === claim.id) { setRewards({ claimId: null, data: null, loading: false }); return; }
    setRewards({ claimId: claim.id, data: null, loading: true });
    try {
      const m = (claim.period_label || '').match(/(\d{4}-\d{2}-\d{2})\s*to\s*(\d{4}-\d{2}-\d{2})/);
      const qs = (m ? `?from=${encodeURIComponent(m[1])}&to=${encodeURIComponent(m[2] + 'T23:59:59')}&` : '?') + 'sources=giveaways';
      const res = await fetch(`${apiUrl}/api/admin/user-rewards/${claim.user_id}${qs}`, { headers: authHeaders() });
      setRewards({ claimId: claim.id, data: res.ok ? await res.json() : null, loading: false });
    } catch (e) {
      setRewards({ claimId: claim.id, data: null, loading: false });
    }
  }, [apiUrl, authHeaders, rewards.claimId]);

  const load = React.useCallback(async (st) => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/lossback?status=${st}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setClaims(data.claims || []);
      }
    } catch (e) { console.error('Load lossback error:', e); }
  }, [apiUrl, authHeaders]);

  const loadFlag = React.useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/settings/feature_lossback_enabled`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEnabled(data.setting?.value === 'true');
      }
    } catch (e) { console.error('Load lossback flag error:', e); }
  }, [apiUrl, authHeaders]);

  React.useEffect(() => { loadFlag(); }, [loadFlag]);
  React.useEffect(() => { setShot({ claimId: null, data: null, loading: false }); load(status); }, [status, load]);

  const refresh = () => { load(status); if (onChanged) onChanged(); };

  const toggleFeature = async () => {
    setBusy(true);
    try {
      await fetch(`${apiUrl}/api/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ key: 'feature_lossback_enabled', value: enabled ? 'false' : 'true' })
      });
      setEnabled(!enabled);
    } catch (e) { alert('Failed to toggle feature'); }
    setBusy(false);
  };

  const viewScreenshot = async (claimId) => {
    if (shot.claimId === claimId) { setShot({ claimId: null, data: null, loading: false }); return; }
    setShot({ claimId, data: null, loading: true });
    try {
      const res = await fetch(`${apiUrl}/api/admin/lossback/${claimId}/screenshot`, { headers: authHeaders() });
      const data = res.ok ? await res.json() : {};
      setShot({ claimId, data: data.screenshot || null, loading: false });
    } catch (e) {
      setShot({ claimId, data: null, loading: false });
    }
  };

  const approve = async (id) => {
    const amount = window.prompt(`Payout amount in $ — 10% of losses, capped at $${LOSSBACK_CAP.toLocaleString()} (e.g. 250). Crosscheck the Shuffle stats first:`, '');
    if (amount === null) return;
    const payout = parseFloat(amount);
    if (!Number.isFinite(payout) || payout <= 0) return alert('Enter a valid amount');
    if (payout > LOSSBACK_CAP) return alert(`Lossback is capped at $${LOSSBACK_CAP.toLocaleString()} per claim`);
    const response = window.prompt('Optional message to the user (leave blank for none):', '');
    if (response === null) return;
    const res = await fetch(`${apiUrl}/api/admin/lossback/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ payoutAmount: payout, response })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to approve');
    }
    refresh();
  };

  const deny = async (id) => {
    const response = window.prompt('Reason for denial (shown to the user, required):', '');
    if (response === null) return;
    if (!response.trim()) return alert('A reason is required');
    const res = await fetch(`${apiUrl}/api/admin/lossback/${id}/deny`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ response })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to deny');
    }
    refresh();
  };

  const del = async (id) => {
    if (!window.confirm('Permanently delete this claim and its screenshot? This cannot be undone.')) return;
    const res = await fetch(`${apiUrl}/api/admin/lossback/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to delete');
    }
    refresh();
  };

  const smallBtn = (bg, border, color) => ({ padding: '0.35rem 0.7rem', background: bg, border: `1px solid ${border}`, borderRadius: '6px', color, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'Outfit, sans-serif' });
  const viewBtn = (active) => ({ padding: '0.45rem 1rem', borderRadius: '8px', border: `1px solid ${active ? 'rgba(168,85,247,0.5)' : 'rgba(255,255,255,0.1)'}`, background: active ? 'rgba(168,85,247,0.15)' : 'rgba(255,255,255,0.03)', color: active ? '#d8b4fe' : '#a1a1aa', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' });
  const fmtMoney = (n) => '$' + Math.round(parseFloat(n || 0)).toLocaleString();

  return (
    <div>
      {/* Feature toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.8rem 1rem', borderRadius: '10px', background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.2)', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Lossback is {enabled === null ? '…' : enabled ? '🟢 LIVE' : '🔴 OFF'}</span>
        <button onClick={toggleFeature} disabled={busy || enabled === null} style={smallBtn(enabled ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)', enabled ? 'rgba(239,68,68,0.35)' : 'rgba(34,197,94,0.35)', enabled ? '#fca5a5' : '#4ade80')}>
          {enabled ? 'Disable' : 'Enable'}
        </button>
        <span style={{ fontSize: '0.75rem', color: '#71717a' }}>Eligibility: $10K+ weighted wager. 10% of deposit losses, capped at $2,000 per claim, one claim per leaderboard period (submitted 17th–22nd). Crosscheck the Shuffle PNL before approving.</span>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <button style={viewBtn(status === 'pending')} onClick={() => setStatus('pending')}>⏳ Pending</button>
        <button style={viewBtn(status === 'approved')} onClick={() => setStatus('approved')}>✅ Approved</button>
        <button style={viewBtn(status === 'denied')} onClick={() => setStatus('denied')}>❌ Denied</button>
      </div>

      {claims.length === 0 ? (
        <p className="admin-no-data">{status === 'pending' ? 'No claims awaiting review 🎉' : 'Nothing here.'}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {claims.map(claim => (
            <div key={claim.id} style={{ padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap' }}>
                {claim.profile_image_url && <img src={claim.profile_image_url} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {claim.display_name || claim.username}
                    <span style={{ color: '#71717a', fontWeight: 400 }}> · payout to: </span>
                    <span style={{ color: '#c4b5fd' }}>{claim.shuffle_username}</span>
                    {claim.payout_amount && <span style={{ color: '#4ade80' }}> · paid {fmtMoney(claim.payout_amount)}</span>}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '0.2rem' }}>
                    Weighted wager: <strong style={{ color: '#fff' }}>{fmtMoney(claim.weighted_wager)}</strong>
                    {' '}· period {claim.period_label} · submitted {new Date(claim.created_at).toLocaleDateString()}
                    {claim.admin_by && <> · reviewed by {claim.admin_by}</>}
                  </div>
                  {claim.admin_response && <div style={{ fontSize: '0.75rem', color: '#71717a', marginTop: '0.2rem' }}>Response: {claim.admin_response}</div>}
                </div>
                <button onClick={() => viewScreenshot(claim.id)} style={smallBtn('rgba(168,85,247,0.12)', 'rgba(168,85,247,0.35)', '#c4b5fd')}>
                  {shot.claimId === claim.id ? 'Hide PNL' : '🖼️ View PNL'}
                </button>
                <button onClick={() => loadRewards(claim)} style={smallBtn('rgba(34,197,94,0.1)', 'rgba(34,197,94,0.3)', '#4ade80')} title="Rewards this user already received in this period">
                  {rewards.claimId === claim.id ? 'Hide rewards' : '🧾 Rewards'}
                </button>
                {status === 'pending' ? (
                  <>
                    <button onClick={() => approve(claim.id)} style={smallBtn('rgba(34,197,94,0.12)', 'rgba(34,197,94,0.35)', '#4ade80')}>✅ Approve</button>
                    <button onClick={() => deny(claim.id)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>❌ Deny</button>
                  </>
                ) : (
                  <button onClick={() => del(claim.id)} style={smallBtn('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.35)', '#fca5a5')}>🗑️ Delete</button>
                )}
              </div>
              {/* Rewards already received in this claim's period — subtract from the payout */}
              {rewards.claimId === claim.id && (
                <div style={{ marginTop: '0.7rem', padding: '0.7rem 0.9rem', background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '10px' }}>
                  {rewards.loading ? (
                    <p style={{ color: '#71717a', fontSize: '0.8rem', margin: 0 }}>Loading rewards…</p>
                  ) : rewards.data ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.76rem', fontWeight: 800, color: '#4ade80' }}>
                          🎁 Giveaways received this period: {fmtMoney(rewards.data.totals.window)}
                        </span>
                        <span style={{ fontSize: '0.68rem', color: '#8b8b96' }}>all-time {fmtMoney(rewards.data.totals.allTime)}</span>
                      </div>
                      {rewards.data.rewards.length === 0 ? (
                        <p style={{ fontSize: '0.72rem', color: '#71717a', margin: 0 }}>No giveaways in this period — full lossback applies.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          {rewards.data.rewards.slice(0, 12).map((r, i) => (
                            <div key={i} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.7rem', color: r.counts ? '#d4d4d8' : '#71717a', opacity: r.counts ? 1 : 0.6 }}>
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.label}{r.detail ? ` — ${r.detail}` : ''}
                              </span>
                              <span style={{ color: '#8b8b96', flexShrink: 0 }}>{r.duplicate ? 'duplicate' : r.status}</span>
                              <strong style={{ color: r.counts ? '#4ade80' : '#71717a', flexShrink: 0, minWidth: '62px', textAlign: 'right' }}>{fmtMoney(r.amount)}</strong>
                            </div>
                          ))}
                          {rewards.data.rewards.length > 12 && (
                            <span style={{ fontSize: '0.66rem', color: '#71717a' }}>+{rewards.data.rewards.length - 12} more — see the Rewards Ledger tab for everything</span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0 }}>Couldn't load rewards.</p>
                  )}
                </div>
              )}
              {shot.claimId === claim.id && (
                <div style={{ marginTop: '0.7rem', textAlign: 'center' }}>
                  {shot.loading ? (
                    <p style={{ color: '#71717a', fontSize: '0.8rem' }}>Loading screenshot…</p>
                  ) : shot.data ? (
                    <img src={shot.data} alt="PNL screenshot" style={{ maxWidth: '100%', maxHeight: '480px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)' }} />
                  ) : (
                    <p style={{ color: '#fca5a5', fontSize: '0.8rem' }}>No screenshot found for this claim.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ShuffleLeaderboard() {
  // URL routing - map paths to pages
  const getPageFromPath = () => {
    const path = window.location.pathname.toLowerCase();
    if (path === '/rewards' || path === '/wager-rewards' || path === '/affiliate' || path === '/affiliate-rewards' || path === '/exclusive-rewards') return 'rewards';
    if (path === '/bonus-hunts' || path === '/bonushunts' || path === '/hunts') return 'bonushunts';
    if (path === '/predictions' || path === '/predict' || path === '/guess') return 'predictions';
    if (path === '/prediction-results' || path === '/predictionresults' || path === '/results') return 'predictionresults';
    if (path === '/my-predictions' || path === '/mypredictions') return 'mypredictions';
    if (path === '/profile' || path === '/my-profile' || path === '/myprofile') return 'profile';
    if (path === '/sessions' || path === '/calendar' || path === '/session-calendar') return 'sessions';
    if (path === '/highlights') return 'highlights';
    if (path === '/slot-picks' || path === '/slotpicks' || path === '/slot-picks-queue' || path === '/queue') return 'slotpicks';
    if (path === '/tournament' || path === '/bracket') return 'tournament';
    if (path === '/challenges' || path === '/challenge') return 'challenges';
    if (path === '/community-hunt' || path === '/communityhunt' || path === '/communityhunts') return 'communityhunt';
    if (path === '/raffle-hunts' || path === '/raffle-hunt' || path === '/rafflehunts') return { page: 'communityhunt', huntTab: 'raffle' };
    if (path === '/community-hunts') return { page: 'communityhunt', huntTab: 'community' };
    if (path === '/points' || path === '/points-shop' || path === '/shop') return 'pointsshop';
    if (path === '/spin-zone' || path === '/spinzone') return 'gauntlet';
    if (path === '/slot-picker' || path === '/slotpicker' || path === '/slots') return { page: 'gauntlet', game: 'slots' };
    // /rejuicer still resolves for anyone holding an old link; /gauntlet now
    // lands on the slot picker since Rejuicer is parked.
    if (path === '/rejuicer') return { page: 'gauntlet', game: 'rejuicer' };
    if (path === '/gauntlet') return { page: 'gauntlet', game: 'slots' };
    if (path === '/raffle' || path === '/raffle-lottery' || path === '/lottery') return 'raffle';
    if (path === '/viewer-hunt' || path === '/viewerhunt' || path === '/vh') return 'viewerhunt';
    if (path === '/chat-giveaway' || path === '/giveaway' || path === '/gw' || path === '/chatgiveaway') return 'chatgiveaway';
    if (path === '/vip' || path === '/vip-dashboard' || path === '/vip-status') return 'vipdashboard';
    if (path === '/weekly' || path === '/weekly-leaderboard') return { page: 'leaderboard', leaderboardMode: 'weekly' };
    if (path === '/signup' || path === '/sign-up' || path === '/join') return 'signup';
    if (path === '/claim' || path === '/claim-win' || path === '/claimwin') return 'claim';
    if (path === '/admin') return 'admin';
    if (path === '/leaderboard' || path === '/lb' || path === '/monthly') return 'leaderboard';
    if (path === '/referrals' || path === '/recruit' || path === '/recruit-and-earn') return 'referrals';
    if (path === '/lossback' || path === '/loss-back' || path === '/cashback') return 'lossback';
    if (path === '/clip-and-earn' || path === '/clips' || path === '/clipearn') return 'clipearn';
    if (path === '/ban-list' || path === '/banlist' || path === '/banned') return 'banlist';
    if (path === '/calculators' || path === '/calculator' || path === '/calc') return 'calculators';
    if (path === '/slot-battles' || path === '/battles' || path === '/battle') return 'battles';
    if (path === '/widget/battles') return 'battlewidget';
    if (path === '/legal' || path === '/terms' || path === '/privacy' || path === '/responsible-gambling' || path === '/rewards-terms') return 'legal';
    if (path === '/' || path === '') return 'home';
    // Anything else is a real 404 — better than silently showing the landing
    // page for a mistyped link. (Auth redirects land on known paths with query
    // params, so they are unaffected.)
    return 'notfound';
  };

  // Helper function for relative time formatting
  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const initialPage = getPageFromPath();
  const [leaderboard, setLeaderboard] = useState([]);
  const [activePlayerCount, setActivePlayerCount] = useState(null); // totalParticipants from /api/leaderboard
  const [featuredPosts, setFeaturedPosts] = useState([]); // admin-curated social posts for the landing page
  const [featureFlags, setFeatureFlags] = useState({ lossback: false, referrals: false, clips: false, weekly: true });
  const [weeklyLbAdminEnabled, setWeeklyLbAdminEnabled] = useState(true); // admin view of the weekly leaderboard feature flag
  const [weeklyLbAdminBusy, setWeeklyLbAdminBusy] = useState(false);
  const [adminPendingCounts, setAdminPendingCounts] = useState(null); // pending review counts across claim systems

  // First-visit welcome modal (logged-out users, once per browser)
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Newcomer checklist state — fetched on login, cached here
  const [checklistData, setChecklistData] = useState(null); // { checklist, pendingSteps, shuffleLinked, shuffleUnderGuild, discordLinked, discordUsername, discordInGuild }
  const [showChecklistPanel, setShowChecklistPanel] = useState(false);
  const [checklistCelebrate, setChecklistCelebrate] = useState(false);
  const [checklistBusyStep, setChecklistBusyStep] = useState(null);
  const [checklistInputs, setChecklistInputs] = useState({});
  const [checklistMsg, setChecklistMsg] = useState(null); // { step, type: 'error'|'info', text }
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activePage, setActivePage] = useState(typeof initialPage === 'object' ? initialPage.page : initialPage);
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelResult, setWheelResult] = useState(null);
  const [wheelRotation, setWheelRotation] = useState(-10000);
  const [isResetting, setIsResetting] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth > 900);
  const [apiStatus, setApiStatus] = useState('idle');
  const [cacheTime, setCacheTime] = useState(null);
  const [leaderboardStale, setLeaderboardStale] = useState(false);
  // Weighted era is permanent since Apr 24 2026 — default true so the rewards page
  // never flashes the old halved tier amounts before /api/leaderboard confirms it.
  const [isWeightedPeriod, setIsWeightedPeriod] = useState(true);
  const [leaderboardMode, setLeaderboardMode] = useState(typeof initialPage === 'object' && initialPage.leaderboardMode ? initialPage.leaderboardMode : 'monthly');
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [weeklyInfo, setWeeklyInfo] = useState(null);
  const [weeklyCountdown, setWeeklyCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [raffleLottery, setRaffleLottery] = useState(null);
  const [raffleResult, setRaffleResult] = useState(null);
  const [raffleRolling, setRaffleRolling] = useState(false);
  const [raffleRollNames, setRaffleRollNames] = useState([]);
  const [raffleParticipants, setRaffleParticipants] = useState(null);
  const [raffleDrawingModal, setRaffleDrawingModal] = useState(false);
  const [raffleReelWinner, setRaffleReelWinner] = useState(null);
  const [raffleLotteryHistory, setRaffleLotteryHistory] = useState(null);
  const [raffleDrawPeriod, setRaffleDrawPeriod] = useState(null); // auto-detected from backend
  const [rafflePreviousAvailable, setRafflePreviousAvailable] = useState(null); // null = loading, true/false
  const [raffleManualSearch, setRaffleManualSearch] = useState('');
  const [raffleSearchResults, setRaffleSearchResults] = useState([]);
  const [raffleSearching, setRaffleSearching] = useState(false);
  const fetchIntervalRef = useRef(null);
  const gridBgRef = useRef(null);

  // Branded splash loader: 'visible' → 'fading' → 'done'. Continues the static
  // splash from index.html so first paint never shows half-loaded UI.
  // Skipped entirely on repeat loads this session and under reduced motion.
  const [appLoaderState, setAppLoaderState] = useState(() => {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'done';
      if (sessionStorage.getItem('snutzy_splash_shown')) return 'done';
    } catch (e) { /* private mode etc. — just show the splash */ }
    return 'visible';
  });

  // Twitch live status
  const [isStreamerLive, setIsStreamerLive] = useState(false);

  // Auth state
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false); // limited admin: hunts, predictions quick action, featured posts
  const [authLoading, setAuthLoading] = useState(true);

  // Prediction game state
  const [predictionData, setPredictionData] = useState({
    activeHunt: null,
    predictionsOpen: false,
    myPrediction: null,
    myPredictions: [], // All predictions for current hunt (multiple slots)
    availableCharges: 0, // Extra prediction charges available
    allPredictions: [],
    predictionCount: 0,
    rewards: { first: '', second: '', third: '' }
  });
  // 'loading' | 'loaded' | 'error' — page-level load state for prediction data.
  // 'error' only fires after all fetch retries fail AND we've never had good data;
  // once loaded, stale data keeps showing while background refreshes happen.
  const [predictionLoadState, setPredictionLoadState] = useState('loading');
  const predictionEverLoadedRef = useRef(false);
  const predictionFetchBusyRef = useRef(false); // poller guard: never stack retry chains
  const [predictionInput, setPredictionInput] = useState('');
  const [predictionInputs, setPredictionInputs] = useState({}); // {slot: value} for multiple slots
  const [activeSlot, setActiveSlot] = useState(1); // Currently editing slot
  const [predictionSubmitting, setPredictionSubmitting] = useState(false);
  const [predictionMessage, setPredictionMessage] = useState(null);
  const [currentPredictionsPage, setCurrentPredictionsPage] = useState(1);
  const [predictionsSortOrder, setPredictionsSortOrder] = useState('newest'); // 'newest', 'highest', 'lowest'
  const PREDICTIONS_PER_PAGE = 20;
  
  // Last completed hunt results
  const [lastHuntResults, setLastHuntResults] = useState({
    hunt: null,
    predictions: [],
    winners: []
  });
  
  // User's prediction history
  const [myPredictionHistory, setMyPredictionHistory] = useState([]);

  // Previous winners state
  const [previousWinners, setPreviousWinners] = useState([]);
  const [previousWinnersPage, setPreviousWinnersPage] = useState(1);
  const previousWinnersPerPage = 10;

  // Admin panel state
  const [adminData, setAdminData] = useState({
    stats: null,
    hunts: [],
    users: [],
    usersTotal: 0,
    selectedHunt: null,
    huntPredictions: [],
    selectedHuntWinners: null,
    huntWinners: [],
    pendingClaims: [],
    paidClaims: [],
    pendingGiveaways: [],
    paidGiveaways: [],
    allGiveaways: [],
    unclaimedWins: [],
    slotPicks: [],
    slotPicksStats: { pending: 0, completed: 0, paid: 0, refundPending: 0, refunded: 0, totalPaidAmount: 0 },
    transferRequests: [],
    selectedUser: null,
    selectedUserDetails: null
  });
  const [adminTab, setAdminTab] = useState('dashboard'); // dashboard, hunts, rewards, paidRewards, giveaways, paidGiveaways, unclaimedWins, slotPicks, slotPicksRefunds, transfers, analytics, users, points

  // "Amount given away" summary bar (paid predictions / giveaways / point-shop tabs)
  const [adminPayoutRange, setAdminPayoutRange] = useState('30d'); // 30d | period | all
  const [adminPayoutSummary, setAdminPayoutSummary] = useState(null);
  const [adminPayoutLoading, setAdminPayoutLoading] = useState(false);
  const fetchPayoutSummary = useCallback(async (range) => {
    setAdminPayoutLoading(true);
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/admin/payout-summary?range=${range}`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) setAdminPayoutSummary(await res.json());
    } catch (e) {
      console.error('Failed to fetch payout summary:', e);
    }
    setAdminPayoutLoading(false);
  }, []);
  const onPayoutRange = useCallback((r) => { setAdminPayoutRange(r); fetchPayoutSummary(r); }, [fetchPayoutSummary]);

  // Admin user-lookup: Shuffle wager + giveaway stats (loaded with the detail modal)
  const [adminUserWagerStats, setAdminUserWagerStats] = useState(null);
  const [adminUserWagerLoading, setAdminUserWagerLoading] = useState(false);
  const [adminWagerWarming, setAdminWagerWarming] = useState(false); // force-refreshing 24h/7d

  // Admin points system state
  const [adminPointsConfig, setAdminPointsConfig] = useState({});
  const [adminPointsStats, setAdminPointsStats] = useState(null);
  const [adminShopItems, setAdminShopItems] = useState([]);
  const [adminPointsRedemptions, setAdminPointsRedemptions] = useState([]);
  const [adminPointsRedemptionsPagination, setAdminPointsRedemptionsPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [adminPointsRedemptionsFilter, setAdminPointsRedemptionsFilter] = useState('pending');
  const [adminPointsSubTab, setAdminPointsSubTab] = useState('overview'); // overview, config, shop, redemptions, grant, payouts, reconcile
  const [redemptionPayoutsData, setRedemptionPayoutsData] = useState(null);
  const fetchRedemptionPayouts = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/admin/points/redemption-history`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setRedemptionPayoutsData(await r.json());
    } catch (e) { console.error('Payouts fetch error:', e); }
  }, []);
  const [wagerReconciliation, setWagerReconciliation] = useState(null);
  const [reconcilingUserId, setReconcilingUserId] = useState(null);
  const [showAddShopItemModal, setShowAddShopItemModal] = useState(false);
  const [editingShopItem, setEditingShopItem] = useState(null);
  const [newShopItem, setNewShopItem] = useState({ name: '', description: '', cost: '', cashValue: '', imageUrl: '', iconKey: 'custom', stock: '', maxPerUser: '', requiresShuffleUsername: false, requiresDiscordUsername: false, fulfillmentNotes: '', category: 'custom', sortOrder: '0' });
  const [adminGrantForm, setAdminGrantForm] = useState({ twitchUsername: '', amount: '', reason: '' });
  const [adminPointsMessage, setAdminPointsMessage] = useState(null);
  const [adminPointsLoading, setAdminPointsLoading] = useState(false);

  const resetAllPoints = async () => {
    const token = getAuthToken();
    const confirmText = window.prompt('This will DELETE all points, transactions, redemptions, and wager snapshots.\n\nType RESET to confirm:');
    if (confirmText !== 'RESET') return;
    setAdminPointsLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ confirm: 'RESET_ALL_POINTS' })
      });
      const data = await response.json();
      if (response.ok) {
        setAdminPointsMessage({ type: 'success', text: `Reset complete! Deleted: ${data.deleted.transactions} transactions, ${data.deleted.wagerSnapshots} snapshots, ${data.deleted.redemptions} redemptions` });
        fetchAdminPointsStats();
      } else {
        setAdminPointsMessage({ type: 'error', text: data.error });
      }
    } catch (e) {
      setAdminPointsMessage({ type: 'error', text: 'Network error' });
    } finally { setAdminPointsLoading(false); }
  };

  const [pointsDiagnostics, setPointsDiagnostics] = useState(null);
  const fetchPointsDiagnostics = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/diagnostics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPointsDiagnostics(data);
      }
    } catch (e) {
      console.error('Diagnostics error:', e);
    }
  };
  const [adminUserSearch, setAdminUserSearch] = useState('');
  const [adminGiveawaySearch, setAdminGiveawaySearch] = useState('');
  const [adminPaidRewardsSearch, setAdminPaidRewardsSearch] = useState('');
  const [adminHuntsPage, setAdminHuntsPage] = useState(1);
  const [adminPaidRewardsPage, setAdminPaidRewardsPage] = useState(1);
  const [adminPaidGiveawaysPage, setAdminPaidGiveawaysPage] = useState(1);
  const [adminSlotPicksPage, setAdminSlotPicksPage] = useState(1);
  const [adminSlotPicksFilter, setAdminSlotPicksFilter] = useState('pending'); // 'pending', 'completed', 'paid'
  const [adminSlotPicksPagination, setAdminSlotPicksPagination] = useState({ total: 0, totalPages: 1 });
  const adminItemsPerPage = 10;
  const [adminEndBalanceInput, setAdminEndBalanceInput] = useState('');
  const [adminRewards, setAdminRewards] = useState({
    first: '$50 Shuffle Balance',
    second: '$30 Shuffle Balance', 
    third: '$20 Shuffle Balance'
  });

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(0);
  const [analyticsPage, setAnalyticsPage] = useState(1);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsMode, setAnalyticsMode] = useState('periods'); // 'periods', 'quick', 'custom'
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareFilter, setCompareFilter] = useState('all'); // all, new, gone, returning
  const [leaderboardSnapshots, setLeaderboardSnapshots] = useState([]);
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState('');
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState('');

  // Slot pick pay modal state
  const [showSlotPickPayModal, setShowSlotPickPayModal] = useState(false);
  const [selectedSlotPickToPay, setSelectedSlotPickToPay] = useState(null);
  const [slotPickPayAmount, setSlotPickPayAmount] = useState('');

  // Winner notification state
  const [myWins, setMyWins] = useState([]);
  const [showWinModal, setShowWinModal] = useState(false);
  const [selectedWinToClaim, setSelectedWinToClaim] = useState(null);
  const [claimShuffleUsername, setClaimShuffleUsername] = useState('');

  // Public slot picks queue state
  const [publicSlotPicks, setPublicSlotPicks] = useState([]);
  const [publicSlotPicksStats, setPublicSlotPicksStats] = useState({ pending: 0, completed: 0, paid: 0 });
  const [slotPicksSessionMax, setSlotPicksSessionMax] = useState(0); // Tracks highest pending count this session

  // Profile page state
  const [profileStats, setProfileStats] = useState(null);
  const [userWagerStats, setUserWagerStats] = useState(() => {
    try { const cached = localStorage.getItem('wagerStats'); return cached ? JSON.parse(cached) : null; } catch { return null; }
  });
  const [userWagerClaims, setUserWagerClaims] = useState([]);
  const [adminWagerClaims, setAdminWagerClaims] = useState([]);
  const [adminLeaderboardTotal, setAdminLeaderboardTotal] = useState('');
  const [adminWagerBulkAdjustment, setAdminWagerBulkAdjustment] = useState('');
  const [adminGiveawaysBulk, setAdminGiveawaysBulk] = useState('');
  const [adminPredictionsBulk, setAdminPredictionsBulk] = useState('');
  const [adminSlotPicksBulk, setAdminSlotPicksBulk] = useState('');
  const [fraudData, setFraudData] = useState(null);
  const [fraudUserDetail, setFraudUserDetail] = useState(null);
  const [fraudSearch, setFraudSearch] = useState('');
  const [fraudSearchResults, setFraudSearchResults] = useState([]);
  const [fraudHideAcked, setFraudHideAcked] = useState(true);
  const [fraudRescanning, setFraudRescanning] = useState(false);
  const [fraudView, setFraudView] = useState('overview');
  const [shadowbans, setShadowbans] = useState([]);
  const [shadowbanInput, setShadowbanInput] = useState('');
  const [shadowbanReason, setShadowbanReason] = useState('');
  const [giveawayAnalysis, setGiveawayAnalysis] = useState(null);
  const [giveawayAnalysisLoading, setGiveawayAnalysisLoading] = useState(false);
  const [vipMonitorData, setVipMonitorData] = useState(null);
  const [viewerHuntData, setViewerHuntData] = useState(null);
  const [viewerHuntHistory, setViewerHuntHistory] = useState(null);
  const [showVhHistory, setShowVhHistory] = useState(false);
  const fetchViewerHuntHistory = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/viewer-hunt/history?limit=15`);
      if (r.ok) setViewerHuntHistory(await r.json());
    } catch(e) {}
  }, []);
  const [drawingCard, setDrawingCard] = useState(false);
  const [revealedCard, setRevealedCard] = useState(null);
  
  const fetchViewerHunt = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/viewer-hunt/current`);
      if (r.ok) setViewerHuntData(await r.json());
    } catch(e) {}
  }, []);
  
  useEffect(() => {
    if (activePage !== 'viewerhunt') return;
    fetchViewerHunt();
    const i = setInterval(fetchViewerHunt, 3000);
    return () => clearInterval(i);
  }, [activePage, fetchViewerHunt]);
  
  const vhAdminAction = async (path, body) => {
    const token = getAuthToken();
    try {
      const r = await fetch(`${BACKEND_API_URL}${path}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
      const data = await r.json();
      if (!r.ok) { alert(data.error || 'Action failed'); return null; }
      fetchViewerHunt();
      return data;
    } catch(e) { alert('Network error'); return null; }
  };

  // Bonus pick admin controls
  const [bonusPickInput, setBonusPickInput] = useState('');
  const grantBonusPick = async (sessionId) => {
    const username = bonusPickInput.trim().replace(/^@/, '');
    if (!username) return;
    const r = await vhAdminAction(`/api/admin/viewer-hunt/${sessionId}/bonus-pick`, { username });
    if (r?.success) setBonusPickInput('');
  };
  const revokeBonusPick = async (sessionId, username) => {
    if (!confirm(`Revoke one bonus pick from ${username}?`)) return;
    await vhAdminAction(`/api/admin/viewer-hunt/${sessionId}/bonus-pick/revoke`, { username });
  };

  // ============================================
  // CHAT GIVEAWAY state + helpers
  // ============================================
  const [cgData, setCgData] = useState(null);
  const [cgHistory, setCgHistory] = useState(null);
  const [cgShowHistory, setCgShowHistory] = useState(false);
  const [cgDrawing, setCgDrawing] = useState(false); // slot-reel animation flag
  const [cgReelWinnerId, setCgReelWinnerId] = useState(null); // winner ID landing on reel
  const [cgReelWinnerData, setCgReelWinnerData] = useState(null); // full winner obj from draw response (avoids race with fetchCg)
  const [cgSelectedWinnerId, setCgSelectedWinnerId] = useState(null); // detail panel
  const [cgWinnerDetails, setCgWinnerDetails] = useState(null);
  const [cgWinnersHistory, setCgWinnersHistory] = useState(null);
  const [cgWinnersPage, setCgWinnersPage] = useState(1);
  const [cgDiagnostics, setCgDiagnostics] = useState(null);

  const fetchCgDiagnostics = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/admin/chat-giveaway/diagnostics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) setCgDiagnostics(await r.json());
    } catch (e) {}
  }, []);

  // True only after all retries fail with nothing loaded yet — the 3s poller
  // keeps retrying in the background, so this doubles as a "retrying…" banner flag.
  const [cgLoadFailed, setCgLoadFailed] = useState(false);
  const cgFetchBusyRef = useRef(false); // poller guard: never stack retry chains

  const fetchCg = useCallback(async () => {
    if (cgFetchBusyRef.current) return; // previous chain (incl. retries) still running
    cgFetchBusyRef.current = true;
    try {
      const r = await fetchWithRetry(`${BACKEND_API_URL}/api/chat-giveaway/current`, {}, { key: 'cg-current' });
      if (r.ok) {
        setCgData(await r.json());
        setCgLoadFailed(false);
      }
    } catch (e) {
      if (!e?.superseded) setCgLoadFailed(true);
    } finally {
      cgFetchBusyRef.current = false;
    }
  }, []);

  const fetchCgHistory = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/chat-giveaway/history?limit=20`);
      if (r.ok) setCgHistory(await r.json());
    } catch (e) {}
  }, []);

  const fetchCgWinnersHistory = useCallback(async (page = 1) => {
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/chat-giveaway/winners?page=${page}&limit=10`);
      if (r.ok) setCgWinnersHistory(await r.json());
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (activePage !== 'chatgiveaway') return;
    fetchCg();
    fetchCgWinnersHistory(cgWinnersPage);
    if (isAdmin) fetchCgDiagnostics();
    const i = setInterval(fetchCg, 3000);
    // Winners history changes rarely (another admin marking paid / setting a prize),
    // so a slower cadence is enough; visibilitychange covers the tab-back case
    // instantly since browsers throttle intervals in background tabs.
    const hist = setInterval(() => fetchCgWinnersHistory(cgWinnersPage), 10000);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      fetchCg();
      fetchCgWinnersHistory(cgWinnersPage);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(i); clearInterval(hist); document.removeEventListener('visibilitychange', onVisible); };
  }, [activePage, fetchCg, fetchCgWinnersHistory, cgWinnersPage, fetchCgDiagnostics, isAdmin]);

  const cgAdminAction = async (path, body, method = 'POST') => {
    const token = getAuthToken();
    try {
      const r = await fetch(`${BACKEND_API_URL}${path}`, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { alert(data.error || 'Action failed'); return null; }
      fetchCg();
      return data;
    } catch (e) { alert('Network error'); return null; }
  };

  // Draw with slot-reel animation. Flow:
  //   1. Fire API immediately + schedule sound
  //   2. Wait for the winner response
  //   3. Set winner data — reel sees this and runs ONE smooth 4s animation into position
  //   4. After 4.5s total (animation + short linger), swap modal to winner panel
  // Doing the API first avoids the two-phase animation snap where the reel would
  // spin to a "pre-settle" position and then jump to the final landing spot.
  const cgDrawWinner = async (sessionId) => {
    if (cgDrawing) return;
    setCgDrawing(true);
    setCgReelWinnerId(null);
    setCgReelWinnerData(null);

    // CS:GO case-opening style sound scheduled against the reel animation timing.
    // Ticks start at t≈0 of animation and decelerate over ~3.7s; thunk lands at 4.0s.
    const scheduleSound = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const masterGain = ctx.createGain();
        masterGain.gain.value = 0.7;
        masterGain.connect(ctx.destination);
        const playTick = (t, pitchShift = 0) => {
          const osc = ctx.createOscillator();
          const filter = ctx.createBiquadFilter();
          const gain = ctx.createGain();
          osc.type = 'square';
          osc.frequency.setValueAtTime(1800 + pitchShift + Math.random() * 200, t);
          filter.type = 'bandpass';
          filter.frequency.value = 2200;
          filter.Q.value = 6;
          osc.connect(filter); filter.connect(gain); gain.connect(masterGain);
          gain.gain.setValueAtTime(0, t);
          gain.gain.linearRampToValueAtTime(0.12, t + 0.002);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
          osc.start(t); osc.stop(t + 0.06);
        };
        const playThunk = (t) => {
          const osc = ctx.createOscillator();
          const oscGain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(160, t);
          osc.frequency.exponentialRampToValueAtTime(55, t + 0.3);
          osc.connect(oscGain); oscGain.connect(masterGain);
          oscGain.gain.setValueAtTime(0, t);
          oscGain.gain.linearRampToValueAtTime(0.35, t + 0.01);
          oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
          osc.start(t); osc.stop(t + 0.5);
          const click = ctx.createOscillator();
          const clickGain = ctx.createGain();
          const clickFilter = ctx.createBiquadFilter();
          click.type = 'triangle';
          click.frequency.value = 900;
          clickFilter.type = 'lowpass';
          clickFilter.frequency.value = 1500;
          click.connect(clickFilter); clickFilter.connect(clickGain); clickGain.connect(masterGain);
          clickGain.gain.setValueAtTime(0, t);
          clickGain.gain.linearRampToValueAtTime(0.18, t + 0.005);
          clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
          click.start(t); click.stop(t + 0.1);
        };
        let offset = 0.03;
        let interval = 0.03;
        const now = ctx.currentTime;
        let tickCount = 0;
        while (offset < 3.7) {
          const pitch = -tickCount * 15 + (Math.random() * 60 - 30);
          playTick(now + offset, pitch);
          interval *= 1.12;
          offset += interval;
          tickCount++;
        }
        playThunk(now + 4.0);
      } catch (e) { /* audio not available */ }
    };

    const r = await cgAdminAction(`/api/admin/chat-giveaway/${sessionId}/draw`);
    if (!r?.success) { setCgDrawing(false); return; }

    // Start sound + reel animation now that we know the winner. Both align perfectly.
    scheduleSound();
    setCgReelWinnerData(r.winner);
    setCgReelWinnerId(r.winner.id);

    // 4s animation + 500ms linger on the winner before swapping to details panel
    setTimeout(() => {
      setCgDrawing(false);
      setCgSelectedWinnerId(r.winner.id);
      fetchCgWinnerDetails(r.winner.id);
    }, 4500);
  };

  const fetchCgWinnerDetails = useCallback(async (winnerId) => {
    const token = getAuthToken();
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/admin/chat-giveaway/winner/${winnerId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) setCgWinnerDetails(await r.json());
    } catch (e) {}
  }, []);

  // Poll winner's live chat messages + status fields (paid/prize/invalidated) while
  // detail panel is open, so changes made by another admin show up in an open modal.
  useEffect(() => {
    if (!cgSelectedWinnerId || activePage !== 'chatgiveaway') return;
    const token = getAuthToken();
    const poll = async () => {
      try {
        const r = await fetch(`${BACKEND_API_URL}/api/admin/chat-giveaway/winner/${cgSelectedWinnerId}/chat`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (r.ok) {
          const data = await r.json();
          setCgWinnerDetails(prev => prev ? {
            ...prev,
            chatMessages: data.messages,
            winner: data.winner ? { ...prev.winner, ...data.winner } : prev.winner
          } : prev);
        }
      } catch (e) {}
    };
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(poll, 2500);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [cgSelectedWinnerId, activePage]);

  // Format follow age e.g. "2 years, 3 months"
  const formatFollowAge = (followedAt) => {
    if (!followedAt) return null;
    const then = new Date(followedAt);
    const now = new Date();
    const ms = now - then;
    if (ms < 0) return 'just now';
    const days = Math.floor(ms / (24 * 3600 * 1000));
    if (days < 1) return 'today';
    if (days < 7) return `${days} day${days === 1 ? '' : 's'}`;
    if (days < 60) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'}`;
    const months = Math.floor(days / 30);
    if (months < 24) {
      const years = Math.floor(months / 12);
      const rem = months % 12;
      if (years < 1) return `${months} month${months === 1 ? '' : 's'}`;
      return rem > 0 ? `${years}y ${rem}mo` : `${years} year${years === 1 ? '' : 's'}`;
    }
    return `${Math.floor(months / 12)} years`;
  };

  
  const drawCard = async (sessionId) => {
    if (drawingCard) return;
    setDrawingCard(true);
    // Web Audio shuffle sound — 12 staccato square-wave riffles spread over 3 seconds
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      for (let i = 0; i < 12; i++) {
        const t = ctx.currentTime + i * 0.25;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 1200 + Math.random() * 800;
        o.type = 'square';
        o.frequency.setValueAtTime(180 + Math.random() * 220, t);
        o.connect(filter);
        filter.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.07, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.13);
        o.start(t);
        o.stop(t + 0.13);
      }
    } catch (e) { /* audio not available */ }
    // Shuffle for 3.5s for dramatic effect
    setTimeout(async () => {
      await vhAdminAction(`/api/admin/viewer-hunt/${sessionId}/draw`);
      setDrawingCard(false);
    }, 3500);
  };
  
  // Card flip / reveal sound — bright ascending chirp + soft thud
  const playCardFlipSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Ascending whoosh
      const o1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      o1.connect(g1);
      g1.connect(ctx.destination);
      o1.type = 'sine';
      o1.frequency.setValueAtTime(400, ctx.currentTime);
      o1.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.15);
      g1.gain.setValueAtTime(0.001, ctx.currentTime);
      g1.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.04);
      g1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      o1.start();
      o1.stop(ctx.currentTime + 0.25);
      // Sparkle ding on top
      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.connect(g2);
      g2.connect(ctx.destination);
      o2.type = 'triangle';
      o2.frequency.setValueAtTime(2400, ctx.currentTime + 0.12);
      g2.gain.setValueAtTime(0.001, ctx.currentTime + 0.12);
      g2.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.14);
      g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      o2.start(ctx.currentTime + 0.12);
      o2.stop(ctx.currentTime + 0.45);
    } catch (e) { /* audio not available */ }
  };
  const [vipFilter, setVipFilter] = useState('all');
  const [vipPeriod, setVipPeriod] = useState('0'); // leaderboard periods back: '0' = current, '1' = previous, ...
  const [vipLoading, setVipLoading] = useState(false);
  const [vipDetailUser, setVipDetailUser] = useState(null);
  const [fraudAckNote, setFraudAckNote] = useState('');
  
  // Claimable giveaways
  const [myClaimableGiveaways, setMyClaimableGiveaways] = useState([]);
  const [pendingShuffleGiveaways, setPendingShuffleGiveaways] = useState([]);
  const [claimableDiscordLinked, setClaimableDiscordLinked] = useState(null);
  const [claimableKickLinked, setClaimableKickLinked] = useState(null);
  const [claimableTwitchName, setClaimableTwitchName] = useState('');
  const [claimableDiscordActual, setClaimableDiscordActual] = useState(null);
  const [claimShuffleInputs, setClaimShuffleInputs] = useState({});
  const [claimPayoutMethod, setClaimPayoutMethod] = useState({}); // id -> 'shuffle' | 'crypto'
  const [claimCryptoInputs, setClaimCryptoInputs] = useState({}); // id -> { coin, address }
  const [claimingId, setClaimingId] = useState(null);
  const [adminClaimableGiveaways, setAdminClaimableGiveaways] = useState([]);
  const [adminClaimableCounts, setAdminClaimableCounts] = useState({});
  const [adminClaimableFilter, setAdminClaimableFilter] = useState('');
  const [newClaimableGiveaway, setNewClaimableGiveaway] = useState({ type: 'twitch', identifier: '', amount: '', description: '' });
  const [publicPayoutStats, setPublicPayoutStats] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Shuffle username linking state
  const [showShuffleModal, setShowShuffleModal] = useState(false);
  const [shuffleUsernameInput, setShuffleUsernameInput] = useState('');
  const [savingShuffleUsername, setSavingShuffleUsername] = useState(false);

  // Transfer request state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferRequest, setTransferRequest] = useState({
    shuffleUsername: '',
    discordUsername: '',
    confirmed: false
  });
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferMessage, setTransferMessage] = useState(null);

  // Tournament state
  const [tournamentData, setTournamentData] = useState({
    tournament: null,
    totalEntries: 0,
    entries: []
  });
  const [tournamentMyEntry, setTournamentMyEntry] = useState(null);
  const [tournamentEntering, setTournamentEntering] = useState(false);
  const [tournamentMessage, setTournamentMessage] = useState(null);
  const [tournamentHistory, setTournamentHistory] = useState([]);
  const [viewingPastTournament, setViewingPastTournament] = useState(null);
  const [pastTournamentBracket, setPastTournamentBracket] = useState(null);
  
  // Admin tournament management state
  const [adminTournaments, setAdminTournaments] = useState([]);
  const [showTournamentAdmin, setShowTournamentAdmin] = useState(false);
  const [tournamentForm, setTournamentForm] = useState({
    title: '', scheduledDate: '', prizePool: '1000', firstPrize: '500', secondPrize: '300', thirdPrize: '200', bonusBuyAmount: '150', bracketSize: '32', setActive: true
  });
  const [editingTournament, setEditingTournament] = useState(null);
  const [tournamentAdminMsg, setTournamentAdminMsg] = useState(null);
  
  // Bracket state
  const [bracketData, setBracketData] = useState({
    bracket: null,
    players: [],
    matches: []
  });
  const [bracketPicking, setBracketPicking] = useState(false);
  const [pickedPlayer, setPickedPlayer] = useState(null);
  const [showPickAnimation, setShowPickAnimation] = useState(false);
  const [bracketAdminTab, setBracketAdminTab] = useState('picker'); // 'picker', 'matches'
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchResult, setMatchResult] = useState({ player1Slot: '', player1Multi: '', player2Slot: '', player2Multi: '', winnerId: null });

  // Challenges state
  const [challenges, setChallenges] = useState([]);
  // Snutzy-exclusive challenges mirrored from Shuffle (see shuffleChallengesCycle)
  const [shuffleCh, setShuffleCh] = useState({ active: [], completed: [], stats: null, feed: null });
  const [chSort, setChSort] = useState('prize');
  const [chDonePage, setChDonePage] = useState(0);
  // Clips mirrored from @Snutzygg on X (see twitterHighlightsCycle)
  const [xHighlights, setXHighlights] = useState([]);
  // Sidebar group collapse, remembered across visits
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snutzy_nav_collapsed') || '{}'); } catch (e) { return {}; }
  });
  const toggleNavSection = useCallback((id) => {
    setNavCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('snutzy_nav_collapsed', JSON.stringify(next)); } catch (e) {}
      return next;
    });
  }, []);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [challengeSearch, setChallengeSearch] = useState('');
  const [challengeSort, setChallengeSort] = useState('reward-desc');
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [claimForm, setClaimForm] = useState({
    discordUsername: '',
    shuffleUsername: '',
    actualMultiplier: '',
    actualBet: '',
    confirmed: false
  });
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimMessage, setClaimMessage] = useState(null);
  // Admin challenge state
  const [showAddChallengeModal, setShowAddChallengeModal] = useState(false);
  const [showCompleteChallengeModal, setShowCompleteChallengeModal] = useState(false);
  const [challengeToComplete, setChallengeToComplete] = useState(null);
  const [newChallenge, setNewChallenge] = useState({
    slotName: '',
    slotImageUrl: '',
    multiplierTarget: '',
    minBet: '',
    prizeAmount: ''
  });
  const [completedByUsername, setCompletedByUsername] = useState('');
  const [challengeActionLoading, setChallengeActionLoading] = useState(false);
  const [challengeClaims, setChallengeClaims] = useState([]);
  const [showClaimsSection, setShowClaimsSection] = useState(false);

  // Community Hunt state
  const [communityHunt, setCommunityHunt] = useState({ hunt: null, participants: [] });

  // Points system state
  const [pointsBalance, setPointsBalance] = useState(null);
  const [pointsBreakdown, setPointsBreakdown] = useState([]);
  const [pointsShopItems, setPointsShopItems] = useState([]);
  const [pointsShopLoading, setPointsShopLoading] = useState(false);
  const [shopSearch, setShopSearch] = useState('');
  const [shopSort, setShopSort] = useState('default');
  const [pointsEarnConfig, setPointsEarnConfig] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [pointsHistoryPage, setPointsHistoryPage] = useState(1);
  const [pointsHistoryTotal, setPointsHistoryTotal] = useState(0);
  const [pointsLeaderboard, setPointsLeaderboard] = useState([]);
  const [pointsLbPage, setPointsLbPage] = useState(1);
  const [pointsLbTotalPages, setPointsLbTotalPages] = useState(1);
  const [myRedemptions, setMyRedemptions] = useState([]);
  const [pointsShopTab, setPointsShopTab] = useState('shop'); // 'shop', 'history', 'leaderboard'
  const [recentRedemptions, setRecentRedemptions] = useState({ redemptions: [], totalFulfilled: 0 });
  const [redeemingItemId, setRedeemingItemId] = useState(null);
  const [redeemMessage, setRedeemMessage] = useState(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedShopItem, setSelectedShopItem] = useState(null);
  const [redeemDiscordUsername, setRedeemDiscordUsername] = useState('');

  // Discord link state
  const [discordLinked, setDiscordLinked] = useState(null); // null=loading, true/false
  const [kickLinked, setKickLinked] = useState(null); // null=loading, true/false
  const [kickInfo, setKickInfo] = useState(null);
  const [discordInfo, setDiscordInfo] = useState(null);
  const [communityHuntHistory, setCommunityHuntHistory] = useState({ hunts: [], pagination: {} });
  const [communityHuntLoading, setCommunityHuntLoading] = useState(false);
  const [communityHuntHistoryPage, setCommunityHuntHistoryPage] = useState(1);
  // Admin community hunt state
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [newParticipant, setNewParticipant] = useState({ identifier: '', identifierType: 'twitch', equity: '', displayName: '' });
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [communityHuntAdminTab, setCommunityHuntAdminTab] = useState('participants');

  // Raffle hunt state
  const [raffleHunt, setRaffleHunt] = useState({ hunt: null, entries: [], allEntries: [] });
  const [raffleMyEntry, setRaffleMyEntry] = useState(null);
  const [raffleLossStreak, setRaffleLossStreak] = useState(0);
  const [raffleBoostMultiplier, setRaffleBoostMultiplier] = useState('1.00');
  const [raffleEligibility, setRaffleEligibility] = useState({ affiliate: false, vip: false, wagered: 0 });
  const [raffleHasShuffleUsername, setRaffleHasShuffleUsername] = useState(false);
  const [raffleLoading, setRaffleLoading] = useState(false);
  const [raffleHistory, setRaffleHistory] = useState({ hunts: [], pagination: {} });
  const [raffleHistoryPage, setRaffleHistoryPage] = useState(1);
  const [communityHuntTab, setCommunityHuntTab] = useState(typeof initialPage === 'object' && initialPage.huntTab ? initialPage.huntTab : 'raffle'); // 'raffle' or 'community'
  const [eligibilityWhitelist, setEligibilityWhitelist] = useState([]);

  // Filter and sort challenges
  const filteredChallenges = useMemo(() => {
    let filtered = challenges;
    
    // Search filter
    if (challengeSearch.trim()) {
      const search = challengeSearch.toLowerCase().trim();
      filtered = filtered.filter(c => c.slotName.toLowerCase().includes(search));
    }
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      switch (challengeSort) {
        case 'reward-desc':
          return b.prizeAmount - a.prizeAmount;
        case 'reward-asc':
          return a.prizeAmount - b.prizeAmount;
        case 'bet-desc':
          return b.minBet - a.minBet;
        case 'bet-asc':
          return a.minBet - b.minBet;
        case 'multi-desc':
          return b.multiplierTarget - a.multiplierTarget;
        case 'multi-asc':
          return a.multiplierTarget - b.multiplierTarget;
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [challenges, challengeSearch, challengeSort]);

  // Admin giveaway form
  const [newGiveaway, setNewGiveaway] = useState({
    recipientType: 'shuffle',
    shuffleUsername: '',
    cryptoAddress: '',
    amount: '',
    description: ''
  });

  // Bonus hunt data
  const [bonusHuntStats, setBonusHuntStats] = useState({
    biggestMulti: { slot: 'Loading...', multi: 0, hunt: null },
    hotSlot: { slot: 'Loading...', avgMulti: 0 },
    coldSlot: { slot: 'Loading...', avgMulti: 0 },
    hotSlots: [],
    coldSlots: [],
    huntHistory: [],
    activeHunt: null,
    total: 0
  });
  const [huntBoardOpen, setHuntBoardOpen] = useState(null);
  const [huntHistoryPage, setHuntHistoryPage] = useState(1);
  const HUNTS_PER_PAGE = 10;

  // Navigation function that updates URL
  const navigateTo = useCallback((page, subPage = null) => {
    const pathMap = {
      'home': '/',
      'leaderboard': '/leaderboard',
      'referrals': '/referrals',
      'lossback': '/lossback',
      'clipearn': '/clip-and-earn',
      'rewards': '/wager-rewards',
      'sessions': '/sessions',
      'bonushunts': '/bonus-hunts',
      'predictions': '/predictions',
      'predictionresults': '/prediction-results',
      'mypredictions': '/my-predictions',
      'profile': '/profile',
      'highlights': '/highlights',
      'slotpicks': '/slot-picks',
      'tournament': '/tournament',
      'challenges': '/challenges',
      'communityhunt': '/community-hunt',
      'pointsshop': '/points',
      'gauntlet': '/spin-zone',
      'signup': '/signup',
      'claim': '/claim',
      'raffle': '/raffle',
      'viewerhunt': '/viewer-hunt',
      'chatgiveaway': '/chat-giveaway',
      'vipdashboard': '/vip',
      'legal': '/legal',
      'banlist': '/ban-list',
      'calculators': '/calculators',
      'battles': '/slot-battles',
      'admin': '/admin'
    };
    
    let path = pathMap[page] || '/';
    if (page === 'gauntlet' && subPage === 'slots') path = '/slot-picker';
    if (page === 'gauntlet' && subPage === 'rejuicer') path = '/rejuicer';
    if (page === 'gauntlet' && subPage === 'chat') path = '/chat-picker';
    if (page === 'communityhunt' && subPage === 'raffle') path = '/raffle-hunts';
    if (page === 'communityhunt' && subPage === 'community') path = '/community-hunts';
    
    window.history.pushState({}, '', path);
    setActivePage(page);
    if (page === 'gauntlet') {
      setSpinZoneGame(subPage);
    }
    if (page === 'communityhunt' && subPage) {
      setCommunityHuntTab(subPage);
    }
  }, []);

  // Per-page browser-tab titles (also what history entries are named)
  useEffect(() => {
    const titles = {
      home: `Snutzy x Shuffle — ${monthlyPoolLabel()} Monthly Leaderboard & Rewards`,
      leaderboard: 'Leaderboard — Snutzy x Shuffle',
      rewards: 'Wager Rewards — Snutzy x Shuffle',
      sessions: 'Session Calendar — Snutzy x Shuffle',
      bonushunts: 'Bonus Hunts — Snutzy x Shuffle',
      predictions: 'Predictions — Snutzy x Shuffle',
      predictionresults: 'Prediction Results — Snutzy x Shuffle',
      mypredictions: 'My Predictions — Snutzy x Shuffle',
      profile: 'Profile — Snutzy x Shuffle',
      highlights: 'Highlights — Snutzy x Shuffle',
      slotpicks: 'Slot Picks — Snutzy x Shuffle',
      challenges: 'Challenges — Snutzy x Shuffle',
      communityhunt: 'Community Hunt — Snutzy x Shuffle',
      pointsshop: 'Points Shop — Snutzy x Shuffle',
      gauntlet: 'Spin Zone — Snutzy x Shuffle',
      signup: 'Sign Up — Snutzy x Shuffle',
      claim: 'Claim Giveaway — Snutzy x Shuffle',
      raffle: 'Raffle — Snutzy x Shuffle',
      viewerhunt: 'Viewer Bonus Hunt — Snutzy x Shuffle',
      chatgiveaway: 'Chat Giveaway — Snutzy x Shuffle',
      vipdashboard: 'VIP Dashboard — Snutzy x Shuffle',
      referrals: 'Recruit & Earn — Snutzy x Shuffle',
      lossback: '10% Lossback — Snutzy x Shuffle',
      clipearn: 'Clip & Earn — Snutzy x Shuffle',
      banlist: 'The Ban List — Snutzy x Shuffle',
      calculators: 'Calculators — Snutzy x Shuffle',
      battles: 'Slot Battles — Snutzy x Shuffle',
      battlewidget: 'Slot Battles Widget',
      admin: 'Admin — Snutzy x Shuffle',
      legal: 'Legal — Snutzy x Shuffle',
      notfound: 'Page Not Found — Snutzy x Shuffle'
    };
    document.title = titles[activePage] || 'Snutzy x Shuffle';
  }, [activePage]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const page = getPageFromPath();
      if (typeof page === 'object') {
        setActivePage(page.page);
        if (page.game) setSpinZoneGame(page.game);
        else setSpinZoneGame(null);
        if (page.huntTab) setCommunityHuntTab(page.huntTab);
        if (page.leaderboardMode) setLeaderboardMode(page.leaderboardMode);
        else setLeaderboardMode('monthly');
      } else {
        setActivePage(page);
        setSpinZoneGame(null);
        setLeaderboardMode('monthly');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Dismiss the splash once fonts are in (avoids font-swap flashes) and a
  // minimum display time has passed, then fade it out. Both promises resolve
  // unconditionally (font wait capped at 2.5s), so the splash can never trap
  // the user if something fails to load.
  useEffect(() => {
    setAppLoaderState(current => {
      if (current === 'done') return current; // skipped this load
      try { sessionStorage.setItem('snutzy_splash_shown', '1'); } catch (e) {}
      const minDelay = new Promise(resolve => setTimeout(resolve, 1000));
      const fontsReady = (document.fonts && document.fonts.ready)
        ? Promise.race([document.fonts.ready.catch(() => {}), new Promise(resolve => setTimeout(resolve, 2500))])
        : Promise.resolve();
      Promise.all([minDelay, fontsReady]).then(() => {
        setAppLoaderState('fading');
        setTimeout(() => setAppLoaderState('done'), 650);
      });
      return current;
    });
  }, []);

  // 3D background mesh — perspective-tilted grid that leans toward the cursor.
  // Direct DOM transform updates via rAF (no React state) with lerp smoothing.
  useEffect(() => {
    const el = gridBgRef.current;
    if (!el) return;
    let tx = 0, ty = 0, cx = 0, cy = 0, raf = null;
    const onMove = (e) => {
      tx = e.clientX / window.innerWidth - 0.5;
      ty = e.clientY / window.innerHeight - 0.5;
    };
    const loop = () => {
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      el.style.transform = `perspective(1100px) rotateX(${16 - cy * 14}deg) rotateY(${cx * 22}deg) translate3d(${cx * -55}px, ${cy * -45}px, 0) scale(1.25)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Set initial spin zone game from URL
  const initialSpinZoneGame = typeof initialPage === 'object' ? initialPage.game : null;

  // ============================================
  // BACKEND API CONFIGURATION
  // Set this to your deployed backend URL
  // ============================================
  const BACKEND_API_URL = 'https://api.snutzy.gg';

  // ============================================
  // BACKGROUND ART CONFIGURATION
  // Replace these URLs with your own images
  // Use PNG images with transparency for best results
  // ============================================
  const BG_ART_LEFT = ''; // e.g., 'https://i.postimg.cc/xxxxx/left-art.png'
  const BG_ART_RIGHT = ''; // e.g., 'https://i.postimg.cc/xxxxx/right-art.png'

  // $40K table until the Oct 24 2026 period starts, $50K after (see leaderboardPrizes.js)
  const prizes = getMonthlyPrizes();
  const poolLabel = monthlyPoolLabel();

  const weeklyPrizes = [
    { rank: 1, prize: 750 },
    { rank: 2, prize: 450 },
    { rank: 3, prize: 300 },
    { rank: 4, prize: 250 },
    { rank: 5, prize: 200 },
    { rank: 6, prize: 150 },
    { rank: 7, prize: 125 },
    { rank: 8, prize: 100 },
    { rank: 9, prize: 100 },
    { rank: 10, prize: 75 }
  ];

  // Wager reward tiers — requirements stay the same, rewards DOUBLE for weighted periods (except Eternal, which stays $10K).
  // Rewards also reset monthly starting from the first weighted period.
  // Prefer the per-user `isWeighted` flag (delivered in the same /api/user/wager-stats payload as the
  // wager numbers) so the tier amounts/unlocks can't race against the global isWeightedPeriod state,
  // which is hydrated separately from /api/leaderboard and defaults to false. When that global flag is
  // stale/false but the user is actually in a weighted period, the unlock math below would otherwise
  // fall back to all-time totalWagered and wrongly unlock every tier. Fall back to the global flag only
  // for logged-out visitors who have no userWagerStats.
  const effectiveWeighted = userWagerStats?.isWeighted ?? isWeightedPeriod;
  const wagerTiers = effectiveWeighted ? [
    { name: 'Wood', icon: '🌲', reward: 50, requirement: 25000, color: '#8B4513' },
    { name: 'Bronze', icon: '🥉', reward: 100, requirement: 50000, color: '#CD7F32' },
    { name: 'Silver', icon: '🥈', reward: 200, requirement: 100000, color: '#C0C0C0' },
    { name: 'Gold', icon: '🥇', reward: 400, requirement: 200000, color: '#FFD700' },
    { name: 'Emerald', icon: '✳️', reward: 1000, requirement: 500000, color: '#50C878' },
    { name: 'Diamond', icon: '💎', reward: 2000, requirement: 1000000, color: '#B9F2FF' },
    { name: 'Eternal', icon: '♾️', reward: 10000, requirement: 10000000, color: '#E6E6FA' }
  ] : [
    { name: 'Wood', icon: '🌲', reward: 25, requirement: 25000, color: '#8B4513' },
    { name: 'Bronze', icon: '🥉', reward: 50, requirement: 50000, color: '#CD7F32' },
    { name: 'Silver', icon: '🥈', reward: 100, requirement: 100000, color: '#C0C0C0' },
    { name: 'Gold', icon: '🥇', reward: 200, requirement: 200000, color: '#FFD700' },
    { name: 'Emerald', icon: '✳️', reward: 500, requirement: 500000, color: '#50C878' },
    { name: 'Diamond', icon: '💎', reward: 1000, requirement: 1000000, color: '#B9F2FF' },
    { name: 'Eternal', icon: '♾️', reward: 10000, requirement: 10000000, color: '#E6E6FA' }
  ];

  // Previous month winners (Nov 22 - Dec 22, 2024)
  const [previousLeaderboardWinners, setPreviousLeaderboardWinners] = useState(null);
  const [previousWeeklyWinners, setPreviousWeeklyWinners] = useState(null);
  const [showPreviousWinners, setShowPreviousWinners] = useState(false);
  const [showPreviousWeeklyWinners, setShowPreviousWeeklyWinners] = useState(false);

  const socialLinks = [
    { 
      name: 'Discord', 
      url: 'https://discord.gg/snutzy', 
      color: '#5865F2',
      icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>'
    },
    { 
      name: 'Twitch', 
      url: 'https://www.twitch.tv/snutzy', 
      color: '#9146FF',
      icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>'
    },
    { 
      name: 'Twitter', 
      url: 'https://x.com/snutztv', 
      color: '#000000',
      icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>'
    },
    { 
      name: 'YouTube', 
      url: 'https://www.youtube.com/@snutztv', 
      color: '#FF0000',
      icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>'
    },
    { 
      name: 'TikTok', 
      url: 'https://www.tiktok.com/@snutzyclips', 
      color: '#00F2EA',
      icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>'
    }
  ];

  // YouTube Shorts - fetched from API

  // ============================================
  // AUTH FUNCTIONS
  // ============================================
  
  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Check for token in URL (from OAuth callback)
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('auth_token');
      
      if (urlToken) {
        // Store token in localStorage
        localStorage.setItem('auth_token', urlToken);
        // Remove token from URL
        window.history.replaceState({}, '', window.location.pathname);
      }

      // Account transfer completed — the auth_token above is already for the new account
      if (params.get('account_transfer') === 'success') {
        alert('✅ Transfer complete!\n\nYour snutzy.gg profile now lives on this Twitch account. Points, predictions, redemptions and linked accounts all came along.');
      }

      // Check for auth error in URL
      if (params.get('auth_error')) {
        const authErr = params.get('auth_error');
        const authDetail = params.get('auth_detail');
        console.error('Auth error:', authErr, authDetail);
        // Show a brief error toast — don't block the page
        if (authErr === 'callback_failed') {
          alert(`Login failed. Please try again.\n\nDetails: ${authDetail || 'Unknown error'}`);
        } else if (authErr === 'transfer_failed') {
          alert(`❌ Account transfer failed — nothing was changed.\n\n${authDetail || 'Please contact an admin.'}`);
        } else if (authErr === 'access_denied') {
          // User cancelled — no alert needed
        } else {
          alert(`Login error: ${authErr}`);
        }
        window.history.replaceState({}, '', window.location.pathname);
        setAuthLoading(false);
        return;
      }

      // Check for discord link success/error
      if (params.get('discord_linked')) {
        setDiscordLinked(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (params.get('discord_error')) {
        console.error('Discord link error:', params.get('discord_error'));
        const err = params.get('discord_error');
        if (err === 'already_linked_other') {
          alert('This Discord account is already linked to a different user.');
        }
        window.history.replaceState({}, '', window.location.pathname);
      }
      // Check for kick link success/error
      if (params.get('kick_linked')) {
        setKickLinked(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (params.get('kick_broadcaster_linked')) {
        alert('✅ Kick broadcaster connected — giveaway winners will now be announced in Kick chat too.');
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (params.get('kick_error')) {
        console.error('Kick link error:', params.get('kick_error'));
        const kerr = params.get('kick_error');
        if (kerr === 'already_linked_other') {
          alert('This Kick account is already linked to a different user.');
        } else if (kerr === 'not_configured') {
          alert('Kick linking is not configured yet.');
        }
        window.history.replaceState({}, '', window.location.pathname);
      }
      
      // Get token from localStorage
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setAuthLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${BACKEND_API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          
          // Collect browser fingerprint for fraud detection
          setTimeout(() => collectFingerprint(), 2000);
          
          // Check if admin
          const adminResponse = await fetch(`${BACKEND_API_URL}/api/admin/check`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (adminResponse.ok) {
            const adminData = await adminResponse.json();
            setIsAdmin(adminData.isAdmin);
            setIsModerator(!!adminData.isModerator);
          }
        } else {
          // Token invalid, clear it
          localStorage.removeItem('auth_token');
        }
      } catch (e) {
        console.log('Auth check failed:', e);
      }
      setAuthLoading(false);
    };
    
    checkAuth();
  }, []);

  // Get auth token for API calls. Memoised so its identity is stable — children
  // (e.g. AdminLossback) use it inside useCallback/useEffect deps, and an unstable
  // identity would re-run those effects on every parent render (it was collapsing
  // the "View PNL" screenshot the instant it opened).
  const getAuthToken = useCallback(() => localStorage.getItem('auth_token'), []);

  // Collect browser fingerprint and send to server
  const collectFingerprint = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      // Canvas fingerprint
      let canvasHash = '';
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 200; canvas.height = 50;
        const ctx = canvas.getContext('2d');
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('Snutzy fp', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Snutzy fp', 4, 17);
        const dataUrl = canvas.toDataURL();
        let hash = 0;
        for (let i = 0; i < dataUrl.length; i++) {
          const char = dataUrl.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash;
        }
        canvasHash = Math.abs(hash).toString(16);
      } catch (e) { /* canvas blocked */ }

      // WebGL renderer
      let webglRenderer = '';
      try {
        const gl = document.createElement('canvas').getContext('webgl');
        if (gl) {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          if (debugInfo) webglRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
        }
      } catch (e) { /* webgl blocked */ }

      // Combined fingerprint hash
      const signals = [
        navigator.userAgent,
        `${screen.width}x${screen.height}`,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        navigator.language,
        navigator.platform,
        canvasHash,
        webglRenderer,
        screen.colorDepth,
        navigator.maxTouchPoints > 0,
        navigator.hardwareConcurrency || 0,
        navigator.deviceMemory || 0
      ].join('|');
      
      let fpHash = 0;
      for (let i = 0; i < signals.length; i++) {
        const char = signals.charCodeAt(i);
        fpHash = ((fpHash << 5) - fpHash) + char;
        fpHash = fpHash & fpHash;
      }
      const fingerprintHash = Math.abs(fpHash).toString(16);

      await fetch(`${BACKEND_API_URL}/api/fingerprint`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fingerprintHash,
          screenResolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          platform: navigator.platform,
          canvasHash,
          webglRenderer: webglRenderer.substring(0, 200),
          colorDepth: screen.colorDepth,
          touchSupport: navigator.maxTouchPoints > 0
        })
      });
    } catch (e) {
      // Silent fail - fingerprinting is best-effort
    }
  }, []);

  // Login with Twitch
  const loginWithTwitch = () => {
    window.location.href = `${BACKEND_API_URL}/auth/twitch`;
  };

  // Logout
  const logout = async () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('wagerStats');
    setUser(null);
    setUserWagerStats(null);
    setIsAdmin(false);
    setIsModerator(false);
    setPredictionData({
      activeHunt: null,
      predictionsOpen: false,
      myPrediction: null,
      allPredictions: [],
      predictionCount: 0,
      rewards: { first: '', second: '', third: '' }
    });
    setMyPredictionHistory([]);
  };

  // ============================================
  // PREDICTION FUNCTIONS
  // ============================================

  // Fetch active hunt and predictions
  const fetchPredictionData = useCallback(async () => {
    // Skip this tick if a previous fetch (including its retries) is still running —
    // the poller must never stack overlapping request chains.
    if (predictionFetchBusyRef.current) return;
    predictionFetchBusyRef.current = true;
    try {
      // Get active hunt — retried with backoff so a slow backend ≠ "no hunt"
      const huntResponse = await fetchWithRetry(`${BACKEND_API_URL}/api/predictions/active-hunt`, {}, { key: 'pred-active-hunt' });
      if (huntResponse.ok) {
        const huntData = await huntResponse.json();

        if (huntData.activeHunt) {
          // Get all predictions for this hunt
          const predsResponse = await fetchWithRetry(`${BACKEND_API_URL}/api/predictions/hunt/${huntData.activeHunt.huntId}`, {}, { key: 'pred-hunt-preds' });
          const predsData = predsResponse.ok ? await predsResponse.json() : { predictions: [], count: 0 };

          // Get user's predictions if logged in (all slots)
          let myPrediction = null;
          let myPredictions = [];
          let availableCharges = 0;
          const token = getAuthToken();
          if (user && token) {
            const myPredsResponse = await fetchWithRetry(`${BACKEND_API_URL}/api/predictions/my-predictions/${huntData.activeHunt.huntId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }, { key: 'pred-my-preds' });
            if (myPredsResponse.ok) {
              const myPredsData = await myPredsResponse.json();
              myPredictions = myPredsData.predictions || [];
              availableCharges = myPredsData.availableCharges || 0;
              // For backwards compatibility, set myPrediction to slot 1
              myPrediction = myPredictions.find(p => p.slot === 1) || null;
            }
          }
          
          setPredictionData({
            activeHunt: huntData.activeHunt,
            predictionsOpen: huntData.predictionsOpen || false,
            myPrediction,
            myPredictions,
            availableCharges,
            allPredictions: predsData.predictions || [],
            predictionCount: huntData.predictionCount || predsData.count || 0,
            rewards: huntData.rewards || { first: '', second: '', third: '' }
          });
          
          // Initialize prediction inputs from existing predictions (only if empty)
          setPredictionInputs(prev => {
            const inputs = { ...prev };
            myPredictions.forEach(p => {
              // Only initialize if this slot doesn't have a value being edited
              if (!inputs[p.slot]) {
                inputs[p.slot] = '';
              }
            });
            return inputs;
          });
        } else {
          setPredictionData({
            activeHunt: null,
            predictionsOpen: false,
            myPrediction: null,
            myPredictions: [],
            availableCharges: 0,
            allPredictions: [],
            predictionCount: 0,
            rewards: { first: '', second: '', third: '' }
          });
          setPredictionInputs({});
        }
        // Definitive answer from the API — safe to show real state ("no hunt" / "paused")
        predictionEverLoadedRef.current = true;
        setPredictionLoadState('loaded');
      } else if (!predictionEverLoadedRef.current) {
        // Non-OK even after retries and nothing cached — surface the error UI
        setPredictionLoadState('error');
      }
    } catch (e) {
      if (!e?.superseded) {
        console.error('Fetch prediction data error:', e);
        // Keep showing last-good data if we have it; only error out when we don't
        if (!predictionEverLoadedRef.current) setPredictionLoadState('error');
      }
    } finally {
      predictionFetchBusyRef.current = false;
    }
  }, [user]);

  // Fetch last completed hunt results
  const fetchLastHuntResults = useCallback(async () => {
    try {
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/predictions/last-completed`, {}, { key: 'pred-last-completed' });
      if (response.ok) {
        const data = await response.json();
        setLastHuntResults({
          hunt: data.hunt,
          predictions: data.predictions || [],
          winners: data.winners || []
        });
      }
    } catch (e) {
      if (!e?.superseded) console.error('Fetch last hunt results error:', e);
    }
  }, []);

  // Fetch all previous winners
  const fetchPreviousWinners = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/predictions/all-winners`);
      if (response.ok) {
        const data = await response.json();
        setPreviousWinners(data.hunts || []);
      }
    } catch (e) {
      console.error('Fetch previous winners error:', e);
    }
  }, []);

  // Fetch user's prediction history
  const fetchMyPredictionHistory = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !user) return;
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/predictions/my-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyPredictionHistory(data.predictions || []);
      }
    } catch (e) {
      console.error('Fetch prediction history error:', e);
    }
  }, [user]);

  // Fetch user profile stats
  const fetchProfileStats = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !user) return;
    
    setProfileLoading(true);
    try {
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/user/profile-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }, { key: 'profile-stats' });
      if (response.ok) {
        const data = await response.json();
        setProfileStats(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Fetch profile stats failed:', response.status, errorData);
      }
    } catch (e) {
      if (e?.superseded) return; // newer call owns the loading state — don't touch it
      console.error('Fetch profile stats error:', e);
    }
    setProfileLoading(false);
  }, [user]);

  // Fetch user wager stats (for rewards page)
  const fetchUserWagerStats = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !user) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/user/wager-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserWagerStats(data);
        try { localStorage.setItem('wagerStats', JSON.stringify(data)); } catch {}
      }
    } catch (e) {
      console.error('Fetch wager stats error:', e);
    }
  }, [user]);

  // Fetch user's wager reward claims (+ Discord link state and this period's
  // reward tickets, which drive the Claim/Pending/Paid button states)
  const [wagerTicketInfo, setWagerTicketInfo] = useState({ discordLinked: false, tickets: [], discordGuildId: null });
  const [wagerClaimBusy, setWagerClaimBusy] = useState(false);
  const fetchUserWagerClaims = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !user) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/user/wager-claims`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserWagerClaims(data.claims || []);
        setWagerTicketInfo({ discordLinked: !!data.discordLinked, tickets: data.tickets || [], discordGuildId: data.discordGuildId || null });
      }
    } catch (e) {
      console.error('Fetch wager claims error:', e);
    }
  }, [user]);

  // Claim unlocked wager tiers → backend opens a Discord ticket channel
  const claimWagerRewards = async () => {
    if (wagerClaimBusy) return;
    setWagerClaimBusy(true);
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/rewards/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (d.error === 'discord_not_linked') {
          if (confirm('You need to connect your Discord account first. Connect now?')) connectDiscord();
        } else {
          alert(d.error || 'Claim failed — please try again');
        }
        return;
      }
      await fetchUserWagerClaims();
      if (d.guildId && d.channelId && confirm(`🎫 Ticket created for ${d.tiers.join(' + ')} ($${d.totalAmount.toLocaleString()})!\n\nAn admin will verify and pay out in your private Discord ticket. Open it now?`)) {
        window.open(`https://discord.com/channels/${d.guildId}/${d.channelId}`, '_blank', 'noopener');
      }
    } catch (e) {
      alert('Network error — please try again');
    } finally {
      setWagerClaimBusy(false);
    }
  };

  // VIP role self-claim (VIP dashboard)
  const [vipClaimStatus, setVipClaimStatus] = useState(null);
  const [vipClaimBusy, setVipClaimBusy] = useState(false);
  const fetchVipClaimStatus = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !user) return;
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/vip/claim-status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setVipClaimStatus(await r.json());
    } catch (e) {}
  }, [user]);

  const claimVipRole = async () => {
    if (vipClaimBusy) return;
    setVipClaimBusy(true);
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/vip/claim-role`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (d.error === 'discord_not_linked') {
          if (confirm('You need to connect your Discord account first. Connect now?')) connectDiscord();
        } else {
          alert(d.error || 'VIP claim failed — please try again');
        }
        return;
      }
      setShowVipClaimPopup(false);
      alert(d.regain
        ? '👑 Welcome back! Your VIP role has been reinstated on Discord.'
        : '👑 VIP role granted! Check #vip-chat on Discord for your welcome. 🎉');
      fetchVipClaimStatus();
      fetchUserWagerStats();
    } catch (e) {
      alert('Network error — please try again');
    } finally {
      setVipClaimBusy(false);
    }
  };

  // Site-wide "you've reached VIP" popup: one eligibility check per session
  // shortly after login. Shows only while there's actually a claim to make
  // (eligible, self-claim configured, role not yet held, never claimed), and
  // "Maybe later" snoozes it for 24h.
  const [showVipClaimPopup, setShowVipClaimPopup] = useState(false);
  useEffect(() => {
    if (!user) { setShowVipClaimPopup(false); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BACKEND_API_URL}/api/vip/claim-status`, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        if (!r.ok || !alive) return;
        const d = await r.json();
        setVipClaimStatus(d);
        if (d.eligible && d.available && !d.hasRole && !d.claimed) {
          let snoozedAt = 0;
          try { snoozedAt = parseInt(localStorage.getItem('vipClaimPopupSnooze') || '0', 10) || 0; } catch {}
          if (Date.now() - snoozedAt > 24 * 3600 * 1000) setShowVipClaimPopup(true);
        }
      } catch (e) {}
    }, 4000); // let the initial page load settle first
    return () => { alive = false; clearTimeout(t); };
  }, [user, getAuthToken]);

  const snoozeVipClaimPopup = () => {
    try { localStorage.setItem('vipClaimPopupSnooze', String(Date.now())); } catch {}
    setShowVipClaimPopup(false);
  };

  // Admin: Fetch all wager reward claims
  const fetchAdminWagerClaims = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/wager-claims`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminWagerClaims(data.claims || []);
      }
    } catch (e) {
      console.error('Fetch admin wager claims error:', e);
    }
  }, []);

  // Admin: Fetch leaderboard total paid setting
  const fetchAdminLeaderboardTotal = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/settings/leaderboard_total_paid`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminLeaderboardTotal(data.setting?.value || '0');
      }
    } catch (e) {
      console.error('Fetch leaderboard total error:', e);
    }
  }, []);

  // Admin: Fetch the weekly leaderboard feature flag (defaults ON unless explicitly 'false')
  const fetchWeeklyLbAdminFlag = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/settings/feature_weekly_leaderboard_enabled`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWeeklyLbAdminEnabled(data.setting?.value !== 'false');
      }
    } catch (e) {
      console.error('Fetch weekly LB flag error:', e);
    }
  }, []);

  // Admin: Toggle the weekly leaderboard on/off
  const toggleWeeklyLbAdmin = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    setWeeklyLbAdminBusy(true);
    const next = !weeklyLbAdminEnabled;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ key: 'feature_weekly_leaderboard_enabled', value: next ? 'true' : 'false' })
      });
      if (response.ok) {
        setWeeklyLbAdminEnabled(next);
        setFeatureFlags(prev => ({ ...prev, weekly: next }));
      } else {
        alert('Failed to update weekly leaderboard setting');
      }
    } catch (e) {
      alert('Failed to update weekly leaderboard setting');
    }
    setWeeklyLbAdminBusy(false);
  }, [weeklyLbAdminEnabled]);

  // Load the weekly leaderboard flag when an admin opens the panel
  useEffect(() => {
    if (activePage === 'admin' && isAdmin) fetchWeeklyLbAdminFlag();
  }, [activePage, isAdmin, fetchWeeklyLbAdminFlag]);

  // Admin: Fetch wager rewards bulk adjustment
  const fetchAdminWagerBulk = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/settings/wager_rewards_bulk_adjustment`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminWagerBulkAdjustment(data.setting?.value || '0');
      }
    } catch (e) {
      console.error('Fetch wager bulk error:', e);
    }
  }, []);

  // Admin: Fetch all bulk adjustments
  const fetchAdminBulkAdjustments = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const keys = ['giveaways_bulk_adjustment', 'predictions_bulk_adjustment', 'slotpicks_bulk_adjustment'];
      const setters = [setAdminGiveawaysBulk, setAdminPredictionsBulk, setAdminSlotPicksBulk];
      for (let i = 0; i < keys.length; i++) {
        const response = await fetch(`${BACKEND_API_URL}/api/admin/settings/${keys[i]}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setters[i](data.setting?.value || '0');
        }
      }
    } catch (e) {
      console.error('Fetch bulk adjustments error:', e);
    }
  }, []);

  // Admin: Fetch fraud clusters
  const fetchFraudClusters = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/clusters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFraudData(data);
      }
    } catch (e) {
      console.error('Fetch fraud clusters error:', e);
    }
  }, []);

  // Admin: Re-scan stored fingerprint IPs against datacenter/VPN lists
  const rescanFraudIps = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    setFraudRescanning(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/rescan-ips`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        alert(`Rescan complete: ${data.scannedIps} IPs checked — ${data.datacenterIps} datacenter, ${data.vpnIps} VPN. ${data.timezoneRowsUpdated} timezone rows updated.`);
        // Refresh clusters with the new flags
        const refresh = await fetch(`${BACKEND_API_URL}/api/admin/fraud/clusters`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (refresh.ok) setFraudData(await refresh.json());
      } else {
        alert(data.error || 'Rescan failed');
      }
    } catch (e) {
      console.error('Fraud rescan error:', e);
      alert('Rescan failed');
    }
    setFraudRescanning(false);
  }, []);

  // Admin: Fetch giveaway behavior analysis
  const fetchGiveawayAnalysis = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    setGiveawayAnalysisLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/giveaway-analysis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGiveawayAnalysis(data);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Analysis failed: ${err.error || response.status}`);
      }
    } catch (e) { console.error('Giveaway analysis error:', e); alert('Analysis failed — check console'); }
    setGiveawayAnalysisLoading(false);
  }, []);

  // Admin: Fetch shadowbans
  const fetchShadowbans = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/shadowbans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setShadowbans(data.shadowbans || []);
      }
    } catch (e) { console.error('Fetch shadowbans error:', e); }
  }, []);

  const addShadowban = async (username, reason) => {
    const token = getAuthToken();
    if (!token || !username.trim()) return;
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/admin/fraud/shadowban`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ twitchUsername: username.trim(), reason: reason.trim() || null })
      });
      const data = await res.json();
      if (res.ok) {
        setShadowbanInput('');
        setShadowbanReason('');
        fetchShadowbans();
      } else {
        alert(data.error || 'Failed to add shadowban');
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const removeShadowban = async (id) => {
    const token = getAuthToken();
    try {
      await fetch(`${BACKEND_API_URL}/api/admin/fraud/shadowban/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchShadowbans();
    } catch (e) { alert('Error: ' + e.message); }
  };

  // Admin: Fetch VIP activity monitor
  const fetchVipMonitor = useCallback(async (period) => {
    const token = getAuthToken();
    if (!token) return;
    const p = period || vipPeriod;
    setVipLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/vip-monitor?period=${p}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVipMonitorData(data);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to load VIP data');
      }
    } catch (e) {
      console.error('VIP monitor error:', e);
    }
    setVipLoading(false);
  }, [vipPeriod]);

  // Admin: Fetch fraud user detail
  const fetchFraudUserDetail = useCallback(async (userId) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFraudUserDetail(data);
        setFraudView('user');
      }
    } catch (e) {
      console.error('Fetch fraud user detail error:', e);
    }
  }, []);

  // Admin: Search users for fraud
  const searchFraudUsers = useCallback(async (query) => {
    const token = getAuthToken();
    if (!token || !query || query.length < 2) { setFraudSearchResults([]); return; }
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFraudSearchResults(data.users);
      }
    } catch (e) {
      console.error('Fraud search error:', e);
    }
  }, []);

  const acknowledgeFraudCluster = useCallback(async (clusterType, clusterKey, note) => {
    const token = getAuthToken();
    try {
      await fetch(`${BACKEND_API_URL}/api/admin/fraud/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clusterType, clusterKey, note })
      });
      // Refresh fraud data
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/clusters`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { const data = await response.json(); setFraudData(data); }
    } catch (e) { console.error('Acknowledge error:', e); }
  }, []);

  const unacknowledgeFraudCluster = useCallback(async (clusterType, clusterKey) => {
    const token = getAuthToken();
    try {
      await fetch(`${BACKEND_API_URL}/api/admin/fraud/acknowledge`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clusterType, clusterKey })
      });
      const response = await fetch(`${BACKEND_API_URL}/api/admin/fraud/clusters`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { const data = await response.json(); setFraudData(data); }
    } catch (e) { console.error('Unacknowledge error:', e); }
  }, []);

  // Claimable giveaways: Fetch my claimable giveaways
  const fetchMyClaimableGiveaways = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/my-claimable-giveaways`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyClaimableGiveaways(data.giveaways || []);
        setPendingShuffleGiveaways(data.pendingShuffle || []);
        setClaimableDiscordLinked(data.discordLinked);
        setClaimableTwitchName(data.twitchName || '');
        setClaimableDiscordActual(data.discordActualUsername || null);
        setClaimableKickLinked(data.kickLinked ?? null);
      }
    } catch (e) {
      console.error('Fetch claimable giveaways error:', e);
    }
  }, []);

  // Admin: Fetch claimable giveaways
  const fetchAdminClaimableGiveaways = useCallback(async (statusFilter) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const url = statusFilter 
        ? `${BACKEND_API_URL}/api/admin/claimable-giveaways?status=${statusFilter}`
        : `${BACKEND_API_URL}/api/admin/claimable-giveaways`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminClaimableGiveaways(data.giveaways || []);
        setAdminClaimableCounts(data.counts || {});
      }
    } catch (e) {
      console.error('Fetch admin claimable giveaways error:', e);
    }
  }, []);

  // Public: Fetch payout stats for top bar
  const fetchPayoutStats = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/payout-stats`);
      if (response.ok) {
        const data = await response.json();
        setPublicPayoutStats(data);
      }
    } catch (e) {
      console.error('Fetch payout stats error:', e);
    }
  }, []);

  // Fetch tournament info
  const fetchTournamentInfo = useCallback(async () => {
    try {
      const url = isAdmin ? `${BACKEND_API_URL}/api/tournament/info?admin=true` : `${BACKEND_API_URL}/api/tournament/info`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setTournamentData(data);
      }
    } catch (e) {
      console.error('Fetch tournament info error:', e);
    }
  }, [isAdmin]);

  // Fetch user's tournament entry
  const fetchTournamentMyEntry = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !user) return;
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/tournament/my-entry`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.entered) {
          setTournamentMyEntry(data.entry);
        } else {
          setTournamentMyEntry(null);
        }
      }
    } catch (e) {
      console.error('Fetch tournament entry error:', e);
    }
  }, [user]);

  // Enter tournament raffle
  const enterTournament = async () => {
    if (!user) {
      setTournamentMessage({ type: 'error', text: 'Please login with Twitch first!' });
      return;
    }
    
    setTournamentEntering(true);
    setTournamentMessage(null);
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/tournament/enter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTournamentMessage({ type: 'success', text: `🎉 You're in! Entry ID: ${data.entryId}` });
        fetchTournamentInfo();
        fetchTournamentMyEntry();
      } else {
        // Handle specific error cases
        if (data.needsLink) {
          setTournamentMessage({ 
            type: 'error', 
            text: '⚠️ You must link your Shuffle username first! Go to your Profile page to link it.',
            action: { label: 'Go to Profile', onClick: () => setActivePage('profile') }
          });
        } else if (data.notAffiliate) {
          setTournamentMessage({ 
            type: 'error', 
            text: data.error || 'Your Shuffle username was not found under guild SNUTZY.'
          });
        } else {
          setTournamentMessage({ type: 'error', text: data.error || 'Failed to enter tournament' });
        }
      }
    } catch (e) {
      setTournamentMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
    
    setTournamentEntering(false);
  };

  // Fetch tournament history (completed tournaments)
  const fetchTournamentHistory = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/tournaments/history`);
      if (response.ok) {
        const data = await response.json();
        setTournamentHistory(data.tournaments || []);
      }
    } catch (e) {
      console.error('Fetch tournament history error:', e);
    }
  }, []);

  // Fetch specific past tournament bracket
  const fetchPastTournamentBracket = async (tournamentId) => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/tournaments/${tournamentId}/bracket`);
      if (response.ok) {
        const data = await response.json();
        setPastTournamentBracket(data);
        setViewingPastTournament(tournamentId);
      }
    } catch (e) {
      console.error('Fetch past tournament bracket error:', e);
    }
  };

  // Admin: Fetch all tournaments for management
  const fetchAdminTournaments = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournaments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminTournaments(data.tournaments || []);
      }
    } catch (e) {
      console.error('Fetch admin tournaments error:', e);
    }
  };

  // Admin: Create tournament
  const createTournament = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournaments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(tournamentForm)
      });
      const data = await response.json();
      if (response.ok) {
        setTournamentAdminMsg({ type: 'success', text: `Tournament created: ${tournamentForm.title}` });
        setTournamentForm({ title: '', scheduledDate: '', prizePool: '1000', firstPrize: '500', secondPrize: '300', thirdPrize: '200', bonusBuyAmount: '150', bracketSize: '32', setActive: true });
        fetchAdminTournaments();
        fetchTournamentInfo();
      } else {
        setTournamentAdminMsg({ type: 'error', text: data.error || 'Failed to create tournament' });
      }
    } catch (e) {
      setTournamentAdminMsg({ type: 'error', text: 'Network error' });
    }
  };

  // Admin: Update tournament
  const updateTournament = async (id, updates) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournaments/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (response.ok) {
        setTournamentAdminMsg({ type: 'success', text: 'Tournament updated' });
        setEditingTournament(null);
        fetchAdminTournaments();
        fetchTournamentInfo();
      } else {
        setTournamentAdminMsg({ type: 'error', text: data.error || 'Failed to update' });
      }
    } catch (e) {
      setTournamentAdminMsg({ type: 'error', text: 'Failed to update: ' + e.message });
    }
  };

  // Admin: Delete tournament
  const deleteTournament = async (id, title) => {
    if (!window.confirm(`Delete tournament "${title}"? This removes all entries and bracket data.`)) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournaments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setTournamentAdminMsg({ type: 'success', text: 'Tournament deleted' });
        fetchAdminTournaments();
        fetchTournamentInfo();
      } else {
        setTournamentAdminMsg({ type: 'error', text: data.error || 'Failed to delete' });
      }
    } catch (e) {
      setTournamentAdminMsg({ type: 'error', text: 'Network error' });
    }
  };

  // Admin: Set active tournament
  const activateTournament = async (id) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournaments/${id}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTournamentAdminMsg({ type: 'success', text: 'Tournament set as active' });
        fetchAdminTournaments();
        fetchTournamentInfo();
        fetchBracketData();
      }
    } catch (e) {
      setTournamentAdminMsg({ type: 'error', text: 'Failed to activate' });
    }
  };

  // Admin: Update tournament status
  const updateTournamentStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournaments/${id}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        setTournamentAdminMsg({ type: 'success', text: `Status changed to ${status}` });
        fetchAdminTournaments();
        fetchTournamentInfo();
      }
    } catch (e) {
      setTournamentAdminMsg({ type: 'error', text: 'Failed to update status' });
    }
  };

  // Admin: Complete tournament
  const completeTournament = async () => {
    // Find winners from bracket data
    const finalsMatch = bracketData.matches.find(m => m.round === 5 && m.match_number === 1);
    if (!finalsMatch?.winner_id) {
      alert('Finals match must have a winner before completing tournament');
      return;
    }
    
    // Get semi-finals losers for 3rd place (both get 3rd)
    const semiFinals = bracketData.matches.filter(m => m.round === 4);
    const semiFinalsLosers = semiFinals.map(m => {
      if (m.winner_id === m.player1_id) return m.player2_id;
      return m.player1_id;
    }).filter(Boolean);
    
    // Winner is finals winner, second is finals loser
    const winnerId = finalsMatch.winner_id;
    const secondPlaceId = finalsMatch.player1_id === winnerId ? finalsMatch.player2_id : finalsMatch.player1_id;
    const thirdPlaceId = semiFinalsLosers[0]; // Just use first semi-final loser
    
    if (!window.confirm('Complete this tournament? This will mark it as finished and show it in tournament history.')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ winnerId, secondPlaceId, thirdPlaceId })
      });
      
      if (response.ok) {
        alert('🏆 Tournament completed!');
        fetchTournamentInfo();
        fetchTournamentHistory();
      } else {
        alert('Failed to complete tournament');
      }
    } catch (e) {
      alert('Error completing tournament');
    }
  };

  // Fetch bracket data
  const fetchBracketData = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/tournament/bracket`);
      if (response.ok) {
        const data = await response.json();
        setBracketData(data);
      }
    } catch (e) {
      console.error('Fetch bracket error:', e);
    }
  }, []);

  // Admin: Pick random player for bracket
  const pickRandomPlayer = async () => {
    if (bracketData.players.length >= 32) {
      alert('Bracket is full! 32 players already picked.');
      return;
    }
    
    const token = getAuthToken();
    setBracketPicking(true);
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/bracket/pick`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPickedPlayer(data.player);
        setShowPickAnimation(true);
        
        // Hide animation after 4 seconds and refresh bracket
        setTimeout(() => {
          setShowPickAnimation(false);
          setPickedPlayer(null);
          fetchBracketData();
        }, 4000);
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to pick player');
      }
    } catch (e) {
      alert('Error picking player');
    }
    
    setBracketPicking(false);
  };

  // Admin: Remove player from bracket
  const removePlayerFromBracket = async (playerId) => {
    if (!window.confirm('Remove this player from the bracket?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/bracket/player/${playerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchBracketData();
      } else {
        alert('Failed to remove player');
      }
    } catch (e) {
      alert('Error removing player');
    }
  };

  // Admin: Update match result
  // Admin: Update match result
  const updateMatchResult = async (match) => {
    if (!matchResult.winnerId) {
      alert('Please select a winner');
      return;
    }
    
    const token = getAuthToken();
    try {
      // Use POST endpoint which creates or updates
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/bracket/match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          round: match.round,
          matchNumber: match.matchNumber,
          player1Id: match.player1?.id,
          player2Id: match.player2?.id,
          player1Slot: matchResult.player1Slot || null,
          player2Slot: matchResult.player2Slot || null,
          player1Multi: matchResult.player1Multi ? parseFloat(matchResult.player1Multi) : null,
          player2Multi: matchResult.player2Multi ? parseFloat(matchResult.player2Multi) : null,
          winnerId: matchResult.winnerId
        })
      });
      
      if (response.ok) {
        setSelectedMatch(null);
        setMatchResult({ player1Slot: '', player1Multi: '', player2Slot: '', player2Multi: '', winnerId: null });
        fetchBracketData();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to update match');
      }
    } catch (e) {
      alert('Error updating match');
    }
  };

  // Admin: Reset bracket
  const resetBracket = async () => {
    if (!window.confirm('⚠️ Reset the entire bracket? This will remove all players and matches!')) return;
    if (!window.confirm('Are you REALLY sure? This cannot be undone!')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/bracket/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchBracketData();
        alert('Bracket has been reset');
      } else {
        alert('Failed to reset bracket');
      }
    } catch (e) {
      alert('Error resetting bracket');
    }
  };

  // Admin: Undo last pick (removes last player, puts them back in raffle pool)
  const undoLastPick = async () => {
    if (bracketData.players.length === 0) return;
    
    const lastPlayer = bracketData.players[bracketData.players.length - 1];
    if (!window.confirm(`Undo pick for ${lastPlayer.display_name}? They will be returned to the raffle pool.`)) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/bracket/undo-pick`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        fetchBracketData();
        alert(`✅ Removed ${data.removedPlayer}. They are back in the raffle pool.`);
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to undo pick');
      }
    } catch (e) {
      alert('Error undoing pick');
    }
  };

  // Admin: Replace a player with a new random pick (keeps same seed)
  const replacePlayer = async (playerId, playerName) => {
    if (!window.confirm(`Replace ${playerName} with a new random player? Their seed position will be kept.`)) return;
    
    const token = getAuthToken();
    setBracketPicking(true);
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/bracket/replace/${playerId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPickedPlayer(data.newPlayer);
        setShowPickAnimation(true);
        
        setTimeout(() => {
          setShowPickAnimation(false);
          setPickedPlayer(null);
          fetchBracketData();
        }, 4000);
        
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to replace player');
      }
    } catch (e) {
      alert('Error replacing player');
    }
    
    setBracketPicking(false);
  };

  // Fetch challenges
  const fetchXHighlights = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/highlights`);
      if (res.ok) {
        const data = await res.json();
        setXHighlights(data.posts || []);
      }
    } catch (e) {
      console.error('Fetch highlights error:', e);
    }
  }, []);

  const updateHighlight = useCallback(async (id, patch) => {
    // optimistic — the embed is expensive to re-render, so don't wait on the round trip
    setXHighlights(prev => patch.hidden
      ? prev.filter(p => p.id !== id)
      : prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
    try {
      await fetch(`${BACKEND_API_URL}/api/admin/highlights/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify(patch)
      });
    } catch (e) { /* next load reconciles */ }
  }, []);

  const fetchChallenges = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/shuffle-challenges`);
      if (response.ok) {
        const data = await response.json();
        setShuffleCh({
          active: data.active || [],
          completed: data.completed || [],
          stats: data.stats || null,
          feed: data.feed || null
        });
      }
    } catch (e) {
      console.error('Fetch challenges error:', e);
    }
  }, []);

  // Fetch current community hunt
  const fetchCommunityHunt = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/community-hunt/current`);
      if (response.ok) {
        const data = await response.json();
        setCommunityHunt(data);
      }
    } catch (e) {
      console.error('Fetch community hunt error:', e);
    }
  }, []);

  // Fetch community hunt history
  const fetchCommunityHuntHistory = useCallback(async (page = 1) => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/community-hunt/history?page=${page}&limit=5`);
      if (response.ok) {
        const data = await response.json();
        setCommunityHuntHistory(data);
      }
    } catch (e) {
      console.error('Fetch community hunt history error:', e);
    }
  }, []);

  // ========== RAFFLE HUNT FUNCTIONS ==========
  
  const fetchRaffleHunt = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/raffle-hunts/active`);
      if (response.ok) {
        const data = await response.json();
        setRaffleHunt(data);
      }
    } catch (e) {
      console.error('Fetch raffle hunt error:', e);
    }
  }, []);

  const fetchRaffleMyEntry = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/raffle-hunts/my-entry`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRaffleMyEntry(data.entry);
        setRaffleEligibility(data.eligibility || {});
        setRaffleHasShuffleUsername(data.hasShuffleUsername);
        setRaffleLossStreak(data.lossStreak || 0);
        setRaffleBoostMultiplier(data.boostMultiplier || '1.00');
      }
    } catch (e) {
      console.error('Fetch raffle entry error:', e);
    }
  }, []);

  const fetchRaffleHistory = useCallback(async (page = 1) => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/raffle-hunts/history?page=${page}&limit=5`);
      if (response.ok) {
        const data = await response.json();
        setRaffleHistory(data);
      }
    } catch (e) {
      console.error('Fetch raffle history error:', e);
    }
  }, []);

  const enterRaffleHunt = async () => {
    const token = getAuthToken();
    if (!token) return;
    setRaffleLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/raffle-hunts/enter`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (response.ok) {
        alert('🎉 ' + data.message);
        fetchRaffleHunt();
        fetchRaffleMyEntry();
      } else {
        alert(data.error || 'Failed to enter');
      }
    } catch (e) {
      alert('Network error');
    }
    setRaffleLoading(false);
  };

  const fetchEligibilityWhitelist = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/eligibility-whitelist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEligibilityWhitelist(data.whitelist || []);
      }
    } catch (e) {
      console.error('Fetch whitelist error:', e);
    }
  };

  // ========== POINTS SYSTEM FUNCTIONS ==========
  
  const fetchPointsBalance = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/points/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPointsBalance(data.balance);
        setPointsBreakdown(data.breakdown || []);
      }
    } catch (e) {
      console.error('Fetch points balance error:', e);
    }
  }, []);

  const fetchPointsShop = useCallback(async () => {
    setPointsShopLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/points/shop`);
      if (response.ok) {
        const data = await response.json();
        setPointsShopItems(data.items || []);
      }
    } catch (e) {
      console.error('Fetch points shop error:', e);
    } finally {
      setPointsShopLoading(false);
    }
  }, []);

  const fetchRecentRedemptions = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/points/recent-redemptions`);
      if (response.ok) {
        const data = await response.json();
        setRecentRedemptions(data);
      }
    } catch (e) { console.error('Fetch recent redemptions error:', e); }
  }, []);

  const fetchPointsHistory = useCallback(async (page = 1) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/points/history?page=${page}&limit=15`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPointsHistory(data.transactions || []);
        setPointsHistoryTotal(data.pagination?.totalPages || 1);
        setPointsHistoryPage(page);
      }
    } catch (e) {
      console.error('Fetch points history error:', e);
    }
  }, []);

  const fetchPointsLeaderboard = useCallback(async (page = 1) => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/points/leaderboard?page=${page}&limit=20`);
      if (response.ok) {
        const data = await response.json();
        setPointsLeaderboard(data.leaderboard || []);
        setPointsLbPage(data.pagination?.page || 1);
        setPointsLbTotalPages(data.pagination?.totalPages || 1);
      }
    } catch (e) {
      console.error('Fetch points leaderboard error:', e);
    }
  }, []);

  const fetchMyRedemptions = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/points/my-redemptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyRedemptions(data.redemptions || []);
      }
    } catch (e) {
      console.error('Fetch my redemptions error:', e);
    }
  }, []);

  const redeemShopItem = async (itemId, discordUsername = '') => {
    const token = getAuthToken();
    if (!token) return;
    setRedeemingItemId(itemId);
    setRedeemMessage(null);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/points/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ itemId, discordUsername })
      });
      const data = await response.json();
      if (response.ok) {
        setRedeemMessage({ type: 'success', text: data.message });
        setPointsBalance(data.newBalance);
        fetchPointsShop();
        fetchMyRedemptions();
        setShowRedeemModal(false);
        setSelectedShopItem(null);
      } else {
        setRedeemMessage({ type: 'error', text: data.error });
      }
    } catch (e) {
      setRedeemMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setRedeemingItemId(null);
    }
  };

  // ========== END POINTS SYSTEM FUNCTIONS ==========

  // ========== DISCORD LINK FUNCTIONS ==========

  const fetchDiscordStatus = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/auth/discord/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDiscordLinked(data.linked);
        setDiscordInfo(data.discord || null);
      }
    } catch (e) {
      console.error('Fetch discord status error:', e);
    }
  }, []);

  const connectDiscord = () => {
    const token = getAuthToken();
    if (!token) return;
    window.location.href = `${BACKEND_API_URL}/auth/discord?token=${encodeURIComponent(token)}`;
  };

  // ========== KICK LINK FUNCTIONS ==========
  const fetchKickStatus = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/auth/kick/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setKickLinked(data.linked);
        setKickInfo(data.kick || null);
      }
    } catch (e) {
      console.error('Fetch kick status error:', e);
    }
  }, []);

  const connectKick = () => {
    const token = getAuthToken();
    if (!token) return;
    window.location.href = `${BACKEND_API_URL}/auth/kick?token=${encodeURIComponent(token)}`;
  };

  const unlinkKick = async () => {
    const token = getAuthToken();
    if (!token) return;
    if (!window.confirm('Unlink your Kick account?')) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/auth/kick/unlink`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setKickLinked(false);
        setKickInfo(null);
      }
    } catch (e) {
      console.error('Unlink kick error:', e);
    }
  };

  const unlinkDiscord = async () => {
    const token = getAuthToken();
    if (!token) return;
    if (!window.confirm('Unlink your Discord account? You will stop earning Discord points.')) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/auth/discord/unlink`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDiscordLinked(false);
        setDiscordInfo(null);
      }
    } catch (e) {
      console.error('Unlink discord error:', e);
    }
  };

  // ========== END DISCORD LINK FUNCTIONS ==========

  // ========== ADMIN POINTS FUNCTIONS ==========

  const fetchAdminPointsConfig = useCallback(async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminPointsConfig(data.config || {});
      }
    } catch (e) { console.error('Fetch admin points config error:', e); }
  }, []);

  const fetchAdminPointsStats = useCallback(async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminPointsStats(data);
      }
    } catch (e) { console.error('Fetch admin points stats error:', e); }
  }, []);

  const fetchWagerReconciliation = useCallback(async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/wager-reconciliation`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWagerReconciliation(data);
      }
    } catch (e) { console.error('Fetch wager reconciliation error:', e); }
  }, []);

  const fetchAdminShopItems = useCallback(async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/shop`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminShopItems(data.items || []);
      }
    } catch (e) { console.error('Fetch admin shop items error:', e); }
  }, []);

  const fetchAdminPointsRedemptions = useCallback(async (status = 'pending', page = 1) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/redemptions?status=${status}&page=${page}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminPointsRedemptions(data.redemptions || []);
        setAdminPointsRedemptionsPagination(data.pagination || { page: 1, total: 0, totalPages: 1 });
      }
    } catch (e) { console.error('Fetch admin points redemptions error:', e); }
  }, []);

  const saveAdminPointsConfig = async (updates) => {
    const token = getAuthToken();
    setAdminPointsLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ updates })
      });
      const data = await response.json();
      if (response.ok) {
        setAdminPointsConfig(data.config || {});
        setAdminPointsMessage({ type: 'success', text: 'Config saved!' });
      } else {
        setAdminPointsMessage({ type: 'error', text: data.error });
      }
    } catch (e) {
      setAdminPointsMessage({ type: 'error', text: 'Network error' });
    } finally { setAdminPointsLoading(false); }
  };

  const createShopItem = async () => {
    const token = getAuthToken();
    setAdminPointsLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/shop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: newShopItem.name,
          description: newShopItem.description,
          cost: parseInt(newShopItem.cost),
          imageUrl: newShopItem.imageUrl || null,
          iconKey: newShopItem.iconKey || 'custom',
          stock: newShopItem.stock ? parseInt(newShopItem.stock) : null,
          maxPerUser: newShopItem.maxPerUser ? parseInt(newShopItem.maxPerUser) : null,
          requiresShuffleUsername: newShopItem.requiresShuffleUsername,
          requiresDiscordUsername: newShopItem.requiresDiscordUsername,
          fulfillmentNotes: newShopItem.fulfillmentNotes,
          category: newShopItem.category,
          sortOrder: parseInt(newShopItem.sortOrder) || 0,
          cashValue: newShopItem.cashValue === '' ? 0 : parseFloat(newShopItem.cashValue) || 0
        })
      });
      if (response.ok) {
        setAdminPointsMessage({ type: 'success', text: 'Shop item created!' });
        setShowAddShopItemModal(false);
        setNewShopItem({ name: '', description: '', cost: '', cashValue: '', imageUrl: '', iconKey: 'custom', stock: '', maxPerUser: '', requiresShuffleUsername: false, requiresDiscordUsername: false, fulfillmentNotes: '', category: 'custom', sortOrder: '0' });
        fetchAdminShopItems();
        fetchPointsShop();
      } else {
        const data = await response.json();
        setAdminPointsMessage({ type: 'error', text: data.error });
      }
    } catch (e) {
      setAdminPointsMessage({ type: 'error', text: 'Network error' });
    } finally { setAdminPointsLoading(false); }
  };

  const updateShopItem = async (itemId, updates) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/shop/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        fetchAdminShopItems();
        fetchPointsShop();
        setAdminPointsMessage({ type: 'success', text: 'Item updated!' });
      }
    } catch (e) { setAdminPointsMessage({ type: 'error', text: 'Network error' }); }
  };

  const deleteShopItem = async (itemId) => {
    const token = getAuthToken();
    if (!window.confirm('Deactivate this shop item?')) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/shop/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchAdminShopItems();
        fetchPointsShop();
        setAdminPointsMessage({ type: 'success', text: 'Item deactivated' });
      }
    } catch (e) { setAdminPointsMessage({ type: 'error', text: 'Network error' }); }
  };

  const fulfillRedemption = async (redemptionId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/redemptions/${redemptionId}/fulfill`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({})
      });
      if (response.ok) {
        fetchAdminPointsRedemptions(adminPointsRedemptionsFilter, adminPointsRedemptionsPagination.page);
        fetchAdminPointsStats();
        setAdminPointsMessage({ type: 'success', text: 'Redemption fulfilled!' });
      }
    } catch (e) { setAdminPointsMessage({ type: 'error', text: 'Network error' }); }
  };

  const cancelRedemption = async (redemptionId) => {
    const token = getAuthToken();
    if (!window.confirm('Cancel this redemption and refund points?')) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/redemptions/${redemptionId}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({})
      });
      if (response.ok) {
        fetchAdminPointsRedemptions(adminPointsRedemptionsFilter, adminPointsRedemptionsPagination.page);
        fetchAdminPointsStats();
        setAdminPointsMessage({ type: 'success', text: 'Redemption cancelled & points refunded' });
      }
    } catch (e) { setAdminPointsMessage({ type: 'error', text: 'Network error' }); }
  };

  const grantPoints = async () => {
    const token = getAuthToken();
    if (!adminGrantForm.twitchUsername || !adminGrantForm.amount || !adminGrantForm.reason) return;
    setAdminPointsLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/points/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          twitchUsername: adminGrantForm.twitchUsername,
          amount: parseInt(adminGrantForm.amount),
          reason: adminGrantForm.reason
        })
      });
      const data = await response.json();
      if (response.ok) {
        setAdminPointsMessage({ type: 'success', text: data.message });
        setAdminGrantForm({ twitchUsername: '', amount: '', reason: '' });
        fetchAdminPointsStats();
      } else {
        setAdminPointsMessage({ type: 'error', text: data.error });
      }
    } catch (e) {
      setAdminPointsMessage({ type: 'error', text: 'Network error' });
    } finally { setAdminPointsLoading(false); }
  };

  // ========== END ADMIN POINTS FUNCTIONS ==========

  // Admin: Create community hunt
  const createCommunityHunt = async (title, startBalance) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/community-hunt/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ title, startBalance })
      });
      const data = await response.json();
      if (response.ok) {
        fetchCommunityHunt();
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch (e) {
      return { success: false, error: 'Network error' };
    }
  };

  // Admin: Add participant to community hunt
  const addCommunityHuntParticipant = async () => {
    if (!communityHunt.hunt || !newParticipant.identifier || !newParticipant.equity) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/community-hunt/participant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          huntId: communityHunt.hunt.id,
          identifier: newParticipant.identifier,
          identifierType: newParticipant.identifierType,
          equity: parseFloat(newParticipant.equity),
          displayName: newParticipant.displayName || null
        })
      });
      
      if (response.ok) {
        setNewParticipant({ identifier: '', identifierType: 'twitch', equity: '', displayName: '' });
        setShowAddParticipantModal(false);
        fetchCommunityHunt();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to add participant');
      }
    } catch (e) {
      alert('Network error');
    }
  };

  // Admin: Update participant
  const updateCommunityHuntParticipant = async (participantId, updates) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/community-hunt/participant/${participantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        fetchCommunityHunt();
        setEditingParticipant(null);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // Admin: Remove participant
  const removeCommunityHuntParticipant = async (participantId) => {
    if (!window.confirm('Remove this participant?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/community-hunt/participant/${participantId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchCommunityHunt();
      }
    } catch (e) {
      alert('Failed to remove participant');
    }
  };

  // Admin: Update community hunt
  const updateCommunityHunt = async (updates) => {
    if (!communityHunt.hunt) return false;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/community-hunt/${communityHunt.hunt.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        fetchCommunityHunt();
        if (updates.status === 'ended') {
          fetchCommunityHuntHistory(1);
        }
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // Admin: Calculate payouts
  const calculateCommunityHuntPayouts = async (endBalance) => {
    if (!communityHunt.hunt) return false;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/community-hunt/${communityHunt.hunt.id}/calculate-payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ endBalance: parseFloat(endBalance) })
      });
      if (response.ok) {
        fetchCommunityHunt();
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // Admin: Delete community hunt
  const deleteCommunityHunt = async () => {
    if (!communityHunt.hunt) return;
    if (!window.confirm('Delete this community hunt? This cannot be undone.')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/community-hunt/${communityHunt.hunt.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchCommunityHunt();
      }
    } catch (e) {
      alert('Failed to delete hunt');
    }
  };

  // Admin: Create new challenge
  const createChallenge = async () => {
    if (!newChallenge.slotName || !newChallenge.multiplierTarget || !newChallenge.minBet || !newChallenge.prizeAmount) {
      return;
    }
    
    setChallengeActionLoading(true);
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/challenges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newChallenge)
      });
      
      if (response.ok) {
        setShowAddChallengeModal(false);
        setNewChallenge({ slotName: '', slotImageUrl: '', multiplierTarget: '', minBet: '', prizeAmount: '' });
        fetchChallenges();
      }
    } catch (e) {
      console.error('Create challenge error:', e);
    }
    
    setChallengeActionLoading(false);
  };

  // Admin: Mark challenge as complete
  const markChallengeComplete = async () => {
    if (!challengeToComplete || !completedByUsername.trim()) return;
    
    setChallengeActionLoading(true);
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/challenges/${challengeToComplete.id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ completedByUsername: completedByUsername.trim() })
      });
      
      if (response.ok) {
        setShowCompleteChallengeModal(false);
        setChallengeToComplete(null);
        setCompletedByUsername('');
        fetchChallenges();
      }
    } catch (e) {
      console.error('Complete challenge error:', e);
    }
    
    setChallengeActionLoading(false);
  };

  // Admin: Delete challenge
  const deleteChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to delete this challenge?')) return;
    
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/challenges/${challengeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchChallenges();
      }
    } catch (e) {
      console.error('Delete challenge error:', e);
    }
  };

  // Admin: Reactivate challenge
  const reactivateChallenge = async (challengeId) => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/challenges/${challengeId}/reactivate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchChallenges();
      }
    } catch (e) {
      console.error('Reactivate challenge error:', e);
    }
  };

  // Fetch challenge claims (admin)
  const fetchChallengeClaims = async (status = 'pending') => {
    const token = getAuthToken();
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/challenge-claims?status=${status}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChallengeClaims(data.claims || []);
      }
    } catch (e) {
      console.error('Fetch challenge claims error:', e);
    }
  };

  // Update claim status (admin)
  const updateClaimStatus = async (claimId, status) => {
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/challenge-claims/${claimId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        fetchChallengeClaims();
      }
    } catch (e) {
      console.error('Update claim status error:', e);
    }
  };

  // Submit challenge claim
  const submitChallengeClaim = async () => {
    if (!selectedChallenge) return;
    
    if (!claimForm.discordUsername.trim()) {
      setClaimMessage({ type: 'error', text: 'Discord username is required' });
      return;
    }
    if (!claimForm.shuffleUsername.trim()) {
      setClaimMessage({ type: 'error', text: 'Shuffle username is required' });
      return;
    }
    if (!claimForm.confirmed) {
      setClaimMessage({ type: 'error', text: 'Please confirm that you meet all requirements' });
      return;
    }
    
    setClaimSubmitting(true);
    setClaimMessage(null);
    
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/challenges/claim`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          challengeId: selectedChallenge.id,
          discordUsername: claimForm.discordUsername.trim(),
          shuffleUsername: claimForm.shuffleUsername.trim(),
          actualMultiplier: claimForm.actualMultiplier ? parseFloat(claimForm.actualMultiplier) : null,
          actualBet: claimForm.actualBet ? parseFloat(claimForm.actualBet) : null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setClaimMessage({ type: 'success', text: '🎉 Claim submitted! We will contact you on Discord to verify.' });
        setClaimForm({ discordUsername: '', shuffleUsername: '', actualMultiplier: '', actualBet: '', confirmed: false });
        setTimeout(() => {
          setShowClaimModal(false);
          setSelectedChallenge(null);
          setClaimMessage(null);
        }, 3000);
      } else {
        setClaimMessage({ type: 'error', text: data.error || 'Failed to submit claim' });
      }
    } catch (e) {
      setClaimMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
    
    setClaimSubmitting(false);
  };

  // Warm the X embed pipeline and the highlights feed during idle time on the
  // very first render, so opening the tab doesn't start from a cold script.
  useEffect(() => {
    preloadSocialEmbeds();
    const warm = () => fetchXHighlights();
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warm, { timeout: 5000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(warm, 3000);
    return () => clearTimeout(t);
  }, [fetchXHighlights]);

  // Fetch prediction data when on predictions, bonushunts, or leaderboard page
  useEffect(() => {
    if (activePage === 'bonushunts' || activePage === 'predictions' || activePage === 'leaderboard' || activePage === 'predictionresults') {
      fetchPredictionData();
      fetchLastHuntResults();
      if (activePage === 'predictionresults') {
        fetchPreviousWinners();
      }
      // Refresh faster when predictions are open, otherwise slower
      const intervalTime = predictionData.predictionsOpen ? 15000 : 60000;
      const interval = setInterval(() => {
        fetchPredictionData();
        fetchLastHuntResults();
      }, intervalTime);
      return () => clearInterval(interval);
    }
  }, [activePage, fetchPredictionData, fetchLastHuntResults, fetchPreviousWinners, predictionData.predictionsOpen]);

  // Fetch user prediction history when on mypredictions page
  useEffect(() => {
    if (activePage === 'mypredictions' && user) {
      fetchMyPredictionHistory();
    }
  }, [activePage, user, fetchMyPredictionHistory]);

  // Fetch profile stats when on profile page - also fetch prediction history for accurate stats
  useEffect(() => {
    if (activePage === 'profile' && user) {
      fetchProfileStats();
      fetchMyPredictionHistory(); // Ensure we have latest prediction data
    }
  }, [activePage, user, fetchProfileStats, fetchMyPredictionHistory]);

  // Account transfer (admin-granted): status drives the profile card, start
  // sends the user through Twitch OAuth on their NEW account.
  const [transferStatus, setTransferStatus] = useState(null);
  useEffect(() => {
    if (activePage !== 'profile' || !user) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_API_URL}/api/me/account-transfer/status`, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        if (r.ok) setTransferStatus(await r.json());
      } catch (e) {}
    })();
  }, [activePage, user, getAuthToken]);

  const startAccountTransfer = async () => {
    if (!confirm('Transfer your snutzy.gg profile to a DIFFERENT Twitch account?\n\nYou will be sent to Twitch — sign in there with the NEW account you want to move to. Your points, predictions, redemptions, giveaway history and linked accounts all move with you.\n\nThis cannot be undone.')) return;
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/me/account-transfer/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) { alert(d.error || 'Could not start the transfer'); return; }
      window.location.href = d.url;
    } catch (e) { alert('Network error — please try again'); }
  };

  // Admin: account-transfer grants + audit log
  const [xferAdmin, setXferAdmin] = useState(null);
  const [xferGrantName, setXferGrantName] = useState('');
  const fetchXferAdmin = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/admin/account-transfer/overview`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (r.ok) setXferAdmin(await r.json());
    } catch (e) {}
  }, [getAuthToken]);
  useEffect(() => {
    if (activePage === 'admin' && fraudView === 'transfers' && isAdmin) fetchXferAdmin();
  }, [activePage, fraudView, isAdmin, fetchXferAdmin]);

  // Admin: manually granted VIPs (wager requirement bypassed)
  const [vipGrants, setVipGrants] = useState(null);
  const [vipGrantName, setVipGrantName] = useState('');
  const [vipGrantReason, setVipGrantReason] = useState('');
  const fetchVipGrants = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/admin/vip/grants`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (r.ok) setVipGrants((await r.json()).grants || []);
    } catch (e) {}
  }, [getAuthToken]);
  useEffect(() => {
    if (activePage === 'admin' && adminTab === 'vip' && isAdmin) fetchVipGrants();
  }, [activePage, adminTab, isAdmin, fetchVipGrants]);

  // Fetch wager stats when on rewards or VIP dashboard page
  useEffect(() => {
    if ((activePage === 'rewards' || activePage === 'vipdashboard') && user) {
      fetchUserWagerStats();
      if (activePage === 'rewards') fetchUserWagerClaims();
      if (activePage === 'vipdashboard') fetchVipClaimStatus();
      
      // VIP dashboard: auto-refresh every 2 minutes so progress bar stays current
      // and resets correctly when the period boundary crosses
      if (activePage === 'vipdashboard') {
        const interval = setInterval(fetchUserWagerStats, 120000);
        return () => clearInterval(interval);
      }
    }
  }, [activePage, user, fetchUserWagerStats, fetchUserWagerClaims, fetchVipClaimStatus]);

  // Fetch claimable giveaways when on claim page
  useEffect(() => {
    if (activePage === 'claim' && user) {
      fetchMyClaimableGiveaways();
      collectFingerprint();
    }
  }, [activePage, user, fetchMyClaimableGiveaways, collectFingerprint]);

  // Fetch tournament data when on tournament page with auto-refresh for bracket
  useEffect(() => {
    if (activePage === 'tournament') {
      fetchTournamentInfo();
      fetchBracketData();
      fetchTournamentHistory();
      if (user) {
        fetchTournamentMyEntry();
      }
      
      // Auto-refresh bracket data every 30 seconds
      const bracketInterval = setInterval(() => {
        fetchBracketData();
      }, 30000);
      
      // Refresh tournament info every 60s
      const tournamentInterval = setInterval(() => {
        fetchTournamentInfo();
      }, 60000);
      
      return () => {
        clearInterval(bracketInterval);
        clearInterval(tournamentInterval);
      };
    }
  }, [activePage, user, fetchTournamentInfo, fetchTournamentMyEntry, fetchBracketData, fetchTournamentHistory]);

  // Fetch challenges when on challenges page
  useEffect(() => {
    if (activePage === 'highlights' && xHighlights.length === 0) {
      fetchXHighlights();
    }
    if (activePage === 'challenges') {
      fetchChallenges();
    }
  }, [activePage, fetchChallenges, fetchXHighlights, xHighlights.length]);

  // Fetch community hunt when on that page
  useEffect(() => {
    if (activePage === 'communityhunt') {
      fetchCommunityHunt();
      fetchCommunityHuntHistory(communityHuntHistoryPage);
      fetchRaffleHunt();
      fetchRaffleHistory(raffleHistoryPage);
      if (user) {
        fetchRaffleMyEntry();
      }
      
      // Auto-refresh raffle hunt when live
      const raffleInterval = setInterval(() => {
        fetchRaffleHunt();
      }, 30000);
      
      // Auto-refresh community hunt when live (for live total updates)
      const communityInterval = setInterval(() => {
        if (communityHunt?.hunt?.status === 'live' || communityHunt?.hunt?.status === 'setup') {
          fetchCommunityHunt();
        }
      }, 15000);
      
      return () => { clearInterval(raffleInterval); clearInterval(communityInterval); };
    }
  }, [activePage, user, fetchCommunityHunt, fetchCommunityHuntHistory, communityHuntHistoryPage, fetchRaffleHunt, fetchRaffleMyEntry, fetchRaffleHistory, raffleHistoryPage]);

  // ============================================
  // NEWCOMER CHECKLIST — fetch, auto-verify, submit
  // ============================================
  const fetchChecklist = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !user) return;
    try {
      const r = await fetchWithRetry(`${BACKEND_API_URL}/api/checklist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }, { key: 'checklist' });
      if (r.ok) {
        const data = await r.json();
        setChecklistData(data);
        if (data.justCompleted) {
          setChecklistCelebrate(true);
          fetchPointsBalance();
        }
        return data;
      }
    } catch (e) {
      if (!e?.superseded) console.error('Checklist fetch error:', e);
    }
    return null;
  }, [user, fetchPointsBalance]);

  // "Check again" on the Shuffle step — re-runs the guild check with visible feedback
  const recheckShuffleStep = async () => {
    setChecklistBusyStep('shuffle');
    setChecklistMsg(null);
    const data = await fetchChecklist();
    setChecklistBusyStep(null);
    if (!data) {
      setChecklistMsg({ step: 'shuffle', type: 'error', text: 'Network error — please try again.' });
    } else if (!data.checklist?.shuffle_verified) {
      setChecklistMsg({ step: 'shuffle', type: 'info', text: "Not showing under guild SNUTZY yet. Shuffle's data refreshes every few minutes — if you just joined, place a bet and check again shortly. Make sure the linked username matches your Shuffle username exactly." });
    }
  };

  // POST to an auto-verify endpoint (discord / twitch-follow) and refresh
  const verifyChecklistStep = async (step) => {
    const token = getAuthToken();
    if (!token) return;
    const endpoint = step === 'discord' ? 'verify/discord' : 'verify/twitch-follow';
    setChecklistBusyStep(step);
    setChecklistMsg(null);
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/checklist/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setChecklistMsg({ step, type: 'error', text: data.error || 'Verification failed — try again.' });
      } else if (!data.verified) {
        const reasons = {
          not_linked: 'Connect your Discord account first.',
          not_member: data.discordUsername
            ? `Your linked Discord account "${data.discordUsername}" isn't in the server. Join with that account — or if you're in the server on a different Discord account, reconnect with that one.`
            : "You're not in the Discord server yet — join it, then verify again.",
          not_following: 'Follow the channel first, then hit verify again.',
          manual_review: 'We couldn\'t check automatically — sent to the team for manual review instead.',
          unavailable: "Couldn't check right now — please try again in a minute."
        };
        setChecklistMsg({ step, type: data.reason === 'manual_review' ? 'info' : 'error', text: reasons[data.reason] || 'Verification failed — try again.' });
      }
      if (data.justCompleted) {
        setChecklistCelebrate(true);
        fetchPointsBalance();
      }
    } catch (e) {
      setChecklistMsg({ step, type: 'error', text: 'Network error — please try again.' });
    }
    setChecklistBusyStep(null);
    fetchChecklist();
  };

  // Submit a username for a manually verified step (Twitter/Instagram/YouTube)
  const submitChecklistStep = async (step) => {
    const token = getAuthToken();
    const username = (checklistInputs[step] || '').trim();
    if (!token || !username) return;
    setChecklistBusyStep(step);
    setChecklistMsg(null);
    try {
      const r = await fetch(`${BACKEND_API_URL}/api/checklist/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ step, username })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setChecklistMsg({ step, type: 'error', text: data.error || 'Failed to submit' });
      } else {
        setChecklistInputs(prev => ({ ...prev, [step]: '' }));
      }
    } catch (e) {
      setChecklistMsg({ step, type: 'error', text: 'Network error — please try again.' });
    }
    setChecklistBusyStep(null);
    fetchChecklist();
  };

  // Progress: how many of the 7 steps are verified
  const checklistRow = checklistData?.checklist || null;
  const checklistDoneCount = checklistRow
    ? CHECKLIST_STEP_DEFS.filter(s => checklistRow[s.verifiedKey]).length
    : 0;

  // First-visit welcome modal: logged-out users only, once per browser, 2s delay.
  // Flag is written when the modal is SHOWN so it can never nag twice.
  useEffect(() => {
    if (authLoading || user) return;
    if (localStorage.getItem('snutzy_visited')) return;
    const t = setTimeout(() => {
      localStorage.setItem('snutzy_visited', '1');
      setShowWelcomeModal(true);
    }, 2000);
    return () => clearTimeout(t);
  }, [authLoading, user]);

  // Fetch points balance, discord status and checklist whenever user logs in
  useEffect(() => {
    if (user) {
      fetchPointsBalance();
      fetchDiscordStatus();
      fetchKickStatus();
      fetchChecklist();
    }
  }, [user, fetchPointsBalance, fetchDiscordStatus, fetchChecklist]);

  // Re-check auto-verified steps (Shuffle guild, Discord server) each time the checklist
  // opens — state loaded at login goes stale once they link/join in another tab.
  useEffect(() => {
    if (showChecklistPanel && user) fetchChecklist();
  }, [showChecklistPanel, user, fetchChecklist]);

  // Fetch raffle/community hunt and tournament status on app mount for sidebar badges
  // Stagger these so they don't compete with the main page data loading
  useEffect(() => {
    const t1 = setTimeout(fetchRaffleHunt, 2000);
    const t2 = setTimeout(fetchCommunityHunt, 3000);
    const t3 = setTimeout(fetchTournamentInfo, 4000);
    const t4 = setTimeout(fetchViewerHunt, 5000);
    const t5 = setTimeout(fetchCg, 6000);
    // Periodic refresh of viewer hunt + giveaway status for sidebar badge (every 30s when not on the page)
    const interval = setInterval(() => {
      if (activePage !== 'viewerhunt') fetchViewerHunt();
      if (activePage !== 'chatgiveaway') fetchCg();
    }, 30000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); clearInterval(interval); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch points shop data when on points page
  useEffect(() => {
    if (activePage === 'pointsshop') {
      fetchPointsShop();
      fetchRecentRedemptions();
      fetch(`${BACKEND_API_URL}/api/points/config`).then(r => r.json()).then(setPointsEarnConfig).catch(() => {});
      // Stagger secondary fetches
      const t1 = setTimeout(fetchPointsLeaderboard, 500);
      if (user) {
        fetchPointsBalance();
        const t2 = setTimeout(() => fetchPointsHistory(1), 300);
        const t3 = setTimeout(fetchMyRedemptions, 600);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
      }
      return () => clearTimeout(t1);
    }
  }, [activePage, user, fetchPointsShop, fetchPointsLeaderboard, fetchRecentRedemptions, fetchPointsBalance, fetchPointsHistory, fetchMyRedemptions]);

  // Submit prediction for a specific slot
  const submitPrediction = async (slot = 1) => {
    if (!user) {
      setPredictionMessage({ type: 'error', text: 'Please login with Twitch first!' });
      return;
    }
    
    if (!predictionData.activeHunt) {
      setPredictionMessage({ type: 'error', text: 'No active hunt to predict on!' });
      return;
    }
    
    // Double-check predictions are still open (in case user has stale page)
    if (!predictionData.predictionsOpen) {
      setPredictionMessage({ type: 'error', text: 'Predictions are currently closed. Please refresh the page.' });
      fetchPredictionData(); // Refresh to get latest status
      return;
    }
    
    // Get the input value for this slot
    const inputValue = slot === 1 ? predictionInput : predictionInputs[slot];
    const amount = parseFloat(inputValue);
    if (isNaN(amount) || amount < 0) {
      setPredictionMessage({ type: 'error', text: 'Please enter a valid amount!' });
      return;
    }
    
    // Check if slot > 1 requires a charge
    if (slot > 1) {
      const existingPred = predictionData.myPredictions.find(p => p.slot === slot);
      if (!existingPred && predictionData.availableCharges <= 0) {
        setPredictionMessage({ type: 'error', text: 'No extra prediction slots available! Redeem channel points for "Extra Prediction".' });
        return;
      }
    }
    
    setPredictionSubmitting(true);
    setPredictionMessage(null);
    
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/predictions/submit`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          huntId: predictionData.activeHunt.huntId,
          predictedBalance: amount,
          slot: slot
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPredictionMessage({ type: 'success', text: slot > 1 ? `Extra prediction #${slot} submitted!` : 'Prediction submitted!' });
        if (slot === 1) {
          setPredictionInput('');
        } else {
          setPredictionInputs(prev => ({ ...prev, [slot]: '' }));
        }
        fetchPredictionData(); // Refresh data
      } else {
        setPredictionMessage({ type: 'error', text: data.error || 'Failed to submit prediction' });
        // Refresh prediction data in case status changed
        fetchPredictionData();
      }
    } catch (e) {
      setPredictionMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
    
    setPredictionSubmitting(false);
  };

  // ============================================
  // ADMIN FUNCTIONS
  // ============================================

  // Fetch admin data
  const fetchAdminData = useCallback(async () => {
    if (!isAdmin && !isModerator) return; // moderators get hunts (other fetches 403 harmlessly)
    
    const token = getAuthToken();
    if (!token) return;
    
    try {
      // Get stats
      const statsResponse = await fetch(`${BACKEND_API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = statsResponse.ok ? await statsResponse.json() : null;
      
      // Get hunts
      const huntsResponse = await fetch(`${BACKEND_API_URL}/api/admin/hunts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const huntsData = huntsResponse.ok ? await huntsResponse.json() : { hunts: [] };
      
      // Get users
      const usersResponse = await fetch(`${BACKEND_API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const usersData = usersResponse.ok ? await usersResponse.json() : { users: [], total: 0 };
      
      setAdminData(prev => ({
        ...prev,
        stats: statsData,
        hunts: huntsData.hunts || [],
        users: usersData.users || [],
        usersTotal: usersData.total || 0
      }));
    } catch (e) {
      console.error('Fetch admin data error:', e);
    }
  }, [isAdmin, isModerator]);

  // Actionable queue counts across all claim systems (admin sidebar badge —
  // total is the sum of every queue badge shown on the admin tabs)
  const fetchPendingCounts = useCallback(async () => {
    if (!isAdmin) return;
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/admin/action-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAdminPendingCounts(await res.json());
    } catch (e) { /* badge is best-effort */ }
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) fetchPendingCounts(); }, [isAdmin, fetchPendingCounts]);

  // Fetch admin data when on admin page (moderators only need hunts + featured posts)
  useEffect(() => {
    if (activePage === 'admin' && (isAdmin || isModerator)) {
      fetchAdminData();
      if (!isAdmin) return;
      fetchPendingClaims();
      fetchPaidClaims();
      fetchGiveaways('pending');
      fetchGiveaways('paid');
      fetchUnclaimedWins();
      fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
      fetchSlotPicksStats();
      fetchTransferRequests();
      // Load points admin data
      fetchAdminPointsConfig();
      fetchAdminPointsStats();
      fetchAdminShopItems();
      fetchAdminPointsRedemptions('pending', 1);
      fetchAdminWagerClaims();
      fetchAdminLeaderboardTotal();
      fetchAdminWagerBulk();
      fetchAdminBulkAdjustments();
      fetchAdminClaimableGiveaways('');
    }
  }, [activePage, isAdmin, isModerator, fetchAdminData]);

  // Auto-refresh slot picks every 10 seconds when viewing the tab
  useEffect(() => {
    if (activePage === 'admin' && adminTab === 'slotPicks' && isAdmin) {
      const interval = setInterval(() => {
        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
        fetchSlotPicksStats();
      }, 10000); // 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [activePage, adminTab, isAdmin, adminSlotPicksFilter, adminSlotPicksPage]);

  // Fetch public slot picks when on that page
  useEffect(() => {
    if (activePage === 'slotpicks') {
      fetchPublicSlotPicks();
    }
  }, [activePage]);

  // Auto-refresh public slot picks every 15 seconds
  useEffect(() => {
    if (activePage === 'slotpicks') {
      const interval = setInterval(() => {
        fetchPublicSlotPicks();
      }, 15000);
      
      return () => clearInterval(interval);
    }
  }, [activePage]);

  // Also fetch public slot picks stats periodically for sidebar badge
  useEffect(() => {
    fetchPublicSlotPicks(); // Initial fetch
    const interval = setInterval(() => {
      fetch(`${BACKEND_API_URL}/api/slot-picks/stats`)
        .then(r => r.json())
        .then(data => setPublicSlotPicksStats(data))
        .catch(() => {});
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Fetch user's wins when logged in or when visiting claim page
  useEffect(() => {
    if (user) {
      fetchMyWins();
    }
  }, [user, activePage]);

  // Fetch payout stats on predictions page
  useEffect(() => {
    if (activePage === 'predictions') {
      fetchPayoutStats();
    }
  }, [activePage]);

  // Show Shuffle username modal if user is logged in but hasn't linked their account
  useEffect(() => {
    if (user && !user.shuffleUsername && !authLoading) {
      // Small delay to not overwhelm user immediately on login
      const timer = setTimeout(() => {
        setShowShuffleModal(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user, authLoading]);

  // Save Shuffle username
  const saveShuffleUsername = async () => {
    if (!shuffleUsernameInput.trim()) {
      alert('Please enter your Shuffle username');
      return;
    }
    
    setSavingShuffleUsername(true);
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/user/shuffle-username`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ shuffleUsername: shuffleUsernameInput.trim() })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local user state
        setUser(prev => ({ ...prev, shuffleUsername: data.shuffleUsername }));
        setShowShuffleModal(false);
        setShuffleUsernameInput('');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to save username');
      }
    } catch (e) {
      alert('Failed to save Shuffle username');
    }
    
    setSavingShuffleUsername(false);
  };

  // Admin: Open predictions
  const adminOpenPredictions = async (huntId, startBalance) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/predictions/open`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          huntId, 
          startBalance,
          rewards: adminRewards
        })
      });
      
      if (response.ok) {
        fetchAdminData();
        fetchPredictionData();
        alert('Predictions opened!');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to open predictions');
      }
    } catch (e) {
      alert('Network error');
    }
  };

  // Admin: Pause predictions
  const adminPausePredictions = async (huntId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/predictions/pause`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ huntId })
      });
      
      if (response.ok) {
        fetchAdminData();
        alert('Predictions paused!');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to pause predictions');
      }
    } catch (e) {
      alert('Network error');
    }
  };

  // Admin: Close hunt and calculate winners
  const adminCloseHunt = async (huntId, endBalance) => {
    if (!endBalance || isNaN(parseFloat(endBalance))) {
      alert('Please enter a valid end balance');
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/predictions/close-hunt`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ huntId, endBalance: parseFloat(endBalance) })
      });
      
      if (response.ok) {
        const data = await response.json();
        fetchAdminData();
        fetchPredictionData();
        fetchLastHuntResults(); // Refresh results immediately
        alert(`Hunt closed! ${data.winnersCount} winners calculated.`);
        setAdminEndBalanceInput('');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to close hunt');
      }
    } catch (e) {
      alert('Network error');
    }
  };

  // Admin: View hunt predictions
  const adminViewHuntPredictions = async (huntId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/predictions/all/${huntId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({
          ...prev,
          selectedHunt: data.hunt,
          huntPredictions: data.predictions || []
        }));
      }
    } catch (e) {
      console.error('Fetch hunt predictions error:', e);
    }
  };

  // Admin: Delete prediction
  const adminDeletePrediction = async (predictionId) => {
    if (!window.confirm('Are you sure you want to delete this prediction?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/predictions/${predictionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Refresh the hunt predictions
        if (adminData.selectedHunt) {
          adminViewHuntPredictions(adminData.selectedHunt.hunt_id);
        }
        fetchAdminData();
      }
    } catch (e) {
      alert('Failed to delete prediction');
    }
  };

  // Admin: Delete entire hunt with all predictions
  const adminDeleteHunt = async (huntId, predictionCount) => {
    const confirmMsg = `⚠️ DELETE HUNT #${huntId}?\n\nThis will permanently delete:\n- The hunt record\n- All ${predictionCount} predictions\n\nThis action cannot be undone!\n\nType "DELETE" to confirm:`;
    const userInput = window.prompt(confirmMsg);
    
    if (userInput !== 'DELETE') {
      if (userInput !== null) {
        alert('Deletion cancelled. You must type "DELETE" exactly to confirm.');
      }
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/hunts/${huntId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ ${data.message}`);
        fetchAdminData();
        // Clear selected hunt if it was the deleted one
        if (adminData.selectedHunt?.hunt_id === huntId) {
          setAdminData(prev => ({ ...prev, selectedHunt: null, huntPredictions: [] }));
        }
      } else {
        alert(`❌ ${data.error || 'Failed to delete hunt'}`);
      }
    } catch (e) {
      alert('Failed to delete hunt');
    }
  };

  // Admin: Edit hunt start balance
  const adminEditHuntStartBalance = async (huntId, currentStartBalance) => {
    const newBalance = window.prompt(
      `Edit Start Balance for Hunt #${huntId}\n\nCurrent: $${currentStartBalance?.toLocaleString() || 'N/A'}\n\nEnter new start balance:`,
      currentStartBalance || ''
    );
    
    if (newBalance === null) return; // Cancelled
    
    const parsed = parseFloat(newBalance);
    if (isNaN(parsed) || parsed < 0) {
      alert('Please enter a valid positive number');
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/hunts/${huntId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ startBalance: parsed })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ Start balance updated to $${parsed.toLocaleString()}`);
        fetchAdminData();
        fetchLastHuntResults(); // Refresh prediction results
        fetchPreviousWinners(); // Refresh previous winners
      } else {
        alert(`❌ ${data.error || 'Failed to update hunt'}`);
      }
    } catch (e) {
      alert('Failed to update hunt');
    }
  };

  // Admin: Get winners for a hunt
  const adminViewHuntWinners = async (huntId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/${huntId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({
          ...prev,
          selectedHuntWinners: huntId,
          huntWinners: data.winners || []
        }));
      }
    } catch (e) {
      console.error('Failed to fetch winners:', e);
    }
  };

  // Admin: Delete/Disqualify a winner
  const adminDisqualifyWinner = async (winnerId, huntId) => {
    if (!window.confirm('Are you sure you want to disqualify this winner? This will remove them from the winners list.')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/${winnerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('Winner disqualified');
        adminViewHuntWinners(huntId);
        fetchAdminData();
      }
    } catch (e) {
      alert('Failed to disqualify winner');
    }
  };

  // Admin: Recalculate winners for a hunt
  const adminRecalculateWinners = async (huntId) => {
    if (!window.confirm('This will recalculate all winners for this hunt based on current predictions. Continue?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/recalculate/${huntId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Winners recalculated! ${data.winnersCount} winners determined.`);
        adminViewHuntWinners(huntId);
        fetchAdminData();
      }
    } catch (e) {
      alert('Failed to recalculate winners');
    }
  };

  // Admin: Update winner reward
  const adminUpdateWinnerReward = async (winnerId, newReward, huntId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/${winnerId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reward: newReward })
      });
      
      if (response.ok) {
        adminViewHuntWinners(huntId);
      }
    } catch (e) {
      alert('Failed to update winner');
    }
  };

  // Fetch user's wins
  const fetchMyWins = async () => {
    const token = getAuthToken();
    if (!token) return;
    
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/predictions/my-wins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyWins(data.wins || []);
        
        // If there are unclaimed wins, show the modal
        const unclaimed = (data.wins || []).filter(w => !w.claimed);
        if (unclaimed.length > 0) {
          setSelectedWinToClaim(unclaimed[0]);
          // Pre-fill Shuffle username from user profile if linked
          if (user?.shuffleUsername) {
            setClaimShuffleUsername(user.shuffleUsername);
          }
          setShowWinModal(true);
        }
      }
    } catch (e) {
      console.error('Failed to fetch wins:', e);
    }
  };

  // Claim a win
  const claimWin = async () => {
    if (!selectedWinToClaim || !claimShuffleUsername.trim()) {
      alert('Please enter your Shuffle username');
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/predictions/claim-win`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          winnerId: selectedWinToClaim.id,
          shuffleUsername: claimShuffleUsername.trim()
        })
      });
      
      if (response.ok) {
        alert('🎉 Prize claimed! An admin will process your reward. Payouts can take up to 14 days.');
        setShowWinModal(false);
        setClaimShuffleUsername('');
        setSelectedWinToClaim(null);
        fetchMyWins(); // Refresh wins
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to claim prize');
      }
    } catch (e) {
      alert('Failed to claim prize');
    }
  };

  // Claim win using already-linked Shuffle username
  const claimWinAuto = async () => {
    if (!selectedWinToClaim || !user?.shuffleUsername) {
      alert('No Shuffle username linked');
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/predictions/claim-win`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          winnerId: selectedWinToClaim.id,
          shuffleUsername: user.shuffleUsername
        })
      });
      
      if (response.ok) {
        alert('🎉 Prize claimed! An admin will send your reward to ' + user.shuffleUsername + '. Payouts can take up to 14 days.');
        setShowWinModal(false);
        setSelectedWinToClaim(null);
        fetchMyWins(); // Refresh wins
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to claim prize');
      }
    } catch (e) {
      alert('Failed to claim prize');
    }
  };

  // Admin: Fetch pending claims
  const fetchPendingClaims = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/claims/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({ ...prev, pendingClaims: data.claims || [] }));
      }
    } catch (e) {
      console.error('Failed to fetch pending claims:', e);
    }
  };

  // Admin: Fetch paid claims
  const fetchPaidClaims = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/claims/paid`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({ ...prev, paidClaims: data.claims || [] }));
      }
    } catch (e) {
      console.error('Failed to fetch paid claims:', e);
    }
  };

  // Admin: Mark claim as paid. Accepts the full claim row so the confirmation
  // can say exactly what is about to happen (who, how much, where).
  const adminMarkClaimPaid = async (claim) => {
    const claimId = typeof claim === 'object' ? claim.id : claim;
    const summary = typeof claim === 'object'
      ? `Pay ${claim.display_name || claim.username} ${claim.actual_reward || claim.reward}${claim.casino_username ? ` (${claim.platform === 'shuffle' || claim.upgraded ? 'Shuffle' : 'Rainbet'}: ${claim.casino_username})` : ''} and mark the claim as paid?`
      : 'Mark this claim as paid?';
    if (!window.confirm(summary)) return;

    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/claims/${claimId}/pay`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchPendingClaims();
        fetchPaidClaims();
        fetchAdminData();
      }
    } catch (e) {
      alert('Failed to mark claim as paid');
    }
  };

  // Admin: Fetch giveaways
  const fetchGiveaways = async (status = null) => {
    const token = getAuthToken();
    try {
      const url = status 
        ? `${BACKEND_API_URL}/api/admin/giveaways?status=${status}`
        : `${BACKEND_API_URL}/api/admin/giveaways`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (status === 'pending') {
          setAdminData(prev => ({ ...prev, pendingGiveaways: data.giveaways || [] }));
        } else if (status === 'paid') {
          setAdminData(prev => ({ ...prev, paidGiveaways: data.giveaways || [] }));
        } else {
          setAdminData(prev => ({ ...prev, allGiveaways: data.giveaways || [] }));
        }
      }
    } catch (e) {
      console.error('Failed to fetch giveaways:', e);
    }
  };

  // Admin: Search paid giveaways
  const searchPaidGiveaways = async (searchTerm) => {
    const token = getAuthToken();
    try {
      let url = `${BACKEND_API_URL}/api/admin/giveaways?status=paid`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({ ...prev, paidGiveaways: data.giveaways || [] }));
      }
    } catch (e) {
      console.error('Failed to search giveaways:', e);
    }
  };

  // Admin: Fetch unclaimed wins
  const fetchUnclaimedWins = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/unclaimed-wins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({ ...prev, unclaimedWins: data.unclaimedWins || [] }));
      }
    } catch (e) {
      console.error('Failed to fetch unclaimed wins:', e);
    }
  };

  // Admin: Pay unclaimed win manually
  const payUnclaimedWin = async (winnerId, username, reward) => {
    if (!window.confirm(`Mark as PAID for ${username}?\n\nReward: ${reward}\n\nThis will create a claim and mark it as paid.`)) {
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/unclaimed-wins/${winnerId}/pay`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ ${data.message}`);
        fetchUnclaimedWins();
        fetchAdminData();
        fetchPendingCounts();
      } else {
        alert(`❌ ${data.error || 'Failed to mark as paid'}`);
      }
    } catch (e) {
      alert('Failed to mark as paid');
    }
  };

  // Admin: Void an unclaimed win — the user will no longer be able to claim it
  const voidUnclaimedWin = async (winnerId, username, reward) => {
    if (!window.confirm(`Void ${username}'s unclaimed ${reward || 'win'}?\n\nThe win leaves the unclaimed queue and ${username} will no longer be able to claim it. Payout totals are not affected.`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/unclaimed-wins/${winnerId}/void`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        alert(`🗑️ ${data.message}`);
        fetchUnclaimedWins();
        fetchAdminData();
        fetchPendingCounts();
      } else {
        alert(`❌ ${data.error || 'Failed to void win'}`);
      }
    } catch (e) {
      alert('Failed to void win');
    }
  };

  // Public: Fetch slot picks queue (no auth required)
  const fetchPublicSlotPicks = async () => {
    try {
      const [queueResponse, statsResponse] = await Promise.all([
        fetch(`${BACKEND_API_URL}/api/slot-picks/queue`),
        fetch(`${BACKEND_API_URL}/api/slot-picks/stats`)
      ]);
      
      if (queueResponse.ok) {
        const queueData = await queueResponse.json();
        setPublicSlotPicks(queueData.queue || []);
      }
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setPublicSlotPicksStats(statsData);
        
        // Track session high watermark for progress bar
        const pendingCount = statsData.pending || 0;
        setSlotPicksSessionMax(prevMax => {
          // Reset if queue hit 0, otherwise update max if current is higher
          if (pendingCount === 0) {
            return 0;
          } else if (pendingCount > prevMax) {
            return pendingCount;
          }
          return prevMax;
        });
      }
    } catch (e) {
      console.error('Failed to fetch public slot picks:', e);
    }
  };

  // Admin: Fetch slot picks
  const fetchSlotPicks = async (status = null, page = 1) => {
    const token = getAuthToken();
    try {
      let url = `${BACKEND_API_URL}/api/admin/slot-picks?page=${page}&limit=${adminItemsPerPage}`;
      if (status) url += `&status=${status}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({ ...prev, slotPicks: data.slotPicks || [] }));
        setAdminSlotPicksPagination(data.pagination || { total: 0, totalPages: 1 });
      }
    } catch (e) {
      console.error('Failed to fetch slot picks:', e);
    }
  };

  // Admin: Fetch slot picks stats
  const fetchSlotPicksStats = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({ ...prev, slotPicksStats: data }));
      }
    } catch (e) {
      console.error('Failed to fetch slot picks stats:', e);
    }
  };

  // Admin: Complete slot pick (checkmark - moves to completed)
  const completeSlotPick = async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/${id}/complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
        fetchSlotPicksStats();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to complete slot pick');
      }
    } catch (e) {
      alert('Failed to complete slot pick');
    }
  };

  // Admin: Remove slot pick (X - didn't hit bonus, just removes)
  const removeSlotPick = async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/${id}/remove`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
        fetchSlotPicksStats();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to remove slot pick');
      }
    } catch (e) {
      alert('Failed to remove slot pick');
    }
  };

  // Admin: Mark for refund
  const refundSlotPick = async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/${id}/refund`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
        fetchSlotPicksStats();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to mark for refund');
      }
    } catch (e) {
      alert('Failed to mark for refund');
    }
  };

  // Admin: Complete refund
  const completeRefund = async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/${id}/refund-complete`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
        fetchSlotPicksStats();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to complete refund');
      }
    } catch (e) {
      alert('Failed to complete refund');
    }
  };

  // Admin: Open pay modal for completed slot pick
  const openSlotPickPayModal = (pick) => {
    setSelectedSlotPickToPay(pick);
    setSlotPickPayAmount('');
    setShowSlotPickPayModal(true);
  };

  // Admin: Pay slot pick
  const paySlotPick = async () => {
    if (!selectedSlotPickToPay || !slotPickPayAmount || parseFloat(slotPickPayAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/${selectedSlotPickToPay.id}/pay`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rewardAmount: slotPickPayAmount })
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowSlotPickPayModal(false);
        setSelectedSlotPickToPay(null);
        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
        fetchSlotPicksStats();
        alert(`✅ Paid $${slotPickPayAmount} to ${data.shuffleUsername || 'user (no Shuffle username)'}`);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to pay slot pick');
      }
    } catch (e) {
      alert('Failed to pay slot pick');
    }
  };

  // Admin: Delete slot pick permanently
  const deleteSlotPick = async (id) => {
    if (!window.confirm('Permanently delete this slot pick?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
        fetchSlotPicksStats();
      }
    } catch (e) {
      alert('Failed to delete slot pick');
    }
  };

  // Submit transfer request (public)
  const submitTransferRequest = async () => {
    if (!transferRequest.shuffleUsername.trim()) {
      setTransferMessage({ type: 'error', text: 'Please enter your Shuffle username' });
      return;
    }
    
    if (!transferRequest.discordUsername.trim()) {
      setTransferMessage({ type: 'error', text: 'Please enter your Discord username for contact purposes' });
      return;
    }
    
    if (!transferRequest.confirmed) {
      setTransferMessage({ type: 'error', text: 'Please confirm that you meet the requirements' });
      return;
    }
    
    setTransferSubmitting(true);
    setTransferMessage(null);
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${BACKEND_API_URL}/api/transfer-request`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shuffleUsername: transferRequest.shuffleUsername.trim(),
          discordUsername: transferRequest.discordUsername.trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTransferMessage({ type: 'success', text: 'Transfer request submitted! We will review it and contact you on Discord if needed.' });
        setTransferRequest({ shuffleUsername: '', discordUsername: '', confirmed: false });
      } else {
        setTransferMessage({ type: 'error', text: data.error || 'Failed to submit request' });
      }
    } catch (e) {
      setTransferMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
    
    setTransferSubmitting(false);
  };

  // Admin: Fetch transfer requests
  const fetchTransferRequests = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/transfer-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({ ...prev, transferRequests: data.transferRequests || [] }));
      }
    } catch (e) {
      console.error('Failed to fetch transfer requests:', e);
    }
  };

  // Admin: Update transfer request status
  const updateTransferRequest = async (id, status, notes = null) => {
    const statusText = status === 'sent' ? 'SENT TO SHUFFLE' : status === 'rejected' ? 'REJECTED' : status;
    if (!window.confirm(`Mark this transfer request as ${statusText}?`)) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/transfer-requests/${id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, notes })
      });
      
      if (response.ok) {
        fetchTransferRequests();
      } else {
        alert('Failed to update transfer request');
      }
    } catch (e) {
      alert('Failed to update transfer request');
    }
  };

  // Admin: Delete transfer request
  const deleteTransferRequest = async (id) => {
    if (!window.confirm('Delete this transfer request?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/transfer-requests/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchTransferRequests();
      }
    } catch (e) {
      alert('Failed to delete transfer request');
    }
  };

  // Admin: Fetch analytics data
  const fetchAnalytics = async (options = {}) => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    const token = getAuthToken();

    let url = `${BACKEND_API_URL}/api/admin/analytics?limit=100`;
    
    if (options.quickPeriod) {
      url += `&quickPeriod=${options.quickPeriod}`;
      setAnalyticsMode('quick');
    } else if (options.startDate && options.endDate) {
      url += `&startDate=${options.startDate}&endDate=${options.endDate}`;
      setAnalyticsMode('custom');
    } else {
      const period = options.period !== undefined ? options.period : 0;
      url += `&period=${period}`;
      setAnalyticsPeriod(period);
      setAnalyticsMode('periods');
    }
    
    url += `&page=${options.page || 1}`;
    
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
        setAnalyticsPage(options.page || 1);
      } else {
        const err = await response.json().catch(() => ({}));
        console.error('Failed to fetch analytics:', err.error || response.status);
        setAnalyticsData(null);
        setAnalyticsError(err.error || `Request failed (${response.status})`);
      }
    } catch (e) {
      console.error('Analytics fetch error:', e);
      setAnalyticsData(null);
      setAnalyticsError(e.message);
    }
    setAnalyticsLoading(false);
  };

  // Fetch custom date range analytics
  const fetchCustomAnalytics = () => {
    if (!analyticsCustomStart || !analyticsCustomEnd) {
      alert('Please select both start and end dates');
      return;
    }
    fetchAnalytics({ startDate: analyticsCustomStart, endDate: analyticsCustomEnd, page: 1 });
  };

  const fetchPeriodComparison = async () => {
    const token = getAuthToken();
    setCompareLoading(true);
    setCompareData(null);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/analytics/compare?top=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompareData(data);
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to compare');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setCompareLoading(false);
  };

  const fetchLeaderboardSnapshots = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/leaderboard-snapshots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeaderboardSnapshots(data.snapshots || []);
      }
    } catch (e) { console.error('Snapshots error:', e); }
  };

  const takeLeaderboardSnapshot = async (type) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/admin/leaderboard-snapshots/take`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type })
      });
      if (res.ok) {
        alert('✅ Snapshot taken!');
        fetchLeaderboardSnapshots();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed');
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  // Admin: Add giveaway
  const adminAddGiveaway = async () => {
    if (!newGiveaway.amount || parseFloat(newGiveaway.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    if (newGiveaway.recipientType === 'shuffle' && !newGiveaway.shuffleUsername.trim()) {
      alert('Please enter a Shuffle username');
      return;
    }
    
    if (newGiveaway.recipientType === 'crypto' && !newGiveaway.cryptoAddress.trim()) {
      alert('Please enter a crypto address');
      return;
    }
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/giveaways`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipientType: newGiveaway.recipientType,
          shuffleUsername: newGiveaway.shuffleUsername || null,
          cryptoAddress: newGiveaway.cryptoAddress || null,
          amount: parseFloat(newGiveaway.amount),
          description: newGiveaway.description || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert('Giveaway added!');
        setNewGiveaway({
          recipientType: 'shuffle',
          shuffleUsername: '',
          cryptoAddress: '',
          amount: '',
          description: ''
        });
        fetchGiveaways('pending');
        fetchAdminData();
      } else {
        alert(data.error || 'Failed to add giveaway');
      }
    } catch (e) {
      console.error('Add giveaway error:', e);
      alert('Failed to add giveaway: ' + e.message);
    }
  };

  // Admin: Mark giveaway as paid
  const adminMarkGiveawayPaid = async (giveawayId) => {
    if (!window.confirm('Mark this giveaway as paid?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/giveaways/${giveawayId}/pay`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchGiveaways('pending');
        fetchGiveaways('paid');
        fetchAdminData();
      }
    } catch (e) {
      alert('Failed to mark giveaway as paid');
    }
  };

  // Admin: Delete giveaway
  const adminDeleteGiveaway = async (giveawayId) => {
    if (!window.confirm('Delete this giveaway?')) return;
    
    const token = getAuthToken();
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/giveaways/${giveawayId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchGiveaways('pending');
        fetchAdminData();
      }
    } catch (e) {
      alert('Failed to delete giveaway');
    }
  };

  // Admin: Search users
  const adminSearchUsers = async (searchTerm) => {
    const token = getAuthToken();
    try {
      const url = searchTerm 
        ? `${BACKEND_API_URL}/api/admin/users?search=${encodeURIComponent(searchTerm)}`
        : `${BACKEND_API_URL}/api/admin/users`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({
          ...prev,
          users: data.users || [],
          usersTotal: data.total || 0
        }));
      }
    } catch (e) {
      console.error('Search users error:', e);
    }
  };

  // Admin: Get user details
  const adminViewUserDetails = async (userId) => {
    const token = getAuthToken();
    setAdminUserWagerStats(null);
    setAdminUserWagerLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAdminData(prev => ({
          ...prev,
          selectedUser: userId,
          selectedUserDetails: data
        }));
      }
    } catch (e) {
      console.error('Get user details error:', e);
    }
    // Shuffle wager + giveaway stats — slower (hits Shuffle), so loaded separately
    try {
      const wr = await fetch(`${BACKEND_API_URL}/api/admin/users/${userId}/wager-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (wr.ok) setAdminUserWagerStats(await wr.json());
    } catch (e) {
      console.error('Get user wager stats error:', e);
    }
    setAdminUserWagerLoading(false);
  };

  // Admin: force the server to (re)fetch the rolling 24h/7d wager windows from
  // Shuffle, staggered past the rate limit, then reload this user's stats.
  const adminWarmWagerRanges = async () => {
    const userId = adminData.selectedUser;
    const token = getAuthToken();
    setAdminWagerWarming(true);
    try {
      await fetch(`${BACKEND_API_URL}/api/admin/wager-ranges/warm`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error('Warm wager ranges error:', e); }
    // The two fetches are staggered (~12s apart) server-side; reload after they finish.
    setTimeout(async () => {
      try {
        const wr = await fetch(`${BACKEND_API_URL}/api/admin/users/${userId}/wager-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (wr.ok) setAdminUserWagerStats(await wr.json());
      } catch (e) { console.error('Reload wager stats error:', e); }
      setAdminWagerWarming(false);
    }, 20000);
  };

  // Admin: Close user details
  const adminCloseUserDetails = () => {
    setAdminData(prev => ({
      ...prev,
      selectedUser: null,
      selectedUserDetails: null
    }));
    setAdminUserWagerStats(null);
    setAdminUserWagerLoading(false);
  };

  const casinoGames = ['Blackjack', 'Roulette', 'CRAPS', 'Dice', 'Baccarat', 'GOD MODE', 'Feature Spins'];
  const gameColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#FF4500', '#9370DB', '#FF1493', '#00CED1'];

  // Slot data for Slot Picker
// Slot catalog lives in public/slots-catalog.json, loaded via useSlotCatalog()

  // Get unique providers
  // Providers sorted by catalog size (biggest first) with per-provider slot counts
  // Clutch Gaming merged into Backseat Gaming (Oct 2026); a name that isn't in
  // the live catalog is simply skipped.
  const MAIN_PROVIDERS = ['Pragmatic Play', 'Hacksaw Gaming', 'Nolimit City', 'BGaming', 'Backseat Gaming', 'Bullshark Games', 'ShadyLady', 'Print Studios', 'Peter & Sons', 'Push Gaming'];

  // The catalog keeps slots Shuffle has delisted (flagged `legacy`) so old battle
  // and hunt records still resolve — the picker only ever offers what's live, so
  // a provider Shuffle drops (3 Oaks, Spinomenal, …) leaves the list on the next
  // weekly catalog refresh and comes back by itself if Shuffle re-adds it.
  const allSlots = useSlotCatalog();
  const liveSlots = React.useMemo(() => allSlots.filter(s => !s.legacy), [allSlots]);
  const providerSlotCounts = liveSlots.reduce((acc, s) => { acc[s.provider] = (acc[s.provider] || 0) + 1; return acc; }, {});
  // Biggest catalog first
  const allProviders = Object.keys(providerSlotCounts).sort((a, b) => providerSlotCounts[b] - providerSlotCounts[a]);
  const spookyProviderCounts = React.useMemo(() => (HALLOWEEN_ON
    ? liveSlots.filter(isSpookySlot).reduce((acc, s) => { acc[s.provider] = (acc[s.provider] || 0) + 1; return acc; }, {})
    : {}), [liveSlots]);

  // Spin Zone state
  const [spinZoneGame, setSpinZoneGame] = useState(initialSpinZoneGame); // null = selection screen, 'rejuicer' or 'slots'
  // Default view is the studios Snutzy actually plays, buy-feature slots only.
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [providerDefaultsApplied, setProviderDefaultsApplied] = useState(false);
  const [providerSearch, setProviderSearch] = useState('');
  const [bonusBuyOnly, setBonusBuyOnly] = useState(true);
  // Seasonal: narrows the reel to spooky titles (see Halloween.jsx). Only offered
  // while HALLOWEEN_ON, so it can't stay stuck on after the season.
  const [spookyOnly, setSpookyOnly] = useState(false);
  const spookyActive = HALLOWEEN_ON && spookyOnly;
  const [slotSpinning, setSlotSpinning] = useState(false);
  const [slotResult, setSlotResult] = useState(null);
  const [slotResultPopup, setSlotResultPopup] = useState(false); // Controls popup visibility
  const [slotRotation, setSlotRotation] = useState(-54000); // Start 10 sets in (10 * 30 * 180)
  const [slotIsResetting, setSlotIsResetting] = useState(false);
  const [shuffleSeed, setShuffleSeed] = useState(0); // Triggers re-shuffle
  const slotAudioContextRef = useRef(null);
  const slotTickIntervalRef = useRef(null);

  // Shuffle function using Fisher-Yates algorithm
  const shuffleArray = useCallback((array, seed) => {
    const shuffled = [...array];
    let currentIndex = shuffled.length;
    // Simple seeded random for consistent shuffle per seed
    const seededRandom = () => {
      const x = Math.sin(seed + currentIndex) * 10000;
      return x - Math.floor(x);
    };
    while (currentIndex !== 0) {
      const randomIndex = Math.floor(seededRandom() * currentIndex);
      currentIndex--;
      [shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]];
    }
    return shuffled;
  }, []);

  // The catalog arrives async, so seed the provider selection the first time
  // it's available rather than at mount.
  React.useEffect(() => {
    if (providerDefaultsApplied || !allProviders.length) return;
    const mains = MAIN_PROVIDERS.filter(p => allProviders.includes(p));
    setSelectedProviders(mains.length ? mains : allProviders);
    setProviderDefaultsApplied(true);
  }, [allProviders, providerDefaultsApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get filtered and shuffled slots based on selected providers (+ bonus buy toggle)
  const filteredSlots = React.useMemo(() => {
    const filtered = liveSlots.filter(slot =>
      selectedProviders.includes(slot.provider) && (!bonusBuyOnly || slot.bonusBuy) && (!spookyActive || isSpookySlot(slot))
    );
    return shuffleSeed > 0 ? shuffleArray(filtered, shuffleSeed) : filtered;
  }, [liveSlots, selectedProviders, bonusBuyOnly, spookyActive, shuffleSeed, shuffleArray]);

  // Shuffle the slots
  const shuffleSlots = () => {
    if (slotSpinning) return;
    setShuffleSeed(Date.now());
    // Reset rotation to show shuffled slots
    setSlotIsResetting(true);
    setSlotRotation(-10 * reelSlots.length * SLOT_ITEM_WIDTH);
    setTimeout(() => setSlotIsResetting(false), 50);
  };

  // Close the result popup
  const closeSlotPopup = () => {
    setSlotResultPopup(false);
  };

  const SLOT_ITEM_WIDTH = 160;
  const ITEM_HEIGHT = 100;

  // ============================================
  // CHAT PICKER STATE & FUNCTIONS
  // ============================================
  const [chatCollecting, setChatCollecting] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatTimer, setChatTimer] = useState(60); // default 60 seconds
  const [chatTimeLeft, setChatTimeLeft] = useState(0);
  const [chatSuggestions, setChatSuggestions] = useState([]); // processed suggestions with counts
  const [chatPickerPhase, setChatPickerPhase] = useState('setup'); // 'setup', 'collecting', 'ready', 'spinning', 'result'
  const [chatResult, setChatResult] = useState(null);
  const [chatResultPopup, setChatResultPopup] = useState(false);
  const chatWsRef = useRef(null);
  const chatTimerRef = useRef(null);
  const chatSpinIntervalRef = useRef(null);
  const [chatSpinDisplay, setChatSpinDisplay] = useState(null);

  const TWITCH_CHANNEL = 'snutzy';

  // Connect to Twitch IRC and collect messages
  const startChatCollection = useCallback(() => {
    setChatMessages([]);
    setChatSuggestions([]);
    setChatResult(null);
    setChatResultPopup(false);
    setChatPickerPhase('collecting');
    setChatTimeLeft(chatTimer);

    // Connect to Twitch IRC via WebSocket
    const ws = new WebSocket('wss://irc-ws.chat.twitch.tv:443');
    chatWsRef.current = ws;

    ws.onopen = () => {
      // Anonymous connection - no auth needed for reading
      ws.send('CAP REQ :twitch.tv/tags');
      ws.send('PASS SCHMOOPIIE');
      ws.send('NICK justinfan' + Math.floor(Math.random() * 99999));
      ws.send('JOIN #' + TWITCH_CHANNEL);
    };

    ws.onmessage = (event) => {
      const message = event.data;
      
      // Handle PING to stay connected
      if (message.startsWith('PING')) {
        ws.send('PONG :tmi.twitch.tv');
        return;
      }

      // Parse PRIVMSG (chat messages)
      if (message.includes('PRIVMSG')) {
        // Extract username and message text
        const usernameMatch = message.match(/display-name=([^;]*)/i);
        const textMatch = message.match(/PRIVMSG #\w+ :(.+)/);
        
        if (usernameMatch && textMatch) {
          const username = usernameMatch[1] || 'Anonymous';
          const text = textMatch[1].trim();
          
          // Only add non-empty messages that aren't commands
          if (text && !text.startsWith('!') && text.length > 1 && text.length < 100) {
            setChatMessages(prev => [...prev, { username, text, timestamp: Date.now() }]);
          }
        }
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    // Start countdown timer
    let timeLeft = chatTimer;
    chatTimerRef.current = setInterval(() => {
      timeLeft--;
      setChatTimeLeft(timeLeft);
      
      if (timeLeft <= 0) {
        stopChatCollection();
      }
    }, 1000);

  }, [chatTimer]);

  // Stop collecting and process results
  const stopChatCollection = useCallback(() => {
    // Close WebSocket
    if (chatWsRef.current) {
      chatWsRef.current.close();
      chatWsRef.current = null;
    }
    
    // Clear timer
    if (chatTimerRef.current) {
      clearInterval(chatTimerRef.current);
      chatTimerRef.current = null;
    }

    setChatCollecting(false);
    setChatTimeLeft(0);

    // Process messages into suggestions with counts
    // Only count unique user + suggestion combinations (no spam advantage)
    setChatMessages(prev => {
      const suggestionMap = new Map();
      const userSuggestionTracker = new Set(); // Track "username|normalized" combos
      
      prev.forEach(msg => {
        const normalized = msg.text.toLowerCase().trim();
        const userSuggestionKey = `${msg.username.toLowerCase()}|${normalized}`;
        
        // Skip if this user already suggested this game
        if (userSuggestionTracker.has(userSuggestionKey)) {
          return;
        }
        userSuggestionTracker.add(userSuggestionKey);
        
        if (suggestionMap.has(normalized)) {
          const existing = suggestionMap.get(normalized);
          existing.count++;
          existing.users.push(msg.username);
        } else {
          suggestionMap.set(normalized, {
            text: msg.text,
            normalized,
            count: 1,
            users: [msg.username]
          });
        }
      });

      const suggestions = Array.from(suggestionMap.values())
        .sort((a, b) => b.count - a.count);
      
      setChatSuggestions(suggestions);
      setChatPickerPhase(suggestions.length > 0 ? 'ready' : 'setup');
      
      return prev;
    });
  }, []);

  // Cancel collection early
  const cancelChatCollection = useCallback(() => {
    if (chatWsRef.current) {
      chatWsRef.current.close();
      chatWsRef.current = null;
    }
    if (chatTimerRef.current) {
      clearInterval(chatTimerRef.current);
      chatTimerRef.current = null;
    }
    setChatCollecting(false);
    setChatTimeLeft(0);
    setChatPickerPhase('setup');
    setChatMessages([]);
  }, []);

  // Spin to pick a winner (weighted by count)
  const spinChatPicker = useCallback(() => {
    if (chatSuggestions.length === 0) return;
    
    setChatPickerPhase('spinning');
    setChatResultPopup(false);
    
    // Build weighted array
    const weighted = [];
    chatSuggestions.forEach(suggestion => {
      for (let i = 0; i < suggestion.count; i++) {
        weighted.push(suggestion);
      }
    });
    
    // Animate through random suggestions
    let spins = 0;
    const totalSpins = 30;
    
    chatSpinIntervalRef.current = setInterval(() => {
      const randomIdx = Math.floor(Math.random() * chatSuggestions.length);
      setChatSpinDisplay(chatSuggestions[randomIdx]);
      spins++;
      
      if (spins >= totalSpins) {
        clearInterval(chatSpinIntervalRef.current);
        
        // Pick final winner from weighted array
        const winnerIdx = Math.floor(Math.random() * weighted.length);
        const winner = weighted[winnerIdx];
        
        // Pick a random user who suggested it
        const randomUserIdx = Math.floor(Math.random() * winner.users.length);
        const winningUser = winner.users[randomUserIdx];
        
        setChatResult({ ...winner, winningUser });
        setChatSpinDisplay(null);
        setChatPickerPhase('result');
        setTimeout(() => setChatResultPopup(true), 300);
      }
    }, 100);
  }, [chatSuggestions]);

  // Reset chat picker
  const resetChatPicker = useCallback(() => {
    setChatPickerPhase('setup');
    setChatMessages([]);
    setChatSuggestions([]);
    setChatResult(null);
    setChatResultPopup(false);
  }, []);

  // Close chat result popup
  const closeChatPopup = () => {
    setChatResultPopup(false);
  };

  // Preload first batch of slot images on site load (for faster slot picker)
  useEffect(() => {
    // Preload first 20 slot images (visible in initial reel view)
    allSlots.slice(0, 20).forEach(slot => {
      const img = new Image();
      img.src = slot.image;
    });
  }, []);

  // Create a smaller reel for display (max 30 unique slots, repeated to fill reel)
  const [reelSlots, setReelSlots] = useState([]);
  
  // Generate reel slots when filteredSlots changes or on shuffle
  useEffect(() => {
    if (filteredSlots.length === 0) {
      setReelSlots([]);
      return;
    }
    
    // Take up to 30 slots for the reel display (keeps rendering fast)
    let displaySlots;
    if (filteredSlots.length <= 30) {
      displaySlots = [...filteredSlots];
    } else {
      // Randomly sample 30 slots from filtered list
      const shuffled = [...filteredSlots].sort(() => Math.random() - 0.5);
      displaySlots = shuffled.slice(0, 30);
    }
    
    setReelSlots(displaySlots);
  }, [filteredSlots, shuffleSeed]);

  // Generate Shuffle game URL from slot name and provider
  const getShuffleUrl = useCallback((slot) => {
    if (!slot) return '#';
    // Catalog entries from the Shuffle API carry the exact game slug — use it directly.
    if (slot.slug) return `https://shuffle.com/games/${slot.slug}`;

    // Provider slug mapping based on Shuffle's URL structure
    const providerSlugs = {
      'Pragmatic Play': 'pragmaticplay',
      'Hacksaw': 'hacksaw',
      'Nolimit City': 'nolimit',
      'BGaming': 'softswiss',
      'Backseat Gaming': 'hacksaw',
      'Peter & Sons': 'peter-sons-pets',
      'Popiplay': 'popiplay',
      'Print Studios': 'relax',
      'Shady Lady': 'shady-lady-shdla',
      'Bullshark Games': 'hacksaw',
      'Push Gaming': 'pushgaming',
      'NetEnt': 'evolution',
      '7Rings': '7-rings',
      'Truelab': 'truelab',
      'Microgaming': 'microgaming',
      'Avatar UX': 'avatarux',
      'Aceroll': 'hacksaw',
      'Nownow Gaming': 'hacksaw'
    };
    
    const providerSlug = providerSlugs[slot.provider] || slot.provider.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const gameSlug = slot.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    
    return `https://shuffle.com/games/${providerSlug}-${gameSlug}`;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chatWsRef.current) chatWsRef.current.close();
      if (chatTimerRef.current) clearInterval(chatTimerRef.current);
      if (chatSpinIntervalRef.current) clearInterval(chatSpinIntervalRef.current);
    };
  }, []);

  // Toggle provider selection
  const toggleProvider = (provider) => {
    if (selectedProviders.includes(provider)) {
      if (selectedProviders.length > 1) {
        setSelectedProviders(selectedProviders.filter(p => p !== provider));
      }
    } else {
      setSelectedProviders([...selectedProviders, provider]);
    }
  };

  const selectAllProviders = () => setSelectedProviders(allProviders);
  const deselectAllProviders = () => setSelectedProviders([allProviders[0]]);
  // The providers the stream actually plays — one-tap preset for the filter
  const selectMainProviders = () => setSelectedProviders(MAIN_PROVIDERS.filter(p => allProviders.includes(p)));

  // Slot picker tick sound - more satisfying click/thock sound
  const playSlotTickSound = useCallback(() => {
    try {
      if (!slotAudioContextRef.current) {
        slotAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = slotAudioContextRef.current;
      
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Create a softer, lower pitched "click" sound
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      oscillator.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Lower frequency for a deeper, less harsh tick
      oscillator.frequency.value = 400 + Math.random() * 100;
      oscillator.type = 'sine';
      
      // Low-pass filter to soften it further
      filter.type = 'lowpass';
      filter.frequency.value = 800;
      
      // Quick attack, fast decay
      gainNode.gain.setValueAtTime(0.06, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.05);
    } catch (e) {}
  }, []);

  // Play win/celebration sound when landing on result
  const playWinSound = useCallback(() => {
    try {
      if (!slotAudioContextRef.current) {
        slotAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = slotAudioContextRef.current;
      
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Create a nice "ding" celebration sound - lower octave for less harshness
      const frequencies = [261.63, 329.63, 392.00, 523.25]; // C4, E4, G4, C5 (major chord, one octave lower)
      
      frequencies.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        const startTime = ctx.currentTime + (i * 0.08);
        const volume = 0.1 - (i * 0.015);
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.03);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.6);
      });
    } catch (e) {}
  }, []);

  const stopSlotSpinSound = useCallback(() => {
    if (slotTickIntervalRef.current) {
      clearTimeout(slotTickIntervalRef.current);
      slotTickIntervalRef.current = null;
    }
  }, []);

  // CS:GO case-opening style spin audio (same recipe as the chat giveaway
  // reel): every tick is pre-scheduled on the Web Audio clock against the 5s
  // CSS spin — decelerating spacing with a downward pitch drift, then a weighty
  // thunk right as the reel lands (the win chord layers on top of it). The old
  // setTimeout chain drifted under load and sounded like a flat metronome.
  const startSlotSpinSound = useCallback(() => {
    try {
      if (!slotAudioContextRef.current) {
        slotAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = slotAudioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const master = ctx.createGain();
      master.gain.value = 0.65;
      master.connect(ctx.destination);

      const playTick = (t, pitchShift) => {
        const osc = ctx.createOscillator();
        const filter = ctx.createBiquadFilter();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(1600 + pitchShift + Math.random() * 180, t);
        filter.type = 'bandpass';
        filter.frequency.value = 2000;
        filter.Q.value = 6;
        osc.connect(filter); filter.connect(gain); gain.connect(master);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.11, t + 0.002);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
        osc.start(t); osc.stop(t + 0.06);
      };
      const playThunk = (t) => {
        const osc = ctx.createOscillator();
        const oscGain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(150, t);
        osc.frequency.exponentialRampToValueAtTime(50, t + 0.3);
        osc.connect(oscGain); oscGain.connect(master);
        oscGain.gain.setValueAtTime(0, t);
        oscGain.gain.linearRampToValueAtTime(0.35, t + 0.01);
        oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
        osc.start(t); osc.stop(t + 0.5);
        const click = ctx.createOscillator();
        const clickGain = ctx.createGain();
        const clickFilter = ctx.createBiquadFilter();
        click.type = 'triangle';
        click.frequency.value = 850;
        clickFilter.type = 'lowpass';
        clickFilter.frequency.value = 1400;
        click.connect(clickFilter); clickFilter.connect(clickGain); clickGain.connect(master);
        clickGain.gain.setValueAtTime(0, t);
        clickGain.gain.linearRampToValueAtTime(0.18, t + 0.005);
        clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
        click.start(t); click.stop(t + 0.1);
      };

      // ~34 ticks: dense at launch (26ms apart), stretching to ~0.4s near the
      // end — matches the cubic-bezier deceleration of the reel well enough
      // that the last few ticks feel like the reel nudging past each card.
      const now = ctx.currentTime;
      let offset = 0.03;
      let interval = 0.026;
      let tickCount = 0;
      while (offset < 4.72) {
        playTick(now + offset, -tickCount * 9 + (Math.random() * 50 - 25));
        interval *= 1.085;
        offset += interval;
        tickCount++;
      }
      playThunk(now + 5.0);
    } catch (e) { /* audio unavailable */ }
  }, []);

  // Spin the slot picker
  const spinSlotPicker = useCallback(() => {
    if (slotSpinning || slotIsResetting || filteredSlots.length === 0) return;
    
    setSlotResult(null);
    setSlotResultPopup(false);
    
    // Pick winner from FULL filtered list (fair odds for all slots)
    const winningSlot = filteredSlots[Math.floor(Math.random() * filteredSlots.length)];

    // Rebuild the reel strip fresh EVERY spin: a new random sample with the
    // winner at a uniformly random index. The old code reused the strip and
    // inserted each winner into the same "middle + 0-4" window, so the previous
    // pick kept reappearing right next to the new landing spot.
    const others = [...filteredSlots]
      .sort(() => Math.random() - 0.5)
      .filter(s => s.name !== winningSlot.name)
      .slice(0, 29);
    const winningIndex = Math.floor(Math.random() * (others.length + 1));
    const strip = [...others.slice(0, winningIndex), winningSlot, ...others.slice(winningIndex)];
    setReelSlots(strip);

    const totalItems = strip.length;
    const itemWidth = SLOT_ITEM_WIDTH;
    
    // Land in the 10th-12th copy of the reel (plenty of buffer)
    const targetCopy = 10 + Math.floor(Math.random() * 3);
    const endPosition = (targetCopy * totalItems + winningIndex) * itemWidth;
    
    // Phase 1: Instantly reset to starting position
    setSlotIsResetting(true);
    setSlotRotation(0);
    
    // Phase 2: After brief reset, start the spin
    setTimeout(() => {
      setSlotIsResetting(false);
      setSlotSpinning(true);
      startSlotSpinSound();
      setSlotRotation(-endPosition);
      
      // Phase 3: Spin complete - play win sound and show the result
      setTimeout(() => {
        stopSlotSpinSound();
        playWinSound();
        setSlotResult(winningSlot);
        setSlotSpinning(false);
        setTimeout(() => setSlotResultPopup(true), 400);
      }, 5000); // Match the new spin duration
    }, 50);
  }, [slotSpinning, slotIsResetting, filteredSlots, startSlotSpinSound, stopSlotSpinSound, playWinSound]);
  const audioContextRef = useRef(null);
  const tickIntervalRef = useRef(null);
  const tickSpeedRef = useRef(30);

  // Generate tick sound for spinning
  const playTickSound = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      
      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Softer, lower frequency for a more pleasant sound
      oscillator.frequency.value = 800 + Math.random() * 200;
      oscillator.type = 'sine'; // Sine wave is softer than square
      
      // Much lower volume
      gainNode.gain.setValueAtTime(0.04, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.03);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.03);
    } catch (e) {
      // Audio not supported
    }
  }, []);

  // Stop spinning sound
  const stopSpinSound = useCallback(() => {
    if (tickIntervalRef.current) {
      clearTimeout(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
  }, []);

  // Play ticks that sync with animation - starts fast, slows down following the easing curve
  const startSpinSound = useCallback(() => {
    const spinDuration = 4000;
    const startTime = Date.now();
    tickSpeedRef.current = 30;
    
    const tick = () => {
      const elapsed = Date.now() - startTime;
      
      if (elapsed < spinDuration) {
        playTickSound();
        
        // Easing: cubic-bezier(0.15, 0.85, 0.35, 1) - fast start, slow end
        // Approximate with exponential slowdown
        const progress = elapsed / spinDuration;
        // Speed starts at 30ms, ends around 300ms
        const speed = 30 + (progress * progress * progress * 400);
        
        tickIntervalRef.current = setTimeout(tick, speed);
      }
    };
    
    tick();
  }, [playTickSound]);

  const getLeaderboardDates = useCallback(() => {
    // Monthly leaderboard: 24th of one month → 24th of next month (UK time)
    // Dynamically determines the current period — same logic as backend.
    
    // Get current UK calendar date (handles GMT/BST automatically)
    const now = new Date();
    const ukStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/London' }); // "YYYY-MM-DD"
    const [ukYear, ukMonth0, ukDay] = ukStr.split('-').map(Number); // month is 1-indexed here
    const ukMonthIdx = ukMonth0 - 1; // 0-indexed for Date constructor
    
    // Helper: compute the UTC timestamp for midnight UK time on the 24th of a given month
    function periodBoundaryUTC(year, monthIndex) {
      // Normalize month overflow (e.g. month 12 → Jan next year)
      const d = new Date(Date.UTC(year, monthIndex, 24));
      const yr = d.getUTCFullYear();
      const mo = d.getUTCMonth();
      
      // Last Sunday of March
      const marchLastSun = new Date(Date.UTC(yr, 2, 31));
      while (marchLastSun.getUTCDay() !== 0) marchLastSun.setUTCDate(marchLastSun.getUTCDate() - 1);
      // Last Sunday of October
      const octLastSun = new Date(Date.UTC(yr, 9, 31));
      while (octLastSun.getUTCDay() !== 0) octLastSun.setUTCDate(octLastSun.getUTCDate() - 1);
      
      const isBST = d >= marchLastSun && d < octLastSun;
      if (isBST) {
        return new Date(Date.UTC(yr, mo, 23, 23, 0, 0)); // midnight BST = 23:00 UTC prev day
      } else {
        return new Date(Date.UTC(yr, mo, 24, 0, 0, 0)); // midnight GMT = 00:00 UTC
      }
    }
    
    let startDate, endDate;
    if (ukDay >= 24) {
      startDate = periodBoundaryUTC(ukYear, ukMonthIdx);
      endDate = periodBoundaryUTC(ukYear, ukMonthIdx + 1);
    } else {
      startDate = periodBoundaryUTC(ukYear, ukMonthIdx - 1);
      endDate = periodBoundaryUTC(ukYear, ukMonthIdx);
    }
    
    const formatDate = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
      endDateTime: endDate,
      startDateTime: startDate
    };
  }, []);

  const calculateCountdown = useCallback(() => {
    const { endDateTime } = getLeaderboardDates();
    const now = new Date();
    const diff = endDateTime.getTime() - now.getTime();

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }, [getLeaderboardDates]);

  const leaderboardEverLoadedRef = useRef(false);
  const fetchLeaderboardData = useCallback(async () => {
    setApiStatus('fetching');

    try {
      // Fetch from secure backend - no API keys exposed.
      // Retried with backoff — a slow backend must not flash an empty leaderboard.
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/leaderboard`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      }, { key: 'leaderboard' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Leaderboard data received');

      // Backend returns pre-sanitized, limited data (top 15 only)
      if (data && data.affiliates && Array.isArray(data.affiliates) && data.affiliates.length > 0) {
        setLeaderboard(data.affiliates);
        leaderboardEverLoadedRef.current = true;
        if (data.totalParticipants > 0) setActivePlayerCount(data.totalParticipants);
        setApiStatus('success');
        if (data.cache_updated_at) {
          setCacheTime(data.cache_updated_at);
        }
        setLeaderboardStale(!!data.stale);
        setIsWeightedPeriod(!!data.isWeighted);
      } else {
        setApiStatus('empty');
      }

      setLastUpdated(new Date());

      // Return the server-recommended refresh interval
      return data.refresh_interval_ms || 3600000;
    } catch (error) {
      if (error?.superseded) return 3600000; // a newer call took over — do nothing
      console.error('Fetch error:', error);
      // Stale-while-revalidate: keep showing the last good leaderboard and just
      // mark it as cached; only surface the error state when we have nothing.
      if (leaderboardEverLoadedRef.current) {
        setApiStatus('success');
        setLeaderboardStale(true);
      } else {
        setApiStatus('error');
      }
      setLastUpdated(new Date());
      return 3600000;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboardData();
    fetchPayoutStats();
    // Featured social posts for the landing page (public endpoint)
    fetchWithRetry(`${BACKEND_API_URL}/api/featured-posts`, {}, { key: 'featured-posts' })
      .then(r => (r.ok ? r.json() : { posts: [] }))
      .then(data => setFeaturedPosts(data.posts || []))
      .catch(() => {});
    // Feature flags (lossback / referrals / clips / weekly) — retried so features
    // never look disabled just because the backend answered slowly on first load
    fetchWithRetry(`${BACKEND_API_URL}/api/features`, {}, { key: 'features' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data) setFeatureFlags({ lossback: false, referrals: false, clips: false, weekly: true, ...data }); })
      .catch(() => {});
  }, []);

  // If the weekly leaderboard is turned off, never leave a user stranded on the weekly view
  useEffect(() => {
    if (!featureFlags.weekly && leaderboardMode === 'weekly') {
      setLeaderboardMode('monthly');
      if (window.location.pathname.startsWith('/weekly')) window.history.replaceState({}, '', '/');
    }
  }, [featureFlags.weekly, leaderboardMode]);

  // ============================================
  // "Who referred you?" prompt — fresh accounts only (48h window, server-checked)
  // ============================================
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);
  // Sidebar badge: unclaimed (eligible) referral rewards for the logged-in user
  const [referralClaimableCount, setReferralClaimableCount] = useState(0);

  useEffect(() => {
    if (!user || !featureFlags.referrals) { setReferralClaimableCount(0); return; }
    const token = getAuthToken();
    if (!token) return;
    fetchWithRetry(`${BACKEND_API_URL}/api/referrals/my-claimable-count`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }, { key: 'referral-claimable' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data) setReferralClaimableCount(data.count || 0); })
      .catch(() => {});
  }, [user, featureFlags.referrals]);

  // Lossback status for the FAB: badge + nudge pill while the claim window is open
  const [lossbackStatus, setLossbackStatus] = useState(null);
  useEffect(() => {
    if (!user || !featureFlags.lossback) { setLossbackStatus(null); return; }
    const token = getAuthToken();
    if (!token) return;
    fetchWithRetry(`${BACKEND_API_URL}/api/lossback/my-claim`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }, { key: 'lossback-status' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data) setLossbackStatus(data); })
      .catch(() => {});
  }, [user, featureFlags.lossback]);
  // Window open + not yet claimed this period → OPEN badge + nudge on the FAB
  const lossbackActionable = !!(lossbackStatus?.enabled && lossbackStatus?.windowOpen && !lossbackStatus?.claimedThisPeriod);

  const [referralPromptInput, setReferralPromptInput] = useState('');
  const [referralPromptSaving, setReferralPromptSaving] = useState(false);
  const [referralPromptError, setReferralPromptError] = useState(null);

  useEffect(() => {
    if (!user || !featureFlags.referrals) return;
    if (localStorage.getItem('referralPromptDismissed')) return;
    const token = getAuthToken();
    if (!token) return;
    fetch(`${BACKEND_API_URL}/api/referrals/my-referrer`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data && data.canSet && !data.referrer) setShowReferralPrompt(true); })
      .catch(() => {});
  }, [user, featureFlags.referrals]);

  const dismissReferralPrompt = () => {
    localStorage.setItem('referralPromptDismissed', '1');
    setShowReferralPrompt(false);
  };

  const submitReferralPrompt = async () => {
    if (!referralPromptInput.trim()) return;
    setReferralPromptSaving(true);
    setReferralPromptError(null);
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/referrals/set-referrer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ username: referralPromptInput.trim() })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        dismissReferralPrompt();
        alert(`Referrer set to ${body.referrer?.displayName || referralPromptInput.trim()}! They'll earn rewards as you play.`);
      } else {
        setReferralPromptError(body.error || 'Failed to set referrer');
      }
    } catch (e) {
      setReferralPromptError('Failed to set referrer');
    }
    setReferralPromptSaving(false);
  };

  const fetchWeeklyLeaderboard = useCallback(async () => {
    try {
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/leaderboard/weekly`, { headers: { 'Accept': 'application/json' } }, { key: 'leaderboard-weekly' });
      if (response.ok) {
        const data = await response.json();
        setWeeklyLeaderboard(data.affiliates || []);
        setWeeklyInfo(data.week || null);
      }
    } catch (e) { if (!e?.superseded) console.error('Weekly leaderboard error:', e); }
  }, []);

  const fetchRaffleLottery = useCallback(async () => {
    try {
      const headers = { 'Accept': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/raffle-lottery/info`, { headers }, { key: 'raffle-info' });
      if (response.ok) {
        const data = await response.json();
        setRaffleLottery(data);
      }
    } catch (e) { if (!e?.superseded) console.error('Raffle lottery error:', e); }
  }, []);

  const fetchRaffleResult = useCallback(async () => {
    try {
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/raffle-lottery/result`, {}, { key: 'raffle-result' });
      if (response.ok) {
        const data = await response.json();
        if (data.hasWinner) setRaffleResult(data.winner);
        else setRaffleResult(null);
        // Store the auto-detected active period
        if (data.activePeriod) {
          setRaffleDrawPeriod(data.activePeriod === 'previous' ? 'previous' : 'current');
        }
      }
    } catch (e) { if (!e?.superseded) console.error('Raffle result error:', e); }
  }, []);

  const fetchPreviousWinnersFromSnapshots = useCallback(async () => {
    try {
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/leaderboard/previous-winners`, {}, { key: 'prev-winners' });
      if (response.ok) {
        const data = await response.json();
        if (data.monthly) setPreviousLeaderboardWinners(data.monthly);
        if (data.weekly) setPreviousWeeklyWinners(data.weekly);
      }
    } catch (e) { if (!e?.superseded) console.error('Previous winners error:', e); }
  }, []);

  const fetchRaffleParticipants = useCallback(async (period = 'current') => {
    try {
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/raffle-lottery/participants${period === 'previous' ? '?period=previous' : ''}`, {}, { key: 'raffle-participants' });
      if (response.ok) {
        const data = await response.json();
        setRaffleParticipants(data);
      }
    } catch (e) { if (!e?.superseded) console.error('Raffle participants error:', e); }
  }, []);

  const fetchRaffleLotteryHistory = useCallback(async () => {
    try {
      const response = await fetchWithRetry(`${BACKEND_API_URL}/api/raffle-lottery/history`, {}, { key: 'raffle-history' });
      if (response.ok) {
        const data = await response.json();
        setRaffleLotteryHistory(data.winners || []);
      }
    } catch (e) { if (!e?.superseded) console.error('Raffle history error:', e); }
  }, []);

  const pickRaffleWinnerAction = async (isReroll = false) => {
    const token = getAuthToken();
    if (!token) return;
    
    setRaffleDrawingModal(true);
    setRaffleReelWinner(null);
    setRaffleRolling(true);

    // CS:GO style sound (same as chat giveaway)
    const scheduleSound = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const masterGain = ctx.createGain();
        masterGain.gain.value = 0.7;
        masterGain.connect(ctx.destination);
        const playTick = (t, pitchShift = 0) => {
          const osc = ctx.createOscillator();
          const filter = ctx.createBiquadFilter();
          const gain = ctx.createGain();
          osc.type = 'square';
          osc.frequency.setValueAtTime(1800 + pitchShift + Math.random() * 200, t);
          filter.type = 'bandpass'; filter.frequency.value = 2200; filter.Q.value = 6;
          osc.connect(filter); filter.connect(gain); gain.connect(masterGain);
          gain.gain.setValueAtTime(0, t);
          gain.gain.linearRampToValueAtTime(0.12, t + 0.002);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
          osc.start(t); osc.stop(t + 0.06);
        };
        const playThunk = (t) => {
          const osc = ctx.createOscillator(); const oscGain = ctx.createGain();
          osc.type = 'sine'; osc.frequency.setValueAtTime(160, t);
          osc.frequency.exponentialRampToValueAtTime(55, t + 0.3);
          osc.connect(oscGain); oscGain.connect(masterGain);
          oscGain.gain.setValueAtTime(0, t);
          oscGain.gain.linearRampToValueAtTime(0.35, t + 0.01);
          oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
          osc.start(t); osc.stop(t + 0.5);
          const click = ctx.createOscillator(); const clickGain = ctx.createGain();
          const clickFilter = ctx.createBiquadFilter();
          click.type = 'triangle'; click.frequency.value = 900;
          clickFilter.type = 'lowpass'; clickFilter.frequency.value = 1500;
          click.connect(clickFilter); clickFilter.connect(clickGain); clickGain.connect(masterGain);
          clickGain.gain.setValueAtTime(0, t);
          clickGain.gain.linearRampToValueAtTime(0.18, t + 0.005);
          clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
          click.start(t); click.stop(t + 0.1);
        };
        let offset = 0.03, interval = 0.03;
        const now = ctx.currentTime;
        let tickCount = 0;
        while (offset < 3.7) {
          playTick(now + offset, -tickCount * 15 + (Math.random() * 60 - 30));
          interval *= 1.12; offset += interval; tickCount++;
        }
        playThunk(now + 4.0);
      } catch (e) { /* audio not available */ }
    };

    try {
      const url = isReroll ? `${BACKEND_API_URL}/api/admin/raffle-lottery/reroll` : `${BACKEND_API_URL}/api/admin/raffle-lottery/pick`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      
      if (!res.ok || !data.winner) {
        setRaffleRolling(false);
        setRaffleDrawingModal(false);
        alert(data.error || 'Failed to pick winner');
        return;
      }

      // Start sound + reel animation now that we know the winner
      scheduleSound();
      setRaffleReelWinner(data.winner.winner_username);
      if (data.participants) setRaffleParticipants({ participants: data.participants, totalParticipants: data.participants.length, totalTickets: data.participants.reduce((s, p) => s + p.tickets, 0) });

      // 4s reel animation + 500ms linger
      setTimeout(() => {
        setRaffleRolling(false);
        setRaffleResult(data.winner);
        // Don't call fetchRaffleResult() here — the direct set from the API response is
        // authoritative. fetchRaffleResult() looks up by current period label which would
        // return null if we drew from a previous period, nuking the modal. The background
        // poll will pick it up on its own schedule.
        fetchRaffleLotteryHistory();
      }, 4500);
    } catch (e) {
      setRaffleRolling(false);
      setRaffleDrawingModal(false);
      alert('Error: ' + e.message);
    }
  };

  const payRaffleWinner = async (id) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-lottery/pay`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (res.ok) { fetchRaffleResult(); fetchRaffleLotteryHistory(); }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const deleteRaffleWinner = async (id) => {
    if (!confirm('Delete this raffle winner entirely? This cannot be undone.')) return;
    const token = getAuthToken();
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-lottery/delete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (res.ok) { setRaffleResult(null); fetchRaffleResult(); fetchRaffleLotteryHistory(); }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const searchRaffleParticipants = async (query) => {
    setRaffleManualSearch(query);
    if (query.length < 2) { setRaffleSearchResults([]); return; }
    setRaffleSearching(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-lottery/search-participants?q=${encodeURIComponent(query)}&period=${raffleDrawPeriod || 'current'}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRaffleSearchResults(data.results || []);
      }
    } catch (e) { console.error('Search error:', e); }
    setRaffleSearching(false);
  };

  const setRaffleWinnerManually = async (username) => {
    if (!confirm(`Set "${username}" as the raffle winner? This will replace any current winner.`)) return;
    const token = getAuthToken();
    try {
      const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-lottery/set-winner`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, period: raffleDrawPeriod || 'current' })
      });
      const data = await res.json();
      if (res.ok && data.winner) {
        setRaffleResult(data.winner);
        if (data.participants) setRaffleParticipants({ participants: data.participants, totalParticipants: data.participants.length, totalTickets: data.participants.reduce((s, p) => s + p.tickets, 0) });
        setRaffleManualSearch('');
        setRaffleSearchResults([]);
        fetchRaffleLotteryHistory();
      } else {
        alert(data.error || 'Failed to set winner');
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  // Dynamic refresh interval - matches backend schedule
  useEffect(() => {
    let timeoutId = null;
    
    const scheduleNext = async () => {
      const interval = await fetchLeaderboardData();
      // Use interval from backend, but also calculate client-side for
      // responsiveness: refresh every 2 min in the final hour of the period.
      // (Previously compared against a hardcoded Apr 2026 date, which silently
      // disabled the fast-refresh path once that period ended.)
      const msRemaining = getLeaderboardDates().endDateTime.getTime() - Date.now();
      let clientInterval;
      if (msRemaining <= 0) clientInterval = 3600000;
      else if (msRemaining <= 3600000) clientInterval = 120000;
      else clientInterval = 300000;
      
      const useInterval = Math.min(interval, clientInterval);
      console.log(`Next leaderboard refresh in ${useInterval/1000}s`);
      timeoutId = setTimeout(scheduleNext, useInterval);
    };
    
    // Start dynamic scheduling after initial fetch
    const startTimer = setTimeout(scheduleNext, 10000);
    
    return () => {
      clearTimeout(startTimer);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchLeaderboardData, getLeaderboardDates]);

  // Weekly view is only reachable while its feature flag is on. Without this,
  // landing on /weekly with the flag off would strand the user (the period
  // toggle is hidden when only one period is active).
  useEffect(() => {
    if (featureFlags.weekly === false && leaderboardMode === 'weekly') {
      setLeaderboardMode('monthly');
      window.history.replaceState({}, '', '/');
    }
  }, [featureFlags.weekly, leaderboardMode]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(calculateCountdown());
      // Weekly countdown
      if (weeklyInfo?.endDate) {
        const now = new Date();
        const diff = new Date(weeklyInfo.endDate).getTime() - now.getTime();
        if (diff <= 0) {
          setWeeklyCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        } else {
          setWeeklyCountdown({
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((diff % (1000 * 60)) / 1000)
          });
        }
      }
    }, 1000);

    setCountdown(calculateCountdown());
    return () => clearInterval(timer);
  }, [calculateCountdown, weeklyInfo]);

  // Fetch weekly leaderboard and raffle lottery
  useEffect(() => {
    fetchWeeklyLeaderboard();
    fetchRaffleLottery();
    fetchRaffleResult();
    fetchPreviousWinnersFromSnapshots();
    const interval = setInterval(() => { fetchWeeklyLeaderboard(); fetchRaffleLottery(); fetchRaffleResult(); }, 300000);
    return () => clearInterval(interval);
  }, [fetchWeeklyLeaderboard, fetchRaffleLottery, fetchRaffleResult, fetchPreviousWinnersFromSnapshots, user]);

  // Fetch raffle participants + history when on the raffle page
  useEffect(() => {
    if (activePage !== 'raffle') return;
    // Wait for active period to be detected from the result endpoint
    if (raffleDrawPeriod) {
      fetchRaffleParticipants(raffleDrawPeriod);
    }
    fetchRaffleLotteryHistory();
  }, [activePage, fetchRaffleParticipants, fetchRaffleLotteryHistory, raffleDrawPeriod]);

  // Check if Snutzy is live on Twitch (via backend)
  useEffect(() => {
    const checkTwitchLive = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/live-status`);
        if (response.ok) {
          const data = await response.json();
          setIsStreamerLive(data.isLive);
        }
      } catch (e) {
        console.log('Live status check failed');
      }
    };

    checkTwitchLive();
    const interval = setInterval(checkTwitchLive, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Fetch bonus hunt data from backend.
  // 'loading' | 'loaded' | 'error' — so the "No active bonus hunt" message never
  // shows just because the backend was slow to answer.
  const [bonusHuntsLoadState, setBonusHuntsLoadState] = useState('loading');
  const fetchBonusHuntData = useCallback(async () => {
      try {
        const response = await fetchWithRetry(`${BACKEND_API_URL}/api/bonus-hunts`, {}, { key: 'bonus-hunts' });
        if (response.ok) {
          const data = await response.json();
          setBonusHuntStats({
            biggestMulti: data.highestMulti ? {
              slot: data.highestMulti.slotName,
              multi: Math.round(data.highestMulti.multi),
              hunt: data.highestMulti.huntDate
            } : { slot: 'No data', multi: 0, hunt: null },
            hotSlot: data.hotSlot ? {
              slot: data.hotSlot.name,
              avgMulti: Math.round(data.hotSlot.avgMulti),
              appearances: data.hotSlot.appearances
            } : { slot: 'No data', avgMulti: 0 },
            hotSlots: data.hotSlots || [],
            coldSlots: data.coldSlots || [],
            coldSlot: data.coldSlot ? {
              slot: data.coldSlot.name,
              avgMulti: Math.round(data.coldSlot.avgMulti),
              appearances: data.coldSlot.appearances
            } : { slot: 'No data', avgMulti: 0 },
            huntHistory: (data.recentHunts || [])
              .filter(hunt => {
                // Exclude placeholder hunts: $0 start or -100% loss
                const startCost = hunt.infoStartCost || 0;
                const amountWon = hunt.infoAmountWon || 0;
                if (startCost === 0) return false;
                const profitPercent = ((amountWon - startCost) / startCost) * 100;
                if (profitPercent <= -99.9) return false;
                return true;
              })
              .map((hunt, idx, filteredArr) => {
                // Format date as YYYY/MM/DD
                const date = new Date(hunt.createdDate);
                const formattedDate = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
                
                // Use correct field names from Myrtle API
                const startVal = hunt.infoStartCost || 0;
                const endVal = hunt.infoAmountWon || 0;
                const profit = endVal - startVal;
                const profitPercent = startVal > 0 ? ((profit / startVal) * 100).toFixed(1) : 0;
                
                // Get best multi from hunt data
                const bestMulti = hunt.highest_payout_betsize > 0 
                  ? hunt.highest_payout_value / hunt.highest_payout_betsize 
                  : (hunt.highestMulti || 0);
                
                return {
                  date: formattedDate,
                  startBalance: startVal,
                  endBalance: endVal,
                  profit: profit,
                  profitPercent: profitPercent,
                  bestSlot: hunt.highest_payout_name || hunt.highestMultiName || 'N/A',
                  bestMulti: Math.round(bestMulti),
                  bestBetSize: hunt.highest_payout_betsize || 0,
                  bestPayout: hunt.highest_payout_value || 0,
                  bonusCount: hunt.bonusCount || 0,
                  huntNumber: data.total - idx
                };
              }),
            activeHunt: data.activeHunt,
            total: data.total
          });
          setBonusHuntsLoadState('loaded');
        } else {
          // Definitive non-OK after retries — keep stale data if we have any
          setBonusHuntsLoadState(prev => (prev === 'loaded' ? 'loaded' : 'error'));
        }
      } catch (e) {
        if (!e?.superseded) {
          console.log('Bonus hunt data fetch failed:', e);
          setBonusHuntsLoadState(prev => (prev === 'loaded' ? 'loaded' : 'error'));
        }
      }
  }, []);

  useEffect(() => {
    fetchBonusHuntData();
    const interval = setInterval(fetchBonusHuntData, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [fetchBonusHuntData]);

  const spinWheel = () => {
    if (wheelSpinning || isResetting) return;
    
    setWheelResult(null);
    
    const winningIndex = Math.floor(Math.random() * casinoGames.length);
    const totalItems = casinoGames.length;
    const spins = 5;
    
    // Start position: show item from middle of reel (10 sets in = 100 items = 10000px)
    // This ensures there are items above and below visible
    const startPosition = 10 * totalItems * ITEM_HEIGHT;
    
    // End position: startPosition + 5 full spins + winning item offset
    const endPosition = startPosition + (spins * totalItems * ITEM_HEIGHT) + (winningIndex * ITEM_HEIGHT);
    
    // Phase 1: Instantly reset to starting position (no animation)
    setIsResetting(true);
    setWheelRotation(-startPosition);
    
    // Phase 2: After reset, start the spin
    setTimeout(() => {
      setIsResetting(false);
      setWheelSpinning(true);
      startSpinSound();
      setWheelRotation(-endPosition);
      
      // Phase 3: Spin complete
      setTimeout(() => {
        stopSpinSound();
        setWheelResult(casinoGames[winningIndex]);
        setWheelSpinning(false);
      }, 4000);
    }, 50);
  };

  const getPrizeForRank = (rank) => {
    const prizeEntry = prizes.find(p => p.rank === rank);
    return prizeEntry ? prizeEntry.prize : 0;
  };

  const getWeeklyPrizeForRank = (rank) => {
    const prizeEntry = weeklyPrizes.find(p => p.rank === rank);
    return prizeEntry ? prizeEntry.prize : 0;
  };

  const formatRequirement = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(0)}M`;
    }
    return `$${(amount / 1000).toFixed(0)}K`;
  };

  const getInitials = (username) => {
    return username.substring(0, 2).toUpperCase();
  };

  const censorUsername = (username) => {
    if (!username) return '***';
    if (username.length <= 3) return username.substring(0, 1) + '***';
    return username.substring(0, 2) + '***' + username.slice(-1);
  };

  // OBS overlay route — transparent, chrome-free (no sidebar/header/FAB)
  if (activePage === 'battlewidget') {
    return <BattleWidget apiUrl={BACKEND_API_URL} slotCatalog={allSlots} />;
  }

  return (
    <div className="app-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Space+Mono:wght@400;700&family=Unbounded:wght@600;700;800;900&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        /* Dark themed scrollbars — native gray ones clash with the design */
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: #08080c; }
        ::-webkit-scrollbar-thumb {
          background: rgba(168, 85, 247, 0.28);
          border-radius: 5px;
          border: 2px solid #08080c;
        }
        ::-webkit-scrollbar-thumb:hover { background: rgba(168, 85, 247, 0.5); }
        .sidebar::-webkit-scrollbar { width: 6px; }
        .sidebar::-webkit-scrollbar-thumb { border: none; border-radius: 3px; }
        * { scrollbar-width: thin; scrollbar-color: rgba(168, 85, 247, 0.35) #08080c; }

        .app-container {
          min-height: 100vh;
          background: #050507;
          color: #fff;
          font-family: 'Outfit', sans-serif;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
        }

        .app-container::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          /* Top-left glow uses viewport-unit geometry (55vw 60vh at 20vw 0) so the
             .nav and .sidebar can paint the exact same gradient and continue it
             seamlessly across their opaque backgrounds — OLED panels made the old
             hard cut at the chrome edges obvious. Multi-stop tails ease each glow
             to zero (a single "transparent 50%" stop leaves a visible Mach-band
             arc where the gradient terminates on high-contrast displays). */
          background:
            radial-gradient(ellipse 55vw 60vh at 20vw 0px, rgba(168, 85, 247, 0.13) 0%, rgba(168, 85, 247, 0.07) 28%, rgba(168, 85, 247, 0.035) 48%, rgba(168, 85, 247, 0.015) 66%, rgba(168, 85, 247, 0.005) 83%, transparent 100%),
            radial-gradient(ellipse at 80% 100%, rgba(168, 85, 247, 0.09) 0%, rgba(168, 85, 247, 0.045) 20%, rgba(168, 85, 247, 0.02) 35%, rgba(168, 85, 247, 0.007) 44%, transparent 55%),
            radial-gradient(ellipse at 60% 40%, rgba(251, 191, 36, 0.03) 0%, rgba(251, 191, 36, 0.014) 30%, rgba(251, 191, 36, 0.005) 55%, transparent 75%);
          pointer-events: none;
          z-index: 0;
        }

        .grid-bg {
          position: fixed;
          inset: -25%;
          background-image:
            linear-gradient(rgba(255, 255, 255, 0.042) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.042) 1px, transparent 1px);
          background-size: 56px 56px;
          pointer-events: none;
          z-index: 0;
          transform: perspective(1100px) rotateX(16deg) scale(1.25);
          transform-origin: 50% 30%;
          will-change: transform;
          mask-image: radial-gradient(ellipse 70% 60% at 50% 35%, black 25%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 35%, black 25%, transparent 75%);
        }

        /* Background Art */
        .bg-art {
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          z-index: 1;
          max-height: 80vh;
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }

        .bg-art-left {
          left: 0;
        }

        .bg-art-right {
          right: 0;
        }

        @media (max-width: 1400px) {
          .bg-art {
            opacity: 0.4;
          }
        }

        @media (max-width: 1100px) {
          .bg-art {
            display: none;
          }
        }

        /* ===== Accessibility globals ===== */
        /* Visible keyboard focus everywhere; mouse clicks are unaffected */
        button:focus-visible, a:focus-visible, input:focus-visible,
        select:focus-visible, textarea:focus-visible,
        [role="button"]:focus-visible, [tabindex]:focus-visible {
          outline: 2px solid rgba(168, 85, 247, 0.85);
          outline-offset: 2px;
        }
        /* Honor reduced-motion for every animation/transition in the app */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }

        /* ===== Splash loader ===== */
        .app-loader {
          position: fixed;
          inset: 0;
          z-index: 10000;
          background: #050507;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          transition: opacity 0.65s ease;
        }
        .app-loader::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 50% 40% at 50% 45%, rgba(168, 85, 247, 0.14), transparent 70%);
          pointer-events: none;
        }
        .app-loader.fading { opacity: 0; pointer-events: none; }
        .app-loader-logo {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          border: 1px solid rgba(168, 85, 247, 0.5);
          animation: boot-pulse 1.8s ease-in-out infinite;
          position: relative;
        }
        @keyframes boot-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 22px rgba(168, 85, 247, 0.3); }
          50% { transform: scale(1.06); box-shadow: 0 0 44px rgba(168, 85, 247, 0.55); }
        }
        .app-loader-title {
          margin-top: 1.2rem;
          font-family: 'Unbounded', 'Outfit', sans-serif;
          font-size: 1.7rem;
          font-weight: 900;
          letter-spacing: 0.06em;
          color: #fff;
          text-shadow: 2px 2px 0 rgba(124, 58, 237, 0.5);
          position: relative;
        }
        .app-loader-sub {
          margin-top: 0.3rem;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.5em;
          text-indent: 0.5em;
          color: #a78bfa;
          text-transform: uppercase;
          position: relative;
        }
        .app-loader-bar {
          margin-top: 1.6rem;
          width: 210px;
          height: 3px;
          border-radius: 3px;
          background: rgba(255, 255, 255, 0.07);
          overflow: hidden;
          position: relative;
        }
        .app-loader-bar span {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 40%;
          border-radius: 3px;
          background: linear-gradient(90deg, #a855f7, #fbbf24);
          animation: boot-sweep 1.2s ease-in-out infinite;
        }
        @keyframes boot-sweep {
          0% { left: -40%; }
          100% { left: 100%; }
        }
        .app-loader-tag {
          margin-top: 1.1rem;
          font-family: 'Space Mono', monospace;
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 3px;
          color: #71717a;
          text-transform: uppercase;
          position: relative;
        }
        @media (prefers-reduced-motion: reduce) {
          .app-loader-logo, .app-loader-bar span { animation: none; }
        }

        .nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          /* Continues the app-container::before top-left glow (same viewport-unit
             geometry) so the light isn't sliced off at the header's bottom edge */
          background-color: rgba(10, 10, 15, 0.98);
          background-image: radial-gradient(ellipse 55vw 60vh at 20vw 0px, rgba(168, 85, 247, 0.13) 0%, rgba(168, 85, 247, 0.07) 28%, rgba(168, 85, 247, 0.035) 48%, rgba(168, 85, 247, 0.015) 66%, rgba(168, 85, 247, 0.005) 83%, transparent 100%);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(139, 92, 246, 0.15);
          /* height auto — first row is 60px, optional Total Given Away strip adds ~52px */
        }
        .nav-content {
          height: 60px;
        }

        .payout-ticker {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          white-space: nowrap;
          overflow: hidden;
        }
        .payout-ticker-total {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          background: rgba(34, 197, 94, 0.08);
          border: 1px solid rgba(34, 197, 94, 0.25);
          border-radius: 6px;
          padding: 0.3rem 0.6rem;
          box-shadow: 0 0 10px rgba(34, 197, 94, 0.12);
        }
        .payout-ticker-total .pt-amount { color: #22c55e; font-weight: 800; font-size: 0.8rem; }
        .payout-ticker-total .pt-label { color: #71717a; font-size: 0.7rem; font-weight: 500; }
        .pt-pill {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          border-radius: 5px;
          padding: 0.25rem 0.5rem;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .pt-pill .pt-pill-label { opacity: 0.6; font-weight: 500; font-size: 0.62rem; }
        .pt-pill.pt-predictions {
          background: rgba(139, 92, 246, 0.08);
          border: 1px solid rgba(139, 92, 246, 0.2);
          color: #a78bfa;
        }
        .pt-pill.pt-wager {
          background: rgba(239, 68, 68, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.2);
          color: #f87171;
        }
        .pt-pill.pt-leaderboard {
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.2);
          color: #fbbf24;
        }
        .pt-pill.pt-giveaways {
          background: rgba(96, 165, 250, 0.08);
          border: 1px solid rgba(96, 165, 250, 0.2);
          color: #60a5fa;
        }
        .pt-pill.pt-slotpicks {
          background: rgba(52, 211, 153, 0.08);
          border: 1px solid rgba(52, 211, 153, 0.2);
          color: #34d399;
        }
        .payout-ticker-breakdown {
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        @media (max-width: 1200px) {
          .pt-pill .pt-pill-label { display: none; }
        }
        @media (max-width: 1000px) {
          .payout-ticker-breakdown { display: none !important; }
        }
        @media (max-width: 700px) {
          .payout-ticker { display: none !important; }
        }

        .nav-content {
          max-width: 100%;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 1.5rem;
          height: 60px;
          box-sizing: border-box;
        }

        .nav-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .sidebar-toggle {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #a1a1aa;
          font-size: 1.05rem;
          cursor: pointer;
          padding: 0;
          border-radius: 10px;
          transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sidebar-toggle:hover {
          background: rgba(168, 85, 247, 0.12);
          border-color: rgba(168, 85, 247, 0.45);
          color: #fff;
        }
        .sidebar-toggle:focus-visible {
          outline: 2px solid rgba(168, 85, 247, 0.8);
          outline-offset: 2px;
        }
        /* Open-state glyph: « (collapse rail) on desktop, ✕ (close drawer) on mobile */
        .sidebar-toggle .st-collapse { display: block; font-size: 1.1rem; line-height: 1; }
        .sidebar-toggle .st-close { display: none; }
        @media (max-width: 900px) {
          .sidebar-toggle .st-collapse { display: none; }
          .sidebar-toggle .st-close { display: block; }
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          text-decoration: none;
          color: #fff;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0.3rem 0.65rem;
          border-radius: 12px;
          transition: background 0.2s ease;
        }

        .logo:hover {
          background: rgba(168, 85, 247, 0.08);
        }

        .logo-img {
          width: 34px;
          height: 34px;
          border-radius: 9px;
          border: 1px solid rgba(168, 85, 247, 0.45);
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }

        .logo:hover .logo-img {
          border-color: #a855f7;
          box-shadow: 0 0 18px rgba(168, 85, 247, 0.45);
        }

        .logo-text {
          font-family: 'Unbounded', 'Outfit', sans-serif;
          font-size: 0.95rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #fff;
        }

        .logo-x {
          font-size: 0.95rem;
          color: #a855f7;
          font-weight: 400;
          margin: 0 0.1rem;
          text-shadow: 0 0 12px rgba(168, 85, 247, 0.8);
        }

        .logo-shuffle {
          height: 20px;
          opacity: 0.9;
        }

        @media (max-width: 500px) {
          .logo-shuffle {
            display: none;
          }
          .logo-x {
            display: none;
          }
        }

        /* Sidebar */
        /* Custom Scrollbars */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        ::-webkit-scrollbar-track {
          background: rgba(10, 10, 20, 0.4);
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.3);
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.5);
        }
        ::-webkit-scrollbar-corner {
          background: transparent;
        }
        /* Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: rgba(139, 92, 246, 0.3) rgba(10, 10, 20, 0.4);
        }

        .sidebar {
          position: fixed;
          top: 60px;
          left: 0;
          width: 230px;
          height: calc(100vh - 60px);
          /* Continues the top-left glow; -60px offsets for the header above so the
             gradient stays viewport-aligned with .nav and .app-container::before */
          background-color: rgba(8, 8, 12, 0.98);
          background-image: radial-gradient(ellipse 55vw 60vh at 20vw -60px, rgba(168, 85, 247, 0.13) 0%, rgba(168, 85, 247, 0.07) 28%, rgba(168, 85, 247, 0.035) 48%, rgba(168, 85, 247, 0.015) 66%, rgba(168, 85, 247, 0.005) 83%, transparent 100%);
          border-right: 1px solid rgba(168, 85, 247, 0.15);
          z-index: 99;
          overflow-y: auto;
          padding: 0.5rem 0;
          transition: transform 0.3s ease, width 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .sidebar.closed {
          transform: translateX(-100%);
        }

        /* Main content wrapper */
        .app-wrapper {
          display: flex;
          min-height: 100vh;
          padding-top: 60px;
        }

        .sidebar-spacer {
          width: 230px;
          flex-shrink: 0;
          transition: width 0.3s ease;
        }

        .sidebar-spacer.collapsed {
          width: 0;
        }

        .main-content-area {
          flex: 1;
          min-width: 0;
          transition: margin-left 0.3s ease;
        }

        .sidebar-section {
          padding: 0.1rem 0.4rem;
        }

        .sidebar-section-title {
          font-family: 'Space Mono', monospace;
          font-size: 0.58rem;
          font-weight: 700;
          color: #5b5b66;
          text-transform: uppercase;
          letter-spacing: 2.5px;
          padding: 0.5rem 0.7rem 0.2rem;
          margin-bottom: 0;
        }

        .sidebar-section-title::before { content: '[ '; color: rgba(168, 85, 247, 0.55); }
        .sidebar-section-title::after { content: ' ]'; color: rgba(168, 85, 247, 0.55); }

        /* Collapsible group header — same look as the plain title, but clickable */
        button.sidebar-section-title {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: 'Space Mono', monospace;
          transition: color 0.15s ease;
        }
        button.sidebar-section-title:hover { color: #a1a1aa; }
        .sidebar-section-chevron {
          margin-left: auto;
          font-size: 0.6rem;
          color: rgba(168, 85, 247, 0.7);
          transition: transform 0.2s ease;
        }
        .sidebar-section-chevron.collapsed { transform: rotate(-90deg); }
        .sidebar-section-count {
          font-size: 0.55rem;
          color: #52525b;
          letter-spacing: 0;
        }
        .sidebar-section-body { overflow: hidden; }

        .sidebar-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.5rem 0.75rem;
          color: #a1a1aa;
          text-decoration: none;
          border-radius: 8px;
          margin: 1px 0.4rem;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
          border: none;
          border-left: 2.5px solid transparent;
          background: none;
          width: calc(100% - 0.8rem);
          font-family: 'Outfit', sans-serif;
          font-size: 0.85rem;
          text-align: left;
        }

        .sidebar-item:hover {
          background: rgba(168, 85, 247, 0.1);
          color: #fff;
        }

        .sidebar-item.active {
          background: linear-gradient(90deg, rgba(168, 85, 247, 0.22), rgba(168, 85, 247, 0.04));
          color: #fff;
          font-weight: 600;
          border-left: 2.5px solid #a855f7;
        }

        .sidebar-item-icon {
          font-size: 1rem;
          width: 20px;
          text-align: center;
          flex-shrink: 0;
        }

        .sidebar-item.highlight {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.2));
          border: 1px solid rgba(139, 92, 246, 0.25);
        }

        .sidebar-item.highlight:hover {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3));
        }

        .sidebar-live-badge {
          margin-left: auto;
          font-size: 0.55rem;
          background: #22c55e;
          padding: 0.1rem 0.35rem;
          border-radius: 8px;
          font-weight: 700;
          color: #fff;
          animation: live-badge-pulse 2s ease-in-out infinite;
        }

        .sidebar-new-badge {
          margin-left: auto;
          font-size: 0.5rem;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          padding: 0.1rem 0.35rem;
          border-radius: 8px;
          font-weight: 700;
          color: #fff;
        }

        @keyframes live-badge-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        /* Featured CTA Button (Join Shuffle) — glassy amber tint, the sidebar's #1 action */
        .sidebar-item.featured-cta {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.12), rgba(245, 158, 11, 0.04));
          color: #fbbf24;
          font-weight: 800;
          border: 1px solid rgba(251, 191, 36, 0.4);
          position: relative;
          overflow: hidden;
          animation: featured-glow 3s ease-in-out infinite;
          font-size: 0.88rem;
          padding: 0.65rem 0.75rem;
          justify-content: center;
          gap: 0.5rem;
          border-radius: 10px;
          letter-spacing: 0.3px;
        }

        .sidebar-item.featured-cta .featured-cta-logo {
          width: 20px;
          height: 20px;
          object-fit: contain;
          display: block;
        }

        @keyframes featured-glow {
          0%, 100% { box-shadow: 0 0 8px rgba(251, 191, 36, 0.1), inset 0 0 14px rgba(251, 191, 36, 0.04); }
          50% { box-shadow: 0 0 16px rgba(251, 191, 36, 0.28), inset 0 0 18px rgba(251, 191, 36, 0.07); }
        }

        .sidebar-item.featured-cta::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(255,255,255,0.15), transparent);
          animation: featured-shine 3s ease-in-out infinite;
        }

        @keyframes featured-shine {
          0% { transform: translateX(-100%) rotate(45deg); }
          100% { transform: translateX(100%) rotate(45deg); }
        }

        .sidebar-item.featured-cta:hover {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.22), rgba(245, 158, 11, 0.09));
          border-color: rgba(251, 191, 36, 0.8);
          transform: translateY(-1px);
        }

        /* Newcomer checklist progress card (sits under Join Shuffle, quieter than it) */
        .sidebar-checklist-card {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.7rem;
          padding: 0.6rem 0.7rem;
          background: rgba(168, 85, 247, 0.08);
          border: 1px solid rgba(168, 85, 247, 0.3);
          border-radius: 12px;
          cursor: pointer;
          text-align: left;
          transition: background 0.2s, border-color 0.2s;
        }
        .sidebar-checklist-card:hover {
          background: rgba(168, 85, 247, 0.16);
          border-color: rgba(168, 85, 247, 0.55);
        }
        .sidebar-checklist-card .checklist-chevron {
          margin-left: auto;
          color: #71717a;
          font-size: 1.05rem;
          line-height: 1;
          transition: transform 0.2s, color 0.2s;
        }
        .sidebar-checklist-card:hover .checklist-chevron {
          transform: translateX(2px);
          color: #c084fc;
        }

        .sidebar-item.featured-cta .sidebar-item-icon {
          animation: sparkle 1.5s ease-in-out infinite;
        }

        @keyframes sparkle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        .sidebar-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.22), transparent);
          margin: 0.35rem 0.75rem;
        }

        /* Social Links */
        .sidebar-socials {
          padding: 0.4rem 0.4rem 0.5rem;
          margin-top: auto;
          border-top: 1px solid rgba(139, 92, 246, 0.1);
        }

        .sidebar-socials .sidebar-section-title {
          padding-left: 0.65rem;
          margin-bottom: 0.1rem;
        }

        .sidebar-social-links {
          display: flex;
          justify-content: center;
          gap: 0.35rem;
          padding: 0.25rem 0.35rem;
          flex-wrap: wrap;
        }

        .sidebar-social-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          transition: all 0.2s;
          text-decoration: none;
        }

        .sidebar-social-btn svg {
          width: 14px;
          height: 14px;
        }

        .sidebar-social-btn:hover {
          transform: translateY(-2px);
        }

        .sidebar-social-btn.twitch {
          background: #9146ff;
        }
        .sidebar-social-btn.twitch:hover {
          box-shadow: 0 4px 15px rgba(145, 70, 255, 0.5);
        }

        .sidebar-social-btn.kick {
          background: #53fc18;
          color: #0b0b0b;
        }
        .sidebar-social-btn.kick:hover {
          box-shadow: 0 4px 15px rgba(83, 252, 24, 0.5);
        }

        .sidebar-social-btn.twitter {
          background: #000;
        }
        .sidebar-social-btn.twitter:hover {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
        }

        .sidebar-social-btn.youtube {
          background: #ff0000;
        }
        .sidebar-social-btn.youtube:hover {
          box-shadow: 0 4px 15px rgba(255, 0, 0, 0.5);
        }

        .sidebar-social-btn.discord {
          background: #5865f2;
        }
        .sidebar-social-btn.discord:hover {
          box-shadow: 0 4px 15px rgba(88, 101, 242, 0.5);
        }

        .sidebar-social-btn.tiktok {
          background: #000;
        }
        .sidebar-social-btn.tiktok:hover {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
        }

        .sidebar-social-btn.instagram {
          background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
        }
        .sidebar-social-btn.instagram:hover {
          box-shadow: 0 4px 15px rgba(225, 48, 108, 0.5);
        }

        .sidebar-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.22), transparent);
          margin: 0.35rem 0.75rem;
        }

        .sidebar-user-section {
          padding: 0.4rem 0.4rem;
          border-top: 1px solid rgba(139, 92, 246, 0.1);
          margin-top: auto;
        }

        .sidebar-user-info {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.6rem;
          background: rgba(14, 14, 18, 0.6);
          border-radius: 8px;
          margin: 0 0.5rem 0.5rem 0.5rem;
        }

        .sidebar-user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid #8b5cf6;
        }

        .sidebar-user-name {
          font-weight: 600;
          font-size: 0.85rem;
          color: #fff;
        }

        .sidebar-user-username {
          font-size: 0.7rem;
          color: #71717a;
        }

        /* Hide old nav-links */
        .nav-links {
          display: none;
        }

        .nav-btn {
          padding: 0.6rem 1rem;
          background: transparent;
          border: none;
          color: #a1a1aa;
          font-family: 'Outfit', sans-serif;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.3s ease;
          position: relative;
          white-space: nowrap;
        }

        .nav-btn:hover {
          color: #fff;
          background: rgba(139, 92, 246, 0.1);
        }

        .nav-btn.active {
          color: #fff;
          background: rgba(139, 92, 246, 0.2);
        }

        .nav-btn.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 30px;
          height: 3px;
          background: linear-gradient(90deg, #a855f7, #c084fc);
          border-radius: 3px;
        }

        /* Live Prediction Banner */
        .live-prediction-banner {
          position: relative;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3));
          border: 2px solid rgba(139, 92, 246, 0.5);
          border-radius: 16px;
          padding: 1.25rem 1.5rem;
          margin-bottom: 2rem;
          cursor: pointer;
          transition: all 0.3s;
          overflow: hidden;
          animation: banner-pulse 2s ease-in-out infinite;
        }

        @keyframes banner-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
          50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6), 0 0 60px rgba(236, 72, 153, 0.3); }
        }

        .live-prediction-banner:hover {
          transform: translateY(-2px);
          border-color: rgba(139, 92, 246, 0.8);
        }

        .live-prediction-banner::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
          animation: banner-shine 3s ease-in-out infinite;
        }

        @keyframes banner-shine {
          0% { transform: translateX(-100%) rotate(45deg); }
          100% { transform: translateX(100%) rotate(45deg); }
        }

        .live-prediction-content {
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
          z-index: 1;
          flex-wrap: wrap;
        }

        .live-prediction-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(34, 197, 94, 0.2);
          padding: 0.4rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          color: #22c55e;
        }

        .live-prediction-dot {
          width: 8px;
          height: 8px;
          background: #22c55e;
          border-radius: 50%;
          animation: live-dot-pulse 1.5s ease-in-out infinite;
        }

        @keyframes live-dot-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        .live-prediction-text {
          flex: 1;
          min-width: 200px;
        }

        .live-prediction-title {
          font-weight: 700;
          font-size: 1.1rem;
          margin-bottom: 0.25rem;
        }

        .live-prediction-subtitle {
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        .live-prediction-cta {
          background: linear-gradient(135deg, #8b5cf6, #ec4899);
          color: white;
          border: none;
          padding: 0.6rem 1.25rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .live-prediction-cta:hover {
          transform: scale(1.05);
        }

        /* Main content styling */
        .main {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }

        /* Mobile responsive */
        @media (max-width: 900px) {
          .sidebar {
            transform: translateX(-100%);
            z-index: 101;
          }
          
          .sidebar.open {
            transform: translateX(0);
          }
          
          .sidebar-spacer {
            width: 0;
          }
          
          .sidebar-overlay {
            position: fixed;
            top: 60px;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 100;
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
          }

          .sidebar-overlay.visible {
            opacity: 1;
            visibility: visible;
          }
          
          .main {
            padding: 1.5rem;
          }
          
          .logo-text {
            display: none;
          }
        }

        @media (min-width: 901px) {
          .sidebar-overlay {
            display: none;
          }
        }

        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          color: #fff;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.5rem;
        }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 80px;
          left: 0;
          right: 0;
          background: rgba(10, 10, 15, 0.98);
          backdrop-filter: blur(20px);
          padding: 1rem;
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
          z-index: 99;
          animation: slideDown 0.3s ease;
        }

        .mobile-menu.open {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .main {
          position: relative;
          z-index: 2;
          max-width: 1000px;
          margin: 0 auto;
          padding: 3rem 1.5rem;
          flex: 1;
        }

        /* Leaderboard Header */
        .leaderboard-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .prize-pool-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 165, 0, 0.2) 100%);
          border: 1px solid rgba(255, 215, 0, 0.4);
          padding: 0.5rem 1.25rem;
          border-radius: 50px;
          margin-bottom: 1rem;
          font-weight: 600;
          color: #ffd700;
        }

        .prize-pool-badge span {
          font-family: 'Space Mono', monospace;
          font-size: 1.1rem;
        }

        .page-title {
          font-family: 'Unbounded', 'Outfit', sans-serif;
          font-size: clamp(1.9rem, 4.5vw, 3.4rem);
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: -0.01em;
          line-height: 1.1;
          margin-bottom: 1rem;
          color: #fff;
          text-shadow: 0.05em 0.05em 0 rgba(124, 58, 237, 0.45);
          filter: drop-shadow(0 0 35px rgba(168, 85, 247, 0.3));
        }

        .page-subtitle {
          color: #a1a1aa;
          font-size: 1.1rem;
          margin-bottom: 1rem;
        }

        .page-subtitle strong {
          color: #a855f7;
        }

        /* CTA Signup Button */
        .cta-wrapper {
          position: relative;
          display: inline-block;
          padding: 4px;
          border-radius: 54px;
          background: conic-gradient(from var(--cta-angle, 0deg), #7c3aed, #a855f7, #c084fc, #818cf8, #6366f1, #8b5cf6, #7c3aed);
          animation: ctaRotate 3s linear infinite, ctaGlow 2.5s ease-in-out infinite;
          box-shadow: 0 0 30px rgba(139, 92, 246, 0.3);
        }
        @property --cta-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
        @keyframes ctaRotate { to { --cta-angle: 360deg; } }
        @keyframes ctaGlow {
          0%, 100% { box-shadow: 0 0 30px rgba(139, 92, 246, 0.3), 0 0 60px rgba(139, 92, 246, 0.1); }
          50% { box-shadow: 0 0 50px rgba(139, 92, 246, 0.5), 0 0 100px rgba(168, 85, 247, 0.2); }
        }

        .cta-pulse-ring {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 100%; height: 100%; border-radius: 54px;
          border: 2px solid rgba(139, 92, 246, 0.5);
          animation: ctaPulseRing 2.5s ease-out infinite;
          pointer-events: none;
        }
        .cta-pulse-ring.delay { animation-delay: 1.25s; }
        @keyframes ctaPulseRing {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          100% { transform: translate(-50%, -50%) scale(1.35); opacity: 0; }
        }

        .cta-signup-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          background: linear-gradient(135deg, #1a1028 0%, #1e1235 50%, #1a1028 100%);
          border: none;
          border-radius: 50px;
          color: #fff;
          font-family: 'Outfit', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .cta-signup-btn::before {
          content: '';
          position: absolute;
          top: 0; left: -100%; width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          animation: ctaShimmer 2.5s ease-in-out infinite;
        }
        @keyframes ctaShimmer {
          0%, 100% { left: -100%; }
          50% { left: 100%; }
        }

        .cta-wrapper:hover .cta-signup-btn {
          background: linear-gradient(135deg, #231538 0%, #2a1845 50%, #231538 100%);
        }
        .cta-wrapper:hover {
          transform: translateY(-3px) scale(1.03);
        }

        .cta-arrow {
          font-size: 1.2rem;
          animation: ctaArrowBounce 1.5s ease-in-out infinite;
        }
        @keyframes ctaArrowBounce {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(5px); }
        }

        .cta-sparkles {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%;
          pointer-events: none; overflow: visible;
        }
        .sparkle {
          position: absolute; color: #f59e0b; font-size: 0.7rem;
          animation: ctaSparkle 3s ease-in-out infinite;
          opacity: 0;
        }
        .sparkle.s1 { top: -8px; left: 15%; animation-delay: 0s; color: #a855f7; }
        .sparkle.s2 { top: -6px; right: 20%; animation-delay: 0.6s; color: #c084fc; }
        .sparkle.s3 { bottom: -8px; left: 25%; animation-delay: 1.2s; color: #818cf8; }
        .sparkle.s4 { top: 50%; right: -10px; animation-delay: 0.3s; color: #7c3aed; }
        .sparkle.s5 { top: 50%; left: -10px; animation-delay: 0.9s; color: #6366f1; }
        @keyframes ctaSparkle {
          0%, 100% { opacity: 0; transform: scale(0.5) translateY(0); }
          50% { opacity: 1; transform: scale(1.2) translateY(-6px); }
        }

        .cta-signup-icon {
          font-size: 1.3rem;
        }

        .cta-signup-text {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          line-height: 1.2;
        }

        .cta-signup-main {
          font-size: 1.1rem;
        }

        .cta-signup-sub {
          font-size: 0.75rem;
          opacity: 0.85;
          font-weight: 500;
        }

        /* Countdown Timer */
        .countdown-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 0.4rem;
        }
        .countdown-reset-note {
          text-align: center;
          font-size: 0.75rem;
          color: #8b8b96;
          margin-bottom: 1.25rem;
        }
        .weighted-info-btn {
          background: none;
          border: none;
          color: #a78bfa;
          font-size: 0.85rem;
          cursor: pointer;
          padding: 0 0.25rem;
          margin-left: 0.2rem;
          line-height: 1;
          vertical-align: 1px;
        }
        .weighted-info-btn:hover { color: #c4b5fd; }
        .weighted-info-btn:focus-visible { outline: 2px solid rgba(168, 85, 247, 0.8); outline-offset: 2px; border-radius: 3px; }

        .countdown-label {
          color: #71717a;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .countdown-timer {
          display: flex;
          gap: 0.25rem;
          align-items: center;
        }

        .countdown-block {
          background: rgba(139, 92, 246, 0.15);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          padding: 0.5rem 0.75rem;
          text-align: center;
          min-width: 50px;
        }

        .countdown-number {
          font-family: 'Space Mono', monospace;
          font-size: 1.25rem;
          font-weight: 700;
          color: #fff;
        }

        .countdown-unit {
          font-size: 0.6rem;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .countdown-separator {
          color: #71717a;
          font-size: 1.25rem;
          font-weight: 700;
        }

        /* Podium Section */
        /* ============================================
           PODIUM - COMPLETE REDESIGN
           ============================================ */
        .podium-section {
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 1.5rem;
          margin-bottom: 2.5rem;
          padding: 2rem 1rem 0;
          position: relative;
        }

        .podium-card {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1.75rem 1.5rem;
          border-radius: 16px;
          transition: transform 0.3s ease;
          text-align: center;
          overflow: hidden;
          min-width: 200px;
        }

        .podium-card:hover {
          transform: translateY(-8px) scale(1.02);
        }

        /* === 1ST PLACE - GOLD === */
        .podium-card.first {
          order: 2;
          min-width: 230px;
          padding: 2.25rem 2.25rem 2rem;
          background: linear-gradient(180deg, 
            rgba(255, 215, 0, 0.2) 0%, 
            rgba(255, 180, 0, 0.1) 50%,
            rgba(139, 90, 0, 0.15) 100%);
          border: 2px solid rgba(255, 215, 0, 0.6);
          border-radius: 20px;
          z-index: 3;
          transform: translateY(-30px);
          animation: goldFlicker 2s ease-in-out infinite, goldPulse 4s ease-in-out infinite;
          box-shadow: 
            0 0 30px rgba(255, 215, 0, 0.4),
            0 0 60px rgba(255, 215, 0, 0.2),
            0 0 100px rgba(255, 215, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
          overflow: visible;
        }

        @keyframes goldFlicker {
          0%, 100% { 
            box-shadow: 
              0 0 30px rgba(255, 215, 0, 0.4),
              0 0 60px rgba(255, 215, 0, 0.2),
              0 0 100px rgba(255, 215, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
          }
          25% { 
            box-shadow: 
              0 0 40px rgba(255, 215, 0, 0.6),
              0 0 80px rgba(255, 215, 0, 0.3),
              0 0 120px rgba(255, 215, 0, 0.15),
              inset 0 1px 0 rgba(255, 255, 255, 0.3);
          }
          50% { 
            box-shadow: 
              0 0 35px rgba(255, 215, 0, 0.5),
              0 0 70px rgba(255, 215, 0, 0.25),
              0 0 110px rgba(255, 215, 0, 0.12),
              inset 0 1px 0 rgba(255, 255, 255, 0.25);
          }
          75% { 
            box-shadow: 
              0 0 45px rgba(255, 215, 0, 0.55),
              0 0 90px rgba(255, 215, 0, 0.28),
              0 0 130px rgba(255, 215, 0, 0.14),
              inset 0 1px 0 rgba(255, 255, 255, 0.28);
          }
        }

        @keyframes goldPulse {
          0%, 100% { border-color: rgba(255, 215, 0, 0.6); }
          50% { border-color: rgba(255, 215, 0, 0.9); }
        }

        /* Crown for 1st place */
        .podium-card.first::before {
          content: '👑';
          position: absolute;
          top: -35px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 2.5rem;
          filter: drop-shadow(0 4px 8px rgba(255, 215, 0, 0.5));
          animation: crownFloat 3s ease-in-out infinite;
          z-index: 10;
        }

        @keyframes crownFloat {
          0%, 100% { transform: translateX(-50%) translateY(0) rotate(-5deg); }
          50% { transform: translateX(-50%) translateY(-8px) rotate(5deg); }
        }

        /* === 2ND PLACE - SILVER === */
        .podium-card.second {
          order: 1;
          padding: 1.75rem 2rem;
          background: linear-gradient(180deg, 
            rgba(192, 192, 192, 0.15) 0%, 
            rgba(160, 160, 160, 0.08) 50%,
            rgba(100, 100, 100, 0.12) 100%);
          border: 2px solid rgba(192, 192, 192, 0.4);
          z-index: 2;
          box-shadow: 
            0 0 20px rgba(192, 192, 192, 0.2),
            0 0 40px rgba(192, 192, 192, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .podium-card.second:hover {
          box-shadow: 
            0 0 30px rgba(192, 192, 192, 0.3),
            0 0 50px rgba(192, 192, 192, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
        }

        /* === 3RD PLACE - BRONZE === */
        .podium-card.third {
          order: 3;
          padding: 1.75rem 2rem;
          background: linear-gradient(180deg, 
            rgba(205, 127, 50, 0.15) 0%, 
            rgba(180, 100, 40, 0.08) 50%,
            rgba(139, 69, 19, 0.12) 100%);
          border: 2px solid rgba(205, 127, 50, 0.4);
          z-index: 2;
          box-shadow: 
            0 0 20px rgba(205, 127, 50, 0.2),
            0 0 40px rgba(205, 127, 50, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .podium-card.third:hover {
          box-shadow: 
            0 0 30px rgba(205, 127, 50, 0.3),
            0 0 50px rgba(205, 127, 50, 0.15),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
        }

        /* === AVATAR SECTION === */
        .podium-avatar {
          position: relative;
          margin-bottom: 1rem;
          flex-shrink: 0;
          display: inline-block;
        }

        .podium-card.first .podium-avatar {
          margin-bottom: 1rem;
          display: block;
          margin-left: auto;
          margin-right: auto;
          width: fit-content;
        }

        .avatar-circle {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          position: relative;
          z-index: 1;
          text-transform: uppercase;
          aspect-ratio: 1 / 1;
          flex-shrink: 0;
        }

        /* Avatar image (Twitch profile pic) */
        .avatar-image {
          border-radius: 50%;
          object-fit: cover;
          position: relative;
          z-index: 1;
          aspect-ratio: 1 / 1;
          flex-shrink: 0;
        }

        .podium-card.first .avatar-image {
          width: 72px;
          height: 72px;
          min-width: 72px;
          min-height: 72px;
          box-shadow: 
            0 0 20px rgba(255, 215, 0, 0.5),
            0 4px 10px rgba(0, 0, 0, 0.3);
          border: 3px solid rgba(255, 215, 0, 0.8);
        }

        .podium-card.second .avatar-image {
          width: 64px;
          height: 64px;
          min-width: 64px;
          min-height: 64px;
          box-shadow: 
            0 0 15px rgba(192, 192, 192, 0.4),
            0 4px 10px rgba(0, 0, 0, 0.3);
          border: 3px solid rgba(192, 192, 192, 0.6);
        }

        .podium-card.third .avatar-image {
          width: 64px;
          height: 64px;
          min-width: 64px;
          min-height: 64px;
          box-shadow: 
            0 0 15px rgba(205, 127, 50, 0.4),
            0 4px 10px rgba(0, 0, 0, 0.3);
          border: 3px solid rgba(205, 127, 50, 0.6);
        }

        /* Table avatar image */
        .table-avatar-image {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
        }

        /* 1st place avatar */
        .podium-card.first .avatar-circle {
          width: 72px;
          height: 72px;
          min-width: 72px;
          min-height: 72px;
          font-size: 1.5rem;
          background: linear-gradient(135deg, #ffd700 0%, #ffb800 50%, #ff9500 100%);
          color: #1a1a0a;
          box-shadow: 
            0 0 20px rgba(255, 215, 0, 0.5),
            0 4px 10px rgba(0, 0, 0, 0.3);
          border: 3px solid rgba(255, 215, 0, 0.8);
        }

        /* 2nd place avatar */
        .podium-card.second .avatar-circle {
          width: 64px;
          height: 64px;
          min-width: 64px;
          min-height: 64px;
          font-size: 1.3rem;
          background: linear-gradient(135deg, #e8e8e8 0%, #c0c0c0 50%, #a8a8a8 100%);
          color: #1a1a1a;
          box-shadow: 
            0 0 15px rgba(192, 192, 192, 0.4),
            0 4px 10px rgba(0, 0, 0, 0.3);
          border: 3px solid rgba(192, 192, 192, 0.6);
        }

        /* 3rd place avatar */
        .podium-card.third .avatar-circle {
          width: 64px;
          height: 64px;
          min-width: 64px;
          min-height: 64px;
          font-size: 1.3rem;
          background: linear-gradient(135deg, #e09050 0%, #cd7f32 50%, #a0522d 100%);
          color: #fff;
          box-shadow: 
            0 0 15px rgba(205, 127, 50, 0.4),
            0 4px 10px rgba(0, 0, 0, 0.3);
          border: 3px solid rgba(205, 127, 50, 0.6);
        }

        /* === RANK BADGE === */
        .rank-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 800;
          z-index: 2;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
        }

        .podium-card.first .rank-badge {
          width: 26px;
          height: 26px;
          font-size: 0.8rem;
          background: linear-gradient(135deg, #ffd700, #ff9500);
          color: #1a1a0a;
          border: 2px solid #fff;
        }

        .podium-card.second .rank-badge {
          background: linear-gradient(135deg, #e0e0e0, #a0a0a0);
          color: #1a1a1a;
          border: 2px solid #fff;
        }

        .podium-card.third .rank-badge {
          background: linear-gradient(135deg, #e09050, #8b4513);
          color: #fff;
          border: 2px solid #fff;
        }

        /* === TEXT ELEMENTS === */
        .podium-username {
          font-weight: 700;
          margin-bottom: 0.5rem;
          text-align: center;
          color: #fff;
        }

        .podium-card.first .podium-username {
          font-size: 1.15rem;
          color: #ffd700;
          text-shadow: 0 0 15px rgba(255, 215, 0, 0.4);
        }

        .podium-card.second .podium-username {
          font-size: 1.05rem;
        }

        .podium-card.third .podium-username {
          font-size: 1.05rem;
        }

        .podium-wager-label {
          font-size: 0.65rem;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 0.25rem;
        }

        .podium-wager {
          font-family: 'Space Mono', monospace;
          font-weight: 700;
          padding: 0.4rem 1rem;
          border-radius: 8px;
          margin-bottom: 0.6rem;
        }

        .podium-card.first .podium-wager {
          background: rgba(255, 215, 0, 0.2);
          color: #ffd700;
          font-size: 1rem;
          border: 1px solid rgba(255, 215, 0, 0.3);
        }

        .podium-card.second .podium-wager {
          background: rgba(192, 192, 192, 0.15);
          color: #d0d0d0;
          font-size: 0.9rem;
          border: 1px solid rgba(192, 192, 192, 0.2);
        }

        .podium-card.third .podium-wager {
          background: rgba(205, 127, 50, 0.15);
          color: #e09050;
          font-size: 0.9rem;
          border: 1px solid rgba(205, 127, 50, 0.2);
        }

        .podium-prize {
          font-weight: 800;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .podium-card.first .podium-prize {
          font-size: 1.5rem;
          color: #ffd700;
          text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
        }

        .podium-card.second .podium-prize {
          font-size: 1.25rem;
          color: #d0d0d0;
        }

        .podium-card.third .podium-prize {
          font-size: 1.25rem;
          color: #e09050;
        }

        .podium-prize-label {
          font-size: 0.6rem;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-weight: 600;
          margin-top: 0.15rem;
        }

        /* === RESPONSIVE === */
        @media (max-width: 600px) {
          .podium-section {
            gap: 0.5rem;
            padding: 2.5rem 0.5rem 0;
          }
          
          .podium-card {
            padding: 1.25rem 1rem;
          }
          
          .podium-card.first {
            padding: 1.5rem 1.25rem;
            transform: translateY(-20px);
          }
          
          .podium-card.first .avatar-circle {
            width: 60px;
            height: 60px;
            font-size: 1.3rem;
          }
          
          .podium-card.second .avatar-circle,
          .podium-card.third .avatar-circle {
            width: 55px;
            height: 55px;
            font-size: 1.1rem;
          }
          
          .podium-card.first .podium-prize { font-size: 1.25rem; }
          .podium-card.second .podium-prize,
          .podium-card.third .podium-prize { font-size: 1.1rem; }
          
          .podium-card.first::before {
            font-size: 2rem;
            top: -30px;
          }
        }

        /* Leaderboard Table */
        .leaderboard-table-container {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          overflow: hidden;
          margin-bottom: 2rem;
        }

        .table-header {
          display: grid;
          grid-template-columns: 80px 1fr 150px 120px;
          padding: 1rem 1.5rem;
          background: rgba(139, 92, 246, 0.1);
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #71717a;
        }

        .table-row {
          display: grid;
          grid-template-columns: 80px 1fr 150px 120px;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid rgba(139, 92, 246, 0.1);
          transition: all 0.3s ease;
          align-items: center;
        }

        .table-row:last-child { border-bottom: none; }

        .table-row:hover {
          background: rgba(139, 92, 246, 0.1);
        }

        .table-rank {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.9rem;
          background: rgba(139, 92, 246, 0.2);
          color: #a855f7;
        }

        .table-user {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .table-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .table-username {
          font-weight: 600;
          font-size: 1rem;
        }

        .table-wager {
          font-family: 'Space Mono', monospace;
          font-weight: 600;
          color: #a855f7;
        }

        .table-prize {
          font-family: 'Space Mono', monospace;
          font-weight: 600;
          color: #22c55e;
        }

        .table-row.no-prize {
          border-bottom-color: rgba(63, 63, 70, 0.3);
        }

        .table-row.no-prize .table-rank {
          color: #71717a;
        }

        .table-row.no-prize .table-avatar {
          background: rgba(63, 63, 70, 0.5);
          color: #71717a;
        }

        .no-prize-dash {
          color: #71717a;
          font-size: 1.2rem;
        }

        .leaderboard-cutoff-note {
          text-align: center;
          padding: 1rem;
          color: #71717a;
          font-size: 0.85rem;
          border-top: 1px dashed rgba(113, 113, 122, 0.3);
          margin-top: 0.5rem;
        }

        /* Prize Pool Grid */
        .prize-grid-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .prize-grid-title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: #a1a1aa;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .prize-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 0.5rem;
        }

        .prize-grid-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.6rem 1rem;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          border: 1px solid rgba(139, 92, 246, 0.15);
        }

        .prize-grid-rank {
          font-weight: 600;
          color: #71717a;
        }

        .prize-grid-amount {
          font-family: 'Space Mono', monospace;
          font-weight: 700;
          color: #22c55e;
          font-size: 0.9rem;
        }

        /* API Status */
        .api-status {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          font-size: 0.78rem;
          color: #71717a;
          padding: 0.5rem 1rem;
          background: rgba(14,14,18,0.5);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 8px;
        }

        .status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
        }

        .status-dot.success { background: #22c55e; }
        .status-dot.error { background: #ef4444; }
        .status-dot.fetching { background: #f59e0b; }
        .status-dot.stale { background: #f59e0b; animation: pulse 2s infinite; }

        /* Loading skeleton */
        .skeleton {
          background: linear-gradient(90deg, rgba(139, 92, 246, 0.1) 25%, rgba(139, 92, 246, 0.2) 50%, rgba(139, 92, 246, 0.1) 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 8px;
        }

        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .podium-skeleton {
          width: 100%;
          height: 200px;
          border-radius: 20px;
        }

        /* Subtle pulsing skeleton blocks (loading placeholders, see <Skel/>) */
        .sk-pulse {
          background: rgba(255, 255, 255, 0.05);
          animation: skPulse 1.6s ease-in-out infinite;
        }
        @keyframes skPulse {
          0%, 100% { background: rgba(255, 255, 255, 0.03); }
          50% { background: rgba(255, 255, 255, 0.08); }
        }

        /* Shown only after all fetch retries are exhausted */
        .load-error-box {
          text-align: center;
          padding: 2rem 1rem;
          color: #a1a1aa;
          font-size: 0.9rem;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
        }
        .load-error-box .retry-btn {
          display: inline-block;
          margin-top: 0.75rem;
          padding: 0.5rem 1.25rem;
          background: rgba(251, 191, 36, 0.12);
          border: 1px solid rgba(251, 191, 36, 0.35);
          border-radius: 8px;
          color: #fbbf24;
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
          transition: background 0.2s;
        }
        .load-error-box .retry-btn:hover { background: rgba(251, 191, 36, 0.2); }

        /* Previous Winners */
        .previous-winners-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          margin-bottom: 2rem;
          overflow: hidden;
        }

        .previous-winners-toggle {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          background: transparent;
          border: none;
          color: #fff;
          font-family: 'Outfit', sans-serif;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .previous-winners-toggle:hover {
          background: rgba(139, 92, 246, 0.1);
        }

        .toggle-arrow {
          color: #a855f7;
          font-size: 0.8rem;
        }

        .previous-winners-content {
          padding: 0 1.5rem 1.5rem;
          border-top: 1px solid rgba(139, 92, 246, 0.2);
        }

        .previous-winners-period {
          text-align: center;
          color: #a1a1aa;
          font-size: 0.9rem;
          padding: 1rem 0;
          margin-bottom: 0.5rem;
        }

        .previous-winners-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .previous-winner-row {
          display: flex;
          align-items: center;
          padding: 0.75rem 1rem;
          background: rgba(30, 30, 40, 0.5);
          border-radius: 10px;
          transition: transform 0.2s ease;
        }

        .previous-winner-row:hover {
          transform: translateX(5px);
        }

        .previous-winner-row.top-1 {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%);
          border: 1px solid rgba(255, 215, 0, 0.3);
        }

        .previous-winner-row.top-2 {
          background: linear-gradient(135deg, rgba(192, 192, 192, 0.15) 0%, rgba(150, 150, 150, 0.1) 100%);
          border: 1px solid rgba(192, 192, 192, 0.3);
        }

        .previous-winner-row.top-3 {
          background: linear-gradient(135deg, rgba(205, 127, 50, 0.15) 0%, rgba(180, 100, 40, 0.1) 100%);
          border: 1px solid rgba(205, 127, 50, 0.3);
        }

        .previous-winner-rank {
          width: 40px;
          font-weight: 600;
          color: #a1a1aa;
        }

        .previous-winner-row.top-1 .previous-winner-rank { color: #ffd700; }
        .previous-winner-row.top-2 .previous-winner-rank { color: #c0c0c0; }
        .previous-winner-row.top-3 .previous-winner-rank { color: #cd7f32; }

        .previous-winner-name {
          flex: 1;
          font-weight: 500;
        }

        .previous-winner-reward {
          font-family: 'Space Mono', monospace;
          font-weight: 600;
          color: #22c55e;
        }

        .previous-winners-total {
          text-align: center;
          padding: 1rem;
          margin-top: 1rem;
          border-top: 1px solid rgba(139, 92, 246, 0.2);
          color: #a1a1aa;
        }

        .previous-winners-total strong {
          color: #22c55e;
          font-family: 'Space Mono', monospace;
        }

        /* Social links */
        .social-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 2rem;
        }

        .social-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .social-grid {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        .social-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          padding: 0.9rem 1.5rem;
          background: rgba(0, 0, 0, 0.4);
          border: 2px solid;
          border-radius: 12px;
          color: #fff;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .social-link::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .social-link:hover {
          transform: translateY(-4px) scale(1.02);
          color: #fff;
        }

        .social-link:hover::before {
          opacity: 0.15;
        }

        .social-link-icon {
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .social-link-icon svg {
          width: 100%;
          height: 100%;
        }

        .social-link:hover .social-link-icon {
          transform: scale(1.15);
        }

        /* Individual social link colors */
        .social-link.discord {
          border-color: #5865F2;
          box-shadow: 0 0 10px rgba(88, 101, 242, 0.2);
        }
        .social-link.discord:hover {
          box-shadow: 0 0 25px rgba(88, 101, 242, 0.6), 0 0 50px rgba(88, 101, 242, 0.3);
          background: rgba(88, 101, 242, 0.15);
        }
        .social-link.discord::before { background: #5865F2; }
        .social-link.discord .social-link-icon { color: #5865F2; }

        .social-link.twitch {
          border-color: #9146FF;
          box-shadow: 0 0 10px rgba(145, 70, 255, 0.2);
        }
        .social-link.twitch:hover {
          box-shadow: 0 0 25px rgba(145, 70, 255, 0.6), 0 0 50px rgba(145, 70, 255, 0.3);
          background: rgba(145, 70, 255, 0.15);
        }
        .social-link.twitch::before { background: #9146FF; }
        .social-link.twitch .social-link-icon { color: #9146FF; }

        .social-link.twitter {
          border-color: #ffffff;
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
        }
        .social-link.twitter:hover {
          box-shadow: 0 0 25px rgba(255, 255, 255, 0.4), 0 0 50px rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.1);
        }
        .social-link.twitter::before { background: #ffffff; }
        .social-link.twitter .social-link-icon { color: #ffffff; }

        .social-link.youtube {
          border-color: #FF0000;
          box-shadow: 0 0 10px rgba(255, 0, 0, 0.2);
        }
        .social-link.youtube:hover {
          box-shadow: 0 0 25px rgba(255, 0, 0, 0.6), 0 0 50px rgba(255, 0, 0, 0.3);
          background: rgba(255, 0, 0, 0.15);
        }
        .social-link.youtube::before { background: #FF0000; }
        .social-link.youtube .social-link-icon { color: #FF0000; }

        .social-link.tiktok {
          border-color: #00F2EA;
          box-shadow: 0 0 10px rgba(0, 242, 234, 0.2);
        }
        .social-link.tiktok:hover {
          box-shadow: 0 0 25px rgba(0, 242, 234, 0.6), 0 0 50px rgba(0, 242, 234, 0.3), 0 0 25px rgba(255, 0, 80, 0.3);
          background: rgba(0, 242, 234, 0.15);
        }
        .social-link.tiktok::before { background: linear-gradient(135deg, #00F2EA, #FF0050); }
        .social-link.tiktok .social-link-icon { color: #00F2EA; }

        /* Public Slot Picks Queue Page */
        .slotpicks-page {
          max-width: 900px;
          margin: 0 auto;
        }

        .slotpicks-intro {
          text-align: center;
          color: #a1a1aa;
          font-size: 1.1rem;
          margin-bottom: 2rem;
        }

        .slotpicks-stats-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .slotpicks-stat-card {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s;
        }

        .slotpicks-stat-card.pending {
          border-color: rgba(245, 158, 11, 0.3);
          background: rgba(245, 158, 11, 0.05);
        }

        .slotpicks-stat-card.completed {
          border-color: rgba(139, 92, 246, 0.3);
          background: rgba(139, 92, 246, 0.05);
        }

        .slotpicks-stat-card.paid {
          border-color: rgba(34, 197, 94, 0.3);
          background: rgba(34, 197, 94, 0.05);
        }

        .slotpicks-stat-card .stat-big-number {
          font-size: 2.5rem;
          font-weight: 700;
          font-family: 'Space Mono', monospace;
          color: #fff;
        }

        .slotpicks-stat-card.pending .stat-big-number { color: #f59e0b; }
        .slotpicks-stat-card.completed .stat-big-number { color: #a855f7; }
        .slotpicks-stat-card.paid .stat-big-number { color: #22c55e; }

        .slotpicks-stat-card .stat-label {
          color: #a1a1aa;
          font-size: 0.9rem;
          margin-top: 0.25rem;
        }

        .slotpicks-stat-card .stat-subtext {
          color: #22c55e;
          font-size: 0.85rem;
          margin-top: 0.25rem;
          font-weight: 600;
        }

        .slotpicks-progress-container {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 2rem;
        }

        .slotpicks-progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .progress-title {
          font-weight: 600;
          color: #fff;
        }

        .progress-count {
          color: #f59e0b;
          font-weight: 600;
        }

        .slotpicks-progress-bar {
          height: 12px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 6px;
          overflow: hidden;
        }

        .slotpicks-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #22c55e, #16a34a);
          border-radius: 6px;
          transition: width 0.5s ease;
        }

        .slotpicks-progress-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 0.5rem;
          font-size: 0.8rem;
          color: #71717a;
        }

        .slotpicks-queue-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .slotpicks-queue-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .slotpicks-queue-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .queue-live-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: #22c55e;
        }

        .live-dot {
          width: 8px;
          height: 8px;
          background: #22c55e;
          border-radius: 50%;
          animation: pulse-dot 2s infinite;
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        .slotpicks-queue-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .slotpicks-queue-item {
          display: grid;
          grid-template-columns: 50px 1.5fr 2fr auto;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.2s;
        }

        .slotpicks-queue-item:hover {
          background: rgba(139, 92, 246, 0.1);
          border-color: rgba(139, 92, 246, 0.2);
        }

        .slotpicks-queue-item:first-child {
          background: rgba(245, 158, 11, 0.1);
          border-color: rgba(245, 158, 11, 0.3);
        }

        .queue-position {
          font-size: 1.1rem;
          font-weight: 700;
          color: #a1a1aa;
          text-align: center;
        }

        .slotpicks-queue-item:first-child .queue-position {
          color: #f59e0b;
        }

        .queue-user {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .queue-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
        }

        .queue-avatar-placeholder {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #8b5cf6, #ec4899);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 700;
          color: #fff;
        }

        .queue-username {
          font-weight: 600;
          color: #fff;
        }

        .queue-slot {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .slot-icon {
          font-size: 1.25rem;
        }

        .slot-name {
          color: #fbbf24;
          font-weight: 600;
        }

        .queue-time {
          color: #71717a;
          font-size: 0.85rem;
          text-align: right;
        }

        .slotpicks-empty {
          text-align: center;
          padding: 3rem 1rem;
        }

        .slotpicks-empty .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .slotpicks-empty p {
          color: #a1a1aa;
          margin: 0.5rem 0;
        }

        .slotpicks-empty .empty-hint {
          font-size: 0.9rem;
          color: #71717a;
        }

        .slotpicks-how-it-works {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
        }

        .slotpicks-how-it-works h3 {
          text-align: center;
          margin-bottom: 1.5rem;
          color: #fff;
        }

        .how-steps {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
        }

        .how-step {
          text-align: center;
          padding: 1rem;
        }

        .step-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #8b5cf6, #ec4899);
          border-radius: 50%;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.75rem;
        }

        .step-text {
          display: block;
          font-size: 0.9rem;
          color: #a1a1aa;
          line-height: 1.4;
        }

        .sidebar-queue-badge {
          background: #f59e0b;
          color: #000;
          font-size: 0.7rem;
          padding: 0.15rem 0.45rem;
          border-radius: 10px;
          font-weight: 700;
          margin-left: auto;
        }

        @media (max-width: 768px) {
          .slotpicks-stats-row {
            grid-template-columns: 1fr;
          }

          .slotpicks-queue-item {
            grid-template-columns: 40px 1fr;
            gap: 0.75rem;
          }

          .queue-slot, .queue-time {
            grid-column: 2;
          }

          .how-steps {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Tournament Page */
        .tournament-page {
          max-width: 900px;
          margin: 0 auto;
        }

        .tournament-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .tournament-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          background: linear-gradient(135deg, #ffd700, #f59e0b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .tournament-subtitle {
          color: #a1a1aa;
          font-size: 1.1rem;
        }

        .tournament-info-card {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .tournament-date-banner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding: 1rem;
          background: rgba(139, 92, 246, 0.15);
          border-radius: 10px;
          margin-bottom: 1.5rem;
        }

        .date-icon {
          font-size: 1.25rem;
        }

        .date-text {
          font-weight: 600;
          color: #fff;
          font-size: 1.1rem;
        }

        .date-separator {
          color: #71717a;
        }

        .stream-link a {
          color: #a855f7;
          text-decoration: none;
        }

        .stream-link a:hover {
          text-decoration: underline;
        }

        .tournament-prizes {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .prize-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1rem 2rem;
          border-radius: 12px;
          min-width: 120px;
        }

        .prize-item.first {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.1));
          border: 1px solid rgba(255, 215, 0, 0.4);
        }

        .prize-item.second {
          background: linear-gradient(135deg, rgba(192, 192, 192, 0.2), rgba(192, 192, 192, 0.1));
          border: 1px solid rgba(192, 192, 192, 0.4);
        }

        .prize-item.third {
          background: linear-gradient(135deg, rgba(205, 127, 50, 0.2), rgba(205, 127, 50, 0.1));
          border: 1px solid rgba(205, 127, 50, 0.4);
        }

        .prize-place {
          font-size: 1rem;
          margin-bottom: 0.25rem;
        }

        .prize-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
        }

        .tournament-format h3 {
          text-align: center;
          margin-bottom: 1rem;
          color: #e4e4e7;
        }

        .format-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        @media (max-width: 800px) {
          .format-steps {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 500px) {
          .format-steps {
            grid-template-columns: 1fr;
          }
        }

        .format-step {
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
        }

        .step-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .step-content strong {
          display: block;
          color: #fff;
          margin-bottom: 0.25rem;
        }

        .step-content p {
          color: #a1a1aa;
          font-size: 0.85rem;
          margin: 0;
        }

        .tournament-raffle-section {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .tournament-raffle-section h2 {
          text-align: center;
          margin-bottom: 1rem;
          font-size: 1.3rem;
        }

        .raffle-requirement {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 10px;
          margin-bottom: 1.5rem;
        }

        .requirement-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .requirement-text {
          color: #fca5a5;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .raffle-entry-box {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          margin-bottom: 1rem;
        }

        .raffle-login-prompt p {
          color: #a1a1aa;
          margin-bottom: 1rem;
        }

        .raffle-entered {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        .entered-check {
          font-size: 2.5rem;
        }

        .entered-info {
          text-align: left;
        }

        .entered-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #22c55e;
          margin: 0 0 0.25rem 0;
        }

        .entered-id {
          color: #e4e4e7;
          margin: 0 0 0.25rem 0;
        }

        .entered-time {
          color: #71717a;
          font-size: 0.85rem;
          margin: 0;
        }

        .raffle-enter p {
          color: #a1a1aa;
          margin-bottom: 1rem;
        }

        .enter-raffle-btn {
          padding: 1rem 2.5rem;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border: none;
          border-radius: 10px;
          color: #fff;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .enter-raffle-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
        }

        .enter-raffle-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .raffle-closed-note {
          color: #f59e0b;
          font-size: 0.9rem;
          margin-top: 0.75rem;
        }

        .tournament-message {
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 0.95rem;
        }

        .tournament-message.success {
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.4);
          color: #4ade80;
        }

        .tournament-message.error {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.4);
          color: #f87171;
        }

        .tournament-message-action {
          display: inline-block;
          margin-left: 1rem;
          padding: 0.4rem 0.8rem;
          background: rgba(139, 92, 246, 0.3);
          border: 1px solid rgba(139, 92, 246, 0.5);
          border-radius: 6px;
          color: #a78bfa;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tournament-message-action:hover {
          background: rgba(139, 92, 246, 0.5);
          color: #fff;
        }

        .raffle-stats {
          text-align: center;
          color: #a1a1aa;
        }

        .raffle-stat strong {
          color: #8b5cf6;
          font-size: 1.2rem;
        }

        .tournament-entries-section {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 16px;
          padding: 1.5rem;
        }

        .tournament-entries-section h2 {
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 1.3rem;
        }

        .entries-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 1rem;
        }

        .entry-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 10px;
          transition: all 0.2s;
        }

        .entry-card:hover {
          border-color: rgba(139, 92, 246, 0.4);
          transform: translateY(-2px);
        }

        .entry-avatar {
          width: 40px;
          height: 40px;
          flex-shrink: 0;
        }

        .entry-avatar img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
        }

        .entry-avatar .avatar-placeholder {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: linear-gradient(135deg, #8b5cf6, #ec4899);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: #fff;
        }

        .entry-info {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .entry-name {
          font-weight: 500;
          color: #fff;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .entry-id {
          font-size: 0.75rem;
          color: #8b5cf6;
          font-family: 'Space Mono', monospace;
        }

        .entries-more {
          text-align: center;
          padding: 1.5rem;
          color: #a1a1aa;
          font-style: italic;
          font-size: 1.1rem;
        }

        .entries-empty {
          text-align: center;
          padding: 2rem;
          color: #71717a;
        }

        /* Tournament Live Badge */
        .tournament-live-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.5);
          border-radius: 20px;
          color: #ef4444;
          font-weight: 700;
          font-size: 0.9rem;
          margin-top: 0.75rem;
          animation: livePulse 2s ease-in-out infinite;
        }

        @keyframes livePulse {
          0%, 100% { box-shadow: 0 0 10px rgba(239, 68, 68, 0.3); }
          50% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.6); }
        }

        .live-dot {
          width: 10px;
          height: 10px;
          background: #ef4444;
          border-radius: 50%;
          animation: blink 1s ease-in-out infinite;
        }

        /* No Tournament Section */
        .no-tournament-section {
          max-width: 800px;
          margin: 0 auto;
        }

        .no-tournament-message {
          text-align: center;
          padding: 3rem 2rem;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 16px;
          margin: 2rem 0;
        }

        .no-tournament-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .no-tournament-message h2 {
          margin: 0 0 1rem 0;
          font-size: 1.5rem;
        }

        .no-tournament-message p {
          color: #a1a1aa;
          max-width: 500px;
          margin: 0 auto 1.5rem;
        }

        .no-tournament-links {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .no-tournament-links .social-link {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s;
        }

        .no-tournament-links .social-link.twitch {
          background: rgba(145, 70, 255, 0.2);
          border: 1px solid rgba(145, 70, 255, 0.4);
          color: #9146ff;
        }

        .no-tournament-links .social-link.discord {
          background: rgba(88, 101, 242, 0.2);
          border: 1px solid rgba(88, 101, 242, 0.4);
          color: #5865f2;
        }

        .no-tournament-links .social-link:hover {
          transform: translateY(-2px);
        }

        /* Previous Tournaments Section */
        .previous-tournaments-section {
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .previous-tournaments-section h2 {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .previous-tournaments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .previous-tournament-card {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.05));
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: 1.25rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .previous-tournament-card:hover {
          transform: translateY(-4px);
          border-color: rgba(139, 92, 246, 0.6);
          box-shadow: 0 8px 25px rgba(139, 92, 246, 0.2);
        }

        .pt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .pt-header h3 {
          margin: 0;
          font-size: 1.1rem;
        }

        .pt-prize-pool {
          background: rgba(251, 191, 36, 0.2);
          color: #fbbf24;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .pt-podium-mini {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .pt-winner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: rgba(251, 191, 36, 0.1);
          border-radius: 10px;
        }

        .pt-avatar {
          width: 40px;
          height: 40px;
        }

        .pt-avatar img, .pt-avatar .avatar-placeholder {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #fbbf24;
        }

        .pt-medal {
          font-size: 1.25rem;
        }

        .pt-name {
          flex: 1;
          font-weight: 500;
        }

        .pt-runners {
          display: flex;
          gap: 0.5rem;
        }

        .pt-runner {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          font-size: 0.9rem;
        }

        .pt-view-bracket {
          text-align: center;
          margin-top: 1rem;
          color: #a78bfa;
          font-size: 0.9rem;
        }

        /* Past Tournament View */
        .past-tournament-view {
          max-width: 100%;
        }

        .back-to-tournaments-btn {
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #fff;
          cursor: pointer;
          margin-bottom: 1rem;
          transition: all 0.2s;
        }

        .back-to-tournaments-btn:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        /* Past Tournament Podium */
        .past-tournament-podium {
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 1rem;
          padding: 2rem 1rem 0 1rem;
          margin: 2rem 0;
        }

        .podium-spot {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .podium-spot.first {
          order: 2;
        }

        .podium-spot.second {
          order: 1;
        }

        .podium-spot.third {
          order: 3;
        }

        .podium-crown {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          animation: bounce 1s ease infinite;
        }

        .podium-avatar {
          width: 80px;
          height: 80px;
          margin-bottom: 0.5rem;
          flex-shrink: 0;
        }

        .podium-avatar img, .podium-avatar .avatar-placeholder {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          display: block;
        }

        .podium-spot.first .podium-avatar {
          width: 100px;
          height: 100px;
        }

        .podium-spot.first .podium-avatar img,
        .podium-spot.first .podium-avatar .avatar-placeholder {
          width: 100px;
          height: 100px;
          border: 3px solid #fbbf24;
        }

        .podium-spot.second .podium-avatar,
        .podium-spot.third .podium-avatar {
          width: 70px;
          height: 70px;
        }

        .podium-spot.second .podium-avatar img,
        .podium-spot.second .podium-avatar .avatar-placeholder {
          width: 70px;
          height: 70px;
          border: 3px solid #94a3b8;
        }

        .podium-spot.third .podium-avatar img,
        .podium-spot.third .podium-avatar .avatar-placeholder {
          width: 70px;
          height: 70px;
          border: 3px solid #cd7f32;
        }

        .podium-name {
          font-weight: 600;
          font-size: 0.95rem;
          margin-bottom: 0.25rem;
          color: #fff;
          line-height: 1.2;
        }

        .podium-spot.first .podium-name {
          font-size: 1.1rem;
          color: #fbbf24;
        }

        .podium-prize {
          color: #a1a1aa;
          font-size: 0.85rem;
          margin-bottom: 0.5rem;
        }

        .podium-block {
          width: 130px;
          border-radius: 8px 8px 0 0;
          font-weight: 700;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .first-block {
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          color: #000;
          height: 100px;
          width: 150px;
        }

        .second-block {
          background: linear-gradient(135deg, #94a3b8, #64748b);
          color: #000;
          height: 70px;
        }

        .third-block {
          background: linear-gradient(135deg, #cd7f32, #a0522d);
          color: #fff;
          height: 50px;
        }

        /* Admin Complete Tournament */
        .admin-complete-tournament {
          text-align: center;
          margin: 2rem 0;
          padding: 1.5rem;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 12px;
        }

        .complete-tournament-btn {
          padding: 1rem 2rem;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border: none;
          border-radius: 10px;
          color: #fff;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .complete-tournament-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
        }

        /* Tournament Bracket Styles */
        .tournament-bracket-section {
          background: linear-gradient(135deg, rgba(14, 14, 18, 0.9), rgba(20, 20, 30, 0.8));
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          margin-top: 2rem;
        }

        .tournament-bracket-section h2 {
          text-align: center;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
          background: linear-gradient(135deg, #a78bfa, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .bracket-admin-section {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .bracket-admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .bracket-admin-header h3 {
          margin: 0;
          font-size: 1.1rem;
        }

        .bracket-admin-tabs {
          display: flex;
          gap: 0.5rem;
        }

        .bracket-admin-tabs button {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          border: 1px solid rgba(139, 92, 246, 0.3);
          background: transparent;
          color: #a1a1aa;
          cursor: pointer;
          transition: all 0.2s;
        }

        .bracket-admin-tabs button.active {
          background: rgba(139, 92, 246, 0.3);
          color: #fff;
          border-color: #8b5cf6;
        }

        .bracket-picker-admin {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .picker-status {
          text-align: center;
        }

        .player-count {
          font-size: 1.25rem;
          font-weight: 700;
          color: #a78bfa;
        }

        .picker-progress-bar {
          height: 8px;
          background: rgba(139, 92, 246, 0.2);
          border-radius: 4px;
          overflow: hidden;
          margin-top: 0.5rem;
        }

        .picker-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #8b5cf6, #a855f7);
          border-radius: 4px;
          transition: width 0.5s ease;
        }

        .picker-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .pick-player-btn {
          padding: 1rem 2rem;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border: none;
          border-radius: 12px;
          color: #fff;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s;
        }

        .pick-player-btn:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 4px 20px rgba(34, 197, 94, 0.4);
        }

        .pick-player-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .reset-bracket-btn {
          padding: 1rem 1.5rem;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.5);
          border-radius: 12px;
          color: #ef4444;
          font-weight: 600;
          cursor: pointer;
        }

        .undo-pick-btn {
          padding: 1rem 1.5rem;
          background: rgba(251, 191, 36, 0.2);
          border: 1px solid rgba(251, 191, 36, 0.5);
          border-radius: 12px;
          color: #fbbf24;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .undo-pick-btn:hover:not(:disabled) {
          background: rgba(251, 191, 36, 0.3);
        }

        .undo-pick-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .selected-players-list h4 {
          margin: 0 0 1rem 0;
          color: #a1a1aa;
        }

        .selected-players-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 0.75rem;
        }

        .selected-player-card {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          position: relative;
        }

        .selected-player-card .player-seed {
          font-size: 0.75rem;
          color: #71717a;
          min-width: 24px;
        }

        .selected-player-card .player-avatar img,
        .selected-player-card .player-avatar .avatar-placeholder {
          width: 28px;
          height: 28px;
          border-radius: 50%;
        }

        .selected-player-card .player-name {
          flex: 1;
          font-size: 0.85rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .remove-player-btn {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ef4444;
          border: none;
          color: #fff;
          font-size: 0.75rem;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .selected-player-card:hover .remove-player-btn {
          opacity: 1;
        }

        .replace-player-btn {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(251, 191, 36, 0.9);
          border: none;
          color: #000;
          font-size: 0.7rem;
          cursor: pointer;
          opacity: 0;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .selected-player-card:hover .replace-player-btn {
          opacity: 1;
        }

        .replace-player-btn:hover {
          transform: scale(1.1);
          background: #fbbf24;
        }

        .selected-players-hint {
          font-size: 0.8rem;
          color: #71717a;
          margin-bottom: 0.75rem;
        }

        /* Match Result Editor */
        .bracket-matches-admin .matches-hint {
          color: #a1a1aa;
          margin-bottom: 1rem;
        }

        .match-result-editor {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 12px;
          padding: 1.25rem;
        }

        .match-result-editor h4 {
          margin: 0 0 1rem 0;
          text-align: center;
        }

        .match-players {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .match-player {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          min-width: 150px;
        }

        .match-player.selected-winner {
          background: rgba(34, 197, 94, 0.2);
          border: 2px solid #22c55e;
        }

        .match-player .player-name {
          font-weight: 600;
        }

        .match-player input {
          width: 120px;
          padding: 0.5rem;
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(0, 0, 0, 0.3);
          color: #fff;
          text-align: center;
        }

        .match-player input.slot-input {
          width: 140px;
          font-size: 0.85rem;
        }

        .match-players .vs {
          font-weight: 700;
          color: #ef4444;
          font-size: 1.25rem;
        }

        .winner-btn {
          padding: 0.4rem 0.75rem;
          border-radius: 6px;
          border: 1px solid rgba(34, 197, 94, 0.5);
          background: transparent;
          color: #22c55e;
          cursor: pointer;
          transition: all 0.2s;
        }

        .winner-btn.active {
          background: #22c55e;
          color: #fff;
        }

        .match-slot-input {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .match-slot-input input {
          padding: 0.5rem 1rem;
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(0, 0, 0, 0.3);
          color: #fff;
          width: 200px;
        }

        .match-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
        }

        .save-match-btn {
          padding: 0.6rem 1.5rem;
          background: #22c55e;
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }

        .cancel-match-btn {
          padding: 0.6rem 1.5rem;
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #a1a1aa;
          cursor: pointer;
        }

        /* Bracket Display */
        .bracket-container {
          overflow-x: auto;
          padding: 2rem 0;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 12px;
          margin-top: 1rem;
        }

        .bracket-rounds {
          display: flex;
          gap: 0.5rem;
          min-width: max-content;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .bracket-round {
          display: flex;
          flex-direction: column;
          min-width: 160px;
        }

        .round-header {
          text-align: center;
          font-weight: 700;
          padding: 0.6rem 1rem;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(168, 85, 247, 0.2));
          border: 1px solid rgba(139, 92, 246, 0.4);
          border-radius: 8px;
          font-size: 0.8rem;
          margin-bottom: 1rem;
          color: #a78bfa;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .round-matches {
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          flex: 1;
        }

        /* Spacing between matches based on round */
        .round-32 .round-matches {
          gap: 4px;
        }

        .round-16 .round-matches {
          gap: 4px;
        }

        .round-16 .bracket-match {
          margin: 18px 0;
        }

        .quarter-finals .bracket-match {
          margin: 58px 0;
        }

        .semi-finals .bracket-match {
          margin: 138px 0;
        }

        .finals .bracket-match {
          margin: 298px 0;
        }

        .bracket-match {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.05));
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 6px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
        }

        .bracket-match::after {
          content: '';
          position: absolute;
          right: -8px;
          top: 50%;
          width: 8px;
          height: 1px;
          background: rgba(255, 255, 255, 0.2);
        }

        .finals .bracket-match::after {
          display: none;
        }

        .bracket-match:hover {
          border-color: rgba(139, 92, 246, 0.6);
          transform: scale(1.02);
          z-index: 10;
        }

        .bracket-match.completed {
          border-color: rgba(34, 197, 94, 0.5);
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.1));
        }

        .bracket-match.editing {
          border-color: #fbbf24;
          box-shadow: 0 0 15px rgba(251, 191, 36, 0.4);
        }

        .match-slot {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.35rem 0.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          font-size: 0.75rem;
          min-height: 24px;
        }

        .match-slot:last-child {
          border-bottom: none;
        }

        .match-slot .seed {
          font-size: 0.65rem;
          color: #71717a;
          min-width: 20px;
          font-weight: 600;
        }

        .match-slot .name {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: 500;
        }

        .match-slot .multi {
          color: #fbbf24;
          font-weight: 700;
          font-size: 0.7rem;
          background: rgba(251, 191, 36, 0.2);
          padding: 0.1rem 0.3rem;
          border-radius: 4px;
        }

        .match-slot .slot-tag {
          color: #a78bfa;
          font-size: 0.6rem;
          background: rgba(139, 92, 246, 0.2);
          padding: 0.1rem 0.25rem;
          border-radius: 3px;
          max-width: 50px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .match-slot .tbd {
          color: #71717a;
          font-style: italic;
        }

        .match-slot.empty {
          opacity: 0.5;
        }

        .match-slot.winner {
          background: rgba(34, 197, 94, 0.2);
        }

        .match-slot.winner .name {
          color: #22c55e;
          font-weight: 600;
        }

        .match-slot.loser {
          opacity: 0.5;
        }

        .match-slot.loser .name {
          text-decoration: line-through;
        }

        .match-slot.champion {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(245, 158, 11, 0.2));
        }

        .match-slot.champion .name {
          color: #fbbf24;
          font-weight: 700;
        }

        .match-slot-name {
          padding: 0.15rem 0.4rem;
          background: rgba(139, 92, 246, 0.3);
          font-size: 0.65rem;
          color: #c4b5fd;
          text-align: center;
          border-top: 1px solid rgba(139, 92, 246, 0.2);
        }

        .finals-match {
          border: 2px solid #fbbf24 !important;
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.1)) !important;
        }

        .finals .round-header {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(245, 158, 11, 0.2));
          border-color: rgba(251, 191, 36, 0.5);
          color: #fbbf24;
        }

        /* Champion Display */
        .champion-display {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 1.5rem 2rem;
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.1));
          border: 2px solid #fbbf24;
          border-radius: 16px;
          margin-top: 1rem;
          animation: championGlow 2s ease-in-out infinite;
          position: relative;
        }

        .champion-display::before {
          content: '🎊';
          position: absolute;
          top: -15px;
          left: 20px;
          font-size: 1.5rem;
          animation: confetti 1s ease infinite;
        }

        .champion-display::after {
          content: '🎊';
          position: absolute;
          top: -15px;
          right: 20px;
          font-size: 1.5rem;
          animation: confetti 1s ease infinite 0.5s;
        }

        @keyframes confetti {
          0%, 100% { transform: rotate(-10deg); }
          50% { transform: rotate(10deg); }
        }
        }

        @keyframes championGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(251, 191, 36, 0.3); }
          50% { box-shadow: 0 0 40px rgba(251, 191, 36, 0.6); }
        }

        .champion-crown {
          font-size: 2.5rem;
          animation: bounce 1s ease infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .champion-avatar img,
        .champion-avatar .avatar-placeholder {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          border: 3px solid #fbbf24;
        }

        .champion-name {
          font-size: 1.5rem;
          font-weight: 700;
          color: #fbbf24;
        }

        .champion-title {
          font-size: 0.9rem;
          color: #f59e0b;
          letter-spacing: 2px;
          font-weight: 600;
        }

        .bracket-empty {
          text-align: center;
          padding: 3rem;
          color: #71717a;
        }

        /* Pick Animation Overlay */
        .pick-animation-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .pick-animation-content {
          text-align: center;
          animation: scaleIn 0.5s ease;
        }

        @keyframes scaleIn {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .pick-sparkles {
          font-size: 3rem;
          animation: sparkle 1s ease infinite;
        }

        .pick-sparkles.bottom {
          margin-top: 1rem;
        }

        @keyframes sparkle {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.2) rotate(10deg); }
        }

        .pick-text {
          font-size: 3rem;
          font-weight: 900;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 1rem 0;
          animation: pulse 0.5s ease infinite;
        }

        .picked-player-card {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(168, 85, 247, 0.2));
          border: 3px solid #8b5cf6;
          border-radius: 20px;
          padding: 2rem 3rem;
          margin: 1rem 0;
          animation: slideUp 0.5s ease 0.2s both;
        }

        @keyframes slideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .picked-avatar img,
        .picked-avatar .avatar-placeholder {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          border: 4px solid #a78bfa;
          margin-bottom: 1rem;
        }

        .picked-name {
          font-size: 2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.5rem;
        }

        .picked-seed {
          font-size: 1.25rem;
          color: #a78bfa;
          font-weight: 600;
        }

        .pick-dismiss-hint {
          margin-top: 1.5rem;
          color: #71717a;
          font-size: 0.9rem;
          animation: fadeInUp 0.5s ease 0.5s both;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .pick-animation-overlay {
          cursor: pointer;
        }

        /* Community Hunt Page */
        .community-hunt-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem;
        }

        .ch-hunt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .ch-hunt-title {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .ch-hunt-title h2 {
          margin: 0;
          font-size: 1.5rem;
        }

        .ch-status-badge {
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .ch-status-badge.setup {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .ch-status-badge.live {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          animation: pulse 2s ease-in-out infinite;
        }

        .ch-status-badge.calculating {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
        }

        .ch-tracker-section {
          margin-bottom: 2rem;
        }

        /* Community Hunt Live Content - Two column layout */
        .ch-live-content {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        @media (max-width: 900px) {
          .ch-live-content {
            grid-template-columns: 1fr;
          }
        }

        .ch-widget-left {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Community Hunt Info Panel */
        .ch-info-section {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 1rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 1rem;
        }

        .ch-info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .ch-info-label {
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        .ch-info-value {
          font-weight: 600;
          color: #fff;
        }

        .ch-info-value.highlight {
          color: #fbbf24;
          font-family: 'Space Mono', monospace;
        }

        .ch-info-value.green {
          color: #22c55e;
          font-family: 'Space Mono', monospace;
          font-size: 1.1rem;
        }

        .ch-info-row.end-balance {
          padding-top: 0.75rem;
          border-top: 1px solid rgba(34, 197, 94, 0.3);
          margin-top: 0.5rem;
        }

        .ch-how-it-works {
          padding: 1rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 1rem;
        }

        .ch-how-it-works h4 {
          margin: 0 0 0.75rem 0;
          font-size: 0.9rem;
          color: #a1a1aa;
        }

        .ch-how-it-works ol {
          margin: 0;
          padding-left: 1.25rem;
          color: #d4d4d8;
          font-size: 0.85rem;
        }

        .ch-how-it-works li {
          margin-bottom: 0.4rem;
        }

        .ch-hunt-status-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #ef4444;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #ef4444;
          animation: pulse 2s ease-in-out infinite;
        }

        .status-dot.live {
          background: #ef4444;
        }

        /* Legacy tracker styles - keeping for backward compatibility */
        .ch-live-tracker {
          margin-bottom: 2rem;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(168, 85, 247, 0.1) 100%);
          border: 2px solid rgba(139, 92, 246, 0.4);
          border-radius: 16px;
          padding: 1.5rem;
          position: relative;
          overflow: hidden;
        }

        .ch-live-tracker::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #8b5cf6, #a855f7, #8b5cf6);
          background-size: 200% 100%;
          animation: shimmer 2s linear infinite;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .ch-live-tracker-inner {
          position: relative;
          z-index: 1;
        }

        .ch-tracker-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
        }

        .ch-tracker-stat {
          background: rgba(14, 14, 18, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 1rem 1.25rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .ch-tracker-stat:hover {
          transform: translateY(-2px);
          border-color: rgba(139, 92, 246, 0.4);
        }

        .ch-tracker-stat.equity {
          border-color: rgba(139, 92, 246, 0.3);
          background: rgba(139, 92, 246, 0.1);
        }

        .ch-tracker-stat.participants {
          border-color: rgba(59, 130, 246, 0.3);
          background: rgba(59, 130, 246, 0.1);
        }

        .ch-tracker-stat.end {
          border-color: rgba(34, 197, 94, 0.3);
          background: rgba(34, 197, 94, 0.1);
        }

        .ch-tracker-label {
          display: block;
          font-size: 0.8rem;
          color: #a1a1aa;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .ch-tracker-value {
          display: block;
          font-family: 'Space Mono', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
        }

        .ch-tracker-stat.equity .ch-tracker-value {
          color: #a78bfa;
        }

        .ch-tracker-stat.participants .ch-tracker-value {
          color: #60a5fa;
        }

        .ch-tracker-stat.end .ch-tracker-value {
          color: #22c55e;
        }

        .ch-tracker-value.highlight {
          color: #22c55e;
          text-shadow: 0 0 20px rgba(34, 197, 94, 0.5);
        }

        .tracker-balance.total-equity {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%);
          border: 1px solid rgba(139, 92, 246, 0.3);
        }

        /* Admin Section */
        .ch-admin-section {
          background: rgba(14, 14, 18, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .ch-admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .ch-admin-header h3 {
          margin: 0;
        }

        .ch-admin-tabs {
          display: flex;
          gap: 0.5rem;
        }

        .ch-admin-tabs button {
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #a1a1aa;
          cursor: pointer;
          transition: all 0.2s;
        }

        .ch-admin-tabs button.active {
          background: rgba(139, 92, 246, 0.2);
          border-color: rgba(139, 92, 246, 0.5);
          color: #a78bfa;
        }

        .ch-admin-settings {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .ch-setting-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .ch-setting-row label {
          min-width: 120px;
          color: #a1a1aa;
        }

        .ch-setting-row input {
          padding: 0.6rem 1rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #fff;
          font-size: 1rem;
          min-width: 200px;
        }

        .ch-status-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .ch-status-buttons button {
          padding: 0.6rem 1.2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
          cursor: pointer;
          transition: all 0.2s;
        }

        .ch-status-buttons button.active {
          background: rgba(139, 92, 246, 0.3);
          border-color: #8b5cf6;
        }

        .ch-status-buttons button.live.active {
          background: rgba(239, 68, 68, 0.3);
          border-color: #ef4444;
        }

        .ch-calculate-btn {
          padding: 0.6rem 1.2rem;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }

        .ch-setting-row.danger {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .ch-end-btn {
          padding: 0.6rem 1.2rem;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }

        .ch-delete-btn {
          padding: 0.6rem 1.2rem;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }

        /* Admin Participants */
        .ch-add-participant-btn {
          padding: 0.6rem 1.2rem;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 1rem;
        }

        .ch-participant-list-admin {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .ch-participant-admin-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .ch-participant-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .ch-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
        }

        .ch-avatar-placeholder {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .ch-avatar-placeholder.large {
          width: 60px;
          height: 60px;
          font-size: 1.5rem;
        }

        .ch-name {
          font-weight: 600;
        }

        .ch-identifier {
          color: #71717a;
          font-size: 0.85rem;
        }

        .ch-participant-values {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .ch-equity-input, .ch-payout-input {
          width: 80px;
          padding: 0.4rem 0.6rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          color: #fff;
          font-size: 0.9rem;
          text-align: right;
        }

        .ch-input-group {
          display: flex;
          align-items: center;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          overflow: hidden;
        }

        .ch-input-group.large {
          min-width: 150px;
        }

        .ch-input-prefix {
          padding: 0.4rem 0.5rem;
          background: rgba(255, 255, 255, 0.05);
          color: #71717a;
          font-size: 0.9rem;
        }

        .ch-input-group input {
          border: none;
          background: transparent;
          padding: 0.4rem 0.6rem;
          color: #fff;
          font-size: 0.9rem;
          width: 100%;
          min-width: 0;
        }

        .ch-input-group input:focus {
          outline: none;
        }

        .ch-setting-hint {
          font-size: 0.75rem;
          color: #71717a;
          font-style: italic;
        }

        .ch-readonly-value {
          padding: 0.6rem 1rem;
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          color: #a78bfa;
          font-weight: 600;
          font-family: 'Space Mono', monospace;
        }

        .ch-participant-header-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr auto;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          margin-bottom: 0.5rem;
          font-size: 0.75rem;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .ch-participant-admin-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr auto;
          gap: 0.5rem;
          align-items: center;
          padding: 0.75rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          margin-bottom: 0.5rem;
          position: relative;
        }

        .ch-user-details {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
        }

        .ch-user-details .ch-name {
          font-weight: 600;
        }

        .ch-user-details .ch-identifier {
          font-size: 0.75rem;
          color: #71717a;
        }

        .ch-participant-values {
          display: contents;
        }

        .ch-pl-badge {
          position: absolute;
          right: 50px;
          top: 50%;
          transform: translateY(-50%);
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .ch-pl-badge.profit {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .ch-pl-badge.loss {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        @media (max-width: 768px) {
          .ch-participant-header-row {
            display: none;
          }
          
          .ch-participant-admin-row {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }
          
          .ch-participant-values {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          
          .ch-pl-badge {
            position: static;
            transform: none;
          }
        }

        .ch-status-select {
          padding: 0.4rem 0.6rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          color: #fff;
          font-size: 0.9rem;
        }

        .ch-remove-btn {
          padding: 0.4rem 0.6rem;
          background: rgba(239, 68, 68, 0.2);
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        /* Participant Cards Grid */
        .ch-participants-section {
          margin-top: 2rem;
        }

        .ch-participants-section h3 {
          margin-bottom: 1rem;
        }

        .ch-participant-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .ch-participant-card {
          background: linear-gradient(135deg, rgba(14, 14, 18, 0.9) 0%, rgba(20, 20, 30, 0.9) 100%);
          border: 2px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: 1.25rem;
          text-align: center;
          position: relative;
          transition: all 0.3s ease;
        }

        .ch-participant-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(139, 92, 246, 0.2);
        }

        .ch-participant-card.profit {
          border-color: rgba(34, 197, 94, 0.5);
        }

        .ch-participant-card.loss {
          border-color: rgba(239, 68, 68, 0.5);
        }

        .ch-card-rank {
          position: absolute;
          top: -10px;
          left: -10px;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.85rem;
        }

        .ch-card-avatar {
          margin-bottom: 0.75rem;
        }

        .ch-card-avatar img {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          border: 2px solid rgba(139, 92, 246, 0.5);
        }

        .ch-card-name {
          font-weight: 700;
          font-size: 1.1rem;
          margin-bottom: 0.75rem;
        }

        .ch-card-equity, .ch-card-payout, .ch-card-profit {
          display: flex;
          justify-content: space-between;
          padding: 0.4rem 0;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .ch-card-equity .label, .ch-card-payout .label, .ch-card-profit .label {
          color: #71717a;
          font-size: 0.85rem;
        }

        .ch-card-equity .value {
          color: #a78bfa;
          font-weight: 600;
        }

        .ch-card-percent {
          padding: 0.5rem 0;
          margin-bottom: 0.25rem;
        }

        .ch-card-percent .percent-value {
          display: block;
          text-align: center;
          font-size: 0.85rem;
          font-weight: 600;
          color: #8b5cf6;
          margin-bottom: 0.35rem;
        }

        .ch-card-percent .percent-bar {
          height: 6px;
          background: rgba(139, 92, 246, 0.2);
          border-radius: 3px;
          overflow: hidden;
        }

        .ch-card-percent .percent-fill {
          height: 100%;
          background: linear-gradient(90deg, #8b5cf6, #a855f7);
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        .ch-card-payout .value {
          color: #fbbf24;
          font-weight: 600;
        }

        .ch-card-profit.positive .value {
          color: #22c55e;
          font-weight: 700;
        }

        .ch-card-profit.negative .value {
          color: #ef4444;
          font-weight: 700;
        }

        .ch-card-paid-badge {
          margin-top: 0.5rem;
          padding: 0.3rem 0.6rem;
          background: rgba(34, 197, 94, 0.2);
          border-radius: 12px;
          font-size: 0.8rem;
          color: #22c55e;
        }

        /* No Hunt State */
        .ch-no-hunt {
          text-align: center;
          padding: 3rem 1rem;
        }

        .ch-no-hunt-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .ch-no-hunt h2 {
          margin-bottom: 0.5rem;
        }

        .ch-no-hunt p {
          color: #a1a1aa;
          margin-bottom: 1.5rem;
        }

        .ch-discord-btn {
          display: inline-block;
          padding: 0.8rem 1.5rem;
          background: linear-gradient(135deg, #5865F2 0%, #4752c4 100%);
          border-radius: 10px;
          color: #fff;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }

        .ch-discord-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(88, 101, 242, 0.4);
        }

        .ch-admin-create {
          margin-top: 2rem;
          padding: 1.5rem;
          background: rgba(14, 14, 18, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
        }

        .ch-admin-create h3 {
          margin-bottom: 1rem;
        }

        .ch-create-form {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .ch-create-form input {
          padding: 0.6rem 1rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #fff;
          min-width: 150px;
        }

        .ch-create-form button {
          padding: 0.6rem 1.2rem;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }

        /* History Section */
        .ch-history-section {
          margin-top: 3rem;
        }

        .ch-history-section h2 {
          margin-bottom: 1.5rem;
        }

        .ch-history-card {
          background: rgba(14, 14, 18, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 1rem;
        }

        .ch-history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .ch-history-header h3 {
          margin: 0;
          font-size: 1.1rem;
        }

        .ch-history-date {
          color: #71717a;
          font-size: 0.9rem;
        }

        .ch-history-stats {
          display: flex;
          gap: 2rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .ch-history-stat {
          display: flex;
          flex-direction: column;
        }

        .ch-history-stat .label {
          color: #71717a;
          font-size: 0.8rem;
        }

        .ch-history-stat .value {
          font-weight: 600;
        }

        .ch-history-stat.profit .value {
          color: #22c55e;
        }

        .ch-history-stat.loss .value {
          color: #ef4444;
        }

        .ch-history-participants {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .ch-mini-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 2px solid rgba(139, 92, 246, 0.3);
        }

        .ch-mini-avatar-placeholder {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .ch-more-participants {
          color: #71717a;
          font-size: 0.85rem;
          margin-left: 0.5rem;
        }

        .ch-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .ch-pagination button {
          padding: 0.5rem 1rem;
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          color: #a78bfa;
          cursor: pointer;
          transition: all 0.2s;
        }

        .ch-pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ch-pagination span {
          color: #a1a1aa;
        }

        /* Modal */
        .ch-modal {
          max-width: 450px;
        }

        .ch-modal-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .ch-modal-form .form-row {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .ch-modal-form label {
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        .ch-modal-form input, .ch-modal-form select {
          padding: 0.6rem 1rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #fff;
          font-size: 1rem;
        }

        /* Challenges Page */
        .challenges-page {
          max-width: 1200px;
          margin: 0 auto;
        }

        .challenges-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .challenges-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }

        .challenges-subtitle {
          color: #a1a1aa;
          font-size: 1.1rem;
          margin-bottom: 1rem;
        }

        .challenges-shuffle-link {
          display: inline-block;
          padding: 0.6rem 1.2rem;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .challenges-shuffle-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4);
        }

        .challenges-admin-section {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 1rem 1.5rem;
          margin-bottom: 1.5rem;
        }

        .challenges-admin-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .challenges-admin-header h3 {
          margin: 0;
          color: #a78bfa;
        }

        .add-challenge-btn {
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .add-challenge-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
        }

        .admin-header-buttons {
          display: flex;
          gap: 0.75rem;
        }

        .view-claims-btn {
          padding: 0.5rem 1rem;
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 8px;
          color: #60a5fa;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-claims-btn:hover, .view-claims-btn.active {
          background: rgba(59, 130, 246, 0.3);
          border-color: #3b82f6;
        }

        .challenge-claims-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .challenge-claims-section h4 {
          margin: 0 0 1rem 0;
          color: #e4e4e7;
        }

        .claims-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .claim-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .claim-item-info {
          flex: 1;
          min-width: 0;
        }

        .claim-item-slot {
          font-weight: 600;
          color: #fff;
          margin-bottom: 0.25rem;
        }

        .claim-item-details {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          font-size: 0.85rem;
          color: #a1a1aa;
          margin-bottom: 0.25rem;
        }

        .claim-item-details strong {
          color: #e4e4e7;
        }

        .claim-item-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          font-size: 0.8rem;
          color: #71717a;
        }

        .claim-item-time {
          font-size: 0.75rem;
          color: #71717a;
          margin-top: 0.25rem;
        }

        .claim-item-actions {
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0;
        }

        .claim-action-btn {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .claim-action-btn.approve {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .claim-action-btn.approve:hover {
          background: #22c55e;
          color: #fff;
        }

        .claim-action-btn.reject {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .claim-action-btn.reject:hover {
          background: #ef4444;
          color: #fff;
        }

        .no-claims {
          color: #71717a;
          text-align: center;
          padding: 1rem;
          margin: 0;
        }

        .challenges-rules-section {
          background: rgba(30, 30, 40, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .challenges-rules-section h3 {
          margin: 0 0 1.25rem 0;
          text-align: center;
          color: #fff;
        }

        .rules-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        @media (max-width: 900px) {
          .rules-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 550px) {
          .rules-grid {
            grid-template-columns: 1fr;
          }
        }

        .rule-card {
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.25);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .rule-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .rule-content strong {
          display: block;
          color: #fff;
          margin-bottom: 0.25rem;
          font-size: 0.95rem;
        }

        .rule-content p {
          margin: 0;
          color: #a1a1aa;
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .challenges-section {
          margin-bottom: 2rem;
        }

        .challenges-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.25rem;
        }

        .challenges-section-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .challenges-controls {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .challenge-search {
          position: relative;
        }

        .challenge-search input {
          padding: 0.5rem 2rem 0.5rem 0.75rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #fff;
          font-size: 0.9rem;
          width: 180px;
        }

        .challenge-search input:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.5);
        }

        .challenge-search input::placeholder {
          color: #71717a;
        }

        .search-clear {
          position: absolute;
          right: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #71717a;
          cursor: pointer;
          font-size: 1.1rem;
          padding: 0;
          line-height: 1;
        }

        .search-clear:hover {
          color: #fff;
        }

        .challenge-sort {
          padding: 0.5rem 0.75rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #fff;
          font-size: 0.9rem;
          cursor: pointer;
        }

        .challenge-sort:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.5);
        }

        .challenges-section h2 {
          margin-bottom: 1.25rem;
          font-size: 1.25rem;
        }

        .challenges-section.completed-section h2 {
          color: #22c55e;
        }

        .challenges-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .challenge-card {
          position: relative;
          background: rgba(30, 30, 40, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
        }

        .challenge-card:hover {
          border-color: rgba(139, 92, 246, 0.5);
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }

        .challenge-card.completed {
          border-color: rgba(34, 197, 94, 0.3);
          opacity: 0.85;
        }

        .challenge-card.completed:hover {
          transform: none;
          box-shadow: none;
        }

        .completed-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(34, 197, 94, 0.15);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10;
          pointer-events: none;
        }

        .completed-badge {
          background: #22c55e;
          color: #fff;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 700;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }

        .completed-by {
          color: #fff;
          font-size: 0.9rem;
          background: rgba(0, 0, 0, 0.5);
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
        }

        .challenge-admin-actions {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          display: flex;
          gap: 0.25rem;
          z-index: 20;
        }

        .challenge-admin-btn {
          width: 28px;
          height: 28px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .challenge-admin-btn.complete {
          background: rgba(34, 197, 94, 0.8);
          color: #fff;
        }

        .challenge-admin-btn.complete:hover {
          background: #22c55e;
        }

        .challenge-admin-btn.delete {
          background: rgba(239, 68, 68, 0.8);
          color: #fff;
        }

        .challenge-admin-btn.delete:hover {
          background: #ef4444;
        }

        .challenge-admin-btn.reactivate {
          background: rgba(59, 130, 246, 0.8);
          color: #fff;
        }

        .challenge-admin-btn.reactivate:hover {
          background: #3b82f6;
        }

        .challenge-image {
          width: 100%;
          height: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.3);
        }

        .challenge-image img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .challenge-image-placeholder {
          font-size: 4rem;
        }

        .challenge-info {
          padding: 1rem;
        }

        .challenge-slot-name {
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
          color: #fff;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .challenge-requirements {
          display: flex;
          gap: 0.75rem;
          font-size: 0.9rem;
        }

        .challenge-multi {
          color: #fbbf24;
          font-weight: 600;
        }

        .challenge-bet {
          color: #a1a1aa;
        }

        .challenge-prize {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: rgba(34, 197, 94, 0.1);
          border-top: 1px solid rgba(34, 197, 94, 0.2);
        }

        .prize-icon {
          font-size: 1.25rem;
        }

        .prize-amount {
          font-size: 1.25rem;
          font-weight: 700;
          color: #22c55e;
        }

        .challenge-actions {
          display: flex;
          justify-content: center;
        }

        .challenge-play-btn, .challenge-claim-btn {
          padding: 0.75rem;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          text-align: center;
          text-decoration: none;
          font-size: 0.9rem;
          width: 100%;
        }

        .challenge-play-btn {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: #fff;
        }

        .challenge-play-btn:hover {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
        }

        .challenge-claim-btn {
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: #fff;
        }

        .challenge-claim-btn:hover {
          background: linear-gradient(135deg, #16a34a, #15803d);
        }

        .challenges-empty {
          grid-column: 1 / -1;
          text-align: center;
          padding: 3rem;
          color: #71717a;
        }

        .challenges-cta {
          text-align: center;
          padding: 2rem;
          background: rgba(139, 92, 246, 0.1);
          border-radius: 12px;
        }

        .challenges-cta p {
          color: #a1a1aa;
          margin-bottom: 1rem;
        }

        .challenges-signup-btn {
          display: inline-block;
          padding: 0.75rem 2rem;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: #fff;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .challenges-signup-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4);
        }

        /* Claim Modal */
        .claim-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .claim-modal {
          background: linear-gradient(135deg, #1a1a2e 0%, #16162a 100%);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .claim-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: #fff;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .claim-modal-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .claim-modal-header {
          padding: 1.5rem;
          text-align: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .claim-modal-header h2 {
          margin: 0 0 0.25rem 0;
        }

        .claim-modal-header p {
          color: #a1a1aa;
          margin: 0;
        }

        .claim-challenge-info {
          padding: 1rem 1.5rem;
          background: rgba(139, 92, 246, 0.1);
          text-align: center;
        }

        .claim-challenge-name {
          font-weight: 600;
          font-size: 1.1rem;
          margin-bottom: 0.25rem;
        }

        .claim-challenge-details {
          display: flex;
          justify-content: center;
          gap: 1rem;
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        .claim-prize {
          color: #22c55e;
          font-weight: 600;
        }

        .claim-form {
          padding: 1.5rem;
        }

        .claim-input-group {
          margin-bottom: 1rem;
        }

        .claim-input-group label {
          display: block;
          color: #e4e4e7;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }

        .claim-input-group label .required {
          color: #ef4444;
        }

        .claim-input-group input {
          width: 100%;
          padding: 0.75rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          color: #fff;
          font-size: 1rem;
        }

        .claim-input-group input:focus {
          outline: none;
          border-color: #8b5cf6;
        }

        .claim-input-group .input-hint {
          display: block;
          font-size: 0.8rem;
          color: #71717a;
          margin-top: 0.25rem;
        }

        .claim-input-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .claim-requirements-box {
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .claim-requirements-box h4 {
          margin: 0 0 0.5rem 0;
          color: #fbbf24;
          font-size: 0.95rem;
        }

        .claim-requirements-box p {
          color: #a1a1aa;
          font-size: 0.85rem;
          margin: 0 0 0.5rem 0;
        }

        .claim-requirements-box ul {
          margin: 0;
          padding-left: 1.25rem;
          color: #d1d5db;
          font-size: 0.85rem;
        }

        .claim-requirements-box li {
          margin-bottom: 0.25rem;
        }

        .claim-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1rem;
          cursor: pointer;
        }

        .claim-checkbox input {
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        .claim-checkbox span {
          color: #d1d5db;
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .claim-submit-btn {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .claim-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(34, 197, 94, 0.4);
        }

        .claim-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .claim-message {
          margin-top: 1rem;
          padding: 0.75rem;
          border-radius: 8px;
          text-align: center;
        }

        .claim-message.success {
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.4);
          color: #4ade80;
        }

        .claim-message.error {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.4);
          color: #f87171;
        }

        /* Highlights page */
        .highlights-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .highlights-intro {
          text-align: center;
          color: #a1a1aa;
          font-size: 1.1rem;
          margin-bottom: 2rem;
        }

        .highlights-intro a {
          color: #FF0000;
          text-decoration: none;
          font-weight: 600;
        }

        .highlights-intro a:hover {
          text-decoration: underline;
        }

        .shorts-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .short-card {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .short-card:hover {
          border-color: rgba(255, 0, 0, 0.5);
          box-shadow: 0 10px 40px rgba(255, 0, 0, 0.2);
          transform: translateY(-5px);
        }

        .short-embed {
          width: 100%;
          aspect-ratio: 9/16;
          border: none;
          background: #000;
        }

        .short-title {
          padding: 1rem;
          font-weight: 600;
          font-size: 0.95rem;
          color: #fff;
          text-align: center;
        }

        @media (max-width: 900px) {
          .shorts-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .shorts-grid {
            grid-template-columns: 1fr;
            max-width: 350px;
            margin-left: auto;
            margin-right: auto;
          }
        }

        .highlights-cta {
          text-align: center;
          margin-top: 2rem;
        }

        .highlights-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          background: #FF0000;
          border: none;
          border-radius: 12px;
          color: #fff;
          font-family: 'Outfit', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(255, 0, 0, 0.3);
        }

        .highlights-cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(255, 0, 0, 0.5);
        }

        .highlights-cta-btn svg {
          width: 24px;
          height: 24px;
        }

        /* Hidden preload container */
        .youtube-preload {
          position: absolute;
          left: -9999px;
          top: -9999px;
          width: 1px;
          height: 1px;
          overflow: hidden;
          pointer-events: none;
          visibility: hidden;
        }

        .youtube-preload iframe {
          width: 1px;
          height: 1px;
        }

        /* Bonus Hunts Page */
        .bonushunts-container {
          max-width: 1100px;
          margin: 0 auto;
        }

        .bonushunts-intro {
          text-align: center;
          margin-bottom: 2rem;
          color: #a1a1aa;
          font-size: 1.1rem;
        }

        /* Stats Row */
        .bonushunts-stats-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }

        @media (max-width: 768px) {
          .bonushunts-stats-row {
            grid-template-columns: 1fr;
          }
        }

        .bonushunts-stat-card {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.2s ease;
        }

        .bonushunts-stat-card:hover {
          transform: translateY(-3px);
          border-color: rgba(139, 92, 246, 0.4);
        }

        .bonushunts-stat-card.biggest-multi {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%);
          border-color: rgba(255, 215, 0, 0.3);
        }

        .bonushunts-stat-card.hot-slot {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
          border-color: rgba(239, 68, 68, 0.3);
        }

        .bonushunts-stat-card.cold-slot {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%);
          border-color: rgba(59, 130, 246, 0.3);
        }

        .stat-icon {
          font-size: 2.5rem;
        }

        .stat-content {
          flex: 1;
        }

        .stat-label {
          color: #a1a1aa;
          font-size: 0.85rem;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          font-family: 'Space Mono', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
        }

        .bonushunts-stat-card.hot-slot .stat-value { color: #ef4444; }
        .bonushunts-stat-card.cold-slot .stat-value { color: #3b82f6; }
        .bonushunts-stat-card.biggest-multi .stat-value { color: #ffd700; }

        .stat-slot {
          color: #71717a;
          font-size: 0.9rem;
          margin-top: 0.25rem;
        }

        /* Live Hunt Section */
        .live-hunt-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .live-hunt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .live-hunt-header h2 {
          font-size: 1.25rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0;
        }

        .live-indicator {
          width: 12px;
          height: 12px;
          background: #ef4444;
          border-radius: 50%;
          animation: livePulse 1.5s ease-in-out infinite;
        }

        @keyframes livePulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          50% { opacity: 0.8; box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        }

        .paused-indicator {
          font-size: 1rem;
        }

        .watch-live-btn {
          background: linear-gradient(135deg, #9146ff 0%, #772ce8 100%);
          color: #fff;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .watch-live-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(145, 70, 255, 0.4);
        }

        /* Live hunt content - side by side */
        .live-hunt-content {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 1.5rem;
          align-items: start;
        }

        @media (max-width: 900px) {
          .live-hunt-content {
            grid-template-columns: 1fr;
          }
        }

        .live-hunt-widget-container {
          background: #0e0e12;
          border-radius: 12px;
          overflow: hidden;
          height: 420px;
        }

        .live-hunt-widget-container.predictions-page {
          height: 520px;
        }

        .live-hunt-widget-container.offline {
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 250px;
        }

        .myrtle-widget {
          width: 166.67%;
          height: 700px;
          border: none;
          background: transparent;
          transform: scale(0.6);
          transform-origin: top left;
          pointer-events: auto;
        }

        .myrtle-widget.predictions-page {
          width: 142.86%;
          height: 743px;
          transform: scale(0.7);
        }

        /* Scroll to prediction button */
        .scroll-to-prediction-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.9rem;
          margin-top: 0.75rem;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1));
          border: 2px solid rgba(34, 197, 94, 0.4);
          border-radius: 10px;
          color: #22c55e;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s ease;
          animation: glowPulse 2s ease-in-out infinite;
        }

        .scroll-to-prediction-btn:hover {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.2));
          border-color: rgba(34, 197, 94, 0.6);
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(34, 197, 94, 0.3);
        }

        .scroll-arrow {
          font-size: 1.2rem;
          animation: bounceDown 1.5s ease-in-out infinite;
        }

        @keyframes glowPulse {
          0%, 100% {
            box-shadow: 0 0 5px rgba(34, 197, 94, 0.2), 0 0 10px rgba(34, 197, 94, 0.1);
          }
          50% {
            box-shadow: 0 0 15px rgba(34, 197, 94, 0.4), 0 0 25px rgba(34, 197, 94, 0.2);
          }
        }

        @keyframes bounceDown {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(4px);
          }
        }

        /* Live Hunt Stats Panel */
        .live-hunt-stats-panel {
          background: rgba(15, 15, 20, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1.25rem;
          height: 420px;
          display: flex;
          flex-direction: column;
        }

        .live-hunt-stats-panel.enhanced {
          background: linear-gradient(180deg, rgba(20, 20, 35, 0.95) 0%, rgba(15, 15, 25, 0.95) 100%);
          border: 1px solid rgba(139, 92, 246, 0.3);
          height: auto;
          min-height: 420px;
        }

        .live-hunt-stats-panel.enhanced.predictions-page {
          min-height: 520px;
        }

        .stats-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .stats-panel-header h3 {
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0;
          color: #fff;
        }

        .live-badge-small {
          font-size: 0.7rem;
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-weight: 600;
          animation: pulse 2s infinite;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .stat-card {
          background: rgba(14, 14, 18, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.15);
          border-radius: 10px;
          padding: 0.85rem;
          text-align: center;
        }

        .stat-card-label {
          display: block;
          font-size: 0.75rem;
          color: #71717a;
          margin-bottom: 0.35rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-card-value {
          font-family: 'Space Mono', monospace;
          font-weight: 700;
          font-size: 1.25rem;
          color: #fff;
        }

        .stat-card-value.gold {
          color: #ffd700;
        }

        .prizes-section {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%);
          border: 1px solid rgba(255, 215, 0, 0.2);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .prizes-section.no-margin {
          margin-bottom: 0;
          flex: 1;
        }

        .prizes-note {
          text-align: center;
          font-size: 0.8rem;
          color: #a1a1aa;
          margin: 0.75rem 0 0 0;
        }

        .prizes-pending {
          text-align: center;
          color: #f59e0b;
          font-size: 0.9rem;
          padding: 1rem 0;
          margin: 0;
        }

        .predictions-count-display {
          background: rgba(139, 92, 246, 0.15);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
          margin: 1rem 0;
        }

        .predictions-count-number {
          display: block;
          font-size: 2rem;
          font-weight: 700;
          color: #a78bfa;
          font-family: 'Space Mono', monospace;
        }

        .predictions-count-label {
          font-size: 0.8rem;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Predictions Widget Left Column */
        .predictions-widget-left {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .predictions-widget-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.75rem;
        }

        .widget-stat-box {
          background: rgba(14, 14, 18, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
        }

        .widget-stat-number {
          display: block;
          font-family: 'Space Mono', monospace;
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffd700;
          margin-bottom: 0.25rem;
        }

        .widget-stat-label {
          font-size: 0.7rem;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .predictions-widget-buttons {
          display: flex;
          gap: 0.75rem;
        }

        .widget-stream-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 1rem;
          background: linear-gradient(135deg, #9333ea, #ec4899);
          border-radius: 10px;
          color: #fff;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.2s;
        }

        .widget-stream-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(147, 51, 234, 0.4);
        }

        .widget-stream-btn.discord {
          background: linear-gradient(135deg, #5865F2, #4752c4);
        }

        .widget-stream-btn.discord:hover {
          box-shadow: 0 4px 20px rgba(88, 101, 242, 0.4);
        }

        .widget-predict-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.85rem 1rem;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1));
          border: 2px solid rgba(34, 197, 94, 0.4);
          border-radius: 10px;
          color: #22c55e;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          animation: glowPulse 2s ease-in-out infinite;
        }

        .widget-predict-btn:hover {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.2));
          border-color: rgba(34, 197, 94, 0.6);
          transform: translateY(-2px);
        }

        /* How It Works section */
        .prediction-how-it-works {
          background: rgba(14, 14, 18, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.15);
          border-radius: 12px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .prediction-how-it-works h4 {
          font-size: 0.9rem;
          color: #a1a1aa;
          margin: 0 0 0.75rem 0;
          text-align: center;
        }

        .prediction-how-it-works ol {
          margin: 0;
          padding-left: 1.25rem;
          font-size: 0.85rem;
          color: #71717a;
        }

        .prediction-how-it-works li {
          margin-bottom: 0.35rem;
        }

        /* Prediction Status Badge */
        .prediction-status-badge {
          text-align: center;
          padding: 0.75rem;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.9rem;
          margin-top: 1rem;
        }

        .prediction-status-badge.open {
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.3);
          color: #22c55e;
        }

        .prediction-status-badge.closed {
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }

        @media (max-width: 600px) {
          .predictions-widget-stats {
            grid-template-columns: 1fr;
          }
          .predictions-widget-buttons {
            flex-direction: column;
          }
        }

        .prediction-starting-balance {
          background: rgba(255, 215, 0, 0.1);
          border: 1px solid rgba(255, 215, 0, 0.25);
          border-radius: 10px;
          padding: 0.75rem 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .starting-balance-label {
          font-size: 0.85rem;
          color: #a1a1aa;
        }

        .starting-balance-value {
          font-family: 'Space Mono', monospace;
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffd700;
        }

        .prizes-header {
          font-size: 0.9rem;
          font-weight: 700;
          color: #ffd700;
          margin-bottom: 0.75rem;
          text-align: center;
        }

        .prizes-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .prize-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.3);
        }

        .prize-item.gold {
          background: rgba(255, 215, 0, 0.15);
          border-left: 3px solid #ffd700;
        }

        .prize-item.silver {
          background: rgba(192, 192, 192, 0.1);
          border-left: 3px solid #c0c0c0;
        }

        .prize-item.bronze {
          background: rgba(205, 127, 50, 0.1);
          border-left: 3px solid #cd7f32;
        }

        .prize-rank {
          font-size: 0.85rem;
          font-weight: 600;
          color: #a1a1aa;
        }

        .prize-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: #fff;
        }

        .watch-stream-cta.enhanced {
          display: block;
          text-align: center;
          padding: 0.85rem;
          background: linear-gradient(135deg, #9333ea, #ec4899);
          border-radius: 10px;
          color: #fff;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.2s;
          margin-top: auto;
        }

        .watch-stream-cta.enhanced:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(147, 51, 234, 0.4);
        }

        .live-hunt-stats-panel h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 1rem 0;
          color: #fff;
        }

        .live-stat-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0.8rem;
          background: rgba(14, 14, 18, 0.8);
          border-radius: 8px;
          margin-bottom: 0.4rem;
        }

        .live-stat-row:last-of-type {
          margin-bottom: 0;
        }

        .live-stat-row.highlight {
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(236, 72, 153, 0.2));
          border: 1px solid rgba(168, 85, 247, 0.4);
        }

        .live-stat-label {
          color: #a1a1aa;
          font-size: 0.8rem;
        }

        .live-stat-value {
          font-family: 'Space Mono', monospace;
          font-weight: 600;
          color: #ffd700;
          font-size: 0.95rem;
        }

        .live-stat-value.required {
          color: #fbbf24;
          font-size: 1rem;
        }

        .live-hunt-cta {
          margin-top: auto;
          text-align: center;
          padding-top: 0.5rem;
        }

        .watch-stream-btn {
          display: inline-block;
          background: linear-gradient(135deg, #9333ea, #ec4899);
          color: #fff;
          padding: 0.6rem 1.25rem;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.85rem;
          transition: all 0.2s ease;
        }

        .watch-stream-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(147, 51, 234, 0.4);
        }

        .no-active-hunt-stats {
          text-align: center;
          padding: 2rem 1rem;
          color: #71717a;
        }

        .no-active-hunt-stats .hint {
          font-size: 0.85rem;
          margin-top: 0.5rem;
        }

        .offline-message {
          text-align: center;
          padding: 2rem;
        }

        .offline-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .offline-message h3 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }

        .offline-message p {
          color: #71717a;
          margin-bottom: 1.5rem;
        }

        .follow-btn {
          background: linear-gradient(135deg, #9146ff 0%, #772ce8 100%);
          color: #fff;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
          display: inline-block;
          transition: all 0.2s ease;
        }

        .follow-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(145, 70, 255, 0.4);
        }

        /* Hunt History */
        .hunt-history-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 1.5rem;
        }

        .hunt-history-section h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0 0 0.25rem 0;
        }

        .hunt-history-subtitle {
          color: #71717a;
          font-size: 0.9rem;
          margin: 0 0 1.5rem 0;
        }

        .hunt-history-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .hunt-history-card {
          border-radius: 12px;
          padding: 1rem 1.25rem;
          transition: transform 0.2s ease;
        }

        .hunt-history-card:hover {
          transform: translateX(5px);
        }

        .hunt-history-card.profit {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.08) 100%);
          border: 1px solid rgba(34, 197, 94, 0.3);
        }

        .hunt-history-card.loss {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.08) 100%);
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .hunt-card-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .hunt-number {
          font-weight: 700;
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        .hunt-date {
          font-family: 'Space Mono', monospace;
          color: #fff;
          font-size: 0.9rem;
        }

        .hunt-result-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: auto;
        }

        .hunt-result-badge.profit {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .hunt-result-badge.loss {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .hunt-card-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 0.75rem;
        }

        @media (max-width: 600px) {
          .hunt-card-stats {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .hunt-stat {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .hunt-stat-label {
          font-size: 0.75rem;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hunt-stat-value {
          font-family: 'Space Mono', monospace;
          font-weight: 600;
          font-size: 1rem;
          color: #fff;
        }

        .hunt-stat-value.profit { color: #22c55e; }
        .hunt-stat-value.loss { color: #ef4444; }

        .hunt-card-best {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-top: 0.75rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          flex-wrap: wrap;
        }

        .best-label {
          color: #a1a1aa;
          font-size: 0.85rem;
        }

        .best-slot-name {
          color: #a855f7;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .best-hit-details {
          color: #71717a;
          font-size: 0.8rem;
        }

        .best-multi-value {
          font-family: 'Space Mono', monospace;
          font-weight: 700;
          color: #ffd700;
          font-size: 1rem;
          margin-left: auto;
        }

        .hunt-history-empty {
          text-align: center;
          padding: 3rem;
          color: #71717a;
        }

        .hunt-history-empty .hint {
          font-size: 0.85rem;
          margin-top: 0.5rem;
        }

        /* Pagination */
        .hunt-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1.5rem;
          flex-wrap: wrap;
        }

        .pagination-btn {
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          color: #fff;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-family: 'Outfit', sans-serif;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .pagination-btn:hover:not(:disabled) {
          background: rgba(139, 92, 246, 0.3);
        }

        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .pagination-pages {
          display: flex;
          gap: 0.25rem;
        }

        .pagination-page {
          background: transparent;
          border: 1px solid rgba(139, 92, 246, 0.2);
          color: #a1a1aa;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          font-family: 'Outfit', sans-serif;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .pagination-page:hover {
          border-color: rgba(139, 92, 246, 0.4);
          color: #fff;
        }

        .pagination-page.active {
          background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
          border-color: transparent;
          color: #fff;
          font-weight: 600;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .pagination-info {
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        /* Previous Winners Section */
        .previous-winners-section {
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(139, 92, 246, 0.2);
        }

        .previous-winners-title {
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          color: #fff;
        }

        .previous-winners-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .previous-hunt-card {
          background: rgba(14, 14, 18, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1rem 1.25rem;
        }

        .previous-hunt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .previous-hunt-id {
          font-weight: 600;
          color: #a78bfa;
        }

        .previous-hunt-date {
          color: #71717a;
          font-size: 0.85rem;
        }

        .previous-hunt-balance {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .previous-hunt-start {
          color: #a1a1aa;
        }

        .previous-hunt-arrow {
          color: #71717a;
        }

        .previous-hunt-end {
          color: #22c55e;
          font-weight: 600;
        }

        .previous-hunt-winners {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .previous-winner-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(20, 20, 30, 0.5);
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          flex: 1;
          min-width: 150px;
        }

        .previous-winner-item.rank-1 {
          border: 1px solid rgba(255, 215, 0, 0.3);
        }

        .previous-winner-item.rank-2 {
          border: 1px solid rgba(192, 192, 192, 0.3);
        }

        .previous-winner-item.rank-3 {
          border: 1px solid rgba(205, 127, 50, 0.3);
        }

        .previous-winner-medal {
          font-size: 1.1rem;
        }

        .previous-winner-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          object-fit: cover;
        }

        .previous-winner-avatar-placeholder {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 700;
          color: #fff;
        }

        .previous-winner-info {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 0;
        }

        .previous-winner-name {
          font-size: 0.85rem;
          color: #fff;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .previous-winner-reward {
          font-size: 0.7rem;
          color: #fbbf24;
          font-weight: 500;
        }

        .previous-winner-prediction {
          font-size: 0.8rem;
          color: #22c55e;
          font-weight: 600;
          white-space: nowrap;
        }

        /* Admin Pagination */
        .admin-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(139, 92, 246, 0.1);
        }

        /* Stat appearances */
        .stat-appearances {
          color: #71717a;
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }

        /* Floating Live Button */
        .floating-live-btn {
          position: fixed;
          bottom: 24px;
          right: 24px;
          background: linear-gradient(135deg, #9146ff 0%, #772ce8 100%);
          color: #fff;
          padding: 0.75rem 1.25rem;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 700;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 20px rgba(145, 70, 255, 0.5);
          z-index: 1000;
          animation: floatPulse 2s ease-in-out infinite;
          transition: transform 0.2s ease;
        }

        .floating-live-btn:hover {
          transform: scale(1.05);
        }

        @keyframes floatPulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(145, 70, 255, 0.5); }
          50% { box-shadow: 0 4px 30px rgba(145, 70, 255, 0.8); }
        }

        .floating-live-indicator {
          width: 10px;
          height: 10px;
          background: #ef4444;
          border-radius: 50%;
          animation: livePulse 1.5s ease-in-out infinite;
        }

        /* When the FAB occupies the bottom-right corner, sit the LIVE pill to its
           left so the two float horizontally (instead of stacking vertically). */
        .floating-live-btn--with-fab {
          right: 94px;
          bottom: 33px;
        }
        @media (max-width: 520px) {
          .floating-live-btn--with-fab { right: 82px; bottom: 25px; }
        }

        /* ========== POINTS SYSTEM STYLES ========== */

        /* Navbar Points Badge */
        .nav-points-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          background: linear-gradient(135deg, rgba(234, 179, 8, 0.15), rgba(234, 179, 8, 0.05));
          border: 1px solid rgba(234, 179, 8, 0.3);
          border-radius: 20px;
          padding: 4px 12px;
          cursor: pointer;
          transition: all 0.2s;
          margin-right: 8px;
        }
        .nav-points-badge:hover {
          background: linear-gradient(135deg, rgba(234, 179, 8, 0.25), rgba(234, 179, 8, 0.1));
          border-color: rgba(234, 179, 8, 0.5);
        }
        .nav-points-icon { font-size: 0.85rem; }
        .nav-points-value { font-size: 0.8rem; font-weight: 700; color: #eab308; }

        /* Navbar VIP Badge */
        .nav-vip-badge {
          display: flex;
          align-items: center;
          gap: 5px;
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(217, 119, 6, 0.08));
          border: 1px solid rgba(245, 158, 11, 0.35);
          border-radius: 20px;
          padding: 4px 12px;
          cursor: pointer;
          transition: all 0.2s;
          margin-right: 8px;
          position: relative;
          overflow: hidden;
        }
        .nav-vip-badge::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(254, 243, 199, 0.15), transparent);
          transition: left 0.6s;
        }
        .nav-vip-badge:hover::before { left: 100%; }
        .nav-vip-badge:hover {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.28), rgba(217, 119, 6, 0.15));
          border-color: rgba(251, 191, 36, 0.6);
          box-shadow: 0 0 12px rgba(245, 158, 11, 0.25);
        }
        .nav-vip-text { font-size: 0.78rem; font-weight: 800; color: #fbbf24; letter-spacing: 0.5px; position: relative; z-index: 1; }
        
        /* Mobile/tablet: hide the inline nav counter (per-page counters still show) and shrink VIP label */
        @media (max-width: 1024px) {
          .nav-total-counter { display: none !important; }
        }
        @media (max-width: 640px) {
          .nav-vip-text { display: none; }
          .nav-vip-badge { padding: 4px 8px !important; }
        }

        .dropdown-points-badge {
          font-size: 0.7rem; background: rgba(234, 179, 8, 0.15); color: #eab308;
          padding: 2px 8px; border-radius: 10px; margin-left: auto; font-weight: 600;
        }

        .sidebar-points-balance {
          display: flex; align-items: center; gap: 6px; padding: 8px 12px; margin: 4px 8px;
          background: linear-gradient(135deg, rgba(234, 179, 8, 0.1), rgba(234, 179, 8, 0.03));
          border: 1px solid rgba(234, 179, 8, 0.2); border-radius: 8px; cursor: pointer; transition: all 0.2s;
        }
        .sidebar-points-balance:hover {
          background: linear-gradient(135deg, rgba(234, 179, 8, 0.2), rgba(234, 179, 8, 0.08));
          border-color: rgba(234, 179, 8, 0.4);
        }
        .sidebar-points-icon { font-size: 0.9rem; }
        .sidebar-points-amount { font-weight: 700; color: #eab308; font-size: 0.9rem; }
        .sidebar-points-label { color: #9ca3af; font-size: 0.75rem; }

        .points-shop-container { max-width: 1000px; margin: 0 auto; }
        .points-shop-intro { color: #9ca3af; text-align: center; margin-bottom: 1.5rem; font-size: 0.95rem; }

        .points-balance-card {
          background: linear-gradient(135deg, #1e1b3a 0%, #1a1333 100%);
          border: 1px solid rgba(234, 179, 8, 0.25); border-radius: 16px; padding: 1.5rem; margin-bottom: 1.5rem;
        }
        .points-balance-main { display: flex; align-items: center; gap: 1rem; }
        .points-balance-icon { font-size: 2.5rem; }
        .points-balance-number {
          font-size: 2.2rem; font-weight: 800;
          background: linear-gradient(135deg, #eab308, #f59e0b);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; line-height: 1;
        }
        .points-balance-label { color: #9ca3af; font-size: 0.85rem; margin-top: 2px; }
        .points-breakdown-row {
          display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 1rem; padding-top: 1rem;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        /* Source chips — one bordered pill per earning source, scannable at a glance */
        .points-breakdown-item {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 0.3rem 0.7rem; font-size: 0.78rem;
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 999px;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .points-breakdown-item:hover {
          border-color: rgba(168,85,247,0.35);
          background: rgba(168,85,247,0.07);
        }
        .breakdown-source { color: #a1a1aa; }
        .breakdown-earned { color: #4ade80; font-weight: 700; font-family: 'Space Mono', monospace; font-size: 0.74rem; }

        .points-login-prompt {
          text-align: center; padding: 2rem; background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; margin-bottom: 1.5rem;
        }
        .points-login-icon { font-size: 2.5rem; margin-bottom: 0.75rem; }
        .points-login-prompt p { color: #9ca3af; margin-bottom: 1rem; }

        .points-tabs {
          display: flex; gap: 4px; margin-bottom: 1.5rem; background: rgba(255,255,255,0.03);
          padding: 4px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.06);
        }
        .points-tab {
          flex: 1; padding: 10px 16px; background: none; border: none; color: #9ca3af;
          font-size: 0.9rem; font-weight: 500; cursor: pointer; border-radius: 8px; transition: all 0.2s;
        }
        .points-tab:hover { color: white; background: rgba(255,255,255,0.05); }
        .points-tab.active { color: white; background: rgba(168, 85, 247, 0.2); border: 1px solid rgba(168, 85, 247, 0.3); }

        .points-shop-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1rem; margin-bottom: 2rem;
        }
        .shop-controls {
          display: flex; gap: 0.75rem; margin-bottom: 1rem;
        }
        .shop-search-input {
          flex: 1; padding: 10px 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px; color: white; font-size: 0.85rem; outline: none; transition: border-color 0.2s;
        }
        .shop-search-input:focus { border-color: rgba(139,92,246,0.5); }
        .shop-search-input::placeholder { color: #6b7280; }
        .shop-sort-select {
          padding: 10px 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px; color: white; font-size: 0.85rem; outline: none; cursor: pointer; min-width: 160px;
        }
        .shop-sort-select option { background: #1a1a2e; color: white; }
        #wc-tier option { background: #1a1a2e; color: #e2e8f0; }
        @media (max-width: 500px) {
          .shop-controls { flex-direction: column; }
          .shop-sort-select { min-width: unset; }
        }
        .points-shop-card {
          background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px; overflow: hidden; transition: all 0.2s; display: flex; flex-direction: column;
        }
        .points-shop-card:hover {
          border-color: rgba(168, 85, 247, 0.3); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        }
        .points-shop-card.out-of-stock { opacity: 0.5; }
        .shop-card-image { width: 100%; height: 160px; overflow: hidden; background: rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; }
        .shop-card-image img { width: 100%; height: 100%; object-fit: contain; }
        .shop-card-body { padding: 1rem; display: flex; flex-direction: column; flex: 1; }
        .shop-card-name { font-size: 1.05rem; font-weight: 700; margin-bottom: 0.4rem; color: white; }
        .shop-card-desc { font-size: 0.82rem; color: #9ca3af; margin-bottom: 0.75rem; line-height: 1.4; flex: 1; }
        .shop-card-meta { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; }
        .shop-card-stock, .shop-card-limit {
          font-size: 0.7rem; padding: 2px 8px; border-radius: 6px; background: rgba(255,255,255,0.05); color: #9ca3af;
        }
        .shop-card-footer {
          display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
          padding-top: 0.75rem; border-top: 1px solid rgba(255,255,255,0.06);
        }
        .shop-card-cost { display: flex; align-items: center; gap: 4px; }
        .shop-cost-icon { font-size: 1rem; }
        .shop-cost-amount { font-size: 1.1rem; font-weight: 800; color: #eab308; }
        .shop-redeem-btn {
          padding: 8px 18px; background: linear-gradient(135deg, #a855f7, #7c3aed); color: white;
          border: none; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
        }
        .shop-redeem-btn:hover:not(:disabled) { background: linear-gradient(135deg, #b975f9, #8b5cf6); transform: translateY(-1px); }
        .shop-redeem-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .shop-redeem-btn.full-width { width: 100%; margin-top: 1rem; padding: 12px; }

        .points-history-container { margin-bottom: 2rem; }
        .points-history-section { margin-bottom: 1.5rem; }
        .points-history-section-title { font-size: 1rem; font-weight: 600; margin-bottom: 0.75rem; color: #d1d5db; }
        .points-history-list { display: flex; flex-direction: column; gap: 6px; }
        .points-history-item {
          display: flex; align-items: center; justify-content: space-between; padding: 10px 14px;
          background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 10px; gap: 1rem;
        }
        .points-history-item:hover { background: rgba(255,255,255,0.04); }
        .history-item-left { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; }
        .history-item-source { font-size: 1.2rem; flex-shrink: 0; }
        .history-item-name, .history-item-detail {
          font-size: 0.85rem; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .history-item-date { font-size: 0.72rem; color: #6b7280; }
        .history-item-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .history-item-cost { font-weight: 600; color: #f87171; font-size: 0.85rem; }
        .history-item-status { font-size: 0.72rem; padding: 2px 8px; border-radius: 6px; }
        .history-item-status.pending { background: rgba(234, 179, 8, 0.15); color: #eab308; }
        .history-item-status.fulfilled { background: rgba(74, 222, 128, 0.15); color: #4ade80; }
        .history-item-status.cancelled { background: rgba(248, 113, 113, 0.15); color: #f87171; }
        .history-item-amount { font-weight: 700; font-size: 0.9rem; flex-shrink: 0; }
        .history-item-amount.positive { color: #4ade80; }
        .history-item-amount.negative { color: #f87171; }

        .points-pagination { display: flex; align-items: center; justify-content: center; gap: 1rem; margin-top: 1rem; }
        .points-pagination button {
          padding: 6px 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          color: white; border-radius: 8px; cursor: pointer; font-size: 0.82rem;
        }
        .points-pagination button:disabled { opacity: 0.3; cursor: not-allowed; }
        .points-pagination span { font-size: 0.82rem; color: #9ca3af; }

        .points-leaderboard-list { display: flex; flex-direction: column; gap: 6px; }
        .points-lb-row {
          display: flex; align-items: center; gap: 12px; padding: 10px 14px;
          background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 10px;
        }
        .points-lb-row.top-1 { border-color: rgba(234, 179, 8, 0.3); background: rgba(234, 179, 8, 0.05); }
        .points-lb-row.top-2 { border-color: rgba(192, 192, 192, 0.3); background: rgba(192, 192, 192, 0.03); }
        .points-lb-row.top-3 { border-color: rgba(205, 127, 50, 0.3); background: rgba(205, 127, 50, 0.03); }
        .points-lb-rank { font-size: 1.1rem; width: 36px; text-align: center; flex-shrink: 0; }
        .points-lb-user { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
        .points-lb-avatar { width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0; }
        .points-lb-name { font-weight: 600; font-size: 0.9rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .points-lb-balance { font-weight: 700; color: #eab308; font-size: 0.9rem; flex-shrink: 0; }

        .points-how-to-earn { margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid rgba(255,255,255,0.06); }
        .points-how-to-earn h3 { text-align: center; font-size: 1.1rem; margin-bottom: 1rem; color: #d1d5db; }
        .points-earn-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 0.75rem; }
        .points-earn-card {
          text-align: center; padding: 1.25rem 1rem; background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06); border-radius: 12px;
        }
        .earn-card-icon { font-size: 1.8rem; display: block; margin-bottom: 0.5rem; }
        .points-earn-card h4 { font-size: 0.9rem; margin-bottom: 0.3rem; }
        .points-earn-card p { font-size: 0.78rem; color: #9ca3af; line-height: 1.4; }

        .points-loading, .points-empty {
          text-align: center; padding: 3rem 1rem; color: #9ca3af; grid-column: 1 / -1;
        }
        .points-empty-icon { font-size: 3rem; margin-bottom: 0.75rem; }

        .points-message {
          position: fixed; bottom: 2rem; left: 50%; transform: translateX(-50%);
          padding: 12px 24px; border-radius: 12px; font-size: 0.9rem; font-weight: 500;
          z-index: 1000; display: flex; align-items: center; gap: 8px; animation: slideUp 0.3s ease;
        }
        .points-message.success { background: rgba(74, 222, 128, 0.15); border: 1px solid rgba(74, 222, 128, 0.3); color: #4ade80; }
        .points-message.error { background: rgba(248, 113, 113, 0.15); border: 1px solid rgba(248, 113, 113, 0.3); color: #f87171; }
        .points-message-close { background: none; border: none; color: inherit; font-size: 1.2rem; cursor: pointer; margin-left: 8px; }
        @keyframes slideUp {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }

        .redeem-modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7);
          display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem;
        }
        .redeem-modal {
          background: #1f2937; border: 1px solid rgba(255,255,255,0.1); border-radius: 16px;
          padding: 2rem; max-width: 420px; width: 100%; position: relative;
        }
        .redeem-modal h2 { font-size: 1.2rem; margin-bottom: 0.5rem; }
        .redeem-modal-cost { color: #eab308; margin-bottom: 1rem; font-weight: 600; }
        .redeem-modal-close {
          position: absolute; top: 1rem; right: 1rem; background: none; border: none;
          color: #9ca3af; font-size: 1.5rem; cursor: pointer;
        }
        .redeem-modal-field { margin-bottom: 1rem; }
        .redeem-modal-field label { display: block; font-size: 0.85rem; color: #9ca3af; margin-bottom: 0.4rem; }
        .redeem-modal-field input {
          width: 100%; padding: 10px 14px; background: rgba(0,0,0,0.3);
          border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: white; font-size: 0.9rem;
        }
        .redeem-modal-field input:focus { outline: none; border-color: rgba(168, 85, 247, 0.5); }

        /* ========== END POINTS SYSTEM STYLES ========== */

        /* Discord Connect Button (Navbar) */
        .nav-discord-connect {
          display: flex;
          align-items: center;
          gap: 5px;
          background: linear-gradient(135deg, rgba(88, 101, 242, 0.2), rgba(88, 101, 242, 0.1));
          border: 1px solid rgba(88, 101, 242, 0.4);
          color: #7289da;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          margin-right: 6px;
        }
        .nav-discord-connect:hover {
          background: linear-gradient(135deg, rgba(88, 101, 242, 0.3), rgba(88, 101, 242, 0.15));
          border-color: rgba(88, 101, 242, 0.6);
          color: #99aaf5;
        }
        .nav-discord-connect svg { flex-shrink: 0; }

        /* Discord Status in Balance Card */
        .points-discord-status {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .discord-linked-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .discord-linked-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.82rem;
          color: #7289da;
          font-weight: 500;
        }
        .discord-linked-badge svg { color: #5865f2; }
        .discord-unlink-btn {
          background: none;
          border: 1px solid rgba(248, 113, 113, 0.2);
          color: #9ca3af;
          padding: 3px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          cursor: pointer;
          transition: all 0.15s;
        }
        .discord-unlink-btn:hover {
          color: #f87171;
          border-color: rgba(248, 113, 113, 0.4);
          background: rgba(248, 113, 113, 0.05);
        }
        .discord-connect-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 14px;
          background: linear-gradient(135deg, rgba(88, 101, 242, 0.12), rgba(88, 101, 242, 0.05));
          border: 1px solid rgba(88, 101, 242, 0.3);
          color: #7289da;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .discord-connect-btn:hover {
          background: linear-gradient(135deg, rgba(88, 101, 242, 0.2), rgba(88, 101, 242, 0.1));
          border-color: rgba(88, 101, 242, 0.5);
          color: #99aaf5;
        }
        .discord-connect-btn svg { flex-shrink: 0; }

        @media (max-width: 768px) {
          .nav-discord-connect .nav-discord-text { display: none; }
          .nav-discord-connect { padding: 6px 8px; margin-right: 4px; }
        }

        /* ========== ADMIN POINTS STYLES ========== */
        .admin-points-panel { }
        .admin-points-msg {
          padding: 10px 16px; border-radius: 8px; margin-bottom: 1rem; display: flex;
          align-items: center; gap: 8px; font-size: 0.85rem;
        }
        .admin-points-msg.success { background: rgba(74,222,128,0.1); border: 1px solid rgba(74,222,128,0.25); color: #4ade80; }
        .admin-points-msg.error { background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25); color: #f87171; }
        .admin-points-msg button {
          margin-left: auto; background: none; border: none; color: inherit; font-size: 1.1rem; cursor: pointer;
        }

        .admin-points-subtabs {
          display: flex; gap: 4px; margin-bottom: 1.25rem; flex-wrap: wrap;
        }
        .admin-points-subtabs button {
          padding: 7px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
          color: #9ca3af; font-size: 0.82rem; border-radius: 8px; cursor: pointer; transition: all 0.15s;
          display: flex; align-items: center; gap: 6px;
        }
        .admin-points-subtabs button:hover { color: white; border-color: rgba(255,255,255,0.15); }
        .admin-points-subtabs button.active {
          color: white; background: rgba(168,85,247,0.15); border-color: rgba(168,85,247,0.3);
        }

        .ap-section { margin-bottom: 1.5rem; }
        .ap-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
        .ap-section-title { font-size: 1rem; font-weight: 600; color: #d1d5db; margin-bottom: 0.5rem; }
        .ap-empty { text-align: center; padding: 2rem; color: #6b7280; font-size: 0.85rem; }

        .ap-source-list { display: flex; flex-direction: column; gap: 6px; }
        .ap-source-row {
          display: flex; align-items: center; gap: 1rem; padding: 8px 12px;
          background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 8px;
        }
        .ap-source-name { flex: 1; font-size: 0.85rem; }
        .ap-source-earned { color: #4ade80; font-weight: 600; font-size: 0.85rem; }
        .ap-source-txns { color: #6b7280; font-size: 0.75rem; }

        .ap-config-grid { display: flex; flex-direction: column; gap: 8px; }
        .ap-config-item {
          display: flex; align-items: center; justify-content: space-between; gap: 1rem;
          padding: 10px 14px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05);
          border-radius: 10px;
        }
        .ap-config-info { flex: 1; min-width: 0; }
        .ap-config-key {
          display: block; font-size: 0.82rem; font-weight: 600; color: #d1d5db; text-transform: capitalize;
        }
        .ap-config-desc { font-size: 0.72rem; color: #6b7280; }
        .ap-config-input {
          width: 100px; padding: 6px 10px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px; color: white; font-size: 0.85rem; text-align: right;
        }
        .ap-config-input:focus { outline: none; border-color: rgba(168,85,247,0.5); }
        .ap-toggle-btn {
          padding: 6px 16px; border: none; border-radius: 20px; font-size: 0.78rem; font-weight: 700;
          cursor: pointer; transition: all 0.2s; min-width: 60px;
        }
        .ap-toggle-btn.on { background: rgba(74,222,128,0.2); color: #4ade80; }
        .ap-toggle-btn.off { background: rgba(248,113,113,0.15); color: #f87171; }
        .ap-toggle-btn:hover { transform: scale(1.05); }

        .ap-add-btn {
          padding: 8px 16px; background: linear-gradient(135deg, #a855f7, #7c3aed); color: white;
          border: none; border-radius: 8px; font-size: 0.82rem; font-weight: 600; cursor: pointer;
        }
        .ap-add-btn:hover { opacity: 0.9; }

        .ap-shop-list { display: flex; flex-direction: column; gap: 6px; }
        .ap-shop-row {
          padding: 10px 14px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05);
          border-radius: 10px; display: flex; align-items: center; justify-content: space-between; gap: 1rem;
          flex-wrap: wrap;
        }
        .ap-shop-row.inactive { opacity: 0.5; }
        .ap-shop-row-main { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex: 1; }
        .ap-shop-name { font-weight: 600; font-size: 0.9rem; }
        .ap-shop-cost { color: #eab308; font-weight: 600; font-size: 0.82rem; }
        .ap-shop-status { font-size: 0.72rem; padding: 2px 8px; border-radius: 6px; }
        .ap-shop-status.active { background: rgba(74,222,128,0.1); color: #4ade80; }
        .ap-shop-status.inactive { background: rgba(248,113,113,0.1); color: #f87171; }
        .ap-shop-stock, .ap-shop-limit { font-size: 0.72rem; color: #6b7280; }
        .ap-shop-row-actions { display: flex; gap: 6px; flex-shrink: 0; }

        .ap-btn-sm {
          padding: 5px 12px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
          color: #d1d5db; border-radius: 6px; font-size: 0.75rem; cursor: pointer; transition: all 0.15s;
        }
        .ap-btn-sm:hover { background: rgba(255,255,255,0.1); }
        .ap-btn-sm.green { border-color: rgba(74,222,128,0.3); color: #4ade80; }
        .ap-btn-sm.green:hover { background: rgba(74,222,128,0.1); }
        .ap-btn-sm.red { border-color: rgba(248,113,113,0.3); color: #f87171; }
        .ap-btn-sm.red:hover { background: rgba(248,113,113,0.1); }

        .ap-filter-btns { display: flex; gap: 4px; }
        .ap-filter-btn {
          padding: 5px 12px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
          color: #9ca3af; font-size: 0.78rem; border-radius: 6px; cursor: pointer;
        }
        .ap-filter-btn.active { color: white; background: rgba(168,85,247,0.15); border-color: rgba(168,85,247,0.3); }

        .ap-redemptions-list { display: flex; flex-direction: column; gap: 8px; }
        .ap-redemption-row {
          padding: 12px 14px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05);
          border-radius: 10px; display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
        }
        .ap-redemption-info { flex: 1; min-width: 0; }
        .ap-redemption-user { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
        .ap-redemption-avatar { width: 28px; height: 28px; border-radius: 50%; }
        .ap-redemption-name { font-weight: 600; font-size: 0.85rem; }
        .ap-redemption-date { font-size: 0.7rem; color: #6b7280; }
        .ap-redemption-item { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .ap-redemption-item-name { font-size: 0.85rem; color: #d1d5db; }
        .ap-redemption-cost { font-size: 0.8rem; color: #eab308; font-weight: 600; }
        .ap-redemption-accounts { display: flex; gap: 10px; font-size: 0.75rem; color: #9ca3af; margin-bottom: 4px; }
        .ap-redemption-notes { font-size: 0.75rem; color: #6b7280; font-style: italic; }
        .ap-redemption-actions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; }

        .ap-grant-form { display: flex; flex-wrap: wrap; gap: 0.75rem; max-width: 600px; }
        .ap-grant-field { flex: 1; min-width: 200px; }
        .ap-grant-field.full { flex-basis: 100%; }
        .ap-grant-field label { display: block; font-size: 0.8rem; color: #9ca3af; margin-bottom: 4px; }
        .ap-grant-field input {
          width: 100%; padding: 9px 12px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px; color: white; font-size: 0.85rem;
        }
        .ap-grant-field input:focus { outline: none; border-color: rgba(168,85,247,0.5); }
        .ap-grant-submit {
          padding: 10px 24px; background: linear-gradient(135deg, #a855f7, #7c3aed); color: white;
          border: none; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer;
          width: 100%; transition: all 0.2s;
        }
        .ap-grant-submit:disabled { opacity: 0.4; cursor: not-allowed; }
        .ap-grant-submit:hover:not(:disabled) { opacity: 0.9; }

        .ap-checkbox-row {
          display: flex; gap: 1.5rem; flex-basis: 100%;
        }
        .ap-checkbox-row label {
          display: flex; align-items: center; gap: 6px; font-size: 0.82rem; color: #d1d5db; cursor: pointer;
        }
        .ap-checkbox-row input[type="checkbox"] { accent-color: #a855f7; }

        .ap-shop-modal {
          background: #1f2937; border: 1px solid rgba(255,255,255,0.1); border-radius: 16px;
          padding: 1.5rem; max-width: 560px; width: 100%; position: relative; max-height: 85vh; overflow-y: auto;
        }
        .ap-shop-modal h2 { font-size: 1.1rem; margin-bottom: 1rem; }
        .ap-shop-modal-form { display: flex; flex-wrap: wrap; gap: 0.75rem; }

        /* ========== END ADMIN POINTS STYLES ========== */

        /* Footer */
        .footer {
          background: #06060a;
          border-top: 1px solid rgba(139, 92, 246, 0.15);
          padding: 3rem 1.5rem;
          margin-top: auto;
          position: relative;
          z-index: 10;
        }

        .footer-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
        }

        .footer-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .footer-logo-img {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          opacity: 0.8;
        }

        .footer-logo-text {
          font-size: 1.5rem;
          font-weight: 800;
          color: #fff;
          opacity: 0.9;
        }

        .footer-logo-x {
          font-size: 1.25rem;
          color: #71717a;
          font-weight: 300;
        }

        .footer-shuffle-logo {
          height: 32px;
          opacity: 0.9;
        }

        .footer-disclaimer {
          color: #71717a;
          font-size: 0.9rem;
          line-height: 1.7;
          max-width: 600px;
          margin: 0 auto;
        }

        .footer-disclaimer a {
          color: #71717a;
          text-decoration: underline;
        }

        .footer-disclaimer a:hover {
          color: #a855f7;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          margin-top: 1.5rem;
          flex-wrap: wrap;
        }

        .footer-link {
          color: #71717a;
          font-size: 0.85rem;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .footer-link:hover {
          color: #a855f7;
        }

        /* Other pages styles */
        .gauntlet-container {
          text-align: center;
          max-width: 600px;
          margin: 0 auto;
        }

        .gauntlet-description {
          color: #a1a1aa;
          margin-bottom: 2rem;
          font-size: 1.1rem;
        }

        /* Spin Zone Selection */
        .spinzone-selection {
          display: flex;
          gap: 2rem;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 2rem;
        }

        .spinzone-card {
          background: rgba(20, 20, 30, 0.8);
          border: 2px solid rgba(139, 92, 246, 0.3);
          border-radius: 20px;
          padding: 2rem;
          width: 280px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .spinzone-card:hover {
          border-color: #a855f7;
          transform: translateY(-5px);
          box-shadow: 0 10px 40px rgba(139, 92, 246, 0.3);
        }

        .spinzone-card-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .spinzone-card-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #fff;
        }

        .spinzone-card-desc {
          color: #a1a1aa;
          font-size: 0.95rem;
        }

        .spinzone-back-btn {
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.4);
          color: #a855f7;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
          transition: all 0.3s ease;
        }

        .spinzone-back-btn:hover {
          background: rgba(139, 92, 246, 0.3);
        }

        /* Provider Filters - Redesigned */
        .filter-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .filter-label {
          font-size: 1rem;
          font-weight: 600;
          color: #fff;
        }

        .filter-actions {
          display: flex;
          gap: 0.5rem;
        }

        .filter-action-btn {
          background: rgba(139, 92, 246, 0.15);
          border: 1px solid rgba(139, 92, 246, 0.3);
          color: #a855f7;
          padding: 0.35rem 0.75rem;
          border-radius: 6px;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 0.8rem;
          transition: all 0.2s ease;
        }

        .filter-action-btn:hover {
          background: rgba(139, 92, 246, 0.25);
          border-color: #a855f7;
        }

        .provider-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 0.5rem;
        }

        .provider-checkbox {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.6rem 0.8rem;
          background: rgba(30, 30, 40, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          user-select: none;
        }

        .provider-checkbox:hover {
          border-color: rgba(139, 92, 246, 0.5);
          background: rgba(40, 40, 55, 0.8);
        }

        .provider-checkbox.checked {
          background: rgba(139, 92, 246, 0.15);
          border-color: #a855f7;
        }

        .provider-checkbox input {
          display: none;
        }

        .provider-checkbox .checkmark {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(139, 92, 246, 0.4);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          color: transparent;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .provider-checkbox.checked .checkmark {
          background: #a855f7;
          border-color: #a855f7;
          color: #fff;
        }

        .provider-checkbox .provider-name {
          font-size: 0.9rem;
          color: #d4d4d8;
        }

        .provider-checkbox.checked .provider-name {
          color: #fff;
        }

        .slot-count {
          text-align: center;
          color: #71717a;
          font-size: 0.85rem;
          margin-top: 1rem;
        }

        /* Horizontal Slot Reel */
        .slot-reel-wrapper {
          display: flex;
          justify-content: center;
          margin: 2rem 0;
        }

        .slot-reel-container {
          position: relative;
          width: 100%;
          max-width: 900px;
          height: 220px;
          overflow: hidden;
          border-radius: 16px;
          background: linear-gradient(135deg, #0a0a14 0%, #12121f 100%);
          border: 3px solid #8b5cf6;
          box-shadow: 0 0 60px rgba(139, 92, 246, 0.4), inset 0 0 100px rgba(0, 0, 0, 0.6);
        }

        /* Physical thump when the reel lands — pairs with the audio thunk */
        .slot-reel-container.landed {
          animation: reel-land-shake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97);
        }
        @keyframes reel-land-shake {
          0% { transform: translateX(0); }
          18% { transform: translateX(-7px); }
          38% { transform: translateX(5px); }
          58% { transform: translateX(-3px); }
          78% { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }

        .slot-reel-viewport {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          overflow: hidden;
        }

        .slot-reel-strip {
          display: flex;
          position: absolute;
          top: 20px;
          left: calc(50% - 80px);
          height: 180px;
        }

        .slot-reel-strip.spinning .slot-reel-item img {
          filter: blur(1px);
        }

        .slot-reel-item {
          width: 140px;
          height: 180px;
          flex-shrink: 0;
          margin: 0 10px;
          border-radius: 12px;
          overflow: hidden;
          background: rgba(20, 20, 30, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .slot-reel-item:first-child {
          margin-left: 10px;
        }

        .slot-reel-item img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 12px;
          transition: filter 0.3s ease;
        }

        .slot-reel-gradient-left {
          position: absolute;
          top: 0;
          left: 0;
          width: 150px;
          height: 100%;
          background: linear-gradient(to right, rgba(10, 10, 20, 1) 0%, rgba(10, 10, 20, 0.8) 40%, transparent 100%);
          pointer-events: none;
          z-index: 2;
        }

        .slot-reel-gradient-right {
          position: absolute;
          top: 0;
          right: 0;
          width: 150px;
          height: 100%;
          background: linear-gradient(to left, rgba(10, 10, 20, 1) 0%, rgba(10, 10, 20, 0.8) 40%, transparent 100%);
          pointer-events: none;
          z-index: 2;
        }

        .slot-reel-pointer {
          position: absolute;
          top: 5px;
          bottom: 5px;
          left: 50%;
          transform: translateX(-50%);
          width: 3px;
          border-radius: 2px;
          background: linear-gradient(to bottom, #ffd700, #ff8c00);
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 40px rgba(255, 140, 0, 0.4);
          z-index: 3;
        }

        .slot-reel-pointer::before {
          content: '';
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;
          border-top: 12px solid #ffd700;
          filter: drop-shadow(0 0 6px rgba(255, 215, 0, 0.8));
        }

        .slot-reel-pointer::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;
          border-bottom: 12px solid #ffd700;
          filter: drop-shadow(0 0 6px rgba(255, 215, 0, 0.8));
        }

        .slot-pointer-arrow {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          z-index: 4;
        }

        .slot-pointer-arrow.top {
          top: -5px;
          border-left: 12px solid transparent;
          border-right: 12px solid transparent;
          border-top: 15px solid #ffd700;
        }

        .slot-pointer-arrow.bottom {
          bottom: -5px;
          border-left: 12px solid transparent;
          border-right: 12px solid transparent;
          border-bottom: 15px solid #ffd700;
        }

        /* Slot Buttons Container */
        .slot-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
        }

        .shuffle-btn {
          background: rgba(139, 92, 246, 0.2);
          border: 2px solid rgba(139, 92, 246, 0.5);
          color: #a855f7;
          padding: 1rem 1.5rem;
          font-size: 1.1rem;
          font-family: 'Outfit', sans-serif;
          font-weight: 600;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .shuffle-btn:hover:not(:disabled) {
          background: rgba(139, 92, 246, 0.3);
          border-color: #a855f7;
          transform: translateY(-2px);
        }

        .shuffle-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Slot Result Popup Overlay */
        .slot-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease;
          backdrop-filter: blur(8px);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .slot-popup {
          background: linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%);
          border: 3px solid #8b5cf6;
          border-radius: 24px;
          padding: 2.5rem 2rem;
          text-align: center;
          position: relative;
          max-width: 90%;
          width: 380px;
          animation: popupBounce 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          box-shadow: 
            0 0 80px rgba(139, 92, 246, 0.6),
            0 0 120px rgba(139, 92, 246, 0.3),
            inset 0 0 60px rgba(139, 92, 246, 0.1);
        }

        @keyframes popupBounce {
          0% { 
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.95);
          }
          100% { 
            opacity: 1;
            transform: scale(1);
          }
        }

        .slot-popup::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(45deg, #8b5cf6, #a855f7, #c084fc, #8b5cf6);
          border-radius: 26px;
          z-index: -1;
          animation: borderGlow 2s ease-in-out infinite;
          background-size: 300% 300%;
        }

        @keyframes borderGlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .slot-popup-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: #a1a1aa;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .slot-popup-close:hover {
          background: rgba(255, 255, 255, 0.2);
          color: #fff;
        }

        .slot-popup-label {
          font-size: 1.2rem;
          color: #ffd700;
          margin-bottom: 1.5rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          font-weight: 600;
          text-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
        }

        .slot-popup-image {
          width: 180px;
          height: 240px;
          object-fit: contain;
          border-radius: 16px;
          margin: 0 auto 1.5rem;
          display: block;
          box-shadow: 
            0 10px 40px rgba(139, 92, 246, 0.5),
            0 0 60px rgba(139, 92, 246, 0.3);
          border: 3px solid rgba(139, 92, 246, 0.5);
          background: rgba(20, 20, 30, 0.8);
          animation: imageGlow 1.5s ease-in-out infinite alternate;
        }

        @keyframes imageGlow {
          from { box-shadow: 0 10px 40px rgba(139, 92, 246, 0.5), 0 0 60px rgba(139, 92, 246, 0.3); }
          to { box-shadow: 0 10px 50px rgba(139, 92, 246, 0.7), 0 0 80px rgba(139, 92, 246, 0.5); }
        }

        .slot-popup-name {
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.5rem;
        }

        .slot-popup-provider {
          color: #a855f7;
          font-size: 1.1rem;
          margin-bottom: 0;
        }

        .slot-popup-game-link {
          display: block;
          text-decoration: none;
          transition: transform 0.2s ease;
          margin-bottom: 1rem;
        }

        .slot-popup-game-link:hover {
          transform: scale(1.02);
        }

        .slot-popup-game-link:hover .slot-popup-image {
          box-shadow: 0 10px 50px rgba(139, 92, 246, 0.6);
        }

        .slot-popup-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
        }

        .slot-popup-play-btn {
          display: inline-block;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: #fff;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(34, 197, 94, 0.3);
        }

        .slot-popup-play-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
        }

        .slot-popup-respin-btn {
          display: inline-block;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          color: #fff;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
        }

        .slot-popup-respin-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
        }

        .slot-popup-hint {
          color: #71717a;
          font-size: 1rem;
        }

        /* ============================================
           CHAT PICKER STYLES
           ============================================ */
        
        .chat-setup {
          text-align: center;
          padding: 2rem 0;
        }

        .chat-setup-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .chat-setup-text {
          color: #a1a1aa;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
        }

        .chat-setup-text strong {
          color: #a855f7;
        }

        .chat-timer-options {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .chat-timer-btn {
          background: rgba(30, 30, 40, 0.8);
          border: 2px solid rgba(139, 92, 246, 0.3);
          color: #a1a1aa;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .chat-timer-btn:hover {
          border-color: rgba(139, 92, 246, 0.6);
        }

        .chat-timer-btn.active {
          background: rgba(139, 92, 246, 0.3);
          border-color: #a855f7;
          color: #fff;
        }

        .chat-hint {
          color: #71717a;
          font-size: 0.9rem;
          margin-top: 1.5rem;
        }

        /* Collecting Phase */
        .chat-collecting {
          text-align: center;
          padding: 2rem 0;
        }

        .chat-collecting-animation {
          position: relative;
          width: 100px;
          height: 100px;
          margin: 0 auto 2rem;
        }

        .chat-pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 80px;
          background: rgba(139, 92, 246, 0.3);
          border-radius: 50%;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.2; }
        }

        .raffle-live-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 320px;
          gap: 1rem;
          margin-bottom: 1.5rem;
          align-items: start;
        }
        @media (max-width: 768px) {
          .raffle-live-grid {
            grid-template-columns: 1fr;
          }
        }

        .chat-collecting-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 3rem;
          animation: bounce 1s ease-in-out infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translate(-50%, -50%) translateY(0); }
          50% { transform: translate(-50%, -50%) translateY(-5px); }
        }

        .chat-timer-display {
          margin-bottom: 1.5rem;
        }

        .chat-timer-number {
          font-size: 4rem;
          font-weight: 800;
          color: #a855f7;
          font-family: 'Space Mono', monospace;
        }

        .chat-timer-label {
          color: #71717a;
          font-size: 0.9rem;
        }

        .chat-messages-count {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 1rem 2rem;
          display: inline-block;
          margin-bottom: 1.5rem;
        }

        .chat-count-number {
          font-size: 2rem;
          font-weight: 700;
          color: #fff;
          margin-right: 0.5rem;
        }

        .chat-count-label {
          color: #a1a1aa;
        }

        .chat-live-feed {
          max-width: 400px;
          margin: 0 auto 1.5rem;
          text-align: left;
        }

        .chat-live-message {
          padding: 0.5rem 0;
          border-bottom: 1px solid rgba(139, 92, 246, 0.1);
          font-size: 0.9rem;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .chat-live-user {
          color: #a855f7;
          font-weight: 600;
          margin-right: 0.5rem;
        }

        .chat-live-text {
          color: #d4d4d8;
        }

        .chat-cancel-btn {
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.4);
          color: #ef4444;
          padding: 0.5rem 1.5rem;
          border-radius: 8px;
          font-family: 'Outfit', sans-serif;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .chat-cancel-btn:hover {
          background: rgba(239, 68, 68, 0.3);
        }

        /* Ready Phase - Heatmap */
        .chat-ready {
          text-align: center;
        }

        .chat-heatmap {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .chat-heatmap-title {
          color: #fff;
          font-weight: 600;
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .chat-heatmap-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;
        }

        .chat-suggestion {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          border: 1px solid;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }

        .chat-suggestion.spinning {
          transform: scale(1.1);
          box-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
        }

        .chat-suggestion.winner {
          background: rgba(139, 92, 246, 0.5) !important;
          border-color: #a855f7 !important;
          transform: scale(1.1);
          box-shadow: 0 0 30px rgba(139, 92, 246, 0.6);
        }

        .chat-suggestion-text {
          color: #fff;
          font-size: 0.9rem;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .chat-suggestion-count {
          background: rgba(255, 255, 255, 0.1);
          color: #a855f7;
          padding: 0.15rem 0.5rem;
          border-radius: 10px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .chat-more {
          color: #71717a;
          font-size: 0.85rem;
          margin-top: 1rem;
        }

        .chat-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .chat-no-results {
          padding: 2rem;
        }

        .chat-no-results-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .chat-no-results p {
          color: #a1a1aa;
          margin-bottom: 1.5rem;
        }

        /* Chat Result Popup */
        .chat-popup {
          width: 400px;
        }

        .chat-result-game {
          font-size: 2rem;
          font-weight: 800;
          color: #fff;
          margin: 1.5rem 0 1rem;
          word-break: break-word;
        }

        .chat-result-meta {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .chat-result-count {
          color: #a855f7;
          font-size: 1rem;
        }

        .chat-result-by {
          color: #a1a1aa;
          font-size: 0.95rem;
        }

        .chat-result-by strong {
          color: #fff;
        }

        .reel-wrapper {
          position: relative;
          margin-bottom: 2rem;
        }

        .reel-container {
          position: relative;
          width: 100%;
          height: 300px;
          background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%);
          border: 3px solid #8b5cf6;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 0 50px rgba(139, 92, 246, 0.3), inset 0 0 100px rgba(0, 0, 0, 0.5);
        }

        .reel-viewport {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
        }

        .reel-strip {
          display: flex;
          flex-direction: column;
          position: absolute;
          top: 100px;
          left: 0;
          right: 0;
        }

        .reel-item {
          height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          flex-shrink: 0;
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
        }

        .reel-pointer {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: calc(100% - 20px);
          height: 100px;
          border: 3px solid #a855f7;
          border-radius: 12px;
          pointer-events: none;
          box-shadow: 0 0 30px rgba(168, 85, 247, 0.4), inset 0 0 30px rgba(168, 85, 247, 0.1);
          z-index: 10;
        }

        .pointer-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          z-index: 11;
        }

        .pointer-arrow.left {
          left: -15px;
          border-top: 15px solid transparent;
          border-bottom: 15px solid transparent;
          border-right: 20px solid #a855f7;
          filter: drop-shadow(0 0 10px rgba(168, 85, 247, 0.8));
        }

        .pointer-arrow.right {
          right: -15px;
          border-top: 15px solid transparent;
          border-bottom: 15px solid transparent;
          border-left: 20px solid #a855f7;
          filter: drop-shadow(0 0 10px rgba(168, 85, 247, 0.8));
        }

        .reel-gradient-top, .reel-gradient-bottom {
          position: absolute;
          left: 0;
          right: 0;
          height: 100px;
          pointer-events: none;
          z-index: 5;
        }

        .reel-gradient-top {
          top: 0;
          background: linear-gradient(to bottom, rgba(15, 15, 26, 1) 0%, rgba(15, 15, 26, 0) 100%);
        }

        .reel-gradient-bottom {
          bottom: 0;
          background: linear-gradient(to top, rgba(15, 15, 26, 1) 0%, rgba(15, 15, 26, 0) 100%);
        }

        .spin-btn {
          padding: 1.25rem 3rem;
          font-size: 1.25rem;
          font-weight: 700;
          font-family: 'Outfit', sans-serif;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          border: none;
          border-radius: 16px;
          color: #fff;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4);
        }

        .spin-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(139, 92, 246, 0.5);
        }

        .spin-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .wheel-result {
          margin-top: 2rem;
          padding: 2rem;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%);
          border: 2px solid #8b5cf6;
          border-radius: 20px;
          animation: resultPop 0.5s ease;
        }

        @keyframes resultPop {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }

        .result-label { color: #a1a1aa; font-size: 1rem; margin-bottom: 0.5rem; }
        .result-game { font-size: 2.5rem; font-weight: 800; color: #ffd700; text-shadow: 0 0 30px rgba(255, 215, 0, 0.5); }
        .result-hint { color: #71717a; margin-top: 0.5rem; }

        .games-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 1rem;
          margin-top: 2rem;
          padding: 2rem;
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
        }

        .game-card {
          padding: 1.25rem;
          border-radius: 12px;
          font-weight: 600;
          text-align: center;
          transition: all 0.3s ease;
        }

        .game-card:hover { transform: scale(1.05); }

        /* Claim page styles */
        .claim-page-container {
          max-width: 600px;
          margin: 0 auto;
          padding: 1rem;
        }
        .claim-login-prompt {
          text-align: center;
          padding: 3rem 2rem;
          background: rgba(14, 14, 18, 0.8);
          border-radius: 16px;
          border: 1px solid rgba(139, 92, 246, 0.3);
        }
        .claim-login-prompt p {
          margin-bottom: 1.5rem;
          color: #a1a1aa;
        }
        .claim-loading {
          text-align: center;
          padding: 3rem 2rem;
          background: rgba(14, 14, 18, 0.8);
          border-radius: 16px;
          color: #a1a1aa;
        }
        .claim-intro {
          text-align: center;
          color: #a1a1aa;
          margin-bottom: 1.5rem;
        }
        .claim-wins-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .claim-win-card {
          display: grid;
          grid-template-columns: 50px 1fr auto auto;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%);
          border: 2px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .claim-win-card:hover {
          border-color: rgba(139, 92, 246, 0.6);
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(139, 92, 246, 0.2);
        }
        .claim-win-rank {
          font-size: 2rem;
          text-align: center;
        }
        .claim-win-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .claim-win-hunt {
          font-weight: 600;
          color: #fff;
        }
        .claim-win-prediction {
          font-size: 0.85rem;
          color: #a1a1aa;
        }
        .claim-win-reward {
          font-weight: 700;
          color: #22c55e;
          font-size: 1.1rem;
        }
        .claim-win-action {
          color: #8b5cf6;
          font-weight: 600;
        }
        .claim-no-wins {
          text-align: center;
          padding: 3rem 2rem;
          background: rgba(14, 14, 18, 0.8);
          border-radius: 16px;
          border: 1px solid rgba(39, 39, 42, 0.5);
        }
        .claim-no-wins p {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .claim-subtext {
          color: #71717a;
          font-size: 0.9rem !important;
          margin-bottom: 1.5rem !important;
        }
        .claim-back-btn {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .claim-back-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4);
        }
        @media (max-width: 500px) {
          .claim-win-card {
            grid-template-columns: 40px 1fr;
            gap: 0.75rem;
          }
          .claim-win-reward, .claim-win-action {
            grid-column: 2;
          }
        }

        /* Signup styles */
        .signup-container { max-width: 800px; margin: 0 auto; }

        .signup-card {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%);
          border: 2px solid rgba(139, 92, 246, 0.4);
          border-radius: 24px;
          padding: 2.5rem;
          margin-bottom: 2rem;
        }

        .signup-shuffle-logo {
          display: block;
          width: 80px;
          height: 80px;
          margin: 0 auto 1.5rem auto;
        }

        .signup-title {
          font-size: 1.75rem;
          font-weight: 700;
          text-align: center;
          margin-bottom: 2rem;
        }

        .signup-link-btn {
          display: block;
          width: 100%;
          padding: 1.25rem;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          border: none;
          border-radius: 12px;
          color: #fff;
          font-size: 1.1rem;
          font-weight: 700;
          font-family: 'Outfit', sans-serif;
          text-decoration: none;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 1.5rem;
        }

        .signup-link-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(139, 92, 246, 0.4);
        }

        /* Transfer Modal Trigger Button */
        .transfer-modal-trigger {
          display: block;
          margin: 0.75rem auto 0;
          padding: 0.5rem 1rem;
          background: transparent;
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 8px;
          color: #22c55e;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .transfer-modal-trigger:hover {
          background: rgba(34, 197, 94, 0.1);
          border-color: rgba(34, 197, 94, 0.5);
        }

        .transfer-modal-trigger.signup-page {
          width: 100%;
          justify-content: center;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        /* Transfer Modal */
        .transfer-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 1rem;
          backdrop-filter: blur(4px);
        }

        .transfer-modal {
          background: linear-gradient(135deg, rgba(20, 20, 30, 0.98), rgba(30, 30, 45, 0.98));
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 16px;
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
        }

        .transfer-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          width: 32px;
          height: 32px;
          border: none;
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
          border-radius: 50%;
          font-size: 1.25rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          z-index: 1;
        }

        .transfer-modal-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .transfer-modal-header {
          padding: 1.5rem 1.5rem 1rem;
          text-align: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .transfer-modal-header h2 {
          margin: 0 0 0.5rem;
          font-size: 1.5rem;
          color: #fff;
        }

        .transfer-modal-header p {
          margin: 0;
          color: #a1a1aa;
          font-size: 0.95rem;
        }

        .transfer-modal-body {
          padding: 1.5rem;
        }

        .transfer-warning-notice {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.4);
          border-radius: 10px;
          margin-bottom: 1.5rem;
          color: #fca5a5;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .transfer-warning-notice .warning-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .transfer-warning-notice strong {
          color: #f87171;
        }

        .transfer-options-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 700px) {
          .transfer-options-grid {
            grid-template-columns: 1fr;
          }
        }

        .transfer-option {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
          padding: 1rem;
        }

        .transfer-option.new-account {
          border-left: 3px solid #22c55e;
        }

        .transfer-option.existing-account {
          border-left: 3px solid #f59e0b;
        }

        .transfer-option.not-eligible {
          border-left: 3px solid #8b5cf6;
        }

        .option-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .option-icon {
          font-size: 1.1rem;
        }

        .option-title {
          font-weight: 600;
          color: #fff;
          font-size: 0.9rem;
        }

        .transfer-option p {
          color: #a1a1aa;
          font-size: 0.85rem;
          line-height: 1.4;
          margin: 0.3rem 0;
        }

        .option-note {
          font-size: 0.75rem !important;
          color: #71717a !important;
          font-style: italic;
          margin-top: 0.5rem !important;
        }

        .transfer-signup-link {
          display: inline-block;
          margin-top: 0.5rem;
          padding: 0.4rem 0.75rem;
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          border-radius: 6px;
          color: #fff;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.8rem;
          transition: all 0.2s;
        }

        .transfer-signup-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
        }

        .transfer-form-divider {
          display: flex;
          align-items: center;
          margin: 1rem 0;
          color: #71717a;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .transfer-form-divider::before,
        .transfer-form-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255, 255, 255, 0.1);
        }

        .transfer-form-divider::before {
          margin-right: 1rem;
        }

        .transfer-form-divider::after {
          margin-left: 1rem;
        }

        .transfer-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .transfer-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        @media (max-width: 500px) {
          .transfer-form-row {
            grid-template-columns: 1fr;
          }
        }

        .transfer-input-group {
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }

        .transfer-input-group label {
          font-size: 0.85rem;
          color: #d1d5db;
          font-weight: 500;
        }

        .transfer-input-group label .required {
          color: #ef4444;
        }

        .transfer-input-group input {
          padding: 0.65rem 0.9rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 8px;
          color: #fff;
          font-size: 0.95rem;
        }

        .transfer-input-group input:focus {
          outline: none;
          border-color: #22c55e;
        }

        .transfer-input-group .input-hint {
          font-size: 0.75rem;
          color: #71717a;
        }

        .transfer-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          cursor: pointer;
          font-size: 0.85rem;
          color: #a1a1aa;
          line-height: 1.4;
        }

        .transfer-checkbox input {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: #22c55e;
          cursor: pointer;
          flex-shrink: 0;
        }

        .transfer-submit-btn {
          padding: 0.75rem 1.25rem;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .transfer-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(34, 197, 94, 0.4);
        }

        .transfer-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .transfer-message {
          padding: 0.65rem 0.9rem;
          border-radius: 8px;
          font-size: 0.85rem;
          text-align: center;
        }

        .transfer-message.success {
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.4);
          color: #22c55e;
        }

        .transfer-message.error {
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.4);
          color: #ef4444;
        }

        /* Admin Transfer Tab */
        .admin-transfer-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .admin-transfer-item {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 1rem;
        }

        .admin-transfer-item.pending {
          border-color: rgba(245, 158, 11, 0.3);
          background: rgba(245, 158, 11, 0.05);
        }

        .admin-transfer-item.sent {
          border-color: rgba(34, 197, 94, 0.3);
          background: rgba(34, 197, 94, 0.05);
        }

        .admin-transfer-item.rejected {
          border-color: rgba(239, 68, 68, 0.3);
          background: rgba(239, 68, 68, 0.05);
        }

        .transfer-item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }

        .transfer-item-user {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .transfer-shuffle {
          font-weight: 600;
          color: #fbbf24;
          font-size: 1.05rem;
        }

        .transfer-discord {
          color: #7289da;
          font-size: 0.9rem;
        }

        .transfer-twitch {
          color: #9146ff;
          font-size: 0.85rem;
        }

        .transfer-item-status .status-badge {
          padding: 0.35rem 0.75rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .status-badge.pending {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .status-badge.sent {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .status-badge.rejected {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .transfer-item-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          font-size: 0.8rem;
          color: #71717a;
          margin-bottom: 0.75rem;
        }

        .transfer-notes {
          color: #a1a1aa;
          font-style: italic;
        }

        .transfer-item-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .admin-small-btn.approve {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .admin-small-btn.approve:hover {
          background: rgba(34, 197, 94, 0.3);
        }

        .admin-small-btn.reject {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .admin-small-btn.reject:hover {
          background: rgba(239, 68, 68, 0.3);
        }

        .admin-small-btn.delete {
          background: rgba(107, 114, 128, 0.2);
          color: #9ca3af;
        }

        .admin-small-btn.delete:hover {
          background: rgba(107, 114, 128, 0.3);
        }

        /* Analytics Tab Styles */
        .analytics-quick-periods {
          margin-bottom: 1rem;
        }

        .quick-period-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .quick-period-btn {
          padding: 0.6rem 1.2rem;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 8px;
          color: #22c55e;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .quick-period-btn:hover:not(:disabled) {
          background: rgba(34, 197, 94, 0.2);
          border-color: rgba(34, 197, 94, 0.5);
        }

        .quick-period-btn.active {
          background: rgba(34, 197, 94, 0.25);
          border-color: #22c55e;
          color: #fff;
        }

        .quick-period-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .analytics-custom-range {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .custom-range-inputs {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .date-input {
          padding: 0.5rem 0.75rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 6px;
          color: #fff;
          font-size: 0.9rem;
        }

        .date-input:focus {
          outline: none;
          border-color: #8b5cf6;
        }

        .date-separator {
          color: #71717a;
        }

        .custom-range-btn {
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          border: none;
          border-radius: 6px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .custom-range-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
        }

        .custom-range-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .analytics-period-selector {
          margin-bottom: 1.5rem;
        }

        .period-label {
          display: block;
          color: #a1a1aa;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }

        .period-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .period-btn {
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: #a1a1aa;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .period-btn:hover:not(:disabled) {
          background: rgba(139, 92, 246, 0.1);
          border-color: rgba(139, 92, 246, 0.3);
          color: #fff;
        }

        .period-btn.active {
          background: rgba(139, 92, 246, 0.2);
          border-color: #8b5cf6;
          color: #fff;
        }

        .period-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .analytics-loading {
          text-align: center;
          padding: 3rem;
          color: #a1a1aa;
        }

        .analytics-empty-state {
          text-align: center;
          padding: 3rem 2rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 12px;
          border: 1px dashed rgba(255, 255, 255, 0.2);
        }

        .analytics-empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .analytics-empty-state h3 {
          margin: 0 0 0.5rem 0;
          color: #fff;
        }

        .analytics-empty-state p {
          color: #a1a1aa;
          margin: 0.25rem 0;
        }

        .analytics-rate-note {
          margin-top: 1rem !important;
          font-size: 0.85rem;
          color: #f59e0b !important;
        }

        .analytics-period-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          padding: 0.75rem 1rem;
          background: rgba(139, 92, 246, 0.1);
          border-radius: 8px;
          border: 1px solid rgba(139, 92, 246, 0.2);
        }

        .period-range {
          font-size: 1.1rem;
          font-weight: 600;
          color: #fff;
        }

        .period-dates {
          color: #a1a1aa;
          font-size: 0.85rem;
        }

        .analytics-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .analytics-stats-grid.simple {
          grid-template-columns: repeat(2, 1fr);
          max-width: 600px;
        }

        @media (max-width: 900px) {
          .analytics-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 500px) {
          .analytics-stats-grid,
          .analytics-stats-grid.simple {
            grid-template-columns: 1fr;
          }
        }

        .analytics-stat-card {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 12px;
          padding: 1.25rem;
          text-align: center;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .analytics-stat-card.total-wagered {
          border-color: rgba(139, 92, 246, 0.3);
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.05));
        }

        .analytics-stat-card.total-users {
          border-color: rgba(59, 130, 246, 0.3);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(96, 165, 250, 0.05));
        }

        .analytics-stat-card.total-deposits {
          border-color: rgba(34, 197, 94, 0.3);
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(74, 222, 128, 0.05));
        }

        .analytics-stat-card.total-ggr {
          border-color: rgba(245, 158, 11, 0.3);
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(251, 191, 36, 0.05));
        }

        .analytics-stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.25rem;
          font-family: 'Space Mono', monospace;
        }

        .analytics-stat-label {
          color: #a1a1aa;
          font-size: 0.85rem;
        }

        .analytics-table-container {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .analytics-table-container h3 {
          margin: 0 0 1rem;
          font-size: 1.1rem;
          color: #fff;
        }

        .analytics-table {
          width: 100%;
          border-collapse: collapse;
        }

        .analytics-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background: rgba(139, 92, 246, 0.1);
          color: #a1a1aa;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .analytics-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          color: #d1d5db;
          font-size: 0.9rem;
        }

        .analytics-table tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }

        .analytics-table tr.top-1 td {
          background: rgba(255, 215, 0, 0.05);
        }

        .analytics-table tr.top-2 td {
          background: rgba(192, 192, 192, 0.05);
        }

        .analytics-table tr.top-3 td {
          background: rgba(205, 127, 50, 0.05);
        }

        .analytics-table .rank-cell {
          width: 60px;
          font-weight: 600;
        }

        .analytics-table .username-cell {
          font-weight: 500;
          color: #fff;
        }

        .analytics-table .wager-cell {
          font-family: 'Space Mono', monospace;
          color: #a855f7;
        }

        .analytics-table .deposits-cell {
          font-family: 'Space Mono', monospace;
          color: #22c55e;
        }

        .analytics-table .ggr-cell {
          font-family: 'Space Mono', monospace;
        }

        .analytics-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .pagination-btn {
          padding: 0.5rem 1rem;
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 6px;
          color: #fff;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pagination-btn:hover:not(:disabled) {
          background: rgba(139, 92, 246, 0.3);
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          color: #a1a1aa;
          font-size: 0.85rem;
        }

        .code-display {
          background: rgba(0, 0, 0, 0.4);
          border: 2px solid rgba(139, 92, 246, 0.5);
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
        }

        .code-label { color: #a1a1aa; font-size: 0.875rem; margin-bottom: 0.5rem; }

        .code-value {
          font-family: 'Space Mono', monospace;
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #c084fc, #a855f7);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: 0.2em;
        }

        .code-hint { color: #71717a; font-size: 0.875rem; margin-top: 0.75rem; }

        .info-card {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 1.5rem;
        }

        .info-title { font-size: 1.25rem; font-weight: 700; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem; }

        .info-item { display: flex; gap: 1rem; margin-bottom: 1.25rem; }
        .info-item:last-child { margin-bottom: 0; }
        .info-icon { font-size: 2rem; flex-shrink: 0; }
        .info-content h4 { font-weight: 600; margin-bottom: 0.25rem; }
        .info-content p { color: #a1a1aa; font-size: 0.95rem; line-height: 1.5; }
        .info-content a { color: #a855f7; text-decoration: none; }
        .info-content a:hover { text-decoration: underline; }

        .disclaimer { text-align: center; color: #71717a; font-size: 0.875rem; margin-top: 2rem; }

        /* Rewards styles */
        .rewards-container { max-width: 900px; margin: 0 auto; }

        .rewards-intro { text-align: center; margin-bottom: 2rem; color: #a1a1aa; font-size: 1.1rem; line-height: 1.6; }

        /* ===== Wager Rewards: status panel + milestone ladder (wr-) ===== */
        .wr-display { font-family: 'Unbounded', 'Outfit', sans-serif; }
        .wr-gold {
          background: linear-gradient(135deg, #fde68a, #fbbf24 45%, #f59e0b);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .wr-eyebrow {
          font-family: 'Space Mono', monospace; font-size: 0.68rem; font-weight: 700;
          letter-spacing: 3px; text-transform: uppercase; color: #a78bfa;
        }
        .wr-panel {
          position: relative; overflow: hidden;
          background: linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015));
          border: 1px solid rgba(168,85,247,0.22); border-radius: 18px;
          padding: 1.4rem 1.5rem; margin-bottom: 2.5rem;
          box-shadow: 0 24px 60px -36px rgba(168,85,247,0.55), inset 0 1px 0 rgba(255,255,255,0.05);
        }
        .wr-panel::before {
          content: ''; position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(80% 120% at 0% 0%, rgba(168,85,247,0.14), transparent 60%);
        }
        .wr-panel > * { position: relative; }
        .wr-panel-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
        .wr-who { display: flex; align-items: center; gap: 0.75rem; min-width: 0; }
        .wr-who img { width: 42px; height: 42px; border-radius: 50%; border: 2px solid rgba(168,85,247,0.45); flex-shrink: 0; }
        .wr-who-name { font-size: 0.98rem; font-weight: 700; color: #fff; margin-top: 0.2rem; overflow-wrap: anywhere; }
        .wr-who-name span { font-size: 0.8rem; font-weight: 500; color: #a1a1aa; }
        .wr-rank {
          display: inline-flex; align-items: center; gap: 0.3rem; margin-top: 0.4rem;
          padding: 0.2rem 0.55rem; border-radius: 999px; cursor: pointer;
          background: rgba(168,85,247,0.1); border: 1px solid rgba(168,85,247,0.3);
          color: #d8b4fe; font-family: 'Outfit', sans-serif; font-size: 0.7rem; font-weight: 700;
        }
        .wr-rank:hover { border-color: rgba(168,85,247,0.6); }
        .wr-reset { text-align: right; }
        .wr-reset-label { font-size: 0.6rem; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: #a1a1aa; margin-bottom: 0.35rem; }
        .wr-countdown { display: inline-flex; align-items: center; gap: 0.3rem; }
        .wr-countdown-cell {
          background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.07);
          border-radius: 8px; padding: 0.3rem 0.45rem; min-width: 38px; text-align: center;
        }
        .wr-countdown-cell span { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 0.92rem; font-weight: 800; color: #e9d5ff; }
        .wr-countdown-cell small { font-size: 0.55rem; color: #a1a1aa; margin-left: 1px; }
        .wr-hero-amount { font-size: clamp(2.5rem, 7vw, 3.6rem); font-weight: 900; line-height: 1.05; letter-spacing: -0.02em; margin-top: 0.45rem; }
        .wr-hero-label { font-size: 0.85rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #e4e4e7; margin-top: 0.3rem; }
        .wr-hero-copy { font-size: 0.92rem; color: #a1a1aa; line-height: 1.6; margin: 1rem 0 0; max-width: 560px; }
        .wr-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.75rem; margin-top: 1.15rem; }
        .wr-stat { background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 0.85rem 1rem; min-width: 0; }
        .wr-stat-label { font-size: 0.62rem; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: #a1a1aa; margin-bottom: 0.35rem; }
        .wr-stat-value { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 1.3rem; font-weight: 800; color: #fff; line-height: 1.2; overflow-wrap: anywhere; }
        .wr-stat-sub { font-size: 0.74rem; color: #a1a1aa; margin-top: 0.3rem; line-height: 1.45; }
        .wr-stat-sub .wr-gold { font-weight: 800; }
        .wr-cta-row { display: flex; align-items: center; gap: 0.65rem 0.85rem; flex-wrap: wrap; margin-top: 1.1rem; }
        .wr-note { font-size: 0.76rem; color: #a1a1aa; line-height: 1.45; }
        .wr-btn-gold, .wr-btn-primary, .wr-btn-discord {
          display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
          padding: 0.7rem 1.3rem; border: none; border-radius: 12px; cursor: pointer;
          font-family: 'Outfit', sans-serif; font-size: 0.92rem; font-weight: 800;
          white-space: nowrap; text-decoration: none;
          transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
        }
        .wr-btn-gold { background: linear-gradient(135deg, #fbbf24, #f59e0b); color: #1c1205; box-shadow: 0 10px 28px -12px rgba(251,191,36,0.7); }
        .wr-btn-primary { background: linear-gradient(135deg, #8b5cf6, #a855f7); color: #fff; box-shadow: 0 10px 28px -12px rgba(168,85,247,0.75); }
        .wr-btn-discord { background: #5865F2; color: #fff; }
        .wr-btn-gold:hover:not(:disabled), .wr-btn-primary:hover, .wr-btn-discord:hover { transform: translateY(-2px); }
        .wr-btn-gold:disabled { opacity: 0.6; cursor: wait; }
        .wr-btn-sm { padding: 0.42rem 0.85rem; border-radius: 9px; font-size: 0.75rem; }
        .wr-chip {
          display: inline-flex; align-items: center; gap: 0.3rem;
          padding: 0.32rem 0.65rem; border-radius: 8px;
          font-size: 0.72rem; font-weight: 700; white-space: nowrap; text-decoration: none;
        }
        .wr-chip.paid { background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.3); color: #4ade80; }
        .wr-chip.pending { background: rgba(245,158,11,0.12); border: 1px solid rgba(245,158,11,0.35); color: #fbbf24; }
        .wr-chip.togo { background: rgba(168,85,247,0.1); border: 1px solid rgba(168,85,247,0.3); color: #d8b4fe; }
        .wr-chip.locked { color: #71717a; padding-right: 0; }
        .wr-section-head { margin: 0 0 1rem; }
        .wr-h2 { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 1.35rem; font-weight: 800; color: #fff; margin: 0.4rem 0 0.45rem; }
        .wr-sub { font-size: 0.88rem; color: #a1a1aa; margin: 0; line-height: 1.6; }
        .wr-sub strong { color: #e9d5ff; font-weight: 700; }
        .wr-cols, .wr-tier {
          display: grid; grid-template-columns: 46px minmax(0, 1fr) 92px 104px 156px;
          align-items: center; column-gap: 0.9rem;
        }
        .wr-cols {
          padding: 0 1.1rem 0.45rem; border: 1px solid transparent;
          font-size: 0.6rem; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: #71717a;
        }
        .wr-cols > :nth-child(n+3) { text-align: right; }
        .wr-cols.no-status, .wr-ladder.no-status .wr-tier { grid-template-columns: 46px minmax(0, 1fr) 110px 120px; }
        .wr-ladder.no-status .wr-status { display: none; }
        .wr-tier {
          position: relative; padding: 0.85rem 1.1rem; border-radius: 14px;
          background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07);
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .wr-tier:hover { border-color: rgba(168,85,247,0.35); }
        .wr-tier.is-next {
          background: rgba(168,85,247,0.07); border-color: rgba(168,85,247,0.45);
          box-shadow: 0 14px 34px -22px rgba(168,85,247,0.8);
        }
        .wr-tier.is-locked { opacity: 0.72; }
        .wr-link { width: 2px; height: 0.65rem; margin-left: calc(1.1rem + 23px); border-radius: 2px; }
        .wr-node {
          width: 46px; height: 46px; border-radius: 50%; display: grid; place-items: center;
          font-size: 1.3rem; background: rgba(0,0,0,0.35); border: 2px solid rgba(255,255,255,0.12);
        }
        .wr-tier-main { min-width: 0; }
        .wr-tier-name {
          font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 0.92rem; font-weight: 700; color: #fff;
          display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap;
        }
        .wr-tier-req { font-size: 0.75rem; color: #a1a1aa; margin-top: 0.2rem; }
        .wr-vip {
          padding: 0.12rem 0.45rem; border-radius: 999px; font-family: 'Outfit', sans-serif;
          font-size: 0.6rem; font-weight: 800; letter-spacing: 0.3px;
          background: rgba(251,191,36,0.14); border: 1px solid rgba(251,191,36,0.4); color: #fbbf24;
        }
        .wr-amt { text-align: right; min-width: 0; }
        .wr-amt-label { display: none; }
        .wr-amt-value { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 1.02rem; font-weight: 800; white-space: nowrap; }
        .wr-total-value { color: #fff; }
        .wr-status { display: flex; justify-content: flex-end; min-width: 0; }
        .wr-progress { grid-column: 2 / -1; margin-top: 0.7rem; }
        .wr-bar { position: relative; height: 6px; background: rgba(255,255,255,0.07); border-radius: 999px; overflow: hidden; }
        .wr-bar > span {
          position: absolute; top: 0; bottom: 0; left: 0; border-radius: 999px;
          background: linear-gradient(90deg, #7c3aed, #a855f7, #d8b4fe); transition: width 1s ease-out;
        }
        .wr-progress-meta { display: flex; justify-content: space-between; gap: 0.5rem; margin-top: 0.35rem; font-size: 0.7rem; color: #a1a1aa; }
        .wr-ladder-sum {
          display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
          margin-top: 0.75rem; padding: 0.9rem 1.1rem; border-radius: 14px;
          border: 1px dashed rgba(168,85,247,0.4); background: rgba(168,85,247,0.05);
          font-size: 0.85rem; font-weight: 600; color: #e4e4e7;
        }
        .wr-ladder-sum .wr-gold { font-size: 1.25rem; font-weight: 800; white-space: nowrap; }
        .wr-grind {
          margin-top: 1.25rem; padding: 1.1rem; border-radius: 14px;
          border: 1px solid rgba(168,85,247,0.3);
          background: linear-gradient(135deg, rgba(168,85,247,0.08), rgba(255,255,255,0.02));
        }
        .wr-grind-head { display: flex; align-items: center; gap: 0.9rem; margin-bottom: 0.9rem; }
        .wr-grind-head .wr-node { flex-shrink: 0; border-color: rgba(168,85,247,0.5); }
        .wr-facts { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 0.5rem; }
        .wr-fact { background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.06); border-radius: 10px; padding: 0.6rem 0.5rem; text-align: center; min-width: 0; }
        .wr-fact .wr-stat-label { margin-bottom: 0.2rem; }
        .wr-fact-value { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 0.95rem; font-weight: 800; color: #fff; white-space: nowrap; }
        /* gold money text must beat the white value colours above */
        .wr-stat-value.wr-gold, .wr-fact-value.wr-gold { color: transparent; }
        .wr-grind-progress { margin-top: 0.9rem; background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.05); border-radius: 10px; padding: 0.8rem 0.9rem; }
        .wr-steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.75rem; }
        .wr-step { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07); border-radius: 14px; padding: 1rem 1.1rem; }
        .wr-step-n { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 0.8rem; font-weight: 800; color: #a78bfa; }
        .wr-step-title { font-family: 'Unbounded', 'Outfit', sans-serif; font-size: 0.88rem; font-weight: 700; color: #fff; margin: 0.4rem 0 0.3rem; }
        .wr-step-desc { font-size: 0.8rem; color: #a1a1aa; line-height: 1.5; }
        .wr-step-desc strong { color: #e9d5ff; }
        .wr-disclaimer { font-size: 0.72rem; color: #71717a; line-height: 1.55; margin: 1rem 0 2.5rem; }
        .wr-disclaimer strong { color: #c4b5fd; }
        .wr-disclaimer a { color: #a855f7; text-decoration: underline; }
        .wr-btn-gold:focus-visible, .wr-btn-primary:focus-visible, .wr-btn-discord:focus-visible, .wr-rank:focus-visible, .wr-chip:focus-visible {
          outline: 2px solid #d8b4fe; outline-offset: 2px;
        }
        @media (max-width: 640px) {
          .wr-panel { padding: 1.1rem; border-radius: 16px; }
          .wr-reset { text-align: left; }
          .wr-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .wr-stats > .wr-stat:first-child { grid-column: 1 / -1; }
          .wr-stat { padding: 0.75rem 0.8rem; }
          .wr-stat-value { font-size: 1.05rem; }
          .wr-stats > .wr-stat:first-child .wr-stat-value { font-size: 1.3rem; }
          .wr-cta-row > .wr-btn-gold, .wr-cta-row > .wr-btn-primary, .wr-cta-row > .wr-btn-discord { width: 100%; }
          .wr-cols { display: none; }
          .wr-tier, .wr-ladder.no-status .wr-tier { grid-template-columns: 42px minmax(0, 1fr) auto; column-gap: 0.75rem; row-gap: 0.55rem; padding: 0.8rem 0.85rem; }
          .wr-node { width: 42px; height: 42px; font-size: 1.15rem; }
          .wr-tier > .wr-node { grid-row: 1 / span 2; align-self: start; }
          .wr-tier-main { grid-column: 2; grid-row: 1; }
          .wr-status { grid-column: 3; grid-row: 1; }
          .wr-amt { grid-row: 2; display: flex; align-items: baseline; gap: 0.35rem; }
          .wr-amt.pay { grid-column: 2; justify-content: flex-start; }
          .wr-amt.total { grid-column: 3; justify-content: flex-end; }
          .wr-amt-label { display: inline; font-size: 0.6rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #71717a; }
          .wr-amt-value { font-size: 0.92rem; }
          .wr-progress { grid-column: 1 / -1; grid-row: 3; margin-top: 0.2rem; }
          .wr-link { margin-left: calc(0.85rem + 21px); }
          .wr-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .wr-facts > .wr-fact:last-child { grid-column: 1 / -1; }
          .wr-steps { grid-template-columns: 1fr; }
        }
        @media (prefers-reduced-motion: reduce) {
          .wr-bar > span { transition: none; }
          .wr-btn-gold, .wr-btn-primary, .wr-btn-discord { transition: none; }
        }

        /* Preview Cards Grid */
        .rewards-preview-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 3rem;
        }

        @media (max-width: 768px) {
          .rewards-preview-grid {
            grid-template-columns: 1fr;
          }
        }

        .rewards-preview-card {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(168, 85, 247, 0.08) 100%);
          border: 2px solid rgba(139, 92, 246, 0.3);
          border-radius: 20px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .rewards-preview-card:hover {
          border-color: #a855f7;
          transform: translateY(-5px);
          box-shadow: 0 10px 40px rgba(139, 92, 246, 0.3);
        }

        .rewards-preview-icon {
          font-size: 2.5rem;
          display: block;
          margin-bottom: 0.75rem;
        }

        .rewards-preview-card h3 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #fff;
          margin: 0 0 0.5rem 0;
        }

        .rewards-preview-highlight {
          font-family: 'Space Mono', monospace;
          font-size: 1.1rem;
          font-weight: 600;
          color: #22c55e;
          margin: 0 0 0.75rem 0;
        }

        .rewards-preview-link {
          color: #a855f7;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .rewards-claim-note {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-top: 1.5rem;
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        .rewards-claim-note strong {
          color: #fff;
        }

        .tiers-grid { display: flex; flex-direction: column; gap: 1rem; }

        .tier-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem 2rem;
          background: rgba(20, 20, 30, 0.6);
          border: 2px solid;
          border-radius: 16px;
          transition: all 0.3s ease;
        }

        .tier-card:hover { transform: translateX(10px); }
        .tier-left { display: flex; align-items: center; gap: 1rem; }
        .tier-icon { font-size: 2.5rem; filter: drop-shadow(0 0 10px currentColor); }
        .tier-info h3 { font-size: 1.25rem; font-weight: 700; margin-bottom: 0.25rem; }
        .tier-requirement { color: #71717a; font-size: 0.9rem; }
        .tier-reward { font-family: 'Space Mono', monospace; font-size: 1.5rem; font-weight: 700; color: #22c55e; }

        .rewards-info {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: 1.5rem;
          margin-top: 2rem;
        }

        .rewards-info h4 { font-weight: 700; margin-bottom: 0.75rem; display: flex; align-items: center; gap: 0.5rem; }
        .rewards-info p { color: #a1a1aa; font-size: 0.9rem; line-height: 1.6; margin-bottom: 0.5rem; }
        .rewards-info p:last-child { margin-bottom: 0; }

        /* Rewards Section Styles */
        .rewards-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .rewards-section-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .rewards-section-icon {
          font-size: 2.5rem;
          background: rgba(139, 92, 246, 0.2);
          padding: 0.75rem;
          border-radius: 16px;
        }

        .rewards-section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
          margin: 0;
        }

        .rewards-section-subtitle {
          color: #a1a1aa;
          font-size: 0.95rem;
          margin: 0.25rem 0 0 0;
        }

        .rewards-action-btn {
          background: rgba(139, 92, 246, 0.2);
          border: 2px solid rgba(139, 92, 246, 0.4);
          color: #a855f7;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .rewards-action-btn:hover {
          background: rgba(139, 92, 246, 0.3);
          border-color: #a855f7;
          transform: translateY(-2px);
        }

        /* Leaderboard Prizes Grid */
        .leaderboard-prizes-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 768px) {
          .leaderboard-prizes-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .prize-item {
          background: rgba(30, 30, 40, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
          transition: all 0.2s ease;
        }

        .prize-item:hover {
          transform: translateY(-3px);
          border-color: rgba(139, 92, 246, 0.4);
        }

        .prize-item.gold {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%);
          border-color: rgba(255, 215, 0, 0.4);
        }

        .prize-item.silver {
          background: linear-gradient(135deg, rgba(192, 192, 192, 0.15) 0%, rgba(150, 150, 150, 0.1) 100%);
          border-color: rgba(192, 192, 192, 0.4);
        }

        .prize-item.bronze {
          background: linear-gradient(135deg, rgba(205, 127, 50, 0.15) 0%, rgba(180, 100, 40, 0.1) 100%);
          border-color: rgba(205, 127, 50, 0.4);
        }

        .prize-rank {
          display: block;
          font-size: 0.9rem;
          color: #a1a1aa;
          margin-bottom: 0.25rem;
        }

        .prize-item.gold .prize-rank { color: #ffd700; }
        .prize-item.silver .prize-rank { color: #c0c0c0; }
        .prize-item.bronze .prize-rank { color: #cd7f32; }

        .prize-amount {
          display: block;
          font-family: 'Space Mono', monospace;
          font-size: 1.1rem;
          font-weight: 700;
          color: #22c55e;
        }

        /* Perks Grid */
        .perks-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        @media (max-width: 600px) {
          .perks-grid {
            grid-template-columns: 1fr;
          }
        }

        .perk-card {
          background: rgba(30, 30, 40, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          transition: all 0.2s ease;
        }

        .perk-card:hover {
          border-color: rgba(139, 92, 246, 0.4);
          transform: translateY(-3px);
        }

        .perk-icon {
          font-size: 2rem;
          display: block;
          margin-bottom: 0.75rem;
        }

        .perk-card h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #fff;
          margin: 0 0 0.5rem 0;
        }

        .perk-card p {
          color: #a1a1aa;
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }

        /* Affiliate styles - keeping for backwards compat */
        .affiliate-container { max-width: 800px; margin: 0 auto; }

        .affiliate-card {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          transition: all 0.3s ease;
        }

        .affiliate-card:hover { border-color: rgba(139, 92, 246, 0.5); box-shadow: 0 10px 40px rgba(139, 92, 246, 0.2); }

        .affiliate-card.highlight {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%);
          border: 2px solid rgba(139, 92, 246, 0.4);
        }

        .affiliate-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
        .affiliate-icon { font-size: 2.5rem; }
        .affiliate-title { font-size: 1.5rem; font-weight: 700; }
        .affiliate-desc { color: #a1a1aa; line-height: 1.6; }

        .affiliate-link {
          display: inline-block;
          margin-top: 1rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
          border-radius: 10px;
          color: #fff;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
        }

        .affiliate-link:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4); }

        .simple-page-title {
          font-size: clamp(2rem, 5vw, 4rem);
          font-weight: 900;
          text-align: center;
          margin-bottom: 3rem;
          color: #fff;
          text-shadow: 0.05em 0.05em 0 rgba(124, 58, 237, 0.45);
        }

        /* Responsive */
        @media (max-width: 900px) {
          .nav-links { display: none; }
          .mobile-menu-btn { display: block; }
        }

        @media (max-width: 768px) {
          .podium-section {
            flex-direction: column;
            align-items: center;
          }

          .podium-card { width: 100%; max-width: 280px; }
          .podium-card.first { order: 1; }
          .podium-card.second { order: 2; }
          .podium-card.third { order: 3; }

          /* Keep the Prize column visible on mobile — compact widths instead of hiding it */
          .table-header, .table-row {
            grid-template-columns: 44px 1fr 92px 74px;
          }
          .table-prize { font-size: 0.8rem; }

          .countdown-container { flex-direction: column; gap: 0.75rem; }

          .tier-card { flex-direction: column; text-align: center; gap: 1rem; }
          .tier-left { flex-direction: column; }
        }

        @media (max-width: 480px) {
          .main { padding: 1.5rem 1rem; }
          .table-header, .table-row { grid-template-columns: 38px 1fr 82px 62px; padding: 0.75rem 0.7rem; font-size: 0.78rem; }
          .table-prize { font-size: 0.74rem; }
          .table-wager { font-size: 0.74rem; }
          .social-link { padding: 0.6rem 1rem; font-size: 0.85rem; }
          .countdown-block { min-width: 45px; padding: 0.4rem 0.5rem; }
          .countdown-number { font-size: 1rem; }
          .footer { padding: 2rem 1rem; }
        }

        /* ============================================
           AUTH & USER STYLES
           ============================================ */
        .user-auth-section {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }

        .twitch-login-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #9146ff;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .twitch-login-btn:hover {
          background: #7c3aed;
          transform: translateY(-1px);
        }

        .twitch-login-btn .twitch-icon {
          width: 18px;
          height: 18px;
        }

        .user-profile-dropdown {
          position: relative;
        }

        .user-profile-trigger {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 8px;
          transition: background 0.2s;
        }

        .user-profile-trigger:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid #9146ff;
        }

        .user-name {
          font-weight: 600;
          font-size: 0.9rem;
          color: #fff;
        }

        .user-dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 0.5rem;
          background: rgba(20, 20, 30, 0.98);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 0.75rem;
          min-width: 200px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          z-index: 1000;
        }

        .user-profile-dropdown:hover .user-dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 0.5rem;
        }

        .dropdown-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
        }

        .dropdown-display-name {
          font-weight: 600;
          color: #fff;
        }

        .dropdown-username {
          font-size: 0.8rem;
          color: #a1a1aa;
        }

        .dropdown-item {
          display: block;
          width: 100%;
          padding: 0.5rem 0.75rem;
          background: none;
          border: none;
          color: #e4e4e7;
          text-align: left;
          cursor: pointer;
          border-radius: 6px;
          font-size: 0.9rem;
          transition: background 0.2s;
        }

        .dropdown-item:hover {
          background: rgba(139, 92, 246, 0.2);
        }

        .dropdown-item.logout-btn {
          color: #ef4444;
        }

        .admin-btn {
          background: linear-gradient(135deg, #f59e0b, #d97706) !important;
          color: #000 !important;
        }

        /* Mobile auth */
        .mobile-auth-section {
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          margin-top: 1rem;
        }

        .mobile-user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .mobile-user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
        }

        .twitch-login-btn.mobile {
          width: 100%;
          justify-content: center;
        }

        /* ============================================
           PREDICTION GAME STYLES
           ============================================ */
        .prediction-game-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .prediction-game-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .prediction-game-header h2 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }

        .prediction-game-header p {
          color: #a1a1aa;
          font-size: 0.95rem;
        }

        .prediction-hunt-info {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 768px) {
          .prediction-hunt-info {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 480px) {
          .prediction-hunt-info {
            grid-template-columns: 1fr;
          }
        }

        .prediction-info-item {
          background: rgba(14, 14, 18, 0.8);
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
        }

        .prediction-info-label {
          display: block;
          font-size: 0.8rem;
          color: #a1a1aa;
          margin-bottom: 0.25rem;
        }

        .prediction-info-value {
          font-family: 'Space Mono', monospace;
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffd700;
        }

        .prediction-form-container {
          max-width: 500px;
          margin: 0 auto 1.5rem;
        }

        .prediction-slots-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .prediction-slot {
          background: rgba(14, 14, 18, 0.8);
          border-radius: 12px;
          padding: 1rem;
          border: 1px solid rgba(139, 92, 246, 0.2);
        }

        .prediction-slot.extra-slot {
          border-color: rgba(236, 72, 153, 0.3);
          background: rgba(236, 72, 153, 0.05);
        }

        .prediction-slot-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.75rem;
        }

        .slot-label {
          font-weight: 600;
          color: #fff;
        }

        .slot-badge {
          font-size: 0.7rem;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-weight: 600;
        }

        .slot-badge.free {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .slot-badge.extra {
          background: rgba(236, 72, 153, 0.2);
          color: #ec4899;
        }

        .prediction-submitted-compact {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .prediction-submitted-compact .prediction-value {
          font-family: 'Space Mono', monospace;
          font-size: 1.25rem;
          color: #ffd700;
          font-weight: 700;
        }

        .prediction-input-row.compact {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .prediction-input-row.compact .prediction-input {
          font-size: 0.9rem;
          padding: 0.5rem;
        }

        .prediction-submit-btn.small {
          padding: 0.5rem 1rem;
          font-size: 0.85rem;
        }

        .extra-slots-promo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: rgba(139, 92, 246, 0.1);
          border: 1px dashed rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          font-size: 0.85rem;
          color: #a1a1aa;
        }

        .extra-slots-promo .promo-icon {
          font-size: 1.25rem;
        }

        .extra-slots-promo strong {
          color: #ec4899;
        }

        .current-pred-slot {
          font-size: 0.75rem;
          background: rgba(236, 72, 153, 0.2);
          color: #ec4899;
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          margin-left: 0.25rem;
        }

        .prediction-form, .prediction-submitted {
          background: rgba(14, 14, 18, 0.8);
          border-radius: 12px;
          padding: 1.25rem;
        }

        .prediction-label {
          display: block;
          font-size: 0.9rem;
          color: #a1a1aa;
          margin-bottom: 0.5rem;
        }

        .prediction-input-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .prediction-dollar {
          font-size: 1.25rem;
          color: #ffd700;
          font-weight: 600;
        }

        .prediction-input {
          flex: 1;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          padding: 0.75rem;
          color: #fff;
          font-family: 'Space Mono', monospace;
          font-size: 1.1rem;
        }

        .prediction-input:focus {
          outline: none;
          border-color: #8b5cf6;
        }

        .prediction-submit-btn {
          background: linear-gradient(135deg, #8b5cf6, #ec4899);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .prediction-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4);
        }

        .prediction-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .prediction-submitted {
          text-align: center;
        }

        .prediction-submitted-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }

        .prediction-submitted-text {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          margin-bottom: 0.75rem;
        }

        .prediction-submitted-text strong {
          font-family: 'Space Mono', monospace;
          font-size: 1.5rem;
          color: #ffd700;
        }

        .prediction-edit-hint {
          font-size: 0.8rem;
          color: #a1a1aa;
          margin-bottom: 0.75rem;
        }

        .prediction-login-prompt {
          text-align: center;
          padding: 2rem;
        }

        .prediction-login-prompt p {
          margin-bottom: 1rem;
          color: #a1a1aa;
        }

        .prediction-message {
          padding: 0.75rem;
          border-radius: 8px;
          margin-top: 1rem;
          text-align: center;
          font-weight: 500;
        }

        .prediction-message.success {
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.4);
          color: #22c55e;
        }

        .prediction-message.error {
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.4);
          color: #ef4444;
        }

        .predictions-list {
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
          padding: 1rem;
        }

        .predictions-list h4 {
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
          color: #a1a1aa;
        }

        .predictions-scroll {
          max-height: 300px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .prediction-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
        }

        .prediction-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
        }

        .prediction-username {
          flex: 1;
          font-size: 0.9rem;
        }

        .prediction-amount {
          font-family: 'Space Mono', monospace;
          font-weight: 600;
          color: #ffd700;
        }

        .prediction-inactive {
          text-align: center;
          padding: 3rem 1rem;
          color: #71717a;
        }

        .prediction-inactive-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .prediction-inactive-hint {
          font-size: 0.9rem;
          margin-top: 0.5rem;
        }

        .prediction-status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .prediction-status-badge.open {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .prediction-status-badge.closed {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .predictions-closed-message {
          text-align: center;
          padding: 2rem;
          background: rgba(14, 14, 18, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }

        .predictions-closed-icon {
          font-size: 2.5rem;
          margin-bottom: 0.75rem;
        }

        .predictions-closed-message p {
          color: #e4e4e7;
          margin: 0;
        }

        .predictions-closed-hint {
          font-size: 0.85rem;
          color: #a1a1aa !important;
          margin-top: 0.5rem !important;
        }

        /* ============================================
           ADMIN PANEL STYLES
           ============================================ */
        .admin-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .admin-stats-row {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0.6rem;
          margin-bottom: 0.75rem;
        }

        @media (max-width: 900px) {
          .admin-stats-row {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 600px) {
          .admin-stats-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .admin-stat-card {
          background: rgba(20, 20, 30, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.15);
          border-radius: 0.5rem;
          padding: 0.65rem 0.75rem;
          text-align: center;
        }

        .admin-stat-clickable {
          cursor: pointer;
          transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
        }
        .admin-stat-clickable:hover {
          transform: translateY(-2px);
          border-color: rgba(139, 92, 246, 0.45);
          background: rgba(30, 25, 45, 0.9);
        }

        .admin-stat-value {
          font-family: 'Space Mono', monospace;
          font-size: 1.15rem;
          font-weight: 700;
          color: #8b5cf6;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .admin-stat-label {
          font-size: 0.7rem;
          color: #71717a;
          margin-top: 0.15rem;
          white-space: nowrap;
        }

        .admin-stats-section-label {
          font-size: 0.7rem;
          font-weight: 600;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-bottom: 0.4rem;
          padding-left: 0.1rem;
        }

        .admin-stat-card.stat-total {
          grid-column: 1 / -1;
          background: rgba(34, 197, 94, 0.06);
          border: 1px solid rgba(34, 197, 94, 0.25);
        }
        .admin-stat-card.stat-total .admin-stat-value {
          font-size: 1.5rem;
          color: #22c55e;
        }

        /* Admin Tabs */
        .admin-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
          padding-bottom: 1rem;
        }

        .admin-tab {
          padding: 0.6rem 1rem;
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          color: #a1a1aa;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .admin-tab:hover {
          background: rgba(139, 92, 246, 0.2);
          color: #fff;
        }

        .admin-tab.active {
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          color: #fff;
          border-color: transparent;
        }

        .admin-tab-badge {
          background: rgba(239, 68, 68, 0.8);
          color: #fff;
          font-size: 0.7rem;
          padding: 0.15rem 0.4rem;
          border-radius: 10px;
          font-weight: 700;
        }

        .admin-tab.active .admin-tab-badge {
          background: rgba(255, 255, 255, 0.3);
        }

        /* Dashboard Cards */
        .admin-dashboard-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 900px) {
          .admin-dashboard-cards {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .admin-dashboard-cards {
            grid-template-columns: 1fr;
          }
        }

        .admin-dashboard-card {
          background: rgba(20, 20, 30, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          padding: 1.25rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .admin-dashboard-card:hover {
          background: rgba(139, 92, 246, 0.15);
          border-color: rgba(139, 92, 246, 0.4);
          transform: translateY(-2px);
        }

        .admin-dashboard-card-icon {
          font-size: 2rem;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(139, 92, 246, 0.15);
          border-radius: 10px;
        }

        .admin-dashboard-card-content {
          flex: 1;
        }

        .admin-dashboard-card-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 0.25rem;
        }

        .admin-dashboard-card-value {
          font-family: 'Space Mono', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          color: #a78bfa;
        }

        .admin-dashboard-card-value.pending {
          color: #f59e0b;
        }

        .admin-dashboard-card-value.success {
          color: #22c55e;
        }

        .admin-dashboard-card-arrow {
          color: #71717a;
          font-size: 1.25rem;
        }

        .admin-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .admin-section h2 {
          font-size: 1.25rem;
          margin-bottom: 1rem;
          color: #fff;
        }

        .admin-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          align-items: center;
        }

        .admin-action-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .admin-action-btn.open {
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
        }

        .admin-action-btn.pause {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: black;
        }

        .admin-action-btn.close {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
        }

        .admin-action-btn:hover {
          transform: translateY(-2px);
        }

        .admin-close-hunt-form {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .admin-input {
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          padding: 0.75rem;
          color: #fff;
          font-family: 'Space Mono', monospace;
          width: 150px;
        }

        .admin-no-hunt {
          color: #a1a1aa;
          font-style: italic;
        }

        .admin-hunts-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .admin-hunt-item {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1.25rem;
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
          border-left: 4px solid #71717a;
        }

        .admin-hunt-item.active {
          border-left-color: #22c55e;
          background: rgba(34, 197, 94, 0.05);
        }

        .admin-hunt-item.paused {
          border-left-color: #f59e0b;
          background: rgba(245, 158, 11, 0.05);
        }

        .admin-hunt-item.completed {
          border-left-color: #8b5cf6;
        }

        .admin-hunt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .admin-hunt-info {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .admin-hunt-id {
          font-weight: 700;
          font-size: 1.1rem;
        }

        .admin-hunt-status {
          padding: 0.25rem 0.6rem;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .admin-hunt-status.active { background: rgba(34, 197, 94, 0.2); color: #22c55e; }
        .admin-hunt-status.paused { background: rgba(245, 158, 11, 0.2); color: #f59e0b; }
        .admin-hunt-status.completed { background: rgba(139, 92, 246, 0.2); color: #8b5cf6; }

        .admin-hunt-predictions {
          color: #a1a1aa;
          font-size: 0.9rem;
        }

        .admin-hunt-balances {
          display: flex;
          gap: 1.5rem;
          font-size: 0.9rem;
        }

        .admin-hunt-balances .start-balance {
          color: #a1a1aa;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .admin-hunt-balances .start-balance strong {
          color: #ffd700;
          font-family: 'Space Mono', monospace;
        }

        .edit-balance-btn {
          background: rgba(139, 92, 246, 0.2);
          border: none;
          border-radius: 4px;
          padding: 0.15rem 0.35rem;
          font-size: 0.75rem;
          cursor: pointer;
          opacity: 0.7;
          transition: all 0.2s;
        }

        .edit-balance-btn:hover {
          opacity: 1;
          background: rgba(139, 92, 246, 0.4);
        }

        .admin-hunt-balances .end-balance {
          color: #a1a1aa;
        }

        .admin-hunt-balances .end-balance strong {
          color: #22c55e;
          font-family: 'Space Mono', monospace;
        }

        .admin-hunt-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(139, 92, 246, 0.1);
        }

        .admin-hunt-view-actions {
          display: flex;
          gap: 0.5rem;
        }

        .admin-hunt-status-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .admin-close-hunt-inline {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .admin-input-small {
          padding: 0.4rem 0.75rem;
          background: rgba(20, 20, 30, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 6px;
          color: #fff;
          font-size: 0.85rem;
          width: 120px;
        }

        .admin-input-small:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.6);
        }

        .hunt-completed-badge {
          color: #22c55e;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .hunt-closed-badge {
          color: #f59e0b;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .admin-hint {
          font-size: 0.85rem;
          color: #71717a;
          margin-top: 0.5rem;
        }

        .admin-small-btn {
          padding: 0.5rem 0.85rem;
          border: none;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          background: rgba(139, 92, 246, 0.2);
          color: #a78bfa;
          transition: all 0.2s;
        }

        .admin-small-btn:hover {
          background: rgba(139, 92, 246, 0.3);
          transform: translateY(-1px);
        }

        .admin-small-btn.open {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .admin-small-btn.open:hover {
          background: rgba(34, 197, 94, 0.3);
        }

        .admin-small-btn.pause {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .admin-small-btn.pause:hover {
          background: rgba(245, 158, 11, 0.3);
        }

        .admin-small-btn.close {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .admin-small-btn.close:hover {
          background: rgba(239, 68, 68, 0.3);
        }

        .admin-small-btn.winners {
          background: rgba(255, 215, 0, 0.15);
          color: #ffd700;
        }

        .admin-small-btn.winners:hover {
          background: rgba(255, 215, 0, 0.25);
        }

        @media (max-width: 700px) {
          .admin-hunt-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .admin-hunt-controls {
            flex-direction: column;
            align-items: flex-start;
          }
          .admin-close-hunt-inline {
            flex-direction: column;
            width: 100%;
          }
          .admin-input-small {
            width: 100%;
          }
        }

        .admin-small-btn.pause { background: rgba(245, 158, 11, 0.2); color: #f59e0b; }
        .admin-small-btn.open { background: rgba(34, 197, 94, 0.2); color: #22c55e; }
        .admin-small-btn.delete { 
          background: rgba(239, 68, 68, 0.2); 
          color: #ef4444; 
          border: 1px solid rgba(239, 68, 68, 0.3);
        }
        .admin-small-btn.delete:hover { 
          background: rgba(239, 68, 68, 0.3); 
        }

        .admin-predictions-table {
          background: rgba(14, 14, 18, 0.6);
          border-radius: 10px;
          overflow: hidden;
        }

        .admin-table-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1.5fr 60px;
          padding: 0.75rem 1rem;
          background: rgba(139, 92, 246, 0.1);
          font-weight: 600;
          font-size: 0.85rem;
          color: #a1a1aa;
        }

        .admin-table-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1.5fr 60px;
          padding: 0.75rem 1rem;
          align-items: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .admin-table-row.paid-row {
          background: rgba(34, 197, 94, 0.05);
        }

        .admin-table-row.unclaimed-row {
          background: rgba(239, 68, 68, 0.05);
        }

        .admin-section-desc {
          color: #71717a;
          font-size: 0.9rem;
          margin-top: 0.5rem;
        }

        .admin-hunt-ref {
          font-family: 'Space Mono', monospace;
          color: #a78bfa;
          font-size: 0.85rem;
        }

        .admin-rank-badge {
          font-size: 1.2rem;
        }

        .admin-reward-text {
          font-size: 0.85rem;
          color: #fbbf24;
        }

        .admin-shuffle-status {
          font-size: 0.85rem;
        }

        .admin-shuffle-status.linked {
          color: #22c55e;
        }

        .admin-shuffle-status.not-linked {
          color: #ef4444;
        }

        .admin-actions-cell {
          display: flex;
          gap: 0.5rem;
        }

        .admin-small-btn.pay {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
          border: 1px solid rgba(34, 197, 94, 0.3);
        }

        .admin-small-btn.pay:hover {
          background: rgba(34, 197, 94, 0.3);
        }

        .admin-no-action {
          font-size: 0.8rem;
          color: #71717a;
          font-style: italic;
        }

        .admin-tab-badge {
          background: #ef4444;
          color: white;
          font-size: 0.7rem;
          padding: 0.15rem 0.4rem;
          border-radius: 10px;
          margin-left: 0.5rem;
        }

        .admin-dashboard-card-value.danger {
          color: #ef4444;
        }

        /* Slot Picks Styles */
        .slot-picks-live {
          font-size: 0.7rem;
          color: #22c55e;
          animation: pulse-live 2s infinite;
          margin-left: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        @keyframes pulse-live {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .refresh-btn {
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          color: #a855f7;
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 0.8rem;
          margin-left: 1rem;
          transition: all 0.2s;
        }

        .refresh-btn:hover {
          background: rgba(139, 92, 246, 0.3);
          transform: scale(1.05);
        }

        .admin-filter-buttons {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .admin-filter-btn {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.2);
          color: #a1a1aa;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          transition: all 0.2s;
        }

        .admin-filter-btn:hover {
          background: rgba(139, 92, 246, 0.2);
          color: #fff;
        }

        .admin-filter-btn.active {
          background: rgba(139, 92, 246, 0.3);
          border-color: rgba(139, 92, 246, 0.5);
          color: #fff;
        }

        .slot-picks-header,
        .slot-pick-row {
          grid-template-columns: 1.2fr 1.5fr 1fr 0.8fr 1.2fr !important;
        }

        .slot-pick-name {
          font-weight: 600;
          color: #fbbf24;
          word-break: break-word;
        }

        .slot-pick-time {
          font-size: 0.8rem;
          color: #71717a;
        }

        .slot-pick-row.pending-row {
          background: rgba(245, 158, 11, 0.05);
        }

        .slot-pick-row.completed-row {
          background: rgba(139, 92, 246, 0.05);
        }

        .slot-pick-row.paid-row {
          background: rgba(34, 197, 94, 0.05);
        }

        .slot-pick-row.refund_pending-row {
          background: rgba(239, 68, 68, 0.05);
        }

        .slot-pick-row.refunded-row {
          background: rgba(107, 114, 128, 0.05);
        }

        .admin-small-btn.remove {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .admin-small-btn.remove:hover {
          background: rgba(239, 68, 68, 0.3);
        }

        .admin-small-btn.refund {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .admin-small-btn.refund:hover {
          background: rgba(245, 158, 11, 0.3);
        }

        .slot-paid-info {
          color: #22c55e;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .slot-refunded-info {
          color: #6b7280;
          font-size: 0.85rem;
        }

        .slot-pick-modal-no-shuffle {
          color: #f59e0b;
          font-size: 0.85rem;
        }

        .admin-small-btn.complete {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .admin-small-btn.complete:hover {
          background: rgba(34, 197, 94, 0.3);
        }

        .admin-avatar-placeholder {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 700;
          color: #fff;
        }

        /* Modal Overlay */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(4px);
        }

        .modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: #a1a1aa;
          font-size: 1.5rem;
          cursor: pointer;
          transition: color 0.2s;
        }

        .modal-close:hover {
          color: #fff;
        }

        /* Slot Pick Result Styles */
        .slot-pick-status.bonus {
          background: rgba(168, 85, 247, 0.2);
          color: #a855f7;
        }

        .slot-pick-status.win {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .slot-reward-badge {
          display: inline-block;
          background: rgba(255, 215, 0, 0.2);
          color: #ffd700;
          font-size: 0.7rem;
          padding: 0.1rem 0.4rem;
          border-radius: 4px;
          margin-left: 0.5rem;
          font-weight: 600;
        }

        .admin-small-btn.bonus {
          background: rgba(168, 85, 247, 0.2);
          color: #a855f7;
        }

        .admin-small-btn.bonus:hover {
          background: rgba(168, 85, 247, 0.3);
        }

        /* Slot Pick Modal */
        .slot-pick-modal {
          background: linear-gradient(135deg, #1a1a2e 0%, #16162a 100%);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: 2rem;
          width: 90%;
          max-width: 500px;
          position: relative;
        }

        .slot-pick-modal h2 {
          margin: 0 0 1.5rem 0;
          text-align: center;
          font-size: 1.5rem;
        }

        .slot-pick-modal-info {
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }

        .slot-pick-modal-user {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .slot-pick-modal-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
        }

        .slot-pick-modal-avatar-placeholder {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          font-weight: 700;
          color: #fff;
        }

        .slot-pick-modal-username {
          font-weight: 600;
          font-size: 1.1rem;
        }

        .slot-pick-modal-shuffle {
          color: #22c55e;
          font-size: 0.85rem;
        }

        .slot-pick-modal-slot {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .slot-pick-modal-slot .label {
          color: #71717a;
          font-size: 0.9rem;
        }

        .slot-pick-modal-slot .slot-name {
          color: #fbbf24;
          font-weight: 600;
          font-size: 1.1rem;
        }

        .slot-pick-modal-form .form-group {
          margin-bottom: 1rem;
        }

        .slot-pick-modal-form label {
          display: block;
          color: #a1a1aa;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }

        .result-type-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .result-btn {
          flex: 1;
          padding: 0.75rem;
          border: 2px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          background: rgba(14, 14, 18, 0.6);
          color: #a1a1aa;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Outfit', sans-serif;
          font-size: 0.9rem;
        }

        .result-btn:hover {
          border-color: rgba(139, 92, 246, 0.4);
          color: #fff;
        }

        .result-btn.active {
          border-color: rgba(139, 92, 246, 0.6);
          background: rgba(139, 92, 246, 0.2);
          color: #fff;
        }

        .result-btn.bonus.active {
          border-color: rgba(168, 85, 247, 0.6);
          background: rgba(168, 85, 247, 0.2);
          color: #a855f7;
        }

        .result-btn.win.active {
          border-color: rgba(34, 197, 94, 0.6);
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .slot-pick-input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          background: rgba(14, 14, 18, 0.6);
          color: #fff;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
        }

        .slot-pick-input:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.5);
        }

        .warning-text {
          color: #f59e0b;
          font-size: 0.8rem;
          margin-top: 0.5rem;
        }

        .slot-pick-modal-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .slot-pick-modal-actions .btn-cancel {
          flex: 1;
          padding: 0.75rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          background: transparent;
          color: #a1a1aa;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
          transition: all 0.2s;
        }

        .slot-pick-modal-actions .btn-cancel:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .slot-pick-modal-actions .btn-confirm {
          flex: 1;
          padding: 0.75rem;
          border: none;
          border-radius: 8px;
          background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
          color: #fff;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          transition: all 0.2s;
        }

        .slot-pick-modal-actions .btn-confirm:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
        }

        .admin-paid-date {
          font-size: 0.8rem;
          color: #22c55e;
        }

        .admin-pred-user {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .admin-pred-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
        }

        .admin-pred-amount {
          font-family: 'Space Mono', monospace;
          color: #ffd700;
        }

        .admin-pred-time {
          font-size: 0.8rem;
          color: #71717a;
        }

        .admin-delete-btn {
          background: rgba(239, 68, 68, 0.2);
          border: none;
          padding: 0.4rem;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .admin-delete-btn:hover {
          background: rgba(239, 68, 68, 0.4);
        }

        .admin-delete-btn.disqualify {
          background: rgba(239, 68, 68, 0.15);
          padding: 0.3rem 0.5rem;
        }

        .admin-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .admin-section-header h2 {
          margin: 0;
        }

        .admin-small-btn.winners {
          background: rgba(255, 215, 0, 0.2);
          color: #ffd700;
        }

        .admin-small-btn.winners:hover {
          background: rgba(255, 215, 0, 0.3);
        }

        .admin-small-btn.recalc {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
        }

        .admin-small-btn.recalc:hover {
          background: rgba(59, 130, 246, 0.3);
        }

        .admin-winners-table {
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
          overflow: hidden;
        }

        .admin-winners-table .admin-table-header {
          display: grid;
          grid-template-columns: 80px 1fr 120px 100px 150px 60px;
          padding: 0.75rem 1rem;
          background: rgba(255, 215, 0, 0.1);
          border-bottom: 1px solid rgba(255, 215, 0, 0.2);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          color: #a1a1aa;
        }

        .admin-winners-table .admin-table-row {
          display: grid;
          grid-template-columns: 80px 1fr 120px 100px 150px 60px;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid rgba(139, 92, 246, 0.1);
          align-items: center;
        }

        .admin-winners-table .admin-table-row.winner-rank-1 {
          background: rgba(255, 215, 0, 0.1);
        }

        .admin-winners-table .admin-table-row.winner-rank-2 {
          background: rgba(192, 192, 192, 0.05);
        }

        .admin-winners-table .admin-table-row.winner-rank-3 {
          background: rgba(205, 127, 50, 0.05);
        }

        .admin-winner-rank {
          font-weight: 700;
        }

        .admin-winner-diff {
          color: #22c55e;
          font-family: 'Space Mono', monospace;
          font-size: 0.85rem;
        }

        .admin-reward-edit {
          background: rgba(20, 20, 30, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 6px;
          padding: 0.3rem 0.5rem;
          color: #ffd700;
          font-size: 0.85rem;
          width: 100%;
          max-width: 140px;
        }

        .admin-reward-edit:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.6);
        }

        .admin-no-winners {
          text-align: center;
          padding: 2rem;
          color: #71717a;
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
        }

        @media (max-width: 900px) {
          .admin-winners-table .admin-table-header,
          .admin-winners-table .admin-table-row {
            grid-template-columns: 60px 1fr 100px 80px 60px;
          }
          .admin-winner-diff { display: none; }
        }

        @media (max-width: 600px) {
          .admin-winners-table .admin-table-header,
          .admin-winners-table .admin-table-row {
            grid-template-columns: 50px 1fr 80px 50px;
          }
          .admin-reward-edit { display: none; }
        }

        .admin-users-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-height: 500px;
          overflow-y: auto;
        }

        .admin-user-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: rgba(14, 14, 18, 0.6);
          border-radius: 8px;
          transition: all 0.2s;
        }

        .admin-user-item.clickable {
          cursor: pointer;
        }

        .admin-user-item.clickable:hover {
          background: rgba(139, 92, 246, 0.15);
          border-color: rgba(139, 92, 246, 0.3);
        }

        .admin-user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .admin-user-info {
          flex: 1;
          min-width: 0;
        }

        .admin-user-name {
          display: block;
          font-weight: 600;
        }

        .admin-user-details-row {
          display: flex;
          gap: 0.75rem;
          margin: 0.15rem 0;
        }

        .admin-user-twitch {
          font-size: 0.8rem;
          color: #9146ff;
        }

        .admin-user-shuffle {
          font-size: 0.8rem;
          color: #a78bfa;
        }

        .admin-user-discord {
          font-size: 0.8rem;
          color: #7289da;
        }

        .discord-username {
          color: #7289da;
        }

        .admin-user-stats {
          font-size: 0.8rem;
          color: #a1a1aa;
        }

        .admin-user-earnings {
          color: #22c55e;
        }

        .admin-user-login {
          font-size: 0.8rem;
          color: #71717a;
          white-space: nowrap;
        }

        .admin-user-view-btn {
          font-size: 0.8rem;
          color: #8b5cf6;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .admin-user-item:hover .admin-user-view-btn {
          opacity: 1;
        }

        /* Admin User Search */
        .admin-user-search {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .admin-search-input {
          flex: 1;
          min-width: 200px;
          padding: 0.6rem 1rem;
          background: rgba(20, 20, 30, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          color: #fff;
          font-size: 0.9rem;
        }

        .admin-search-input:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.6);
        }

        .admin-search-btn {
          padding: 0.6rem 1rem;
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          border: none;
          border-radius: 8px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .admin-search-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 10px rgba(139, 92, 246, 0.3);
        }

        .admin-search-clear {
          padding: 0.6rem 1rem;
          background: rgba(113, 113, 122, 0.2);
          border: 1px solid rgba(113, 113, 122, 0.3);
          border-radius: 8px;
          color: #a1a1aa;
          cursor: pointer;
          transition: all 0.2s;
        }

        .admin-search-clear:hover {
          background: rgba(113, 113, 122, 0.3);
        }

        /* Admin User Details Modal */
        .admin-user-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 1rem;
        }

        .admin-user-modal {
          background: linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 20px;
          padding: 1.5rem;
          max-width: 700px;
          width: 100%;
          max-height: 85vh;
          overflow-y: auto;
          position: relative;
        }

        .admin-user-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: #71717a;
          font-size: 1.5rem;
          cursor: pointer;
        }

        .admin-user-modal-close:hover {
          color: #fff;
        }

        .admin-user-modal-header {
          display: flex;
          gap: 1rem;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
        }

        .admin-user-modal-avatar {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          border: 3px solid rgba(139, 92, 246, 0.4);
        }

        .admin-user-modal-info h2 {
          margin: 0 0 0.25rem 0;
          font-size: 1.3rem;
        }

        .admin-user-modal-usernames {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin: 0.25rem 0;
        }

        .admin-user-modal-usernames .twitch-username {
          color: #9146ff;
          font-size: 0.9rem;
        }

        .admin-user-modal-usernames .shuffle-username {
          color: #a78bfa;
          font-size: 0.9rem;
        }

        .admin-user-modal-dates {
          color: #71717a;
          font-size: 0.8rem;
          margin: 0;
        }

        .admin-user-modal-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .admin-user-stat-box {
          background: rgba(14, 14, 18, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.15);
          border-radius: 10px;
          padding: 0.75rem;
          text-align: center;
        }

        .admin-user-stat-box.highlight {
          background: rgba(34, 197, 94, 0.1);
          border-color: rgba(34, 197, 94, 0.3);
        }

        .admin-user-stat-box .stat-number {
          display: block;
          font-size: 1.25rem;
          font-weight: 700;
          color: #fff;
        }

        .admin-user-stat-box.highlight .stat-number {
          color: #22c55e;
        }

        .admin-user-stat-box .stat-label {
          font-size: 0.75rem;
          color: #71717a;
        }

        .admin-user-modal-section {
          margin-bottom: 1.25rem;
        }

        .admin-user-modal-section h3 {
          font-size: 1rem;
          margin-bottom: 0.75rem;
          color: #a1a1aa;
        }

        .admin-user-predictions-list,
        .admin-user-wins-list,
        .admin-user-claims-list {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .admin-user-pred-item,
        .admin-user-win-item,
        .admin-user-claim-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem;
          background: rgba(14, 14, 18, 0.6);
          border-radius: 6px;
          font-size: 0.85rem;
        }

        .admin-user-win-item.rank-1 {
          background: rgba(255, 215, 0, 0.1);
          border-left: 3px solid #ffd700;
        }

        .admin-user-win-item.rank-2 {
          background: rgba(192, 192, 192, 0.1);
          border-left: 3px solid #c0c0c0;
        }

        .admin-user-win-item.rank-3 {
          background: rgba(205, 127, 50, 0.1);
          border-left: 3px solid #cd7f32;
        }

        .admin-user-claim-item.paid {
          border-left: 3px solid #22c55e;
        }

        .admin-user-claim-item.pending {
          border-left: 3px solid #f59e0b;
        }

        .pred-hunt, .win-hunt, .claim-shuffle {
          color: #a1a1aa;
          min-width: 80px;
        }

        .pred-guess, .win-guess {
          font-family: 'Space Mono', monospace;
          color: #ffd700;
        }

        .pred-status, .win-claimed {
          min-width: 70px;
        }

        .pred-date, .claim-date {
          color: #71717a;
          margin-left: auto;
        }

        .win-reward, .claim-reward {
          color: #a78bfa;
        }

        .claim-status.paid {
          color: #22c55e;
        }

        .claim-status.pending {
          color: #f59e0b;
        }

        .admin-more-note {
          text-align: center;
          color: #71717a;
          font-size: 0.8rem;
          margin-top: 0.5rem;
        }

        @media (max-width: 600px) {
          .admin-user-modal-stats {
            grid-template-columns: repeat(2, 1fr);
          }
          .admin-user-modal-header {
            flex-direction: column;
            text-align: center;
          }
        }

        /* Admin stat card variants */
        .admin-stat-card.highlight-pending {
          border-color: rgba(245, 158, 11, 0.25);
        }
        .admin-stat-card.highlight-pending .admin-stat-value {
          color: #f59e0b;
        }
        .admin-stat-card.highlight-unclaimed {
          border-color: rgba(239, 68, 68, 0.25);
        }
        .admin-stat-card.highlight-unclaimed .admin-stat-value {
          color: #ef4444;
        }
        .admin-stat-card.highlight-paid {
          border-color: rgba(34, 197, 94, 0.2);
        }
        .admin-stat-card.highlight-paid .admin-stat-value {
          color: #22c55e;
        }

        /* Admin claims table */
        .admin-claims-table {
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
          overflow: hidden;
        }
        .admin-claims-table .admin-table-header {
          display: grid;
          grid-template-columns: 1fr 100px 150px 120px 60px 100px;
          padding: 0.75rem 1rem;
          background: rgba(255, 215, 0, 0.1);
          border-bottom: 1px solid rgba(255, 215, 0, 0.2);
        }
        .admin-claims-table .admin-table-row {
          display: grid;
          grid-template-columns: 1fr 100px 150px 120px 60px 100px;
          padding: 0.75rem 1rem;
          align-items: center;
        }
        .admin-shuffle-user {
          color: #8b5cf6;
          font-weight: 600;
        }
        .admin-platform-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .admin-platform-badge.rainbet {
          background: rgba(249, 115, 22, 0.2);
          color: #f97316;
        }
        .admin-platform-badge.shuffle {
          background: rgba(139, 92, 246, 0.2);
          color: #a78bfa;
        }
        .upgraded-note {
          color: #22c55e;
          cursor: help;
        }
        .admin-small-btn.paid {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }
        .admin-small-btn.paid:hover {
          background: rgba(34, 197, 94, 0.3);
        }
        .admin-no-data {
          text-align: center;
          padding: 1.5rem;
          color: #71717a;
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
        }

        /* Giveaway form */
        .admin-giveaway-form {
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }
        .admin-giveaway-form h4 {
          margin-bottom: 1rem;
          color: #ffd700;
        }
        .giveaway-form-row {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }
        .admin-select {
          background: rgba(20, 20, 30, 0.8);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          padding: 0.5rem 1rem;
          color: #fff;
          font-size: 0.9rem;
        }
        .admin-input.amount-input {
          width: 120px;
        }
        .admin-input.description-input {
          flex: 1;
          min-width: 200px;
        }
        .admin-action-btn.add-giveaway {
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          color: #fff;
          white-space: nowrap;
        }

        /* Giveaways table */
        .admin-giveaways-table {
          background: rgba(14, 14, 18, 0.6);
          border-radius: 12px;
          overflow: hidden;
        }
        .admin-giveaways-table .admin-table-header {
          display: grid;
          grid-template-columns: 1fr 100px 1fr 120px;
          padding: 0.75rem 1rem;
          background: rgba(139, 92, 246, 0.1);
        }
        .admin-giveaways-table .admin-table-row {
          display: grid;
          grid-template-columns: 1fr 100px 1fr 120px;
          padding: 0.75rem 1rem;
          align-items: center;
        }
        .admin-giveaway-amount {
          color: #22c55e;
          font-weight: 700;
          font-family: 'Space Mono', monospace;
        }
        .admin-giveaway-desc {
          color: #a1a1aa;
          font-size: 0.9rem;
        }
        .admin-giveaway-actions {
          display: flex;
          gap: 0.5rem;
        }

        /* Sidebar Shuffle Link */
        .sidebar-shuffle-link {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 0.75rem;
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          margin-bottom: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .sidebar-shuffle-link:hover {
          background: rgba(139, 92, 246, 0.2);
          border-color: rgba(139, 92, 246, 0.4);
        }
        .shuffle-linked-icon {
          font-size: 1rem;
        }
        .shuffle-linked-name {
          flex: 1;
          color: #a78bfa;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .shuffle-edit-icon {
          font-size: 0.75rem;
          opacity: 0.6;
        }
        .shuffle-unlinked-icon {
          color: #fbbf24;
        }
        .shuffle-unlinked-text {
          flex: 1;
          color: #fbbf24;
          font-size: 0.85rem;
          font-weight: 500;
        }

        /* Shuffle Username Modal */
        .shuffle-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 1rem;
        }
        .shuffle-modal {
          background: linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%);
          border: 2px solid rgba(139, 92, 246, 0.5);
          border-radius: 24px;
          padding: 2rem;
          max-width: 420px;
          width: 100%;
          position: relative;
          animation: modalPop 0.3s ease-out;
          box-shadow: 0 0 60px rgba(139, 92, 246, 0.3);
        }
        .shuffle-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: #71717a;
          font-size: 1.5rem;
          cursor: pointer;
          transition: color 0.2s;
        }
        .shuffle-modal-close:hover {
          color: #fff;
        }
        .shuffle-modal-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        .shuffle-modal-icon {
          font-size: 3.5rem;
          margin-bottom: 0.5rem;
        }
        .shuffle-modal-header h2 {
          color: #a78bfa;
          font-size: 1.5rem;
          margin: 0.5rem 0;
        }
        .shuffle-modal-header p {
          color: #a1a1aa;
          font-size: 0.95rem;
          line-height: 1.5;
        }
        .shuffle-modal-form {
          margin-bottom: 1.5rem;
        }
        .shuffle-input-wrapper {
          display: flex;
          align-items: center;
          background: rgba(20, 20, 30, 0.8);
          border: 2px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 0 1rem;
          margin-bottom: 1rem;
          transition: border-color 0.2s;
        }
        .shuffle-input-wrapper:focus-within {
          border-color: rgba(139, 92, 246, 0.6);
        }
        .shuffle-input-prefix {
          color: #8b5cf6;
          font-size: 1.1rem;
          font-weight: 600;
        }
        .shuffle-username-input {
          flex: 1;
          background: none;
          border: none;
          padding: 0.85rem 0.5rem;
          color: #fff;
          font-size: 1rem;
          outline: none;
        }
        .shuffle-username-input::placeholder {
          color: #71717a;
        }
        .shuffle-save-btn {
          width: 100%;
          padding: 0.9rem;
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          border: none;
          border-radius: 12px;
          color: #fff;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .shuffle-save-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(139, 92, 246, 0.4);
        }
        .shuffle-save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .shuffle-modal-benefits {
          background: rgba(139, 92, 246, 0.1);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .benefits-title {
          font-size: 0.85rem;
          color: #a1a1aa;
          margin-bottom: 0.5rem;
        }
        .shuffle-modal-benefits ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .shuffle-modal-benefits li {
          padding: 0.35rem 0;
          font-size: 0.9rem;
          color: #e4e4e7;
        }
        .shuffle-skip-btn {
          width: 100%;
          padding: 0.75rem;
          background: transparent;
          border: 1px solid rgba(113, 113, 122, 0.3);
          border-radius: 10px;
          color: #71717a;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .shuffle-skip-btn:hover {
          background: rgba(113, 113, 122, 0.1);
          color: #a1a1aa;
        }

        /* Win Modal */
        .win-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 1rem;
        }
        .win-modal {
          background: linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%);
          border: 2px solid rgba(255, 215, 0, 0.5);
          border-radius: 24px;
          padding: 2rem;
          max-width: 450px;
          width: 100%;
          position: relative;
          animation: modalPop 0.3s ease-out;
          box-shadow: 0 0 60px rgba(255, 215, 0, 0.3);
        }
        @keyframes modalPop {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .win-modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: #71717a;
          font-size: 1.5rem;
          cursor: pointer;
          transition: color 0.2s;
        }
        .win-modal-close:hover {
          color: #fff;
        }
        .win-modal-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        .win-modal-icon {
          font-size: 4rem;
          animation: bounce 1s ease infinite;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .win-modal-header h2 {
          color: #ffd700;
          font-size: 1.75rem;
          margin: 0.5rem 0;
        }
        .win-modal-header p {
          color: #a1a1aa;
        }
        .win-modal-details {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 16px;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }
        .win-modal-rank {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #ffd700;
        }
        .win-modal-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .win-stat {
          text-align: center;
          padding: 0.75rem;
          background: rgba(139, 92, 246, 0.1);
          border-radius: 8px;
        }
        .win-stat.highlight {
          grid-column: 1 / -1;
          background: rgba(34, 197, 94, 0.1);
        }
        .win-stat-label {
          display: block;
          font-size: 0.75rem;
          color: #71717a;
          margin-bottom: 0.25rem;
        }
        .win-stat-value {
          font-family: 'Space Mono', monospace;
          font-weight: 700;
          color: #fff;
        }
        .win-stat.highlight .win-stat-value {
          color: #22c55e;
        }
        .win-modal-prize {
          text-align: center;
          padding: 1rem;
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.1));
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: 12px;
        }
        .prize-label {
          display: block;
          font-size: 0.8rem;
          color: #a1a1aa;
          margin-bottom: 0.25rem;
        }
        .prize-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffd700;
        }
        .win-modal-claim {
          text-align: center;
        }
        .win-modal-claim p {
          color: #a1a1aa;
          margin-bottom: 0.75rem;
        }
        .win-modal-auto-claim {
          text-align: center;
        }
        .win-auto-claim-info {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 1rem;
        }
        .win-auto-icon {
          font-size: 2rem;
          display: block;
          margin-bottom: 0.5rem;
        }
        .win-auto-claim-info p {
          color: #a1a1aa;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
        }
        .win-shuffle-username {
          display: inline-block;
          background: rgba(139, 92, 246, 0.2);
          color: #a78bfa;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1.1rem;
        }
        .win-claim-input {
          width: 100%;
          padding: 0.75rem 1rem;
          background: rgba(20, 20, 30, 0.8);
          border: 2px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          color: #fff;
          font-size: 1rem;
          margin-bottom: 1rem;
        }
        .win-claim-input:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.6);
        }
        .win-claim-btn {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(135deg, #ffd700, #ffaa00);
          border: none;
          border-radius: 12px;
          color: #000;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }
        .win-claim-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(255, 215, 0, 0.4);
        }
        .win-claim-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .win-modal-note {
          text-align: center;
          font-size: 0.8rem;
          color: #71717a;
          margin-top: 1rem;
        }
        .win-modal-note .link-btn {
          background: none;
          border: none;
          color: #a78bfa;
          text-decoration: underline;
          cursor: pointer;
          font-size: 0.8rem;
          padding: 0;
        }
        .win-modal-note .link-btn:hover {
          color: #c4b5fd;
        }

        @media (max-width: 600px) {
          .admin-table-header, .admin-table-row {
            grid-template-columns: 1fr 1fr 60px;
          }
          .admin-pred-time { display: none; }
          .admin-claims-table .admin-table-header,
          .admin-claims-table .admin-table-row {
            grid-template-columns: 1fr 100px 80px;
          }
          .admin-giveaways-table .admin-table-header,
          .admin-giveaways-table .admin-table-row {
            grid-template-columns: 1fr 80px 80px;
          }
          .admin-giveaway-desc { display: none; }
        }

        .predictions-page-container { max-width: 1200px; margin: 0 auto; }
        .predictions-intro { text-align: center; color: #a1a1aa; margin-bottom: 1.5rem; }
        
        .prediction-prizes-stat {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05));
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 12px;
          margin-bottom: 2rem;
        }
        .prizes-icon { font-size: 1.25rem; }
        .prizes-text { color: #a1a1aa; }
        .prizes-amount { 
          font-family: 'Space Mono', monospace; 
          font-weight: 700; 
          color: #22c55e; 
          font-size: 1.1rem;
        }
        
        .predictions-widget { margin-bottom: 2rem; }
        .no-active-hunt-message { text-align: center; padding: 3rem; color: #71717a; background: rgba(14, 14, 18, 0.6); border-radius: 12px; }
        .rewards-preview { flex-direction: column; align-items: flex-start !important; }
        .rewards-mini-list { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.85rem; color: #ffd700; }
        
        .last-hunt-results-section { background: rgba(20, 20, 30, 0.6); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 20px; padding: 1.5rem; margin-top: 2rem; }
        .last-hunt-results-section h2 { text-align: center; margin-bottom: 1.5rem; color: #ffd700; }
        .last-hunt-summary { display: flex; justify-content: center; gap: 2rem; margin-bottom: 2rem; flex-wrap: wrap; }
        .last-hunt-summary.enhanced { gap: 1rem; }
        .last-hunt-stat-card { display: flex; align-items: center; gap: 0.75rem; background: rgba(14, 14, 18, 0.8); padding: 1rem 1.25rem; border-radius: 12px; border: 1px solid rgba(139, 92, 246, 0.15); }
        .last-hunt-stat-card.result { border-color: rgba(34, 197, 94, 0.3); background: rgba(34, 197, 94, 0.1); }
        .last-hunt-stat-icon { font-size: 1.5rem; }
        .last-hunt-stat-label { display: block; font-size: 0.75rem; color: #a1a1aa; text-transform: uppercase; letter-spacing: 0.5px; }
        .last-hunt-stat-value { font-family: 'Space Mono', monospace; font-size: 1.25rem; font-weight: 700; color: #fff; }
        .last-hunt-stat-value.highlight { color: #22c55e; font-size: 1.4rem; }
        .last-hunt-stat { text-align: center; }
        .last-hunt-stat.result .last-hunt-value { color: #22c55e; font-size: 1.5rem; }
        .last-hunt-label { display: block; font-size: 0.8rem; color: #a1a1aa; }
        .last-hunt-value { font-family: 'Space Mono', monospace; font-size: 1.25rem; font-weight: 700; color: #ffd700; }
        
        .winners-podium { margin-bottom: 2rem; }
        .winners-podium h3 { text-align: center; margin-bottom: 1rem; }
        .winners-podium.enhanced h3 { font-size: 1.2rem; color: #ffd700; margin-bottom: 1.5rem; }
        
        /* Prediction Podium - Similar to Leaderboard Podium */
        .prediction-podium {
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .prediction-podium-card {
          background: rgba(14, 14, 18, 0.9);
          border-radius: 16px;
          padding: 1.25rem;
          text-align: center;
          min-width: 140px;
          border: 2px solid transparent;
          position: relative;
        }
        .prediction-podium-card.first {
          border-color: #ffd700;
          background: linear-gradient(180deg, rgba(40, 35, 20, 0.95) 0%, rgba(25, 22, 15, 0.95) 100%);
          box-shadow: 0 0 30px rgba(255, 215, 0, 0.3);
          transform: translateY(-15px);
          padding: 1.5rem;
          min-width: 160px;
        }
        .prediction-podium-card.second {
          border-color: #c0c0c0;
          background: linear-gradient(180deg, rgba(35, 35, 40, 0.95) 0%, rgba(20, 20, 25, 0.95) 100%);
        }
        .prediction-podium-card.third {
          border-color: #cd7f32;
          background: linear-gradient(180deg, rgba(40, 30, 25, 0.95) 0%, rgba(25, 20, 18, 0.95) 100%);
        }
        .prediction-podium-crown {
          position: absolute;
          top: -25px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 1.75rem;
          animation: crownFloat 3s ease-in-out infinite;
        }
        .prediction-podium-rank {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .prediction-podium-card.first .prediction-podium-rank { font-size: 1.75rem; }
        .prediction-podium-avatar {
          width: 55px;
          height: 55px;
          border-radius: 50%;
          margin-bottom: 0.5rem;
          border: 3px solid rgba(255, 255, 255, 0.2);
        }
        .prediction-podium-card.first .prediction-podium-avatar {
          width: 65px;
          height: 65px;
          border-color: rgba(255, 215, 0, 0.5);
        }
        .prediction-podium-name {
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 0.35rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .prediction-podium-guess {
          font-family: 'Space Mono', monospace;
          color: #ffd700;
          font-size: 1rem;
          font-weight: 700;
        }
        .prediction-podium-card.first .prediction-podium-guess { font-size: 1.15rem; }
        .prediction-podium-diff {
          font-size: 0.75rem;
          color: #22c55e;
          margin-top: 0.25rem;
        }
        .prediction-podium-reward {
          margin-top: 0.5rem;
          padding: 0.3rem 0.6rem;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(236, 72, 153, 0.3));
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #fff;
        }

        /* Current Hunt Predictions Section */
        .current-predictions-section {
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 20px;
          padding: 1.5rem;
          margin-top: 2rem;
        }
        .current-predictions-section h2 {
          text-align: center;
          margin-bottom: 0.5rem;
          color: #fff;
        }
        .current-predictions-note {
          text-align: center;
          color: #f59e0b;
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
          padding: 0.5rem;
          background: rgba(245, 158, 11, 0.1);
          border-radius: 8px;
        }
        .current-predictions-note.live-note {
          color: #22c55e;
          background: rgba(34, 197, 94, 0.1);
        }
        .live-predictions-badge {
          margin-left: 0.75rem;
          font-size: 0.75rem;
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          padding: 0.2rem 0.6rem;
          border-radius: 12px;
          animation: live-badge-pulse 2s ease-in-out infinite;
        }

        /* User's own prediction at top */
        .my-current-prediction {
          margin-bottom: 1.5rem;
        }
        .my-current-prediction-label {
          font-size: 0.8rem;
          color: #a78bfa;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.5rem;
          text-align: center;
        }
        .my-current-prediction-card {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 1rem 1.5rem;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.1));
          border: 2px solid rgba(139, 92, 246, 0.4);
          border-radius: 12px;
          animation: myPredictionGlow 2s ease-in-out infinite;
        }
        @keyframes myPredictionGlow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(139, 92, 246, 0.3), 0 0 20px rgba(139, 92, 246, 0.1);
          }
          50% {
            box-shadow: 0 0 20px rgba(139, 92, 246, 0.5), 0 0 40px rgba(139, 92, 246, 0.2);
          }
        }
        .my-current-prediction-card .current-pred-avatar {
          width: 40px;
          height: 40px;
        }
        .my-current-prediction-card .current-pred-name {
          font-weight: 600;
          font-size: 1.1rem;
        }
        .my-current-prediction-card .current-pred-guess {
          font-size: 1.25rem;
        }

        .predictions-sort-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }
        .sort-label {
          font-size: 0.85rem;
          color: #a1a1aa;
          margin-right: 0.25rem;
        }
        .sort-btn {
          background: rgba(14, 14, 18, 0.6);
          border: 1px solid rgba(139, 92, 246, 0.2);
          color: #a1a1aa;
          padding: 0.4rem 0.75rem;
          border-radius: 8px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .sort-btn:hover {
          border-color: rgba(139, 92, 246, 0.4);
          color: #e4e4e7;
        }
        .sort-btn.active {
          background: rgba(139, 92, 246, 0.2);
          border-color: #8b5cf6;
          color: #a78bfa;
        }

        .current-predictions-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .current-pred-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: rgba(14, 14, 18, 0.6);
          border-radius: 10px;
          border-left: 3px solid rgba(139, 92, 246, 0.3);
        }
        .current-pred-item.my-prediction {
          background: rgba(139, 92, 246, 0.15);
          border-left-color: #a78bfa;
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-left-width: 3px;
        }
        .you-badge {
          display: inline-block;
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          color: #fff;
          font-size: 0.65rem;
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          margin-left: 0.5rem;
          font-weight: 700;
          vertical-align: middle;
        }
        .current-pred-rank {
          font-weight: 700;
          color: #71717a;
          min-width: 40px;
        }
        .current-pred-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
        }
        .current-pred-name {
          flex: 1;
          font-weight: 500;
        }
        .current-pred-guess {
          font-family: 'Space Mono', monospace;
          color: #ffd700;
          font-weight: 600;
        }
        .current-pred-time {
          font-size: 0.8rem;
          color: #71717a;
        }

        /* Pagination */
        .predictions-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(139, 92, 246, 0.15);
        }
        .pagination-btn {
          padding: 0.5rem 1rem;
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          color: #a78bfa;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .pagination-btn:hover:not(:disabled) {
          background: rgba(139, 92, 246, 0.3);
        }
        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .pagination-info {
          color: #71717a;
          font-size: 0.9rem;
        }

        @media (max-width: 600px) {
          .current-pred-time { display: none; }
        }
        
        .podium-row { display: flex; justify-content: center; gap: 1.5rem; flex-wrap: wrap; }
        .podium-place { background: rgba(14, 14, 18, 0.8); border-radius: 16px; padding: 1.5rem; text-align: center; min-width: 150px; border: 2px solid transparent; }
        .podium-place.place-1 { border-color: #ffd700; box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
        .podium-place.place-2 { border-color: #c0c0c0; }
        .podium-place.place-3 { border-color: #cd7f32; }
        .podium-rank { font-size: 2rem; margin-bottom: 0.5rem; }
        .podium-avatar { width: 60px; height: 60px; border-radius: 50%; margin-bottom: 0.5rem; }
        .podium-name { font-weight: 600; margin-bottom: 0.25rem; }
        .podium-prediction { font-family: 'Space Mono', monospace; color: #ffd700; font-size: 1.1rem; }
        .podium-diff { font-size: 0.8rem; color: #a1a1aa; }
        .podium-reward { margin-top: 0.5rem; padding: 0.25rem 0.5rem; background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3)); border-radius: 8px; font-size: 0.85rem; color: #e4e4e7; }
        
        .predictions-leaderboard { margin-top: 1.5rem; }
        .predictions-leaderboard h3 { margin-bottom: 1rem; font-size: 1rem; color: #a1a1aa; }
        .predictions-leaderboard-list { display: flex; flex-direction: column; gap: 0.5rem; max-height: 400px; overflow-y: auto; }
        .predictions-lb-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem 1rem; background: rgba(14, 14, 18, 0.6); border-radius: 10px; border-left: 3px solid transparent; }
        .predictions-lb-item.winner-1 { border-left-color: #ffd700; background: rgba(255, 215, 0, 0.1); }
        .predictions-lb-item.winner-2 { border-left-color: #c0c0c0; background: rgba(192, 192, 192, 0.1); }
        .predictions-lb-item.winner-3 { border-left-color: #cd7f32; background: rgba(205, 127, 50, 0.1); }
        .predictions-lb-rank { font-weight: 700; color: #71717a; min-width: 40px; }
        .predictions-lb-avatar { width: 32px; height: 32px; border-radius: 50%; }
        .predictions-lb-name { flex: 1; font-weight: 500; }
        .predictions-lb-guess { font-family: 'Space Mono', monospace; color: #ffd700; }
        .predictions-lb-diff { font-size: 0.8rem; color: #22c55e; min-width: 100px; text-align: right; }
        .predictions-lb-reward { background: linear-gradient(135deg, #8b5cf6, #ec4899); padding: 0.2rem 0.5rem; border-radius: 6px; font-size: 0.75rem; font-weight: 600; }
        .predictions-more-note { text-align: center; color: #71717a; font-size: 0.85rem; margin-top: 1rem; }
        
        /* Prediction Results Page */
        .prediction-results-container { max-width: 900px; margin: 0 auto; }
        .results-hunt-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding: 1rem 1.5rem; background: rgba(20, 20, 30, 0.6); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 12px; }
        .results-hunt-id { font-size: 1.25rem; font-weight: 700; color: #8b5cf6; }
        .results-hunt-date { color: #a1a1aa; font-size: 0.9rem; }
        .predictions-leaderboard.full { margin-top: 2rem; }
        .predictions-leaderboard.full h3 { font-size: 1.1rem; margin-bottom: 1rem; color: #e4e4e7; }
        .no-results-message { text-align: center; padding: 4rem 2rem; background: rgba(20, 20, 30, 0.6); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 16px; }
        .no-results-icon { font-size: 4rem; margin-bottom: 1rem; }
        .no-results-message h3 { margin: 0 0 0.5rem 0; font-size: 1.5rem; }
        .no-results-message p { color: #a1a1aa; margin-bottom: 1.5rem; }
        
        /* Link button in predictions */
        .link-btn { background: none; border: none; color: #8b5cf6; font-weight: 600; cursor: pointer; text-decoration: underline; font-size: inherit; padding: 0; transition: color 0.2s; }
        .link-btn:hover { color: #a78bfa; }
        .current-predictions-note.results-link { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
        
        @media (max-width: 600px) {
          .prediction-podium { flex-direction: column; align-items: center; }
          .prediction-podium-card.first { transform: translateY(0); order: -1; }
          .last-hunt-summary.enhanced { flex-direction: column; align-items: stretch; gap: 0.75rem; }
          .results-hunt-header { flex-direction: column; gap: 0.5rem; text-align: center; }
        }
        
        .my-predictions-container { max-width: 900px; margin: 0 auto; }
        .login-required-message, .no-predictions-message { text-align: center; padding: 3rem; background: rgba(20, 20, 30, 0.6); border-radius: 16px; border: 1px solid rgba(139, 92, 246, 0.2); }
        .no-predictions-icon { font-size: 3rem; margin-bottom: 1rem; }
        .my-predictions-list { display: flex; flex-direction: column; gap: 1rem; }
        .my-prediction-card { background: rgba(20, 20, 30, 0.6); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 16px; padding: 1.25rem; }
        .my-prediction-card.completed { border-left: 4px solid #22c55e; }
        .my-prediction-card.pending { border-left: 4px solid #f59e0b; }
        .my-pred-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
        .my-pred-hunt { font-weight: 600; font-size: 1.1rem; }
        .my-pred-status { padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.8rem; font-weight: 600; }
        .my-pred-status.completed { background: rgba(34, 197, 94, 0.2); color: #22c55e; }
        .my-pred-status.active { background: rgba(34, 197, 94, 0.2); color: #22c55e; }
        .my-pred-status.paused { background: rgba(245, 158, 11, 0.2); color: #f59e0b; }
        .my-pred-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
        .my-pred-stat { text-align: center; }
        .my-pred-label { display: block; font-size: 0.75rem; color: #a1a1aa; margin-bottom: 0.25rem; }
        .my-pred-value { font-family: 'Space Mono', monospace; font-weight: 600; }
        .my-pred-value.guess { color: #ffd700; font-size: 1.25rem; }
        .my-pred-value.diff.close { color: #22c55e; }
        .my-pred-value.rank.rank-1 { color: #ffd700; }
        .my-pred-value.rank.rank-2 { color: #c0c0c0; }
        .my-pred-value.rank.rank-3 { color: #cd7f32; }
        .my-pred-reward { background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3)); padding: 0.75rem; border-radius: 10px; text-align: center; font-weight: 600; margin-bottom: 0.75rem; }
        .my-pred-date { font-size: 0.8rem; color: #71717a; text-align: right; }
        
        /* Profile Page */
        .profile-container { max-width: 900px; margin: 0 auto; }
        .profile-header { display: flex; align-items: center; gap: 1.5rem; background: rgba(20, 20, 30, 0.6); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 16px; padding: 2rem; margin-bottom: 2rem; }
        .profile-avatar { width: 100px; height: 100px; border-radius: 50%; border: 3px solid rgba(139, 92, 246, 0.5); }
        .profile-info { flex: 1; }
        .profile-name { font-size: 1.75rem; margin: 0 0 0.25rem 0; }
        .profile-username { color: #a1a1aa; margin: 0 0 0.75rem 0; }
        .profile-shuffle { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; }
        .profile-shuffle .shuffle-linked { color: #ffd700; font-weight: 500; }
        .edit-shuffle-btn { background: rgba(139, 92, 246, 0.2); border: 1px solid rgba(139, 92, 246, 0.3); color: #a78bfa; padding: 0.25rem 0.75rem; border-radius: 6px; font-size: 0.8rem; cursor: pointer; transition: all 0.2s; }
        .edit-shuffle-btn:hover { background: rgba(139, 92, 246, 0.3); }
        .link-shuffle-btn { background: linear-gradient(135deg, #f59e0b, #d97706); border: none; color: #000; padding: 0.5rem 1rem; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .link-shuffle-btn:hover { transform: translateY(-2px); }
        .profile-member-since { font-size: 0.85rem; color: #71717a; margin: 0.5rem 0 0 0; }
        .profile-loading, .profile-error { text-align: center; padding: 3rem; color: #a1a1aa; }
        
        .profile-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-bottom: 2rem; }
        @media (max-width: 768px) { .profile-stats-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .profile-stats-grid { grid-template-columns: 1fr; } .profile-header { flex-direction: column; text-align: center; } }
        
        .profile-stat-card { background: rgba(20, 20, 30, 0.6); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 12px; padding: 1.25rem; text-align: center; transition: all 0.2s; }
        .profile-stat-card:hover { border-color: rgba(139, 92, 246, 0.4); transform: translateY(-2px); }
        .profile-stat-card.highlight-gold { border-color: rgba(255, 215, 0, 0.3); background: rgba(255, 215, 0, 0.05); }
        .profile-stat-card.highlight-money { border-color: rgba(34, 197, 94, 0.3); background: rgba(34, 197, 94, 0.05); }
        .profile-stat-icon { font-size: 1.5rem; margin-bottom: 0.5rem; }
        .profile-stat-value { font-family: 'Space Mono', monospace; font-size: 2rem; font-weight: 700; color: #fff; }
        .profile-stat-card.highlight-gold .profile-stat-value { color: #ffd700; }
        .profile-stat-card.highlight-money .profile-stat-value { color: #22c55e; }
        .profile-stat-label { font-size: 0.8rem; color: #a1a1aa; margin-top: 0.25rem; }
        
        .profile-section { background: rgba(20, 20, 30, 0.6); border: 1px solid rgba(139, 92, 246, 0.2); border-radius: 16px; padding: 1.5rem; margin-bottom: 1.5rem; }
        .profile-section.muted { opacity: 0.7; }
        .profile-section h3 { margin: 0 0 1rem 0; font-size: 1.1rem; }
        
        .money-breakdown { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
        .money-breakdown-item { display: flex; flex-direction: column; align-items: center; padding: 1rem 2rem; border-radius: 12px; min-width: 140px; }
        .money-breakdown-item.predictions { background: rgba(139, 92, 246, 0.1); border: 1px solid rgba(139, 92, 246, 0.3); }
        .money-breakdown-item.giveaways { background: rgba(236, 72, 153, 0.1); border: 1px solid rgba(236, 72, 153, 0.3); }
        .money-breakdown-item.slotpicks { background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.3); }
        .money-breakdown-item.pending-predictions { background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.3); }
        .money-breakdown-item.pending-giveaways { background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.3); }
        .money-breakdown-item.pending-slotpicks { background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.3); }
        .money-amount.pending { color: #f59e0b; }
        .pending-note { text-align: center; color: #a1a1aa; font-size: 0.8rem; margin-top: 1rem; font-style: italic; }
        .money-icon { font-size: 1.5rem; margin-bottom: 0.25rem; }
        .money-amount { font-family: 'Space Mono', monospace; font-size: 1.5rem; font-weight: 700; color: #22c55e; }
        .money-label { font-size: 0.8rem; color: #a1a1aa; margin-top: 0.25rem; }
        
        .win-breakdown { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
        .win-breakdown-item { display: flex; flex-direction: column; align-items: center; padding: 1rem 2rem; border-radius: 12px; min-width: 100px; }
        .win-breakdown-item.gold { background: rgba(255, 215, 0, 0.1); border: 1px solid rgba(255, 215, 0, 0.3); }
        .win-breakdown-item.silver { background: rgba(192, 192, 192, 0.1); border: 1px solid rgba(192, 192, 192, 0.3); }
        .win-breakdown-item.bronze { background: rgba(205, 127, 50, 0.1); border: 1px solid rgba(205, 127, 50, 0.3); }
        .win-medal { font-size: 1.5rem; }
        .win-count { font-family: 'Space Mono', monospace; font-size: 1.75rem; font-weight: 700; }
        .win-breakdown-item.gold .win-count { color: #ffd700; }
        .win-breakdown-item.silver .win-count { color: #c0c0c0; }
        .win-breakdown-item.bronze .win-count { color: #cd7f32; }
        .win-label { font-size: 0.8rem; color: #a1a1aa; }
        
        .best-prediction-card { background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(139, 92, 246, 0.1)); border: 1px solid rgba(34, 197, 94, 0.3); border-radius: 12px; padding: 1.25rem; }
        .best-pred-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
        .best-pred-hunt { font-weight: 600; }
        .best-pred-rank { padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.85rem; font-weight: 600; }
        .best-pred-rank.rank-1 { background: rgba(255, 215, 0, 0.2); color: #ffd700; }
        .best-pred-rank.rank-2 { background: rgba(192, 192, 192, 0.2); color: #c0c0c0; }
        .best-pred-rank.rank-3 { background: rgba(205, 127, 50, 0.2); color: #cd7f32; }
        .best-pred-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; text-align: center; }
        @media (max-width: 500px) { .best-pred-stats { grid-template-columns: 1fr; } }
        .best-pred-stat .label { display: block; font-size: 0.75rem; color: #a1a1aa; margin-bottom: 0.25rem; }
        .best-pred-stat .value { font-family: 'Space Mono', monospace; font-size: 1.25rem; font-weight: 600; }
        .best-pred-stat .value.guess { color: #ffd700; }
        .best-pred-stat .value.actual { color: #22c55e; }
        .best-pred-stat .value.diff { color: #a78bfa; }
        .best-pred-reward { margin-top: 1rem; text-align: center; background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3)); padding: 0.5rem 1rem; border-radius: 8px; font-weight: 600; }
        
        .accuracy-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
        @media (max-width: 500px) { .accuracy-stats { grid-template-columns: 1fr; } }
        .accuracy-stat { display: flex; justify-content: space-between; padding: 0.75rem 1rem; background: rgba(14, 14, 18, 0.5); border-radius: 8px; }
        .accuracy-label { color: #a1a1aa; }
        .accuracy-value { font-family: 'Space Mono', monospace; font-weight: 600; color: #fff; }
        
        .claims-status { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
        .claims-stat { display: flex; flex-direction: column; align-items: center; padding: 1rem 1.5rem; background: rgba(14, 14, 18, 0.5); border-radius: 12px; min-width: 100px; }
        .claims-stat.paid { border: 1px solid rgba(34, 197, 94, 0.3); }
        .claims-stat.pending { border: 1px solid rgba(245, 158, 11, 0.3); }
        .claims-icon { font-size: 1.25rem; margin-bottom: 0.25rem; }
        .claims-value { font-family: 'Space Mono', monospace; font-size: 1.5rem; font-weight: 700; }
        .claims-stat.paid .claims-value { color: #22c55e; }
        .claims-stat.pending .claims-value { color: #f59e0b; }
        .claims-label { font-size: 0.75rem; color: #a1a1aa; }
        
        .worst-prediction { color: #a1a1aa; font-size: 0.9rem; }
        .worst-guess { color: #ef4444; font-family: 'Space Mono', monospace; }
        
        .profile-empty-state { text-align: center; padding: 3rem; }
        .profile-empty-state .empty-icon { font-size: 3rem; margin-bottom: 1rem; }
        .profile-empty-state h3 { margin: 0 0 0.5rem 0; }
        .profile-empty-state p { color: #a1a1aa; margin-bottom: 1.5rem; }
        
        .admin-rewards-setup { width: 100%; background: rgba(14, 14, 18, 0.6); border-radius: 12px; padding: 1rem; margin-bottom: 1rem; }
        .admin-rewards-setup h4 { margin-bottom: 1rem; font-size: 0.95rem; color: #e4e4e7; }
        .admin-rewards-inputs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
        .admin-reward-input { display: flex; flex-direction: column; gap: 0.25rem; }
        .admin-reward-input label { font-size: 0.8rem; color: #a1a1aa; }
        .admin-reward-input input { width: 100%; }
        .admin-action-buttons { display: flex; gap: 1rem; flex-wrap: wrap; }
        @media (max-width: 768px) { .admin-rewards-inputs { grid-template-columns: 1fr; } }
        
        .prediction-promo-section { background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.2)); border: 1px solid rgba(139, 92, 246, 0.3); border-radius: 16px; padding: 1.5rem; margin-bottom: 2rem; }
        .prediction-promo-content { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; }
        .prediction-promo-icon { font-size: 2.5rem; }
        .prediction-promo-text { flex: 1; min-width: 200px; }
        .prediction-promo-text h3 { margin: 0 0 0.25rem 0; font-size: 1.25rem; }
        .prediction-promo-text p { margin: 0; color: #a1a1aa; font-size: 0.9rem; }
        .prediction-promo-btn { background: linear-gradient(135deg, #8b5cf6, #ec4899); color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 10px; font-weight: 600; cursor: pointer; transition: all 0.2s; white-space: nowrap; }
        .prediction-promo-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4); }
      `}</style>

      <div className="grid-bg" ref={gridBgRef} />
      {HALLOWEEN_ON && <HalloweenDecor navHeight={publicPayoutStats?.total > 0 ? 112 : 60} />}
      <EmbedStyles />

      {/* Splash loader — covers the UI until fonts/first paint settle */}
      {appLoaderState !== 'done' && (
        <div className={`app-loader ${appLoaderState === 'fading' ? 'fading' : ''}`}>
          <img src="https://i.postimg.cc/J0xRJ1dH/Photos-MO1q5GQBt-Q.png" alt="" className="app-loader-logo" />
          <div className="app-loader-title">SNUTZY</div>
          <div className="app-loader-sub">× Shuffle</div>
          <div className="app-loader-bar"><span /></div>
          <div className="app-loader-tag">{HALLOWEEN_ON ? '[ Summoning the spirits ]' : '[ Shuffling the deck ]'}</div>
        </div>
      )}

      {/* Background Art - Left */}
      {BG_ART_LEFT && (
        <img src={BG_ART_LEFT} alt="" className="bg-art bg-art-left" />
      )}

      {/* Background Art - Right */}
      {BG_ART_RIGHT && (
        <img src={BG_ART_RIGHT} alt="" className="bg-art bg-art-right" />
      )}

      <nav className="nav">
        <div className="nav-content">
          <div className="nav-left">
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-expanded={sidebarOpen}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            >
              {sidebarOpen ? (
                <>
                  {/* desktop: collapses the rail; mobile: closes the drawer */}
                  <span className="st-collapse" aria-hidden="true">«</span>
                  <span className="st-close" aria-hidden="true">✕</span>
                </>
              ) : '☰'}
            </button>
            <div className="logo" onClick={() => navigateTo('home')}>
              <img src="https://i.postimg.cc/J0xRJ1dH/Photos-MO1q5GQBt-Q.png" alt="Snutzy Logo" className="logo-img" />
              <span className="logo-text">Snutzy</span>
              <span className="logo-x">×</span>
              <img src="/shuffle-wordmark.png" alt="Shuffle" className="logo-shuffle" />
            </div>
          </div>

          {/* Total Given Away - inline center, hidden on mobile to prevent overflow */}
          {publicPayoutStats?.total > 0 && (
            <div className="nav-total-counter" style={{
              flex:1,
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              gap:'0.6rem',
              minWidth:0,
              padding:'0 1rem'
            }}>
              <span style={{fontSize:'0.65rem', fontWeight:700, color:'#3b82f6', letterSpacing:'1.5px', textTransform:'uppercase', whiteSpace:'nowrap'}}>Total Given Away</span>
              <OdometerCounter value={publicPayoutStats.total} size="small" />
            </div>
          )}

          {/* User Auth Section */}
          <div className="user-auth-section">
            {authLoading ? (
              <div className="auth-loading">...</div>
            ) : user ? (
              <>
                <div className="nav-vip-badge" onClick={() => navigateTo('chatgiveaway')} title="Chat Giveaway" style={{background: cgData?.session?.status === 'open' ? 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(236,72,153,0.2))' : undefined, borderColor: cgData?.session?.status === 'open' ? 'rgba(168,85,247,0.5)' : undefined}}>
                  <span style={{fontSize:'16px', lineHeight:1}}>🎉</span>
                  <span className="nav-vip-text">Chat Giveaway</span>
                  {cgData?.session?.status === 'open' && <span style={{marginLeft:'0.3rem', padding:'0.05rem 0.35rem', background:'rgba(34,197,94,0.2)', border:'1px solid rgba(34,197,94,0.5)', borderRadius:'3px', fontSize:'0.55rem', fontWeight:800, color:'#22c55e', letterSpacing:'1px'}}>LIVE</span>}
                </div>
                <div className="nav-vip-badge" onClick={() => navigateTo('vipdashboard')} title="VIP Dashboard">
                  <svg viewBox="0 0 24 20" width="18" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="navCrownGold" x1="0" y1="0" x2="24" y2="20">
                        <stop offset="0%" stopColor="#fde68a"/>
                        <stop offset="50%" stopColor="#fbbf24"/>
                        <stop offset="100%" stopColor="#d97706"/>
                      </linearGradient>
                    </defs>
                    <path d="M2 6 L5.5 13 L9 4 L12 12 L15 4 L18.5 13 L22 6 L20.5 17 L3.5 17 Z" fill="url(#navCrownGold)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" strokeLinejoin="round"/>
                    <circle cx="2" cy="6" r="1.2" fill="#fef3c7" stroke="rgba(0,0,0,0.2)" strokeWidth="0.3"/>
                    <circle cx="22" cy="6" r="1.2" fill="#fef3c7" stroke="rgba(0,0,0,0.2)" strokeWidth="0.3"/>
                    <circle cx="12" cy="3" r="1.4" fill="#fca5a5" stroke="rgba(0,0,0,0.2)" strokeWidth="0.3"/>
                    <rect x="3.5" y="17" width="17" height="1.4" fill="#92400e" opacity="0.4"/>
                  </svg>
                  <span className="nav-vip-text">VIP Dashboard</span>
                </div>
              <div className="user-profile-dropdown">
                <div className="user-profile-trigger">
                  {user && discordLinked === false && (
                    <button className="nav-discord-connect" onClick={connectDiscord}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
                      <span className="nav-discord-text">Connect Discord</span>
                    </button>
                  )}
                  {pointsBalance !== null && (
                    <div className="nav-points-badge" onClick={(e) => { e.stopPropagation(); navigateTo('pointsshop'); }}>
                      <span className="nav-points-icon">💎</span>
                      <span className="nav-points-value">{pointsBalance.toLocaleString()}</span>
                    </div>
                  )}
                  <img src={user.profileImage} alt={user.displayName} className="user-avatar" />
                  <span className="user-name">{user.displayName}</span>
                </div>
                <div className="user-dropdown-menu">
                  <div className="dropdown-user-info">
                    <img src={user.profileImage} alt={user.displayName} className="dropdown-avatar" />
                    <div>
                      <div className="dropdown-display-name">{user.displayName}</div>
                      <div className="dropdown-username">@{user.username}</div>
                    </div>
                  </div>
                  <button className="dropdown-item" onClick={() => { navigateTo('profile'); setSidebarOpen(false); }}>
                    👤 My Profile
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateTo('pointsshop'); setSidebarOpen(false); }}>
                    💎 Points Shop {pointsBalance !== null && <span className="dropdown-points-badge">{pointsBalance.toLocaleString()} pts</span>}
                  </button>
                  <button className="dropdown-item" onClick={() => { navigateTo('mypredictions'); setSidebarOpen(false); }}>
                    📊 My Predictions
                  </button>
                  {featureFlags.referrals && (
                    <button className="dropdown-item" onClick={() => { navigateTo('referrals'); setSidebarOpen(false); }}>
                      🤝 Referrals
                    </button>
                  )}
                  {(isAdmin || isModerator) && (
                    <button className="dropdown-item" onClick={() => { navigateTo('admin'); setSidebarOpen(false); }}>
                      ⚙️ Admin Panel
                    </button>
                  )}
                  <button className="dropdown-item logout-btn" onClick={logout}>
                    🚪 Logout
                  </button>
                </div>
              </div>
              </>
            ) : (
              <button className="twitch-login-btn" onClick={loginWithTwitch}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="twitch-icon">
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                </svg>
                Login with Twitch
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Sidebar Overlay - only on mobile */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`} 
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        {/* Join Shuffle - Featured at top */}
        <div className="sidebar-section">
          <button className={`sidebar-item featured-cta ${activePage === 'signup' ? 'active' : ''}`} onClick={() => { navigateTo('signup'); }}>
            <img src="/shuffle-s.png" alt="" className="featured-cta-logo" /> Join Shuffle
          </button>
        </div>

        {/* Newcomer checklist — in-flow progress card (disappears once completed) */}
        {user && checklistRow && !checklistRow.points_awarded && (
          <div className="sidebar-section">
            <button className="sidebar-checklist-card" onClick={() => setShowChecklistPanel(true)} title="Open the newcomer checklist">
              <div style={{position:'relative', width:'38px', height:'38px', flexShrink:0}}>
                <svg width="38" height="38" viewBox="0 0 38 38" style={{transform:'rotate(-90deg)', display:'block'}}>
                  <defs>
                    <linearGradient id="clRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="#fbbf24" />
                    </linearGradient>
                  </defs>
                  <circle cx="19" cy="19" r="16" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                  <circle
                    cx="19" cy="19" r="16" fill="none" stroke="url(#clRingGrad)" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 16}
                    strokeDashoffset={2 * Math.PI * 16 * (1 - checklistDoneCount / CHECKLIST_STEP_DEFS.length)}
                    style={{transition:'stroke-dashoffset 0.6s ease'}}
                  />
                </svg>
                <span style={{position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.58rem', fontWeight:800, color:'#e2e8f0', letterSpacing:'0.3px'}}>
                  {checklistDoneCount}/{CHECKLIST_STEP_DEFS.length}
                </span>
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:'0.78rem', fontWeight:700, color:'#e2e8f0', whiteSpace:'nowrap'}}>Newcomer Tasks</div>
                <div style={{fontSize:'0.66rem', color:'#8b8b96', marginTop:'2px', whiteSpace:'nowrap'}}>
                  {checklistDoneCount} of {CHECKLIST_STEP_DEFS.length} done · <span style={{color:'#fbbf24', fontWeight:700}}>+500 pts</span>
                </div>
              </div>
              <span className="checklist-chevron">›</span>
            </button>
          </div>
        )}

        <div className="sidebar-divider" />

        <SidebarSection
          id="main" title="Main"
          collapsed={!!navCollapsed.main} onToggle={toggleNavSection}
          containsActive={['home','leaderboard','rewards','challenges','pointsshop','claim','referrals','vipdashboard'].includes(activePage)}
        >
          <button className={`sidebar-item ${activePage === 'home' ? 'active' : ''}`} onClick={() => { navigateTo('home'); }}>
            <span className="sidebar-item-icon">🏠</span> Home
          </button>
          <button className={`sidebar-item ${activePage === 'leaderboard' ? 'active' : ''}`} onClick={() => { navigateTo('leaderboard'); }}>
            <span className="sidebar-item-icon">🏆</span> Leaderboard
          </button>
          <button className={`sidebar-item ${activePage === 'rewards' ? 'active' : ''}`} onClick={() => { navigateTo('rewards'); }}>
            <span className="sidebar-item-icon">🎁</span> Wager Rewards
          </button>
          {/* Tournament tab hidden for now — restore by removing the `false &&` */}
          {false && (
          <button className={`sidebar-item ${activePage === 'tournament' ? 'active' : ''} ${tournamentData.tournament?.status === 'live' ? 'highlight' : ''}`} onClick={() => { navigateTo('tournament'); }}>
            <span className="sidebar-item-icon">⚔️</span> Tournament
            {tournamentData.tournament?.status === 'live' && <span className="sidebar-live-badge">LIVE</span>}
            {tournamentData.tournament && tournamentData.tournament.status !== 'live' && tournamentData.tournament.status !== 'completed' && <span className="sidebar-new-badge">OPEN</span>}
          </button>
          )}
          <button className={`sidebar-item ${activePage === 'challenges' ? 'active' : ''}`} onClick={() => { navigateTo('challenges'); }}>
            <span className="sidebar-item-icon">⭐</span> Challenges
          </button>
          <button className={`sidebar-item ${activePage === 'pointsshop' ? 'active' : ''}`} onClick={() => { navigateTo('pointsshop'); }}>
            <span className="sidebar-item-icon">💎</span> Points Shop
          </button>
          <button className={`sidebar-item ${activePage === 'claim' ? 'active' : ''}`} onClick={() => { navigateTo('claim'); }}>
            <span className="sidebar-item-icon">✅</span> Claim Giveaway
          </button>
          {/* $5K raffle halted for now — no sidebar tab for anyone (streamer is an admin
              and shows the site on stream). Admins manage it by going to /raffle directly. */}
          {featureFlags.referrals && (
          <button className={`sidebar-item ${activePage === 'referrals' ? 'active' : ''}`} onClick={() => { navigateTo('referrals'); }}>
            <span className="sidebar-item-icon">🤝</span> Referrals
            {referralClaimableCount > 0 && (
              <span className="sidebar-queue-badge">{referralClaimableCount}</span>
            )}
          </button>
          )}
        </SidebarSection>

        <div className="sidebar-divider" />

        <SidebarSection
          id="hunts" title="Bonus Hunts"
          collapsed={!!navCollapsed.hunts} onToggle={toggleNavSection}
          containsActive={['bonushunts','predictions','predictionresults','communityhunt','viewerhunt'].includes(activePage)}
        >
          <button className={`sidebar-item ${activePage === 'bonushunts' ? 'active' : ''}`} onClick={() => { navigateTo('bonushunts'); }}>
            <span className="sidebar-item-icon">📊</span> Hunt Stats
          </button>
          {/* Predictions + Prediction Results merged into one tab (in-page switcher) */}
          <button className={`sidebar-item ${(activePage === 'predictions' || activePage === 'predictionresults') ? 'active' : ''} ${predictionData.predictionsOpen ? 'highlight' : ''}`} onClick={() => { navigateTo('predictions'); }}>
            <span className="sidebar-item-icon">🎯</span> Predictions
            {predictionData.predictionsOpen ? (
              <span className="sidebar-live-badge">LIVE</span>
            ) : (lastHuntResults.hunt?.completedAt && (Date.now() - new Date(lastHuntResults.hunt.completedAt).getTime() < 2 * 60 * 60 * 1000)) ? (
              <span className="sidebar-new-badge">RESULTS</span>
            ) : null}
          </button>
          {/* Slot Calls Queue tab hidden for now — restore by removing the `false &&` */}
          {false && (
          <button className={`sidebar-item ${activePage === 'slotpicks' ? 'active' : ''}`} onClick={() => { navigateTo('slotpicks'); }}>
            <span className="sidebar-item-icon">📣</span> Slot Calls Queue
            {publicSlotPicksStats.pending > 0 && (
              <span className="sidebar-queue-badge">{publicSlotPicksStats.pending}</span>
            )}
          </button>
          )}
          <button className={`sidebar-item ${activePage === 'communityhunt' ? 'active' : ''} ${communityHunt?.hunt?.status === 'live' || raffleHunt?.hunt?.status === 'live' ? 'highlight' : ''}`} onClick={() => { navigateTo('communityhunt', 'raffle'); }}>
            <span className="sidebar-item-icon">🎟️</span> Raffle Hunts
            {(communityHunt?.hunt?.status === 'live' || raffleHunt?.hunt?.status === 'live') && <span className="sidebar-live-badge">LIVE</span>}
            {raffleHunt?.hunt?.status === 'open' && !raffleHunt?.hunt?.entries_closed_at && <span className="sidebar-new-badge">OPEN</span>}
          </button>
          <button className={`sidebar-item ${activePage === 'viewerhunt' ? 'active' : ''} ${viewerHuntData?.session?.status === 'open' || viewerHuntData?.session?.status === 'drawing' ? 'highlight' : ''}`} onClick={() => { navigateTo('viewerhunt'); }}>
            <span className="sidebar-item-icon">🎴</span> Viewer Bonus Hunt
            {viewerHuntData?.session?.status === 'open' && <span className="sidebar-live-badge">LIVE</span>}
            {viewerHuntData?.session?.status === 'drawing' && <span className="sidebar-new-badge">DRAWING</span>}
            {viewerHuntData?.session?.status === 'closed' && <span className="sidebar-new-badge">READY</span>}
          </button>
        </SidebarSection>

        <div className="sidebar-divider" />

        <SidebarSection
          id="games" title="Games"
          collapsed={!!navCollapsed.games} onToggle={toggleNavSection}
          containsActive={['battles','gauntlet','banlist','calculators'].includes(activePage)}
        >
          <button className={`sidebar-item ${activePage === 'battles' ? 'active' : ''}`} onClick={() => { navigateTo('battles'); }}>
            <span className="sidebar-item-icon">⚔️</span> Slot Battles          </button>
          <button className={`sidebar-item ${activePage === 'gauntlet' && spinZoneGame === 'slots' ? 'active' : ''}`} onClick={() => { navigateTo('gauntlet', 'slots'); }}>
            <span className="sidebar-item-icon">🎰</span> Slot Picker
          </button>
          {/* Rejuicer parked — unused. Game code and /rejuicer route remain intact. */}
          <button className={`sidebar-item ${activePage === 'banlist' ? 'active' : ''}`} onClick={() => { navigateTo('banlist'); }}>
            <span className="sidebar-item-icon">🚫</span> Ban List          </button>
          <button className={`sidebar-item ${activePage === 'calculators' ? 'active' : ''}`} onClick={() => { navigateTo('calculators'); }}>
            <span className="sidebar-item-icon">🧮</span> Calculators          </button>
        </SidebarSection>

        <div className="sidebar-divider" />

        <SidebarSection
          id="content" title="Content"
          collapsed={!!navCollapsed.content} onToggle={toggleNavSection}
          containsActive={['sessions','highlights','clipearn'].includes(activePage)}
        >
          <button className={`sidebar-item ${activePage === 'sessions' ? 'active' : ''}`} onClick={() => { navigateTo('sessions'); }}>
            <span className="sidebar-item-icon">📅</span> Sessions          </button>
          <button className={`sidebar-item ${activePage === 'highlights' ? 'active' : ''}`} onClick={() => { navigateTo('highlights'); }}>
            <span className="sidebar-item-icon">🎬</span> Highlights
          </button>
          {featureFlags.clips && (
            <button className={`sidebar-item ${activePage === 'clipearn' ? 'active' : ''}`} onClick={() => { navigateTo('clipearn'); }}>
              <span className="sidebar-item-icon">✂️</span> Clip &amp; Earn
            </button>
          )}
        </SidebarSection>

        {(isAdmin || isModerator) && (
          <>
            <div className="sidebar-divider" />
            <div className="sidebar-section">
              <div className="sidebar-section-title">{isAdmin ? 'Admin' : 'Moderator'}</div>
              <button className={`sidebar-item ${activePage === 'admin' ? 'active' : ''}`} onClick={() => { navigateTo('admin'); }}>
                <span className="sidebar-item-icon">⚙️</span> {isAdmin ? 'Admin Panel' : 'Mod Panel'}
                {isAdmin && adminPendingCounts?.total > 0 && (
                  <span className="sidebar-queue-badge">{adminPendingCounts.total}</span>
                )}
              </button>
            </div>
          </>
        )}

        {/* Socials section */}
        <div className="sidebar-socials">
          <div className="sidebar-section-title">Socials</div>
          <div className="sidebar-social-links">
            <a href="https://www.twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="sidebar-social-btn twitch" title="Twitch">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
            </a>
            <a href="https://kick.com/snutzy" target="_blank" rel="noopener noreferrer" className="sidebar-social-btn kick" title="Kick">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1.5 1.5h6v5.25h1.75V4.125h1.75V1.5h6.5v5.25h-1.75v2.625h-1.75v2.625h-1.75v1.5h1.75v2.625h1.75v2.625h1.75V22.5h-6.5v-2.625H9.25V17.25H7.5V22.5h-6z"/></svg>
            </a>
            <a href="https://x.com/snutztv" target="_blank" rel="noopener noreferrer" className="sidebar-social-btn twitter" title="Twitter">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://www.youtube.com/@snutztv" target="_blank" rel="noopener noreferrer" className="sidebar-social-btn youtube" title="YouTube">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
            <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" className="sidebar-social-btn discord" title="Discord">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
            </a>
            <a href="https://www.tiktok.com/@snutzyclips" target="_blank" rel="noopener noreferrer" className="sidebar-social-btn tiktok" title="TikTok">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
            </a>
            <a href="https://www.instagram.com/snutztv/" target="_blank" rel="noopener noreferrer" className="sidebar-social-btn instagram" title="Instagram">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
          </div>
        </div>

        {/* User section at bottom of sidebar */}
        {user && (
          <div className="sidebar-user-section">
            <div className="sidebar-user-info">
              <img src={user.profileImage} alt={user.displayName} className="sidebar-user-avatar" />
              <div>
                <div className="sidebar-user-name">{user.displayName}</div>
                <div className="sidebar-user-username">@{user.username}</div>
              </div>
            </div>
            
            {/* Shuffle Username Link */}
            <div className="sidebar-shuffle-link" onClick={() => setShowShuffleModal(true)}>
              {user.shuffleUsername ? (
                <>
                  <span className="shuffle-linked-icon">🎰</span>
                  <span className="shuffle-linked-name">{user.shuffleUsername}</span>
                  <span className="shuffle-edit-icon">✏️</span>
                </>
              ) : (
                <>
                  <span className="shuffle-unlinked-icon">⚠️</span>
                  <span className="shuffle-unlinked-text">Link Shuffle Account</span>
                </>
              )}
            </div>

            {/* Points Balance */}
            {pointsBalance !== null && (
              <div className="sidebar-points-balance" onClick={() => navigateTo('pointsshop')}>
                <span className="sidebar-points-icon">💎</span>
                <span className="sidebar-points-amount">{pointsBalance.toLocaleString()}</span>
                <span className="sidebar-points-label">points</span>
              </div>
            )}
            
            <button className="sidebar-item" onClick={() => { navigateTo('mypredictions'); }}>
              <span className="sidebar-item-icon">📊</span> My Predictions
            </button>
            <button className="sidebar-item" onClick={logout}>
              <span className="sidebar-item-icon">🚪</span> Logout
            </button>
          </div>
        )}
      </aside>

      {/* App wrapper for sidebar layout */}
      <div className="app-wrapper" style={{paddingTop: publicPayoutStats?.total > 0 ? '112px' : '60px'}}>
        <div className={`sidebar-spacer ${!sidebarOpen ? 'collapsed' : ''}`}></div>
        <div className="main-content-area">
          {activePage === 'home' && (
            <LandingPage
              navigateTo={navigateTo}
              leaderboard={leaderboard}
              isStreamerLive={isStreamerLive}
              publicPayoutStats={publicPayoutStats}
              prizes={prizes}
              activePlayers={activePlayerCount}
              featuredPosts={featuredPosts}
              featureFlags={featureFlags}
            />
          )}
          <main className="main" style={activePage === 'home' ? { display: 'none' } : undefined}>
        {/* Live Prediction Banner - Show on homepage when predictions are open */}
        {activePage === 'leaderboard' && predictionData.predictionsOpen && (
          <div className="live-prediction-banner" onClick={() => navigateTo('predictions')}>
            <div className="live-prediction-content">
              <div className="live-prediction-indicator">
                <div className="live-prediction-dot"></div>
                LIVE
              </div>
              <div className="live-prediction-text">
                <div className="live-prediction-title">🎯 Prediction Game is LIVE!</div>
                <div className="live-prediction-subtitle">Guess the ending balance and win prizes!</div>
              </div>
              <button className="live-prediction-cta">Make Your Prediction →</button>
            </div>
          </div>
        )}

        {activePage === 'legal' && <LegalPage />}

        {activePage === 'notfound' && (
          <div style={{minHeight:'55vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'2rem'}}>
            <div style={{fontFamily:"'Space Mono', monospace", fontSize:'0.8rem', fontWeight:700, letterSpacing:'3px', color:'#a78bfa', marginBottom:'0.75rem'}}>404</div>
            <h1 style={{fontFamily:"'Unbounded', 'Outfit', sans-serif", fontSize:'clamp(1.4rem, 3.5vw, 2.2rem)', fontWeight:800, marginBottom:'0.75rem'}}>This page doesn't exist</h1>
            <p style={{color:'#a1a1aa', fontSize:'0.95rem', marginBottom:'1.5rem', maxWidth:'420px'}}>
              The link may be old or mistyped. Everything worth finding is one click away.
            </p>
            <div style={{display:'flex', gap:'0.75rem', flexWrap:'wrap', justifyContent:'center'}}>
              <button onClick={() => navigateTo('home')} style={{padding:'0.7rem 1.5rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'10px', color:'#fff', fontWeight:700, fontSize:'0.9rem', cursor:'pointer'}}>
                Go home
              </button>
              <button onClick={() => navigateTo('leaderboard')} style={{padding:'0.7rem 1.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'10px', color:'#e4e4e7', fontWeight:600, fontSize:'0.9rem', cursor:'pointer'}}>
                View leaderboard
              </button>
            </div>
          </div>
        )}

        {activePage === 'leaderboard' && (
          <div>
            {/* Header Section */}
            <div className="leaderboard-header">
              {publicPayoutStats?.leaderboard > 0 && (
                <div style={{display:'flex', justifyContent:'center', marginBottom:'1rem'}}>
                  <OdometerCounter value={publicPayoutStats.leaderboard} label="Total Leaderboard Paid" size="small" />
                </div>
              )}
              <div className="prize-pool-badge">
                💰 <span>{poolLabel}</span> Monthly Prize Pool
              </div>
              <h1 className="page-title">Leaderboard</h1>
              <p className="page-subtitle">
                Wager under guild name <strong>SNUTZY</strong> on Shuffle to climb the leaderboard and win prizes!
              </p>
              <div className="cta-wrapper">
                <div className="cta-pulse-ring"></div>
                <div className="cta-pulse-ring delay"></div>
                <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer" className="cta-signup-btn">
                  <span className="cta-signup-icon">🎰</span>
                  <span className="cta-signup-text">
                    <span className="cta-signup-main">Sign Up & Start Wagering</span>
                    <span className="cta-signup-sub">{poolLabel} in monthly leaderboard prizes</span>
                  </span>
                  <span className="cta-arrow">→</span>
                </a>
                <div className="cta-sparkles">
                  <span className="sparkle s1">✦</span>
                  <span className="sparkle s2">✦</span>
                  <span className="sparkle s3">✧</span>
                  <span className="sparkle s4">✦</span>
                  <span className="sparkle s5">✧</span>
                </div>
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="countdown-container">
              <span className="countdown-label">{leaderboardMode === 'weekly' ? 'Week resets in' : 'Resets in'}</span>
              <div className="countdown-timer">
                {(() => {
                  const cd = leaderboardMode === 'weekly' ? weeklyCountdown : countdown;
                  return (
                    <>
                      <div className="countdown-block">
                        <div className="countdown-number">{String(cd.days).padStart(2, '0')}</div>
                        <div className="countdown-unit">Days</div>
                      </div>
                      <span className="countdown-separator">:</span>
                      <div className="countdown-block">
                        <div className="countdown-number">{String(cd.hours).padStart(2, '0')}</div>
                        <div className="countdown-unit">Hrs</div>
                      </div>
                      <span className="countdown-separator">:</span>
                      <div className="countdown-block">
                        <div className="countdown-number">{String(cd.minutes).padStart(2, '0')}</div>
                        <div className="countdown-unit">Min</div>
                      </div>
                      <span className="countdown-separator">:</span>
                      <div className="countdown-block">
                        <div className="countdown-number">{String(cd.seconds).padStart(2, '0')}</div>
                        <div className="countdown-unit">Sec</div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Exact reset moment alongside the countdown */}
            {(() => {
              const end = leaderboardMode === 'weekly'
                ? (weeklyInfo?.endDate ? new Date(weeklyInfo.endDate) : null)
                : getLeaderboardDates().endDateTime;
              if (!end) return null;
              const ukDate = end.toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short' });
              return <div className="countdown-reset-note">Resets {ukDate}, 00:00 UK time</div>;
            })()}

            {/* Monthly/Weekly Toggle — hidden while only one period is active */}
            {featureFlags.weekly && (
            <div style={{display:'flex', justifyContent:'center', gap:'0.4rem', marginBottom:'1.25rem', background:'rgba(0,0,0,0.3)', padding:'0.35rem', borderRadius:'0.75rem', width:'fit-content', margin:'0 auto 1.25rem'}}>
              <button
                onClick={() => { setLeaderboardMode('monthly'); window.history.replaceState({}, '', '/'); }}
                style={{
                  padding:'0.55rem 1.5rem', borderRadius:'0.5rem', border:'none', cursor:'pointer',
                  fontSize:'0.85rem', fontWeight:700, transition:'all 0.2s',
                  background: leaderboardMode === 'monthly' ? 'rgba(139,92,246,0.3)' : 'transparent',
                  color: leaderboardMode === 'monthly' ? '#c084fc' : '#71717a',
                  border: leaderboardMode === 'monthly' ? '1px solid rgba(139,92,246,0.3)' : '1px solid transparent'
                }}
              >
                🗓️ Monthly
              </button>
              <button
                onClick={() => { setLeaderboardMode('weekly'); fetchWeeklyLeaderboard(); window.history.replaceState({}, '', '/weekly'); }}
                style={{
                  padding:'0.55rem 1.5rem', borderRadius:'0.5rem', border:'none', cursor:'pointer',
                  fontSize:'0.85rem', fontWeight:700, transition:'all 0.2s',
                  background: leaderboardMode === 'weekly' ? 'rgba(139,92,246,0.3)' : 'transparent',
                  color: leaderboardMode === 'weekly' ? '#c084fc' : '#71717a',
                  border: leaderboardMode === 'weekly' ? '1px solid rgba(139,92,246,0.3)' : '1px solid transparent'
                }}
              >
                📅 Weekly {weeklyInfo && `(Week ${weeklyInfo.number})`}
              </button>
            </div>
            )}


            {/* MONTHLY LEADERBOARD */}
            {leaderboardMode === 'monthly' && (
              <>

            {/* Podium Section - Top 3 */}
            {(loading || (apiStatus === 'fetching' && leaderboard.length === 0)) ? (
              <>
                <div className="podium-section">
                  <div className="skeleton podium-skeleton" />
                </div>
                {/* Skeleton rows matching the table layout — no layout shift on load */}
                <div className="leaderboard-table-container">
                  <div className="table-header">
                    <div>#</div>
                    <div>User</div>
                    <div>Wagered</div>
                    <div>Prize</div>
                  </div>
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="table-row">
                      <div><Skel w="28px" h="0.9rem" /></div>
                      <div className="table-user">
                        <Skel w="32px" h="32px" r="50%" style={{ flexShrink: 0 }} />
                        <Skel w="110px" h="0.9rem" />
                      </div>
                      <div><Skel w="80px" h="0.9rem" /></div>
                      <div><Skel w="56px" h="0.9rem" /></div>
                    </div>
                  ))}
                </div>
              </>
            ) : apiStatus === 'error' && leaderboard.length === 0 ? (
              <div className="load-error-box" style={{ marginBottom: '2rem' }}>
                <p>Having trouble loading the leaderboard — the server may be under load.</p>
                <button className="retry-btn" onClick={() => { setLoading(true); fetchLeaderboardData(); }}>↻ Retry</button>
              </div>
            ) : leaderboard.length >= 3 ? (
              <div className="podium-section">
                {/* 2nd Place */}
                <div className="podium-card second">
                  <div className="podium-avatar">
                    {leaderboard[1].profile_image_url ? (
                      <img src={leaderboard[1].profile_image_url} alt={leaderboard[1].username} className="avatar-image" />
                    ) : (
                      <div className="avatar-circle">{getInitials(leaderboard[1].username)}</div>
                    )}
                    <div className="rank-badge">2</div>
                  </div>
                  <div className="podium-username">{censorUsername(leaderboard[1].username)}</div>
                  <div className="podium-wager-label">Wagered</div>
                  <div className="podium-wager">${leaderboard[1].wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                  <div className="podium-prize">${getPrizeForRank(2).toLocaleString()}</div>
                  <div className="podium-prize-label">Reward</div>
                </div>

                {/* 1st Place */}
                <div className="podium-card first">
                  <div className="podium-avatar">
                    {leaderboard[0].profile_image_url ? (
                      <img src={leaderboard[0].profile_image_url} alt={leaderboard[0].username} className="avatar-image" />
                    ) : (
                      <div className="avatar-circle">{getInitials(leaderboard[0].username)}</div>
                    )}
                    <div className="rank-badge">1</div>
                  </div>
                  <div className="podium-username">{censorUsername(leaderboard[0].username)}</div>
                  <div className="podium-wager-label">Wagered</div>
                  <div className="podium-wager">${leaderboard[0].wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                  <div className="podium-prize">${getPrizeForRank(1).toLocaleString()}</div>
                  <div className="podium-prize-label">Reward</div>
                </div>

                {/* 3rd Place */}
                <div className="podium-card third">
                  <div className="podium-avatar">
                    {leaderboard[2].profile_image_url ? (
                      <img src={leaderboard[2].profile_image_url} alt={leaderboard[2].username} className="avatar-image" />
                    ) : (
                      <div className="avatar-circle">{getInitials(leaderboard[2].username)}</div>
                    )}
                    <div className="rank-badge">3</div>
                  </div>
                  <div className="podium-username">{censorUsername(leaderboard[2].username)}</div>
                  <div className="podium-wager-label">Wagered</div>
                  <div className="podium-wager">${leaderboard[2].wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                  <div className="podium-prize">${getPrizeForRank(3).toLocaleString()}</div>
                  <div className="podium-prize-label">Reward</div>
                </div>
              </div>
            ) : null}

            {/* Rest of Leaderboard (4-15) */}
            {leaderboard.length > 3 && (
              <div className="leaderboard-table-container">
                <div className="table-header">
                  <div>#</div>
                  <div>User</div>
                  <div>
                    Wagered
                    {isWeightedPeriod && (
                      <button
                        className="weighted-info-btn"
                        title="How wagering counts — weighted by game RTP"
                        aria-label="How wagering counts"
                        onClick={() => document.getElementById('weighted-info')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      >ⓘ</button>
                    )}
                  </div>
                  <div>Prize</div>
                </div>
                {leaderboard.slice(3, 20).map((player) => {
                  const isOutOfPrizes = player.rank > 15;
                  const fadeLevel = isOutOfPrizes ? 0.7 - ((player.rank - 16) * 0.12) : 1;
                  
                  return (
                    <div 
                      key={player.rank} 
                      className={`table-row ${isOutOfPrizes ? 'no-prize' : ''}`}
                      style={isOutOfPrizes ? { opacity: fadeLevel } : {}}
                    >
                      <div>
                        <span className="table-rank">{player.rank}th</span>
                      </div>
                      <div className="table-user">
                        {player.profile_image_url ? (
                          <img src={player.profile_image_url} alt={player.username} className="table-avatar-image" />
                        ) : (
                          <div className="table-avatar">{getInitials(player.username)}</div>
                        )}
                        <span className="table-username">{censorUsername(player.username)}</span>
                      </div>
                      <div className="table-wager">${player.wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                      <div className="table-prize">
                        {isOutOfPrizes ? (
                          <span className="no-prize-dash">—</span>
                        ) : (
                          `$${getPrizeForRank(player.rank).toLocaleString()}`
                        )}
                      </div>
                    </div>
                  );
                })}
                {leaderboard.length > 15 && (
                  <div className="leaderboard-cutoff-note">
                    Top 15 receive prizes • Keep wagering to climb the ranks!
                  </div>
                )}
              </div>
            )}

            {/* API Status */}
            <div className="api-status">
              <span className={`status-dot ${leaderboardStale ? 'stale' : apiStatus}`} />
              <span>{leaderboardStale ? 'Cached data' : apiStatus === 'success' ? 'Live data' : apiStatus === 'fetching' ? 'Updating...' : 'Connecting...'}</span>
              {cacheTime && <span>• Last updated {(() => {
                const date = new Date(cacheTime);
                const ago = Math.floor((Date.now() - date.getTime()) / 1000);
                const relative = ago < 60 ? 'just now' : ago < 3600 ? `${Math.floor(ago / 60)}m ago` : `${Math.floor(ago / 3600)}h ${Math.floor((ago % 3600) / 60)}m ago`;
                return `${relative} (${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`;
              })()}</span>}
            </div>
            <div style={{textAlign:'center', fontSize:'0.7rem', color:'#71717a', marginTop:'0.35rem'}}>
              Usernames are partially hidden (e.g. ya***r) to protect player privacy.
            </div>

            {/* Weighted Wagering explainer (anchor target for the ⓘ next to the
                Wagered column header) */}
            {isWeightedPeriod && (
              <div id="weighted-info" style={{background:'rgba(139,92,246,0.08)', border:'1px solid rgba(139,92,246,0.2)', borderRadius:'12px', padding:'1rem 1.25rem', marginTop:'1rem', marginBottom:'1rem'}}>
                <div style={{fontSize:'0.85rem', fontWeight:700, color:'#c084fc', marginBottom:'0.5rem'}}>⚖️ How wagering counts</div>
                <div style={{fontSize:'0.75rem', color:'#a1a1aa', marginBottom:'0.6rem', lineHeight:1.5}}>
                  Leaderboard positions use <strong style={{color:'#d4d4d8'}}>weighted</strong> wagers: the higher a game's RTP, the smaller the share of your wager that counts. Wager reward tiers pay 2× their pre-April amounts, and tier progress starts fresh each monthly period.
                </div>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.4rem', fontSize:'0.72rem'}}>
                  <div style={{padding:'0.55rem 0.45rem', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'6px', textAlign:'center'}}>
                    <div style={{color:'#22c55e', fontWeight:800, fontSize:'1.05rem'}}>1×</div>
                    <div style={{color:'#a1a1aa', fontSize:'0.65rem', marginTop:'0.15rem'}}>RTP ≤ 98%</div>
                    <div style={{color:'#71717a', fontSize:'0.6rem'}}>Most slots</div>
                  </div>
                  <div style={{padding:'0.55rem 0.45rem', background:'rgba(251,191,36,0.07)', border:'1px solid rgba(251,191,36,0.22)', borderRadius:'6px', textAlign:'center'}}>
                    <div style={{color:'#fbbf24', fontWeight:800, fontSize:'1.05rem'}}>0.5×</div>
                    <div style={{color:'#a1a1aa', fontSize:'0.65rem', marginTop:'0.15rem'}}>RTP 98–99%</div>
                    <div style={{color:'#71717a', fontSize:'0.6rem'}}>High-RTP slots</div>
                  </div>
                  <div style={{padding:'0.55rem 0.45rem', background:'rgba(248,113,113,0.07)', border:'1px solid rgba(248,113,113,0.22)', borderRadius:'6px', textAlign:'center'}}>
                    <div style={{color:'#f87171', fontWeight:800, fontSize:'1.05rem'}}>0.1×</div>
                    <div style={{color:'#a1a1aa', fontSize:'0.65rem', marginTop:'0.15rem'}}>RTP ≥ 99%</div>
                    <div style={{color:'#71717a', fontSize:'0.6rem'}}>Originals / Live / Blackjack</div>
                  </div>
                </div>
              </div>
            )}

            {/* Raffle Lottery Banner — $5K raffle halted, hidden for everyone (the
                leaderboard is shown on stream by admin accounts). Re-enable by restoring
                the leaderboardMode === 'monthly' condition. */}
            {false && leaderboardMode === 'monthly' && (
              <div style={{
                position:'relative', overflow:'hidden',
                background:'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(139,92,246,0.1) 50%, rgba(236,72,153,0.06) 100%)',
                border:'1px solid rgba(245,158,11,0.2)',
                borderRadius:'12px', padding:'0.75rem 1.25rem', marginBottom:'1rem',
              }}>
                <div style={{position:'absolute', top:'-50%', right:'-10%', width:'150px', height:'150px', background:'radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%)', borderRadius:'50%', pointerEvents:'none'}} />
                
                <div style={{display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap', position:'relative', zIndex:1}}>
                  {/* Custom raffle ticket icon */}
                  <div style={{flexShrink:0}}>
                    <svg width="40" height="40" viewBox="0 0 52 52" fill="none">
                      <defs>
                        <linearGradient id="ticketGrad" x1="0" y1="0" x2="52" y2="52">
                          <stop offset="0%" stopColor="#fbbf24"/>
                          <stop offset="100%" stopColor="#f59e0b"/>
                        </linearGradient>
                        <linearGradient id="starGrad" x1="0" y1="0" x2="20" y2="20">
                          <stop offset="0%" stopColor="#fde68a"/>
                          <stop offset="100%" stopColor="#fbbf24"/>
                        </linearGradient>
                      </defs>
                      <rect x="4" y="12" width="44" height="28" rx="6" fill="url(#ticketGrad)" opacity="0.9"/>
                      <rect x="4" y="12" width="44" height="28" rx="6" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none"/>
                      <circle cx="16" cy="12" r="3" fill="#0e0e12"/>
                      <circle cx="16" cy="40" r="3" fill="#0e0e12"/>
                      <line x1="16" y1="16" x2="16" y2="36" stroke="rgba(0,0,0,0.2)" strokeWidth="1" strokeDasharray="2 2"/>
                      <path d="M33 22l2.4 4.8 5.3.8-3.8 3.7.9 5.3L33 34l-4.8 2.6.9-5.3-3.8-3.7 5.3-.8z" fill="url(#starGrad)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5"/>
                      <text x="10" y="31" fontSize="12" fontWeight="800" fill="rgba(0,0,0,0.4)" fontFamily="monospace">$</text>
                    </svg>
                  </div>
                  
                  <div style={{flex:1, minWidth:'160px'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.15rem'}}>
                      <span style={{fontSize:'1rem', fontWeight:900, color:'#fbbf24', textShadow:'0 0 15px rgba(245,158,11,0.3)'}}>$5,000 MONTHLY RAFFLE</span>
                      <span style={{fontSize:'0.55rem', fontWeight:700, padding:'0.1rem 0.35rem', background:'rgba(245,158,11,0.2)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'3px', color:'#fcd34d', letterSpacing:'0.5px'}}>NEW</span>
                    </div>
                    <div style={{fontSize:'0.75rem', color:'#d4d4d8'}}>
                      Every <strong style={{color:'#fbbf24'}}>$100 wagered</strong> = 1 ticket. Winner drawn at leaderboard reset!
                    </div>
                  </div>
                  
                  <div style={{display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap'}}>
                    <div style={{textAlign:'center', padding:'0.35rem 0.7rem', background:'rgba(0,0,0,0.4)', borderRadius:'8px', border:'1px solid rgba(245,158,11,0.12)'}}>
                      <div style={{fontSize:'1rem', fontWeight:800, color:'#fbbf24'}}>{(raffleLottery?.totalTickets || 0).toLocaleString()}</div>
                      <div style={{fontSize:'0.58rem', color:'#71717a'}}>Total Tickets</div>
                    </div>
                    <div style={{textAlign:'center', padding:'0.35rem 0.7rem', background:'rgba(0,0,0,0.4)', borderRadius:'8px', border:'1px solid rgba(139,92,246,0.12)'}}>
                      <div style={{fontSize:'1rem', fontWeight:800, color:'#a78bfa'}}>{raffleLottery?.totalParticipants || 0}</div>
                      <div style={{fontSize:'0.58rem', color:'#71717a'}}>Participants</div>
                    </div>
                    {user && raffleLottery?.userShuffleLinked ? (
                      <div style={{textAlign:'center', padding:'0.35rem 0.7rem', background:'rgba(34,197,94,0.08)', borderRadius:'8px', border:'1px solid rgba(34,197,94,0.2)'}}>
                        <div style={{fontSize:'1rem', fontWeight:800, color:'#22c55e'}}>{raffleLottery.userTickets}</div>
                        <div style={{fontSize:'0.58rem', color:'#71717a'}}>Your Tickets</div>
                        {raffleLottery.progressToNext > 0 && raffleLottery.progressToNext < 100 && (
                          <div style={{marginTop:'0.2rem', width:'50px', height:'3px', background:'rgba(255,255,255,0.08)', borderRadius:'2px', overflow:'hidden'}}>
                            <div style={{width:`${raffleLottery.progressToNext}%`, height:'100%', background:'linear-gradient(90deg, #22c55e, #4ade80)', borderRadius:'2px'}} />
                          </div>
                        )}
                      </div>
                    ) : user && raffleLottery && !raffleLottery.userShuffleLinked ? (
                      <button onClick={() => navigateTo('profile')} style={{padding:'0.35rem 0.7rem', background:'rgba(139,92,246,0.2)', border:'1px solid rgba(139,92,246,0.25)', borderRadius:'8px', color:'#c084fc', fontSize:'0.72rem', fontWeight:600, cursor:'pointer'}}>
                        🔗 Link Shuffle
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* Raffle Winner Display / Rolling Animation */}
            {leaderboardMode === 'monthly' && (raffleResult || raffleRolling || user?.username?.toLowerCase() === 'notpa1n') && (
              <div style={{
                position:'relative', overflow:'hidden',
                background: raffleResult?.status === 'paid' ? 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.04))' : 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(139,92,246,0.06))',
                border: `1px solid ${raffleResult?.status === 'paid' ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.15)'}`,
                borderRadius:'16px', padding:'1.25rem 1.5rem', marginBottom:'1.5rem', textAlign:'center'
              }}>
                {/* Rolling animation */}
                {raffleRolling && (
                  <div>
                    <div style={{fontSize:'0.8rem', color:'#fbbf24', fontWeight:700, marginBottom:'0.75rem', letterSpacing:'2px'}}>PICKING WINNER...</div>
                    <div style={{
                      height:'48px', overflow:'hidden', position:'relative', 
                      background:'rgba(0,0,0,0.4)', borderRadius:'10px', margin:'0 auto', maxWidth:'300px',
                      border:'1px solid rgba(245,158,11,0.3)'
                    }}>
                      <div style={{
                        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                        animation:'raffleScroll 0.1s linear infinite', height:'100%'
                      }}>
                        {raffleRollNames.slice(-1).map((name, i) => (
                          <div key={i} style={{fontSize:'1.3rem', fontWeight:800, color:'#fbbf24', padding:'0.5rem'}}>{name}</div>
                        ))}
                      </div>
                    </div>
                    <style>{`@keyframes rafflePulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
                    <div style={{marginTop:'0.5rem', fontSize:'0.7rem', color:'#71717a', animation:'rafflePulse 1s infinite'}}>Rolling through {(raffleLottery?.totalTickets || 0).toLocaleString()} tickets...</div>
                  </div>
                )}

                {/* Winner revealed */}
                {raffleResult && !raffleRolling && (
                  <div>
                    <div style={{fontSize:'0.7rem', color:'#71717a', marginBottom:'0.4rem', letterSpacing:'1px', textTransform:'uppercase'}}>
                      {raffleResult.status === 'paid' ? '✅ Raffle Winner — Paid' : '🎰 Raffle Winner'}
                    </div>
                    <div style={{
                      display:'inline-flex', alignItems:'center', gap:'0.75rem',
                      padding:'0.75rem 1.5rem', background:'rgba(0,0,0,0.4)',
                      borderRadius:'12px', border:'1px solid rgba(245,158,11,0.3)', margin:'0 auto'
                    }}>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="14" fill="rgba(245,158,11,0.2)" stroke="#fbbf24" strokeWidth="1.5"/>
                        <text x="16" y="21" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fbbf24">★</text>
                      </svg>
                      <div style={{textAlign:'left'}}>
                        <div style={{fontSize:'1.3rem', fontWeight:900, color:'#fbbf24', textShadow:'0 0 20px rgba(245,158,11,0.4)'}}>
                          {censorUsername(raffleResult.winner_username)}
                        </div>
                        <div style={{fontSize:'0.72rem', color:'#a1a1aa'}}>
                          {raffleResult.winner_tickets} tickets • ${parseFloat(raffleResult.winner_wager || 0).toLocaleString(undefined, {maximumFractionDigits:0})} wagered
                        </div>
                      </div>
                      <div style={{textAlign:'right', marginLeft:'0.5rem'}}>
                        <div style={{fontSize:'1.5rem', fontWeight:900, color:'#22c55e'}}>$5,000</div>
                        <div style={{fontSize:'0.62rem', color:'#71717a'}}>{raffleResult.total_tickets?.toLocaleString()} total tickets</div>
                      </div>
                    </div>
                    <div style={{marginTop:'0.5rem', fontSize:'0.68rem', color:'#71717a'}}>
                      Drawn {new Date(raffleResult.picked_at).toLocaleString()} • {raffleResult.total_participants} participants
                    </div>
                  </div>
                )}

                {/* No winner yet */}
                {!raffleResult && !raffleRolling && user?.username?.toLowerCase() === 'notpa1n' && (
                  <div style={{fontSize:'0.85rem', color:'#71717a', padding:'0.5rem 0'}}>
                    {raffleDrawPeriod === 'previous'
                      ? 'Previous period raffle ready to draw!'
                      : 'Tickets accumulating for this period...'}
                  </div>
                )}

                {/* Admin controls - notpa1n only */}
                {user?.username?.toLowerCase() === 'notpa1n' && !raffleRolling && (
                  <div style={{marginTop:'0.75rem', display:'flex', gap:'0.5rem', justifyContent:'center', flexWrap:'wrap'}}>
                    {!raffleResult && (
                      <button onClick={() => pickRaffleWinnerAction(false)}
                        style={{padding:'0.4rem 0.9rem', background:'linear-gradient(135deg, #f59e0b, #d97706)', border:'none', borderRadius:'8px', color:'#000', fontSize:'0.78rem', fontWeight:700, cursor:'pointer'}}>
                        🎰 Pick Winner
                      </button>
                    )}
                    {raffleResult && (
                      <>
                        {raffleResult.status !== 'paid' && (
                          <button onClick={() => payRaffleWinner(raffleResult.id)}
                            style={{padding:'0.4rem 0.9rem', background:'#22c55e', border:'none', borderRadius:'8px', color:'#000', fontSize:'0.78rem', fontWeight:700, cursor:'pointer'}}>
                            💰 Mark as Paid
                          </button>
                        )}
                        <button onClick={() => { if (confirm('Reroll the raffle winner? This will replace the current winner.')) pickRaffleWinnerAction(true); }}
                          style={{padding:'0.4rem 0.9rem', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'8px', color:'#f87171', fontSize:'0.78rem', fontWeight:600, cursor:'pointer'}}>
                          🔄 Reroll
                        </button>
                        <button onClick={async () => { if (!confirm('Delete this raffle winner entirely? This cannot be undone.')) return; try { const token = getAuthToken(); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-lottery/delete`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ id: raffleResult.id }) }); const data = await res.json(); if (res.ok) { setRaffleResult(null); alert('✅ Raffle winner deleted'); } else { alert('Error: ' + (data.error || res.status)); } } catch(e) { alert('Network error: ' + e.message); } }}
                          style={{padding:'0.4rem 0.9rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', color:'#ef4444', fontSize:'0.78rem', fontWeight:600, cursor:'pointer'}}>
                          🗑️ Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

              </>
            )}

            {/* WEEKLY LEADERBOARD */}
            {leaderboardMode === 'weekly' && (
              <div>
                {weeklyInfo && (
                  <div style={{textAlign:'center', marginBottom:'1rem'}}>
                    <div style={{display:'inline-flex', alignItems:'center', gap:'0.5rem', padding:'0.35rem 0.9rem', background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.2)', borderRadius:'8px', marginBottom:'0.4rem'}}>
                      <span style={{fontSize:'0.85rem', fontWeight:700, color:'#c084fc'}}>💰 $2,500 Weekly Prize Pool</span>
                    </div>
                    <div style={{fontSize:'1.1rem', fontWeight:800, color:'#c084fc', marginBottom:'0.2rem'}}>
                      📅 Week {weeklyInfo.number}: {weeklyInfo.label}
                    </div>
                    <div style={{fontSize:'0.75rem', color:'#71717a'}}>
                      Top 10 wagerers win prizes every week!
                    </div>
                  </div>
                )}

                {weeklyLeaderboard.length >= 3 ? (
                  <div className="podium-section">
                    {/* 2nd Place */}
                    <div className="podium-card second">
                      <div className="podium-avatar">
                        {weeklyLeaderboard[1].profile_image_url ? (
                          <img src={weeklyLeaderboard[1].profile_image_url} alt={weeklyLeaderboard[1].username} className="avatar-image" />
                        ) : (
                          <div className="avatar-circle">{getInitials(weeklyLeaderboard[1].username)}</div>
                        )}
                        <div className="rank-badge">2</div>
                      </div>
                      <div className="podium-username">{censorUsername(weeklyLeaderboard[1].username)}</div>
                      <div className="podium-wager">${weeklyLeaderboard[1].wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                      <div className="podium-prize">${getWeeklyPrizeForRank(2).toLocaleString()}</div>
                    </div>

                    {/* 1st Place */}
                    <div className="podium-card first">
                      <div className="crown">👑</div>
                      <div className="podium-avatar">
                        {weeklyLeaderboard[0].profile_image_url ? (
                          <img src={weeklyLeaderboard[0].profile_image_url} alt={weeklyLeaderboard[0].username} className="avatar-image" />
                        ) : (
                          <div className="avatar-circle">{getInitials(weeklyLeaderboard[0].username)}</div>
                        )}
                        <div className="rank-badge">1</div>
                      </div>
                      <div className="podium-username">{censorUsername(weeklyLeaderboard[0].username)}</div>
                      <div className="podium-wager">${weeklyLeaderboard[0].wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                      <div className="podium-prize">${getWeeklyPrizeForRank(1).toLocaleString()}</div>
                    </div>

                    {/* 3rd Place */}
                    <div className="podium-card third">
                      <div className="podium-avatar">
                        {weeklyLeaderboard[2].profile_image_url ? (
                          <img src={weeklyLeaderboard[2].profile_image_url} alt={weeklyLeaderboard[2].username} className="avatar-image" />
                        ) : (
                          <div className="avatar-circle">{getInitials(weeklyLeaderboard[2].username)}</div>
                        )}
                        <div className="rank-badge">3</div>
                      </div>
                      <div className="podium-username">{censorUsername(weeklyLeaderboard[2].username)}</div>
                      <div className="podium-wager">${weeklyLeaderboard[2].wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                      <div className="podium-prize">${getWeeklyPrizeForRank(3).toLocaleString()}</div>
                    </div>
                  </div>
                ) : weeklyLeaderboard.length === 0 ? (
                  <div style={{textAlign:'center', padding:'3rem', color:'#71717a'}}>
                    <div style={{fontSize:'2rem', marginBottom:'0.5rem'}}>📊</div>
                    <div>Weekly data loading...</div>
                  </div>
                ) : null}

                {weeklyLeaderboard.length > 3 && (
                  <div className="leaderboard-table-container">
                    <div className="table-header">
                      <div>#</div>
                      <div>User</div>
                      <div>Wagered</div>
                      <div>Prize</div>
                    </div>
                    {weeklyLeaderboard.slice(3, 20).map((player) => {
                      const isOutOfPrizes = player.rank > 10;
                      const fadeLevel = isOutOfPrizes ? 0.7 - ((player.rank - 11) * 0.12) : 1;
                      return (
                      <div key={player.rank} className={`table-row ${isOutOfPrizes ? 'no-prize' : ''}`} style={isOutOfPrizes ? { opacity: fadeLevel } : {}}>
                        <div>
                          <span className="table-rank">{player.rank}th</span>
                        </div>
                        <div className="table-user">
                          {player.profile_image_url ? (
                            <img src={player.profile_image_url} alt={player.username} className="table-avatar-image" />
                          ) : (
                            <div className="table-avatar">{getInitials(player.username)}</div>
                          )}
                          <span className="table-username">{censorUsername(player.username)}</span>
                        </div>
                        <div className="table-wager">${player.wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                        <div className="table-prize">
                          {isOutOfPrizes ? (
                            <span className="no-prize-dash">—</span>
                          ) : (
                            `$${getWeeklyPrizeForRank(player.rank).toLocaleString()}`
                          )}
                        </div>
                      </div>
                      );
                    })}
                    {weeklyLeaderboard.length > 10 && (
                      <div className="leaderboard-cutoff-note">
                        Top 10 receive prizes • Keep wagering to climb the ranks!
                      </div>
                    )}
                  </div>
                )}

                {/* Weekly API Status */}
                <div className="api-status">
                  <span className="status-dot success" />
                  <span>Weekly data</span>
                  {weeklyInfo && <span>• {weeklyInfo.label}</span>}
                </div>
              </div>
            )}

            {/* Previous Monthly Winners Section */}
            {previousLeaderboardWinners && (
            <div className="previous-winners-section">
              <button 
                className="previous-winners-toggle"
                onClick={() => setShowPreviousWinners(!showPreviousWinners)}
              >
                <span>🏆 Previous Monthly Winners ({(() => {
                  // Format "2026-03-23 to 2026-04-23" → "Mar 24 – Apr 24, 2026"
                  const parts = previousLeaderboardWinners.periodLabel.split(' to ');
                  if (parts.length === 2) {
                    const fmt = (d) => new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                    const year = parts[1].slice(0, 4);
                    return `${fmt(parts[0])} – ${fmt(parts[1])}, ${year}`;
                  }
                  return previousLeaderboardWinners.periodLabel;
                })()})</span>
                <span className="toggle-arrow">{showPreviousWinners ? '▲' : '▼'}</span>
              </button>
              
              {showPreviousWinners && (
                <div className="previous-winners-content">
                  <div className="previous-winners-period">{(() => {
                    const parts = previousLeaderboardWinners.periodLabel.split(' to ');
                    if (parts.length === 2) {
                      const fmt = (d) => new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                      const year = parts[1].slice(0, 4);
                      return `${fmt(parts[0])} – ${fmt(parts[1])}, ${year}`;
                    }
                    return previousLeaderboardWinners.periodLabel;
                  })()} • {previousLeaderboardWinners.totalParticipants} participants</div>
                  <div className="previous-winners-list">
                    {previousLeaderboardWinners.winners.map((winner) => (
                      <div key={winner.rank} className={`previous-winner-row ${winner.rank <= 3 ? `top-${winner.rank}` : ''}`} style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                        <span className="previous-winner-rank" style={{minWidth:'36px'}}>
                          {winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : winner.rank === 3 ? '🥉' : `#${winner.rank}`}
                        </span>
                        <span className="previous-winner-name" style={{flex:1, minWidth:0}}>{censorUsername(winner.username)}</span>
                        <span style={{fontSize:'0.75rem', color:'#71717a', fontFamily:'monospace', minWidth:'75px', textAlign:'right'}}>
                          ${winner.wager >= 1000000 ? (winner.wager / 1000000).toFixed(2) + 'M' : winner.wager >= 1000 ? Math.floor(winner.wager / 1000).toLocaleString() + 'K' : Math.floor(winner.wager).toLocaleString()}
                        </span>
                        <span className="previous-winner-reward" style={{minWidth:'65px', textAlign:'right', fontWeight:800}}>
                          ${(winner.prize || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="previous-winners-total">
                    Total Paid: <strong>${previousLeaderboardWinners.winners.reduce((sum, w) => sum + (w.prize || 0), 0).toLocaleString()}</strong>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Previous Weekly Winners Section — gated on the same weekly feature flag as
                the weekly leaderboard itself (weekly is retired for now) */}
            {featureFlags.weekly && previousWeeklyWinners && (
            <div className="previous-winners-section">
              <button 
                className="previous-winners-toggle"
                onClick={() => setShowPreviousWeeklyWinners(!showPreviousWeeklyWinners)}
              >
                <span>📅 Previous Weekly Winners ({previousWeeklyWinners.periodLabel})</span>
                <span className="toggle-arrow">{showPreviousWeeklyWinners ? '▲' : '▼'}</span>
              </button>
              
              {showPreviousWeeklyWinners && (
                <div className="previous-winners-content">
                  <div className="previous-winners-period">{previousWeeklyWinners.periodLabel} • {previousWeeklyWinners.totalParticipants} participants</div>
                  <div className="previous-winners-list">
                    {previousWeeklyWinners.winners.map((winner) => (
                      <div key={winner.rank} className={`previous-winner-row ${winner.rank <= 3 ? `top-${winner.rank}` : ''}`} style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                        <span className="previous-winner-rank" style={{minWidth:'36px'}}>
                          {winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : winner.rank === 3 ? '🥉' : `#${winner.rank}`}
                        </span>
                        <span className="previous-winner-name" style={{flex:1, minWidth:0}}>{censorUsername(winner.username)}</span>
                        <span style={{fontSize:'0.75rem', color:'#71717a', fontFamily:'monospace', minWidth:'75px', textAlign:'right'}}>
                          ${winner.wager >= 1000000 ? (winner.wager / 1000000).toFixed(2) + 'M' : winner.wager >= 1000 ? Math.floor(winner.wager / 1000).toLocaleString() + 'K' : Math.floor(winner.wager).toLocaleString()}
                        </span>
                        <span className="previous-winner-reward" style={{minWidth:'55px', textAlign:'right', fontWeight:800}}>
                          ${(winner.prize || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="previous-winners-total">
                    Total Paid: <strong>${previousWeeklyWinners.winners.reduce((sum, w) => sum + (w.prize || 0), 0).toLocaleString()}</strong>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Social Links */}
            <div className="social-section">
              <div className="social-title">Follow Snutzy</div>
              <div className="social-grid">
                {socialLinks.map((link) => (
                  <a 
                    key={link.name} 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className={`social-link ${link.name.toLowerCase()}`}
                  >
                    <span className="social-link-icon" dangerouslySetInnerHTML={{ __html: link.icon }} />
                    {link.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* The Ban List — mugshot wall of banned slots */}
        {activePage === 'banlist' && (
          <BanListPage apiUrl={BACKEND_API_URL} getToken={getAuthToken} isAdmin={isAdmin} slotCatalog={allSlots} />
        )}

        {/* Calculators — Shuffle bonus estimators */}
        {activePage === 'calculators' && <CalculatorsPage />}

        {/* Slot Battles — chat-entered bonus buy brackets */}
        {activePage === 'battles' && (
          <SlotBattlesPage apiUrl={BACKEND_API_URL} getToken={getAuthToken} isAdmin={isAdmin} slotCatalog={allSlots} />
        )}

        {/* Recruit & Earn referral dashboard */}
        {activePage === 'referrals' && (
          featureFlags.referrals ? (
            <ReferralsPage apiUrl={BACKEND_API_URL} getToken={getAuthToken} user={user} loginWithTwitch={loginWithTwitch} />
          ) : (
            <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
              <h1 className="page-title">Recruit &amp; Earn</h1>
              <p className="page-subtitle">Coming soon — get paid for referring your friends to the guild.</p>
            </div>
          )
        )}

        {/* 10% Lossback — rules + claim flow (also reachable from the FAB) */}
        {activePage === 'lossback' && (
          <LossbackPage apiUrl={BACKEND_API_URL} getToken={getAuthToken} user={user} loginWithTwitch={loginWithTwitch} enabled={featureFlags.lossback} />
        )}

        {/* Clip & Earn — the flag already gates this; a stale bookmark lands on
            the notice below rather than a submission form for a parked feature. */}
        {activePage === 'clipearn' && (
          featureFlags.clips ? (
            <ClipEarnPage apiUrl={BACKEND_API_URL} getToken={getAuthToken} user={user} loginWithTwitch={loginWithTwitch} />
          ) : (
            <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
              <h1 className="page-title">Clip &amp; Earn</h1>
              <p className="page-subtitle">This one's paused for now — keep an eye on the Discord for when it's back.</p>
            </div>
          )
        )}

        {activePage === 'highlights' && (
          <div className="highlights-container">
            <h1 className="simple-page-title">Latest Highlights</h1>
            <p className="highlights-intro">
              The biggest wins and best moments, straight from{' '}
              <a href="https://x.com/Snutzygg" target="_blank" rel="noopener noreferrer">@Snutzygg on X</a>.
            </p>

            {/* Admin-curated posts stay on top — hand-picked, not auto-synced */}
            {featuredPosts.filter(p => !p.is_giveaway).length > 0 && (
              <div style={{ margin: '0 0 3rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '1.6rem' }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase', color: '#c084fc', marginBottom: '0.6rem' }}>[ FEATURED ]</div>
                  <h2 style={{ fontFamily: "'Unbounded', 'Outfit', sans-serif", fontSize: '1.5rem', fontWeight: 800, color: '#fff', margin: 0 }}>Pinned Posts</h2>
                </div>
                <div className="lp-posts-grid">
                  {featuredPosts.filter(p => !p.is_giveaway).map(post => (
                    <SocialEmbed key={post.id} post={post} />
                  ))}
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center', marginBottom: '1.6rem' }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase', color: '#c084fc', marginBottom: '0.6rem' }}>[ LATEST CLIPS ]</div>
              <h2 style={{ fontFamily: "'Unbounded', 'Outfit', sans-serif", fontSize: '1.5rem', fontWeight: 800, color: '#fff', margin: 0 }}>Fresh from X</h2>
            </div>

            {xHighlights.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#71717a', fontSize: '0.88rem', margin: '2rem 0' }}>
                No clips synced yet — they'll appear here automatically once posted on X.
              </p>
            ) : (
              <div className="lp-posts-grid">
                {xHighlights.map(post => (
                  <div key={post.id} style={{ position: 'relative' }}>
                    <SocialEmbed post={{ platform: 'twitter', url: post.url, title: post.text || 'Snutzy clip' }} />
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.4rem', justifyContent: 'center' }}>
                        <button onClick={() => updateHighlight(post.id, { pinned: !post.pinned })} style={{
                          padding: '0.25rem 0.6rem', borderRadius: '7px', fontSize: '0.66rem', fontWeight: 700, cursor: 'pointer',
                          background: post.pinned ? 'rgba(251,191,36,0.16)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${post.pinned ? 'rgba(251,191,36,0.4)' : 'rgba(255,255,255,0.12)'}`,
                          color: post.pinned ? '#fbbf24' : '#a1a1aa'
                        }}>{post.pinned ? '📌 Pinned' : '📌 Pin'}</button>
                        <button onClick={() => updateHighlight(post.id, { hidden: true })} style={{
                          padding: '0.25rem 0.6rem', borderRadius: '7px', fontSize: '0.66rem', fontWeight: 700, cursor: 'pointer',
                          background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171'
                        }}>🙈 Hide</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="highlights-cta">
              <a href="https://x.com/Snutzygg" target="_blank" rel="noopener noreferrer" className="highlights-cta-btn" style={{ background: '#000' }}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                Follow for More
              </a>
            </div>
          </div>
        )}

        {/* Public Slot Picks Queue Page */}
        {activePage === 'slotpicks' && (
          <div className="slotpicks-page">
            <h1 className="simple-page-title">📣 Slot Calls Queue</h1>
            {publicPayoutStats?.slotPicks > 0 && (
              <div style={{display:'flex', justifyContent:'center', margin:'0.5rem 0 1.5rem'}}>
                <OdometerCounter value={publicPayoutStats.slotPicks} label="Total Slot Calls Paid" size="small" />
              </div>
            )}
            <p className="slotpicks-intro">
              Viewers can redeem channel points to pick a slot for Snutzy to spin! Here's the current queue.
            </p>

            {/* Stats Cards */}
            <div className="slotpicks-stats-row">
              <div className="slotpicks-stat-card pending">
                <div className="stat-big-number">{publicSlotPicksStats.pending || 0}</div>
                <div className="stat-label">In Queue</div>
              </div>
              <div className="slotpicks-stat-card completed">
                <div className="stat-big-number">{publicSlotPicksStats.completed || 0}</div>
                <div className="stat-label">Waiting for Reward</div>
              </div>
              <div className="slotpicks-stat-card paid">
                <div className="stat-big-number">{publicSlotPicksStats.allTimePaid || 0}</div>
                <div className="stat-label">Total Paid Out</div>
                <div className="stat-subtext">${(publicSlotPicksStats.allTimePaidAmount || 0).toLocaleString()}</div>
              </div>
            </div>

            {/* Progress Visualization */}
            {slotPicksSessionMax > 0 && (
              <div className="slotpicks-progress-container">
                <div className="slotpicks-progress-header">
                  <span className="progress-title">Queue Progress</span>
                  <span className="progress-count">
                    {slotPicksSessionMax - publicSlotPicksStats.pending} / {slotPicksSessionMax} completed
                  </span>
                </div>
                <div className="slotpicks-progress-bar">
                  <div 
                    className="slotpicks-progress-fill"
                    style={{ 
                      width: `${Math.round(((slotPicksSessionMax - publicSlotPicksStats.pending) / slotPicksSessionMax) * 100)}%` 
                    }}
                  />
                </div>
                <div className="slotpicks-progress-labels">
                  <span>{publicSlotPicksStats.pending} remaining</span>
                  <span>{slotPicksSessionMax - publicSlotPicksStats.pending} done</span>
                </div>
              </div>
            )}

            {/* Queue List */}
            <div className="slotpicks-queue-section">
              <div className="slotpicks-queue-header">
                <h2>📋 Current Queue</h2>
                <span className="queue-live-indicator">
                  <span className="live-dot"></span>
                  Live Updates
                </span>
              </div>

              {publicSlotPicks.length > 0 ? (
                <div className="slotpicks-queue-list">
                  {publicSlotPicks.map((pick, idx) => (
                    <div key={pick.id} className="slotpicks-queue-item">
                      <div className="queue-position">#{idx + 1}</div>
                      <div className="queue-user">
                        {pick.profileImage ? (
                          <img src={pick.profileImage} alt="" className="queue-avatar" />
                        ) : (
                          <div className="queue-avatar-placeholder">
                            {(pick.displayName || '?').substring(0, 2).toUpperCase()}
                          </div>
                        )}
                        <span className="queue-username">{pick.displayName}</span>
                      </div>
                      <div className="queue-slot">
                        <span className="slot-icon">🎰</span>
                        <span className="slot-name">{pick.slotName}</span>
                      </div>
                      <div className="queue-time">
                        {formatTimeAgo(new Date(pick.createdAt))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="slotpicks-empty">
                  <div className="empty-icon">🎰</div>
                  <p>No picks in queue right now!</p>
                  <p className="empty-hint">Redeem "Pick a Slot" with channel points on Twitch to add yours.</p>
                </div>
              )}
            </div>

            {/* How it works */}
            <div className="slotpicks-how-it-works">
              <h3>How It Works</h3>
              <div className="how-steps">
                <div className="how-step">
                  <span className="step-number">1</span>
                  <span className="step-text">Redeem "Pick a Slot" channel points on Twitch</span>
                </div>
                <div className="how-step">
                  <span className="step-number">2</span>
                  <span className="step-text">Enter the slot name you want Snutzy to spin</span>
                </div>
                <div className="how-step">
                  <span className="step-number">3</span>
                  <span className="step-text">Watch your pick get spun on stream!</span>
                </div>
                <div className="how-step">
                  <span className="step-number">4</span>
                  <span className="step-text">If it hits a bonus, you get a reward! 💰</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tournament Page */}
        {activePage === 'tournament' && (
          <div className="page-content tournament-page">

            {/* Admin: Tournament Management Panel */}
            {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && (
              <div style={{marginBottom: '1.5rem'}}>
                <button 
                  onClick={() => { setShowTournamentAdmin(!showTournamentAdmin); if (!showTournamentAdmin) fetchAdminTournaments(); }}
                  style={{
                    background: showTournamentAdmin ? 'rgba(239,68,68,0.15)' : 'rgba(168,85,247,0.15)',
                    border: '1px solid ' + (showTournamentAdmin ? 'rgba(239,68,68,0.3)' : 'rgba(168,85,247,0.3)'),
                    color: showTournamentAdmin ? '#f87171' : '#c084fc',
                    padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                  }}
                >
                  {showTournamentAdmin ? '✕ Close Admin Panel' : '⚙️ Tournament Admin'}
                </button>

                {showTournamentAdmin && (
                  <div style={{
                    background: 'rgba(15,15,25,0.95)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '0.75rem',
                    padding: '1.5rem', marginTop: '0.75rem'
                  }}>
                    <h3 style={{color: '#c084fc', margin: '0 0 1rem 0', fontSize: '1.1rem'}}>⚙️ Tournament Management</h3>

                    {tournamentAdminMsg && (
                      <div style={{
                        padding: '0.5rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.85rem',
                        background: tournamentAdminMsg.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                        color: tournamentAdminMsg.type === 'success' ? '#22c55e' : '#f87171',
                        border: '1px solid ' + (tournamentAdminMsg.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)')
                      }}>
                        {tournamentAdminMsg.text}
                        <button onClick={() => setTournamentAdminMsg(null)} style={{float:'right', background:'none', border:'none', color:'inherit', cursor:'pointer'}}>✕</button>
                      </div>
                    )}

                    {/* Create New Tournament */}
                    <div style={{
                      background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.15)', borderRadius: '0.5rem',
                      padding: '1rem', marginBottom: '1.25rem'
                    }}>
                      <h4 style={{color: '#e2e8f0', margin: '0 0 0.75rem 0', fontSize: '0.95rem'}}>➕ Create New Tournament</h4>
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem'}}>
                        <div style={{gridColumn: '1 / -1'}}>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>Title *</label>
                          <input 
                            value={tournamentForm.title} onChange={e => setTournamentForm({...tournamentForm, title: e.target.value})}
                            placeholder="e.g. March Bracket Tournament"
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          />
                        </div>
                        <div>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>Date (leave blank for TBA)</label>
                          <input 
                            type="date" value={tournamentForm.scheduledDate} onChange={e => setTournamentForm({...tournamentForm, scheduledDate: e.target.value})}
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          />
                        </div>
                        <div>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>Prize Pool ($)</label>
                          <input 
                            type="number" value={tournamentForm.prizePool} onChange={e => setTournamentForm({...tournamentForm, prizePool: e.target.value})}
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          />
                        </div>
                        <div>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>1st Prize ($)</label>
                          <input 
                            type="number" value={tournamentForm.firstPrize} onChange={e => setTournamentForm({...tournamentForm, firstPrize: e.target.value})}
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          />
                        </div>
                        <div>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>2nd Prize ($)</label>
                          <input 
                            type="number" value={tournamentForm.secondPrize} onChange={e => setTournamentForm({...tournamentForm, secondPrize: e.target.value})}
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          />
                        </div>
                        <div>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>3rd Prize ($)</label>
                          <input 
                            type="number" value={tournamentForm.thirdPrize} onChange={e => setTournamentForm({...tournamentForm, thirdPrize: e.target.value})}
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          />
                        </div>
                        <div>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>Bonus Buy ($)</label>
                          <input 
                            type="number" value={tournamentForm.bonusBuyAmount} onChange={e => setTournamentForm({...tournamentForm, bonusBuyAmount: e.target.value})}
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          />
                        </div>
                        <div>
                          <label style={{fontSize: '0.75rem', color: '#9ca3af', display: 'block', marginBottom: '0.25rem'}}>Bracket Size</label>
                          <select 
                            value={tournamentForm.bracketSize} onChange={e => setTournamentForm({...tournamentForm, bracketSize: e.target.value})}
                            style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.85rem', boxSizing:'border-box'}}
                          >
                            <option value="8">8 players</option>
                            <option value="16">16 players</option>
                            <option value="32">32 players</option>
                            <option value="64">64 players</option>
                          </select>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <input 
                            type="checkbox" checked={tournamentForm.setActive} onChange={e => setTournamentForm({...tournamentForm, setActive: e.target.checked})}
                            id="set-active-check"
                          />
                          <label htmlFor="set-active-check" style={{fontSize: '0.8rem', color: '#9ca3af', cursor: 'pointer'}}>Set as active tournament</label>
                        </div>
                      </div>
                      <button 
                        onClick={createTournament}
                        disabled={!tournamentForm.title}
                        style={{
                          marginTop: '0.75rem', padding: '0.5rem 1.25rem', background: 'rgba(168,85,247,0.3)', border: '1px solid rgba(168,85,247,0.5)',
                          borderRadius: '0.375rem', color: '#c084fc', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                          opacity: (!tournamentForm.title) ? 0.5 : 1
                        }}
                      >
                        🏆 Create Tournament
                      </button>
                    </div>

                    {/* Tournament List */}
                    <h4 style={{color: '#e2e8f0', margin: '0 0 0.75rem 0', fontSize: '0.95rem'}}>📋 All Tournaments ({adminTournaments.length})</h4>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                      {adminTournaments.map(t => (
                        <div key={t.id} style={{
                          background: t.is_active ? 'rgba(168,85,247,0.12)' : 'rgba(255,255,255,0.03)',
                          border: '1px solid ' + (t.is_active ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.08)'),
                          borderRadius: '0.5rem', padding: '0.75rem'
                        }}>
                          {editingTournament === t.id ? (
                            /* Edit Mode */
                            <div>
                              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem'}}>
                                <div style={{gridColumn: '1 / -1'}}>
                                  <input 
                                    defaultValue={t.title} id={`edit-title-${t.id}`}
                                    style={{width:'100%', padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem', boxSizing:'border-box'}}
                                  />
                                </div>
                                <input type="date" defaultValue={t.scheduled_date?.split('T')[0]} id={`edit-date-${t.id}`}
                                  style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}}
                                />
                                <input type="number" defaultValue={t.prize_pool} id={`edit-pool-${t.id}`} placeholder="Prize Pool"
                                  style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}}
                                />
                                <input type="number" defaultValue={t.first_prize} id={`edit-1st-${t.id}`} placeholder="1st"
                                  style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}}
                                />
                                <input type="number" defaultValue={t.second_prize} id={`edit-2nd-${t.id}`} placeholder="2nd"
                                  style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}}
                                />
                                <input type="number" defaultValue={t.third_prize} id={`edit-3rd-${t.id}`} placeholder="3rd"
                                  style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}}
                                />
                                <input type="number" defaultValue={t.bonus_buy_amount} id={`edit-bb-${t.id}`} placeholder="Bonus Buy"
                                  style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}}
                                />
                              </div>
                              <div style={{display: 'flex', gap: '0.5rem'}}>
                                <button onClick={() => {
                                  updateTournament(t.id, {
                                    title: document.getElementById(`edit-title-${t.id}`).value,
                                    scheduledDate: document.getElementById(`edit-date-${t.id}`).value,
                                    prizePool: document.getElementById(`edit-pool-${t.id}`).value,
                                    firstPrize: document.getElementById(`edit-1st-${t.id}`).value,
                                    secondPrize: document.getElementById(`edit-2nd-${t.id}`).value,
                                    thirdPrize: document.getElementById(`edit-3rd-${t.id}`).value,
                                    bonusBuyAmount: document.getElementById(`edit-bb-${t.id}`).value
                                  });
                                }} style={{padding:'0.35rem 0.75rem', background:'rgba(34,197,94,0.2)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'0.25rem', color:'#22c55e', cursor:'pointer', fontSize:'0.8rem'}}>
                                  💾 Save
                                </button>
                                <button onClick={() => setEditingTournament(null)} style={{padding:'0.35rem 0.75rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'0.25rem', color:'#9ca3af', cursor:'pointer', fontSize:'0.8rem'}}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Display Mode */
                            <div>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem'}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                  <strong style={{color: '#e2e8f0', fontSize: '0.9rem'}}>{t.title}</strong>
                                  {t.is_active && <span style={{background: 'rgba(168,85,247,0.3)', color: '#c084fc', padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600}}>ACTIVE</span>}
                                  <span style={{
                                    background: t.status === 'open' ? 'rgba(34,197,94,0.2)' : t.status === 'live' ? 'rgba(239,68,68,0.2)' : t.status === 'closed' ? 'rgba(234,179,8,0.2)' : 'rgba(107,114,128,0.2)',
                                    color: t.status === 'open' ? '#22c55e' : t.status === 'live' ? '#f87171' : t.status === 'closed' ? '#eab308' : '#9ca3af',
                                    padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase'
                                  }}>{t.status}</span>
                                </div>
                                <span style={{color: '#6b7280', fontSize: '0.75rem'}}>{t.scheduled_date ? t.scheduled_date.split('T')[0] : 'TBA'}</span>
                              </div>
                              <div style={{color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.5rem'}}>
                                💰 ${t.prize_pool} pool · 🥇 ${t.first_prize} / 🥈 ${t.second_prize} / 🥉 ${t.third_prize} · 🎰 ${t.bonus_buy_amount} BB · 👥 {t.bracket_size || 32} bracket · 🎟️ {t.entry_count} entries · {t.bracket_count} in bracket
                              </div>
                              <div style={{display: 'flex', gap: '0.35rem', flexWrap: 'wrap'}}>
                                {!t.is_active && t.status !== 'completed' && (
                                  <button onClick={() => activateTournament(t.id)} style={{padding:'0.25rem 0.6rem', background:'rgba(168,85,247,0.2)', border:'1px solid rgba(168,85,247,0.4)', borderRadius:'0.25rem', color:'#c084fc', cursor:'pointer', fontSize:'0.75rem'}}>
                                    ⭐ Set Active
                                  </button>
                                )}
                                <button onClick={() => setEditingTournament(t.id)} style={{padding:'0.25rem 0.6rem', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'0.25rem', color:'#60a5fa', cursor:'pointer', fontSize:'0.75rem'}}>
                                  ✏️ Edit
                                </button>
                                {t.status === 'open' && (
                                  <button onClick={() => updateTournamentStatus(t.id, 'closed')} style={{padding:'0.25rem 0.6rem', background:'rgba(234,179,8,0.15)', border:'1px solid rgba(234,179,8,0.3)', borderRadius:'0.25rem', color:'#eab308', cursor:'pointer', fontSize:'0.75rem'}}>
                                    🔒 Close Raffle
                                  </button>
                                )}
                                {t.status === 'closed' && (
                                  <>
                                    <button onClick={() => updateTournamentStatus(t.id, 'open')} style={{padding:'0.25rem 0.6rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'0.25rem', color:'#22c55e', cursor:'pointer', fontSize:'0.75rem'}}>
                                      🔓 Reopen Raffle
                                    </button>
                                    <button onClick={() => updateTournamentStatus(t.id, 'live')} style={{padding:'0.25rem 0.6rem', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'0.25rem', color:'#f87171', cursor:'pointer', fontSize:'0.75rem'}}>
                                      🔴 Go Live
                                    </button>
                                  </>
                                )}
                                {t.status === 'live' && (
                                  <button onClick={() => updateTournamentStatus(t.id, 'closed')} style={{padding:'0.25rem 0.6rem', background:'rgba(234,179,8,0.15)', border:'1px solid rgba(234,179,8,0.3)', borderRadius:'0.25rem', color:'#eab308', cursor:'pointer', fontSize:'0.75rem'}}>
                                    ⏸ Pause
                                  </button>
                                )}
                                {t.status === 'completed' && (
                                  <button onClick={() => updateTournamentStatus(t.id, 'open')} style={{padding:'0.25rem 0.6rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'0.25rem', color:'#22c55e', cursor:'pointer', fontSize:'0.75rem'}}>
                                    🔄 Reopen
                                  </button>
                                )}
                                {!t.is_active && (
                                  <button onClick={() => deleteTournament(t.id, t.title)} style={{padding:'0.25rem 0.6rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'0.25rem', color:'#f87171', cursor:'pointer', fontSize:'0.75rem'}}>
                                    🗑️ Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {adminTournaments.length === 0 && (
                        <div style={{color: '#6b7280', textAlign: 'center', padding: '1rem', fontSize: '0.85rem'}}>No tournaments yet. Create one above!</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Viewing Past Tournament Bracket */}
            {viewingPastTournament && pastTournamentBracket ? (
              <div className="past-tournament-view">
                <button 
                  className="back-to-tournaments-btn"
                  onClick={() => { setViewingPastTournament(null); setPastTournamentBracket(null); }}
                >
                  ← Back to Tournaments
                </button>
                <div className="tournament-header">
                  <h1>🏆 {pastTournamentBracket.tournament?.title || 'Past Tournament'}</h1>
                  <p className="tournament-subtitle">Completed Tournament Results</p>
                </div>
                
                {/* Past Tournament Podium */}
                {(() => {
                  // Derive winners from the bracket matches
                  const finalsMatch = pastTournamentBracket.matches?.find(m => m.round === 5 && m.match_number === 1);
                  const semiFinals = pastTournamentBracket.matches?.filter(m => m.round === 4) || [];
                  
                  const winnerId = finalsMatch?.winner_id;
                  const secondPlaceId = finalsMatch ? (finalsMatch.player1_id === winnerId ? finalsMatch.player2_id : finalsMatch.player1_id) : null;
                  
                  const semiFinalsLosers = semiFinals.map(m => {
                    if (m.winner_id === m.player1_id) return m.player2_id;
                    return m.player1_id;
                  }).filter(Boolean);
                  const thirdPlaceId = semiFinalsLosers[0];
                  
                  const winner = pastTournamentBracket.players?.find(p => p.id === winnerId);
                  const second = pastTournamentBracket.players?.find(p => p.id === secondPlaceId);
                  const third = pastTournamentBracket.players?.find(p => p.id === thirdPlaceId);
                  
                  return (
                    <div className="past-tournament-podium">
                      {/* 2nd Place */}
                      <div className="podium-spot second">
                        <div className="podium-avatar">
                          {second?.profile_image_url ? (
                            <img src={second.profile_image_url} alt="" />
                          ) : (
                            <div className="avatar-placeholder">{second?.display_name?.charAt(0) || '?'}</div>
                          )}
                        </div>
                        <div className="podium-name">{second?.display_name || 'TBD'}</div>
                        <div className="podium-prize">🥈 ${pastTournamentBracket.tournament?.second_prize || 300}</div>
                        <div className="podium-block second-block">2nd</div>
                      </div>
                      
                      {/* 1st Place */}
                      <div className="podium-spot first">
                        <div className="podium-crown">👑</div>
                        <div className="podium-avatar">
                          {winner?.profile_image_url ? (
                            <img src={winner.profile_image_url} alt="" />
                          ) : (
                            <div className="avatar-placeholder">{winner?.display_name?.charAt(0) || '?'}</div>
                          )}
                        </div>
                        <div className="podium-name">{winner?.display_name || 'TBD'}</div>
                        <div className="podium-prize">🥇 ${pastTournamentBracket.tournament?.first_prize || 500}</div>
                        <div className="podium-block first-block">1st</div>
                      </div>
                      
                      {/* 3rd Place */}
                      <div className="podium-spot third">
                        <div className="podium-avatar">
                          {third?.profile_image_url ? (
                            <img src={third.profile_image_url} alt="" />
                          ) : (
                            <div className="avatar-placeholder">{third?.display_name?.charAt(0) || '?'}</div>
                          )}
                        </div>
                        <div className="podium-name">{third?.display_name || 'TBD'}</div>
                        <div className="podium-prize">🥉 ${pastTournamentBracket.tournament?.third_prize || 200}</div>
                        <div className="podium-block third-block">3rd</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Past Tournament Bracket Display - reuse bracket display logic */}
                <div className="tournament-bracket-section">
                  <h2>📊 Full Bracket</h2>
                  <div className="bracket-container">
                    <div className="bracket-rounds">
                      {/* Round of 32 */}
                      <div className="bracket-round round-32">
                        <div className="round-header">Round of 32</div>
                        <div className="round-matches">
                          {Array.from({ length: 16 }, (_, i) => {
                            const match = pastTournamentBracket.matches.find(m => m.round === 1 && m.match_number === i + 1);
                            const player1 = pastTournamentBracket.players[i * 2];
                            const player2 = pastTournamentBracket.players[i * 2 + 1];
                            return (
                              <div key={i} className={`bracket-match ${match?.winner_id ? 'completed' : ''}`}>
                                <div className={`match-slot ${player1 ? '' : 'empty'} ${match?.winner_id === player1?.id ? 'winner' : match?.winner_id && match?.winner_id !== player1?.id ? 'loser' : ''}`}>
                                  <span className="seed">#{i * 2 + 1}</span>
                                  {player1 ? <span className="name">{player1.display_name}</span> : <span className="tbd">TBD</span>}
                                  {match?.player1_multiplier && <span className="multi">{match.player1_multiplier}x</span>}
                                </div>
                                <div className={`match-slot ${player2 ? '' : 'empty'} ${match?.winner_id === player2?.id ? 'winner' : match?.winner_id && match?.winner_id !== player2?.id ? 'loser' : ''}`}>
                                  <span className="seed">#{i * 2 + 2}</span>
                                  {player2 ? <span className="name">{player2.display_name}</span> : <span className="tbd">TBD</span>}
                                  {match?.player2_multiplier && <span className="multi">{match.player2_multiplier}x</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Simplified later rounds for past brackets */}
                      {[2, 3, 4, 5].map(round => {
                        const roundNames = { 2: 'Round of 16', 3: 'Quarter Finals', 4: 'Semi Finals', 5: '🏆 Finals' };
                        const matchCounts = { 2: 8, 3: 4, 4: 2, 5: 1 };
                        return (
                          <div key={round} className={`bracket-round ${round === 5 ? 'finals' : ''}`}>
                            <div className="round-header">{roundNames[round]}</div>
                            <div className="round-matches">
                              {Array.from({ length: matchCounts[round] }, (_, i) => {
                                const match = pastTournamentBracket.matches.find(m => m.round === round && m.match_number === i + 1);
                                const player1 = match?.player1_id ? pastTournamentBracket.players.find(p => p.id === match.player1_id) : null;
                                const player2 = match?.player2_id ? pastTournamentBracket.players.find(p => p.id === match.player2_id) : null;
                                return (
                                  <div key={i} className={`bracket-match ${match?.winner_id ? 'completed' : ''} ${round === 5 ? 'finals-match' : ''}`}>
                                    <div className={`match-slot ${player1 ? '' : 'empty'} ${match?.winner_id === player1?.id ? (round === 5 ? 'champion' : 'winner') : ''}`}>
                                      {player1 ? <span className="name">{player1.display_name}</span> : <span className="tbd">TBD</span>}
                                      {match?.player1_multiplier && <span className="multi">{match.player1_multiplier}x</span>}
                                    </div>
                                    <div className={`match-slot ${player2 ? '' : 'empty'} ${match?.winner_id === player2?.id ? (round === 5 ? 'champion' : 'winner') : ''}`}>
                                      {player2 ? <span className="name">{player2.display_name}</span> : <span className="tbd">TBD</span>}
                                      {match?.player2_multiplier && <span className="multi">{match.player2_multiplier}x</span>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : tournamentData.tournament?.status === 'completed' ? (
              /* No Active Tournament - Show upcoming/history */
              <div className="no-tournament-section">
                <div className="tournament-header">
                  <h1>⚔️ Bracket Tournaments</h1>
                  <p className="tournament-subtitle">No upcoming tournament announced yet</p>
                </div>
                
                <div className="no-tournament-message">
                  <div className="no-tournament-icon">📅</div>
                  <h2>Stay Tuned!</h2>
                  <p>The next bracket tournament hasn't been announced yet. Follow us on Twitch and Discord to be the first to know!</p>
                  <div className="no-tournament-links">
                    <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="social-link twitch">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
                      twitch.tv/snutzy
                    </a>
                    <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" className="social-link discord">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z"/></svg>
                      discord.gg/snutzy
                    </a>
                  </div>
                </div>

                {/* Previous Tournaments Section */}
                {tournamentHistory.length > 0 && (
                  <div className="previous-tournaments-section">
                    <h2>🏆 Previous Tournaments</h2>
                    <div className="previous-tournaments-grid">
                      {tournamentHistory.map((t) => (
                        <div key={t.id} className="previous-tournament-card" onClick={() => fetchPastTournamentBracket(t.id)}>
                          <div className="pt-header">
                            <h3>{t.title}</h3>
                            <span className="pt-prize-pool">${t.prizePool} Prize Pool</span>
                          </div>
                          <div className="pt-podium-mini">
                            <div className="pt-winner">
                              <div className="pt-avatar">
                                {t.winner?.avatar ? (
                                  <img src={t.winner.avatar} alt="" />
                                ) : (
                                  <div className="avatar-placeholder">1</div>
                                )}
                              </div>
                              <span className="pt-medal">🥇</span>
                              <span className="pt-name">{t.winner?.name || 'N/A'}</span>
                            </div>
                            <div className="pt-runners">
                              <div className="pt-runner">
                                <span className="pt-medal">🥈</span>
                                <span className="pt-name">{t.second?.name || 'N/A'}</span>
                              </div>
                              <div className="pt-runner">
                                <span className="pt-medal">🥉</span>
                                <span className="pt-name">{t.third?.name || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                          <div className="pt-view-bracket">View Full Bracket →</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Active Tournament */
              <>
                <div className="tournament-header">
                  <h1>⚔️ {tournamentData.tournament?.name || 'Bracket Tournament'}</h1>
                  <p className="tournament-subtitle">{tournamentData.tournament?.bracketSize || 32} players. Single elimination. ${tournamentData.tournament?.prizePool || 1000} prize pool.</p>
                  {tournamentData.tournament?.status === 'live' && (
                    <div className="tournament-live-badge">
                      <span className="live-dot"></span>
                      LIVE NOW
                    </div>
                  )}
                </div>

            {/* Tournament Info Card */}
            <div className="tournament-info-card">
              <div className="tournament-date-banner">
                <span className="date-icon">📅</span>
                <span className="date-text">{tournamentData.tournament?.date ? new Date(tournamentData.tournament.date.toString().split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '📌 Date TBA'}</span>
                <span className="date-separator">•</span>
                <span className="stream-link">Live on <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer">twitch.tv/snutzy</a></span>
              </div>

              <div className="tournament-prizes">
                <div className="prize-item first">
                  <span className="prize-place">🥇 1st</span>
                  <span className="prize-amount">${tournamentData.tournament?.prizes?.first || 500}</span>
                </div>
                <div className="prize-item second">
                  <span className="prize-place">🥈 2nd</span>
                  <span className="prize-amount">${tournamentData.tournament?.prizes?.second || 300}</span>
                </div>
                <div className="prize-item third">
                  <span className="prize-place">🥉 3rd</span>
                  <span className="prize-amount">${tournamentData.tournament?.prizes?.third || 200}</span>
                </div>
              </div>
              <div style={{textAlign:'center', marginTop:'0.5rem', marginBottom:'0.25rem'}}>
                <span style={{display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.35rem 0.85rem', background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.25)', borderRadius:'8px', fontSize:'0.82rem', fontWeight:600, color:'#c084fc'}}>
                  💎 All selected participants receive <span style={{color:'#fbbf24'}}>3,000 Points</span>
                </span>
              </div>

              <div className="tournament-format">
                <h3>How It Works</h3>
                <div className="format-steps">
                  <div className="format-step">
                    <span className="step-icon">🎟️</span>
                    <div className="step-content">
                      <strong>Raffle Entry</strong>
                      <p>Enter the raffle below for a chance to be selected</p>
                    </div>
                  </div>
                  <div className="format-step">
                    <span className="step-icon">🎲</span>
                    <div className="step-content">
                      <strong>{tournamentData.tournament?.bracketSize || 32} Selected</strong>
                      <p>{tournamentData.tournament?.bracketSize || 32} winners will be randomly rolled into the bracket and receive 💎 3,000 Points</p>
                    </div>
                  </div>
                  <div className="format-step">
                    <span className="step-icon">🎰</span>
                    <div className="step-content">
                      <strong>Random Slot Roll</strong>
                      <p>Each player gets a random slot rolled using our Slot Picker</p>
                    </div>
                  </div>
                  <div className="format-step">
                    <span className="step-icon">💰</span>
                    <div className="step-content">
                      <strong>~${tournamentData.tournament?.bonusBuyAmount || 150} Bonus Buy</strong>
                      <p>We buy a bonus on your rolled slot (amount based on multiplier)</p>
                    </div>
                  </div>
                  <div className="format-step">
                    <span className="step-icon">⚔️</span>
                    <div className="step-content">
                      <strong>Head-to-Head</strong>
                      <p>Higher multiplier from each matchup advances to the next round</p>
                    </div>
                  </div>
                  <div className="format-step">
                    <span className="step-icon">🏆</span>
                    <div className="step-content">
                      <strong>Single Elimination</strong>
                      <p>{(() => {
                        const size = tournamentData.tournament?.bracketSize || 32;
                        const rounds = [];
                        let s = size;
                        while (s > 2) { rounds.push(s); s = Math.floor(s / 2); }
                        return rounds.join(' → ') + ' → Finals until we have our top 3!';
                      })()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Raffle Entry Section */}
            <div className="tournament-raffle-section">
              <h2>🎟️ Enter the Raffle</h2>
              
              <div className="raffle-requirement">
                <span className="requirement-icon">⚠️</span>
                <span className="requirement-text">
                  <strong style={{color: '#ef4444'}}>Requirement:</strong> You must be signed up under guild <strong>SNUTZY</strong> on Shuffle and have your Shuffle username linked in your Profile. This is automatically verified when you enter.
                </span>
              </div>

              <div className="raffle-entry-box">
                {!user ? (
                  <div className="raffle-login-prompt">
                    <p>Login with Twitch to enter the raffle</p>
                    <button className="twitch-login-btn" onClick={() => window.location.href = `${BACKEND_API_URL}/auth/twitch`}>
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                      </svg>
                      Login with Twitch
                    </button>
                  </div>
                ) : tournamentMyEntry ? (
                  <div className="raffle-entered">
                    <div className="entered-check">✅</div>
                    <div className="entered-info">
                      <p className="entered-title">You're in the raffle!</p>
                      <p className="entered-id">Entry ID: <strong>{tournamentMyEntry.entryId}</strong></p>
                      <p className="entered-time">Entered {formatTimeAgo(new Date(tournamentMyEntry.enteredAt))}</p>
                    </div>
                  </div>
                ) : (
                  <div className="raffle-enter">
                    <p>Click below to enter the tournament raffle</p>
                    <button 
                      className="enter-raffle-btn"
                      onClick={enterTournament}
                      disabled={tournamentEntering || !tournamentData.tournament?.isOpen}
                    >
                      {tournamentEntering ? 'Entering...' : '🎟️ Enter Raffle'}
                    </button>
                    {!tournamentData.tournament?.isOpen && (
                      <p className="raffle-closed-note">Raffle entries are currently closed</p>
                    )}
                  </div>
                )}
                
                {tournamentMessage && (
                  <div className={`tournament-message ${tournamentMessage.type}`}>
                    {tournamentMessage.text}
                    {tournamentMessage.action && (
                      <button 
                        className="tournament-message-action"
                        onClick={tournamentMessage.action.onClick}
                      >
                        {tournamentMessage.action.label}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="raffle-stats">
                <span className="raffle-stat">
                  <strong>{tournamentData.totalEntries}</strong> entries
                </span>
                {/* Admin: Clear all entries button */}
                {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && (
                  <button 
                    className="admin-clear-entries-btn"
                    onClick={async () => {
                      if (window.confirm(`Are you sure you want to clear ALL ${tournamentData.totalEntries} raffle entries? This cannot be undone!`)) {
                        try {
                          const token = localStorage.getItem('auth_token');
                          const response = await fetch(`${BACKEND_API_URL}/api/admin/tournament/entries-all`, {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            alert(`✅ Cleared ${data.deletedCount} entries!`);
                            fetchTournamentInfo();
                            setTournamentMyEntry(null);
                          } else {
                            alert('Failed to clear entries');
                          }
                        } catch (e) {
                          alert('Error clearing entries');
                        }
                      }
                    }}
                    style={{ marginLeft: '1rem', padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem' }}
                  >
                    🗑️ Clear All Entries (Admin)
                  </button>
                )}
              </div>
            </div>

            {/* Entries Display */}
            <div className="tournament-entries-section">
              <h2>🎫 Raffle Entries</h2>
              
              {tournamentData.entries.length > 0 ? (
                <>
                  <div className="entries-grid">
                    {tournamentData.entries.map((entry) => (
                      <div key={entry.entryId} className="entry-card" style={{position:'relative'}}>
                        {isAdmin && (
                          <button 
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Remove ${entry.displayName} (${entry.entryId}) from the tournament raffle?`)) return;
                              try {
                                const token = localStorage.getItem('auth_token');
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/tournament/entries/${entry.entryId}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  fetchTournamentInfo();
                                } else {
                                  const d = await res.json();
                                  alert(d.error || 'Failed to remove');
                                }
                              } catch(err) { alert('Error: ' + err.message); }
                            }}
                            style={{position:'absolute', top:'0.2rem', right:'0.2rem', width:'20px', height:'20px', borderRadius:'50%', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', color:'#f87171', fontSize:'0.6rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, lineHeight:1, zIndex:2}}
                            title={`Remove ${entry.displayName}`}
                          >✕</button>
                        )}
                        <div className="entry-avatar">
                          {entry.profileImageUrl ? (
                            <img src={entry.profileImageUrl} alt={entry.displayName} />
                          ) : (
                            <div className="avatar-placeholder">{entry.displayName?.charAt(0).toUpperCase()}</div>
                          )}
                        </div>
                        <div className="entry-info">
                          <span className="entry-name">{entry.displayName}</span>
                          <span className="entry-id">{entry.entryId}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {!isAdmin && tournamentData.totalEntries > 20 && (
                    <div className="entries-more">
                      ...and {tournamentData.totalEntries - 20} more entries!
                    </div>
                  )}
                </>
              ) : (
                <div className="entries-empty">
                  <p>No entries yet. Be the first to enter!</p>
                </div>
              )}
            </div>

            {/* Tournament Bracket Section */}
            <div className="tournament-bracket-section">
              <h2>⚔️ Tournament Bracket</h2>
              
              {/* Admin Bracket Controls */}
              {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && (
                <div className="bracket-admin-section">
                  <div className="bracket-admin-header">
                    <h3>🎮 Bracket Admin</h3>
                    <div className="bracket-admin-tabs">
                      <button 
                        className={bracketAdminTab === 'picker' ? 'active' : ''}
                        onClick={() => setBracketAdminTab('picker')}
                      >
                        🎲 Player Picker
                      </button>
                      <button 
                        className={bracketAdminTab === 'matches' ? 'active' : ''}
                        onClick={() => setBracketAdminTab('matches')}
                      >
                        ⚔️ Match Results
                      </button>
                    </div>
                  </div>
                  
                  {bracketAdminTab === 'picker' && (
                    <div className="bracket-picker-admin">
                      <div className="picker-status">
                        <span className="player-count">{bracketData.players.length}/32 Players Selected</span>
                        <div className="picker-progress-bar">
                          <div className="picker-progress-fill" style={{ width: `${(bracketData.players.length / 32) * 100}%` }}></div>
                        </div>
                      </div>
                      
                      <div className="picker-actions">
                        <button 
                          className="pick-player-btn"
                          onClick={pickRandomPlayer}
                          disabled={bracketPicking || bracketData.players.length >= 32}
                        >
                          {bracketPicking ? '🎰 Rolling...' : '🎲 Pick Random Player'}
                        </button>
                        <button 
                          className="undo-pick-btn"
                          onClick={undoLastPick}
                          disabled={bracketData.players.length === 0}
                        >
                          ↩️ Undo Last Pick
                        </button>
                        <button 
                          className="reset-bracket-btn"
                          onClick={resetBracket}
                        >
                          🗑️ Reset Bracket
                        </button>
                      </div>
                      
                      {/* Selected Players List */}
                      {bracketData.players.length > 0 && (
                        <div className="selected-players-list">
                          <h4>Selected Players ({bracketData.players.length})</h4>
                          <p className="selected-players-hint">Click 🔄 to replace a player with a new random pick (keeps same seed)</p>
                          <div className="selected-players-grid">
                            {bracketData.players.map((player, idx) => (
                              <div key={player.id} className="selected-player-card">
                                <span className="player-seed">#{idx + 1}</span>
                                <div className="player-avatar">
                                  {player.profile_image_url ? (
                                    <img src={player.profile_image_url} alt="" />
                                  ) : (
                                    <div className="avatar-placeholder">{player.display_name?.charAt(0)}</div>
                                  )}
                                </div>
                                <span className="player-name">{player.display_name}</span>
                                <button 
                                  className="replace-player-btn"
                                  onClick={() => replacePlayer(player.id, player.display_name)}
                                  title="Replace with new random player"
                                >
                                  🔄
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {bracketAdminTab === 'matches' && (
                    <div className="bracket-matches-admin">
                      <p className="matches-hint">Click on a match in the bracket below to set the result</p>
                      
                      {selectedMatch && (
                        <div className="match-result-editor">
                          <h4>Match #{selectedMatch.matchNumber} - Round {selectedMatch.round}</h4>
                          <div className="match-players">
                            <div className={`match-player ${matchResult.winnerId === selectedMatch.player1?.id ? 'selected-winner' : ''}`}>
                              <span className="player-name">{selectedMatch.player1?.display_name || 'TBD'}</span>
                              <input 
                                type="text"
                                placeholder="Slot name"
                                value={matchResult.player1Slot}
                                onChange={(e) => setMatchResult(prev => ({ ...prev, player1Slot: e.target.value }))}
                                className="slot-input"
                              />
                              <input 
                                type="number"
                                placeholder="Multiplier (e.g. 125.5x)"
                                value={matchResult.player1Multi}
                                onChange={(e) => setMatchResult(prev => ({ ...prev, player1Multi: e.target.value }))}
                                step="0.01"
                              />
                              {selectedMatch.player1 && (
                                <button 
                                  className={`winner-btn ${matchResult.winnerId === selectedMatch.player1.id ? 'active' : ''}`}
                                  onClick={() => setMatchResult(prev => ({ ...prev, winnerId: selectedMatch.player1.id }))}
                                >
                                  🏆 Winner
                                </button>
                              )}
                            </div>
                            <span className="vs">VS</span>
                            <div className={`match-player ${matchResult.winnerId === selectedMatch.player2?.id ? 'selected-winner' : ''}`}>
                              <span className="player-name">{selectedMatch.player2?.display_name || 'TBD'}</span>
                              <input 
                                type="text"
                                placeholder="Slot name"
                                value={matchResult.player2Slot}
                                onChange={(e) => setMatchResult(prev => ({ ...prev, player2Slot: e.target.value }))}
                                className="slot-input"
                              />
                              <input 
                                type="number"
                                placeholder="Multiplier (e.g. 85.2x)"
                                value={matchResult.player2Multi}
                                onChange={(e) => setMatchResult(prev => ({ ...prev, player2Multi: e.target.value }))}
                                step="0.01"
                              />
                              {selectedMatch.player2 && (
                                <button 
                                  className={`winner-btn ${matchResult.winnerId === selectedMatch.player2.id ? 'active' : ''}`}
                                  onClick={() => setMatchResult(prev => ({ ...prev, winnerId: selectedMatch.player2.id }))}
                                >
                                  🏆 Winner
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="match-actions">
                            <button className="save-match-btn" onClick={() => updateMatchResult(selectedMatch)}>
                              ✅ Save Result
                            </button>
                            <button className="cancel-match-btn" onClick={() => {
                              setSelectedMatch(null);
                              setMatchResult({ player1Slot: '', player1Multi: '', player2Slot: '', player2Multi: '', winnerId: null });
                            }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Bracket Display */}
              {bracketData.players.length > 0 ? (
                <div className="bracket-container">
                  <div className="bracket-rounds">
                    {/* Round of 32 */}
                    <div className="bracket-round round-32">
                      <div className="round-header">Round of 32</div>
                      <div className="round-matches">
                        {Array.from({ length: 16 }, (_, i) => {
                          const match = bracketData.matches.find(m => m.round === 1 && m.match_number === i + 1);
                          const player1 = bracketData.players[i * 2];
                          const player2 = bracketData.players[i * 2 + 1];
                          return (
                            <div 
                              key={i} 
                              className={`bracket-match ${match?.winner_id ? 'completed' : ''} ${selectedMatch?.id === match?.id ? 'editing' : ''}`}
                              onClick={() => {
                                if (user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && player1 && player2) {
                                  setSelectedMatch({ ...match, matchNumber: i + 1, round: 1, player1, player2 });
                                  setBracketAdminTab('matches');
                                }
                              }}
                            >
                              <div className={`match-slot ${player1 ? '' : 'empty'} ${match?.winner_id === player1?.id ? 'winner' : match?.winner_id && match?.winner_id !== player1?.id ? 'loser' : ''}`}>
                                <span className="seed">#{i * 2 + 1}</span>
                                {player1 ? (
                                  <>
                                    <span className="name">{player1.display_name}</span>
                                    {match?.player1_slot && <span className="slot-tag">{match.player1_slot}</span>}
                                    {match?.player1_multiplier && <span className="multi">{match.player1_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                              <div className={`match-slot ${player2 ? '' : 'empty'} ${match?.winner_id === player2?.id ? 'winner' : match?.winner_id && match?.winner_id !== player2?.id ? 'loser' : ''}`}>
                                <span className="seed">#{i * 2 + 2}</span>
                                {player2 ? (
                                  <>
                                    <span className="name">{player2.display_name}</span>
                                    {match?.player2_slot && <span className="slot-tag">{match.player2_slot}</span>}
                                    {match?.player2_multiplier && <span className="multi">{match.player2_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Round of 16 */}
                    <div className="bracket-round round-16">
                      <div className="round-header">Round of 16</div>
                      <div className="round-matches">
                        {Array.from({ length: 8 }, (_, i) => {
                          const match = bracketData.matches.find(m => m.round === 2 && m.match_number === i + 1);
                          const prevMatch1 = bracketData.matches.find(m => m.round === 1 && m.match_number === i * 2 + 1);
                          const prevMatch2 = bracketData.matches.find(m => m.round === 1 && m.match_number === i * 2 + 2);
                          const player1 = prevMatch1?.winner_id ? bracketData.players.find(p => p.id === prevMatch1.winner_id) : null;
                          const player2 = prevMatch2?.winner_id ? bracketData.players.find(p => p.id === prevMatch2.winner_id) : null;
                          return (
                            <div 
                              key={i} 
                              className={`bracket-match ${match?.winner_id ? 'completed' : ''}`}
                              onClick={() => {
                                if (user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && player1 && player2) {
                                  setSelectedMatch({ ...match, matchNumber: i + 1, round: 2, player1, player2 });
                                  setBracketAdminTab('matches');
                                }
                              }}
                            >
                              <div className={`match-slot ${player1 ? '' : 'empty'} ${match?.winner_id === player1?.id ? 'winner' : match?.winner_id && match?.winner_id !== player1?.id ? 'loser' : ''}`}>
                                {player1 ? (
                                  <>
                                    <span className="name">{player1.display_name}</span>
                                    {match?.player1_multiplier && <span className="multi">{match.player1_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                              <div className={`match-slot ${player2 ? '' : 'empty'} ${match?.winner_id === player2?.id ? 'winner' : match?.winner_id && match?.winner_id !== player2?.id ? 'loser' : ''}`}>
                                {player2 ? (
                                  <>
                                    <span className="name">{player2.display_name}</span>
                                    {match?.player2_multiplier && <span className="multi">{match.player2_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                              {match?.slot_name && <div className="match-slot-name">{match.slot_name}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Quarter Finals */}
                    <div className="bracket-round quarter-finals">
                      <div className="round-header">Quarter Finals</div>
                      <div className="round-matches">
                        {Array.from({ length: 4 }, (_, i) => {
                          const match = bracketData.matches.find(m => m.round === 3 && m.match_number === i + 1);
                          const prevMatch1 = bracketData.matches.find(m => m.round === 2 && m.match_number === i * 2 + 1);
                          const prevMatch2 = bracketData.matches.find(m => m.round === 2 && m.match_number === i * 2 + 2);
                          const player1 = prevMatch1?.winner_id ? bracketData.players.find(p => p.id === prevMatch1.winner_id) : null;
                          const player2 = prevMatch2?.winner_id ? bracketData.players.find(p => p.id === prevMatch2.winner_id) : null;
                          return (
                            <div 
                              key={i} 
                              className={`bracket-match ${match?.winner_id ? 'completed' : ''}`}
                              onClick={() => {
                                if (user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && player1 && player2) {
                                  setSelectedMatch({ ...match, matchNumber: i + 1, round: 3, player1, player2 });
                                  setBracketAdminTab('matches');
                                }
                              }}
                            >
                              <div className={`match-slot ${player1 ? '' : 'empty'} ${match?.winner_id === player1?.id ? 'winner' : ''}`}>
                                {player1 ? (
                                  <>
                                    <span className="name">{player1.display_name}</span>
                                    {match?.player1_multiplier && <span className="multi">{match.player1_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                              <div className={`match-slot ${player2 ? '' : 'empty'} ${match?.winner_id === player2?.id ? 'winner' : ''}`}>
                                {player2 ? (
                                  <>
                                    <span className="name">{player2.display_name}</span>
                                    {match?.player2_multiplier && <span className="multi">{match.player2_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Semi Finals */}
                    <div className="bracket-round semi-finals">
                      <div className="round-header">Semi Finals</div>
                      <div className="round-matches">
                        {Array.from({ length: 2 }, (_, i) => {
                          const match = bracketData.matches.find(m => m.round === 4 && m.match_number === i + 1);
                          const prevMatch1 = bracketData.matches.find(m => m.round === 3 && m.match_number === i * 2 + 1);
                          const prevMatch2 = bracketData.matches.find(m => m.round === 3 && m.match_number === i * 2 + 2);
                          const player1 = prevMatch1?.winner_id ? bracketData.players.find(p => p.id === prevMatch1.winner_id) : null;
                          const player2 = prevMatch2?.winner_id ? bracketData.players.find(p => p.id === prevMatch2.winner_id) : null;
                          return (
                            <div 
                              key={i} 
                              className={`bracket-match ${match?.winner_id ? 'completed' : ''}`}
                              onClick={() => {
                                if (user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && player1 && player2) {
                                  setSelectedMatch({ ...match, matchNumber: i + 1, round: 4, player1, player2 });
                                  setBracketAdminTab('matches');
                                }
                              }}
                            >
                              <div className={`match-slot ${player1 ? '' : 'empty'} ${match?.winner_id === player1?.id ? 'winner' : ''}`}>
                                {player1 ? (
                                  <>
                                    <span className="name">{player1.display_name}</span>
                                    {match?.player1_multiplier && <span className="multi">{match.player1_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                              <div className={`match-slot ${player2 ? '' : 'empty'} ${match?.winner_id === player2?.id ? 'winner' : ''}`}>
                                {player2 ? (
                                  <>
                                    <span className="name">{player2.display_name}</span>
                                    {match?.player2_multiplier && <span className="multi">{match.player2_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Finals */}
                    <div className="bracket-round finals">
                      <div className="round-header">🏆 Finals</div>
                      <div className="round-matches">
                        {(() => {
                          const match = bracketData.matches.find(m => m.round === 5 && m.match_number === 1);
                          const prevMatch1 = bracketData.matches.find(m => m.round === 4 && m.match_number === 1);
                          const prevMatch2 = bracketData.matches.find(m => m.round === 4 && m.match_number === 2);
                          const player1 = prevMatch1?.winner_id ? bracketData.players.find(p => p.id === prevMatch1.winner_id) : null;
                          const player2 = prevMatch2?.winner_id ? bracketData.players.find(p => p.id === prevMatch2.winner_id) : null;
                          return (
                            <div 
                              className={`bracket-match finals-match ${match?.winner_id ? 'completed' : ''}`}
                              onClick={() => {
                                if (user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && player1 && player2) {
                                  setSelectedMatch({ ...match, matchNumber: 1, round: 5, player1, player2 });
                                  setBracketAdminTab('matches');
                                }
                              }}
                            >
                              <div className={`match-slot ${player1 ? '' : 'empty'} ${match?.winner_id === player1?.id ? 'champion' : ''}`}>
                                {player1 ? (
                                  <>
                                    <span className="name">{player1.display_name}</span>
                                    {match?.player1_multiplier && <span className="multi">{match.player1_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                              <div className={`match-slot ${player2 ? '' : 'empty'} ${match?.winner_id === player2?.id ? 'champion' : ''}`}>
                                {player2 ? (
                                  <>
                                    <span className="name">{player2.display_name}</span>
                                    {match?.player2_multiplier && <span className="multi">{match.player2_multiplier}x</span>}
                                  </>
                                ) : <span className="tbd">TBD</span>}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Champion Display */}
                      {(() => {
                        const finalsMatch = bracketData.matches.find(m => m.round === 5 && m.match_number === 1);
                        const champion = finalsMatch?.winner_id ? bracketData.players.find(p => p.id === finalsMatch.winner_id) : null;
                        return champion ? (
                          <div className="champion-display">
                            <div className="champion-crown">👑</div>
                            <div className="champion-avatar">
                              {champion.profile_image_url ? (
                                <img src={champion.profile_image_url} alt="" />
                              ) : (
                                <div className="avatar-placeholder">{champion.display_name?.charAt(0)}</div>
                              )}
                            </div>
                            <div className="champion-name">{champion.display_name}</div>
                            <div className="champion-title">CHAMPION</div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bracket-empty">
                  <p>Bracket will appear here once players are selected from the raffle</p>
                </div>
              )}
            </div>

            {/* Pick Animation Overlay */}
            {showPickAnimation && pickedPlayer && (
              <div 
                className="pick-animation-overlay"
                onClick={() => {
                  setShowPickAnimation(false);
                  setPickedPlayer(null);
                  fetchBracketData();
                }}
              >
                <div className="pick-animation-content" onClick={(e) => e.stopPropagation()}>
                  <div className="pick-sparkles">✨</div>
                  <div className="pick-text">SELECTED!</div>
                  <div className="picked-player-card">
                    <div className="picked-avatar">
                      {pickedPlayer.profile_image_url ? (
                        <img src={pickedPlayer.profile_image_url} alt="" />
                      ) : (
                        <div className="avatar-placeholder">{pickedPlayer.display_name?.charAt(0)}</div>
                      )}
                    </div>
                    <div className="picked-name">{pickedPlayer.display_name}</div>
                    <div className="picked-seed">Seed #{bracketData.players.length + 1}</div>
                  </div>
                  <div className="pick-sparkles bottom">🎉</div>
                  <div className="pick-dismiss-hint">Click anywhere to dismiss</div>
                </div>
              </div>
            )}

            {/* Admin: Complete Tournament Button */}
            {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && bracketData.matches.find(m => m.round === 5 && m.winner_id) && (
              <div className="admin-complete-tournament">
                <button onClick={completeTournament} className="complete-tournament-btn">
                  🏆 Complete Tournament & Archive Results
                </button>
              </div>
            )}

            {/* Previous Tournaments Section (shown below active tournament) */}
            {tournamentHistory.length > 0 && (
              <div className="previous-tournaments-section">
                <h2>🏆 Previous Tournaments</h2>
                <div className="previous-tournaments-grid">
                  {tournamentHistory.map((t) => (
                    <div key={t.id} className="previous-tournament-card" onClick={() => fetchPastTournamentBracket(t.id)}>
                      <div className="pt-header">
                        <h3>{t.title}</h3>
                        <span className="pt-prize-pool">${t.prizePool} Prize Pool</span>
                      </div>
                      <div className="pt-podium-mini">
                        <div className="pt-winner">
                          <div className="pt-avatar">
                            {t.winner?.avatar ? (
                              <img src={t.winner.avatar} alt="" />
                            ) : (
                              <div className="avatar-placeholder">1</div>
                            )}
                          </div>
                          <span className="pt-medal">🥇</span>
                          <span className="pt-name">{t.winner?.name || 'N/A'}</span>
                        </div>
                        <div className="pt-runners">
                          <div className="pt-runner">
                            <span className="pt-medal">🥈</span>
                            <span className="pt-name">{t.second?.name || 'N/A'}</span>
                          </div>
                          <div className="pt-runner">
                            <span className="pt-medal">🥉</span>
                            <span className="pt-name">{t.third?.name || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="pt-view-bracket">View Full Bracket →</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* Challenges Page */}
        {activePage === 'sessions' && (
          <SessionsPage apiUrl={BACKEND_API_URL} isAdmin={isAdmin} getToken={getAuthToken} />
        )}

        {activePage === 'challenges' && (() => {
          const chStats = shuffleCh.stats || { liveCount: 0, livePool: 0, biggest: 0, completedTotal: 0, completedPaid: 0 };
          const chFeed = shuffleCh.feed || {};
          const sorted = [...(shuffleCh.active || [])].sort((a, b) => {
            if (chSort === 'multiplier') return (b.multiplier || 0) - (a.multiplier || 0);
            if (chSort === 'minbet') return (a.minBet || 0) - (b.minBet || 0);
            return (b.prize || 0) - (a.prize || 0);
          });
          const money = n => '$' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
          const ago = d => {
            if (!d) return '';
            const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
            if (mins < 1) return 'just now';
            if (mins < 60) return `${mins}m ago`;
            const h = Math.floor(mins / 60);
            if (h < 24) return `${h}h ago`;
            return `${Math.floor(h / 24)}d ago`;
          };
          const sortBtn = (key, label) => (
            <button key={key} onClick={() => setChSort(key)} style={{
              padding: '0.35rem 0.8rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
              background: chSort === key ? 'rgba(168,85,247,0.18)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${chSort === key ? 'rgba(168,85,247,0.5)' : 'rgba(255,255,255,0.1)'}`,
              color: chSort === key ? '#d8b4fe' : '#a1a1aa'
            }}>{label}</button>
          );

          return (
          <div className="page-content" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h1 style={{ margin: 0, fontSize: '1.8rem', color: '#fff' }}>⭐ Exclusive Challenges</h1>
              <p style={{ margin: '0.4rem 0 0', color: '#a1a1aa', fontSize: '0.9rem' }}>
                Hit the target multiplier on the slot and the prize is paid automatically by Shuffle.
                These are only playable by members signed up under guild <strong style={{ color: '#fbbf24' }}>SNUTZY</strong>.
              </p>
              <a href="https://shuffle.com/challenges" target="_blank" rel="noopener noreferrer"
                 style={{ display: 'inline-block', marginTop: '0.7rem', fontSize: '0.8rem', color: '#a855f7', fontWeight: 600 }}>
                Open on Shuffle.com →
              </a>
            </div>

            {chFeed.stale && (
              <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '12px', padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#fbbf24', textAlign: 'center' }}>
                ⚠️ Live sync is behind — showing the last known data{chFeed.lastUpdated ? ` from ${ago(chFeed.lastUpdated)}` : ''}. Check Shuffle for the current list.
              </div>
            )}

            {/* live totals */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.7rem', marginBottom: '1.4rem' }}>
              {[
                { label: 'Live challenges', value: chStats.liveCount, color: '#a855f7' },
                { label: 'Prize pool live', value: money(chStats.livePool), color: '#fbbf24' },
                { label: 'Biggest prize', value: money(chStats.biggest), color: '#fbbf24' },
                { label: 'Completed', value: chStats.completedTotal, color: '#22c55e' },
                { label: 'Paid out to players', value: money(chStats.completedPaid), color: '#22c55e' }
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '0.9rem 1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.35rem', fontWeight: 900, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '0.68rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.15rem' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* active */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.8rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', color: '#e2e8f0' }}>🎯 Live now</h2>
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                {sortBtn('prize', 'Prize')}{sortBtn('multiplier', 'Multiplier')}{sortBtn('minbet', 'Min bet')}
              </div>
            </div>

            {sorted.length === 0 ? (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: '16px', padding: '2.5rem 1rem', textAlign: 'center', color: '#71717a', fontSize: '0.85rem' }}>
                No live challenges right now — new ones drop regularly, check back soon.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '1rem' }}>
                {sorted.map(c => (
                  <div key={c.id} style={{ background: 'linear-gradient(180deg, rgba(168,85,247,0.07), rgba(255,255,255,0.02))', border: '1px solid rgba(168,85,247,0.22)', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ position: 'relative', aspectRatio: '16/10', background: 'rgba(0,0,0,0.4)' }}>
                      {c.image && <img src={c.image} alt={c.game} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                      <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'rgba(0,0,0,0.78)', border: '1px solid rgba(168,85,247,0.5)', borderRadius: '999px', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 900, color: '#d8b4fe' }}>
                        {Number(c.multiplier || 0).toLocaleString()}×
                      </div>
                    </div>
                    <div style={{ padding: '0.8rem 0.9rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                      <div>
                        <div style={{ fontWeight: 800, color: '#fff', fontSize: '0.92rem', lineHeight: 1.2 }}>{c.game}</div>
                        <div style={{ fontSize: '0.7rem', color: '#71717a', marginTop: '0.1rem' }}>{c.provider}</div>
                      </div>
                      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '0.5rem' }}>
                        <div>
                          <div style={{ fontSize: '0.62rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Min bet</div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e2e8f0' }}>{money(c.minBet)}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '0.62rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Prize</div>
                          <div style={{ fontSize: '1.15rem', fontWeight: 900, color: '#fbbf24', lineHeight: 1.1 }}>{money(c.prize)}</div>
                        </div>
                      </div>
                      <a href={c.slug ? `https://shuffle.com/casino/games/${c.slug}` : 'https://shuffle.com/challenges'}
                         target="_blank" rel="noopener noreferrer"
                         style={{ display: 'block', textAlign: 'center', padding: '0.45rem', borderRadius: '9px', background: 'rgba(168,85,247,0.16)', border: '1px solid rgba(168,85,247,0.4)', color: '#d8b4fe', fontSize: '0.75rem', fontWeight: 700, textDecoration: 'none' }}>
                        Play on Shuffle →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* completed */}
            {(shuffleCh.completed || []).length > 0 && (() => {
              const DONE_PER_PAGE = 10;
              const doneList = shuffleCh.completed;
              const donePages = Math.max(1, Math.ceil(doneList.length / DONE_PER_PAGE));
              const dp = Math.min(chDonePage, donePages - 1);
              const slice = doneList.slice(dp * DONE_PER_PAGE, dp * DONE_PER_PAGE + DONE_PER_PAGE);
              const pageBtn = (label, to, disabled) => (
                <button onClick={() => setChDonePage(to)} disabled={disabled} style={{
                  padding: '0.35rem 0.75rem', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700,
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
                  color: '#a1a1aa', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 1
                }}>{label}</button>
              );
              return (
              <div style={{ marginTop: '2.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.9rem' }}>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#e2e8f0' }}>🏆 Completed challenges</h2>
                  <span style={{ fontSize: '0.8rem', color: '#71717a' }}>
                    {chStats.completedTotal} completed · <strong style={{ color: '#22c55e' }}>{money(chStats.completedPaid)}</strong> paid out
                  </span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' }}>
                  {slice.map((c, i) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.9rem 1.1rem', borderTop: i ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                      {c.image && <img src={c.image} alt="" loading="lazy" style={{ width: '72px', height: '52px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, border: '1px solid rgba(255,255,255,0.08)' }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.game}</div>
                        <div style={{ fontSize: '0.82rem', color: '#8b8b96', marginTop: '0.15rem' }}>
                          {Number(c.multiplier || 0).toLocaleString()}× · min {money(c.minBet)}
                        </div>
                        <div style={{ fontSize: '0.82rem', marginTop: '0.2rem' }}>
                          <span style={{ color: '#71717a' }}>Won by </span>
                          {/* ~4 in 10 winners keep their handle public; the rest come back empty */}
                          <strong style={{ color: c.winner ? '#c4b5fd' : '#71717a', fontStyle: c.winner ? 'normal' : 'italic' }}>
                            {c.winner || 'Anonymous'}
                          </strong>
                          {c.winnerVip && <span style={{ color: '#52525b' }}> · {String(c.winnerVip).replace(/_/g, ' ')}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 900, color: '#22c55e', fontSize: '1.2rem' }}>{money(c.prize)}</div>
                        {c.completedAt && <div style={{ fontSize: '0.72rem', color: '#52525b', marginTop: '0.1rem' }}>{ago(c.completedAt)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                {donePages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', marginTop: '0.9rem' }}>
                    {pageBtn('‹ Prev', dp - 1, dp === 0)}
                    <span style={{ fontSize: '0.8rem', color: '#71717a' }}>Page {dp + 1} of {donePages}</span>
                    {pageBtn('Next ›', dp + 1, dp >= donePages - 1)}
                  </div>
                )}
              </div>
              );
            })()}

            <div style={{ marginTop: '2rem', textAlign: 'center', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '1.3rem' }}>
              <p style={{ margin: '0 0 0.7rem', color: '#a1a1aa', fontSize: '0.85rem' }}>Not playing under guild SNUTZY yet?</p>
              <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer"
                 style={{ display: 'inline-block', padding: '0.6rem 1.4rem', borderRadius: '10px', background: 'linear-gradient(135deg,#fbbf24,#f59e0b)', color: '#1c1205', fontWeight: 800, fontSize: '0.85rem', textDecoration: 'none' }}>
                Join the guild to unlock these →
              </a>
            </div>

            {chFeed.lastUpdated && !chFeed.stale && (
              <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.68rem', color: '#52525b' }}>
                Synced with Shuffle {ago(chFeed.lastUpdated)}
              </div>
            )}
          </div>
          );
        })()}

        {/* Add Challenge Modal (Admin) */}
        {showAddChallengeModal && (
          <div className="claim-modal-overlay" onClick={() => setShowAddChallengeModal(false)}>
            <div className="claim-modal" onClick={(e) => e.stopPropagation()}>
              <button className="claim-modal-close" onClick={() => setShowAddChallengeModal(false)}>×</button>
              
              <div className="claim-modal-header">
                <h2>➕ Add New Challenge</h2>
                <p>Create a new slot challenge</p>
              </div>
              
              <div className="claim-form">
                <div className="claim-input-group">
                  <label>Slot Name <span className="required">*</span></label>
                  <input 
                    type="text"
                    placeholder="e.g., The Count"
                    value={newChallenge.slotName}
                    onChange={(e) => setNewChallenge(prev => ({ ...prev, slotName: e.target.value }))}
                  />
                </div>
                
                <div className="claim-input-group">
                  <label>Slot Image URL</label>
                  <input 
                    type="text"
                    placeholder="https://example.com/image.png"
                    value={newChallenge.slotImageUrl}
                    onChange={(e) => setNewChallenge(prev => ({ ...prev, slotImageUrl: e.target.value }))}
                  />
                </div>
                
                <div className="claim-input-row">
                  <div className="claim-input-group">
                    <label>Multiplier Target <span className="required">*</span></label>
                    <input 
                      type="number"
                      placeholder="e.g., 500"
                      value={newChallenge.multiplierTarget}
                      onChange={(e) => setNewChallenge(prev => ({ ...prev, multiplierTarget: e.target.value }))}
                    />
                  </div>
                  <div className="claim-input-group">
                    <label>Min Bet ($) <span className="required">*</span></label>
                    <input 
                      type="number"
                      step="0.01"
                      placeholder="e.g., 1.00"
                      value={newChallenge.minBet}
                      onChange={(e) => setNewChallenge(prev => ({ ...prev, minBet: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="claim-input-group">
                  <label>Prize Amount ($) <span className="required">*</span></label>
                  <input 
                    type="number"
                    step="0.01"
                    placeholder="e.g., 500.00"
                    value={newChallenge.prizeAmount}
                    onChange={(e) => setNewChallenge(prev => ({ ...prev, prizeAmount: e.target.value }))}
                  />
                </div>
                
                <button 
                  className="claim-submit-btn"
                  onClick={createChallenge}
                  disabled={challengeActionLoading || !newChallenge.slotName || !newChallenge.multiplierTarget || !newChallenge.minBet || !newChallenge.prizeAmount}
                >
                  {challengeActionLoading ? 'Creating...' : 'Create Challenge'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Challenge Modal (Admin) */}
        {showCompleteChallengeModal && challengeToComplete && (
          <div className="claim-modal-overlay" onClick={() => { setShowCompleteChallengeModal(false); setChallengeToComplete(null); }}>
            <div className="claim-modal" onClick={(e) => e.stopPropagation()}>
              <button className="claim-modal-close" onClick={() => { setShowCompleteChallengeModal(false); setChallengeToComplete(null); }}>×</button>
              
              <div className="claim-modal-header">
                <h2>✅ Mark Challenge Complete</h2>
                <p>{challengeToComplete.slotName} - {challengeToComplete.multiplierTarget}x</p>
              </div>
              
              <div className="claim-form">
                <div className="claim-input-group">
                  <label>Winner Username <span className="required">*</span></label>
                  <input 
                    type="text"
                    placeholder="Shuffle or Twitch username of winner"
                    value={completedByUsername}
                    onChange={(e) => setCompletedByUsername(e.target.value)}
                  />
                  <span className="input-hint">This will be displayed on the completed challenge card</span>
                </div>
                
                <button 
                  className="claim-submit-btn"
                  onClick={markChallengeComplete}
                  disabled={challengeActionLoading || !completedByUsername.trim()}
                >
                  {challengeActionLoading ? 'Saving...' : 'Mark as Completed'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Community Hunt Page */}
        {activePage === 'communityhunt' && (
          <div className="community-hunt-container">
            <h1 className="simple-page-title">🤝 Community Hunt</h1>
            
            {/* Tab Switcher */}
            <div style={{
              display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', background: 'rgba(0,0,0,0.3)',
              padding: '0.35rem', borderRadius: '0.75rem', width: 'fit-content'
            }}>
              <button
                onClick={() => { setCommunityHuntTab('raffle'); window.history.replaceState({}, '', '/raffle-hunts'); }}
                style={{
                  padding: '0.5rem 1.25rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                  background: communityHuntTab === 'raffle' ? 'rgba(168,85,247,0.3)' : 'transparent',
                  color: communityHuntTab === 'raffle' ? '#c084fc' : '#9ca3af',
                  ...(communityHuntTab === 'raffle' ? { border: '1px solid rgba(168,85,247,0.3)' } : { border: '1px solid transparent' })
                }}
              >
                🎟️ Raffle Hunt {raffleHunt?.hunt?.status === 'open' && !raffleHunt?.hunt?.entries_closed_at && '(OPEN)'}
                {raffleHunt?.hunt?.status === 'live' && '(LIVE)'}
              </button>
              <button
                onClick={() => { setCommunityHuntTab('community'); window.history.replaceState({}, '', '/community-hunts'); }}
                style={{
                  padding: '0.5rem 1.25rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                  background: communityHuntTab === 'community' ? 'rgba(168,85,247,0.3)' : 'transparent',
                  color: communityHuntTab === 'community' ? '#c084fc' : '#9ca3af',
                  ...(communityHuntTab === 'community' ? { border: '1px solid rgba(168,85,247,0.3)' } : { border: '1px solid transparent' })
                }}
              >
                🤝 Community Hunt {communityHunt?.hunt?.status === 'live' && '(LIVE)'}
              </button>
            </div>

            {/* ==================== RAFFLE HUNT TAB ==================== */}
            {communityHuntTab === 'raffle' && (
              <div>
                {/* Admin Controls */}
                {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && (
                  <div style={{
                    background: 'rgba(15,15,25,0.95)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '0.75rem',
                    padding: '1rem', marginBottom: '1.25rem'
                  }}>
                    <h3 style={{color: '#c084fc', margin: '0 0 0.75rem 0', fontSize: '0.95rem'}}>⚙️ Raffle Hunt Admin</h3>
                    
                    {/* Create New */}
                    <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem'}}>
                      <select id="rh-type" defaultValue="affiliate" onChange={(e) => { const maxInput = document.getElementById('rh-max'); maxInput.value = e.target.value === 'vip' ? '10' : '20'; maxInput.placeholder = e.target.value === 'vip' ? 'Max (10)' : 'Max (20)'; }} style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.8rem'}}>
                        <option value="affiliate">👥 Affiliate</option>
                        <option value="vip">⭐ VIP ($50k+ weighted)</option>
                      </select>
                      <input id="rh-name" placeholder="Hunt name" style={{flex:1, minWidth:'150px', padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.8rem'}} />
                      <input id="rh-max" type="number" placeholder="Max (20)" defaultValue="20" style={{width:'70px', padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.8rem'}} />
                      <button onClick={async () => {
                        const token = localStorage.getItem('auth_token');
                        const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            name: document.getElementById('rh-name').value || (document.getElementById('rh-type').value === 'vip' ? 'VIP Community Hunt' : 'Community Hunt'),
                            huntType: document.getElementById('rh-type').value,
                            maxParticipants: parseInt(document.getElementById('rh-max').value) || 20
                          })
                        });
                        if (res.ok) { document.getElementById('rh-name').value = ''; fetchRaffleHunt(); alert('Hunt created!'); }
                        else { const d = await res.json(); alert(d.error); }
                      }} style={{padding:'0.4rem 0.75rem', background:'rgba(168,85,247,0.3)', border:'1px solid rgba(168,85,247,0.5)', borderRadius:'0.375rem', color:'#c084fc', cursor:'pointer', fontWeight:600, fontSize:'0.8rem'}}>
                        Create
                      </button>
                    </div>

                    {/* Active Hunt Controls */}
                    {raffleHunt.hunt && (
                      <div style={{padding:'0.75rem', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'0.5rem'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem', marginBottom:'0.5rem'}}>
                          <div>
                            <strong style={{color:'#e2e8f0'}}>{raffleHunt.hunt.name}</strong>
                            <button onClick={async () => { const n = prompt('Edit hunt title:', raffleHunt.hunt.name); if (n === null || !n.trim()) return; const token = localStorage.getItem('auth_token'); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ name: n.trim() }) }); if (res.ok) fetchRaffleHunt(); else { const d = await res.json().catch(() => ({})); alert(d.error || 'Rename failed'); } }} title="Edit hunt title" style={{marginLeft:'0.35rem', padding:'0.1rem 0.35rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'4px', color:'#a1a1aa', fontSize:'0.65rem', cursor:'pointer'}}>✏️</button>
                            <span style={{marginLeft:'0.5rem', padding:'0.15rem 0.5rem', borderRadius:'999px', fontSize:'0.7rem', fontWeight:600, background: raffleHunt.hunt.hunt_type === 'vip' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(168,85,247,0.3)', color: raffleHunt.hunt.hunt_type === 'vip' ? '#000' : '#c084fc'}}>{raffleHunt.hunt.hunt_type === 'vip' ? '⭐ VIP' : '👥 Affiliate'}</span>
                            <span style={{marginLeft:'0.5rem', fontSize:'0.8rem', color:'#9ca3af'}}>Status: <strong style={{color: raffleHunt.hunt.status === 'open' ? '#22c55e' : raffleHunt.hunt.status === 'live' ? '#f87171' : '#eab308'}}>{raffleHunt.hunt.status.toUpperCase()}</strong>{raffleHunt.hunt.status === 'open' && raffleHunt.hunt.entries_closed_at && <strong style={{color:'#fbbf24'}}> (ENTRIES CLOSED)</strong>} | {raffleHunt.hunt.entryCount || 0} entries</span>
                          </div>
                          <div style={{display:'flex', gap:'0.35rem', flexWrap:'wrap'}}>
                            <button onClick={async () => { if (!window.confirm('Delete this hunt?')) return; const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); fetchRaffleHunt(); }} style={{padding:'0.3rem 0.5rem', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'0.25rem', color:'#f87171', fontSize:'0.75rem', cursor:'pointer'}}>🗑️</button>
                            
                            {raffleHunt.hunt.status === 'draft' && (
                              <button onClick={async () => { const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'open' }) }); fetchRaffleHunt(); }} style={{padding:'0.3rem 0.6rem', background:'#22c55e', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>Open Entries</button>
                            )}
                            {raffleHunt.hunt.status === 'open' && (
                              <>
                                <input id="rh-roll-count" type="number" placeholder="# winners" defaultValue={raffleHunt.hunt.max_participants || 20} style={{width:'70px', padding:'0.3rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}} />
                                <button onClick={async () => { const count = document.getElementById('rh-roll-count').value; const already = raffleHunt.entries.length; if (!window.confirm(already > 0 ? `Roll ${count} ADDITIONAL winners? The ${already} already selected stay in place (total becomes ${already} + up to ${count}).` : `Roll ${count} winners?`)) return; const token = localStorage.getItem('auth_token'); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/roll`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ count: parseInt(count) }) }); const d = await res.json(); alert(d.message || d.error); fetchRaffleHunt(); }} style={{padding:'0.3rem 0.6rem', background:'#8b5cf6', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>🎲 Roll</button>
                                {raffleHunt.hunt.entries_closed_at && (
                                  <button onClick={async () => { if (!window.confirm('Reopen entries for a second window?\n\n• Existing selected winners stay in place\n• Previous non-winners go back into the pool (their bad-luck +1 from the last roll is undone)\n• New eligible people can enter until you roll again')) return; const token = localStorage.getItem('auth_token'); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/reopen-entries`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }); const d = await res.json().catch(() => ({})); if (res.ok) { alert(`✅ Entries reopened — ${d.returnedToPool} previous non-winner(s) returned to the pool.`); fetchRaffleHunt(); } else { alert(d.error || 'Failed to reopen'); } }} style={{padding:'0.3rem 0.6rem', background:'rgba(34,197,94,0.2)', border:'1px solid rgba(34,197,94,0.45)', borderRadius:'0.25rem', color:'#22c55e', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>🔓 Reopen Entries</button>
                                )}
                                <button onClick={async () => { if (!window.confirm(raffleHunt.hunt.entries_closed_at ? 'Go live with this hunt?' : 'Go live WITHOUT rolling winners?\n\nNo winners have been rolled yet — you probably want 🎲 Roll first.')) return; const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'live' }) }); fetchRaffleHunt(); }} style={{padding:'0.3rem 0.6rem', background:'#ef4444', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>🔴 Go Live</button>
                              </>
                            )}
                            {raffleHunt.hunt.status === 'live' && (
                              <>
                                <button onClick={async () => { if (!window.confirm('Undo Go Live and put the hunt back to OPEN?\n\n• All entries and any selected winners stay exactly as they are\n• No Discord announcement is posted')) return; const token = localStorage.getItem('auth_token'); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'open' }) }); if (!res.ok) { const d = await res.json().catch(() => ({})); alert(d.error || 'Failed to undo Go Live'); } fetchRaffleHunt(); }} style={{padding:'0.3rem 0.6rem', background:'rgba(234,179,8,0.15)', border:'1px solid rgba(234,179,8,0.35)', borderRadius:'0.25rem', color:'#eab308', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>↩️ Undo Go Live</button>
                                <input id="rh-start" type="number" placeholder="Start $" defaultValue={raffleHunt.hunt.start_balance || ''} style={{width:'90px', padding:'0.3rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}} />
                                <input id="rh-end" type="number" placeholder="End $" defaultValue={raffleHunt.hunt.end_balance || ''} style={{width:'90px', padding:'0.3rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.8rem'}} />
                                <button onClick={async () => { const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ startBalance: document.getElementById('rh-start').value || null, endBalance: document.getElementById('rh-end').value || null }) }); fetchRaffleHunt(); alert('Updated!'); }} style={{padding:'0.3rem 0.6rem', background:'rgba(168,85,247,0.3)', border:'1px solid rgba(168,85,247,0.5)', borderRadius:'0.25rem', color:'#c084fc', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>Update</button>
                                <button onClick={async () => { if (!window.confirm('Complete hunt?')) return; const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'completed' }) }); fetchRaffleHunt(); fetchRaffleHistory(); }} style={{padding:'0.3rem 0.6rem', background:'#22c55e', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>✅ Complete</button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Host Controls */}
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap', padding:'0.5rem', background:'rgba(168,85,247,0.08)', borderRadius:'0.375rem', marginBottom:'0.5rem', fontSize:'0.8rem'}}>
                          <span style={{color:'#9ca3af'}}>👑 Host:</span>
                          <strong style={{color:'#c084fc'}}>{raffleHunt.hunt.host_display_name || raffleHunt.hunt.host_username || 'Not set'}</strong>
                          <input id="rh-host" placeholder="Twitch username" style={{width:'120px', padding:'0.25rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.75rem'}} />
                          <button onClick={async () => { const u = document.getElementById('rh-host').value.trim(); if (!u) return; const token = localStorage.getItem('auth_token'); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/host`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ username: u }) }); const d = await res.json(); alert(d.message || d.error); document.getElementById('rh-host').value = ''; fetchRaffleHunt(); }} style={{padding:'0.25rem 0.5rem', background:'#8b5cf6', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.7rem', fontWeight:600, cursor:'pointer'}}>Change</button>
                        </div>

                        {/* Selected Participants with payouts */}
                        {raffleHunt.entries.length > 0 && (
                          <div>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.35rem'}}>
                              <span style={{fontSize:'0.8rem', color:'#9ca3af'}}>Selected ({raffleHunt.entries.length}):</span>
                              <div style={{display:'flex', gap:'0.35rem', alignItems:'center'}}>
                                <span style={{fontSize:'0.7rem', color:'#9ca3af'}}>Bulk $:</span>
                                <input type="number" placeholder="each" id="rh-bulk" style={{width:'55px', padding:'0.2rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.75rem'}} />
                                <button onClick={async () => { const p = document.getElementById('rh-bulk').value; if (!p) return; if (!window.confirm(`Set $${p} for all?`)) return; const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/bulk-payout`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ payout: parseFloat(p) }) }); fetchRaffleHunt(); }} style={{padding:'0.2rem 0.4rem', background:'#8b5cf6', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.7rem', fontWeight:600, cursor:'pointer'}}>Apply All</button>
                              </div>
                            </div>
                            <div style={{display:'grid', gap:'0.35rem', maxHeight:'350px', overflowY:'auto'}}>
                              {raffleHunt.entries.map((entry, idx) => (
                                <div key={entry.id} style={{display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.4rem', background: entry.is_host ? 'rgba(168,85,247,0.12)' : 'rgba(0,0,0,0.2)', borderRadius:'0.375rem', border: entry.is_host ? '1px solid rgba(168,85,247,0.3)' : '1px solid transparent'}}>
                                  <span style={{width:'20px', fontSize:'0.75rem', color:'#9ca3af'}}>{entry.is_host ? '👑' : `#${idx+1}`}</span>
                                  <img src={entry.profile_image_url || ''} alt="" style={{width:24, height:24, borderRadius:'50%'}} />
                                  <span style={{flex:1, fontSize:'0.8rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#e2e8f0'}}>
                                    {entry.display_name} <span style={{color:'#6b7280'}}>({entry.shuffle_username})</span>
                                    {entry.is_host && <span style={{marginLeft:'0.3rem', fontSize:'0.6rem', color:'#c084fc', background:'rgba(168,85,247,0.2)', padding:'0.1rem 0.3rem', borderRadius:'4px'}}>HOST</span>}
                                  </span>
                                  <span style={{color:'#9ca3af', fontSize:'0.75rem'}}>$</span>
                                  <input type="number" placeholder="0" defaultValue={entry.payout || ''} id={`rh-pay-${entry.id}`} style={{width:'60px', padding:'0.25rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.75rem'}} />
                                  <button onClick={async () => { const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/entries/${entry.id}`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ payout: document.getElementById(`rh-pay-${entry.id}`).value || null }) }); fetchRaffleHunt(); }} style={{padding:'0.2rem 0.4rem', background:'#22c55e', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.65rem', fontWeight:600, cursor:'pointer'}}>💾</button>
                                  <button onClick={async () => { if (!window.confirm(`Reroll ${entry.display_name}?`)) return; const token = localStorage.getItem('auth_token'); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/reroll/${entry.id}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }); const d = await res.json(); alert(d.message || d.error); fetchRaffleHunt(); }} style={{padding:'0.2rem 0.35rem', background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'0.25rem', color:'#c084fc', fontSize:'0.65rem', cursor:'pointer'}}>🎲</button>
                                  <button onClick={async () => { if (!window.confirm(`Remove ${entry.display_name}?`)) return; const token = localStorage.getItem('auth_token'); await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/entries/${entry.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); fetchRaffleHunt(); }} style={{padding:'0.2rem 0.35rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'0.25rem', color:'#f87171', fontSize:'0.65rem', cursor:'pointer'}}>✕</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Eligibility Whitelist Management */}
                {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && (
                  <div style={{
                    background: 'rgba(15,15,25,0.95)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: '0.75rem',
                    padding: '1rem', marginBottom: '1.25rem'
                  }}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem'}}>
                      <h3 style={{color: '#eab308', margin: 0, fontSize: '0.95rem'}}>📋 Eligibility Whitelist</h3>
                      <button onClick={fetchEligibilityWhitelist} style={{padding:'0.3rem 0.6rem', background:'rgba(234,179,8,0.15)', border:'1px solid rgba(234,179,8,0.3)', borderRadius:'0.25rem', color:'#eab308', fontSize:'0.75rem', cursor:'pointer', fontWeight:600}}>🔄 Load / Refresh</button>
                    </div>
                    <p style={{fontSize:'0.75rem', color:'#6b7280', margin:'0 0 0.75rem 0'}}>Manually grant VIP or Affiliate status to users who should bypass Shuffle wager checks.</p>
                    
                    {/* Add to whitelist form */}
                    <div style={{display:'flex', gap:'0.4rem', flexWrap:'wrap', alignItems:'center', marginBottom:'0.75rem'}}>
                      <input id="wl-username" placeholder="Twitch or Shuffle username" style={{flex:1, minWidth:'140px', padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.8rem'}} />
                      <select id="wl-type" defaultValue="affiliate" style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.8rem'}}>
                        <option value="affiliate">👥 Affiliate</option>
                        <option value="vip">⭐ VIP</option>
                      </select>
                      <input id="wl-reason" placeholder="Reason (optional)" style={{width:'130px', padding:'0.4rem', background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'0.375rem', color:'#e2e8f0', fontSize:'0.8rem'}} />
                      <button onClick={async () => {
                        const username = document.getElementById('wl-username').value.trim();
                        if (!username) return alert('Enter a username');
                        const token = localStorage.getItem('auth_token');
                        const res = await fetch(`${BACKEND_API_URL}/api/admin/eligibility-whitelist`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            username,
                            eligibilityType: document.getElementById('wl-type').value,
                            reason: document.getElementById('wl-reason').value || null
                          })
                        });
                        const data = await res.json();
                        if (res.ok) {
                          alert(data.message);
                          document.getElementById('wl-username').value = '';
                          document.getElementById('wl-reason').value = '';
                          fetchEligibilityWhitelist();
                        } else {
                          alert(data.error || 'Failed to add');
                        }
                      }} style={{padding:'0.4rem 0.75rem', background:'#22c55e', border:'none', borderRadius:'0.375rem', color:'#fff', fontSize:'0.8rem', fontWeight:600, cursor:'pointer'}}>
                        + Add
                      </button>
                    </div>

                    {/* Current whitelist entries */}
                    {eligibilityWhitelist.length > 0 ? (
                      <div style={{display:'grid', gap:'0.35rem', maxHeight:'250px', overflowY:'auto'}}>
                        {eligibilityWhitelist.map(entry => (
                          <div key={entry.id} style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.4rem 0.6rem', background:'rgba(0,0,0,0.25)', borderRadius:'0.375rem', fontSize:'0.8rem'}}>
                            <img src={entry.profile_image_url || ''} alt="" style={{width:24, height:24, borderRadius:'50%'}} />
                            <span style={{flex:1, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                              {entry.display_name || entry.username}
                              {entry.shuffle_username && <span style={{color:'#6b7280'}}> ({entry.shuffle_username})</span>}
                            </span>
                            <span style={{
                              padding:'0.15rem 0.4rem', borderRadius:'4px', fontSize:'0.65rem', fontWeight:700,
                              background: entry.eligibility_type === 'vip' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(168,85,247,0.3)',
                              color: entry.eligibility_type === 'vip' ? '#000' : '#c084fc'
                            }}>{entry.eligibility_type === 'vip' ? '⭐ VIP' : '👥 AFF'}</span>
                            {entry.reason && <span style={{color:'#6b7280', fontSize:'0.7rem', maxWidth:'100px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}} title={entry.reason}>💬 {entry.reason}</span>}
                            <span style={{color:'#4b5563', fontSize:'0.65rem'}}>{entry.added_by_username && `by ${entry.added_by_username}`}</span>
                            <button onClick={async () => {
                              if (!window.confirm(`Remove ${entry.display_name || entry.username} from whitelist?`)) return;
                              const token = localStorage.getItem('auth_token');
                              await fetch(`${BACKEND_API_URL}/api/admin/eligibility-whitelist/${entry.id}`, {
                                method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
                              });
                              fetchEligibilityWhitelist();
                            }} style={{padding:'0.2rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'0.25rem', color:'#f87171', fontSize:'0.7rem', cursor:'pointer'}}>✕</button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{textAlign:'center', padding:'0.75rem', color:'#4b5563', fontSize:'0.8rem', background:'rgba(0,0,0,0.15)', borderRadius:'0.375rem'}}>
                        Click "🔄 Load / Refresh" to see whitelisted users
                      </div>
                    )}
                  </div>
                )}

                {/* Public Raffle Hunt Display */}
                {raffleHunt.hunt ? (
                  <div>
                    {/* Hunt Info Card */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(168,85,247,0.04))',
                      border: '1px solid rgba(168,85,247,0.25)', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem'
                    }}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'1rem'}}>
                        <div>
                          <div style={{display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap', marginBottom:'0.5rem'}}>
                            <h2 style={{fontSize:'1.5rem', fontWeight:700, color:'#e2e8f0', margin:0}}>{raffleHunt.hunt.name}</h2>
                            <span style={{padding:'0.2rem 0.6rem', borderRadius:'4px', fontSize:'0.7rem', fontWeight:700, background: raffleHunt.hunt.hunt_type === 'vip' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(168,85,247,0.3)', color: raffleHunt.hunt.hunt_type === 'vip' ? '#000' : '#c084fc', textTransform:'uppercase'}}>
                              {raffleHunt.hunt.hunt_type === 'vip' ? '⭐ VIP' : '👥 Affiliate'}
                            </span>
                            <span style={{padding:'0.25rem 0.75rem', borderRadius:'20px', fontSize:'0.75rem', fontWeight:700, background: raffleHunt.hunt.status === 'open' ? 'rgba(34,197,94,0.2)' : raffleHunt.hunt.status === 'live' ? 'rgba(239,68,68,0.2)' : raffleHunt.hunt.status === 'draft' ? 'rgba(234,179,8,0.2)' : 'rgba(107,114,128,0.2)', color: raffleHunt.hunt.status === 'open' ? '#22c55e' : raffleHunt.hunt.status === 'live' ? '#ef4444' : raffleHunt.hunt.status === 'draft' ? '#eab308' : '#9ca3af', border: '1px solid ' + (raffleHunt.hunt.status === 'open' ? 'rgba(34,197,94,0.4)' : raffleHunt.hunt.status === 'live' ? 'rgba(239,68,68,0.4)' : 'rgba(234,179,8,0.4)')}}>
                              {raffleHunt.hunt.status === 'open' ? '🎟️ ENTRIES OPEN' : raffleHunt.hunt.status === 'live' ? '🔴 LIVE' : raffleHunt.hunt.status === 'draft' ? '📝 COMING SOON' : '✅ COMPLETED'}
                            </span>
                          </div>
                          {raffleHunt.hunt.description && <p style={{color:'#9ca3af', marginBottom:'0.75rem'}}>{raffleHunt.hunt.description}</p>}
                          <div style={{display:'flex', gap:'1.5rem', flexWrap:'wrap', fontSize:'0.9rem', color:'#9ca3af'}}>
                            {raffleHunt.hunt.status !== 'draft' && <span>👥 {raffleHunt.hunt.entryCount || 0} entries</span>}
                            {raffleHunt.entries.length > 0 && <span>🎯 {raffleHunt.entries.length} selected</span>}
                            {raffleHunt.hunt.start_balance && <span>💰 Start: ${parseFloat(raffleHunt.hunt.start_balance).toLocaleString()}</span>}
                            {raffleHunt.hunt.end_balance && <span>🏁 End: ${parseFloat(raffleHunt.hunt.end_balance).toLocaleString()}</span>}
                          </div>
                          
                          {/* Eligibility Info */}
                          {raffleHunt.hunt.status === 'open' && user && (
                            <div style={{marginTop:'0.75rem', fontSize:'0.8rem'}}>
                              {raffleHunt.hunt.hunt_type === 'vip' ? (
                                <span style={{color: raffleEligibility.vip ? '#22c55e' : '#ef4444'}}>
                                  {raffleEligibility.vip 
                                    ? (raffleEligibility.admin ? '✅ You qualify (Admin)' : raffleEligibility.whitelisted ? '✅ You qualify (Whitelisted)' : `✅ You qualify ($${(raffleEligibility.wagered || 0).toLocaleString()} wagered)`) 
                                    : `❌ VIP requires $50k+ weighted wagered (or legacy VIP status)`}
                                </span>
                              ) : (
                                <span style={{color: raffleEligibility.affiliate ? '#22c55e' : '#ef4444'}}>
                                  {raffleEligibility.affiliate 
                                    ? (raffleEligibility.admin ? '✅ You qualify (Admin)' : raffleEligibility.whitelisted ? '✅ You qualify (Whitelisted)' : `✅ You qualify ($${(raffleEligibility.wagered || 0).toLocaleString()} wagered)`) 
                                    : `❌ Requires $1,000+ wagered under guild SNUTZY (You: $${(raffleEligibility.wagered || 0).toLocaleString()})`}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Entry Button */}
                        {raffleHunt.hunt.status === 'open' && (
                          <div style={{textAlign:'right'}}>
                            {!user ? (
                              <button onClick={() => window.location.href = `${BACKEND_API_URL}/auth/twitch`} style={{padding:'0.75rem 1.5rem', background:'rgba(168,85,247,0.3)', border:'1px solid rgba(168,85,247,0.5)', borderRadius:'0.75rem', color:'#c084fc', fontWeight:600, cursor:'pointer', fontSize:'0.9rem'}}>
                                🔐 Login to Enter
                              </button>
                            ) : raffleMyEntry?.status === 'entered' ? (
                              <div style={{padding:'0.75rem 1.5rem', background:'rgba(34,197,94,0.2)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'0.75rem', color:'#22c55e', fontWeight:600}}>
                                ✅ You're Entered!
                                {raffleLossStreak > 0 && <div style={{fontSize:'0.72rem', color:'#22c55e', marginTop:'0.25rem'}}>🍀 Bad luck protection: {raffleBoostMultiplier}× boost ({raffleLossStreak} loss{raffleLossStreak === 1 ? '' : 'es'} in a row)</div>}
                              </div>
                            ) : raffleMyEntry?.status === 'selected' ? (
                              <div style={{padding:'0.75rem 1.5rem', background:'rgba(168,85,247,0.2)', border:'1px solid rgba(168,85,247,0.4)', borderRadius:'0.75rem', color:'#c084fc', fontWeight:600}}>
                                🎉 You're Selected!
                              </div>
                            ) : raffleMyEntry?.status === 'not_selected' ? (
                              <div style={{padding:'0.75rem 1.5rem', background:'rgba(113,113,122,0.15)', border:'1px solid rgba(113,113,122,0.3)', borderRadius:'0.75rem', color:'#a1a1aa', fontWeight:600}}>
                                😔 Not selected this time
                                {raffleLossStreak > 0 && <div style={{fontSize:'0.72rem', color:'#22c55e', marginTop:'0.25rem'}}>🍀 Your bad luck protection is now at {raffleBoostMultiplier}× for next time!</div>}
                              </div>
                            ) : raffleHunt.hunt.entries_closed_at ? (
                              <div style={{padding:'0.75rem 1.5rem', background:'rgba(113,113,122,0.15)', border:'1px solid rgba(113,113,122,0.3)', borderRadius:'0.75rem', color:'#a1a1aa', fontWeight:600}}>
                                🔒 Entries closed — winners have been drawn
                              </div>
                            ) : !raffleHasShuffleUsername ? (
                              <button onClick={() => navigateTo('profile')} style={{padding:'0.75rem 1.5rem', background:'rgba(168,85,247,0.2)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'0.75rem', color:'#c084fc', fontWeight:600, cursor:'pointer', fontSize:'0.85rem'}}>
                                🔗 Link Shuffle Username First
                              </button>
                            ) : (
                              <button onClick={enterRaffleHunt} disabled={raffleLoading || (raffleHunt.hunt.hunt_type === 'vip' && !raffleEligibility.vip) || (raffleHunt.hunt.hunt_type === 'affiliate' && !raffleEligibility.affiliate)} style={{padding:'0.75rem 1.5rem', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', border:'none', borderRadius:'0.75rem', color:'#fff', fontWeight:600, cursor:'pointer', fontSize:'0.9rem', opacity: ((raffleHunt.hunt.hunt_type === 'vip' && !raffleEligibility.vip) || (raffleHunt.hunt.hunt_type === 'affiliate' && !raffleEligibility.affiliate)) ? 0.5 : 1}}>
                                {raffleLoading ? 'Entering...' : '🎟️ Enter Raffle'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ===== RAFFLE ENTRIES (when open) ===== */}
                    {raffleHunt.hunt.status === 'open' && raffleHunt.allEntries?.length > 0 && (
                      <div style={{background:'rgba(14,14,18,0.8)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'1rem', padding:'1.25rem', marginBottom:'1.5rem'}}>
                        <h3 style={{fontSize:'1rem', fontWeight:600, marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.5rem', color:'#e2e8f0'}}>
                          🎟️ Raffle Entries ({raffleHunt.allEntries.length})
                        </h3>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:'0.6rem'}}>
                          {raffleHunt.allEntries.map((entry) => (
                            <div key={entry.id} style={{
                              position:'relative', overflow:'hidden',
                              background: 'linear-gradient(145deg, rgba(168,85,247,0.08), rgba(168,85,247,0.02))',
                              border: user && entry.user_id === user.id ? '1px solid rgba(168,85,247,0.5)' : '1px solid rgba(255,255,255,0.06)',
                              borderRadius:'0.75rem', padding:'0.75rem', textAlign:'center', transition:'transform 0.15s',
                            }}>
                              {isAdmin && (
                                <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Remove ${entry.display_name || entry.username} from raffle?`)) return; try { const token = localStorage.getItem('auth_token'); const res = await fetch(`${BACKEND_API_URL}/api/admin/raffle-hunts/${raffleHunt.hunt.id}/entries/${entry.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); const d = await res.json(); if (!res.ok) alert(d.error || 'Failed to remove'); fetchRaffleHunt(); } catch(err) { alert('Error: ' + err.message); } }} style={{position:'absolute', top:'0.25rem', right:'0.25rem', width:'18px', height:'18px', borderRadius:'50%', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', color:'#f87171', fontSize:'0.55rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, lineHeight:1, zIndex:2}}>✕</button>
                              )}
                              {user && entry.user_id === user.id && (
                                <div style={{position:'absolute', top:0, left:0, padding:'0.15rem 0.4rem', background:'rgba(168,85,247,0.4)', borderRadius:'0.75rem 0 0.5rem 0', fontSize:'0.55rem', fontWeight:700, color:'#e9d5ff', letterSpacing:'0.5px'}}>YOU</div>
                              )}
                              <img 
                                src={entry.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                                alt="" 
                                style={{width:44, height:44, borderRadius:'50%', border:'2px solid rgba(168,85,247,0.3)', marginBottom:'0.4rem', objectFit:'cover'}}
                              />
                              <div style={{fontWeight:600, fontSize:'0.8rem', color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                                {entry.display_name || entry.username}
                              </div>
                              <div style={{fontSize:'0.65rem', color:'#6b7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                                {entry.shuffle_username}
                              </div>
                              {entry.loss_streak > 0 && (
                                <div style={{marginTop:'0.25rem', fontSize:'0.6rem', color:'#22c55e', fontWeight:700}} title={`Lost ${entry.loss_streak} raffle${entry.loss_streak === 1 ? '' : 's'} in a row — ${Math.min(1 + entry.loss_streak * 0.25, 3).toFixed(2)}× boost`}>
                                  🍀 {Math.min(1 + entry.loss_streak * 0.25, 3).toFixed(2)}×
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ===== LIVE HUNT: Myrtle Widget + Participants ===== */}
                    {raffleHunt.hunt.status === 'live' && raffleHunt.entries.length > 0 && (
                      <div className="raffle-live-grid">
                        {/* Left: Myrtle Bonus Hunt Tracker */}
                        <div style={{background:'rgba(14,14,18,0.95)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'1rem', overflow:'hidden'}}>
                          <div style={{padding:'0.6rem 1rem', background:'rgba(0,0,0,0.4)', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', gap:'0.5rem'}}>
                            <span>📊</span>
                            <span style={{fontWeight:600, fontSize:'0.9rem', color:'#e2e8f0'}}>Live Hunt Tracker</span>
                            <span style={{marginLeft:'auto', padding:'0.2rem 0.6rem', background:'rgba(239,68,68,0.2)', color:'#ef4444', borderRadius:'20px', fontSize:'0.7rem', fontWeight:700, display:'flex', alignItems:'center', gap:'0.35rem'}}>
                              <span style={{width:7, height:7, background:'#ef4444', borderRadius:'50%', display:'inline-block', animation:'pulse 1.5s infinite'}}></span>
                              LIVE
                            </span>
                          </div>
                          <div className="live-hunt-widget-container predictions-page">
                            <iframe 
                              src="https://myrtlebot.com/widgets/myrtleBonusHunt.html?guildId=370284467845070859"
                              className="myrtle-widget predictions-page"
                              title="Live Bonus Hunt"
                            />
                          </div>
                        </div>

                        {/* Right: Hunt Info + Participants */}
                        <div style={{display:'flex', flexDirection:'column', gap:'0.75rem'}}>
                          {/* Hunt Type Badge */}
                          <div style={{
                            padding:'1rem', borderRadius:'0.75rem', overflow:'hidden',
                            background: raffleHunt.hunt.hunt_type === 'vip' ? 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))' : 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(168,85,247,0.05))',
                            border: raffleHunt.hunt.hunt_type === 'vip' ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(168,85,247,0.3)'
                          }}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.6rem'}}>
                              <span style={{fontSize:'1.3rem'}}>{raffleHunt.hunt.hunt_type === 'vip' ? '⭐' : '👥'}</span>
                              <div style={{flex:1}}>
                                <div style={{fontWeight:700, fontSize:'1rem', color:'#e2e8f0'}}>{raffleHunt.hunt.hunt_type === 'vip' ? 'VIP Hunt' : 'Affiliate Hunt'}</div>
                                <div style={{fontSize:'0.75rem', color:'#9ca3af'}}>{raffleHunt.hunt.name}</div>
                              </div>
                              <span style={{padding:'0.25rem 0.6rem', background:'rgba(239,68,68,0.2)', color:'#ef4444', borderRadius:'20px', fontSize:'0.7rem', fontWeight:700, display:'flex', alignItems:'center', gap:'0.3rem'}}>
                                <span style={{width:7, height:7, background:'#ef4444', borderRadius:'50%', display:'inline-block', animation:'pulse 1.5s infinite'}}></span>
                                LIVE
                              </span>
                            </div>
                            {(raffleHunt.hunt.start_balance || raffleHunt.hunt.end_balance) && (
                              <div style={{display:'flex', gap:'1rem', marginTop:'0.6rem', fontSize:'0.8rem', color:'#9ca3af'}}>
                                {raffleHunt.hunt.start_balance && <span>💰 Start: ${parseFloat(raffleHunt.hunt.start_balance).toLocaleString()}</span>}
                                {raffleHunt.hunt.end_balance && <span>🏁 End: ${parseFloat(raffleHunt.hunt.end_balance).toLocaleString()}</span>}
                              </div>
                            )}
                          </div>

                          {/* Participants List */}
                          <div style={{background:'rgba(14,14,18,0.95)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'0.75rem', overflow:'hidden', display:'flex', flexDirection:'column', maxHeight:'520px'}}>
                            <div style={{padding:'0.6rem 0.75rem', background:'rgba(0,0,0,0.3)', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                              <span style={{fontWeight:600, fontSize:'0.85rem', color:'#e2e8f0'}}>🎮 Participants ({raffleHunt.entries.length})</span>
                            </div>
                            <div style={{overflowY:'auto', padding:'0.5rem', display:'flex', flexDirection:'column', gap:'0.35rem'}}>
                              {raffleHunt.entries.map((entry, idx) => (
                                <div key={entry.id} style={{
                                  display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.45rem 0.5rem',
                                  background: entry.is_host ? 'rgba(168,85,247,0.1)' : user && entry.user_id === user.id ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)',
                                  border: entry.is_host ? '1px solid rgba(168,85,247,0.25)' : user && entry.user_id === user.id ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(255,255,255,0.04)',
                                  borderRadius:'0.5rem'
                                }}>
                                  <span style={{fontSize:'0.65rem', fontWeight:700, color:'#4b5563', width:'18px', textAlign:'center'}}>{entry.is_host ? '👑' : `#${idx + 1}`}</span>
                                  <img src={entry.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} alt="" style={{width:28, height:28, borderRadius:'50%', border: entry.is_host ? '2px solid rgba(168,85,247,0.4)' : '1.5px solid rgba(255,255,255,0.08)', objectFit:'cover'}} />
                                  <div style={{flex:1, minWidth:0}}>
                                    <div style={{fontWeight:600, fontSize:'0.78rem', color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                                      {entry.display_name || entry.username}
                                      {entry.is_host && <span style={{marginLeft:'0.3rem', padding:'0.1rem 0.35rem', background:'rgba(168,85,247,0.2)', borderRadius:'999px', fontSize:'0.55rem', fontWeight:700, color:'#c084fc'}}>HOST</span>}
                                      {user && entry.user_id === user.id && <span style={{marginLeft:'0.3rem', padding:'0.1rem 0.35rem', background:'rgba(34,197,94,0.2)', borderRadius:'999px', fontSize:'0.55rem', fontWeight:700, color:'#22c55e'}}>YOU</span>}
                                    </div>
                                    <div style={{fontSize:'0.6rem', color:'#4b5563'}}>{entry.shuffle_username}</div>
                                  </div>
                                  {entry.payout && <span style={{fontSize:'0.75rem', fontWeight:700, color:'#22c55e'}}>${parseFloat(entry.payout).toLocaleString()}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ===== SELECTED PARTICIPANTS (not live - after roll or completed) ===== */}
                    {raffleHunt.hunt.status !== 'live' && raffleHunt.entries.length > 0 && (
                      <div style={{background:'rgba(14,14,18,0.8)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'1rem', padding:'1.25rem', marginBottom:'1.5rem'}}>
                        <h3 style={{fontSize:'1.1rem', fontWeight:600, marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.5rem', color:'#e2e8f0'}}>
                          🏆 {raffleHunt.hunt.status === 'live' ? 'Hunt Participants' : 'Selected Participants'} ({raffleHunt.entries.length})
                        </h3>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:'0.75rem'}}>
                          {raffleHunt.entries.map((entry, idx) => (
                            <div key={entry.id} style={{
                              position:'relative', overflow:'hidden',
                              background: entry.is_host 
                                ? 'linear-gradient(145deg, rgba(168,85,247,0.18), rgba(168,85,247,0.06))' 
                                : 'linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                              border: entry.is_host 
                                ? '1px solid rgba(168,85,247,0.4)' 
                                : user && entry.user_id === user.id 
                                  ? '1px solid rgba(34,197,94,0.4)' 
                                  : '1px solid rgba(255,255,255,0.06)',
                              borderRadius:'0.75rem', padding:'1rem 0.75rem', textAlign:'center',
                            }}>
                              {/* Host crown or rank number */}
                              <div style={{position:'absolute', top:'0.4rem', left:'0.5rem', fontSize:'0.65rem', fontWeight:700, color: entry.is_host ? '#c084fc' : '#4b5563'}}>
                                {entry.is_host ? '👑' : `#${idx + 1}`}
                              </div>
                              {/* You badge */}
                              {user && entry.user_id === user.id && (
                                <div style={{position:'absolute', top:0, right:0, padding:'0.15rem 0.5rem', background: entry.is_host ? 'rgba(168,85,247,0.5)' : 'rgba(34,197,94,0.4)', borderRadius:'0 0.75rem 0 0.5rem', fontSize:'0.55rem', fontWeight:700, color:'#fff', letterSpacing:'0.5px'}}>YOU</div>
                              )}
                              <img 
                                src={entry.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} 
                                alt="" 
                                style={{
                                  width:52, height:52, borderRadius:'50%', objectFit:'cover', marginBottom:'0.5rem',
                                  border: entry.is_host ? '2.5px solid #a855f7' : '2px solid rgba(255,255,255,0.1)',
                                  boxShadow: entry.is_host ? '0 0 12px rgba(168,85,247,0.3)' : 'none'
                                }}
                              />
                              <div style={{fontWeight:600, fontSize:'0.85rem', color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'0.15rem'}}>
                                {entry.display_name || entry.username}
                              </div>
                              <div style={{fontSize:'0.65rem', color:'#6b7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'0.35rem'}}>
                                🎰 {entry.shuffle_username}
                              </div>
                              {entry.is_host && (
                                <span style={{display:'inline-block', padding:'0.15rem 0.5rem', background:'rgba(168,85,247,0.2)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'999px', fontSize:'0.6rem', fontWeight:700, color:'#c084fc', letterSpacing:'0.3px'}}>HOST</span>
                              )}
                              {entry.payout && (
                                <div style={{marginTop:'0.35rem', padding:'0.2rem 0.5rem', background:'rgba(34,197,94,0.12)', borderRadius:'0.375rem', display:'inline-block'}}>
                                  <span style={{fontSize:'0.8rem', fontWeight:700, color:'#22c55e'}}>${parseFloat(entry.payout).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* How It Works for raffle */}
                    {raffleHunt.hunt.status === 'open' && (
                      <div style={{background:'rgba(14,14,18,0.8)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'1rem', padding:'1.25rem', marginBottom:'1.5rem'}}>
                        <h3 style={{fontSize:'1rem', fontWeight:600, marginBottom:'0.75rem', color:'#e2e8f0'}}>How Raffle Hunts Work</h3>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'0.75rem'}}>
                          {[
                            { icon: '🎟️', title: 'Enter Raffle', desc: raffleHunt.hunt.hunt_type === 'vip' ? 'VIP members ($50k+ weighted wagered) can enter' : 'Shuffle affiliates with $1k+ wagered under guild SNUTZY can enter' },
                            { icon: '🎲', title: 'Winners Rolled', desc: `${raffleHunt.hunt.max_participants || 20} winners randomly selected from all entries` },
                            { icon: '🎰', title: 'Free Equity', desc: 'Selected winners get free equity in the community hunt' },
                            { icon: '💰', title: 'Payouts', desc: 'Winnings distributed equally based on hunt results' }
                          ].map((step, i) => (
                            <div key={i} style={{padding:'0.75rem', background:'rgba(168,85,247,0.06)', borderRadius:'0.5rem', border:'1px solid rgba(168,85,247,0.12)'}}>
                              <div style={{fontSize:'1.25rem', marginBottom:'0.35rem'}}>{step.icon}</div>
                              <div style={{fontWeight:600, fontSize:'0.85rem', color:'#e2e8f0', marginBottom:'0.25rem'}}>{step.title}</div>
                              <div style={{fontSize:'0.75rem', color:'#9ca3af', lineHeight:1.4}}>{step.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bad Luck Protection Info */}
                    <div style={{background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'12px', padding:'1rem 1.25rem', marginBottom:'1.5rem'}}>
                      <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.5rem'}}>
                        <span style={{fontSize:'1.1rem'}}>🍀</span>
                        <span style={{fontSize:'0.9rem', fontWeight:700, color:'#22c55e'}}>Bad Luck Protection</span>
                      </div>
                      <div style={{fontSize:'0.78rem', color:'#a1a1aa', lineHeight:1.6, marginBottom:'0.6rem'}}>
                        Haven't won a giveaway in a while? Your odds automatically increase each time you enter and don't win. The boost stacks until you win, then resets.
                      </div>
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(100px, 1fr))', gap:'0.4rem', fontSize:'0.72rem'}}>
                        {[
                          { losses: '1', boost: '1.25×' },
                          { losses: '2', boost: '1.5×' },
                          { losses: '4', boost: '2×' },
                          { losses: '6', boost: '2.5×' },
                          { losses: '8+', boost: '3× (max)' }
                        ].map((tier, i) => (
                          <div key={i} style={{padding:'0.4rem', background:'rgba(0,0,0,0.3)', borderRadius:'6px', textAlign:'center'}}>
                            <div style={{color:'#22c55e', fontWeight:700}}>{tier.boost}</div>
                            <div style={{color:'#71717a', fontSize:'0.6rem', marginTop:'0.1rem'}}>{tier.losses} loss{tier.losses === '1' ? '' : 'es'}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Announcement note */}
                    {raffleHunt.hunt.status === 'draft' && (
                      <div style={{textAlign:'center', padding:'2rem', background:'rgba(14,14,18,0.8)', borderRadius:'1rem', border:'1px solid rgba(255,255,255,0.08)', marginBottom:'1.5rem'}}>
                        <div style={{fontSize:'2.5rem', marginBottom:'0.75rem'}}>🔜</div>
                        <h3 style={{marginBottom:'0.5rem', color:'#e2e8f0'}}>Raffle Hunt Coming Soon!</h3>
                        <p style={{color:'#9ca3af', fontSize:'0.9rem'}}>This raffle will open soon. Follow announcements on <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#c084fc', textDecoration:'none'}}>Discord</a> and <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#c084fc', textDecoration:'none'}}>twitch.tv/snutzy</a>!</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{textAlign:'center', padding:'3rem', background:'rgba(14,14,18,0.8)', borderRadius:'1rem', border:'1px solid rgba(255,255,255,0.08)'}}>
                    <div style={{fontSize:'3rem', marginBottom:'1rem'}}>🎟️</div>
                    <h3 style={{marginBottom:'0.5rem', color:'#e2e8f0'}}>No Raffle Hunt Active</h3>
                    <p style={{color:'#9ca3af', marginBottom:'1rem'}}>Raffle hunts are announced on <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#c084fc', textDecoration:'none'}}>Discord</a> and on <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#c084fc', textDecoration:'none'}}>twitch.tv/snutzy</a> — stay tuned!</p>
                    <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" style={{display:'inline-block', padding:'0.6rem 1.25rem', background:'rgba(168,85,247,0.3)', border:'1px solid rgba(168,85,247,0.5)', borderRadius:'0.5rem', color:'#c084fc', fontWeight:600, textDecoration:'none', fontSize:'0.85rem'}}>
                      💬 Join Discord
                    </a>
                  </div>
                )}

                {/* ===== RAFFLE HUNT HISTORY ===== */}
                {raffleHistory.hunts?.length > 0 && (
                  <div style={{marginTop:'1.5rem'}}>
                    <h3 style={{fontSize:'1.1rem', fontWeight:600, marginBottom:'1rem', color:'#e2e8f0'}}>📜 Previous Raffle Hunts</h3>
                    <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
                      {raffleHistory.hunts.map(hunt => {
                        const profit = hunt.end_balance && hunt.start_balance ? parseFloat(hunt.end_balance) - parseFloat(hunt.start_balance) : null;
                        return (
                          <div key={hunt.id} style={{background:'rgba(14,14,18,0.8)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'1rem', overflow:'hidden'}}>
                            {/* Hunt Header */}
                            <div style={{padding:'1rem 1.25rem', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem'}}>
                              <div style={{display:'flex', alignItems:'center', gap:'0.6rem', flexWrap:'wrap'}}>
                                <strong style={{color:'#e2e8f0', fontSize:'1rem'}}>{hunt.name}</strong>
                                <span style={{padding:'0.15rem 0.4rem', borderRadius:'4px', fontSize:'0.65rem', fontWeight:700, background: hunt.hunt_type === 'vip' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(168,85,247,0.3)', color: hunt.hunt_type === 'vip' ? '#000' : '#c084fc'}}>{hunt.hunt_type === 'vip' ? '⭐ VIP' : '👥 Affiliate'}</span>
                                <span style={{padding:'0.15rem 0.5rem', borderRadius:'999px', fontSize:'0.65rem', fontWeight:600, background:'rgba(34,197,94,0.15)', color:'#22c55e', border:'1px solid rgba(34,197,94,0.2)'}}>✅ Completed</span>
                              </div>
                              <div style={{display:'flex', gap:'1rem', fontSize:'0.8rem', color:'#9ca3af', flexWrap:'wrap'}}>
                                {hunt.start_balance && <span>💰 ${parseFloat(hunt.start_balance).toLocaleString()}</span>}
                                {hunt.end_balance && <span>🏁 ${parseFloat(hunt.end_balance).toLocaleString()}</span>}
                                {profit !== null && (
                                  <span style={{fontWeight:600, color: profit >= 0 ? '#22c55e' : '#ef4444'}}>
                                    {profit >= 0 ? '📈' : '📉'} {profit >= 0 ? '+' : ''}${profit.toLocaleString()}
                                  </span>
                                )}
                                {parseFloat(hunt.total_payout) > 0 && <span>💸 ${parseFloat(hunt.total_payout).toLocaleString()} paid</span>}
                              </div>
                            </div>
                            {/* Participants Grid */}
                            {hunt.participants?.length > 0 && (
                              <div style={{padding:'1rem 1.25rem'}}>
                                <div style={{fontSize:'0.8rem', color:'#6b7280', marginBottom:'0.6rem'}}>{hunt.participants.length} participants</div>
                                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:'0.5rem'}}>
                                  {hunt.participants.map((p, pIdx) => (
                                    <div key={p.entry_id} style={{
                                      background: p.is_host ? 'rgba(168,85,247,0.1)' : 'rgba(255,255,255,0.02)',
                                      border: p.is_host ? '1px solid rgba(168,85,247,0.25)' : '1px solid rgba(255,255,255,0.05)',
                                      borderRadius:'0.6rem', padding:'0.6rem', textAlign:'center', position:'relative'
                                    }}>
                                      {p.is_host && <div style={{position:'absolute', top:'0.25rem', left:'0.35rem', fontSize:'0.6rem'}}>👑</div>}
                                      <img src={p.profile_image_url || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/ebe4cd89-b4f4-4e6c-87ab-6fa9d4bce61f-profile_image-70x70.png'} alt="" style={{width:36, height:36, borderRadius:'50%', border: p.is_host ? '2px solid rgba(168,85,247,0.4)' : '2px solid rgba(255,255,255,0.08)', objectFit:'cover', marginBottom:'0.3rem'}} />
                                      <div style={{fontWeight:600, fontSize:'0.75rem', color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                                        {p.display_name || p.username}
                                      </div>
                                      {p.payout && (
                                        <div style={{marginTop:'0.2rem', fontSize:'0.7rem', fontWeight:700, color:'#22c55e'}}>${parseFloat(p.payout).toLocaleString()}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Date */}
                            {hunt.completed_at && (
                              <div style={{padding:'0.5rem 1.25rem', borderTop:'1px solid rgba(255,255,255,0.04)', fontSize:'0.7rem', color:'#4b5563'}}>
                                Completed {new Date(hunt.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {raffleHistory.pagination?.hasMore && (
                      <button onClick={() => setRaffleHistoryPage(p => p + 1)} style={{marginTop:'0.75rem', padding:'0.5rem 1rem', background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'0.5rem', color:'#c084fc', cursor:'pointer', fontWeight:600, fontSize:'0.85rem', width:'100%'}}>
                        Load More
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ==================== COMMUNITY HUNT TAB ==================== */}
            {communityHuntTab === 'community' && (
              <>

            {/* Live Community Hunt Total Banner */}
            {communityHunt.hunt && (communityHunt.hunt.status === 'live' || communityHunt.hunt.status === 'setup') && communityHunt.participants.length > 0 && (
              <div style={{
                position:'relative', overflow:'hidden',
                background:'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(16,185,129,0.06))',
                border:'1px solid rgba(34,197,94,0.2)', borderRadius:'14px',
                padding:'1.25rem', marginBottom:'1.25rem', textAlign:'center'
              }}>
                <div style={{position:'absolute', top:'-40%', right:'-5%', width:'160px', height:'160px', background:'radial-gradient(circle, rgba(34,197,94,0.1) 0%, transparent 70%)', borderRadius:'50%', pointerEvents:'none'}} />
                <div style={{position:'absolute', bottom:'-30%', left:'5%', width:'120px', height:'120px', background:'radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)', borderRadius:'50%', pointerEvents:'none'}} />
                
                <div style={{position:'relative', zIndex:1}}>
                  <div style={{fontSize:'0.7rem', color:'#71717a', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'0.3rem'}}>
                    {communityHunt.hunt.status === 'live' ? '🔴 LIVE — Community Hunt Total' : '🔧 Setup — Community Hunt Total'}
                  </div>
                  <div style={{fontSize:'2.5rem', fontWeight:900, fontFamily:'monospace', letterSpacing:'-1px', marginBottom:'0.2rem'}}>
                    <span style={{color:'#22c55e', textShadow:'0 0 30px rgba(34,197,94,0.4)'}}>
                      ${parseFloat(communityHunt.hunt.total_equity || 0).toLocaleString(undefined, {maximumFractionDigits:0})}
                    </span>
                  </div>
                  <div style={{display:'flex', justifyContent:'center', gap:'1.5rem', fontSize:'0.78rem', color:'#a1a1aa'}}>
                    <span>{communityHunt.participants.length} participant{communityHunt.participants.length !== 1 ? 's' : ''}</span>
                    <span>•</span>
                    <span>Start: ${parseFloat(communityHunt.hunt.start_balance || 0).toLocaleString()}</span>
                  </div>
                </div>

                <style>{`
                  @keyframes chPulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                  }
                `}</style>
                {communityHunt.hunt.status === 'live' && (
                  <div style={{
                    position:'absolute', top:'0.6rem', right:'0.8rem',
                    width:'8px', height:'8px', borderRadius:'50%', background:'#22c55e',
                    animation:'chPulse 2s infinite', boxShadow:'0 0 8px rgba(34,197,94,0.6)'
                  }} />
                )}
              </div>
            )}
            
            {/* Current Hunt Section */}
            {communityHunt.hunt ? (
              <div className="community-hunt-active">
                {/* Hunt Header */}
                <div className="ch-hunt-header">
                  <div className="ch-hunt-title">
                    <h2>{communityHunt.hunt.title || 'Community Hunt'}</h2>
                    <span className={`ch-status-badge ${communityHunt.hunt.status}`}>
                      {communityHunt.hunt.status === 'setup' && '🔧 Setup'}
                      {communityHunt.hunt.status === 'live' && '🔴 LIVE'}
                      {communityHunt.hunt.status === 'calculating' && '📊 Calculating'}
                    </span>
                  </div>
                </div>

                {/* Balance Tracker - Two column layout like predictions page */}
                {communityHunt.hunt.status === 'live' && (
                  <div className="ch-live-content">
                    <div className="ch-widget-left">
                      <div className="live-hunt-widget-container predictions-page">
                        <iframe 
                          src="https://myrtlebot.com/widgets/myrtleBonusHunt.html?guildId=370284467845070859"
                          className="myrtle-widget predictions-page"
                          title="Live Bonus Hunt"
                        />
                      </div>
                      
                      {/* Stats under widget */}
                      <div className="predictions-widget-stats">
                        <div className="widget-stat-box">
                          <span className="widget-stat-number">{communityHunt.participants.length}</span>
                          <span className="widget-stat-label">Participants</span>
                        </div>
                        <div className="widget-stat-box">
                          <span className="widget-stat-number">${parseFloat(communityHunt.hunt.start_balance).toLocaleString()}</span>
                          <span className="widget-stat-label">Starting Balance</span>
                        </div>
                        <div className="widget-stat-box">
                          <span className="widget-stat-number">${parseFloat(communityHunt.hunt.total_equity).toLocaleString()}</span>
                          <span className="widget-stat-label">Total Equity</span>
                        </div>
                      </div>
                      
                      {/* Buttons under stats */}
                      <div className="predictions-widget-buttons">
                        <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" className="widget-stream-btn discord">
                          🎙️ Join Discord Voice Chat
                        </a>
                        <button 
                          className="widget-predict-btn"
                          onClick={() => document.getElementById('ch-participants-section')?.scrollIntoView({ behavior: 'smooth' })}
                        >
                          <span>View Participants</span>
                          <span className="scroll-arrow">↓</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="live-hunt-stats-panel enhanced predictions-page">
                      <div className="stats-panel-header">
                        <h3>💰 Community Hunt Info</h3>
                        <span className="live-badge-small">🔴 LIVE</span>
                      </div>
                      
                      <div className="ch-info-section">
                        <div className="ch-info-row">
                          <span className="ch-info-label">Hunt Title</span>
                          <span className="ch-info-value">{communityHunt.hunt.title || 'Community Hunt'}</span>
                        </div>
                        <div className="ch-info-row">
                          <span className="ch-info-label">Start Balance</span>
                          <span className="ch-info-value highlight">${parseFloat(communityHunt.hunt.start_balance).toLocaleString()}</span>
                        </div>
                        <div className="ch-info-row">
                          <span className="ch-info-label">Total Equity</span>
                          <span className="ch-info-value">${parseFloat(communityHunt.hunt.total_equity).toLocaleString()}</span>
                        </div>
                        <div className="ch-info-row">
                          <span className="ch-info-label">Participants</span>
                          <span className="ch-info-value">{communityHunt.participants.length}</span>
                        </div>
                        {communityHunt.hunt.end_balance && (
                          <div className="ch-info-row end-balance">
                            <span className="ch-info-label">End Balance</span>
                            <span className="ch-info-value green">${parseFloat(communityHunt.hunt.end_balance).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="ch-how-it-works">
                        <h4>How It Works</h4>
                        <ol>
                          <li>Community members pool equity together</li>
                          <li>Watch live in Discord voice chat</li>
                          <li>Payouts distributed based on equity %</li>
                        </ol>
                      </div>
                      
                      <div className="ch-hunt-status-indicator">
                        <span className="status-dot live"></span>
                        <span>Hunt in Progress</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Controls */}
                {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && (
                  <div className="ch-admin-section">
                    <div className="ch-admin-header">
                      <h3>⚙️ Admin Controls</h3>
                      <div className="ch-admin-tabs">
                        <button 
                          className={communityHuntAdminTab === 'participants' ? 'active' : ''}
                          onClick={() => setCommunityHuntAdminTab('participants')}
                        >
                          Participants
                        </button>
                        <button 
                          className={communityHuntAdminTab === 'settings' ? 'active' : ''}
                          onClick={() => setCommunityHuntAdminTab('settings')}
                        >
                          Settings
                        </button>
                      </div>
                    </div>

                    {communityHuntAdminTab === 'settings' && (
                      <div className="ch-admin-settings">
                        <div className="ch-setting-row">
                          <label>Title:</label>
                          <input 
                            type="text" 
                            defaultValue={communityHunt.hunt.title}
                            onBlur={(e) => updateCommunityHunt({ title: e.target.value })}
                            placeholder="Community Hunt"
                          />
                          <span className="ch-setting-hint">Press Tab or click away to save</span>
                        </div>
                        <div className="ch-setting-row">
                          <label>Start Balance:</label>
                          <div className="ch-input-group large">
                            <span className="ch-input-prefix">$</span>
                            <input 
                              type="number" 
                              defaultValue={communityHunt.hunt.start_balance}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) updateCommunityHunt({ startBalance: val });
                              }}
                              placeholder="0"
                            />
                          </div>
                          <span className="ch-setting-hint">Edit and click away to save</span>
                        </div>
                        <div className="ch-setting-row">
                          <label>Total Equity:</label>
                          <div className="ch-readonly-value">${parseFloat(communityHunt.hunt.total_equity || 0).toLocaleString()}</div>
                          <span className="ch-setting-hint">Sum of all participant equities</span>
                        </div>
                        <div className="ch-setting-row">
                          <label>Status:</label>
                          <div className="ch-status-buttons">
                            <button 
                              className={communityHunt.hunt.status === 'setup' ? 'active' : ''}
                              onClick={() => updateCommunityHunt({ status: 'setup' })}
                            >
                              {communityHunt.hunt.status === 'live' ? '⏸️ Back to Setup' : '🔧 Setup'}
                            </button>
                            <button 
                              className={`live ${communityHunt.hunt.status === 'live' ? 'active' : ''}`}
                              onClick={() => updateCommunityHunt({ status: 'live' })}
                            >
                              🔴 Go Live
                            </button>
                          </div>
                          {communityHunt.hunt.status === 'live' && (
                            <span className="ch-setting-hint">Hunt is LIVE - tracker is visible to everyone</span>
                          )}
                        </div>
                        <div className="ch-setting-row">
                          <label>End Balance:</label>
                          <div className="ch-input-group large">
                            <span className="ch-input-prefix">$</span>
                            <input 
                              type="number" 
                              id="ch-end-balance"
                              defaultValue={communityHunt.hunt.end_balance || ''}
                              placeholder="Enter final balance..."
                            />
                          </div>
                          <button 
                            className="ch-calculate-btn"
                            onClick={() => {
                              const val = document.getElementById('ch-end-balance').value;
                              if (val) {
                                if (window.confirm(`Calculate payouts based on end balance of $${parseFloat(val).toLocaleString()}?\n\nThis will distribute the balance proportionally based on each participant's equity percentage.`)) {
                                  calculateCommunityHuntPayouts(val);
                                }
                              } else {
                                alert('Please enter an end balance first');
                              }
                            }}
                          >
                            📊 Calculate Payouts
                          </button>
                        </div>
                        <div className="ch-setting-row danger">
                          <button className="ch-end-btn" onClick={() => {
                            if (window.confirm('End this community hunt?\n\nThis will move it to the history section.')) {
                              updateCommunityHunt({ status: 'ended' });
                            }
                          }}>
                            🏁 End Community Hunt
                          </button>
                          <button className="ch-delete-btn" onClick={deleteCommunityHunt}>
                            🗑️ Delete Hunt
                          </button>
                        </div>
                      </div>
                    )}

                    {communityHuntAdminTab === 'participants' && (
                      <div className="ch-admin-participants">
                        <button className="ch-add-participant-btn" onClick={() => setShowAddParticipantModal(true)}>
                          ➕ Add Participant
                        </button>
                        <div className="ch-participant-list-admin">
                          <div className="ch-participant-header-row">
                            <span className="ch-header-user">User</span>
                            <span className="ch-header-equity">Equity ($)</span>
                            <span className="ch-header-payout">Payout ($)</span>
                            <span className="ch-header-status">Status</span>
                            <span className="ch-header-actions">Actions</span>
                          </div>
                          {communityHunt.participants.map(p => (
                            <div key={p.id} className="ch-participant-admin-row">
                              <div className="ch-participant-info">
                                {p.profile_image_url ? (
                                  <img src={p.profile_image_url} alt="" className="ch-avatar" />
                                ) : (
                                  <div className="ch-avatar-placeholder">{p.display_name[0]}</div>
                                )}
                                <div className="ch-user-details">
                                  <span className="ch-name">{p.display_name}</span>
                                  <span className="ch-identifier">{p.identifier_type}: {p.identifier}</span>
                                </div>
                              </div>
                              <div className="ch-participant-values">
                                <div className="ch-input-group">
                                  <span className="ch-input-prefix">$</span>
                                  <input 
                                    type="number" 
                                    defaultValue={p.equity}
                                    onBlur={(e) => {
                                      const val = parseFloat(e.target.value);
                                      if (!isNaN(val) && val !== parseFloat(p.equity)) {
                                        updateCommunityHuntParticipant(p.id, { equity: val });
                                      }
                                    }}
                                    className="ch-equity-input"
                                    placeholder="0"
                                  />
                                </div>
                                <div className="ch-input-group">
                                  <span className="ch-input-prefix">$</span>
                                  <input 
                                    type="number" 
                                    defaultValue={p.payout || ''}
                                    onBlur={(e) => {
                                      const val = e.target.value ? parseFloat(e.target.value) : null;
                                      updateCommunityHuntParticipant(p.id, { payout: val });
                                    }}
                                    className="ch-payout-input"
                                    placeholder="—"
                                  />
                                </div>
                                <select 
                                  defaultValue={p.payout_status}
                                  onChange={(e) => updateCommunityHuntParticipant(p.id, { payoutStatus: e.target.value })}
                                  className="ch-status-select"
                                >
                                  <option value="pending">⏳ Pending</option>
                                  <option value="paid">✅ Paid</option>
                                </select>
                                <button 
                                  className="ch-remove-btn" 
                                  onClick={() => removeCommunityHuntParticipant(p.id)}
                                  title="Remove participant"
                                >
                                  🗑️
                                </button>
                              </div>
                              {p.profit_loss !== null && (
                                <div className={`ch-pl-badge ${p.profit_loss >= 0 ? 'profit' : 'loss'}`}>
                                  {p.profit_loss >= 0 ? '+' : ''}{parseFloat(p.profit_loss).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Participant Cards - Public View */}
                <div className="ch-participants-section" id="ch-participants-section">
                  <h3>👥 Participants ({communityHunt.participants.length})</h3>
                  <div className="ch-participant-grid">
                    {communityHunt.participants.map((p, idx) => {
                      const equityPercent = communityHunt.hunt.total_equity > 0 
                        ? ((parseFloat(p.equity) / parseFloat(communityHunt.hunt.total_equity)) * 100).toFixed(1)
                        : 0;
                      return (
                        <div key={p.id} className={`ch-participant-card ${p.profit_loss > 0 ? 'profit' : p.profit_loss < 0 ? 'loss' : ''}`}>
                          <div className="ch-card-rank">#{idx + 1}</div>
                          <div className="ch-card-avatar">
                            {p.profile_image_url ? (
                              <img src={p.profile_image_url} alt="" />
                            ) : (
                              <div className="ch-avatar-placeholder large">{p.display_name[0]}</div>
                            )}
                          </div>
                          <div className="ch-card-name">{p.display_name}</div>
                          <div className="ch-card-equity">
                            <span className="label">Equity</span>
                            <span className="value">${parseFloat(p.equity).toLocaleString()}</span>
                          </div>
                          <div className="ch-card-percent">
                            <span className="percent-value">{equityPercent}%</span>
                            <div className="percent-bar">
                              <div className="percent-fill" style={{ width: `${Math.min(equityPercent, 100)}%` }}></div>
                            </div>
                          </div>
                          {p.payout !== null && (
                            <>
                              <div className="ch-card-payout">
                                <span className="label">Payout</span>
                                <span className="value">${parseFloat(p.payout).toLocaleString()}</span>
                              </div>
                              <div className={`ch-card-profit ${p.profit_loss >= 0 ? 'positive' : 'negative'}`}>
                                <span className="label">P/L</span>
                                <span className="value">
                                  {p.profit_loss >= 0 ? '+' : ''}{parseFloat(p.profit_loss).toLocaleString()}
                                </span>
                              </div>
                            </>
                          )}
                          {p.payout_status === 'paid' && (
                            <div className="ch-card-paid-badge">✅ Paid</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="ch-no-hunt">
                <div className="ch-no-hunt-icon">🤝</div>
                <h2>No Active Community Hunt</h2>
                <p>There isn't a community hunt running right now. Check back later or join our <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#c084fc', textDecoration:'none'}}>Discord</a> and watch <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#c084fc', textDecoration:'none'}}>twitch.tv/snutzy</a> for announcements!</p>
                <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" className="ch-discord-btn">
                  Join Discord for Updates
                </a>
                
                {/* Admin: Create New Hunt */}
                {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && (
                  <div className="ch-admin-create">
                    <h3>⚙️ Admin: Create New Hunt</h3>
                    <div className="ch-create-form">
                      <input type="text" id="new-ch-title" placeholder="Hunt title (optional)" />
                      <input type="number" id="new-ch-balance" placeholder="Starting balance" />
                      <button onClick={async () => {
                        const title = document.getElementById('new-ch-title').value;
                        const balance = document.getElementById('new-ch-balance').value;
                        if (!balance) { alert('Enter starting balance'); return; }
                        const result = await createCommunityHunt(title, parseFloat(balance));
                        if (!result.success) alert(result.error);
                      }}>
                        Create Community Hunt
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Previous Community Hunts */}
            {communityHuntHistory.hunts.length > 0 && (
              <div className="ch-history-section">
                <h2>📜 Previous Community Hunts</h2>
                {communityHuntHistory.hunts.map(hunt => (
                  <div key={hunt.id} className="ch-history-card">
                    <div className="ch-history-header">
                      <h3>{hunt.title || 'Community Hunt'}</h3>
                      <span className="ch-history-date">
                        {hunt.ended_at && new Date(hunt.ended_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="ch-history-stats">
                      <div className="ch-history-stat">
                        <span className="label">Start</span>
                        <span className="value">${parseFloat(hunt.start_balance).toLocaleString()}</span>
                      </div>
                      <div className="ch-history-stat">
                        <span className="label">End</span>
                        <span className="value">${parseFloat(hunt.end_balance).toLocaleString()}</span>
                      </div>
                      <div className={`ch-history-stat ${parseFloat(hunt.end_balance) >= parseFloat(hunt.start_balance) ? 'profit' : 'loss'}`}>
                        <span className="label">Result</span>
                        <span className="value">
                          {parseFloat(hunt.end_balance) >= parseFloat(hunt.start_balance) ? '+' : ''}
                          ${(parseFloat(hunt.end_balance) - parseFloat(hunt.start_balance)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="ch-history-participants">
                      {hunt.participants.slice(0, 6).map((p, idx) => (
                        <div key={p.id} className="ch-history-participant">
                          {p.profile_image_url ? (
                            <img src={p.profile_image_url} alt="" className="ch-mini-avatar" title={p.display_name} />
                          ) : (
                            <div className="ch-mini-avatar-placeholder" title={p.display_name}>{p.display_name[0]}</div>
                          )}
                        </div>
                      ))}
                      {hunt.participants.length > 6 && (
                        <span className="ch-more-participants">+{hunt.participants.length - 6} more</span>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Pagination */}
                {communityHuntHistory.pagination.totalPages > 1 && (
                  <div className="ch-pagination">
                    <button 
                      disabled={communityHuntHistoryPage <= 1}
                      onClick={() => setCommunityHuntHistoryPage(p => p - 1)}
                    >
                      ← Previous
                    </button>
                    <span>Page {communityHuntHistoryPage} of {communityHuntHistory.pagination.totalPages}</span>
                    <button 
                      disabled={communityHuntHistoryPage >= communityHuntHistory.pagination.totalPages}
                      onClick={() => setCommunityHuntHistoryPage(p => p + 1)}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Add Participant Modal */}
            {showAddParticipantModal && (
              <div className="modal-overlay" onClick={() => setShowAddParticipantModal(false)}>
                <div className="modal-content ch-modal" onClick={e => e.stopPropagation()}>
                  <h2>Add Participant</h2>
                  <div className="ch-modal-form">
                    <div className="form-row">
                      <label>Username Type:</label>
                      <select 
                        value={newParticipant.identifierType}
                        onChange={e => setNewParticipant({...newParticipant, identifierType: e.target.value})}
                      >
                        <option value="twitch">Twitch Username</option>
                        <option value="shuffle">Shuffle Username</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="form-row">
                      <label>Username:</label>
                      <input 
                        type="text"
                        value={newParticipant.identifier}
                        onChange={e => setNewParticipant({...newParticipant, identifier: e.target.value})}
                        placeholder="Enter username..."
                      />
                    </div>
                    <div className="form-row">
                      <label>Display Name (optional):</label>
                      <input 
                        type="text"
                        value={newParticipant.displayName}
                        onChange={e => setNewParticipant({...newParticipant, displayName: e.target.value})}
                        placeholder="Leave blank to auto-detect"
                      />
                    </div>
                    <div className="form-row">
                      <label>Equity Amount:</label>
                      <input 
                        type="number"
                        value={newParticipant.equity}
                        onChange={e => setNewParticipant({...newParticipant, equity: e.target.value})}
                        placeholder="e.g. 500"
                      />
                    </div>
                    <div className="modal-buttons">
                      <button className="cancel-btn" onClick={() => setShowAddParticipantModal(false)}>Cancel</button>
                      <button className="confirm-btn" onClick={addCommunityHuntParticipant}>Add Participant</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {activePage === 'bonushunts' && (
          <div className="bonushunts-container">
            <h1 className="simple-page-title">Bonus Hunts</h1>
            <p className="bonushunts-intro">
              Track live bonus hunts, stats, and history from Snutzy's streams!
            </p>

            {/* Stats Cards Row */}
            <div className="bonushunts-stats-row">
              <div className="bonushunts-stat-card biggest-multi">
                <div className="stat-icon">🏆</div>
                <div className="stat-content">
                  <div className="stat-label">Biggest Multi (Last 25 Hunts)</div>
                  <div className="stat-value">{bonusHuntStats.biggestMulti.multi}x</div>
                  <div className="stat-slot">{bonusHuntStats.biggestMulti.slot}</div>
                </div>
              </div>
              
              <div className="bonushunts-stat-card hot-slot">
                <div className="stat-icon">🔥</div>
                <div className="stat-content">
                  <div className="stat-label">Hot Slot</div>
                  <div className="stat-value">{bonusHuntStats.hotSlot.avgMulti}x avg</div>
                  <div className="stat-slot">{bonusHuntStats.hotSlot.slot}</div>
                  <div className="stat-appearances">{bonusHuntStats.hotSlot.appearances || 0} appearances</div>
                </div>
              </div>
              
              <div className="bonushunts-stat-card cold-slot">
                <div className="stat-icon">🥶</div>
                <div className="stat-content">
                  <div className="stat-label">Cold Slot</div>
                  <div className="stat-value">{bonusHuntStats.coldSlot.avgMulti}x avg</div>
                  <div className="stat-slot">{bonusHuntStats.coldSlot.slot}</div>
                  <div className="stat-appearances">{bonusHuntStats.coldSlot.appearances || 0} appearances</div>
                </div>
              </div>
            </div>

            {/* Hot / cold leaderboards — the backend ranks every slot with 3+
                appearances; these panels expose both ends of that ranking. */}
            {(bonusHuntStats.hotSlots?.length > 0 || bonusHuntStats.coldSlots?.length > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem', margin: '1.5rem 0' }}>
                {[
                  { key: 'hot', title: '🔥 Hottest slots', rows: bonusHuntStats.hotSlots || [], accent: '#ef4444', tint: 'rgba(239,68,68,0.09)', border: 'rgba(239,68,68,0.28)' },
                  { key: 'cold', title: '🥶 Coldest slots', rows: bonusHuntStats.coldSlots || [], accent: '#3b82f6', tint: 'rgba(59,130,246,0.09)', border: 'rgba(59,130,246,0.28)' }
                ].map(panel => {
                  const open = huntBoardOpen === panel.key;
                  const shown = open ? panel.rows : panel.rows.slice(0, 3);
                  return (
                    <div key={panel.key} style={{
                      background: `linear-gradient(180deg, ${panel.tint}, rgba(255,255,255,0.02))`,
                      border: `1px solid ${panel.border}`, borderRadius: '16px', overflow: 'hidden'
                    }}>
                      <div style={{ padding: '0.9rem 1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                        <span style={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>{panel.title}</span>
                        <span style={{ fontSize: '0.7rem', color: '#8b8b96' }}>avg multi · 3+ hits</span>
                      </div>
                      <div>
                        {shown.map((s, i) => {
                          const art = findSlotArt(allSlots, s.name);
                          const rank = panel.key === 'hot' ? i + 1 : i + 1;
                          return (
                            <div key={s.name} style={{
                              display: 'flex', alignItems: 'center', gap: '0.75rem',
                              padding: '0.6rem 1.1rem', borderTop: '1px solid rgba(255,255,255,0.05)'
                            }}>
                              <span style={{ width: '18px', fontSize: '0.78rem', fontWeight: 800, color: '#71717a', flexShrink: 0 }}>{rank}</span>
                              {art?.image ? (
                                <img src={art.image} alt="" loading="lazy" style={{ width: '46px', height: '34px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, border: '1px solid rgba(255,255,255,0.1)' }} />
                              ) : (
                                <span style={{ width: '46px', height: '34px', borderRadius: '6px', flexShrink: 0, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>🎰</span>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {art?.name || s.name}
                                </div>
                                <div style={{ fontSize: '0.68rem', color: '#71717a' }}>
                                  {s.appearances} hits{s.bestMulti ? ` · best ${Math.round(s.bestMulti)}x` : ''}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontWeight: 900, color: panel.accent, fontSize: '1rem' }}>{Math.round(s.avgMulti)}x</div>
                                <div style={{ fontSize: '0.62rem', color: '#52525b' }}>avg</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {panel.rows.length > 3 && (
                        <button
                          onClick={() => setHuntBoardOpen(open ? null : panel.key)}
                          style={{
                            width: '100%', padding: '0.6rem', border: 'none', cursor: 'pointer',
                            borderTop: '1px solid rgba(255,255,255,0.06)',
                            background: 'rgba(255,255,255,0.03)', color: panel.accent,
                            fontWeight: 700, fontSize: '0.75rem'
                          }}>
                          {open ? 'Show less ▴' : `Show top ${panel.rows.length} ▾`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Live Bonus Hunt Widget */}
            <div className="live-hunt-section">
              <div className="live-hunt-header">
                <h2>
                  {isStreamerLive ? (
                    <>
                      <span className="live-indicator"></span>
                      Live Bonus Hunt
                    </>
                  ) : (
                    <>
                      <span className="paused-indicator">⏸️</span>
                      Bonus Hunt (Offline)
                    </>
                  )}
                </h2>
                {isStreamerLive && (
                  <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="watch-live-btn">
                    Watch Live →
                  </a>
                )}
              </div>
              
              {isStreamerLive ? (
                <div className="live-hunt-content">
                  <div className="live-hunt-widget-container">
                    <iframe 
                      src="https://myrtlebot.com/widgets/myrtleBonusHunt.html?guildId=370284467845070859"
                      className="myrtle-widget"
                      title="Live Bonus Hunt"
                    />
                  </div>
                  
                  <div className="live-hunt-stats-panel">
                    <h3>📊 Hunt Statistics</h3>
                    
                    {bonusHuntStats.activeHunt ? (
                      <>
                        <div className="live-stat-row">
                          <span className="live-stat-label">Starting Balance</span>
                          <span className="live-stat-value">${bonusHuntStats.activeHunt.infoStartCost?.toLocaleString() || 'N/A'}</span>
                        </div>
                        
                        <div className="live-stat-row">
                          <span className="live-stat-label">Total Bonuses</span>
                          <span className="live-stat-value">{bonusHuntStats.activeHunt.bonusesList?.length || bonusHuntStats.activeHunt.bonusCountTotal || 'N/A'}</span>
                        </div>
                        
                        <div className="live-stat-row">
                          <span className="live-stat-label">Avg Bet Size</span>
                          <span className="live-stat-value">
                            {(() => {
                              const bonuses = bonusHuntStats.activeHunt.bonusesList;
                              if (bonuses && bonuses.length > 0) {
                                const totalBet = bonuses.reduce((sum, b) => sum + (b.betSize || 0), 0);
                                return `$${(totalBet / bonuses.length).toFixed(2)}`;
                              }
                              return 'N/A';
                            })()}
                          </span>
                        </div>
                        
                        <div className="live-hunt-cta">
                          <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="watch-stream-btn">
                            📺 Watch Stream
                          </a>
                        </div>
                      </>
                    ) : (
                      <div className="no-active-hunt-stats">
                        <p>Hunt data loading...</p>
                        <p className="hint">Stats will appear when a hunt is active</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="live-hunt-widget-container offline">
                  <div className="offline-message">
                    <div className="offline-icon">📺</div>
                    <h3>Stream is currently offline</h3>
                    <p>The live bonus hunt tracker will appear here when Snutzy goes live!</p>
                    <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="follow-btn">
                      Follow on Twitch to get notified
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Prediction Game Promo */}
            <div className="prediction-promo-section">
              <div className="prediction-promo-content">
                <div className="prediction-promo-icon">🎯</div>
                <div className="prediction-promo-text">
                  <h3>Guess the Ending Balance!</h3>
                  <p>Predict the final result of the bonus hunt and win prizes. Top 3 closest guesses win!</p>
                </div>
                <button className="prediction-promo-btn" onClick={() => navigateTo('predictions')}>
                  Make a Prediction →
                </button>
              </div>
            </div>

            {/* Hunt History */}
            <div className="hunt-history-section">
              <h2>Recent Bonus Hunts</h2>
              <p className="hunt-history-subtitle">
                Showing {Math.min((huntHistoryPage - 1) * HUNTS_PER_PAGE + 1, bonusHuntStats.huntHistory.length)}-{Math.min(huntHistoryPage * HUNTS_PER_PAGE, bonusHuntStats.huntHistory.length)} of {bonusHuntStats.huntHistory.length} hunts
              </p>
              
              <div className="hunt-history-list">
                {bonusHuntStats.huntHistory.length > 0 ? (
                  bonusHuntStats.huntHistory
                    .slice((huntHistoryPage - 1) * HUNTS_PER_PAGE, huntHistoryPage * HUNTS_PER_PAGE)
                    .map((hunt, idx) => {
                      const actualIdx = (huntHistoryPage - 1) * HUNTS_PER_PAGE + idx;
                      return (
                        <div key={actualIdx} className={`hunt-history-card ${hunt.profit >= 0 ? 'profit' : 'loss'}`}>
                          <div className="hunt-card-header">
                            <span className="hunt-number">Bonus Hunt #{hunt.huntNumber || (bonusHuntStats.total - actualIdx)}</span>
                            <span className="hunt-date">{hunt.date}</span>
                            <span className={`hunt-result-badge ${hunt.profit >= 0 ? 'profit' : 'loss'}`}>
                              {hunt.profit >= 0 ? '+' : ''}{hunt.profitPercent}%
                            </span>
                          </div>
                          
                          <div className="hunt-card-stats">
                            <div className="hunt-stat">
                              <span className="hunt-stat-label">Start</span>
                              <span className="hunt-stat-value">${hunt.startBalance?.toLocaleString()}</span>
                            </div>
                            <div className="hunt-stat">
                              <span className="hunt-stat-label">Result</span>
                              <span className="hunt-stat-value">${hunt.endBalance?.toLocaleString()}</span>
                            </div>
                            <div className="hunt-stat pl-stat">
                              <span className="hunt-stat-label">P/L</span>
                              <span className={`hunt-stat-value ${hunt.profit >= 0 ? 'profit' : 'loss'}`}>
                                {hunt.profit >= 0 ? '+$' : '-$'}{Math.abs(hunt.profit)?.toLocaleString()}
                              </span>
                            </div>
                            <div className="hunt-stat">
                              <span className="hunt-stat-label">Bonuses</span>
                              <span className="hunt-stat-value">{hunt.bonusCount || 'N/A'}</span>
                            </div>
                          </div>
                          
                          {hunt.bestSlot && hunt.bestSlot !== 'N/A' && (
                            <div className="hunt-card-best">
                              <span className="best-label">🏆 Best Hit:</span>
                              <span className="best-slot-name">{hunt.bestSlot}</span>
                              {hunt.bestBetSize > 0 && hunt.bestPayout > 0 && (
                                <span className="best-hit-details">
                                  (${hunt.bestBetSize?.toFixed(2)} → ${hunt.bestPayout?.toLocaleString()})
                                </span>
                              )}
                              <span className="best-multi-value">{hunt.bestMulti > 0 ? `${hunt.bestMulti.toLocaleString()}x` : ''}</span>
                            </div>
                          )}
                        </div>
                      );
                    })
                ) : (
                  <div className="hunt-history-empty">
                    <p>Loading hunt history...</p>
                    <p className="hint">Fetching data from Myrtle API...</p>
                  </div>
                )}
              </div>
              
              {/* Pagination */}
              {bonusHuntStats.huntHistory.length > HUNTS_PER_PAGE && (
                <div className="hunt-pagination">
                  <button 
                    className="pagination-btn"
                    onClick={() => setHuntHistoryPage(p => Math.max(1, p - 1))}
                    disabled={huntHistoryPage === 1}
                  >
                    ← Previous
                  </button>
                  
                  <div className="pagination-pages">
                    {Array.from({ length: Math.ceil(bonusHuntStats.huntHistory.length / HUNTS_PER_PAGE) }, (_, i) => (
                      <button
                        key={i + 1}
                        className={`pagination-page ${huntHistoryPage === i + 1 ? 'active' : ''}`}
                        onClick={() => setHuntHistoryPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  
                  <button 
                    className="pagination-btn"
                    onClick={() => setHuntHistoryPage(p => Math.min(Math.ceil(bonusHuntStats.huntHistory.length / HUNTS_PER_PAGE), p + 1))}
                    disabled={huntHistoryPage >= Math.ceil(bonusHuntStats.huntHistory.length / HUNTS_PER_PAGE)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activePage === 'rewards' && (() => {
          // Every milestone pays its own reward and they stack, so a tier's running
          // total = that tier + every tier below it. Computed from wagerTiers so the
          // legacy (pre-weighted) amounts stay right too.
          const hasStats = !!(user && userWagerStats && !userWagerStats.noShuffleLinked);
          // Weighted periods: this period's weighted wager (resets monthly). Legacy: all-time wager.
          const userWager = effectiveWeighted
            ? (userWagerStats?.periodWagered || 0)
            : (userWagerStats?.totalWagered || 0);
          const fmtMoney = (n) => `$${Math.round(n).toLocaleString()}`;
          const fmtWager = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(2)}M` : n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${Math.round(n)}`;
          let runningTotal = 0;
          const tiers = wagerTiers.map((tier) => {
            runningTotal += tier.reward;
            // A ticket that's open and covers this tier = claim submitted, payout pending
            const pendingTicket = wagerTicketInfo.tickets.find(t => t.status === 'open' && t.tier_names.split(',').includes(tier.name));
            return {
              ...tier,
              cumulative: runningTotal,
              isUnlocked: userWager >= tier.requirement,
              isClaimed: userWagerClaims.some(c => c.tier_name === tier.name),
              pendingTicket,
              ticketUrl: pendingTicket?.discord_channel_id && wagerTicketInfo.discordGuildId
                ? `https://discord.com/channels/${wagerTicketInfo.discordGuildId}/${pendingTicket.discord_channel_id}` : null
            };
          });
          const milestonesTotal = runningTotal;
          const nextTier = tiers.find(t => !t.isUnlocked) || null;
          const exampleIdx = Math.min(3, tiers.length - 1);
          const sumRewards = (list) => list.reduce((s, t) => s + t.reward, 0);
          const unlockedTiers = tiers.filter(t => t.isUnlocked);
          const claimableAmount = sumRewards(unlockedTiers.filter(t => !t.isClaimed && !t.pendingTicket));
          const pendingTiers = unlockedTiers.filter(t => !t.isClaimed && t.pendingTicket);
          const pendingAmount = sumRewards(pendingTiers);
          const pendingTicketUrl = pendingTiers.find(t => t.ticketUrl)?.ticketUrl || null;
          const paidAmount = sumRewards(unlockedTiers.filter(t => t.isClaimed));
          // Guests with nothing unlocked have no status to show — drop the column
          const showStatus = hasStats || unlockedTiers.length > 0;

          // Grind Area: a flat reward for every $250K wagered between $1M and $10M
          const grindThreshold = 1000000;
          const grindStep = 250000;
          const grindReward = effectiveWeighted ? 500 : 250;
          const eternalThreshold = 10000000;
          const grindMaxSteps = Math.floor((eternalThreshold - grindThreshold) / grindStep);
          const grindProgress = userWager >= grindThreshold ? userWager - grindThreshold : 0;
          const grindEarnedSteps = Math.min(Math.floor(grindProgress / grindStep), grindMaxSteps);
          const grindEarned = grindEarnedSteps * grindReward;
          // Count how many grind tiers admin has marked as claimed
          const grindClaimsCount = userWagerClaims.filter(c => c.tier_name.startsWith('Grind_')).length;
          const nextGrindAt = grindThreshold + ((grindEarnedSteps + 1) * grindStep);
          const progressToNextGrind = ((grindProgress % grindStep) / grindStep) * 100;
          const earnedTotal = sumRewards(unlockedTiers) + grindEarned;

          const resetBlock = isWeightedPeriod && (
            <div className="wr-reset">
              <div className="wr-reset-label">Resets in</div>
              <div className="wr-countdown" role="timer" aria-label={`Resets in ${countdown.days} days, ${countdown.hours} hours, ${countdown.minutes} minutes`}>
                {[
                  { val: countdown.days, label: 'd' },
                  { val: countdown.hours, label: 'h' },
                  { val: countdown.minutes, label: 'm' },
                  { val: countdown.seconds, label: 's' }
                ].map((t, i) => (
                  <div key={i} className="wr-countdown-cell" aria-hidden="true">
                    <span>{String(t.val).padStart(2, '0')}</span><small>{t.label}</small>
                  </div>
                ))}
              </div>
            </div>
          );

          return (
          <div className="rewards-container">
            <h1 className="simple-page-title">Wager Rewards</h1>
            {publicPayoutStats?.wagerRewards > 0 && (
              <div style={{display:'flex', justifyContent:'center', margin:'0.5rem 0 1.5rem'}}>
                <OdometerCounter value={publicPayoutStats.wagerRewards} label="Total Wager Rewards Paid" size="small" />
              </div>
            )}
            <p className="rewards-intro">
              Wager on <strong style={{ color: '#a855f7' }}>Shuffle</strong> under guild <strong style={{ color: '#a855f7' }}>SNUTZY</strong> and get paid at every milestone. Each one pays its own cash reward, and they all stack.
            </p>

            {hasStats ? (
              /* ===== YOUR PERIOD: earned so far, next payout, one claim ===== */
              <div className="wr-panel">
                <div className="wr-panel-top">
                  <div className="wr-who">
                    {user.profileImage && <img src={user.profileImage} alt="" />}
                    <div style={{minWidth:0}}>
                      <div className="wr-eyebrow">[ {effectiveWeighted ? 'Your period' : 'Your progress'} ]</div>
                      <div className="wr-who-name">{user.displayName}{userWagerStats.shuffleUsername && <span> · 🎰 {userWagerStats.shuffleUsername}</span>}</div>
                      {userWagerStats.leaderboardRank && (
                        <button className="wr-rank" onClick={() => navigateTo('leaderboard')} title="See the monthly leaderboard">
                          🏆 #{userWagerStats.leaderboardRank} on the monthly leaderboard
                        </button>
                      )}
                    </div>
                  </div>
                  {resetBlock}
                </div>

                <div className="wr-stats">
                  <div className="wr-stat">
                    <div className="wr-stat-label">{effectiveWeighted ? 'Earned this period' : 'Earned so far'}</div>
                    <div className="wr-stat-value wr-gold">{fmtMoney(earnedTotal)}</div>
                    <div className="wr-stat-sub">
                      {unlockedTiers.length} of {tiers.length} milestones{grindEarned > 0 ? ` + ${fmtMoney(grindEarned)} Grind Area` : ''}
                    </div>
                  </div>
                  <div className="wr-stat">
                    <div className="wr-stat-label">{effectiveWeighted ? 'Weighted wager' : 'Total wagered'}</div>
                    <div className="wr-stat-value">{fmtWager(userWager)}</div>
                    <div className="wr-stat-sub">
                      {effectiveWeighted
                        ? (userWagerStats.periodWageredRaw > 0 && userWagerStats.periodWageredRaw !== userWagerStats.periodWagered
                            ? `${fmtWager(userWagerStats.periodWageredRaw)} raw this period`
                            : 'This period')
                        : (userWagerStats.periodWagered > 0 ? `${fmtWager(userWagerStats.periodWagered)} this period` : 'All-time')}
                    </div>
                  </div>
                  <div className="wr-stat">
                    <div className="wr-stat-label">Next milestone</div>
                    {nextTier ? (
                      <>
                        <div className="wr-stat-value">{nextTier.icon} {nextTier.name}</div>
                        <div className="wr-stat-sub">
                          {fmtWager(nextTier.requirement - userWager)} to go · pays <span className="wr-gold">+{fmtMoney(nextTier.reward)}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="wr-stat-value">All done 🏁</div>
                        <div className="wr-stat-sub">Every milestone reached{effectiveWeighted ? ' this period' : ''}</div>
                      </>
                    )}
                  </div>
                </div>

                <div className="wr-cta-row">
                  {claimableAmount > 0 && (
                    wagerTicketInfo.discordLinked ? (
                      <button className="wr-btn-gold" onClick={claimWagerRewards} disabled={wagerClaimBusy}>
                        🎫 {wagerClaimBusy ? 'Creating ticket…' : `Claim ${fmtMoney(claimableAmount)}`}
                      </button>
                    ) : (
                      <button className="wr-btn-discord" onClick={connectDiscord}>
                        🔗 Connect Discord to claim {fmtMoney(claimableAmount)}
                      </button>
                    )
                  )}
                  {pendingAmount > 0 && (
                    pendingTicketUrl ? (
                      <a className="wr-chip pending" href={pendingTicketUrl} target="_blank" rel="noopener noreferrer" title="Open your Discord ticket">
                        ⏳ {fmtMoney(pendingAmount)} pending · open your ticket
                      </a>
                    ) : (
                      <span className="wr-chip pending">⏳ {fmtMoney(pendingAmount)} pending in your Discord ticket</span>
                    )
                  )}
                  <span className="wr-note">
                    {claimableAmount > 0
                      ? (wagerTicketInfo.discordLinked
                          ? 'One private Discord ticket covers every milestone you’ve unlocked.'
                          : 'Claims open a private ticket in our Discord, so your Discord needs to be connected.')
                      : pendingAmount > 0
                        ? 'An admin will pay you out in your Discord ticket.'
                        : unlockedTiers.length > 0
                          ? (grindEarnedSteps > grindClaimsCount
                              ? `✅ Milestones paid — claim your ${fmtMoney((grindEarnedSteps - grindClaimsCount) * grindReward)} of Grind Area rewards below.`
                              : `✅ All caught up — ${fmtMoney(paidAmount)} paid out${effectiveWeighted ? ' this period' : ''}.`)
                          : nextTier
                            ? `Wager ${formatRequirement(nextTier.requirement)}${effectiveWeighted ? ' this period' : ''} to unlock your first ${fmtMoney(nextTier.reward)}.`
                            : null}
                  </span>
                </div>
              </div>
            ) : (
              /* ===== GUEST / NO SHUFFLE LINKED: the pitch ===== */
              <div className="wr-panel">
                <div className="wr-panel-top">
                  <div style={{minWidth:0}}>
                    <div className="wr-eyebrow">[ Every month ]</div>
                    <div className="wr-hero-amount wr-display wr-gold">$15,000+</div>
                    <div className="wr-hero-label">in wager rewards</div>
                  </div>
                  {resetBlock}
                </div>
                <p className="wr-hero-copy">
                  {tiers.length} milestones that each pay their own cash reward, plus <span className="wr-gold" style={{fontWeight:800}}>{fmtMoney(grindReward)}</span> for every $250K you wager past $1M.
                  {effectiveWeighted ? ' Progress resets on the 24th, so you can earn it all again every month.' : ''}
                </p>
                {(!user || userWagerStats?.noShuffleLinked) && (
                  <div className="wr-cta-row">
                    {!user ? (
                      <>
                        <button className="wr-btn-primary" onClick={loginWithTwitch}>Log in with Twitch</button>
                        <span className="wr-note">Then link your Shuffle username to track your progress.</span>
                      </>
                    ) : (
                      <>
                        <button className="wr-btn-primary" onClick={() => navigateTo('profile')}>Link your Shuffle username</button>
                        <span className="wr-note">Add it on your Profile to see your progress here.</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ===== MILESTONE LADDER: what each tier pays + running total ===== */}
            <div className="wr-section-head">
              <div className="wr-eyebrow">[ The milestones ]</div>
              <h2 className="wr-h2">{tiers.length} milestones. Every one pays.</h2>
              <p className="wr-sub">
                Each milestone pays its own reward and they stack. Reach {tiers[exampleIdx].name} at {formatRequirement(tiers[exampleIdx].requirement)} and you've earned{' '}
                <strong>{tiers.slice(0, exampleIdx + 1).map(t => fmtMoney(t.reward)).join(' + ')} = {fmtMoney(tiers[exampleIdx].cumulative)}</strong>.
                {effectiveWeighted ? ' Progress counts your weighted wager this period.' : ''}
              </p>
            </div>
            <div className={`wr-cols${showStatus ? '' : ' no-status'}`} aria-hidden="true">
              <span /><span>Milestone</span><span>Pays</span><span title="This milestone plus every one below it">Total</span>{showStatus && <span>Status</span>}
            </div>
            <div className={`wr-ladder${showStatus ? '' : ' no-status'}`}>
              {tiers.map((tier, idx) => {
                const isNext = hasStats && nextTier?.name === tier.name;
                const isBronze = tier.name === 'Bronze';
                const progress = Math.min((userWager / tier.requirement) * 100, 100);
                return (
                  <React.Fragment key={tier.name}>
                    {idx > 0 && (
                      <div className="wr-link" aria-hidden="true" style={{ background: tier.isUnlocked ? `linear-gradient(${tiers[idx - 1].color}, ${tier.color})` : 'rgba(255,255,255,0.09)' }} />
                    )}
                    <div
                      className={`wr-tier${isNext ? ' is-next' : ''}${hasStats && !tier.isUnlocked && !isNext ? ' is-locked' : ''}`}
                      style={tier.isUnlocked ? { background: `linear-gradient(135deg, ${tier.color}16, rgba(255,255,255,0.02))`, borderColor: `${tier.color}55` } : undefined}
                    >
                      <div className="wr-node" style={{
                        borderColor: tier.isUnlocked ? tier.color : isNext ? 'rgba(168,85,247,0.6)' : undefined,
                        boxShadow: tier.isUnlocked ? `0 0 16px ${tier.color}55` : undefined
                      }}>
                        <span style={{ filter: hasStats && !tier.isUnlocked ? 'grayscale(0.6)' : 'none' }}>{tier.icon}</span>
                      </div>
                      <div className="wr-tier-main">
                        <div className="wr-tier-name">
                          {tier.name}
                          {isBronze && <span className="wr-vip">⭐ VIP</span>}
                        </div>
                        <div className="wr-tier-req">Wager {formatRequirement(tier.requirement)}{isBronze ? ' · + VIP Discord' : ''}</div>
                      </div>
                      <div className="wr-amt pay">
                        <span className="wr-amt-label">Pays</span>
                        <span className="wr-amt-value wr-gold">+{fmtMoney(tier.reward)}</span>
                      </div>
                      <div className="wr-amt total" title={`${tier.name} plus every milestone below it`}>
                        <span className="wr-amt-label">Total</span>
                        <span className="wr-amt-value wr-total-value">{fmtMoney(tier.cumulative)}</span>
                      </div>
                      <div className="wr-status">
                        {tier.isUnlocked && user && tier.isClaimed && (
                          <span className="wr-chip paid">✅ Paid</span>
                        )}
                        {tier.isUnlocked && user && !tier.isClaimed && tier.pendingTicket && (
                          tier.ticketUrl ? (
                            <a className="wr-chip pending" href={tier.ticketUrl} target="_blank" rel="noopener noreferrer" title="Open your Discord ticket">⏳ Pending</a>
                          ) : (
                            <span className="wr-chip pending">⏳ Pending</span>
                          )
                        )}
                        {tier.isUnlocked && user && !tier.isClaimed && !tier.pendingTicket && (
                          wagerTicketInfo.discordLinked ? (
                            <button className="wr-btn-gold wr-btn-sm" onClick={claimWagerRewards} disabled={wagerClaimBusy}>
                              🎫 {wagerClaimBusy ? 'Creating…' : 'Claim'}
                            </button>
                          ) : (
                            <button className="wr-btn-discord wr-btn-sm" onClick={connectDiscord} title="Claims open a private Discord ticket, so your Discord must be connected">
                              🔗 Connect Discord
                            </button>
                          )
                        )}
                        {tier.isUnlocked && !user && (
                          <span className="wr-chip paid">✅ Unlocked</span>
                        )}
                        {!tier.isUnlocked && isNext && (
                          <span className="wr-chip togo">{fmtWager(tier.requirement - userWager)} to go</span>
                        )}
                        {!tier.isUnlocked && hasStats && !isNext && (
                          <span className="wr-chip locked">🔒 Locked</span>
                        )}
                      </div>
                      {isNext && (
                        <div className="wr-progress">
                          <div className="wr-bar"><span style={{ width: `${progress}%` }} /></div>
                          <div className="wr-progress-meta">
                            <span>{fmtWager(userWager)} / {formatRequirement(tier.requirement)}</span>
                            <span>{progress.toFixed(1)}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="wr-ladder-sum">
              <span>All {tiers.length} milestones{effectiveWeighted ? ', every month' : ''}</span>
              <span className="wr-display wr-gold">= {fmtMoney(milestonesTotal)}</span>
            </div>

            {/* ===== GRIND AREA: on top of the milestones, $1M → $10M ===== */}
            <div className="wr-grind">
              <div className="wr-grind-head">
                <div className="wr-node" aria-hidden="true">🔥</div>
                <div style={{flex:1, minWidth:0}}>
                  <div className="wr-tier-name">Grind Area <span className="wr-chip togo" style={{padding:'0.12rem 0.5rem', fontSize:'0.6rem'}}>$1M+ WAGERED</span></div>
                  <div className="wr-tier-req">
                    On top of the milestones: <span className="wr-gold" style={{fontWeight:800}}>+{fmtMoney(grindReward)}</span> for every $250K you wager past $1M, up to $10M.
                  </div>
                </div>
              </div>
              <div className="wr-facts">
                {[
                  { label: 'Starts at', value: '$1M' },
                  { label: 'Every', value: '$250K' },
                  { label: 'Pays', value: `+${fmtMoney(grindReward)}`, money: true },
                  { label: 'Ends at', value: '$10M' },
                  { label: 'Worth up to', value: fmtMoney(grindMaxSteps * grindReward), money: true }
                ].map(f => (
                  <div key={f.label} className="wr-fact">
                    <div className="wr-stat-label">{f.label}</div>
                    <div className={`wr-fact-value${f.money ? ' wr-gold' : ''}`}>{f.value}</div>
                  </div>
                ))}
              </div>

              {hasStats && userWager > 0 && (
                <div className="wr-grind-progress">
                  {userWager < grindThreshold ? (
                    <>
                      <div className="wr-progress-meta" style={{marginTop:0, marginBottom:'0.4rem'}}>
                        <span style={{color:'#e4e4e7', fontWeight:600}}>Progress to the Grind Area</span>
                        <span style={{color:'#d8b4fe', fontWeight:700}}>{((userWager / grindThreshold) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="wr-bar"><span style={{ width: `${(userWager / grindThreshold) * 100}%` }} /></div>
                      <div className="wr-progress-meta">
                        <span>{fmtWager(userWager)} / $1M</span>
                        <span>{fmtWager(grindThreshold - userWager)} to go</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.5rem', flexWrap:'wrap', marginBottom: userWager < eternalThreshold ? '0.6rem' : 0}}>
                        <div>
                          <div style={{fontSize:'0.85rem', color:'#e9d5ff', fontWeight:700}}>🔥 You're in the Grind Area</div>
                          <div style={{fontSize:'0.72rem', color:'#a1a1aa', marginTop:'0.15rem'}}>
                            {grindEarnedSteps} reward{grindEarnedSteps !== 1 ? 's' : ''} earned ({grindClaimsCount} claimed) · {fmtMoney(grindEarned)} total
                          </div>
                        </div>
                        {grindEarnedSteps > grindClaimsCount && (
                          <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" className="wr-btn-gold wr-btn-sm">
                            🎫 Claim {fmtMoney((grindEarnedSteps - grindClaimsCount) * grindReward)}
                          </a>
                        )}
                        {grindEarnedSteps > 0 && grindEarnedSteps <= grindClaimsCount && (
                          <span className="wr-chip paid">✅ All claimed</span>
                        )}
                      </div>
                      {userWager < eternalThreshold && (
                        <>
                          <div className="wr-progress-meta" style={{marginTop:0, marginBottom:'0.3rem'}}>
                            <span>Next +{fmtMoney(grindReward)} at {fmtWager(nextGrindAt)}</span>
                            <span style={{color:'#d8b4fe', fontWeight:700}}>{progressToNextGrind.toFixed(1)}%</span>
                          </div>
                          <div className="wr-bar"><span style={{ width: `${progressToNextGrind}%` }} /></div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ===== HOW TO CLAIM ===== */}
            <div className="wr-section-head" style={{marginTop:'2.25rem'}}>
              <div className="wr-eyebrow">[ How to claim ]</div>
            </div>
            <div className="wr-steps">
              {[
                { n: '01', title: 'Hit a milestone', desc: <>Wager on Shuffle under guild <strong>SNUTZY</strong>. Each milestone unlocks once your {effectiveWeighted ? 'weighted wager this period' : 'total wager'} passes it.</> },
                { n: '02', title: 'Hit Claim', desc: <>Connect your Discord, then hit <strong>Claim</strong>. One private ticket covers every milestone you've unlocked.</> },
                { n: '03', title: 'Get paid', desc: <>An admin checks your wager and pays you out in the ticket. Each milestone pays once {effectiveWeighted ? 'per period' : 'per account'}.</> }
              ].map(s => (
                <div key={s.n} className="wr-step">
                  <div className="wr-step-n">{s.n}</div>
                  <div className="wr-step-title">{s.title}</div>
                  <div className="wr-step-desc">{s.desc}</div>
                </div>
              ))}
            </div>
            <p className="wr-disclaimer">
              ⚠️ All rewards are manually verified and issued at our discretion. Wagers must be made under guild <strong>SNUTZY</strong>, and any wager abuse results in disqualification. Grind Area rewards are claimed in our <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer">Discord server</a>.
            </p>

            {/* ===== MONTHLY LEADERBOARD - Condensed ===== */}
            <div id="leaderboard-section" style={{background:'rgba(14,14,18,0.6)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'1rem', padding:'1.25rem', marginBottom:'1.5rem'}}>
              <div style={{display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1rem'}}>
                <span style={{fontSize:'1.3rem'}}>🏆</span>
                <div style={{flex:1}}>
                  <h2 style={{fontSize:'1.1rem', fontWeight:700, color:'#e2e8f0', margin:0}}>Monthly Leaderboard</h2>
                  <p style={{fontSize:'0.8rem', color:'#9ca3af', margin:0}}>{poolLabel} prize pool • Top 15 win every month</p>
                </div>
                <button onClick={() => navigateTo('leaderboard')} style={{padding:'0.35rem 0.75rem', background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'0.375rem', color:'#c4b5fd', fontSize:'0.75rem', fontWeight:600, cursor:'pointer'}}>View Standings →</button>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(100px, 1fr))', gap:'0.5rem'}}>
                {prizes.map(({ rank, prize }) => ({
                  rank: rank + (rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'),
                  amount: `$${prize.toLocaleString()}`,
                  ...(rank === 1 ? { color: '#FFD700', bg: 'rgba(255,215,0,0.1)' }
                    : rank === 2 ? { color: '#C0C0C0', bg: 'rgba(192,192,192,0.08)' }
                    : rank === 3 ? { color: '#CD7F32', bg: 'rgba(205,127,50,0.08)' }
                    : rank <= 5 ? { color: '#9ca3af', bg: 'rgba(255,255,255,0.03)' }
                    : rank <= 10 ? { color: '#6b7280', bg: 'rgba(255,255,255,0.02)' }
                    : { color: '#4b5563', bg: 'rgba(255,255,255,0.01)' }),
                })).map((p, i) => (
                  <div key={i} style={{padding:'0.5rem', background:p.bg, border:'1px solid rgba(255,255,255,0.04)', borderRadius:'0.5rem', textAlign:'center'}}>
                    <div style={{fontSize:'0.7rem', color:p.color, fontWeight:700}}>{p.rank}</div>
                    <div style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0'}}>{p.amount}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ===== COMMUNITY PERKS - Condensed ===== */}
            <div style={{background:'rgba(14,14,18,0.6)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'1rem', padding:'1.25rem'}}>
              <div style={{display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1rem'}}>
                <span style={{fontSize:'1.3rem'}}>🎁</span>
                <div>
                  <h2 style={{fontSize:'1.1rem', fontWeight:700, color:'#e2e8f0', margin:0}}>Community Perks</h2>
                  <p style={{fontSize:'0.8rem', color:'#9ca3af', margin:0}}>Extra benefits for guild SNUTZY members</p>
                </div>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'0.75rem'}}>
                <div style={{padding:'1rem', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'0.75rem'}}>
                  <div style={{fontSize:'1.25rem', marginBottom:'0.35rem'}}>👑</div>
                  <h4 style={{fontSize:'0.9rem', fontWeight:700, color:'#f59e0b', margin:'0 0 0.25rem 0'}}>VIP Discord Rank</h4>
                  <p style={{fontSize:'0.78rem', color:'#9ca3af', margin:0}}>Wager $50,000+ (weighted) to unlock VIP status, exclusive channels & perks</p>
                </div>
                <div style={{padding:'1rem', background:'rgba(168,85,247,0.06)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'0.75rem'}}>
                  <div style={{fontSize:'1.25rem', marginBottom:'0.35rem'}}>🎰</div>
                  <h4 style={{fontSize:'0.9rem', fontWeight:700, color:'#c4b5fd', margin:'0 0 0.25rem 0'}}>Affiliate Giveaways</h4>
                  <p style={{fontSize:'0.78rem', color:'#9ca3af', margin:0}}>Exclusive giveaways on Discord for all guild SNUTZY members</p>
                </div>
                <div style={{padding:'1rem', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'0.75rem'}}>
                  <div style={{fontSize:'1.25rem', marginBottom:'0.35rem'}}>📺</div>
                  <h4 style={{fontSize:'0.9rem', fontWeight:700, color:'#86efac', margin:'0 0 0.25rem 0'}}>Stream Giveaways</h4>
                  <p style={{fontSize:'0.78rem', color:'#9ca3af', margin:0}}>Regular giveaways during live streams on Twitch</p>
                </div>
              </div>
              <div style={{marginTop:'1rem', textAlign:'center'}}>
                <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" className="rewards-action-btn" style={{display:'inline-block'}}>Join Discord →</a>
              </div>
            </div>
          </div>
          );
        })()}

        {activePage === 'gauntlet' && (
          <div className="gauntlet-container" style={{ maxWidth: spinZoneGame === 'slots' ? '950px' : spinZoneGame === 'chat' ? '700px' : '600px' }}>
            
            {/* Game Selection Screen */}
            {!spinZoneGame && (
              <>
                <h1 className="simple-page-title">Spin Zone</h1>
                <p className="gauntlet-description">Choose your game and let fate decide!</p>
                
                <div className="spinzone-selection">
                  {/* Rejuicer card removed — feature parked */}
                  
                  <div className="spinzone-card" onClick={() => navigateTo('gauntlet', 'slots')}>
                    <div className="spinzone-card-icon">🎰</div>
                    <div className="spinzone-card-title">Slot Picker</div>
                    <div className="spinzone-card-desc">Can't decide which slot to play? Let the reel choose for you!</div>
                  </div>

                  <div className="spinzone-card" onClick={() => navigateTo('gauntlet', 'chat')}>
                    <div className="spinzone-card-icon">💬</div>
                    <div className="spinzone-card-title">Chat Picker</div>
                    <div className="spinzone-card-desc">Let Twitch chat decide! Collect suggestions and spin to pick a winner.</div>
                  </div>
                </div>
              </>
            )}

            {/* Rejuicer Gauntlet */}
            {spinZoneGame === 'rejuicer' && (
              <>
                <button className="spinzone-back-btn" onClick={() => navigateTo('gauntlet')}>← Back to Games</button>
                <h1 className="simple-page-title">Rejuicer Gauntlet</h1>
                <p className="gauntlet-description">Spin the wheel to find out which game is going to rejuice your balance!</p>
                
                <div className="reel-wrapper">
                  <div className="reel-container">
                    <div className="reel-viewport">
                      <div className="reel-strip" style={{ 
                          transform: `translateY(${wheelRotation}px)`, 
                          transition: isResetting ? 'none' : (wheelSpinning ? 'transform 4s cubic-bezier(0.15, 0.85, 0.35, 1)' : 'none')
                        }}>
                        {[...Array(25)].flatMap((_, i) => casinoGames.map((game, j) => (
                          <div key={`${i}-${j}`} className="reel-item" style={{ background: gameColors[j] + '30' }}>{game}</div>
                        )))}
                      </div>
                    </div>
                    <div className="reel-gradient-top" />
                    <div className="reel-gradient-bottom" />
                    <div className="reel-pointer" />
                    <div className="pointer-arrow left" />
                    <div className="pointer-arrow right" />
                  </div>
                </div>

                <button className="spin-btn" onClick={spinWheel} disabled={wheelSpinning}>
                  {wheelSpinning ? '🎰 Spinning...' : '🎰 Spin the Gauntlet'}
                </button>

                {wheelResult && (
                  <div className="wheel-result">
                    <div className="result-label">Your rejuicer is...</div>
                    <div className="result-game">{wheelResult}</div>
                    <div className="result-hint">Good luck! 🍀</div>
                  </div>
                )}
              </>
            )}

            {/* Slot Picker */}
            {spinZoneGame === 'slots' && (
              <>
                <button className="spinzone-back-btn" onClick={() => navigateTo('gauntlet')}>← Back to Games</button>
                <h1 className="simple-page-title">Slot Picker</h1>
                <p className="gauntlet-description">Can't decide which slot to play? Let the reel choose for you!</p>
                
                {/* Horizontal Slot Reel */}
                <div className="slot-reel-wrapper">
                  <div className={`slot-reel-container ${slotResult && !slotSpinning ? 'landed' : ''}`}>
                    <div className="slot-reel-viewport">
                      <div className={`slot-reel-strip ${slotSpinning ? 'spinning' : ''}`} style={{ 
                          transform: `translateX(${slotRotation}px)`, 
                          transition: slotIsResetting ? 'none' : (slotSpinning ? 'transform 5s cubic-bezier(0.12, 0.8, 0.2, 1)' : 'none')
                        }}>
                        {[...Array(20)].flatMap((_, i) => reelSlots.map((slot, j) => (
                          <div key={`${i}-${j}`} className="slot-reel-item">
                            <img src={slot.image} alt={slot.name} />
                          </div>
                        )))}
                      </div>
                    </div>
                    <div className="slot-reel-gradient-left" />
                    <div className="slot-reel-gradient-right" />
                    <div className="slot-reel-pointer" />
                  </div>
                </div>

                <div className="slot-buttons">
                  <button className="spin-btn" onClick={spinSlotPicker} disabled={slotSpinning || filteredSlots.length === 0}>
                    {slotSpinning ? '🎰 Spinning...' : '🎰 Pick a Slot'}
                  </button>
                  <button className="shuffle-btn" onClick={shuffleSlots} disabled={slotSpinning}>
                    🔀 Shuffle
                  </button>
                </div>

                {/* Result Popup Overlay */}
                {slotResultPopup && slotResult && (
                  <div className="slot-popup-overlay" onClick={closeSlotPopup}>
                    <div className="slot-popup" onClick={(e) => e.stopPropagation()}>
                      <button className="slot-popup-close" onClick={closeSlotPopup}>✕</button>
                      <div className="slot-popup-label">Your slot is...</div>
                      <a href={getShuffleUrl(slotResult)} target="_blank" rel="noopener noreferrer" className="slot-popup-game-link">
                        <img src={slotResult.image} alt={slotResult.name} className="slot-popup-image" />
                        <div className="slot-popup-name">{slotResult.name}</div>
                        <div className="slot-popup-provider">{slotResult.provider}</div>
                      </a>
                      {/* Slot info chips (from the Shuffle catalog; hidden when unknown) */}
                      {(slotResult.bonusBuy || slotResult.rtp != null) && (
                        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.5rem', margin: '0.75rem 0 0.25rem' }}>
                          {/* Only ever a positive claim. Shuffle's BONUS_BUY category is
                              reliable when set but very incomplete when absent, so an
                              untagged slot shows no chip rather than "No Bonus Buy". */}
                          {slotResult.bonusBuy && (
                            <span style={{
                              padding: '0.3rem 0.75rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700,
                              background: 'rgba(34,197,94,0.12)',
                              border: '1px solid rgba(34,197,94,0.35)',
                              color: '#4ade80'
                            }}>
                              💰 Bonus Buy
                            </span>
                          )}
                          {slotResult.rtp != null && (
                            <span style={{ padding: '0.3rem 0.75rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', color: '#d8b4fe' }}>
                              📊 RTP {slotResult.rtp}%
                            </span>
                          )}
                          {slotResult.volatility && (
                            <span style={{
                              padding: '0.3rem 0.75rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700,
                              background: slotResult.volatility === 'high' ? 'rgba(239,68,68,0.12)' : slotResult.volatility === 'medium' ? 'rgba(251,191,36,0.1)' : 'rgba(34,197,94,0.1)',
                              border: `1px solid ${slotResult.volatility === 'high' ? 'rgba(239,68,68,0.35)' : slotResult.volatility === 'medium' ? 'rgba(251,191,36,0.3)' : 'rgba(34,197,94,0.3)'}`,
                              color: slotResult.volatility === 'high' ? '#fca5a5' : slotResult.volatility === 'medium' ? '#fbbf24' : '#4ade80'
                            }}>
                              {slotResult.volatility === 'high' ? '🌶️ High Volatility' : slotResult.volatility === 'medium' ? '🌗 Medium Volatility' : '🧊 Low Volatility'}
                            </span>
                          )}
                          {slotResult.maxWin && (
                            <span style={{ padding: '0.3rem 0.75rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}>
                              🏆 Max Win {slotResult.maxWin.toLocaleString()}x
                            </span>
                          )}
                        </div>
                      )}
                      <div className="slot-popup-buttons">
                        <a href={getShuffleUrl(slotResult)} target="_blank" rel="noopener noreferrer" className="slot-popup-play-btn">
                          🎰 Play Now
                        </a>
                        <button className="slot-popup-respin-btn" onClick={() => { closeSlotPopup(); setTimeout(spinSlotPicker, 100); }}>
                          🔄 Respin
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Provider Filters */}
                <div className="filter-section">
                  <div className="filter-header">
                    <span className="filter-label">🎯 Filter by Provider</span>
                    <div className="filter-actions">
                      <button
                        className="filter-action-btn"
                        onClick={selectMainProviders}
                        style={{ background: 'rgba(251,191,36,0.1)', borderColor: 'rgba(251,191,36,0.35)', color: '#fbbf24' }}
                        title="Pragmatic, Hacksaw, Nolimit, BGaming, Clutch, Backseat, Bullshark, ShadyLady, Print Studios"
                      >
                        ⭐ Main Providers
                      </button>
                      <button className="filter-action-btn" onClick={selectAllProviders}>Select All</button>
                      <button className="filter-action-btn" onClick={deselectAllProviders}>Select None</button>
                    </div>
                  </div>
                  {/* Halloween edition toggle (seasonal, see Halloween.jsx) */}
                  {HALLOWEEN_ON && (
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none',
                      padding: '0.55rem 0.8rem', borderRadius: '10px', marginBottom: '0.6rem',
                      background: spookyOnly ? 'linear-gradient(135deg, rgba(249,115,22,0.16), rgba(168,85,247,0.14))' : 'rgba(30,30,40,0.6)',
                      border: `1px solid ${spookyOnly ? 'rgba(249,115,22,0.55)' : 'rgba(139,92,246,0.2)'}`,
                      boxShadow: spookyOnly ? '0 0 18px rgba(249,115,22,0.18)' : 'none',
                      transition: 'all 0.15s ease'
                    }}>
                      <input type="checkbox" checked={spookyOnly} onChange={() => { if (!slotSpinning) setSpookyOnly(!spookyOnly); }} style={{ display: 'none' }} />
                      <span style={{
                        width: '34px', height: '19px', borderRadius: '999px', position: 'relative', flexShrink: 0,
                        background: spookyOnly ? 'linear-gradient(135deg, #fb923c, #ea580c)' : 'rgba(255,255,255,0.12)',
                        transition: 'background 0.2s ease'
                      }}>
                        <span style={{
                          position: 'absolute', top: '2px', left: spookyOnly ? '17px' : '2px',
                          width: '15px', height: '15px', borderRadius: '50%', background: '#fff',
                          transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
                        }} />
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: spookyOnly ? '#fdba74' : '#d4d4d8' }}>
                        🎃 Halloween slots only
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#a1a1aa' }}>
                        zombies, vampires, witches, the dead…
                      </span>
                    </label>
                  )}
                  {/* Bonus buy toggle */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none',
                    padding: '0.55rem 0.8rem', borderRadius: '10px', marginBottom: '0.8rem',
                    background: bonusBuyOnly ? 'rgba(251,191,36,0.1)' : 'rgba(30,30,40,0.6)',
                    border: `1px solid ${bonusBuyOnly ? 'rgba(251,191,36,0.5)' : 'rgba(139,92,246,0.2)'}`,
                    transition: 'all 0.15s ease'
                  }}>
                    <input type="checkbox" checked={bonusBuyOnly} onChange={() => setBonusBuyOnly(!bonusBuyOnly)} style={{ display: 'none' }} />
                    <span style={{
                      width: '34px', height: '19px', borderRadius: '999px', position: 'relative', flexShrink: 0,
                      background: bonusBuyOnly ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : 'rgba(255,255,255,0.12)',
                      transition: 'background 0.2s ease'
                    }}>
                      <span style={{
                        position: 'absolute', top: '2px', left: bonusBuyOnly ? '17px' : '2px',
                        width: '15px', height: '15px', borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
                      }} />
                    </span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: bonusBuyOnly ? '#fbbf24' : '#d4d4d8' }}>
                      💰 Bonus Buy slots only
                    </span>
                  </label>
                  <div style={{ position: 'relative', marginBottom: '0.8rem' }}>
                    <span style={{ position: 'absolute', left: '0.8rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', pointerEvents: 'none', opacity: 0.6 }}>🔍</span>
                    <input
                      value={providerSearch}
                      onChange={e => setProviderSearch(e.target.value)}
                      placeholder={`Search ${allProviders.length} providers…`}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.6rem 2.2rem 0.6rem 2.3rem', background: 'rgba(30,30,40,0.6)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '8px', color: '#fff', fontSize: '0.88rem', fontFamily: 'Outfit, sans-serif', outline: 'none' }}
                    />
                    {providerSearch && (
                      <button onClick={() => setProviderSearch('')} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#a1a1aa', cursor: 'pointer', fontSize: '1rem', padding: '0.2rem 0.4rem' }}>×</button>
                    )}
                  </div>
                  <div className="provider-grid">
                    {allProviders
                      .filter(p => !providerSearch.trim() || p.toLowerCase().includes(providerSearch.trim().toLowerCase()))
                      .map(provider => {
                        const checked = selectedProviders.includes(provider);
                        return (
                      <label key={provider} className={`provider-checkbox ${checked ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProvider(provider)}
                        />
                        <span className="checkmark">✓</span>
                        <span className="provider-name">{provider}</span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 700, color: checked ? '#c4b5fd' : '#71717a', background: 'rgba(139,92,246,0.12)', borderRadius: '999px', padding: '0.1rem 0.5rem', flexShrink: 0 }}>
                          {spookyActive ? (spookyProviderCounts[provider] || 0) : providerSlotCounts[provider]}
                        </span>
                      </label>
                        );
                      })}
                    {providerSearch.trim() && !allProviders.some(p => p.toLowerCase().includes(providerSearch.trim().toLowerCase())) && (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#71717a', fontSize: '0.85rem', padding: '0.8rem 0' }}>
                        No providers match "{providerSearch.trim()}"
                      </div>
                    )}
                  </div>
                  <div className="slot-count">
                    {selectedProviders.length}/{allProviders.length} providers · {filteredSlots.length} {spookyActive ? '🎃 spooky ' : ''}slot{filteredSlots.length !== 1 ? 's' : ''} in the reel
                  </div>
                </div>
              </>
            )}

            {/* Chat Picker */}
            {spinZoneGame === 'chat' && (
              <>
                <button className="spinzone-back-btn" onClick={() => { navigateTo('gauntlet'); resetChatPicker(); }}>← Back to Games</button>
                <h1 className="simple-page-title">Chat Picker</h1>
                <p className="gauntlet-description">Let Twitch chat suggest games and spin to pick a winner!</p>

                {/* Setup Phase */}
                {chatPickerPhase === 'setup' && (
                  <div className="chat-setup">
                    <div className="chat-setup-icon">💬</div>
                    <p className="chat-setup-text">Select how long to collect suggestions from <strong>twitch.tv/{TWITCH_CHANNEL}</strong></p>
                    
                    <div className="chat-timer-options">
                      {[30, 60, 120, 300].map(seconds => (
                        <button 
                          key={seconds}
                          className={`chat-timer-btn ${chatTimer === seconds ? 'active' : ''}`}
                          onClick={() => setChatTimer(seconds)}
                        >
                          {seconds < 60 ? `${seconds}s` : `${seconds / 60}m`}
                        </button>
                      ))}
                    </div>

                    <button className="spin-btn" onClick={startChatCollection}>
                      📡 Start Collecting
                    </button>

                    <p className="chat-hint">Chat messages will be collected and grouped. More mentions = higher chance to win!</p>
                  </div>
                )}

                {/* Collecting Phase */}
                {chatPickerPhase === 'collecting' && (
                  <div className="chat-collecting">
                    <div className="chat-collecting-animation">
                      <div className="chat-pulse"></div>
                      <div className="chat-collecting-icon">📡</div>
                    </div>
                    
                    <div className="chat-timer-display">
                      <div className="chat-timer-number">{chatTimeLeft}</div>
                      <div className="chat-timer-label">seconds remaining</div>
                    </div>

                    <div className="chat-messages-count">
                      <span className="chat-count-number">{chatMessages.length}</span>
                      <span className="chat-count-label">messages collected</span>
                    </div>

                    <div className="chat-live-feed">
                      {chatMessages.slice(-5).reverse().map((msg, idx) => (
                        <div key={idx} className="chat-live-message" style={{ opacity: 1 - (idx * 0.15) }}>
                          <span className="chat-live-user">{msg.username}:</span>
                          <span className="chat-live-text">{msg.text}</span>
                        </div>
                      ))}
                    </div>

                    <button className="chat-cancel-btn" onClick={cancelChatCollection}>
                      ✕ Cancel
                    </button>
                  </div>
                )}

                {/* Ready Phase - Show heat map and spin */}
                {(chatPickerPhase === 'ready' || chatPickerPhase === 'spinning' || chatPickerPhase === 'result') && (
                  <div className="chat-ready">
                    {chatSuggestions.length > 0 ? (
                      <>
                        <div className="chat-heatmap">
                          <div className="chat-heatmap-title">📊 Suggestions ({chatSuggestions.reduce((acc, s) => acc + s.count, 0)} total)</div>
                          <div className="chat-heatmap-grid">
                            {chatSuggestions.slice(0, 20).map((suggestion, idx) => {
                              const maxCount = chatSuggestions[0].count;
                              const intensity = suggestion.count / maxCount;
                              const isSpinning = chatPickerPhase === 'spinning' && chatSpinDisplay?.normalized === suggestion.normalized;
                              const isWinner = chatPickerPhase === 'result' && chatResult?.normalized === suggestion.normalized;
                              
                              return (
                                <div 
                                  key={idx} 
                                  className={`chat-suggestion ${isSpinning ? 'spinning' : ''} ${isWinner ? 'winner' : ''}`}
                                  style={{ 
                                    background: `rgba(139, 92, 246, ${0.1 + intensity * 0.5})`,
                                    borderColor: `rgba(139, 92, 246, ${0.3 + intensity * 0.5})`
                                  }}
                                >
                                  <span className="chat-suggestion-text">{suggestion.text}</span>
                                  <span className="chat-suggestion-count">{suggestion.count}x</span>
                                </div>
                              );
                            })}
                          </div>
                          {chatSuggestions.length > 20 && (
                            <div className="chat-more">+{chatSuggestions.length - 20} more suggestions</div>
                          )}
                        </div>

                        <div className="chat-buttons">
                          <button 
                            className="spin-btn" 
                            onClick={spinChatPicker}
                            disabled={chatPickerPhase === 'spinning'}
                          >
                            {chatPickerPhase === 'spinning' ? '🎲 Picking...' : '🎲 Pick Winner'}
                          </button>
                          <button className="shuffle-btn" onClick={resetChatPicker}>
                            🔄 Start Over
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="chat-no-results">
                        <div className="chat-no-results-icon">😢</div>
                        <p>No suggestions were collected. Try again when chat is more active!</p>
                        <button className="spin-btn" onClick={resetChatPicker}>Try Again</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Result Popup */}
                {chatResultPopup && chatResult && (
                  <div className="slot-popup-overlay" onClick={closeChatPopup}>
                    <div className="slot-popup chat-popup" onClick={(e) => e.stopPropagation()}>
                      <button className="slot-popup-close" onClick={closeChatPopup}>✕</button>
                      <div className="slot-popup-label">Chat has spoken!</div>
                      <div className="chat-result-game">{chatResult.text}</div>
                      <div className="chat-result-meta">
                        <span className="chat-result-count">Suggested {chatResult.count}x</span>
                        <span className="chat-result-by">Picked from <strong>{chatResult.winningUser}</strong></span>
                      </div>
                      <div className="slot-popup-hint">Good luck! 🍀</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Raffle Lottery Page — $5K raffle halted for now: users hitting /raffle see an
            unavailable notice; admins keep full access to manage/draw. */}
        {activePage === 'raffle' && !isAdmin && (
          <div style={{maxWidth:'520px', margin:'3rem auto', textAlign:'center', background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'16px', padding:'2rem 1.5rem'}}>
            <div style={{fontSize:'2rem', marginBottom:'0.6rem'}}>🎟️</div>
            <h2 style={{color:'#e2e8f0', fontSize:'1.1rem', margin:'0 0 0.5rem'}}>The raffle is taking a break</h2>
            <p style={{color:'#a1a1aa', fontSize:'0.85rem', lineHeight:1.6, margin:0}}>
              The $5K raffle isn't running right now. Keep an eye on our{' '}
              <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#a855f7'}}>Discord</a>{' '}
              for announcements — there's still plenty to win on the leaderboards, giveaways and the points shop.
            </p>
          </div>
        )}
        {activePage === 'raffle' && isAdmin && (
          <div style={{maxWidth:'700px', margin:'0 auto'}}>
            {/* Header */}
            <div style={{textAlign:'center', marginBottom:'1.5rem'}}>
              <div style={{display:'inline-flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.5rem'}}>
                <svg width="48" height="48" viewBox="0 0 52 52" fill="none">
                  <defs>
                    <linearGradient id="rTicket" x1="0" y1="0" x2="52" y2="52"><stop offset="0%" stopColor="#fbbf24"/><stop offset="100%" stopColor="#f59e0b"/></linearGradient>
                    <linearGradient id="rStar" x1="0" y1="0" x2="20" y2="20"><stop offset="0%" stopColor="#fde68a"/><stop offset="100%" stopColor="#fbbf24"/></linearGradient>
                  </defs>
                  <rect x="4" y="12" width="44" height="28" rx="6" fill="url(#rTicket)" opacity="0.9"/>
                  <rect x="4" y="12" width="44" height="28" rx="6" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none"/>
                  <circle cx="16" cy="12" r="3" fill="#0e0e12"/><circle cx="16" cy="40" r="3" fill="#0e0e12"/>
                  <line x1="16" y1="16" x2="16" y2="36" stroke="rgba(0,0,0,0.2)" strokeWidth="1" strokeDasharray="2 2"/>
                  <path d="M33 22l2.4 4.8 5.3.8-3.8 3.7.9 5.3L33 34l-4.8 2.6.9-5.3-3.8-3.7 5.3-.8z" fill="url(#rStar)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5"/>
                  <text x="10" y="31" fontSize="12" fontWeight="800" fill="rgba(0,0,0,0.4)" fontFamily="monospace">$</text>
                </svg>
                <h1 style={{fontSize:'1.6rem', fontWeight:900, color:'#fbbf24', margin:0, textShadow:'0 0 25px rgba(245,158,11,0.3)'}}>$5,000 Monthly Raffle</h1>
              </div>
              <p style={{color:'#a1a1aa', fontSize:'0.85rem', margin:0}}>
                Every month, one lucky wagerer wins $5,000! The more you wager, the higher your chances.
              </p>
            </div>

            {/* Prize + Countdown */}
            <div style={{
              background:'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(139,92,246,0.08))',
              border:'1px solid rgba(245,158,11,0.2)', borderRadius:'14px',
              padding:'1.25rem', marginBottom:'1rem', textAlign:'center'
            }}>
              <div style={{fontSize:'2.5rem', fontWeight:900, color:'#fbbf24', textShadow:'0 0 30px rgba(245,158,11,0.4)'}}>$5,000</div>
              <div style={{fontSize:'0.75rem', color:'#71717a', marginBottom:'0.75rem'}}>Grand Prize — Drawn at leaderboard reset</div>
              <div style={{display:'flex', justifyContent:'center', gap:'0.5rem'}}>
                {[
                  { val: countdown.days, label: 'Days' },
                  { val: countdown.hours, label: 'Hrs' },
                  { val: countdown.minutes, label: 'Min' },
                  { val: countdown.seconds, label: 'Sec' }
                ].map((t, i) => (
                  <div key={i} style={{textAlign:'center'}}>
                    <div style={{background:'rgba(0,0,0,0.4)', borderRadius:'8px', padding:'0.4rem 0.6rem', minWidth:'42px', border:'1px solid rgba(255,255,255,0.06)'}}>
                      <div style={{fontSize:'1.2rem', fontWeight:800, color:'#e2e8f0', fontFamily:'monospace'}}>{String(t.val).padStart(2, '0')}</div>
                    </div>
                    <div style={{fontSize:'0.55rem', color:'#71717a', marginTop:'0.2rem'}}>{t.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Row */}
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.6rem', marginBottom:'1rem'}}>
              <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(245,158,11,0.12)', borderRadius:'10px', padding:'0.75rem', textAlign:'center'}}>
                <div style={{fontSize:'1.4rem', fontWeight:800, color:'#fbbf24'}}>{(raffleLottery?.totalTickets || 0).toLocaleString()}</div>
                <div style={{fontSize:'0.65rem', color:'#71717a'}}>Total Tickets</div>
              </div>
              <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(139,92,246,0.12)', borderRadius:'10px', padding:'0.75rem', textAlign:'center'}}>
                <div style={{fontSize:'1.4rem', fontWeight:800, color:'#a78bfa'}}>{raffleLottery?.totalParticipants || 0}</div>
                <div style={{fontSize:'0.65rem', color:'#71717a'}}>Participants</div>
              </div>
              <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(34,197,94,0.12)', borderRadius:'10px', padding:'0.75rem', textAlign:'center'}}>
                <div style={{fontSize:'1.4rem', fontWeight:800, color:'#22c55e'}}>${(raffleLottery?.prize || 5000).toLocaleString()}</div>
                <div style={{fontSize:'0.65rem', color:'#71717a'}}>Prize Pool</div>
              </div>
            </div>

            {/* Your Tickets Section */}
            {user ? (
              raffleLottery?.userShuffleLinked ? (
                <div style={{
                  background:'rgba(14,14,18,0.7)', border:'1px solid rgba(34,197,94,0.15)',
                  borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem'
                }}>
                  <div style={{fontSize:'0.8rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>🎟️ Your Raffle Status</div>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem', marginBottom:'1rem'}}>
                    <div style={{textAlign:'center'}}>
                      <div style={{fontSize:'1.8rem', fontWeight:900, color:'#22c55e'}}>{raffleLottery?.userTickets || 0}</div>
                      <div style={{fontSize:'0.65rem', color:'#71717a'}}>Your Tickets</div>
                    </div>
                    <div style={{textAlign:'center'}}>
                      <div style={{fontSize:'1.8rem', fontWeight:900, color:'#fbbf24'}}>
                        {raffleLottery?.totalTickets > 0 && raffleLottery?.userTickets > 0
                          ? ((raffleLottery.userTickets / raffleLottery.totalTickets) * 100).toFixed(2) + '%'
                          : '0%'}
                      </div>
                      <div style={{fontSize:'0.65rem', color:'#71717a'}}>Win Chance</div>
                    </div>
                    <div style={{textAlign:'center'}}>
                      <div style={{fontSize:'1.8rem', fontWeight:900, color:'#a78bfa'}}>${(raffleLottery?.userWager || 0).toLocaleString()}</div>
                      <div style={{fontSize:'0.65rem', color:'#71717a'}}>Wagered</div>
                    </div>
                  </div>

                  {/* Progress to next ticket */}
                  <div style={{background:'rgba(0,0,0,0.3)', borderRadius:'10px', padding:'0.75rem'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.4rem'}}>
                      <span style={{fontSize:'0.72rem', color:'#a1a1aa'}}>Progress to next ticket</span>
                      <span style={{fontSize:'0.72rem', fontWeight:700, color:'#22c55e'}}>
                        ${(raffleLottery?.userWager || 0).toLocaleString()} / ${(raffleLottery?.nextTicketAt || 100).toLocaleString()}
                      </span>
                    </div>
                    <div style={{width:'100%', height:'8px', background:'rgba(255,255,255,0.06)', borderRadius:'4px', overflow:'hidden'}}>
                      <div style={{
                        width:`${raffleLottery?.progressToNext || 0}%`, height:'100%',
                        background:'linear-gradient(90deg, #22c55e, #4ade80)', borderRadius:'4px',
                        transition:'width 0.5s ease'
                      }} />
                    </div>
                    <div style={{fontSize:'0.65rem', color:'#71717a', marginTop:'0.3rem'}}>
                      ${((raffleLottery?.nextTicketAt || 100) - (raffleLottery?.userWager || 0)).toLocaleString()} more to earn ticket #{(raffleLottery?.userTickets || 0) + 1}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  background:'rgba(14,14,18,0.7)', border:'1px solid rgba(139,92,246,0.2)',
                  borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem', textAlign:'center'
                }}>
                  <div style={{fontSize:'1.5rem', marginBottom:'0.5rem'}}>🔗</div>
                  <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.3rem'}}>Link Your Shuffle Account</div>
                  <div style={{fontSize:'0.78rem', color:'#71717a', marginBottom:'0.75rem'}}>
                    Connect your Shuffle username to automatically earn raffle tickets from your wagers.
                  </div>
                  <button onClick={() => navigateTo('profile')} style={{
                    padding:'0.5rem 1.25rem', background:'rgba(139,92,246,0.2)', border:'1px solid rgba(139,92,246,0.3)',
                    borderRadius:'8px', color:'#c084fc', fontSize:'0.82rem', fontWeight:700, cursor:'pointer'
                  }}>Link Shuffle Username →</button>
                </div>
              )
            ) : (
              <div style={{
                background:'rgba(14,14,18,0.7)', border:'1px solid rgba(139,92,246,0.15)',
                borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem', textAlign:'center'
              }}>
                <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.3rem'}}>Log in to see your tickets</div>
                <div style={{fontSize:'0.78rem', color:'#71717a'}}>
                  Sign in with Twitch and link your Shuffle username to track your raffle tickets.
                </div>
              </div>
            )}

            {/* Winner Display (hidden while draw modal is open to prevent render cascade) */}
            {raffleResult && !raffleRolling && !raffleDrawingModal && (
              <div style={{
                background: raffleResult.status === 'paid' ? 'rgba(34,197,94,0.06)' : 'rgba(245,158,11,0.06)',
                border:`1px solid ${raffleResult.status === 'paid' ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.15)'}`,
                borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem', textAlign:'center'
              }}>
                <div style={{fontSize:'0.7rem', color:'#71717a', letterSpacing:'1px', textTransform:'uppercase', marginBottom:'0.5rem'}}>
                  {raffleResult.status === 'paid' ? '✅ Winner — Paid' : '🎰 Winner'}
                </div>
                <div style={{
                  display:'inline-flex', alignItems:'center', gap:'0.75rem',
                  padding:'0.75rem 1.5rem', background:'rgba(0,0,0,0.4)',
                  borderRadius:'12px', border:'1px solid rgba(245,158,11,0.3)'
                }}>
                  <div style={{textAlign:'left'}}>
                    <div style={{fontSize:'1.3rem', fontWeight:900, color:'#fbbf24', textShadow:'0 0 20px rgba(245,158,11,0.4)'}}>
                      {censorUsername(raffleResult.winner_username)}
                    </div>
                    <div style={{fontSize:'0.72rem', color:'#a1a1aa'}}>
                      {raffleResult.winner_tickets} tickets • ${parseFloat(raffleResult.winner_wager || 0).toLocaleString(undefined, {maximumFractionDigits:0})} wagered
                      {raffleResult.total_tickets > 0 && ` • ${((raffleResult.winner_tickets / raffleResult.total_tickets) * 100).toFixed(2)}% chance`}
                    </div>
                  </div>
                  <div style={{textAlign:'right', marginLeft:'0.5rem'}}>
                    <div style={{fontSize:'1.5rem', fontWeight:900, color:'#22c55e'}}>$5,000</div>
                  </div>
                </div>
                <div style={{marginTop:'0.5rem', fontSize:'0.68rem', color:'#71717a'}}>
                  Drawn {new Date(raffleResult.picked_at).toLocaleString()} • {raffleResult.total_participants} participants • {raffleResult.total_tickets?.toLocaleString()} total tickets
                </div>

                {/* Admin controls inline */}
                {isAdmin && (
                  <div style={{marginTop:'0.75rem', display:'flex', gap:'0.5rem', justifyContent:'center', flexWrap:'wrap'}}>
                    <button onClick={() => pickRaffleWinnerAction(true)} style={{padding:'0.45rem 1rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.35)', borderRadius:'8px', color:'#fbbf24', fontSize:'0.78rem', fontWeight:700, cursor:'pointer'}}>🔄 Reroll (ineligible)</button>
                    {raffleResult.status !== 'paid' && (
                      <button onClick={() => payRaffleWinner(raffleResult.id)} style={{padding:'0.45rem 1rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.35)', borderRadius:'8px', color:'#22c55e', fontSize:'0.78rem', fontWeight:700, cursor:'pointer'}}>✅ Mark as Paid</button>
                    )}
                    <button onClick={() => deleteRaffleWinner(raffleResult.id)} style={{padding:'0.45rem 1rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'8px', color:'#f87171', fontSize:'0.68rem', fontWeight:600, cursor:'pointer'}}>🗑️ Delete</button>
                  </div>
                )}

                {/* Admin: Manual winner override */}
                {isAdmin && (
                  <div style={{marginTop:'0.75rem', padding:'0.75rem', background:'rgba(0,0,0,0.25)', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.06)'}}>
                    <div style={{fontSize:'0.72rem', color:'#71717a', marginBottom:'0.4rem', fontWeight:600}}>✏️ Manually Set Winner</div>
                    <div style={{display:'flex', gap:'0.4rem', alignItems:'center'}}>
                      <input
                        type="text" value={raffleManualSearch}
                        onChange={e => searchRaffleParticipants(e.target.value)}
                        placeholder="Search Shuffle username..."
                        style={{flex:1, padding:'0.4rem 0.7rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'6px', color:'#e2e8f0', fontSize:'0.78rem', outline:'none'}}
                      />
                      {raffleSearching && <span style={{fontSize:'0.7rem', color:'#71717a'}}>...</span>}
                    </div>
                    {raffleSearchResults.length > 0 && (
                      <div style={{marginTop:'0.4rem', display:'flex', flexDirection:'column', gap:'0.2rem', maxHeight:'180px', overflowY:'auto'}}>
                        {raffleSearchResults.map(p => (
                          <div key={p.username} onClick={() => setRaffleWinnerManually(p.username)}
                            style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.4rem 0.6rem', background:'rgba(0,0,0,0.3)', borderRadius:'6px', cursor:'pointer', border:'1px solid transparent', transition:'border-color 0.15s'}}
                            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(245,158,11,0.4)'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                          >
                            <span style={{fontSize:'0.8rem', fontWeight:700, color:'#e2e8f0'}}>{p.username}</span>
                            <span style={{fontSize:'0.7rem', color:'#a1a1aa'}}>{p.tickets} tickets • ${parseFloat(p.wager || 0).toLocaleString(undefined, {maximumFractionDigits:0})} • {p.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {raffleManualSearch.length >= 2 && raffleSearchResults.length === 0 && !raffleSearching && (
                      <div style={{marginTop:'0.3rem', fontSize:'0.7rem', color:'#71717a', fontStyle:'italic'}}>No participants found matching "{raffleManualSearch}"</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Admin: Draw button */}
            {isAdmin && !raffleResult && !raffleRolling && !raffleDrawingModal && (
              <div style={{textAlign:'center', marginBottom:'1rem'}}>
                <button onClick={() => pickRaffleWinnerAction(false)} style={{
                  padding:'0.85rem 2.5rem', background:'linear-gradient(135deg, #f59e0b, #d97706)',
                  border:'none', borderRadius:'12px', color:'#000', fontSize:'1rem', fontWeight:900,
                  cursor:'pointer', boxShadow:'0 0 25px rgba(245,158,11,0.3)', transition:'all 0.2s ease'
                }}>
                  🎲 Draw $5,000 Raffle Winner
                </button>
              </div>
            )}

            {/* Runners-up / Top Participants */}
            {!raffleDrawingModal && raffleParticipants?.participants?.length > 0 && (
              <div style={{
                background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)',
                borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem'
              }}>
                <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>
                  🏅 {raffleResult ? 'Closest to Winning' : 'Top Participants by Win Chance'}
                </div>
                <div style={{display:'flex', flexDirection:'column', gap:'0.3rem'}}>
                  {raffleParticipants.participants.slice(0, 20).map((p, i) => {
                    const isWinner = raffleResult && p.username.toLowerCase() === raffleResult.winner_username?.toLowerCase();
                    return (
                      <div key={p.username} style={{
                        display:'flex', alignItems:'center', gap:'0.6rem',
                        padding:'0.5rem 0.75rem', background: isWinner ? 'rgba(245,158,11,0.1)' : 'rgba(0,0,0,0.2)',
                        borderRadius:'8px', borderLeft: isWinner ? '3px solid #fbbf24' : '3px solid transparent'
                      }}>
                        <span style={{fontSize:'0.72rem', color:'#71717a', fontFamily:'monospace', minWidth:'24px'}}>#{p.rank}</span>
                        <span style={{fontSize:'0.82rem', fontWeight: isWinner ? 800 : 600, color: isWinner ? '#fbbf24' : '#e2e8f0', flex:1, minWidth:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                          {censorUsername(p.username)}
                          {isWinner && <span style={{marginLeft:'0.5rem', fontSize:'0.65rem', color:'#fbbf24'}}>🏆 WINNER</span>}
                        </span>
                        <span style={{fontSize:'0.72rem', color:'#a1a1aa', minWidth:'70px', textAlign:'right'}}>{p.tickets.toLocaleString()} tickets</span>
                        <span style={{fontSize:'0.72rem', fontWeight:700, color:'#fbbf24', minWidth:'55px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{p.percentage}%</span>
                      </div>
                    );
                  })}
                </div>
                {raffleParticipants.participants.length > 20 && (
                  <div style={{marginTop:'0.6rem', fontSize:'0.68rem', color:'#71717a', fontStyle:'italic', textAlign:'center'}}>
                    Showing top 20 of {raffleParticipants.totalParticipants} participants
                  </div>
                )}
              </div>
            )}

            {/* Raffle Draw Modal (full-screen overlay with reel) */}
            {raffleDrawingModal && (
              <div
                style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:5000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', backdropFilter:'blur(6px)'}}
                onClick={(e) => { if (e.target === e.currentTarget && !raffleRolling) { setRaffleDrawingModal(false); } }}
              >
                <div style={{maxWidth:'440px', width:'100%', background:'rgba(14,14,18,0.95)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'20px', overflow:'hidden'}} onClick={e => e.stopPropagation()}>
                  {raffleRolling ? (
                    <div style={{padding:'2.5rem 2rem', textAlign:'center'}}>
                      <div style={{fontSize:'0.85rem', fontWeight:800, color:'#fbbf24', letterSpacing:'3px', marginBottom:'1.5rem'}}>🎰 DRAWING $5,000 WINNER...</div>
                      <div style={{display:'flex', justifyContent:'center'}}>
                        <RaffleSlotReel participants={raffleParticipants?.participants || []} settledWinnerUsername={raffleReelWinner} />
                      </div>
                      <div style={{marginTop:'1rem', fontSize:'0.72rem', color:'#71717a'}}>
                        Rolling through {(raffleLottery?.totalTickets || 0).toLocaleString()} tickets...
                      </div>
                    </div>
                  ) : raffleResult ? (
                    <div style={{padding:'2rem 1.75rem', textAlign:'center'}}>
                      <div style={{fontSize:'0.75rem', color:'#fbbf24', fontWeight:800, letterSpacing:'3px', marginBottom:'1rem'}}>🎉 WINNER</div>
                      <div style={{fontSize:'2rem', fontWeight:900, color:'#fbbf24', textShadow:'0 0 30px rgba(245,158,11,0.5)', marginBottom:'0.25rem'}}>
                        {raffleResult.winner_username || 'Unknown'}
                      </div>
                      <div style={{fontSize:'2.5rem', fontWeight:900, color:'#22c55e', marginBottom:'0.25rem'}}>$5,000</div>
                      <div style={{fontSize:'0.78rem', color:'#a1a1aa', marginBottom:'1.25rem'}}>
                        {raffleResult.winner_tickets || 0} tickets • ${parseFloat(raffleResult.winner_wager || 0).toLocaleString(undefined, {maximumFractionDigits:0})} wagered
                        {Number(raffleResult.total_tickets) > 0 && ` • ${((Number(raffleResult.winner_tickets) / Number(raffleResult.total_tickets)) * 100).toFixed(2)}% chance`}
                      </div>
                      <div style={{display:'flex', gap:'0.5rem', justifyContent:'center'}}>
                        <button onClick={() => setRaffleDrawingModal(false)} style={{padding:'0.6rem 1.5rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'10px', color:'#fff', fontWeight:800, cursor:'pointer'}}>Close</button>
                        <button onClick={() => { pickRaffleWinnerAction(true); }} style={{padding:'0.6rem 1.5rem', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.35)', borderRadius:'10px', color:'#f87171', fontWeight:700, cursor:'pointer'}}>🔄 Reroll</button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Previous Raffle Winners (public) — hidden for now */}
            {false && raffleLotteryHistory && raffleLotteryHistory.length > 0 && (
              <div style={{
                background:'rgba(14,14,18,0.7)', border:'1px solid rgba(245,158,11,0.1)',
                borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem'
              }}>
                <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>🏆 Previous Raffle Winners</div>
                <div style={{display:'flex', flexDirection:'column', gap:'0.4rem'}}>
                  {raffleLotteryHistory.map(w => (
                    <div key={w.id} style={{
                      display:'flex', alignItems:'center', gap:'0.6rem',
                      padding:'0.55rem 0.75rem', background:'rgba(0,0,0,0.25)',
                      borderRadius:'8px', borderLeft:'3px solid rgba(245,158,11,0.4)'
                    }}>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:'0.85rem', fontWeight:700, color:'#fbbf24'}}>{censorUsername(w.winner_username)}</div>
                        <div style={{fontSize:'0.65rem', color:'#71717a'}}>
                          {w.winner_tickets} tickets • ${parseFloat(w.winner_wager || 0).toLocaleString(undefined, {maximumFractionDigits:0})} wagered
                          {w.total_tickets > 0 && ` • ${((w.winner_tickets / w.total_tickets) * 100).toFixed(2)}% chance`}
                        </div>
                      </div>
                      <div style={{textAlign:'right', flexShrink:0}}>
                        <div style={{fontSize:'1rem', fontWeight:900, color:'#22c55e'}}>${parseFloat(w.prize_amount || 5000).toLocaleString()}</div>
                        <div style={{fontSize:'0.6rem', color: w.status === 'paid' ? '#22c55e' : '#fbbf24', fontWeight:700}}>
                          {w.status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                        </div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => deleteRaffleWinner(w.id)} title="Delete this winner" style={{padding:'0.2rem 0.45rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.6rem', cursor:'pointer', fontWeight:600, flexShrink:0}}>🗑️</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How It Works */}
            <div style={{
              background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)',
              borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem'
            }}>
              <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>How It Works</div>
              {[
                { icon: '🎰', title: 'Wager on Shuffle', desc: 'Play under guild name SNUTZY on Shuffle.com during the leaderboard period.' },
                { icon: '🎟️', title: 'Earn Tickets Automatically', desc: 'Every $100 you wager earns 1 raffle ticket. Tickets accumulate all month.' },
                { icon: '🔗', title: 'Link Your Account', desc: 'Make sure your Shuffle username is connected on the site so your tickets count.' },
                { icon: '🏆', title: 'Winner Drawn at Reset', desc: 'When the monthly leaderboard resets, one ticket is randomly selected. More tickets = higher chance!' },
                { icon: '💰', title: 'Win $5,000', desc: 'The winner receives $5,000 paid directly to their Shuffle account.' }
              ].map((step, i) => (
                <div key={i} style={{display:'flex', gap:'0.75rem', alignItems:'flex-start', marginBottom: i < 4 ? '0.6rem' : 0}}>
                  <div style={{fontSize:'1.2rem', flexShrink:0, marginTop:'0.1rem'}}>{step.icon}</div>
                  <div>
                    <div style={{fontSize:'0.8rem', fontWeight:700, color:'#e2e8f0'}}>{step.title}</div>
                    <div style={{fontSize:'0.72rem', color:'#71717a', lineHeight:'1.4'}}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ */}
            <div style={{
              background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)',
              borderRadius:'14px', padding:'1.25rem'
            }}>
              <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>FAQ</div>
              {[
                { q: 'How are tickets calculated?', a: 'Your total wager during the monthly leaderboard period is divided by $100. For example, $5,000 wagered = 50 tickets.' },
                { q: 'Do I need to do anything to enter?', a: 'Just wager under guild SNUTZY with your Shuffle username linked on the site. Tickets are calculated automatically.' },
                { q: 'When is the winner drawn?', a: 'The winner is drawn automatically when the monthly leaderboard resets (24th of each month at 00:00 UK time).' },
                { q: 'How does the random selection work?', a: 'A random ticket number is picked from the total pool. If you have 50 out of 1,000 total tickets, you have a 5% chance of winning.' },
                { q: 'How is the prize paid?', a: 'The $5,000 prize is sent directly to your Shuffle account within 48 hours.' }
              ].map((faq, i) => (
                <div key={i} style={{marginBottom: i < 4 ? '0.6rem' : 0}}>
                  <div style={{fontSize:'0.78rem', fontWeight:700, color:'#c084fc'}}>{faq.q}</div>
                  <div style={{fontSize:'0.72rem', color:'#71717a', lineHeight:'1.4'}}>{faq.a}</div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div style={{textAlign:'center', marginTop:'1.25rem'}}>
              <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer" style={{
                display:'inline-block', padding:'0.65rem 2rem',
                background:'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius:'10px',
                color:'#000', fontSize:'0.9rem', fontWeight:800, textDecoration:'none'
              }}>Start Wagering on Shuffle →</a>
            </div>
          </div>
        )}

        {/* Viewer Bonus Hunt Page */}
        {activePage === 'viewerhunt' && (() => {
          const vh = viewerHuntData;
          const session = vh?.session;
          const entries = vh?.entries || [];
          const stats = vh?.stats || {};
          const pendingEntries = entries.filter(e => e.status === 'pending');
          const drawnEntries = entries.filter(e => e.drawn_order).sort((a,b) => b.drawn_order - a.drawn_order);
          const activeDrawSet = vh?.activeDrawSet;
          const inSetCards = activeDrawSet ? entries.filter(e => e.draw_set_id === activeDrawSet.id && e.status === 'in_set') : [];
          const setRevealedCards = activeDrawSet ? entries.filter(e => e.draw_set_id === activeDrawSet.id && e.status === 'drawn') : [];
          const hasUnmarkedReveal = setRevealedCards.length > 0;
          const isOpen = session?.status === 'open';
          const isClosed = session?.status === 'closed' || session?.status === 'drawing';
          const noSession = !session || session.status === 'finished';
          
          return (
            <div style={{maxWidth:'1100px', margin:'0 auto', padding:'0 1rem'}}>
              {/* Header */}
              <div style={{textAlign:'center', marginBottom:'1.5rem'}}>
                <h1 style={{fontSize:'2rem', fontWeight:900, margin:'0 0 0.4rem', background:'linear-gradient(135deg, #a855f7, #ec4899)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent'}}>🎴 Viewer Bonus Hunt</h1>
                <p style={{color:'#a1a1aa', fontSize:'0.9rem', margin:0}}>Type <code style={{background:'rgba(168,85,247,0.15)', padding:'0.15rem 0.5rem', borderRadius:'4px', color:'#c084fc'}}>!slot &lt;slot name&gt;</code> in chat to enter. One pick per person — re-typing overwrites your previous pick. You can keep entering and changing picks even while we draw, until your card is pulled.</p>
              </div>

              {/* Status Banner */}
              {noSession && (
                <div style={{textAlign:'center', padding:'3rem', background:'rgba(14,14,18,0.7)', borderRadius:'16px', border:'1px dashed rgba(168,85,247,0.2)'}}>
                  <div style={{fontSize:'3rem', marginBottom:'0.5rem'}}>🎴</div>
                  <div style={{color:'#a1a1aa', fontWeight:600}}>No active viewer hunt right now</div>
                  <p style={{color:'#71717a', fontSize:'0.8rem'}}>Check back when an admin starts a session!</p>
                  {isAdmin && (
                    <button onClick={() => { const t = prompt('Session title (optional):'); vhAdminAction('/api/admin/viewer-hunt/create', { title: t }); }} style={{marginTop:'1rem', padding:'0.7rem 1.5rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'10px', color:'#fff', fontSize:'0.9rem', fontWeight:700, cursor:'pointer'}}>+ Create New Session</button>
                  )}
                </div>
              )}

              {session && session.status !== 'finished' && (
                <>
                  {/* Status pill */}
                  <div style={{textAlign:'center', marginBottom:'1.25rem'}}>
                    <span style={{display:'inline-block', padding:'0.5rem 1.25rem', borderRadius:'999px', fontWeight:800, fontSize:'0.85rem', letterSpacing:'1.5px', background: isOpen ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: isOpen ? '#22c55e' : '#fbbf24', border: `1px solid ${isOpen ? 'rgba(34,197,94,0.4)' : 'rgba(245,158,11,0.4)'}`, boxShadow: isOpen ? '0 0 20px rgba(34,197,94,0.2)' : 'none'}}>
                      {isOpen && <span style={{display:'inline-block', width:'8px', height:'8px', borderRadius:'50%', background:'#22c55e', marginRight:'0.5rem', animation:'pulse 1.5s infinite'}}></span>}
                      {isOpen ? (drawnEntries.length > 0 ? 'OPEN — DRAWING LIVE' : 'ENTRIES OPEN') : session.status === 'closed' ? 'POOL LOCKED' : 'DRAWING'}
                    </span>
                  </div>

                  {/* Stats grid */}
                  <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'0.75rem', marginBottom:'1.5rem'}}>
                    {[
                      {label:'Total Entries', value:stats.totalEntries||0, color:'#a78bfa'},
                      {label:'In the Pool', value:stats.pending||0, color:'#22c55e'},
                      {label:'Drawn', value:(stats.drawn||0)+(stats.gotBonus||0)+(stats.noBonus||0), color:'#fbbf24'},
                      {label:'Got Bonus', value:stats.gotBonus||0, color:'#22c55e'}
                    ].map((s,i) => (
                      <div key={i} style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'0.85rem', textAlign:'center'}}>
                        <div style={{fontSize:'0.62rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.3rem'}}>{s.label}</div>
                        <div style={{fontSize:'1.7rem', fontWeight:900, color:s.color, fontFamily:'monospace'}}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Card draw area */}
                  <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(168,85,247,0.2)', borderRadius:'16px', padding:'2rem', marginBottom:'1.5rem', minHeight:'460px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden'}}>
                    <div style={{position:'absolute', inset:0, background:'radial-gradient(circle at center, rgba(168,85,247,0.06) 0%, transparent 70%)', pointerEvents:'none'}} />
                    
                    {drawingCard ? (
                      <div style={{position:'relative', zIndex:1, textAlign:'center'}}>
                        <div style={{position:'relative', width:'230px', height:'320px', margin:'0 auto'}}>
                          {[0,1,2,3,4].map(i => (
                            <div key={i} style={{position:'absolute', top:0, left:0, width:'230px', height:'320px', borderRadius:'16px', background:'#7c3aed', backgroundImage:`url(/shuffle-tile.png)`, backgroundSize:'cover', backgroundPosition:'center', border:'2px solid rgba(255,255,255,0.2)', boxShadow:'0 8px 30px rgba(168,85,247,0.4)', animation:`shuffle${i} 0.5s ease-in-out infinite`}} />
                          ))}
                        </div>
                        <div style={{marginTop:'1.25rem', color:'#c084fc', fontWeight:700, letterSpacing:'2px', fontSize:'0.95rem'}}>SHUFFLING...</div>
                      </div>
                    ) : activeDrawSet && (inSetCards.length > 0 || setRevealedCards.length > 0) ? (
                      <div style={{position:'relative', zIndex:1, width:'100%'}}>
                        <div style={{textAlign:'center', marginBottom:'1.25rem', color:'#c084fc', fontSize:'0.75rem', fontWeight:700, letterSpacing:'2px'}}>
                          {hasUnmarkedReveal ? '✋ MARK THE REVEALED CARD FIRST' : `🎴 PICK A CARD (${inSetCards.length} REMAINING)`}
                        </div>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(2, 220px)', justifyContent:'center', gap:'1rem'}}>
                          {/* Render in fixed order: draw_set_id sorted by id */}
                          {[...inSetCards, ...setRevealedCards].sort((a,b) => a.id - b.id).map(card => {
                            const isRevealed = card.status === 'drawn';
                            const isLocked = !isRevealed && hasUnmarkedReveal;
                            return (
                              <div key={card.id} 
                                onClick={() => {
                                  if (isRevealed) return;
                                  if (isLocked) { alert('Mark the currently revealed card as Got Bonus or No Bonus first.'); return; }
                                  playCardFlipSound();
                                  vhAdminAction(`/api/admin/viewer-hunt/entry/${card.id}/reveal`);
                                }}
                                className={!isRevealed && !isLocked && isAdmin ? 'vh-card-interactive' : ''}
                                style={{
                                  // minHeight (not fixed height): the revealed card grows to fit
                                  // avatar + slot art + buttons; grid stretch keeps the card-backs
                                  // in the same row matched, and the tile background covers any size.
                                  width:'220px', minHeight:'310px', borderRadius:'16px',
                                  border:'2px solid rgba(255,255,255,0.15)',
                                  cursor: isRevealed ? 'default' : isLocked ? 'not-allowed' : isAdmin ? 'pointer' : 'default',
                                  opacity: isLocked ? 0.5 : 1,
                                  transition:'all 0.3s ease',
                                  position:'relative', overflow:'hidden',
                                  background: isRevealed ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(168,85,247,0.12) 100%)' : '#7c3aed',
                                  backgroundImage: isRevealed ? undefined : `url(/shuffle-tile.png)`,
                                  backgroundSize:'cover', backgroundPosition:'center',
                                  boxShadow: isRevealed ? '0 12px 40px rgba(168,85,247,0.5)' : '0 8px 25px rgba(0,0,0,0.4)',
                                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'1.1rem',
                                  animation: isRevealed ? 'cardReveal 0.6s ease-out' : undefined
                                }}>
                                {isRevealed && (
                                  <>
                                    {card.twitch_avatar ? <img src={card.twitch_avatar} alt="" style={{width:'60px', height:'60px', borderRadius:'50%', border:'3px solid rgba(168,85,247,0.6)', marginBottom:'0.5rem'}} /> : <div style={{width:'60px', height:'60px', borderRadius:'50%', background:'rgba(168,85,247,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.6rem', marginBottom:'0.5rem'}}>👤</div>}
                                    <div style={{fontSize:'0.95rem', fontWeight:800, color:'#fff', textAlign:'center', marginBottom:'0.5rem', lineHeight:1.15, wordBreak:'break-word'}}>{card.twitch_display_name}</div>
                                    <div style={{padding:'0.5rem', background:'rgba(0,0,0,0.4)', borderRadius:'8px', textAlign:'center', width:'100%'}}>
                                      {(() => {
                                        // Fuzzy-match the chat-typed name against the Shuffle catalog
                                        // so the card shows real art rather than plain text.
                                        const art = findSlotArt(allSlots, card.slot_name);
                                        return art?.image ? (
                                          <img src={art.image} alt="" loading="lazy" style={{width:'96px', aspectRatio:'3 / 4', objectFit:'cover', borderRadius:'8px', margin:'0 auto 0.4rem', display:'block', border:'1px solid rgba(251,191,36,0.35)', boxShadow:'0 6px 18px rgba(0,0,0,0.45)'}} />
                                        ) : null;
                                      })()}
                                      <div style={{fontSize:'0.65rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1.2px', marginBottom:'0.1rem'}}>Slot</div>
                                      <div style={{fontSize:'0.95rem', fontWeight:900, color:'#fbbf24', lineHeight:1.15, wordBreak:'break-word'}}>{card.slot_name}</div>
                                    </div>
                                    {isAdmin && (
                                      <div style={{display:'flex', gap:'0.35rem', marginTop:'0.55rem', width:'100%'}}>
                                        <button onClick={(e) => { e.stopPropagation(); vhAdminAction(`/api/admin/viewer-hunt/entry/${card.id}/mark`, {result:'got_bonus'}); }} title="Got Bonus" style={{flex:1, padding:'0.5rem', background:'rgba(34,197,94,0.2)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'6px', color:'#22c55e', fontSize:'0.8rem', fontWeight:700, cursor:'pointer'}}>✅</button>
                                        <button onClick={(e) => { e.stopPropagation(); vhAdminAction(`/api/admin/viewer-hunt/entry/${card.id}/mark`, {result:'no_bonus'}); }} title="No Bonus" style={{flex:1, padding:'0.5rem', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.35)', borderRadius:'6px', color:'#f87171', fontSize:'0.8rem', fontWeight:700, cursor:'pointer'}}>❌</button>
                                        <button onClick={(e) => { e.stopPropagation(); vhAdminAction(`/api/admin/viewer-hunt/entry/${card.id}/return-to-pool`); }} title="Put Back in Pool (duplicate / unsuitable — viewer stays entered, card returns to pending)" style={{flex:1, padding:'0.5rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'6px', color:'#fbbf24', fontSize:'0.8rem', fontWeight:700, cursor:'pointer'}}>↩️</button>
                                        <button onClick={(e) => { e.stopPropagation(); const s = prompt(`Change ${card.twitch_display_name}'s slot:`, card.slot_name); if (s === null) return; if (s.trim().length < 2) { alert('Slot name too short'); return; } vhAdminAction(`/api/admin/viewer-hunt/entry/${card.id}/edit-slot`, { slotName: s.trim() }); }} title="Edit slot pick" style={{flex:1, padding:'0.5rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', color:'#d4d4d8', fontSize:'0.8rem', fontWeight:700, cursor:'pointer'}}>✏️</button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {isAdmin && inSetCards.length > 0 && (
                          <div style={{textAlign:'center', marginTop:'1.25rem'}}>
                            <button onClick={() => { if (confirm('Reshuffle remaining cards back into the pool?')) vhAdminAction(`/api/admin/viewer-hunt/${session.id}/reshuffle`); }} style={{padding:'0.6rem 1.25rem', background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.35)', borderRadius:'8px', color:'#fbbf24', fontSize:'0.78rem', fontWeight:700, cursor:'pointer'}}>🔄 Reshuffle Remaining</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{textAlign:'center', position:'relative', zIndex:1}}>
                        <div style={{fontSize:'4rem', marginBottom:'0.5rem', opacity:0.5}}>🎴</div>
                        <div style={{color:'#a1a1aa', fontSize:'0.95rem'}}>{isOpen ? `${pendingEntries.length} entries in the pool — chat keeps adding` : isClosed ? 'Pool locked, ready to draw!' : ''}</div>
                        {isAdmin && (isOpen || isClosed) && pendingEntries.length > 0 && (
                          <button onClick={() => drawCard(session.id)} style={{marginTop:'1rem', padding:'0.85rem 2rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'12px', color:'#fff', fontSize:'1rem', fontWeight:800, cursor:'pointer', boxShadow:'0 0 25px rgba(168,85,247,0.4)'}}>🎴 Draw Pack of Cards</button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Admin controls */}
                  {isAdmin && (
                    <div style={{display:'flex', gap:'0.5rem', justifyContent:'center', marginBottom:'1.25rem', flexWrap:'wrap'}}>
                      {session.status === 'setup' && <button onClick={() => vhAdminAction(`/api/admin/viewer-hunt/${session.id}/open`)} style={{padding:'0.6rem 1.25rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'8px', color:'#22c55e', fontWeight:700, cursor:'pointer'}}>▶ Open Entries</button>}
                      {isOpen && <button onClick={() => { if (confirm('Lock the pool? Chat won\'t be able to add or change picks anymore. (Optional — you can keep entries open while drawing if you prefer.)')) vhAdminAction(`/api/admin/viewer-hunt/${session.id}/close`); }} style={{padding:'0.6rem 1.25rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'8px', color:'#fbbf24', fontWeight:700, cursor:'pointer'}}>🔒 Lock Pool (optional)</button>}
                      <button onClick={() => { if (confirm('Finish session? No more changes.')) vhAdminAction(`/api/admin/viewer-hunt/${session.id}/finish`); }} style={{padding:'0.6rem 1.25rem', background:'rgba(113,113,122,0.15)', border:'1px solid rgba(113,113,122,0.3)', borderRadius:'8px', color:'#a1a1aa', fontWeight:700, cursor:'pointer'}}>🏁 Finish</button>
                    </div>
                  )}

                  {/* Bonus picks admin card */}
                  {isAdmin && session.status !== 'finished' && (
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'12px', padding:'1rem 1.25rem', marginBottom:'1.25rem'}}>
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.6rem', gap:'0.75rem', flexWrap:'wrap'}}>
                        <div style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0'}}>🎁 Bonus Picks</div>
                        <div style={{fontSize:'0.7rem', color:'#71717a'}}>Grant a viewer an extra <code style={{color:'#c084fc'}}>!slot</code> call</div>
                      </div>
                      <div style={{display:'flex', gap:'0.4rem', marginBottom:'0.75rem'}}>
                        <input
                          type="text"
                          placeholder="twitch username"
                          value={bonusPickInput}
                          onChange={(e) => setBonusPickInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); grantBonusPick(session.id); } }}
                          style={{flex:1, padding:'0.5rem 0.75rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(168,85,247,0.25)', borderRadius:'6px', color:'#fff', fontSize:'0.85rem', outline:'none'}}
                        />
                        <button onClick={() => grantBonusPick(session.id)} style={{padding:'0.5rem 1rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'6px', color:'#fff', fontSize:'0.8rem', fontWeight:700, cursor:'pointer'}}>+ Grant</button>
                      </div>
                      {viewerHuntData?.bonusAllowances?.length > 0 ? (
                        <div style={{display:'flex', flexDirection:'column', gap:'0.3rem'}}>
                          {viewerHuntData.bonusAllowances.map(a => (
                            <div key={a.twitch_username_lower} style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.4rem 0.65rem', background:'rgba(168,85,247,0.08)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'6px', fontSize:'0.78rem'}}>
                              <span style={{color:'#e2e8f0', fontWeight:600, flex:1}}>{a.twitch_username_lower}</span>
                              <span style={{color:'#c084fc', fontWeight:700, fontSize:'0.72rem'}}>{a.max_picks} picks</span>
                              <button onClick={() => revokeBonusPick(session.id, a.twitch_username_lower)} title="Revoke one bonus pick" style={{padding:'0.2rem 0.5rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'4px', color:'#f87171', fontSize:'0.68rem', cursor:'pointer', fontWeight:700}}>−1</button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{fontSize:'0.72rem', color:'#71717a', fontStyle:'italic'}}>No bonus picks granted yet this session.</div>
                      )}
                    </div>
                  )}

                  {/* Recent draws */}
                  {drawnEntries.length > 0 && (
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'1.25rem', marginBottom:'1.5rem'}}>
                      <div style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>📜 Drawn Cards ({drawnEntries.length})</div>
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem'}}>
                        {drawnEntries.map(e => (
                          <div key={e.id} style={{display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 0.75rem', background:'rgba(0,0,0,0.2)', borderRadius:'8px', borderLeft:`3px solid ${e.status==='got_bonus'||e.status==='winner'?'#22c55e':e.status==='no_bonus'?'#ef4444':e.status==='skipped'?'#71717a':'#fbbf24'}`}}>
                            <span style={{fontSize:'0.7rem', color:'#71717a', fontFamily:'monospace', minWidth:'24px'}}>#{e.drawn_order}</span>
                            {e.twitch_avatar && <img src={e.twitch_avatar} alt="" style={{width:'22px', height:'22px', borderRadius:'50%'}} />}
                            <span style={{fontSize:'0.78rem', color:'#e2e8f0', fontWeight:600, minWidth:'120px'}}>{e.twitch_display_name}</span>
                            <span style={{fontSize:'0.78rem', color:'#fbbf24', flex:1}}>{e.slot_name}</span>
                            <span style={{fontSize:'0.65rem', padding:'0.15rem 0.5rem', borderRadius:'4px', fontWeight:700, background: e.status==='got_bonus'||e.status==='winner'?'rgba(34,197,94,0.15)':e.status==='no_bonus'?'rgba(239,68,68,0.12)':e.status==='skipped'?'rgba(113,113,122,0.15)':'rgba(245,158,11,0.15)', color: e.status==='got_bonus'||e.status==='winner'?'#22c55e':e.status==='no_bonus'?'#f87171':e.status==='skipped'?'#a1a1aa':'#fbbf24'}}>{e.status==='winner'?`🏆 $${e.prize_amount}`:e.status.replace('_',' ')}</span>
                            {/* Slot edit — typo fixes / banned picks, any drawn row */}
                            {isAdmin && (
                              <button onClick={() => { const s = prompt(`Change ${e.twitch_display_name}'s slot:`, e.slot_name); if (s === null) return; if (s.trim().length < 2) { alert('Slot name too short'); return; } vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/edit-slot`, { slotName: s.trim() }); }} title="Edit slot pick" style={{padding:'0.2rem 0.45rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'4px', color:'#a1a1aa', fontSize:'0.65rem', cursor:'pointer'}}>✏️</button>
                            )}
                            {/* Unmarked (revealed / force-picked) cards get their mark buttons
                                right here — force-picked cards never enter the card grid. */}
                            {isAdmin && e.status === 'drawn' && (
                              <>
                                <button onClick={() => vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/mark`, {result:'got_bonus'})} title="Got Bonus" style={{padding:'0.2rem 0.5rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.35)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>✅</button>
                                <button onClick={() => vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/mark`, {result:'no_bonus'})} title="No Bonus" style={{padding:'0.2rem 0.5rem', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>❌</button>
                              </>
                            )}
                            {/* Re-mark buttons — for fixing accidental got/no calls.
                                Only show on terminal non-winner states; flipping a winner needs
                                its own dedicated unmark flow above. */}
                            {isAdmin && e.status === 'no_bonus' && (
                              <button onClick={() => { if (confirm(`Change "${e.twitch_display_name}" — ${e.slot_name} from NO BONUS to GOT BONUS?`)) vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/mark`, {result:'got_bonus'}); }} title="Re-mark as Got Bonus" style={{padding:'0.2rem 0.5rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>✏️ → ✅</button>
                            )}
                            {isAdmin && e.status === 'got_bonus' && (
                              <button onClick={() => { if (confirm(`Change "${e.twitch_display_name}" — ${e.slot_name} from GOT BONUS to NO BONUS?${e.prize_amount > 0 ? ' Prize amount will be cleared.' : ''}`)) vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/mark`, {result:'no_bonus', prizeAmount: 0}); }} title="Re-mark as No Bonus" style={{padding:'0.2rem 0.5rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>✏️ → ❌</button>
                            )}
                            {isAdmin && e.status === 'got_bonus' && (
                              <button onClick={() => { const m = prompt('Multiplier (e.g. 250 for 250x):', ''); if (m === null) return; const p = prompt('Prize amount $:', '0'); if (p !== null) vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/mark`, {result:'winner', prizeAmount: parseFloat(p)||0, multiplier: m === '' ? null : parseFloat(m)}); }} style={{padding:'0.2rem 0.5rem', background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'4px', color:'#c084fc', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>🏆 Mark Winner</button>
                            )}
                            {isAdmin && e.status === 'winner' && (
                              <>
                                <button onClick={() => { const m = prompt('Edit multiplier (e.g. 250):', e.multiplier || ''); if (m === null) return; const p = prompt('Edit prize amount $:', e.prize_amount); if (p !== null) vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/mark`, {result:'winner', prizeAmount: parseFloat(p)||0, multiplier: m === '' ? null : parseFloat(m)}); }} style={{padding:'0.2rem 0.5rem', background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'4px', color:'#c084fc', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>✏️ Edit</button>
                                <button onClick={() => { if (confirm('Unmark winner? Prize will be removed from totals.')) vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/mark`, {result:'got_bonus', prizeAmount: 0}); }} style={{padding:'0.2rem 0.5rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>↩</button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending pool list */}
                  {pendingEntries.length > 0 && (
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'1.25rem'}}>
                      <div style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>🎴 In the Pool ({pendingEntries.length})</div>
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'0.5rem'}}>
                        {pendingEntries.map(e => (
                          <div key={e.id} style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 0.7rem', background:'rgba(168,85,247,0.05)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'8px'}}>
                            {e.twitch_avatar && <img src={e.twitch_avatar} alt="" style={{width:'22px', height:'22px', borderRadius:'50%'}} />}
                            {(() => {
                              const art = findSlotArt(allSlots, e.slot_name);
                              return art?.image
                                ? <img src={art.image} alt="" loading="lazy" style={{width:'30px', height:'22px', objectFit:'cover', borderRadius:'4px', flexShrink:0, border:'1px solid rgba(255,255,255,0.1)'}} />
                                : null;
                            })()}
                            <div style={{minWidth:0, flex:1}}>
                              <div style={{fontSize:'0.72rem', color:'#c084fc', fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', display:'flex', alignItems:'center', gap:'0.35rem'}}>
                                <span>{e.twitch_display_name}</span>
                                {e.pick_number > 1 && <span style={{fontSize:'0.6rem', padding:'0.05rem 0.35rem', background:'rgba(251,191,36,0.18)', border:'1px solid rgba(251,191,36,0.35)', borderRadius:'3px', color:'#fbbf24', fontWeight:800}}>#{e.pick_number}</span>}
                              </div>
                              <div style={{fontSize:'0.7rem', color:'#a1a1aa', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{e.slot_name}</div>
                            </div>
                            {isAdmin && (
                              <div style={{display:'flex', gap:'0.25rem', flexShrink:0}}>
                                <button onClick={() => { const s = prompt(`Change ${e.twitch_display_name}'s slot pick:`, e.slot_name); if (s === null) return; if (s.trim().length < 2) { alert('Slot name too short'); return; } vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/edit-slot`, { slotName: s.trim() }); }} title="Edit this viewer's slot pick" style={{padding:'0.2rem 0.4rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'4px', color:'#a1a1aa', fontSize:'0.65rem', cursor:'pointer'}}>✏️</button>
                                <button onClick={() => { if (confirm(`Pick "${e.twitch_display_name}" — ${e.slot_name} directly into the drawn list?\n\n(Skips the card pack — use for restoring someone returned to the pool by mistake.)`)) vhAdminAction(`/api/admin/viewer-hunt/entry/${e.id}/force-pick`); }} title="Force-pick straight into the drawn list (recover an accidental return-to-pool)" style={{padding:'0.2rem 0.4rem', background:'rgba(168,85,247,0.12)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'4px', color:'#c084fc', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>⭐</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Viewer Hunt prize counter — uses publicPayoutStats from app-level fetch */}
              {publicPayoutStats?.viewerHunt > 0 && (
                <div style={{display:'flex', justifyContent:'center', margin:'1.5rem 0'}}>
                  <OdometerCounter value={publicPayoutStats.viewerHunt} label="Viewer Hunt $ Won" size="small" />
                </div>
              )}
              
              {/* Past Sessions History (collapsible) */}
              <div style={{marginTop:'1.5rem', marginBottom:'1.5rem'}}>
                <button onClick={() => { const next = !showVhHistory; setShowVhHistory(next); if (next) fetchViewerHuntHistory(); }} style={{width:'100%', padding:'0.85rem 1.25rem', background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', color:'#e2e8f0', fontSize:'0.85rem', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                  <span>📜 Past Sessions & Winners</span>
                  <span style={{fontSize:'0.7rem', color:'#71717a'}}>{showVhHistory ? '▲ Hide' : '▼ Show'}</span>
                </button>
                {showVhHistory && viewerHuntHistory && (
                  <div style={{marginTop:'0.6rem', display:'flex', flexDirection:'column', gap:'0.6rem'}}>
                    {viewerHuntHistory.sessions.length === 0 ? (
                      <div style={{textAlign:'center', padding:'1.5rem', color:'#71717a', fontSize:'0.85rem', background:'rgba(14,14,18,0.5)', borderRadius:'10px'}}>No past sessions yet</div>
                    ) : viewerHuntHistory.sessions.map(s => (
                      <div key={s.id} style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'12px', padding:'1rem 1.25rem'}}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.6rem', flexWrap:'wrap', gap:'0.5rem'}}>
                          <div>
                            <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0'}}>{s.title || `Session #${s.id}`}</div>
                            <div style={{fontSize:'0.68rem', color:'#71717a'}}>{new Date(s.finished_at || s.created_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}</div>
                          </div>
                          <div style={{display:'flex', alignItems:'center', gap:'0.85rem', fontSize:'0.7rem'}}>
                            <span style={{color:'#a1a1aa'}}>{s.totalEntries} entries</span>
                            <span style={{color:'#fbbf24'}}>{s.total_drawn} drawn</span>
                            {parseFloat(s.total_paid_out) > 0 && <span style={{color:'#22c55e', fontWeight:700}}>${parseFloat(s.total_paid_out).toLocaleString()} paid</span>}
                            {isAdmin && (
                              <button onClick={async () => { if (!confirm(`Delete session "${s.title || `#${s.id}`}" entirely? This removes all entries and prize records.`)) return; const t = getAuthToken(); const r = await fetch(`${BACKEND_API_URL}/api/admin/viewer-hunt/${s.id}`, {method:'DELETE', headers:{'Authorization':`Bearer ${t}`}}); if (r.ok) { fetchViewerHuntHistory(); fetchViewerHunt(); } else { const d = await r.json(); alert(d.error||'Failed'); } }} style={{padding:'0.25rem 0.6rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'5px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', fontWeight:700}}>🗑️ Delete</button>
                            )}
                          </div>
                        </div>
                        {s.winners.length > 0 ? (
                          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:'0.5rem', marginTop:'0.6rem'}}>
                            {s.winners.map(w => (
                              <div key={w.id} style={{display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.55rem 0.7rem', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'8px'}}>
                                {w.twitch_avatar && <img src={w.twitch_avatar} alt="" style={{width:'28px', height:'28px', borderRadius:'50%'}} />}
                                <div style={{flex:1, minWidth:0}}>
                                  <div style={{fontSize:'0.75rem', fontWeight:700, color:'#22c55e', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>🏆 {w.twitch_display_name}</div>
                                  <div style={{fontSize:'0.68rem', color:'#a1a1aa', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{w.slot_name}</div>
                                </div>
                                <div style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                  {w.multiplier && <div style={{fontSize:'0.7rem', fontWeight:800, color:'#fbbf24', background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.3)', padding:'0.15rem 0.4rem', borderRadius:'5px', fontFamily:'monospace'}}>{parseFloat(w.multiplier).toLocaleString()}x</div>}
                                  <div style={{fontSize:'0.85rem', fontWeight:900, color:'#22c55e', fontFamily:'monospace'}}>${parseFloat(w.prize_amount).toLocaleString()}</div>
                                </div>
                                {isAdmin && (
                                  <div style={{display:'flex', flexDirection:'column', gap:'0.15rem'}}>
                                    <button onClick={async () => { const m = prompt('Edit multiplier (e.g. 250):', w.multiplier || ''); if (m === null) return; const p = prompt('Edit prize $:', w.prize_amount); if (p === null) return; await vhAdminAction(`/api/admin/viewer-hunt/entry/${w.id}/mark`, {result:'winner', prizeAmount: parseFloat(p)||0, multiplier: m === '' ? null : parseFloat(m)}); fetchViewerHuntHistory(); }} title="Edit prize" style={{padding:'0.1rem 0.3rem', background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'3px', color:'#c084fc', fontSize:'0.55rem', cursor:'pointer', fontWeight:700}}>✏️</button>
                                    <button onClick={async () => { if (!confirm('Remove winner status? Prize amount will be removed from totals.')) return; await vhAdminAction(`/api/admin/viewer-hunt/entry/${w.id}/mark`, {result:'got_bonus', prizeAmount: 0}); fetchViewerHuntHistory(); }} title="Unmark winner" style={{padding:'0.1rem 0.3rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'3px', color:'#f87171', fontSize:'0.55rem', cursor:'pointer', fontWeight:700}}>↩</button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{fontSize:'0.72rem', color:'#71717a', textAlign:'center', padding:'0.4rem'}}>No winners marked for this session</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <style>{`
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
                .vh-card-interactive:hover { transform: translateY(-12px) scale(1.04); box-shadow: 0 16px 50px rgba(168,85,247,0.7), 0 0 30px rgba(192,132,252,0.5) !important; border-color: rgba(192,132,252,0.8) !important; }
                @keyframes cardReveal { 0%{transform:scale(0.3) rotateY(180deg);opacity:0} 100%{transform:scale(1) rotateY(0);opacity:1} }
                @keyframes shuffle0 { 0%,100%{transform:translateX(0) rotate(0)} 50%{transform:translateX(-30px) rotate(-8deg)} }
                @keyframes shuffle1 { 0%,100%{transform:translateX(0) rotate(0)} 50%{transform:translateX(20px) rotate(5deg)} }
                @keyframes shuffle2 { 0%,100%{transform:translateX(0) rotate(0)} 50%{transform:translateX(-15px) rotate(-3deg)} }
                @keyframes shuffle3 { 0%,100%{transform:translateX(0) rotate(0)} 50%{transform:translateX(25px) rotate(6deg)} }
                @keyframes shuffle4 { 0%,100%{transform:translateX(0) rotate(0)} 50%{transform:translateX(-10px) rotate(-4deg)} }
              `}</style>
            </div>
          );
        })()}

        {/* ============================================
            CHAT GIVEAWAY PAGE
            ============================================ */}
        {activePage === 'chatgiveaway' && (() => {
          // Public-readable page. Admin-only sections are gated individually below.
          const session = cgData?.session;
          const winners = cgData?.winners || [];
          const activeWinners = winners.filter(w => !w.invalidated_at);
          const entryCount = cgData?.entryCount || 0;
          const isOpen = session?.status === 'open';
          const isClosed = session?.status === 'closed';
          const isFinished = session?.status === 'finished';
          // Draw whenever there are entries. Admin can run through multiple winners
          // back-to-back; prize amounts get entered later once each bonus plays out.
          const canDraw = (isOpen || isClosed) && entryCount > 0;

          return (
            <div style={{maxWidth:'1100px', margin:'0 auto', padding:'0 1rem'}}>
              <div style={{textAlign:'center', marginBottom:'1.5rem'}}>
                <div style={{fontSize:'1.8rem', fontWeight:900, background:'linear-gradient(135deg, #a855f7, #ec4899)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', marginBottom:'0.3rem'}}>🎉 Chat Giveaway</div>
                <div style={{color:'#a1a1aa', fontSize:'0.85rem'}}>Keyword-based giveaways with weighted Shuffle linking + anti-repeat</div>
              </div>

              {/* Diagnostics banner — shown when chat announce is broken */}
              {cgDiagnostics && (!cgDiagnostics.hasToken || !cgDiagnostics.tokenValid || (cgDiagnostics.missingScopes && cgDiagnostics.missingScopes.length > 0)) && (
                <div style={{background:'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(239,68,68,0.08))', border:'1px solid rgba(245,158,11,0.35)', borderRadius:'12px', padding:'1rem 1.25rem', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap'}}>
                  <div style={{fontSize:'1.5rem'}}>⚠️</div>
                  <div style={{flex:1, minWidth:'260px'}}>
                    <div style={{fontSize:'0.9rem', fontWeight:800, color:'#fbbf24', marginBottom:'0.2rem'}}>Chat announcements aren't working</div>
                    <div style={{fontSize:'0.78rem', color:'#e2e8f0', lineHeight:1.45}}>
                      {!cgDiagnostics.hasToken && 'The broadcaster account has never authorized the app. '}
                      {cgDiagnostics.hasToken && !cgDiagnostics.tokenValid && 'The broadcaster token is expired or invalid. '}
                      {cgDiagnostics.hasToken && cgDiagnostics.tokenValid && cgDiagnostics.missingScopes?.length > 0 && (
                        <>The broadcaster token is missing the scope{cgDiagnostics.missingScopes.length === 1 ? '' : 's'}: <code style={{color:'#fbbf24'}}>{cgDiagnostics.missingScopes.join(', ')}</code>. </>
                      )}
                      Re-authorize as the broadcaster account to fix this.
                    </div>
                  </div>
                  <a href={`${BACKEND_API_URL}${cgDiagnostics.reAuthUrl || '/auth/twitch/broadcaster'}`} style={{padding:'0.55rem 1.1rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'8px', color:'#fff', fontWeight:800, fontSize:'0.82rem', textDecoration:'none', whiteSpace:'nowrap'}}>Re-authorize →</a>
                </div>
              )}

              {/* Bad Luck Protection Info — hidden while a session has it switched off */}
              {!(session && !isFinished && session.badluck_enabled === false) && (
              <div style={{background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'12px', padding:'0.85rem 1.1rem', marginBottom:'1.25rem', display:'flex', alignItems:'flex-start', gap:'0.75rem'}}>
                <span style={{fontSize:'1.2rem', flexShrink:0, marginTop:'0.1rem'}}>🍀</span>
                <div>
                  <div style={{fontSize:'0.82rem', fontWeight:700, color:'#22c55e', marginBottom:'0.2rem'}}>Bad Luck Protection</div>
                  <div style={{fontSize:'0.72rem', color:'#a1a1aa', lineHeight:1.5}}>
                    Eligible if you've wagered <strong style={{color:'#22c55e'}}>$5,000+</strong> under SNUTZY <em>or</em> watched <strong style={{color:'#22c55e'}}>40+ hours</strong> in the last 2 months. Each giveaway you enter without winning adds <strong style={{color:'#22c55e'}}>+0.10×</strong> to your draw odds (1.10×, 1.20×, 1.30×…), up to <strong style={{color:'#22c55e'}}>3×</strong> — or up to <strong style={{color:'#fbbf24'}}>10×</strong> if you're a <strong style={{color:'#fbbf24'}}>👑 VIP</strong>. Winning resets the boost. Look for the <span style={{padding:'0.05rem 0.25rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'3px', color:'#22c55e', fontSize:'0.65rem', fontWeight:700}}>🍀1.20×</span> badge next to eligible entries!
                  </div>
                </div>
              </div>
              )}

              {/* ---------- Initial load: never imply "no giveaway" while the server is still answering ---------- */}
              {!cgData && (
                cgLoadFailed ? (
                  <div className="load-error-box" style={{ marginBottom: '1.5rem' }}>
                    <p>Having trouble reaching the server — retrying automatically…</p>
                    <button className="retry-btn" onClick={fetchCg}>↻ Retry now</button>
                  </div>
                ) : (
                  <div style={{ background: 'rgba(14,14,18,0.7)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '16px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                    <Skel w="180px" h="1.1rem" />
                    <Skel w="260px" h="0.8rem" />
                    <Skel h="44px" r="10px" style={{ marginTop: '0.4rem' }} />
                  </div>
                )
              )}

              {/* ---------- No session: admin setup form ---------- */}
              {cgData && (!session || isFinished) && isAdmin && (() => {
                const defaultState = { keyword:'shuffle', weightedShuffle:true, excludePreviousWinners:true, announceInChat:true, vipBonusEnabled:true, platforms:'twitch', badluckEnabled:true };
                return (
                  <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(168,85,247,0.2)', borderRadius:'16px', padding:'1.75rem', marginBottom:'1.5rem'}}>
                    <div style={{fontSize:'1rem', fontWeight:800, color:'#e2e8f0', marginBottom:'1rem'}}>Create a new giveaway</div>
                    <KickSetupPanel apiUrl={BACKEND_API_URL} getToken={getAuthToken} onConnectBroadcaster={() => {
                      const t = getAuthToken();
                      if (t) window.location.href = `${BACKEND_API_URL}/auth/kick/broadcaster?token=${encodeURIComponent(t)}`;
                    }} />
                    <CgCreateForm defaults={defaultState} onSubmit={async (vals) => {
                      const r = await cgAdminAction('/api/admin/chat-giveaway/create', vals);
                      if (r?.success) await cgAdminAction(`/api/admin/chat-giveaway/${r.session.id}/open`);
                    }} />
                  </div>
                );
              })()}

              {cgData && !session && !isAdmin && (
                <div style={{textAlign:'center', padding:'3rem 1rem', color:'#71717a'}}>
                  <div style={{fontSize:'3rem', marginBottom:'0.5rem', opacity:0.5}}>🎁</div>
                  No giveaway running right now. Check back when the stream's live!
                </div>
              )}

              {/* ---------- Active session ---------- */}
              {session && !isFinished && (
                <>
                  {/* Status banner */}
                  <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(168,85,247,0.2)', borderRadius:'16px', padding:'1.25rem 1.5rem', marginBottom:'1rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap'}}>
                    <div style={{minWidth:0, flex:1}}>
                      <div style={{display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'0.4rem', flexWrap:'wrap'}}>
                        <div style={{fontSize:'1.1rem', fontWeight:800, color:'#fff'}}>Giveaway #{session.finish_number || cgData?.nextFinishNumber || '?'}</div>
                        {isOpen && <span style={{padding:'0.15rem 0.55rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'4px', fontSize:'0.68rem', fontWeight:800, color:'#22c55e', letterSpacing:'1px'}}>● LIVE</span>}
                        {isClosed && <span style={{padding:'0.15rem 0.55rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'4px', fontSize:'0.68rem', fontWeight:800, color:'#fbbf24', letterSpacing:'1px'}}>LOCKED</span>}
                      </div>
                      <div style={{color:'#a1a1aa', fontSize:'0.75rem', display:'flex', gap:'0.9rem', flexWrap:'wrap'}}>
                        <span>Type <code style={{color:'#c084fc', background:'rgba(168,85,247,0.1)', padding:'0.05rem 0.35rem', borderRadius:'3px'}}>{session.keyword}</code> in chat to enter</span>
                        {session.weighted_shuffle && <span style={{color:'#fbbf24'}}>SNUTZY affiliates: +{Math.round((Number(session.shuffle_multiplier) - 1) * 100)}% chance</span>}
                        {session.vip_bonus_enabled && <span style={{color:'#fbbf24'}}>👑 VIP: +{Math.round((Number(session.vip_multiplier) - 1) * 100)}% more</span>}
                        {session.exclude_previous_winners && <span>No repeat winners</span>}
                        {session.badluck_enabled !== false && <span style={{color:'#22c55e'}}>🍀 Bad luck protection: +0.10× per loss (up to 3×, VIPs up to 10×)</span>}
                        {session.badluck_enabled === false && <span style={{color:'#71717a'}}>🍀 Bad luck protection: off this giveaway — everyone draws at 1×, streaks untouched</span>}
                        {session.badluck_enabled !== false && <span style={{color:'#71717a', fontStyle:'italic', fontSize:'0.68rem'}}>(Nik's family tree does not get increased odds, we swear)</span>}
                      </div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:'2.5rem', fontWeight:900, color:'#c084fc', lineHeight:1, fontVariantNumeric:'tabular-nums'}}>{entryCount}</div>
                      <div style={{fontSize:'0.7rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1.5px'}}>entries</div>
                    </div>
                  </div>

                  {/* Admin controls */}
                  {isAdmin && (
                    <div style={{display:'flex', gap:'0.5rem', justifyContent:'center', marginBottom:'1.25rem', flexWrap:'wrap'}}>
                      {isOpen && <button onClick={() => cgAdminAction(`/api/admin/chat-giveaway/${session.id}/close`)} style={{padding:'0.6rem 1.25rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:'8px', color:'#fbbf24', fontWeight:700, cursor:'pointer'}}>🔒 Lock Entries</button>}
                      {isClosed && <button onClick={() => cgAdminAction(`/api/admin/chat-giveaway/${session.id}/open`)} style={{padding:'0.6rem 1.25rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.4)', borderRadius:'8px', color:'#22c55e', fontWeight:700, cursor:'pointer'}}>🔓 Reopen Entries</button>}
                      <button disabled={!canDraw || cgDrawing} onClick={() => cgDrawWinner(session.id)} style={{padding:'0.7rem 1.75rem', background: canDraw && !cgDrawing ? 'linear-gradient(135deg, #a855f7, #ec4899)' : 'rgba(113,113,122,0.2)', border:'none', borderRadius:'10px', color: canDraw && !cgDrawing ? '#fff' : '#71717a', fontWeight:800, cursor: canDraw && !cgDrawing ? 'pointer' : 'not-allowed', boxShadow: canDraw && !cgDrawing ? '0 0 25px rgba(168,85,247,0.4)' : 'none', fontSize:'0.95rem'}} title={entryCount === 0 ? 'No entries yet' : ''}>🎲 Draw Winner</button>
                      <button onClick={() => { if (confirm('Finish the giveaway? No more changes after this.')) cgAdminAction(`/api/admin/chat-giveaway/${session.id}/finish`); }} style={{padding:'0.7rem 1.75rem', background:'linear-gradient(135deg, #22c55e, #16a34a)', border:'none', borderRadius:'10px', color:'#fff', fontWeight:800, cursor:'pointer', fontSize:'0.95rem', boxShadow:'0 0 20px rgba(34,197,94,0.25)'}}>🏁 Finish Giveaway</button>
                    </div>
                  )}
                  {/* Small-text delete action, intentionally less prominent than Finish */}
                  {isAdmin && (
                    <div style={{textAlign:'center', marginBottom:'1.25rem', display:'flex', gap:'1.25rem', justifyContent:'center', flexWrap:'wrap'}}>
                      <button onClick={async () => {
                        if (!confirm('Re-check VIP status for every entry in this giveaway? This updates was_vip using the latest Shuffle wager data.')) return;
                        const r = await cgAdminAction(`/api/admin/chat-giveaway/${session.id}/recheck-vip`);
                        if (r?.success) {
                          const parts = [`Checked ${r.checked} entries`];
                          if (r.promoted) parts.push(`${r.promoted} newly VIP`);
                          if (r.demoted) parts.push(`${r.demoted} no longer VIP`);
                          if (r.affiliatePromoted) parts.push(`${r.affiliatePromoted} newly verified as SNUTZY affiliates`);
                          if (r.affiliateDemoted) parts.push(`${r.affiliateDemoted} no longer verified as SNUTZY affiliates`);
                          if (!r.promoted && !r.demoted && !r.affiliatePromoted && !r.affiliateDemoted) parts.push('no changes');
                          alert(parts.join(' · '));
                        }
                      }} style={{padding:'0.3rem 0.7rem', background:'transparent', border:'none', color:'#fbbf24', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', textDecoration:'underline dotted', textUnderlineOffset:'3px'}}>👑 Re-check VIP status for all entries</button>
                      <button onClick={async () => {
                        if (!confirm('Fix duplicate entries? Removes kick-side duplicates from viewers who linked mid-giveaway, and migrates their pre-link entries onto their linked identity.')) return;
                        const r = await cgAdminAction(`/api/admin/chat-giveaway/${session.id}/dedupe-entries`);
                        if (r?.success) {
                          alert(`${r.removed} duplicate(s) removed · ${r.migrated} entry(ies) migrated to linked identity${!r.removed && !r.migrated ? ' — all clean!' : ''}`);
                        }
                      }} style={{padding:'0.3rem 0.7rem', background:'transparent', border:'none', color:'#53fc18', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', textDecoration:'underline dotted', textUnderlineOffset:'3px'}}>🧹 Fix duplicate entries</button>
                      <button onClick={async () => { if (!confirm('Delete this giveaway entirely? All entries and winners will be wiped. This cannot be undone.')) return; const t = getAuthToken(); const r = await fetch(`${BACKEND_API_URL}/api/admin/chat-giveaway/${session.id}`, {method:'DELETE', headers:{'Authorization':`Bearer ${t}`}}); if (r.ok) { setCgSelectedWinnerId(null); setCgWinnerDetails(null); fetchCg(); fetchCgWinnersHistory(cgWinnersPage); } else { const d = await r.json().catch(() => ({})); alert(d.error || 'Delete failed'); } }} style={{padding:'0.3rem 0.7rem', background:'transparent', border:'none', color:'#71717a', fontSize:'0.7rem', fontWeight:600, cursor:'pointer', textDecoration:'underline dotted', textUnderlineOffset:'3px'}}>Delete this giveaway without recording it</button>
                    </div>
                  )}

                  {/* Winners strip (modal handles drawing animation + detail view) */}
                  {winners.length > 0 && (
                    <div style={{marginBottom:'1.5rem'}}>
                      <div style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.6rem'}}>🏆 Winners</div>
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:'0.75rem'}}>
                        {winners.map(w => (
                          <div key={w.id} onClick={isAdmin ? () => { setCgSelectedWinnerId(w.id); fetchCgWinnerDetails(w.id); } : undefined} style={{cursor: isAdmin ? 'pointer' : 'default', background: w.invalidated_at ? 'rgba(113,113,122,0.08)' : 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(236,72,153,0.08))', border: w.invalidated_at ? '1px solid rgba(113,113,122,0.3)' : '1px solid rgba(168,85,247,0.35)', borderRadius:'12px', padding:'0.9rem 1rem', display:'flex', alignItems:'center', gap:'0.75rem', opacity: w.invalidated_at ? 0.55 : 1, transition:'all 0.2s ease', outline: cgSelectedWinnerId === w.id ? '2px solid #c084fc' : 'none'}}>
                            {w.twitch_avatar ? <img src={w.twitch_avatar} alt="" style={{width:'44px', height:'44px', borderRadius:'50%', border:'2px solid rgba(168,85,247,0.4)'}} /> : <div style={{width:'44px', height:'44px', borderRadius:'50%', background:'rgba(168,85,247,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.3rem'}}>👤</div>}
                            <div style={{minWidth:0, flex:1}}>
                              <div style={{fontSize:'0.9rem', fontWeight:800, color:'#fff', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', textDecoration: w.invalidated_at ? 'line-through' : 'none'}}>{w.twitch_display_name}</div>
                              <div style={{fontSize:'0.7rem', color:'#a1a1aa', display:'flex', gap:'0.35rem', alignItems:'center', flexWrap:'wrap'}}>
                                {parseFloat(w.prize_amount) > 0 && <span style={{color:'#22c55e', fontWeight:800}}>${parseFloat(w.prize_amount).toFixed(2)}</span>}
                                {w.had_vip && <span style={{color:'#fbbf24', fontWeight:700}}>👑 VIP</span>}
                                {w.had_shuffle_linked && !w.had_vip && <span style={{color:'#fbbf24', fontWeight:700}}>🎰 Linked</span>}
                                {parseFloat(w.bad_luck_multiplier) > 1.001 && <span title={`Had ${parseFloat(w.bad_luck_multiplier).toFixed(2)}× bad luck protection when rolled`} style={{color:'#22c55e', fontWeight:700}}>🍀{parseFloat(w.bad_luck_multiplier).toFixed(2)}×</span>}
                                {w.invalidated_at && <span style={{color:'#f87171'}}>invalidated</span>}
                                <CgPaidPill winner={w} onToggle={isAdmin ? async (winner, isPaid) => {
                                  await cgAdminAction(`/api/admin/chat-giveaway/winner/${winner.id}/${isPaid ? 'mark-unpaid' : 'mark-paid'}`);
                                } : undefined} compact />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Winner modal (animation + details) is rendered below at page scope */}

                  {/* Live entries list — updates every 3s via the /current poll */}
                  {cgData?.entries && cgData.entries.length > 0 && (
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'1rem 1.25rem', marginBottom:'1.25rem'}}>
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem', gap:'0.5rem'}}>
                        <div style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0'}}>📥 Live Entries <span style={{color:'#71717a', fontWeight:500, fontSize:'0.78rem', marginLeft:'0.4rem'}}>({entryCount})</span></div>
                        <div style={{display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.68rem', color: isOpen ? '#22c55e' : '#71717a', fontWeight:700}}>
                          <span style={{width:'6px', height:'6px', borderRadius:'50%', background: isOpen ? '#22c55e' : '#71717a', animation: isOpen ? 'pulse 1.5s ease-in-out infinite' : 'none'}} />
                          {isOpen ? 'LIVE' : 'LOCKED'}
                        </div>
                      </div>
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:'0.35rem', maxHeight:'280px', overflowY:'auto', paddingRight:'0.25rem'}}>
                        {cgData.entries.map(e => (
                          <a key={e.id} href={`https://twitch.tv/${e.twitch_username}`} target="_blank" rel="noopener noreferrer" title={`Entered ${new Date(e.entered_at).toLocaleTimeString()}`} style={{display:'flex', alignItems:'center', gap:'0.45rem', padding:'0.35rem 0.55rem', background:'rgba(168,85,247,0.06)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'6px', textDecoration:'none', transition:'background 0.15s ease'}} onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(168,85,247,0.15)'} onMouseLeave={ev => ev.currentTarget.style.background = 'rgba(168,85,247,0.06)'}>
                            {e.twitch_avatar ? <img src={e.twitch_avatar} alt="" style={{width:'22px', height:'22px', borderRadius:'50%', flexShrink:0}} /> : <div style={{width:'22px', height:'22px', borderRadius:'50%', background:'rgba(168,85,247,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', flexShrink:0}}>👤</div>}
                            <span style={{fontSize:'0.75rem', color:'#e2e8f0', fontWeight:600, minWidth:0, flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{e.twitch_display_name}</span>
                            {e.was_vip && <span title="VIP affiliate (under guild SNUTZY, $50k+ weighted wagered or legacy VIP) — gets +25% chance × 1.25 VIP boost" style={{fontSize:'0.75rem', flexShrink:0}}>👑</span>}
                            {e.is_snutzy_affiliate && !e.was_vip && <span title="Affiliate under guild SNUTZY — gets +25% chance" style={{fontSize:'0.75rem', flexShrink:0}}>🎰</span>}
                            {e.has_shuffle_linked && !e.is_snutzy_affiliate && <span title="Shuffle account linked but not under guild SNUTZY — no chance boost" style={{fontSize:'0.75rem', flexShrink:0, opacity:0.5, filter:'grayscale(1)'}}>🎰</span>}
                            {session.badluck_enabled !== false && e.wager_streak > 0 && <span title={`Bad luck protection — ${e.wager_streak} giveaway${e.wager_streak === 1 ? '' : 's'} without a win → ${Math.min(1 + 0.10 * e.wager_streak, e.was_vip ? 10 : 3).toFixed(2)}× boost${e.was_vip ? ' (VIP cap 10×)' : ''}`} style={{fontSize:'0.6rem', flexShrink:0, padding:'0.1rem 0.3rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'4px', color:'#22c55e', fontWeight:700, fontVariantNumeric:'tabular-nums'}}>🍀{Math.min(1 + 0.10 * e.wager_streak, e.was_vip ? 10 : 3).toFixed(2)}×</span>}
                          </a>
                        ))}
                      </div>
                      {entryCount > cgData.entries.length && (
                        <div style={{marginTop:'0.6rem', fontSize:'0.68rem', color:'#71717a', fontStyle:'italic', textAlign:'center'}}>Showing newest {cgData.entries.length} of {entryCount} entries</div>
                      )}
                    </div>
                  )}

                  {/* Scope toggles panel (admin) */}
                  {isAdmin && (
                    <div style={{background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.04)', borderRadius:'10px', padding:'0.85rem 1.1rem', fontSize:'0.75rem', color:'#a1a1aa', display:'flex', gap:'1.2rem', flexWrap:'wrap'}}>
                      <span>🎰 Shuffle weighting: <strong style={{color: session.weighted_shuffle ? '#22c55e' : '#71717a'}}>{session.weighted_shuffle ? `on (${session.shuffle_multiplier}×)` : 'off'}</strong></span>
                      <span>🚫 Skip repeat winners: <strong style={{color: session.exclude_previous_winners ? '#22c55e' : '#71717a'}}>{session.exclude_previous_winners ? 'on' : 'off'}</strong></span>
                      <span style={{marginLeft:'auto', fontSize:'0.7rem', color:'#71717a', fontStyle:'italic'}}>Set at creation — create a new giveaway to change</span>
                    </div>
                  )}
                </>
              )}

              {/* Winners history — paginated, all-time, public */}
              <div style={{marginTop:'2rem'}}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem', gap:'0.75rem', flexWrap:'wrap'}}>
                  <div style={{fontSize:'1rem', fontWeight:800, color:'#e2e8f0'}}>🏆 Previous Giveaway Winners</div>
                  {cgWinnersHistory && <div style={{fontSize:'0.75rem', color:'#71717a'}}>{cgWinnersHistory.totalCount} total winner{cgWinnersHistory.totalCount === 1 ? '' : 's'}</div>}
                </div>
                {cgWinnersHistory && cgWinnersHistory.winners.length === 0 ? (
                  <div style={{padding:'2rem', textAlign:'center', color:'#71717a', fontSize:'0.85rem', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'10px'}}>No winners yet. Run your first giveaway above!</div>
                ) : (
                  <div style={{display:'flex', flexDirection:'column', gap:'0.4rem'}}>
                    {cgWinnersHistory?.winners.map(w => (
                      <div key={w.id} onClick={isAdmin ? () => { setCgSelectedWinnerId(w.id); fetchCgWinnerDetails(w.id); } : undefined} style={{cursor: isAdmin ? 'pointer' : 'default', textDecoration:'none', display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.65rem 0.85rem', background: cgSelectedWinnerId === w.id ? 'rgba(168,85,247,0.12)' : 'rgba(14,14,18,0.6)', border: cgSelectedWinnerId === w.id ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.05)', borderRadius:'8px', transition:'all 0.15s ease'}} onMouseEnter={e => { if (isAdmin) { e.currentTarget.style.background = 'rgba(168,85,247,0.08)'; e.currentTarget.style.borderColor = 'rgba(168,85,247,0.3)'; }}} onMouseLeave={e => { if (cgSelectedWinnerId !== w.id) { e.currentTarget.style.background = 'rgba(14,14,18,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; }}}>
                        {w.twitch_avatar ? <img src={w.twitch_avatar} alt="" style={{width:'38px', height:'38px', borderRadius:'50%', border:'2px solid rgba(168,85,247,0.3)'}} /> : <div style={{width:'38px', height:'38px', borderRadius:'50%', background:'rgba(168,85,247,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem'}}>👤</div>}
                        <div style={{minWidth:0, flex:1}}>
                          <div style={{fontSize:'0.88rem', fontWeight:700, color:'#e2e8f0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{w.twitch_display_name}</div>
                        </div>
                        {w.had_vip && <span title="Was a VIP under guild SNUTZY when drawn" style={{fontSize:'0.8rem'}}>👑</span>}
                        {w.had_shuffle_linked && !w.had_vip && <span title="Was Shuffle-linked" style={{fontSize:'0.8rem'}}>🎰</span>}
                        <CgPaidPill winner={w} onToggle={isAdmin ? async (winner, isPaid) => {
                          await cgAdminAction(`/api/admin/chat-giveaway/winner/${winner.id}/${isPaid ? 'mark-unpaid' : 'mark-paid'}`);
                          fetchCgWinnersHistory(cgWinnersPage);
                        } : undefined} compact />
                        <div style={{textAlign:'right', minWidth:'75px'}}>
                          {parseFloat(w.prize_amount) > 0 ? (
                            <div style={{fontSize:'1rem', fontWeight:900, color:'#22c55e', fontVariantNumeric:'tabular-nums'}}>${parseFloat(w.prize_amount).toFixed(2)}</div>
                          ) : (
                            <div style={{fontSize:'0.7rem', color:'#71717a', fontStyle:'italic'}}>no prize</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {cgWinnersHistory && cgWinnersHistory.totalPages > 1 && (
                  <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:'0.5rem', marginTop:'1rem'}}>
                    <button onClick={() => setCgWinnersPage(p => Math.max(1, p - 1))} disabled={cgWinnersPage <= 1} style={{padding:'0.4rem 0.8rem', background: cgWinnersPage > 1 ? 'rgba(168,85,247,0.15)' : 'rgba(113,113,122,0.1)', border:'1px solid rgba(168,85,247,0.25)', borderRadius:'6px', color: cgWinnersPage > 1 ? '#c084fc' : '#52525b', fontSize:'0.8rem', fontWeight:700, cursor: cgWinnersPage > 1 ? 'pointer' : 'not-allowed'}}>← Prev</button>
                    <div style={{fontSize:'0.78rem', color:'#a1a1aa', padding:'0 0.75rem', fontVariantNumeric:'tabular-nums'}}>Page {cgWinnersHistory.page} of {cgWinnersHistory.totalPages}</div>
                    <button onClick={() => setCgWinnersPage(p => Math.min(cgWinnersHistory.totalPages, p + 1))} disabled={cgWinnersPage >= cgWinnersHistory.totalPages} style={{padding:'0.4rem 0.8rem', background: cgWinnersPage < cgWinnersHistory.totalPages ? 'rgba(168,85,247,0.15)' : 'rgba(113,113,122,0.1)', border:'1px solid rgba(168,85,247,0.25)', borderRadius:'6px', color: cgWinnersPage < cgWinnersHistory.totalPages ? '#c084fc' : '#52525b', fontSize:'0.8rem', fontWeight:700, cursor: cgWinnersPage < cgWinnersHistory.totalPages ? 'pointer' : 'not-allowed'}}>Next →</button>
                  </div>
                )}
              </div>

              {/* ========== WINNER MODAL ========== */}
              {(cgDrawing || cgSelectedWinnerId) && (
                <div
                  style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:5000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', backdropFilter:'blur(6px)'}}
                  onClick={(e) => {
                    // Backdrop click closes — but never while animation is running
                    if (e.target === e.currentTarget && !cgDrawing) {
                      setCgSelectedWinnerId(null);
                      setCgWinnerDetails(null);
                    }
                  }}
                >
                  <div style={{background:'linear-gradient(135deg, rgba(20,20,28,0.98), rgba(30,20,45,0.98))', border:'1px solid rgba(168,85,247,0.4)', borderRadius:'20px', maxWidth: cgDrawing ? '480px' : '780px', width:'100%', maxHeight:'92vh', overflowY:'auto', position:'relative', boxShadow:'0 20px 80px rgba(168,85,247,0.4), 0 0 40px rgba(168,85,247,0.2) inset'}}>
                    {!cgDrawing && (
                      <button onClick={() => { setCgSelectedWinnerId(null); setCgWinnerDetails(null); }} style={{position:'absolute', top:'0.9rem', right:'0.9rem', width:'32px', height:'32px', background:'rgba(113,113,122,0.2)', border:'1px solid rgba(113,113,122,0.3)', borderRadius:'8px', color:'#e2e8f0', fontSize:'1rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10}}>✕</button>
                    )}

                    {cgDrawing ? (
                      /* Drawing animation */
                      <div style={{padding:'3rem 2rem', textAlign:'center'}}>
                        <div style={{fontSize:'0.78rem', color:'#c084fc', fontWeight:800, letterSpacing:'3px', marginBottom:'1.5rem'}}>🎲 DRAWING A WINNER...</div>
                        <div style={{display:'flex', justifyContent:'center'}}>
                          <CgSlotReel cgData={cgData} settledWinner={cgReelWinnerData} />
                        </div>
                      </div>
                    ) : cgWinnerDetails ? (() => {
                      const d = cgWinnerDetails;
                      const w = d.winner;
                      const followText = d.followedAt ? formatFollowAge(d.followedAt) : (d.followLookupAvailable ? 'Not following' : null);
                      const pastWins = Math.max(0, d.winCount - 1);
                      return (
                        <div style={{padding:'2rem 1.75rem 1.75rem'}}>
                          {/* Big header with avatar + name + badges */}
                          <div style={{textAlign:'center', marginBottom:'1.25rem'}}>
                            <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'0.75rem', marginBottom:'0.75rem'}}>
                              <div style={{fontSize:'0.72rem', color:'#c084fc', fontWeight:800, letterSpacing:'3px'}}>🎉 WINNER</div>
                              {w.drawn_at && !w.invalidated_at && (
                                <CgElapsedTimer since={w.drawn_at} style={{fontSize:'0.8rem', fontWeight:800, color:'#fbbf24', fontVariantNumeric:'tabular-nums', padding:'0.2rem 0.55rem', background:'rgba(251,191,36,0.12)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:'6px'}} />
                              )}
                            </div>
                            {w.twitch_avatar
                              ? <img src={w.twitch_avatar} alt="" style={{width:'120px', height:'120px', borderRadius:'50%', border:'4px solid #c084fc', boxShadow:'0 0 40px rgba(168,85,247,0.5)', marginBottom:'0.75rem'}} />
                              : <div style={{width:'120px', height:'120px', borderRadius:'50%', background:'rgba(168,85,247,0.2)', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'3.2rem', marginBottom:'0.75rem', border:'4px solid #c084fc'}}>👤</div>}
                            <div style={{fontSize:'1.6rem', fontWeight:900, color:'#fff', textDecoration: w.invalidated_at ? 'line-through' : 'none', lineHeight:1.1}}>{w.twitch_display_name}</div>
                            <a href={`https://twitch.tv/${w.twitch_username}`} target="_blank" rel="noopener noreferrer" style={{fontSize:'0.78rem', color:'#a1a1aa', textDecoration:'none'}}>@{w.twitch_username} ↗</a>

                            {/* Badges row */}
                            <div style={{display:'flex', gap:'0.5rem', justifyContent:'center', marginTop:'0.75rem', flexWrap:'wrap'}}>
                              {w.had_vip && <span style={{padding:'0.3rem 0.7rem', background:'linear-gradient(135deg, rgba(251,191,36,0.25), rgba(245,158,11,0.2))', border:'1px solid rgba(251,191,36,0.5)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:800, color:'#fbbf24'}}>👑 VIP</span>}
                              {/* Three Shuffle states:
                                  - Affiliate verified → purple "🎰 {username}" (the +25% boost case)
                                  - Linked but not under SNUTZY → amber warning ("⚠️ Shuffle linked but NOT a SNUTZY affiliate")
                                  - No shuffle linked → no badge.
                                  Older winners pre-v8 have had_snutzy_affiliate = NULL → fall back to legacy
                                  display so historical winners don't suddenly get a misleading warning. */}
                              {w.had_shuffle_linked && (w.had_snutzy_affiliate === true || w.had_snutzy_affiliate === null) && (
                                <span style={{padding:'0.3rem 0.7rem', background:'rgba(168,85,247,0.18)', border:'1px solid rgba(168,85,247,0.4)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:700, color:'#c084fc', display:'inline-flex', alignItems:'center', gap:'0.4rem'}}>
                                  🎰 {w.shuffle_username || 'Shuffle-linked'}
                                  {w.shuffle_username && (
                                    <button onClick={(e) => { navigator.clipboard?.writeText(w.shuffle_username); const b = e.currentTarget; b.textContent = '✅'; setTimeout(() => { b.textContent = '📋'; }, 1200); }} title="Copy Shuffle username" style={{background:'none', border:'none', cursor:'pointer', fontSize:'0.75rem', padding:0, lineHeight:1}}>📋</button>
                                  )}
                                </span>
                              )}
                              {w.had_shuffle_linked && w.had_snutzy_affiliate === false && (
                                <span title="This winner has a Shuffle account linked but is NOT signed up under our SNUTZY affiliate guild. They did not get the +25% chance boost." style={{padding:'0.3rem 0.7rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.45)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:700, color:'#fbbf24', display:'inline-flex', alignItems:'center', gap:'0.4rem'}}>
                                  ⚠️ {w.shuffle_username || 'Linked'} — not under SNUTZY guild
                                  {w.shuffle_username && (
                                    <button onClick={(e) => { navigator.clipboard?.writeText(w.shuffle_username); const b = e.currentTarget; b.textContent = '✅'; setTimeout(() => { b.textContent = '📋'; }, 1200); }} title="Copy Shuffle username" style={{background:'none', border:'none', cursor:'pointer', fontSize:'0.75rem', padding:0, lineHeight:1}}>📋</button>
                                  )}
                                </span>
                              )}
                              {cgWinnerDetails?.lifetimeWager !== null && cgWinnerDetails?.lifetimeWager !== undefined && cgWinnerDetails.lifetimeWager <= 50 && (
                                <span title={`Lifetime wagered: $${cgWinnerDetails.lifetimeWager.toFixed(2)}`} style={{padding:'0.3rem 0.7rem', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.35)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:700, color:'#f87171'}}>🥚🪿</span>
                              )}
                              {parseFloat(w.bad_luck_multiplier) > 1.001 && <span title={`Bad luck protection: this winner had a ${parseFloat(w.bad_luck_multiplier).toFixed(2)}× chance boost when they were rolled, from previous losses`} style={{padding:'0.3rem 0.7rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.35)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:700, color:'#22c55e'}}>🍀 {parseFloat(w.bad_luck_multiplier).toFixed(2)}× bad luck boost</span>}
                              {pastWins > 0 && <span style={{padding:'0.3rem 0.7rem', background:'rgba(168,85,247,0.12)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:700, color:'#c084fc'}} title="Won giveaways on this tool before">🎉 {pastWins} past win{pastWins === 1 ? '' : 's'}</span>}
                              {pastWins === 0 && <span style={{padding:'0.3rem 0.7rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:700, color:'#22c55e'}}>✨ First-time winner</span>}
                              {followText && <span style={{padding:'0.3rem 0.7rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:600, color:'#a1a1aa'}}>Following: {followText}</span>}
                              {w.invalidated_at && <span style={{padding:'0.3rem 0.7rem', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:'6px', fontSize:'0.75rem', fontWeight:700, color:'#f87171'}}>❌ Invalidated</span>}
                              <CgPaidPill winner={w} onToggle={async (winner, isPaid) => {
                                await cgAdminAction(`/api/admin/chat-giveaway/winner/${winner.id}/${isPaid ? 'mark-unpaid' : 'mark-paid'}`);
                                fetchCgWinnerDetails(winner.id);
                              }} />
                            </div>
                          </div>

                          {/* Chat feed */}
                          <div style={{marginBottom:'1.25rem'}}>
                            <div style={{fontSize:'0.78rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.5rem', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                              <span>💬 Chat from {w.twitch_display_name}</span>
                              <span style={{fontSize:'0.65rem', color:'#22c55e', fontWeight:700}}>● live</span>
                            </div>
                            <CgChatFeed messages={d.chatMessages} drawnAt={w.drawn_at} />
                          </div>

                          {/* Prize input (enter after the bonus plays out) + actions */}
                          {isAdmin && !w.invalidated_at && (
                            <>
                              <CgPrizeInput winner={w} onSave={async (amount) => {
                                const r = await cgAdminAction(`/api/admin/chat-giveaway/winner/${w.id}/prize`, { amount });
                                if (r?.success) { fetchCgWinnerDetails(w.id); fetchCgWinnersHistory(cgWinnersPage); }
                              }} />
                              <div style={{display:'flex', gap:'0.5rem', marginTop:'0.85rem'}}>
                                <button onClick={() => { setCgSelectedWinnerId(null); setCgWinnerDetails(null); }} style={{flex:1, padding:'0.7rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'8px', color:'#fff', fontWeight:800, fontSize:'0.85rem', cursor:'pointer'}}>✓ Accept & close</button>
                                <button onClick={async () => { const reason = prompt('Reason (optional):', 'did not respond'); if (reason === null) return; await cgAdminAction(`/api/admin/chat-giveaway/winner/${w.id}/invalidate`, { reason }); setCgSelectedWinnerId(null); setCgWinnerDetails(null); }} style={{flex:1, padding:'0.7rem', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.35)', borderRadius:'8px', color:'#f87171', fontWeight:700, fontSize:'0.85rem', cursor:'pointer'}}>✕ Close winner & redraw</button>
                              </div>
                            </>
                          )}
                          {isAdmin && w.invalidated_at && (
                            <button onClick={async () => { await cgAdminAction(`/api/admin/chat-giveaway/winner/${w.id}/restore`); fetchCgWinnerDetails(w.id); }} style={{width:'100%', padding:'0.7rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.35)', borderRadius:'8px', color:'#22c55e', fontWeight:700, fontSize:'0.82rem', cursor:'pointer'}}>Restore winner</button>
                          )}
                        </div>
                      );
                    })() : (
                      <div style={{padding:'3rem', textAlign:'center', color:'#71717a'}}>Loading winner...</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Site-wide VIP claim popup — appears once eligibility is reached, until claimed or snoozed */}
        {showVipClaimPopup && user && (
          <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.82)', zIndex:6000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', backdropFilter:'blur(5px)'}}
            onClick={(e) => { if (e.target === e.currentTarget) snoozeVipClaimPopup(); }}>
            <div style={{background:'linear-gradient(135deg, rgba(28,22,10,0.98), rgba(20,16,28,0.98))', border:'1px solid rgba(251,191,36,0.45)', borderRadius:'20px', maxWidth:'440px', width:'100%', padding:'2rem 1.75rem', textAlign:'center', position:'relative', boxShadow:'0 20px 80px rgba(245,158,11,0.25), 0 0 40px rgba(245,158,11,0.1) inset'}}>
              <button onClick={snoozeVipClaimPopup} style={{position:'absolute', top:'0.85rem', right:'0.85rem', width:'30px', height:'30px', background:'rgba(113,113,122,0.2)', border:'1px solid rgba(113,113,122,0.3)', borderRadius:'8px', color:'#e2e8f0', fontSize:'0.95rem', cursor:'pointer'}}>✕</button>
              <div style={{fontSize:'3rem', marginBottom:'0.5rem'}}>👑</div>
              <div style={{fontSize:'1.4rem', fontWeight:900, background:'linear-gradient(135deg, #fbbf24, #f59e0b)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', marginBottom:'0.5rem'}}>
                {vipClaimStatus?.regain ? "You can reclaim VIP!" : "You've reached VIP! 🎉"}
              </div>
              <div style={{fontSize:'0.85rem', color:'#a1a1aa', lineHeight:1.55, marginBottom:'1.25rem'}}>
                {vipClaimStatus?.regain
                  ? 'You\'ve wagered enough since your removal to get your VIP role back. Claim it now and it\'s applied on Discord instantly.'
                  : 'Your wagering under guild SNUTZY has earned you VIP status. Claim your Discord VIP role now — exclusive giveaways, free-equity bonus hunts and #vip-chat await.'}
              </div>
              {vipClaimStatus?.discordLinked ? (
                <button onClick={claimVipRole} disabled={vipClaimBusy} style={{width:'100%', padding:'0.85rem', background: vipClaimBusy ? 'rgba(107,114,128,0.3)' : 'linear-gradient(135deg, #fbbf24, #d97706)', border:'none', borderRadius:'10px', color:'#1a1206', fontWeight:900, fontSize:'0.95rem', cursor: vipClaimBusy ? 'wait' : 'pointer', boxShadow:'0 0 25px rgba(245,158,11,0.3)'}}>
                  {vipClaimBusy ? 'Claiming…' : '👑 Claim VIP role'}
                </button>
              ) : (
                <button onClick={connectDiscord} style={{width:'100%', padding:'0.85rem', background:'#5865F2', border:'none', borderRadius:'10px', color:'#fff', fontWeight:800, fontSize:'0.9rem', cursor:'pointer'}}>
                  🔗 Connect Discord to claim
                </button>
              )}
              <button onClick={snoozeVipClaimPopup} style={{width:'100%', marginTop:'0.6rem', padding:'0.6rem', background:'transparent', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', color:'#71717a', fontWeight:600, fontSize:'0.8rem', cursor:'pointer'}}>
                Maybe later
              </button>
            </div>
          </div>
        )}

        {/* VIP Dashboard Page */}
        {activePage === 'vipdashboard' && (
          <div style={{maxWidth:'780px', margin:'0 auto'}}>
            {/* Header */}
            <div style={{textAlign:'center', marginBottom:'1.5rem'}}>
              <div style={{display:'inline-flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.4rem'}}>
                <svg width="48" height="40" viewBox="0 0 24 20" fill="none">
                  <defs>
                    <linearGradient id="vipHeaderCrown" x1="0" y1="0" x2="24" y2="20">
                      <stop offset="0%" stopColor="#fde68a"/>
                      <stop offset="50%" stopColor="#fbbf24"/>
                      <stop offset="100%" stopColor="#d97706"/>
                    </linearGradient>
                  </defs>
                  <path d="M2 6 L5.5 13 L9 4 L12 12 L15 4 L18.5 13 L22 6 L20.5 17 L3.5 17 Z" fill="url(#vipHeaderCrown)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" strokeLinejoin="round"/>
                  <circle cx="2" cy="6" r="1.4" fill="#fef3c7" stroke="rgba(0,0,0,0.2)" strokeWidth="0.3"/>
                  <circle cx="22" cy="6" r="1.4" fill="#fef3c7" stroke="rgba(0,0,0,0.2)" strokeWidth="0.3"/>
                  <circle cx="12" cy="3" r="1.6" fill="#fca5a5" stroke="rgba(0,0,0,0.2)" strokeWidth="0.3"/>
                  <rect x="3.5" y="17" width="17" height="1.4" fill="#92400e" opacity="0.4"/>
                </svg>
                <h1 style={{fontSize:'1.7rem', fontWeight:900, color:'#fbbf24', margin:0, textShadow:'0 0 25px rgba(245,158,11,0.3)'}}>VIP Dashboard</h1>
              </div>
              <p style={{color:'#a1a1aa', fontSize:'0.85rem', margin:0, maxWidth:'520px', marginLeft:'auto', marginRight:'auto'}}>
                Track your VIP status and progress. SNUTZY VIPs get exclusive Discord giveaways and free-equity bonus hunts.
              </p>
            </div>

            {!user ? (
              <div style={{textAlign:'center', padding:'3rem 2rem', background:'rgba(14,14,18,0.7)', borderRadius:'16px', border:'1px solid rgba(245,158,11,0.15)'}}>
                <div style={{fontSize:'2.5rem', marginBottom:'0.75rem'}}>👑</div>
                <div style={{fontSize:'1.1rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.4rem'}}>Login to view your VIP status</div>
                <p style={{color:'#71717a', fontSize:'0.85rem', maxWidth:'380px', margin:'0 auto 1.25rem'}}>Sign in with Twitch and link your Shuffle username to track your progress towards VIP.</p>
                <button onClick={() => { window.location.href = `${BACKEND_API_URL}/auth/twitch`; }} style={{padding:'0.7rem 1.75rem', background:'#9146ff', border:'none', borderRadius:'10px', color:'#fff', fontSize:'0.95rem', fontWeight:600, cursor:'pointer'}}>
                  Login with Twitch
                </button>
              </div>
            ) : !user.shuffleUsername ? (
              <div style={{textAlign:'center', padding:'2rem', background:'rgba(14,14,18,0.7)', borderRadius:'16px', border:'1px solid rgba(139,92,246,0.2)'}}>
                <div style={{fontSize:'2rem', marginBottom:'0.5rem'}}>🔗</div>
                <div style={{fontSize:'1rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.4rem'}}>Link Your Shuffle Account</div>
                <p style={{color:'#71717a', fontSize:'0.85rem', maxWidth:'380px', margin:'0 auto 1rem'}}>Connect your shuffle.com username so we can track your VIP progress automatically.</p>
                <button onClick={() => navigateTo('profile')} style={{padding:'0.6rem 1.5rem', background:'rgba(139,92,246,0.2)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'10px', color:'#c084fc', fontSize:'0.9rem', fontWeight:700, cursor:'pointer'}}>
                  Link Shuffle Username →
                </button>
              </div>
            ) : (() => {
              const totalWagered = userWagerStats?.totalWagered || 0;
              const periodWagered = userWagerStats?.periodWagered || 0;
              const periodWageredRaw = userWagerStats?.periodWageredRaw || periodWagered;
              const isWeighted = userWagerStats?.isWeighted || false;
              const vipRemoved = userWagerStats?.vipRemoved || null;
              const vipInfo = userWagerStats?.vip || null;
              const VIP_THRESHOLD = vipInfo?.threshold || 50000;
              const MAINTAIN_REQUIRED = vipInfo?.maintainRequired || 15000;
              // July 2026 rules: new VIPs qualify on WEIGHTED wager since the weighted
              // era; legacy VIPs (grandfathered at the rule change) keep VIP. The
              // backend computes both — fall back to the old raw check if absent.
              const vipWagerBase = vipInfo ? (vipInfo.weightedEraWager || 0) : totalWagered;
              const isVip = vipInfo ? vipInfo.isVip : totalWagered >= VIP_THRESHOLD;
              // Admin-granted VIP: no wager requirement, no maintenance pressure —
              // the dashboard says so instead of showing at-risk warnings.
              const isGrantedVip = !!vipInfo?.granted;
              const progressToVip = Math.min((vipWagerBase / VIP_THRESHOLD) * 100, 100);
              const maintainProgress = Math.min((periodWagered / MAINTAIN_REQUIRED) * 100, 100);
              const isMaintaining = periodWagered >= MAINTAIN_REQUIRED;
              const dollarsToVip = Math.max(VIP_THRESHOLD - vipWagerBase, 0);
              const dollarsToMaintain = Math.max(MAINTAIN_REQUIRED - periodWagered, 0);
              
              // Discord VIP claim card (shown in all states). When the user is
              // eligible (VIP, or removed + regain-eligible) and self-claim is
              // configured, this becomes a one-click role claim; otherwise it
              // falls back to the manual-ticket instructions.
              const vipEligibleToClaim = isVip || !!vipRemoved?.regainEligible;
              const selfClaimAvailable = vipClaimStatus?.available;
              // Actionable = there's a claim to make (role not held yet). Rendered
              // prominently at the TOP of the page; the passive states (role held →
              // re-sync, or not yet eligible → info) stay at the bottom.
              const vipClaimActionable = vipEligibleToClaim && selfClaimAvailable && !vipClaimStatus?.hasRole;
              const discordDisclaimer = (
                <div style={{background:'rgba(88,101,242,0.06)', border:'1px solid rgba(88,101,242,0.2)', borderRadius:'14px', padding:'1rem 1.25rem', marginBottom:'1rem'}}>
                  <div style={{display:'flex', alignItems:'flex-start', gap:'0.75rem'}}>
                    <div style={{fontSize:'1.2rem', flexShrink:0}}>👑</div>
                    <div style={{fontSize:'0.78rem', color:'#a1a1aa', lineHeight:'1.5', flex:1}}>
                      {vipEligibleToClaim && selfClaimAvailable ? (
                        vipClaimStatus?.hasRole ? (
                          <>
                            <strong style={{color:'#22c55e'}}>✅ VIP Discord role active.</strong> You're all set — enjoy <strong style={{color:'#fff'}}>#vip-chat</strong>! If the role is ever missing, hit the button to re-sync it.
                            <div style={{marginTop:'0.6rem'}}>
                              <button onClick={claimVipRole} disabled={vipClaimBusy} style={{padding:'0.4rem 0.85rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'8px', color:'#d4d4d8', fontSize:'0.72rem', fontWeight:700, cursor: vipClaimBusy ? 'wait' : 'pointer'}}>{vipClaimBusy ? 'Syncing…' : '🔄 Re-sync VIP role'}</button>
                            </div>
                          </>
                        ) : vipClaimStatus?.discordLinked ? (
                          <>
                            <strong style={{color:'#fbbf24'}}>{vipRemoved?.regainEligible ? 'You\'re eligible to reclaim VIP!' : 'You qualify for VIP!'}</strong> Claim your Discord VIP role with one click — the bot grants it instantly and welcomes you in <strong style={{color:'#fff'}}>#vip-chat</strong>.
                            <div style={{marginTop:'0.6rem'}}>
                              <button onClick={claimVipRole} disabled={vipClaimBusy} style={{padding:'0.55rem 1.2rem', background: vipClaimBusy ? 'rgba(107,114,128,0.3)' : 'linear-gradient(135deg, #fbbf24, #d97706)', border:'none', borderRadius:'8px', color:'#1a1206', fontSize:'0.8rem', fontWeight:800, cursor: vipClaimBusy ? 'wait' : 'pointer'}}>{vipClaimBusy ? 'Claiming…' : '👑 Claim VIP role'}</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <strong style={{color:'#7289da'}}>Connect Discord to claim your VIP role.</strong> Once connected, you can claim it here with one click.
                            <div style={{marginTop:'0.6rem'}}>
                              <button onClick={connectDiscord} style={{padding:'0.5rem 1rem', background:'#5865F2', border:'none', borderRadius:'8px', color:'#fff', fontSize:'0.78rem', fontWeight:700, cursor:'pointer'}}>🔗 Connect Discord</button>
                            </div>
                          </>
                        )
                      ) : (
                        <>
                          <strong style={{color:'#7289da'}}>VIP role is claimed right here.</strong> Once you're eligible, a <strong style={{color:'#fbbf24'}}>Claim VIP role</strong> button appears on this page and the bot grants your Discord role automatically.
                          <div style={{marginTop:'0.5rem'}}>
                            <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#7289da', textDecoration:'none', fontSize:'0.75rem', fontWeight:600}}>Join our Discord →</a>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
              
              // ============================================
              // REMOVED VIP STATE
              // ============================================
              if (vipRemoved) {
                return (
                  <>
                    {/* Removed Status Banner */}
                    <div style={{textAlign:'center', marginBottom:'1.25rem'}}>
                      <div style={{display:'inline-flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 1.25rem', background:'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(220,38,38,0.08))', border:'1px solid rgba(239,68,68,0.4)', borderRadius:'999px', boxShadow:'0 0 20px rgba(239,68,68,0.15)'}}>
                        <span style={{fontSize:'1rem'}}>⚠️</span>
                        <span style={{fontSize:'0.85rem', fontWeight:800, color:'#f87171', letterSpacing:'1px'}}>VIP STATUS REMOVED</span>
                      </div>
                    </div>

                    {/* Removed explanation banner */}
                    <div style={{
                      background:'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(220,38,38,0.04))',
                      border:'1px solid rgba(239,68,68,0.2)', borderRadius:'14px',
                      padding:'1.25rem', marginBottom:'1rem'
                    }}>
                      <div style={{display:'flex', alignItems:'flex-start', gap:'0.85rem'}}>
                        <div style={{fontSize:'1.5rem', flexShrink:0}}>🚫</div>
                        <div>
                          <div style={{fontSize:'0.95rem', fontWeight:700, color:'#fca5a5', marginBottom:'0.35rem'}}>Your VIP status has been removed</div>
                          <p style={{fontSize:'0.8rem', color:'#a1a1aa', lineHeight:'1.5', margin:'0 0 0.5rem 0'}}>
                            Your VIP role was removed on <strong style={{color:'#e2e8f0'}}>{new Date(vipRemoved.removedAt).toLocaleDateString('en-GB', {day:'numeric', month:'long', year:'numeric'})}</strong>{vipRemoved.reason ? ` — Reason: ${vipRemoved.reason}` : '.'} Don't worry — you can earn it back!
                          </p>
                          <p style={{fontSize:'0.78rem', color:'#71717a', margin:0}}>
                            To regain VIP, wager <strong style={{color:'#fbbf24'}}>${(vipRemoved.regainRequired || 25000).toLocaleString()}</strong> (weighted) more on Shuffle under guild <strong style={{color:'#e2e8f0'}}>SNUTZY</strong> since your removal. Once you hit the threshold, you can reclaim your role right from this page.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Regain Progress Card */}
                    <div style={{
                      background:'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(217,119,6,0.05))',
                      border:'1px solid rgba(245,158,11,0.25)',
                      borderRadius:'16px', padding:'1.5rem', marginBottom:'1rem', position:'relative', overflow:'hidden'
                    }}>
                      <div style={{position:'absolute', top:'-30%', right:'-10%', width:'200px', height:'200px', background:'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)', borderRadius:'50%', pointerEvents:'none'}} />
                      <div style={{position:'relative', zIndex:1}}>
                        <div style={{fontSize:'0.7rem', color:'#71717a', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'0.4rem'}}>Progress to Regain VIP</div>
                        <div style={{display:'flex', alignItems:'baseline', gap:'0.6rem', marginBottom:'0.75rem', flexWrap:'wrap'}}>
                          <span style={{fontSize:'2rem', fontWeight:900, color:'#fff', fontFamily:'monospace'}}>${vipRemoved.wagerSinceRemoval.toLocaleString(undefined, {maximumFractionDigits:0})}</span>
                          <span style={{fontSize:'1rem', color:'#71717a', fontFamily:'monospace'}}>/ ${vipRemoved.regainRequired.toLocaleString()}</span>
                          <span style={{marginLeft:'auto', fontSize:'1.2rem', fontWeight:800, color: vipRemoved.regainEligible ? '#22c55e' : '#fbbf24'}}>{vipRemoved.regainProgress.toFixed(1)}%</span>
                        </div>
                        
                        <div style={{width:'100%', height:'14px', background:'rgba(255,255,255,0.06)', borderRadius:'7px', overflow:'hidden', marginBottom:'0.6rem'}}>
                          <div style={{
                            width:`${vipRemoved.regainProgress}%`, height:'100%',
                            background: vipRemoved.regainEligible 
                              ? 'linear-gradient(90deg, #22c55e, #4ade80)' 
                              : 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                            borderRadius:'7px', transition:'width 0.6s ease',
                            boxShadow: vipRemoved.regainEligible ? '0 0 12px rgba(34,197,94,0.5)' : '0 0 12px rgba(245,158,11,0.4)'
                          }} />
                        </div>
                        
                        {vipRemoved.regainEligible ? (
                          <div style={{
                            background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)',
                            borderRadius:'10px', padding:'0.75rem 1rem', marginTop:'0.75rem'
                          }}>
                            <div style={{fontSize:'0.85rem', fontWeight:700, color:'#22c55e', marginBottom:'0.25rem'}}>✅ Eligible to Reclaim VIP!</div>
                            <div style={{fontSize:'0.75rem', color:'#a1a1aa'}}>Use the <strong style={{color:'#fbbf24'}}>👑 Claim VIP role</strong> button in the card above — the bot reinstates your role instantly.</div>
                          </div>
                        ) : (
                          <div style={{fontSize:'0.82rem', color:'#a1a1aa', textAlign:'center'}}>
                            <strong style={{color:'#fbbf24'}}>${(vipRemoved.regainRequired - vipRemoved.wagerSinceRemoval).toLocaleString()}</strong> more weighted wager to be eligible to reclaim VIP
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.6rem', marginBottom:'1rem'}}>
                      <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(139,92,246,0.12)', borderRadius:'10px', padding:'0.85rem', textAlign:'center'}}>
                        <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.25rem'}}>All-Time</div>
                        <div style={{fontSize:'1.2rem', fontWeight:800, color:'#a78bfa', fontFamily:'monospace'}}>${totalWagered >= 1000000 ? (totalWagered/1000000).toFixed(2)+'M' : totalWagered >= 1000 ? (totalWagered/1000).toFixed(1)+'K' : totalWagered.toFixed(0)}</div>
                      </div>
                      <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(245,158,11,0.12)', borderRadius:'10px', padding:'0.85rem', textAlign:'center'}}>
                        <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.25rem'}}>Weighted Since Removal</div>
                        <div style={{fontSize:'1.2rem', fontWeight:800, color:'#fbbf24', fontFamily:'monospace'}}>${vipRemoved.wagerSinceRemoval >= 1000 ? (vipRemoved.wagerSinceRemoval/1000).toFixed(1)+'K' : vipRemoved.wagerSinceRemoval.toFixed(0)}</div>
                      </div>
                      <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(239,68,68,0.12)', borderRadius:'10px', padding:'0.85rem', textAlign:'center'}}>
                        <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.25rem'}}>Status</div>
                        <div style={{fontSize:'1.2rem', fontWeight:800, color:'#f87171'}}>Removed</div>
                      </div>
                    </div>

                    {discordDisclaimer}

                    {/* CTA */}
                    <div style={{textAlign:'center', marginBottom:'1.5rem'}}>
                      <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer" style={{
                        display:'inline-block', padding:'0.75rem 2rem',
                        background:'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius:'10px',
                        color:'#000', fontSize:'0.95rem', fontWeight:800, textDecoration:'none',
                        boxShadow:'0 0 20px rgba(245,158,11,0.3)'
                      }}>
                        Wager on Shuffle →
                      </a>
                      <div style={{fontSize:'0.7rem', color:'#71717a', marginTop:'0.5rem'}}>
                        Use guild <strong style={{color:'#71717a'}}>SNUTZY</strong> · gamble responsibly
                      </div>
                    </div>
                  </>
                );
              }
              
              return (
                <>
                  {/* Status Badge */}
                  <div style={{textAlign:'center', marginBottom:'1.25rem'}}>
                    {isVip ? (
                      <div style={{display:'inline-flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 1.25rem', background:'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(217,119,6,0.1))', border:'1px solid rgba(251,191,36,0.4)', borderRadius:'999px', boxShadow:'0 0 20px rgba(245,158,11,0.15)'}}>
                        <span style={{fontSize:'1rem'}}>👑</span>
                        <span style={{fontSize:'0.85rem', fontWeight:800, color:'#fbbf24', letterSpacing:'1px'}}>VIP MEMBER</span>
                        {isGrantedVip ? (
                          <span style={{fontSize:'0.7rem', color:'#c084fc', fontWeight:700, marginLeft:'0.3rem'}}>⭐ ADMIN GRANTED</span>
                        ) : (
                          <>
                            {!isMaintaining && <span style={{fontSize:'0.7rem', color:'#f87171', fontWeight:700, marginLeft:'0.3rem'}}>⚠️ AT RISK</span>}
                            {isMaintaining && <span style={{fontSize:'0.7rem', color:'#22c55e', fontWeight:700, marginLeft:'0.3rem'}}>✓ ACTIVE</span>}
                          </>
                        )}
                      </div>
                    ) : (
                      <div style={{display:'inline-flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 1.25rem', background:'rgba(139,92,246,0.12)', border:'1px solid rgba(139,92,246,0.25)', borderRadius:'999px'}}>
                        <span style={{fontSize:'1rem'}}>🌱</span>
                        <span style={{fontSize:'0.85rem', fontWeight:800, color:'#a78bfa', letterSpacing:'1px'}}>ASPIRING VIP</span>
                      </div>
                    )}
                  </div>

                  {/* Claimable VIP role goes front and center; passive states render at the bottom */}
                  {vipClaimActionable && discordDisclaimer}

                  {/* Main Progress Card */}
                  <div style={{
                    background:'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(139,92,246,0.05))',
                    border:`1px solid ${isVip ? 'rgba(245,158,11,0.25)' : 'rgba(139,92,246,0.2)'}`,
                    borderRadius:'16px', padding:'1.5rem', marginBottom:'1rem', position:'relative', overflow:'hidden'
                  }}>
                    <div style={{position:'absolute', top:'-30%', right:'-10%', width:'200px', height:'200px', background:`radial-gradient(circle, ${isVip ? 'rgba(245,158,11,0.08)' : 'rgba(139,92,246,0.06)'} 0%, transparent 70%)`, borderRadius:'50%', pointerEvents:'none'}} />
                    
                    <div style={{position:'relative', zIndex:1}}>
                      {!isVip ? (
                        <>
                          <div style={{fontSize:'0.7rem', color:'#71717a', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'0.4rem'}}>Progress Towards VIP (Weighted Wager)</div>
                          <div style={{display:'flex', alignItems:'baseline', gap:'0.6rem', marginBottom:'0.75rem', flexWrap:'wrap'}}>
                            <span style={{fontSize:'2rem', fontWeight:900, color:'#fff', fontFamily:'monospace'}}>${vipWagerBase.toLocaleString(undefined, {maximumFractionDigits:0})}</span>
                            <span style={{fontSize:'1rem', color:'#71717a', fontFamily:'monospace'}}>/ ${VIP_THRESHOLD.toLocaleString()}</span>
                            <span style={{marginLeft:'auto', fontSize:'1.2rem', fontWeight:800, color:'#a78bfa'}}>{progressToVip.toFixed(1)}%</span>
                          </div>
                          
                          {/* Big progress bar */}
                          <div style={{width:'100%', height:'14px', background:'rgba(255,255,255,0.06)', borderRadius:'7px', overflow:'hidden', marginBottom:'0.6rem', position:'relative'}}>
                            <div style={{
                              width:`${progressToVip}%`, height:'100%',
                              background:'linear-gradient(90deg, #8b5cf6, #a855f7, #c084fc)',
                              borderRadius:'7px', transition:'width 0.6s ease',
                              boxShadow:'0 0 12px rgba(168,85,247,0.5)'
                            }} />
                          </div>
                          
                          <div style={{fontSize:'0.82rem', color:'#a1a1aa', textAlign:'center'}}>
                            <strong style={{color:'#fbbf24'}}>${dollarsToVip.toLocaleString()}</strong> more weighted wager to unlock VIP status 👑
                          </div>
                        </>
                      ) : isGrantedVip ? (
                        <>
                          <div style={{fontSize:'0.7rem', color:'#71717a', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'0.4rem'}}>VIP Status</div>
                          <div style={{display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.6rem'}}>
                            <span style={{fontSize:'2rem'}}>⭐</span>
                            <span style={{fontSize:'1.3rem', fontWeight:900, color:'#fbbf24'}}>Admin-Granted VIP</span>
                          </div>
                          <div style={{fontSize:'0.82rem', color:'#a1a1aa', lineHeight:1.55}}>
                            Your VIP status was granted directly by the SNUTZY team, so the ${MAINTAIN_REQUIRED.toLocaleString()} per-period maintenance requirement doesn't apply to you. Enjoy the perks! 👑
                            {periodWagered > 0 && (
                              <div style={{marginTop:'0.45rem', fontSize:'0.72rem', color:'#71717a'}}>Wagered this period: ${periodWagered.toLocaleString(undefined, {maximumFractionDigits:0})}{isWeighted ? ' (weighted)' : ''}</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{fontSize:'0.7rem', color:'#71717a', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'0.4rem'}}>VIP Maintenance — Current Period{isWeighted ? ' (Weighted)' : ''}</div>
                          <div style={{display:'flex', alignItems:'baseline', gap:'0.6rem', marginBottom:'0.75rem', flexWrap:'wrap'}}>
                            <span style={{fontSize:'2rem', fontWeight:900, color:'#fff', fontFamily:'monospace'}}>${periodWagered.toLocaleString(undefined, {maximumFractionDigits:0})}</span>
                            <span style={{fontSize:'1rem', color:'#71717a', fontFamily:'monospace'}}>/ ${MAINTAIN_REQUIRED.toLocaleString()}</span>
                            <span style={{marginLeft:'auto', fontSize:'1.2rem', fontWeight:800, color: isMaintaining ? '#22c55e' : maintainProgress > 50 ? '#f59e0b' : '#ef4444'}}>{maintainProgress.toFixed(1)}%</span>
                          </div>
                          {isWeighted && periodWageredRaw !== periodWagered && (
                            <div style={{fontSize:'0.68rem', color:'#71717a', marginBottom:'0.5rem'}}>Raw wager: ${periodWageredRaw.toLocaleString(undefined, {maximumFractionDigits:0})}</div>
                          )}
                          
                          {/* Big progress bar */}
                          <div style={{width:'100%', height:'14px', background:'rgba(255,255,255,0.06)', borderRadius:'7px', overflow:'hidden', marginBottom:'0.6rem'}}>
                            <div style={{
                              width:`${maintainProgress}%`, height:'100%',
                              background: isMaintaining 
                                ? 'linear-gradient(90deg, #22c55e, #4ade80)' 
                                : maintainProgress > 50 
                                  ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' 
                                  : 'linear-gradient(90deg, #ef4444, #f87171)',
                              borderRadius:'7px', transition:'width 0.6s ease',
                              boxShadow: isMaintaining ? '0 0 12px rgba(34,197,94,0.4)' : maintainProgress > 50 ? '0 0 12px rgba(245,158,11,0.4)' : '0 0 12px rgba(239,68,68,0.4)'
                            }} />
                          </div>
                          
                          {isMaintaining ? (
                            <div style={{fontSize:'0.82rem', color:'#22c55e', textAlign:'center', fontWeight:600}}>
                              ✅ You've maintained VIP this period! Keep it up.
                            </div>
                          ) : (
                            <div style={{fontSize:'0.82rem', color:'#a1a1aa', textAlign:'center'}}>
                              <strong style={{color:'#f59e0b'}}>${dollarsToMaintain.toLocaleString()}</strong> more weighted wager this period to maintain VIP
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div style={{display:'grid', gridTemplateColumns: isWeighted && periodWageredRaw !== periodWagered ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap:'0.6rem', marginBottom:'1rem'}}>
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(139,92,246,0.12)', borderRadius:'10px', padding:'0.85rem', textAlign:'center'}}>
                      <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.25rem'}}>All-Time</div>
                      <div style={{fontSize:'1.2rem', fontWeight:800, color:'#a78bfa', fontFamily:'monospace'}}>${totalWagered >= 1000000 ? (totalWagered/1000000).toFixed(2)+'M' : totalWagered >= 1000 ? (totalWagered/1000).toFixed(1)+'K' : totalWagered.toFixed(0)}</div>
                    </div>
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(34,197,94,0.12)', borderRadius:'10px', padding:'0.85rem', textAlign:'center'}}>
                      <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.25rem'}}>{isWeighted ? 'Weighted' : 'This Period'}</div>
                      <div style={{fontSize:'1.2rem', fontWeight:800, color:'#22c55e', fontFamily:'monospace'}}>${periodWagered >= 1000 ? (periodWagered/1000).toFixed(1)+'K' : periodWagered.toFixed(0)}</div>
                    </div>
                    {isWeighted && periodWageredRaw !== periodWagered && (
                      <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'0.85rem', textAlign:'center'}}>
                        <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.25rem'}}>Raw Wager</div>
                        <div style={{fontSize:'1.2rem', fontWeight:800, color:'#a1a1aa', fontFamily:'monospace'}}>${periodWageredRaw >= 1000 ? (periodWageredRaw/1000).toFixed(1)+'K' : periodWageredRaw.toFixed(0)}</div>
                      </div>
                    )}
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(245,158,11,0.12)', borderRadius:'10px', padding:'0.85rem', textAlign:'center'}}>
                      <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'0.25rem'}}>Status</div>
                      <div style={{fontSize:'1.2rem', fontWeight:800, color: isVip ? (isGrantedVip ? '#c084fc' : isMaintaining ? '#22c55e' : '#f59e0b') : '#a78bfa'}}>
                        {isVip ? (isGrantedVip ? 'Granted' : isMaintaining ? 'Active' : 'At Risk') : 'Pre-VIP'}
                      </div>
                    </div>
                  </div>

                  {/* Why we do this */}
                  <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem'}}>
                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.6rem', display:'flex', alignItems:'center', gap:'0.4rem'}}>
                      💡 Why VIP Maintenance?
                    </div>
                    <p style={{fontSize:'0.8rem', color:'#a1a1aa', lineHeight:'1.6', margin:0}}>
                      Our goal is to <strong style={{color:'#e2e8f0'}}>actively reward people who consistently wager under guild SNUTZY</strong> — not just people who wagered $50k once and disappeared. By requiring at least <strong style={{color:'#fbbf24'}}>${MAINTAIN_REQUIRED.toLocaleString()} weighted wagered each leaderboard period</strong>, we make sure VIP perks go to community members who are actually engaged. This frees up budget for bigger raffle prizes, weekly leaderboard payouts, and exclusive VIP-only giveaways.
                    </p>
                  </div>

                  {/* What VIPs get */}
                  <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem'}}>
                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>👑 VIP Perks</div>
                    {[
                      { icon: '🎁', title: 'Exclusive VIP Discord Giveaways', desc: 'VIP-only giveaways in Discord with bigger prize pools that regular members can\'t enter' },
                      { icon: '🎰', title: 'Free-Equity VIP Bonus Hunts', desc: 'Exclusive community bonus hunts where VIPs get free equity — no buy-in required' }
                    ].map((perk, i) => (
                      <div key={i} style={{display:'flex', gap:'0.75rem', alignItems:'flex-start', marginBottom: i < 1 ? '0.6rem' : 0}}>
                        <div style={{fontSize:'1.1rem', flexShrink:0}}>{perk.icon}</div>
                        <div>
                          <div style={{fontSize:'0.82rem', fontWeight:700, color:'#e2e8f0'}}>{perk.title}</div>
                          <div style={{fontSize:'0.72rem', color:'#71717a', lineHeight:'1.4'}}>{perk.desc}</div>
                        </div>
                      </div>
                    ))}
                    <div style={{marginTop:'0.75rem', padding:'0.5rem 0.75rem', background:'rgba(139,92,246,0.05)', border:'1px solid rgba(139,92,246,0.12)', borderRadius:'8px', fontSize:'0.7rem', color:'#a1a1aa'}}>
                      💡 More VIP perks coming soon as the community grows.
                    </div>
                  </div>

                  {/* Other ways to earn */}
                  <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'14px', padding:'1.25rem', marginBottom:'1rem'}}>
                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem'}}>🎯 Earn More on Snutzy.gg</div>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem'}}>
                      <button onClick={() => navigateTo('leaderboard')} style={{padding:'0.7rem', background:'rgba(139,92,246,0.08)', border:'1px solid rgba(139,92,246,0.2)', borderRadius:'10px', color:'#c084fc', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', textAlign:'left'}}>
                        🏆 Monthly Leaderboard<br/><span style={{fontSize:'0.68rem', color:'#71717a', fontWeight:500}}>Top 15 share {monthlyPoolShort()}</span>
                      </button>
                      {featureFlags.weekly && (
                      <button onClick={() => { setLeaderboardMode('weekly'); navigateTo('leaderboard'); }} style={{padding:'0.7rem', background:'rgba(139,92,246,0.08)', border:'1px solid rgba(139,92,246,0.2)', borderRadius:'10px', color:'#c084fc', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', textAlign:'left'}}>
                        📅 Weekly Leaderboard<br/><span style={{fontSize:'0.68rem', color:'#71717a', fontWeight:500}}>Top 10 share $2,500</span>
                      </button>
                      )}
                      <button onClick={() => navigateTo('pointsshop')} style={{padding:'0.7rem', background:'rgba(234,179,8,0.08)', border:'1px solid rgba(234,179,8,0.2)', borderRadius:'10px', color:'#eab308', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', textAlign:'left'}}>
                        💎 Points Shop<br/><span style={{fontSize:'0.68rem', color:'#71717a', fontWeight:500}}>Redeem for prizes</span>
                      </button>
                    </div>
                  </div>

                  {!vipClaimActionable && discordDisclaimer}

                  {/* CTA */}
                  <div style={{textAlign:'center', marginBottom:'1.5rem'}}>
                    <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer" style={{
                      display:'inline-block', padding:'0.75rem 2rem',
                      background:'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius:'10px',
                      color:'#000', fontSize:'0.95rem', fontWeight:800, textDecoration:'none',
                      boxShadow:'0 0 20px rgba(245,158,11,0.3)'
                    }}>
                      Wager on Shuffle →
                    </a>
                    <div style={{fontSize:'0.7rem', color:'#71717a', marginTop:'0.5rem'}}>
                      Use guild <strong style={{color:'#71717a'}}>SNUTZY</strong> · gamble responsibly
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {activePage === 'signup' && (
          <div className="signup-container">
            <h1 className="simple-page-title">Join Shuffle</h1>
            <div className="signup-card">
              <img src="/shuffle-s.png" alt="Shuffle" className="signup-shuffle-logo" />
              <div className="signup-title">🎰 Get Started Now</div>
              <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer" className="signup-link-btn">Sign Up with Guild SNUTZY →</a>
              <div className="code-display">
                <div className="code-label">Or enter this code when signing up:</div>
                <div className="code-value">SNUTZY</div>
                <div className="code-hint">If you forget, you can add the guild within 24 hours of signing up.</div>
              </div>
            </div>
            
            {/* Why Shuffle is Better */}
            <div className="info-card" style={{ borderColor: 'rgba(34, 197, 94, 0.3)', background: 'rgba(34, 197, 94, 0.05)' }}>
              <div className="info-title">💎 Why Shuffle is the Best Choice</div>
              <div className="info-item">
                <div className="info-icon">📈</div>
                <div className="info-content">
                  <h4>Rewards That Stack</h4>
                  <p>Playing under guild SNUTZY adds our rewards on top of everything Shuffle already gives you: <strong>Shuffle's rank-up and weekly/monthly rewards, plus our {poolLabel} leaderboard, wager rewards, and giveaways.</strong></p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🎲</div>
                <div className="info-content">
                  <h4>Better RTP on Original Games</h4>
                  <p>Shuffle offers higher RTP on their original games compared to competitors - more value on every spin!</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">⬆️</div>
                <div className="info-content">
                  <h4>Rank Up Rewards</h4>
                  <p>Earn rewards as you level up your account - the more you play, the more you earn back!</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🏆</div>
                <div className="info-content">
                  <h4>Weekly & Monthly Rewards</h4>
                  <p>Shuffle offers additional weekly and monthly reward programs on top of everything else.</p>
                </div>
              </div>
            </div>

            {/* Current Promotions */}
            <div className="info-card" style={{ borderColor: 'rgba(251, 191, 36, 0.3)', background: 'rgba(251, 191, 36, 0.05)' }}>
              <div className="info-title">🔥 Current Shuffle Promotions</div>
              <div className="info-item">
                <div className="info-icon">🎰</div>
                <div className="info-content">
                  <h4>Hacksaw Boosted RTP! <span style={{ color: '#22c55e', fontSize: '0.8rem', marginLeft: '0.5rem' }}>LIVE</span></h4>
                  <p>Get more from every spin with a boosted RTP on your favourite Hacksaw titles.</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">⬆️</div>
                <div className="info-content">
                  <h4>Boosted Level Up Rewards! <span style={{ color: '#22c55e', fontSize: '0.8rem', marginLeft: '0.5rem' }}>LIVE</span><span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: '0.5rem' }}>Ends Mar 18, 2026</span></h4>
                  <p>Shuffle is boosting level up rewards! Simply rank up and claim your rewards from your VIP Panel. Every new rank from Silver to Sapphire unlocks a <strong>Rank Up Bonus</strong>, <strong>Level Up Reload</strong>, and <strong>Recent Play Bonus</strong>. When you reach a new rank, the VIP team will reach out through live support — just reply with your currency of choice!</p>
                </div>
              </div>
              <div className="info-item" style={{ borderTop: '1px solid rgba(251, 191, 36, 0.15)', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
                <div className="info-icon">✨</div>
                <div className="info-content">
                  <h4 style={{ color: '#fbbf24' }}>+ Many More</h4>
                  <p>Shuffle constantly runs new promotions and events. <a href="https://shuffle.com/promotions" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>Check out all current promotions →</a></p>
                </div>
              </div>
            </div>

            <div className="info-card" style={{ borderColor: 'rgba(168, 85, 247, 0.3)', background: 'rgba(168, 85, 247, 0.05)' }}>
              <div className="info-title">🎉 Guild SNUTZY Rewards</div>
              <div className="info-item">
                <div className="info-icon">🏅</div>
                <div className="info-content">
                  <h4>Monthly Leaderboard</h4>
                  <p>Compete on our leaderboard for extra prizes every month - rewards on top of Shuffle's own rewards!</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">🎰</div>
                <div className="info-content">
                  <h4>Discord Giveaways</h4>
                  <p>Regular giveaways in our Discord for all members using guild SNUTZY.</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">📺</div>
                <div className="info-content">
                  <h4>Twitch Giveaways</h4>
                  <p>Giveaways during live streams. Follow to stay updated!</p>
                </div>
              </div>
            </div>
            
            <div className="info-card">
              <div className="info-title">🎁 First Deposit Bonus</div>
              <div className="info-item">
                <div className="info-icon">💵</div>
                <div className="info-content">
                  <h4>100% Deposit Match (up to $1,000)</h4>
                  <p>First-time depositors can get a 100% match on deposits between $20 - $1,000 USD. <strong>Contact live support BEFORE making your first deposit</strong> to agree to the terms and activate your bonus. 35x rollover requirement on deposit + bonus.</p>
                </div>
              </div>
            </div>
            
            <div className="info-card">
              <div className="info-title">ℹ️ Account Info</div>
              <div className="info-content">
                <p>New accounts are required to enter personal information, but no matter what you enter you will be able to withdraw.</p>
              </div>
            </div>

            {/* Shuffle Deposit Guide */}
            <div className="info-card" style={{ borderColor: 'rgba(34, 197, 94, 0.3)', background: 'rgba(34, 197, 94, 0.05)' }}>
              <div className="info-title">💰 Shuffle Deposit Guide</div>
              
              {/* .com warning */}
              <div style={{padding:'0.75rem 1rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'10px', marginBottom:'1rem'}}>
                <div style={{fontSize:'0.85rem', fontWeight:700, color:'#fca5a5', marginBottom:'0.25rem'}}>🚨 Use shuffle.com — NOT shuffle.us</div>
                <div style={{fontSize:'0.78rem', color:'#a1a1aa', lineHeight:'1.5'}}>The .us version is a separate site. Our guild SNUTZY is on <strong style={{color:'#fff'}}>shuffle.com</strong>. Make sure you sign up on the correct site or you won't be able to claim rewards or giveaways.</div>
              </div>

              {/* VPN section */}
              <div className="info-item">
                <div className="info-icon">🌐</div>
                <div className="info-content">
                  <h4>Need a VPN?</h4>
                  <p>If Shuffle isn't loading in your region, you'll need a VPN. We recommend:</p>
                  <ul style={{margin:'0.4rem 0 0.4rem 1rem', padding:0, fontSize:'0.85rem', color:'#a1a1aa', lineHeight:'1.6'}}>
                    <li><strong style={{color:'#e2e8f0'}}>Paid:</strong> NordVPN (most popular)</li>
                    <li><strong style={{color:'#e2e8f0'}}>Free:</strong> Urban VPN (Chrome extension)</li>
                  </ul>
                  <p style={{fontSize:'0.8rem', marginTop:'0.4rem'}}>Recommended countries: 🇮🇪 Ireland · 🇳🇴 Norway · 🇦🇹 Austria · 🇳🇿 New Zealand · 🇩🇪 Germany</p>
                </div>
              </div>

              {/* Easiest method */}
              <div className="info-item" style={{borderTop:'1px solid rgba(34,197,94,0.15)', paddingTop:'0.75rem', marginTop:'0.5rem'}}>
                <div className="info-icon">⚡</div>
                <div className="info-content">
                  <h4 style={{color:'#22c55e'}}>Easiest Method — Buy Directly on Shuffle</h4>
                  <p>No external wallets needed!</p>
                  <ol style={{margin:'0.4rem 0 0 1rem', padding:0, fontSize:'0.85rem', color:'#a1a1aa', lineHeight:'1.6'}}>
                    <li>Click <strong style={{color:'#e2e8f0'}}>Wallet → Buy Crypto</strong></li>
                    <li>Choose <strong style={{color:'#e2e8f0'}}>Moonpay</strong> or <strong style={{color:'#e2e8f0'}}>Swapped</strong></li>
                    <li>Pay with 💳 Bank Card or  Apple Pay</li>
                    <li>Crypto goes straight to your Shuffle balance</li>
                  </ol>
                </div>
              </div>

              {/* Alternative method */}
              <div className="info-item" style={{borderTop:'1px solid rgba(34,197,94,0.15)', paddingTop:'0.75rem'}}>
                <div className="info-icon">🔄</div>
                <div className="info-content">
                  <h4>Alternative — Use an Exchange</h4>
                  <p>If you prefer lower fees or already have crypto:</p>
                  
                  <div style={{marginTop:'0.6rem'}}>
                    <div style={{fontSize:'0.82rem', fontWeight:700, color:'#22c55e', marginBottom:'0.3rem'}}>Step 1: Buy Crypto</div>
                    <ul style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.82rem', color:'#a1a1aa', lineHeight:'1.55'}}>
                      <li>Buy on <strong style={{color:'#e2e8f0'}}>Coinbase</strong> or other exchanges</li>
                      <li>Use <strong style={{color:'#e2e8f0'}}>Debit Card</strong> for fastest delivery</li>
                      <li>Avoid ACH if you need speed</li>
                      <li>Best coins: <strong style={{color:'#e2e8f0'}}>USDC/USDT</strong> or <strong style={{color:'#e2e8f0'}}>ETH/SOL</strong></li>
                    </ul>
                  </div>

                  <div style={{marginTop:'0.6rem'}}>
                    <div style={{fontSize:'0.82rem', fontWeight:700, color:'#22c55e', marginBottom:'0.3rem'}}>Step 2: Coinbase → Exodus (intermediate wallet)</div>
                    <ol style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.82rem', color:'#a1a1aa', lineHeight:'1.55'}}>
                      <li>Open Exodus → select your coin → Receive → copy address</li>
                      <li>Coinbase → Send → paste address</li>
                      <li>Make sure same coin + network → Confirm</li>
                    </ol>
                  </div>

                  <div style={{marginTop:'0.6rem'}}>
                    <div style={{fontSize:'0.82rem', fontWeight:700, color:'#22c55e', marginBottom:'0.3rem'}}>Step 3: Exodus → Shuffle</div>
                    <ol style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.82rem', color:'#a1a1aa', lineHeight:'1.55'}}>
                      <li>Shuffle → Wallet → Deposit → copy address</li>
                      <li>Exodus → Send → paste address → Confirm</li>
                      <li>Wait for confirmations → balance appears</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Critical warnings */}
              <div style={{padding:'0.75rem 1rem', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'10px', marginTop:'0.75rem'}}>
                <div style={{fontSize:'0.85rem', fontWeight:700, color:'#fbbf24', marginBottom:'0.4rem'}}>⚠️ Critical Warnings</div>
                <ul style={{margin:'0 0 0 1rem', padding:0, fontSize:'0.78rem', color:'#a1a1aa', lineHeight:'1.6'}}>
                  <li>Coin + network <strong style={{color:'#fbbf24'}}>MUST</strong> match between sender and receiver</li>
                  <li>Always double-check addresses before sending</li>
                  <li>Crypto transactions are <strong style={{color:'#fbbf24'}}>irreversible</strong></li>
                  <li>Save your wallet recovery phrase <strong style={{color:'#fbbf24'}}>OFFLINE</strong></li>
                </ul>
              </div>

              <div style={{textAlign:'center', marginTop:'1rem', fontSize:'0.78rem', color:'#71717a'}}>
                Still have questions? Ask in <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" style={{color:'#a78bfa'}}>Discord</a> or in stream chat. Gamble responsibly · only wager what you can afford to lose.
              </div>
            </div>
            
            <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer" className="signup-link-btn" style={{ marginBottom: '1rem' }}>Sign Up Now →</a>
            
            <p className="disclaimer">Must be 18+ to participate. Gamble responsibly.</p>
          </div>
        )}

        {/* Predictions Page */}
        {activePage === 'predictions' && (
          <div className="predictions-page-container">
            <h1 className="simple-page-title">🎯 Predictions</h1>
            {/* Active | Past Results switcher — both tabs live under the one sidebar item */}
            <div style={{display:'flex', justifyContent:'center', gap:'0.5rem', margin:'0.75rem 0 1.25rem'}}>
              <button style={{padding:'0.55rem 1.5rem', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, background:'rgba(168,85,247,0.3)', color:'#c084fc', border:'1px solid rgba(168,85,247,0.3)'}}>
                🎯 Active Prediction
              </button>
              <button onClick={() => navigateTo('predictionresults')} style={{padding:'0.55rem 1.5rem', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, background:'transparent', color:'#71717a', border:'1px solid transparent'}}>
                🥇 Past Results
              </button>
            </div>
            <p className="predictions-intro">
              Guess the ending balance of the bonus hunt! Top 3 closest predictions win prizes. Paid out to Shuffle accounts only.
            </p>

            {/* Total Prizes Paid */}
            {publicPayoutStats?.predictions > 0 && (
              <div style={{display:'flex', justifyContent:'center', margin:'1rem 0 1.5rem'}}>
                <OdometerCounter value={publicPayoutStats.predictions} label="Total Predictions Paid" size="small" />
              </div>
            )}

            {/* Live Bonus Hunt Widget */}
            <div className="live-hunt-section predictions-widget">
              <div className="live-hunt-header">
                <h2>
                  {isStreamerLive ? (
                    <>
                      <span className="live-indicator"></span>
                      Live Bonus Hunt
                    </>
                  ) : (
                    <>
                      <span className="paused-indicator">📺</span>
                      Current Hunt
                    </>
                  )}
                </h2>
                {isStreamerLive && (
                  <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="watch-live-btn">
                    Watch Live →
                  </a>
                )}
              </div>
              
              {bonusHuntStats.activeHunt ? (
                <div className="live-hunt-content">
                  <div className="predictions-widget-left">
                    <div className="live-hunt-widget-container predictions-page">
                      <iframe 
                        src="https://myrtlebot.com/widgets/myrtleBonusHunt.html?guildId=370284467845070859"
                        className="myrtle-widget predictions-page"
                        title="Live Bonus Hunt"
                      />
                    </div>
                    
                    {/* Stats under widget */}
                    <div className="predictions-widget-stats">
                      <div className="widget-stat-box">
                        <span className="widget-stat-number">{predictionData.predictionCount}</span>
                        <span className="widget-stat-label">Predictions Made</span>
                      </div>
                      <div className="widget-stat-box">
                        <span className="widget-stat-number">${predictionData.activeHunt?.startBalance?.toLocaleString() || bonusHuntStats.activeHunt.infoStartCost?.toLocaleString() || '0'}</span>
                        <span className="widget-stat-label">Starting Balance</span>
                      </div>
                      <div className="widget-stat-box">
                        <span className="widget-stat-number">{predictionData.activeHunt?.totalBonuses || bonusHuntStats.activeHunt.bonusesList?.length || 0}</span>
                        <span className="widget-stat-label">Total Bonuses</span>
                      </div>
                    </div>
                    
                    {/* Buttons under stats */}
                    <div className="predictions-widget-buttons">
                      <a href="https://twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="widget-stream-btn">
                        {isStreamerLive ? '🔴 Watch Live Stream' : '📺 Follow on Twitch'}
                      </a>
                      <button 
                        className="widget-predict-btn"
                        onClick={() => document.getElementById('prediction-form-section')?.scrollIntoView({ behavior: 'smooth' })}
                      >
                        <span>Make Your Prediction</span>
                        <span className="scroll-arrow">↓</span>
                      </button>
                    </div>
                  </div>
                  
                  <div className="live-hunt-stats-panel enhanced predictions-page">
                    <div className="stats-panel-header">
                      <h3>🏆 Prediction Prizes</h3>
                      {isStreamerLive && <span className="live-badge-small">🔴 LIVE</span>}
                    </div>
                    
                    {predictionData.rewards?.first ? (
                      <div className="prizes-section no-margin">
                        <div className="prizes-list">
                          <div className="prize-item gold">
                            <span className="prize-rank">🥇 1st Place</span>
                            <span className="prize-value">{predictionData.rewards.first}</span>
                          </div>
                          {predictionData.rewards.second && (
                            <div className="prize-item silver">
                              <span className="prize-rank">🥈 2nd Place</span>
                              <span className="prize-value">{predictionData.rewards.second}</span>
                            </div>
                          )}
                          {predictionData.rewards.third && (
                            <div className="prize-item bronze">
                              <span className="prize-rank">🥉 3rd Place</span>
                              <span className="prize-value">{predictionData.rewards.third}</span>
                            </div>
                          )}
                        </div>
                        <p className="prizes-note">Closest predictions to the final balance win!</p>
                      </div>
                    ) : (
                      <div className="prizes-section no-margin">
                        <p className="prizes-pending">Prize pool will be announced soon!</p>
                      </div>
                    )}
                    
                    {/* How it works */}
                    <div className="prediction-how-it-works">
                      <h4>How It Works</h4>
                      <ol>
                        <li>Guess the final balance after all bonuses open</li>
                        <li>Submit your prediction before they close</li>
                        <li>Closest guesses win prizes!</li>
                      </ol>
                    </div>
                    
                    {/* Prediction status */}
                    <div className={`prediction-status-badge ${predictionData.predictionsOpen ? 'open' : 'closed'}`}>
                      {predictionData.predictionsOpen ? '🟢 Predictions Open' : '🔴 Predictions Closed'}
                    </div>
                  </div>
                </div>
              ) : bonusHuntsLoadState === 'loading' ? (
                <div className="no-active-hunt-message" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
                  <Skel w="220px" h="1rem" />
                  <Skel w="160px" h="0.8rem" />
                </div>
              ) : bonusHuntsLoadState === 'error' ? (
                <div className="load-error-box">
                  <p>Having trouble loading hunt data — the server may be waking up.</p>
                  <button className="retry-btn" onClick={() => { setBonusHuntsLoadState('loading'); fetchBonusHuntData(); }}>↻ Retry</button>
                </div>
              ) : (
                <div className="no-active-hunt-message">
                  <p>No active bonus hunt right now. Check back when Snutzy goes live!</p>
                </div>
              )}
            </div>

            {/* Prediction Form */}
            <div className="prediction-game-section" id="prediction-form-section">
              <div className="prediction-game-header">
                <h2>🎯 Make Your Prediction</h2>
              </div>
              
              {predictionLoadState === 'loading' && !predictionData.activeHunt ? (
                /* Skeleton matches the loaded form's shape so nothing jumps when data lands */
                <div className="prediction-game-content">
                  <div className="prediction-hunt-info">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className="prediction-info-item">
                        <Skel w="80px" h="0.7rem" />
                        <Skel w="95px" h="1.15rem" style={{ marginTop: '0.45rem' }} />
                      </div>
                    ))}
                  </div>
                  <div className="prediction-form-container">
                    <Skel h="52px" r="10px" />
                    <Skel w="45%" h="0.8rem" style={{ margin: '0.9rem auto 0' }} />
                  </div>
                </div>
              ) : predictionLoadState === 'error' ? (
                <div className="load-error-box">
                  <p>Having trouble loading predictions — the server may be waking up.</p>
                  <button className="retry-btn" onClick={() => { setPredictionLoadState('loading'); fetchPredictionData(); }}>↻ Retry</button>
                </div>
              ) : predictionData.activeHunt ? (
                <div className="prediction-game-content">
                  <div className="prediction-hunt-info">
                    <div className="prediction-info-item">
                      <span className="prediction-info-label">Starting Balance</span>
                      <span className="prediction-info-value">${predictionData.activeHunt.startBalance?.toLocaleString() || 'N/A'}</span>
                    </div>
                    <div className="prediction-info-item">
                      <span className="prediction-info-label">Total Bonuses</span>
                      <span className="prediction-info-value">{predictionData.activeHunt.totalBonuses || 'N/A'}</span>
                    </div>
                    <div className="prediction-info-item">
                      <span className="prediction-info-label">Predictions</span>
                      <span className="prediction-info-value">{predictionData.predictionCount}</span>
                    </div>
                    <div className="prediction-info-item">
                      <span className="prediction-info-label">Status</span>
                      <span className={`prediction-status-badge ${predictionData.predictionsOpen ? 'open' : 'closed'}`}>
                        {predictionData.predictionsOpen ? '🟢 Open' : '🔴 Closed'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="prediction-form-container">
                    {!predictionData.predictionsOpen ? (
                      <div className="predictions-closed-message">
                        <div className="predictions-closed-icon">⏸️</div>
                        <p>Predictions are currently paused.</p>
                        <p className="predictions-closed-hint">Good luck to everyone who entered! 🍀</p>
                      </div>
                    ) : user ? (
                      <div className="prediction-slots-container">
                        {/* Slot 1 - Free prediction */}
                        <div className="prediction-slot">
                          <div className="prediction-slot-header">
                            <span className="slot-label">Prediction #1</span>
                            <span className="slot-badge free">Free</span>
                          </div>
                          {predictionData.myPredictions.find(p => p.slot === 1) ? (
                            <div className="prediction-submitted-compact">
                              <span className="prediction-value">${parseFloat(predictionData.myPredictions.find(p => p.slot === 1).predictedBalance).toLocaleString()}</span>
                              <div className="prediction-input-row compact">
                                <span className="prediction-dollar">$</span>
                                <input 
                                  type="number" 
                                  className="prediction-input"
                                  placeholder="Update..."
                                  value={predictionInput}
                                  onChange={(e) => setPredictionInput(e.target.value)}
                                  min="0"
                                  step="100"
                                />
                                <button 
                                  className="prediction-submit-btn small"
                                  onClick={() => submitPrediction(1)}
                                  disabled={predictionSubmitting}
                                >
                                  {predictionSubmitting ? '...' : 'Update'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="prediction-input-row">
                              <span className="prediction-dollar">$</span>
                              <input 
                                type="number" 
                                className="prediction-input"
                                placeholder="Enter your guess..."
                                value={predictionInput}
                                onChange={(e) => setPredictionInput(e.target.value)}
                                min="0"
                                step="100"
                              />
                              <button 
                                className="prediction-submit-btn"
                                onClick={() => submitPrediction(1)}
                                disabled={predictionSubmitting}
                              >
                                {predictionSubmitting ? '...' : 'Submit'}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Extra slots - from charges or already used this hunt */}
                        {(() => {
                          const slots = [];
                          
                          // First, show all existing extra predictions (slots > 1)
                          const existingExtraSlots = predictionData.myPredictions
                            .filter(p => p.slot > 1)
                            .sort((a, b) => a.slot - b.slot);
                          
                          existingExtraSlots.forEach(existingPred => {
                            const slotNum = existingPred.slot;
                            slots.push(
                              <div key={slotNum} className="prediction-slot extra-slot">
                                <div className="prediction-slot-header">
                                  <span className="slot-label">Prediction #{slotNum}</span>
                                  <span className="slot-badge extra">Extra</span>
                                </div>
                                <div className="prediction-submitted-compact">
                                  <span className="prediction-value">${parseFloat(existingPred.predictedBalance).toLocaleString()}</span>
                                  <div className="prediction-input-row compact">
                                    <span className="prediction-dollar">$</span>
                                    <input 
                                      type="number" 
                                      className="prediction-input"
                                      placeholder="Update..."
                                      value={predictionInputs[slotNum] || ''}
                                      onChange={(e) => setPredictionInputs(prev => ({ ...prev, [slotNum]: e.target.value }))}
                                      min="0"
                                      step="100"
                                    />
                                    <button 
                                      className="prediction-submit-btn small"
                                      onClick={() => submitPrediction(slotNum)}
                                      disabled={predictionSubmitting}
                                    >
                                      {predictionSubmitting ? '...' : 'Update'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                          
                          // Then, if user has available charges, show one empty slot to use
                          if (predictionData.availableCharges > 0) {
                            const nextSlot = existingExtraSlots.length > 0 
                              ? Math.max(...existingExtraSlots.map(p => p.slot)) + 1 
                              : 2;
                            
                            slots.push(
                              <div key={nextSlot} className="prediction-slot extra-slot">
                                <div className="prediction-slot-header">
                                  <span className="slot-label">Prediction #{nextSlot}</span>
                                  <span className="slot-badge extra">Extra ({predictionData.availableCharges} available)</span>
                                </div>
                                <div className="prediction-input-row">
                                  <span className="prediction-dollar">$</span>
                                  <input 
                                    type="number" 
                                    className="prediction-input"
                                    placeholder="Use extra prediction..."
                                    value={predictionInputs[nextSlot] || ''}
                                    onChange={(e) => setPredictionInputs(prev => ({ ...prev, [nextSlot]: e.target.value }))}
                                    min="0"
                                    step="100"
                                  />
                                  <button 
                                    className="prediction-submit-btn"
                                    onClick={() => submitPrediction(nextSlot)}
                                    disabled={predictionSubmitting}
                                  >
                                    {predictionSubmitting ? '...' : 'Use Charge'}
                                  </button>
                                </div>
                              </div>
                            );
                          }
                          
                          return slots;
                        })()}

                        {/* Extra slots promo */}
                        {predictionData.availableCharges === 0 && predictionData.myPredictions.filter(p => p.slot > 1).length === 0 && (
                          <div className="extra-slots-promo">
                            <span className="promo-icon">🎫</span>
                            <span>Want more predictions? Redeem <strong>"Extra Prediction"</strong> with channel points!</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="prediction-login-prompt">
                        <p>Login with Twitch to make your prediction!</p>
                        <button className="twitch-login-btn" onClick={loginWithTwitch}>
                          <svg viewBox="0 0 24 24" fill="currentColor" className="twitch-icon">
                            <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                          </svg>
                          Login with Twitch
                        </button>
                      </div>
                    )}
                    
                    {predictionMessage && (
                      <div className={`prediction-message ${predictionMessage.type}`}>
                        {predictionMessage.text}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="prediction-inactive">
                  <div className="prediction-inactive-icon">⏳</div>
                  <p>No active prediction game right now.</p>
                  <p className="prediction-inactive-hint">Predictions open when a bonus hunt starts!</p>
                </div>
              )}
            </div>


            {/* Current/Previous Hunt Predictions - Always show when there are predictions */}
            {predictionData.allPredictions.length > 0 && (
              <div className="current-predictions-section">
                <h2>
                  📋 {predictionData.activeHunt?.status === 'completed' ? 'Previous' : 'Current'} Hunt Predictions ({predictionData.predictionCount})
                  {predictionData.predictionsOpen && <span className="live-predictions-badge">🔴 LIVE</span>}
                </h2>
                {predictionData.predictionsOpen && (
                  <p className="current-predictions-note live-note">Predictions are coming in live! Watch the list update as people submit their guesses.</p>
                )}
                {!predictionData.predictionsOpen && predictionData.activeHunt?.status !== 'completed' && (
                  <p className="current-predictions-note">Predictions are currently paused. Results will be shown after the hunt ends.</p>
                )}
                {predictionData.activeHunt?.status === 'completed' && (
                  <p className="current-predictions-note results-link">
                    This hunt has ended! <button className="link-btn" onClick={() => navigateTo('predictionresults')}>View Results & Winners →</button>
                  </p>
                )}
                
                {/* User's own predictions highlighted at top */}
                {user && predictionData.myPredictions.length > 0 && (
                  <div className="my-current-prediction">
                    <div className="my-current-prediction-label">Your Prediction{predictionData.myPredictions.length > 1 ? 's' : ''}</div>
                    {predictionData.myPredictions.map((pred, idx) => (
                      <div key={idx} className="my-current-prediction-card">
                        <img src={user.profileImage} alt={user.displayName} className="current-pred-avatar" />
                        <span className="current-pred-name">{user.displayName || user.username}</span>
                        {pred.slot > 1 && <span className="current-pred-slot">#{pred.slot}</span>}
                        <span className="current-pred-guess">${parseFloat(pred.predictedBalance).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Sort Controls */}
                <div className="predictions-sort-controls">
                  <span className="sort-label">Sort by:</span>
                  <button 
                    className={`sort-btn ${predictionsSortOrder === 'newest' ? 'active' : ''}`}
                    onClick={() => { setPredictionsSortOrder('newest'); setCurrentPredictionsPage(1); }}
                  >
                    🕐 Newest
                  </button>
                  <button 
                    className={`sort-btn ${predictionsSortOrder === 'highest' ? 'active' : ''}`}
                    onClick={() => { setPredictionsSortOrder('highest'); setCurrentPredictionsPage(1); }}
                  >
                    📈 Highest
                  </button>
                  <button 
                    className={`sort-btn ${predictionsSortOrder === 'lowest' ? 'active' : ''}`}
                    onClick={() => { setPredictionsSortOrder('lowest'); setCurrentPredictionsPage(1); }}
                  >
                    📉 Lowest
                  </button>
                </div>
                
                <div className="current-predictions-list">
                  {[...predictionData.allPredictions]
                    .sort((a, b) => {
                      if (predictionsSortOrder === 'highest') {
                        return parseFloat(b.predicted_balance) - parseFloat(a.predicted_balance);
                      } else if (predictionsSortOrder === 'lowest') {
                        return parseFloat(a.predicted_balance) - parseFloat(b.predicted_balance);
                      } else {
                        return new Date(b.created_at) - new Date(a.created_at);
                      }
                    })
                    .slice((currentPredictionsPage - 1) * PREDICTIONS_PER_PAGE, currentPredictionsPage * PREDICTIONS_PER_PAGE)
                    .map((pred, idx) => {
                      const isMyPrediction = user && predictionData.myPrediction && 
                        parseFloat(pred.predicted_balance) === parseFloat(predictionData.myPrediction.predicted_balance) &&
                        pred.username?.toLowerCase() === user.username?.toLowerCase();
                      return (
                        <div key={idx} className={`current-pred-item ${isMyPrediction ? 'my-prediction' : ''}`}>
                          <span className="current-pred-rank">#{(currentPredictionsPage - 1) * PREDICTIONS_PER_PAGE + idx + 1}</span>
                          <img src={pred.profile_image_url} alt={pred.display_name} className="current-pred-avatar" />
                          <span className="current-pred-name">{pred.display_name || pred.username} {isMyPrediction && <span className="you-badge">YOU</span>}</span>
                          <span className="current-pred-guess">${parseFloat(pred.predicted_balance).toLocaleString()}</span>
                          <span className="current-pred-time">{new Date(pred.created_at).toLocaleTimeString()}</span>
                        </div>
                      );
                    })}
                </div>
                
                {/* Pagination */}
                {predictionData.allPredictions.length > PREDICTIONS_PER_PAGE && (
                  <div className="predictions-pagination">
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPredictionsPage(p => Math.max(1, p - 1))}
                      disabled={currentPredictionsPage === 1}
                    >
                      ← Prev
                    </button>
                    <span className="pagination-info">
                      Page {currentPredictionsPage} of {Math.ceil(predictionData.allPredictions.length / PREDICTIONS_PER_PAGE)}
                    </span>
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPredictionsPage(p => Math.min(Math.ceil(predictionData.allPredictions.length / PREDICTIONS_PER_PAGE), p + 1))}
                      disabled={currentPredictionsPage >= Math.ceil(predictionData.allPredictions.length / PREDICTIONS_PER_PAGE)}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {/* Prediction Results Page */}
        {activePage === 'predictionresults' && (
          <div className="prediction-results-container">
            <h1 className="simple-page-title">🎯 Predictions</h1>
            {/* Active | Past Results switcher — both tabs live under the one sidebar item */}
            <div style={{display:'flex', justifyContent:'center', gap:'0.5rem', margin:'0.75rem 0 1.25rem'}}>
              <button onClick={() => navigateTo('predictions')} style={{padding:'0.55rem 1.5rem', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, background:'transparent', color:'#71717a', border:'1px solid transparent'}}>
                🎯 Active Prediction
              </button>
              <button style={{padding:'0.55rem 1.5rem', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.85rem', fontWeight:700, background:'rgba(168,85,247,0.3)', color:'#c084fc', border:'1px solid rgba(168,85,247,0.3)'}}>
                🥇 Past Results
              </button>
            </div>
            
            {lastHuntResults.hunt ? (
              <>
                {/* Hunt Info Header */}
                <div className="results-hunt-header">
                  <div className="results-hunt-id">Hunt #{lastHuntResults.hunt.huntId}</div>
                  <div className="results-hunt-date">
                    {lastHuntResults.hunt.completedAt && new Date(lastHuntResults.hunt.completedAt).toLocaleDateString()}
                  </div>
                  {/* Admin: Delete All Winners Button */}
                  {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && lastHuntResults.winners.length > 0 && (
                    <button
                      onClick={async () => {
                        if (window.confirm(`⚠️ DELETE ALL ${lastHuntResults.winners.length} winners for Hunt #${lastHuntResults.hunt.huntId}?\n\nThis will reset the hunt status so it can be closed again. This action cannot be undone!`)) {
                          try {
                            const token = localStorage.getItem('auth_token');
                            const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/all/${lastHuntResults.hunt.huntId}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const data = await response.json();
                            if (response.ok) {
                              alert(`✅ ${data.message}`);
                              fetchLastHuntResults();
                            } else {
                              alert(data.error || 'Failed to delete winners');
                            }
                          } catch (e) {
                            alert('Error deleting winners');
                          }
                        }
                      }}
                      style={{ marginLeft: 'auto', padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.3)', border: '1px solid rgba(239, 68, 68, 0.7)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
                    >
                      ⚠️ Delete All Winners
                    </button>
                  )}
                  {/* Admin: Remove Duplicates Button */}
                  {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && lastHuntResults.winners.length > 3 && (
                    <button
                      onClick={async () => {
                        if (window.confirm(`Remove duplicate winners for Hunt #${lastHuntResults.hunt.huntId}? This will keep only the first entry for each user.`)) {
                          try {
                            const token = localStorage.getItem('auth_token');
                            const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/duplicates/${lastHuntResults.hunt.huntId}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const data = await response.json();
                            if (response.ok) {
                              alert(`✅ Removed ${data.deletedCount} duplicate winners!`);
                              fetchLastHuntResults();
                            } else {
                              alert(data.error || 'Failed to remove duplicates');
                            }
                          } catch (e) {
                            alert('Error removing duplicates');
                          }
                        }
                      }}
                      style={{ marginLeft: '0.5rem', padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem' }}
                    >
                      🗑️ Remove Duplicates
                    </button>
                  )}
                </div>

                {/* Summary Stats */}
                <div className="last-hunt-summary enhanced">
                  <div className="last-hunt-stat-card">
                    <span className="last-hunt-stat-icon">💰</span>
                    <div>
                      <span className="last-hunt-stat-label">Start</span>
                      <span className="last-hunt-stat-value">${lastHuntResults.hunt.startBalance?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="last-hunt-stat-card result">
                    <span className="last-hunt-stat-icon">🎯</span>
                    <div>
                      <span className="last-hunt-stat-label">Final Result</span>
                      <span className="last-hunt-stat-value highlight">${lastHuntResults.hunt.endBalance?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="last-hunt-stat-card">
                    <span className="last-hunt-stat-icon">👥</span>
                    <div>
                      <span className="last-hunt-stat-label">Predictions</span>
                      <span className="last-hunt-stat-value">{lastHuntResults.predictions.length}</span>
                    </div>
                  </div>
                  <div className="last-hunt-stat-card">
                    <span className="last-hunt-stat-icon">📊</span>
                    <div>
                      <span className="last-hunt-stat-label">Avg Prediction</span>
                      <span className="last-hunt-stat-value">
                        ${lastHuntResults.predictions.length > 0 
                          ? Math.round(lastHuntResults.predictions.reduce((sum, p) => sum + parseFloat(p.predicted_balance), 0) / lastHuntResults.predictions.length).toLocaleString()
                          : '0'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Winners Podium */}
                {lastHuntResults.winners.length > 0 && (
                  <div className="prediction-podium">
                    {/* 2nd Place */}
                    {lastHuntResults.winners.find(w => w.rank === 2) && (
                      <div className="prediction-podium-card second">
                        <div className="prediction-podium-rank">🥈</div>
                        <img src={lastHuntResults.winners.find(w => w.rank === 2).profile_image_url} alt="" className="prediction-podium-avatar" />
                        <div className="prediction-podium-name">{lastHuntResults.winners.find(w => w.rank === 2).display_name || lastHuntResults.winners.find(w => w.rank === 2).username}</div>
                        <div className="prediction-podium-guess">${parseFloat(lastHuntResults.winners.find(w => w.rank === 2).predicted_balance).toLocaleString()}</div>
                        <div className="prediction-podium-diff">${parseFloat(lastHuntResults.winners.find(w => w.rank === 2).difference).toLocaleString()} off</div>
                        {lastHuntResults.hunt.rewardSecond && (
                          <div className="prediction-podium-reward">{lastHuntResults.hunt.rewardSecond}</div>
                        )}
                      </div>
                    )}
                    
                    {/* 1st Place */}
                    {lastHuntResults.winners.find(w => w.rank === 1) && (
                      <div className="prediction-podium-card first">
                        <div className="prediction-podium-crown">👑</div>
                        <div className="prediction-podium-rank">🥇</div>
                        <img src={lastHuntResults.winners.find(w => w.rank === 1).profile_image_url} alt="" className="prediction-podium-avatar" />
                        <div className="prediction-podium-name">{lastHuntResults.winners.find(w => w.rank === 1).display_name || lastHuntResults.winners.find(w => w.rank === 1).username}</div>
                        <div className="prediction-podium-guess">${parseFloat(lastHuntResults.winners.find(w => w.rank === 1).predicted_balance).toLocaleString()}</div>
                        <div className="prediction-podium-diff">${parseFloat(lastHuntResults.winners.find(w => w.rank === 1).difference).toLocaleString()} off</div>
                        {lastHuntResults.hunt.rewardFirst && (
                          <div className="prediction-podium-reward">{lastHuntResults.hunt.rewardFirst}</div>
                        )}
                      </div>
                    )}
                    
                    {/* 3rd Place */}
                    {lastHuntResults.winners.find(w => w.rank === 3) && (
                      <div className="prediction-podium-card third">
                        <div className="prediction-podium-rank">🥉</div>
                        <img src={lastHuntResults.winners.find(w => w.rank === 3).profile_image_url} alt="" className="prediction-podium-avatar" />
                        <div className="prediction-podium-name">{lastHuntResults.winners.find(w => w.rank === 3).display_name || lastHuntResults.winners.find(w => w.rank === 3).username}</div>
                        <div className="prediction-podium-guess">${parseFloat(lastHuntResults.winners.find(w => w.rank === 3).predicted_balance).toLocaleString()}</div>
                        <div className="prediction-podium-diff">${parseFloat(lastHuntResults.winners.find(w => w.rank === 3).difference).toLocaleString()} off</div>
                        {lastHuntResults.hunt.rewardThird && (
                          <div className="prediction-podium-reward">{lastHuntResults.hunt.rewardThird}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Full Leaderboard (4th onwards) */}
                {lastHuntResults.predictions.length > 3 && (
                  <div className="predictions-leaderboard full">
                    <h3>Full Leaderboard</h3>
                    <div className="predictions-leaderboard-list">
                      {(() => {
                        // Get winner usernames to exclude from the list
                        const winnerUsernames = lastHuntResults.winners.map(w => (w.username || '').toLowerCase());
                        // Filter out winners and take positions 4-10
                        const nonWinners = lastHuntResults.predictions
                          .filter(pred => !winnerUsernames.includes((pred.username || '').toLowerCase()))
                          .slice(0, 7); // Take 7 to show positions 4-10
                        
                        return nonWinners.map((pred, idx) => (
                          <div key={idx} className="predictions-lb-item">
                            <span className="predictions-lb-rank">#{idx + 4}</span>
                            <img src={pred.profile_image_url} alt={pred.display_name} className="predictions-lb-avatar" />
                            <span className="predictions-lb-name">{pred.display_name || pred.username}</span>
                            <span className="predictions-lb-guess">${parseFloat(pred.predicted_balance).toLocaleString()}</span>
                            <span className="predictions-lb-diff">${parseFloat(pred.difference).toLocaleString()} off</span>
                          </div>
                        ));
                      })()}
                    </div>
                    {lastHuntResults.predictions.length > 10 && (
                      <p className="predictions-more-note">+ {lastHuntResults.predictions.length - 10} more predictions</p>
                    )}
                  </div>
                )}

                {/* Previous Winners Section */}
                {previousWinners.length > 1 && (
                  <div className="previous-winners-section">
                    <h2 className="previous-winners-title">📜 Previous Hunt Winners</h2>
                    <div className="previous-winners-list">
                      {previousWinners
                        .slice(1) // Skip the most recent (already shown above)
                        .slice((previousWinnersPage - 1) * previousWinnersPerPage, previousWinnersPage * previousWinnersPerPage)
                        .map((hunt, idx) => (
                          <div key={idx} className="previous-hunt-card">
                            <div className="previous-hunt-header">
                              <span className="previous-hunt-id">Hunt #{hunt.huntId}</span>
                              <span className="previous-hunt-date">
                                {new Date(hunt.completedAt).toLocaleDateString()}
                              </span>
                              {/* Admin: Remove Duplicates Button - show if more than 3 winners */}
                              {user && ['notpa1n', 'snutzy', 'jaelawtv'].includes(user.username?.toLowerCase()) && hunt.winners.length > 3 && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Remove duplicate winners for Hunt #${hunt.huntId}?`)) {
                                      try {
                                        const token = localStorage.getItem('auth_token');
                                        const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/duplicates/${hunt.huntId}`, {
                                          method: 'DELETE',
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        const data = await response.json();
                                        if (response.ok) {
                                          alert(`✅ Removed ${data.deletedCount} duplicates!`);
                                          fetchPreviousWinners();
                                        } else {
                                          alert(data.error || 'Failed');
                                        }
                                      } catch (err) {
                                        alert('Error');
                                      }
                                    }
                                  }}
                                  style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontSize: '0.7rem' }}
                                >
                                  🗑️ Fix Duplicates
                                </button>
                              )}
                            </div>
                            <div className="previous-hunt-balance">
                              <span className="previous-hunt-start">${parseFloat(hunt.startBalance).toLocaleString()}</span>
                              <span className="previous-hunt-arrow">→</span>
                              <span className="previous-hunt-end">${parseFloat(hunt.endBalance).toLocaleString()}</span>
                            </div>
                            <div className="previous-hunt-winners">
                              {hunt.winners.map((winner, wIdx) => {
                                const reward = winner.rank === 1 ? hunt.rewardFirst 
                                  : winner.rank === 2 ? hunt.rewardSecond 
                                  : hunt.rewardThird;
                                return (
                                  <div key={wIdx} className={`previous-winner-item rank-${winner.rank}`}>
                                    <span className="previous-winner-medal">
                                      {winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : '🥉'}
                                    </span>
                                    {winner.profileImageUrl ? (
                                      <img src={winner.profileImageUrl} alt={winner.displayName} className="previous-winner-avatar" />
                                    ) : (
                                      <div className="previous-winner-avatar-placeholder">
                                        {(winner.displayName || winner.username || '?').substring(0, 2).toUpperCase()}
                                      </div>
                                    )}
                                    <div className="previous-winner-info">
                                      <span className="previous-winner-name">{winner.displayName || winner.username}</span>
                                      {reward && <span className="previous-winner-reward">{reward}</span>}
                                    </div>
                                    <span className="previous-winner-prediction">${parseFloat(winner.predictedBalance).toLocaleString()}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                    </div>
                    
                    {/* Pagination */}
                    {previousWinners.length - 1 > previousWinnersPerPage && (
                      <div className="pagination-controls">
                        <button 
                          className="pagination-btn"
                          onClick={() => setPreviousWinnersPage(p => Math.max(1, p - 1))}
                          disabled={previousWinnersPage === 1}
                        >
                          ← Previous
                        </button>
                        <span className="pagination-info">
                          Page {previousWinnersPage} of {Math.ceil((previousWinners.length - 1) / previousWinnersPerPage)}
                        </span>
                        <button 
                          className="pagination-btn"
                          onClick={() => setPreviousWinnersPage(p => Math.min(Math.ceil((previousWinners.length - 1) / previousWinnersPerPage), p + 1))}
                          disabled={previousWinnersPage >= Math.ceil((previousWinners.length - 1) / previousWinnersPerPage)}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="no-results-message">
                <div className="no-results-icon">🏆</div>
                <h3>No results yet</h3>
                <p>Results will appear here after a prediction hunt is completed.</p>
                <button className="nav-btn" onClick={() => navigateTo('predictions')}>
                  Go to Predictions →
                </button>
              </div>
            )}
          </div>
        )}

        {/* My Predictions Page */}
        {activePage === 'mypredictions' && (
          <div className="my-predictions-container">
            <h1 className="simple-page-title">📊 My Predictions</h1>
            
            {!user ? (
              <div className="login-required-message">
                <p>Please login with Twitch to view your prediction history.</p>
                <button className="twitch-login-btn" onClick={loginWithTwitch}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="twitch-icon">
                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                  </svg>
                  Login with Twitch
                </button>
              </div>
            ) : myPredictionHistory.length === 0 ? (
              <div className="no-predictions-message">
                <div className="no-predictions-icon">🎯</div>
                <p>You haven't made any predictions yet!</p>
                <button className="nav-btn" onClick={() => navigateTo('predictions')}>
                  Make Your First Prediction →
                </button>
              </div>
            ) : (
              <div className="my-predictions-list">
                {myPredictionHistory.map((pred, idx) => (
                  <div key={idx} className={`my-prediction-card ${pred.hunt_status === 'completed' ? 'completed' : 'pending'}`}>
                    <div className="my-pred-header">
                      <span className="my-pred-hunt">Hunt #{pred.hunt_id}</span>
                      <span className={`my-pred-status ${pred.hunt_status}`}>
                        {pred.hunt_status === 'completed' ? '✅ Completed' : pred.hunt_status === 'active' ? '🟢 Active' : '⏳ Pending'}
                      </span>
                    </div>
                    
                    <div className="my-pred-details">
                      <div className="my-pred-stat">
                        <span className="my-pred-label">Your Guess</span>
                        <span className="my-pred-value guess">${parseFloat(pred.predicted_balance).toLocaleString()}</span>
                      </div>
                      
                      {pred.hunt_status === 'completed' && pred.end_balance && (
                        <>
                          <div className="my-pred-stat">
                            <span className="my-pred-label">Actual Result</span>
                            <span className="my-pred-value">${parseFloat(pred.end_balance).toLocaleString()}</span>
                          </div>
                          <div className="my-pred-stat">
                            <span className="my-pred-label">Difference</span>
                            <span className={`my-pred-value diff ${pred.difference < 1000 ? 'close' : ''}`}>
                              ${parseFloat(pred.difference || Math.abs(pred.predicted_balance - pred.end_balance)).toLocaleString()}
                            </span>
                          </div>
                          <div className="my-pred-stat">
                            <span className="my-pred-label">Your Rank</span>
                            <span className={`my-pred-value rank ${pred.my_rank ? `rank-${pred.my_rank}` : ''}`}>
                              {pred.my_rank ? `#${pred.my_rank}` : `#${pred.calculated_rank || '?'}`} / {pred.total_predictions}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {pred.my_reward && (
                      <div className="my-pred-reward">
                        🏆 You won: {pred.my_reward}
                      </div>
                    )}
                    
                    <div className="my-pred-date">
                      {new Date(pred.created_at).toLocaleDateString()} at {new Date(pred.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* My Profile Page */}
        {activePage === 'profile' && (
          <div className="profile-container">
            <h1 className="simple-page-title">👤 My Profile</h1>
            
            {!user ? (
              <div className="login-required-message">
                <p>Please login with Twitch to view your profile.</p>
                <button className="twitch-login-btn" onClick={loginWithTwitch}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="twitch-icon">
                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                  </svg>
                  Login with Twitch
                </button>
              </div>
            ) : (
              <>
                {/* Profile Header */}
                <div className="profile-header">
                  <img src={user.profileImage} alt={user.displayName} className="profile-avatar" />
                  <div className="profile-info">
                    <h2 className="profile-name">{user.displayName}</h2>
                    <p className="profile-username">@{user.username}</p>
                    {user.shuffleUsername ? (
                      <div className="profile-shuffle">
                        <span className="shuffle-linked">🎰 {user.shuffleUsername}</span>
                        <button className="edit-shuffle-btn" onClick={() => setShowShuffleModal(true)}>Edit</button>
                      </div>
                    ) : (
                      <button className="link-shuffle-btn" onClick={() => setShowShuffleModal(true)}>
                        🔗 Link Shuffle Account
                      </button>
                    )}
                    {profileStats?.account?.createdAt && (
                      <p className="profile-member-since">Member since {new Date(profileStats.account.createdAt).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>

                {/* Account transfer — only visible while an admin has granted it */}
                {transferStatus?.granted && (
                  <div style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: '16px', padding: '1.1rem 1.3rem', margin: '1.2rem 0' }}>
                    <div style={{ fontWeight: 800, color: '#fbbf24', marginBottom: '0.4rem' }}>🔁 Account transfer available</div>
                    <div style={{ fontSize: '0.85rem', color: '#a1a1aa', marginBottom: '0.85rem', lineHeight: 1.55 }}>
                      An admin has granted you a one-time transfer of this profile — points, predictions, redemptions, giveaway history and linked accounts — to a different Twitch account. You'll sign into the new account on Twitch to prove you own it.
                      {transferStatus.expiresAt && <> This offer expires {new Date(transferStatus.expiresAt).toLocaleDateString()}.</>}
                    </div>
                    <button onClick={startAccountTransfer} style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #fbbf24, #d97706)', border: 'none', borderRadius: '8px', color: '#1a1206', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer' }}>
                      Transfer to a different Twitch account
                    </button>
                  </div>
                )}

                {/* Connected accounts — visible hub for platform links */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '1.1rem 1.3rem', margin: '1.2rem 0' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '2px', color: '#8b8b96', marginBottom: '0.8rem' }}>🔗 CONNECTED ACCOUNTS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.6rem' }}>
                    {/* Twitch — the login itself */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.7rem 0.9rem', background: 'rgba(145,70,255,0.08)', border: '1px solid rgba(145,70,255,0.3)', borderRadius: '12px' }}>
                      <svg viewBox="0 0 24 24" fill="#a970ff" width="20" height="20" style={{ flexShrink: 0 }}><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#fff' }}>Twitch</div>
                        <div style={{ fontSize: '0.68rem', color: '#c4b5fd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{user.username}</div>
                      </div>
                      <span style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '1px', color: '#a970ff', flexShrink: 0 }}>LOGIN ✓</span>
                    </div>
                    {/* Discord */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.7rem 0.9rem', background: discordLinked ? 'rgba(88,101,242,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${discordLinked ? 'rgba(88,101,242,0.35)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '12px' }}>
                      <svg viewBox="0 0 24 24" fill={discordLinked ? '#7289da' : '#71717a'} width="20" height="20" style={{ flexShrink: 0 }}><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#fff' }}>Discord</div>
                        <div style={{ fontSize: '0.68rem', color: discordLinked ? '#93a3f5' : '#71717a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {discordLinked ? (discordInfo?.discord_username || 'Connected') : 'Not connected'}
                        </div>
                      </div>
                      {discordLinked ? (
                        <button onClick={unlinkDiscord} style={{ flexShrink: 0, padding: '0.3rem 0.6rem', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#a1a1aa', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>Unlink</button>
                      ) : (
                        <button onClick={connectDiscord} style={{ flexShrink: 0, padding: '0.32rem 0.7rem', borderRadius: '7px', border: 'none', background: '#5865f2', color: '#fff', fontSize: '0.66rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>Connect</button>
                      )}
                    </div>
                    {/* Kick */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.7rem 0.9rem', background: kickLinked ? 'rgba(83,252,24,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${kickLinked ? 'rgba(83,252,24,0.3)' : 'rgba(255,255,255,0.1)'}`, borderRadius: '12px' }}>
                      <span style={{ flexShrink: 0, fontWeight: 900, fontSize: '0.72rem', color: kickLinked ? '#53fc18' : '#71717a', border: `2px solid ${kickLinked ? '#53fc18' : '#71717a'}`, borderRadius: '5px', padding: '0.08rem 0.3rem' }}>K</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#fff' }}>Kick</div>
                        <div style={{ fontSize: '0.68rem', color: kickLinked ? '#86efac' : '#71717a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {kickLinked ? (kickInfo?.kick_username || 'Connected') : 'Not connected'}
                        </div>
                      </div>
                      {kickLinked ? (
                        <button onClick={unlinkKick} style={{ flexShrink: 0, padding: '0.3rem 0.6rem', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#a1a1aa', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>Unlink</button>
                      ) : (
                        <button onClick={connectKick} style={{ flexShrink: 0, padding: '0.32rem 0.7rem', borderRadius: '7px', border: 'none', background: '#53fc18', color: '#0a0a0a', fontSize: '0.66rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}>Connect</button>
                      )}
                    </div>
                  </div>
                </div>

                {profileLoading && !profileStats ? (
                  /* Pulsing placeholders shaped like the stat tiles — no "$0"/"N/A" flash */
                  <div className="profile-stats-grid">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className="profile-stat-card">
                        <Skel w="36px" h="36px" r="50%" style={{ margin: '0 auto' }} />
                        <Skel w="70px" h="1.4rem" style={{ margin: '0.6rem auto 0.45rem' }} />
                        <Skel w="100px" h="0.7rem" style={{ margin: '0 auto' }} />
                      </div>
                    ))}
                  </div>
                ) : profileStats ? (
                  <>
                    {/* Quick Stats */}
                    <div className="profile-stats-grid">
                      <div className="profile-stat-card">
                        <div className="profile-stat-icon">🎯</div>
                        <div className="profile-stat-value">{profileStats.predictions.total}</div>
                        <div className="profile-stat-label">Total Predictions</div>
                      </div>
                      <div className="profile-stat-card highlight-gold">
                        <div className="profile-stat-icon">🏆</div>
                        <div className="profile-stat-value">{profileStats.wins.total}</div>
                        <div className="profile-stat-label">Total Wins</div>
                      </div>
                      <div className="profile-stat-card highlight-money">
                        <div className="profile-stat-icon">💰</div>
                        <div className="profile-stat-value">${profileStats.moneyWon?.total?.toLocaleString() || 0}</div>
                        <div className="profile-stat-label">Money Won</div>
                      </div>
                      <div className="profile-stat-card">
                        <div className="profile-stat-icon">🎮</div>
                        <div className="profile-stat-value">
                          {profileStats.bestRank ? (
                            profileStats.bestRank === 1 ? '🥇 1st' :
                            profileStats.bestRank === 2 ? '🥈 2nd' :
                            profileStats.bestRank === 3 ? '🥉 3rd' :
                            `#${profileStats.bestRank}`
                          ) : '-'}
                        </div>
                        <div className="profile-stat-label">Best Rank</div>
                      </div>
                    </div>

                    {/* Wager Stats Section */}
                    {profileStats.wagerStats && profileStats.wagerStats.totalWagered > 0 && (
                      <div className="profile-section">
                        <h3>🎰 Shuffle Wager Stats</h3>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:'0.75rem'}}>
                          <div style={{padding:'1rem', background:'rgba(168,85,247,0.08)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'0.75rem', textAlign:'center'}}>
                            <div style={{fontSize:'1.4rem', fontWeight:800, color:'#a855f7'}}>${profileStats.wagerStats.totalWagered >= 1000000 ? (profileStats.wagerStats.totalWagered / 1000000).toFixed(2) + 'M' : profileStats.wagerStats.totalWagered >= 1000 ? (profileStats.wagerStats.totalWagered / 1000).toFixed(1) + 'K' : profileStats.wagerStats.totalWagered.toFixed(0)}</div>
                            <div style={{fontSize:'0.75rem', color:'#9ca3af', marginTop:'0.2rem'}}>Total Wagered</div>
                          </div>
                          {profileStats.wagerStats.leaderboardRank && (
                            <div style={{padding:'1rem', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'0.75rem', textAlign:'center'}}>
                              <div style={{fontSize:'1.4rem', fontWeight:800, color:'#f59e0b'}}>#{profileStats.wagerStats.leaderboardRank} <span style={{fontSize:'0.7rem', color:'#6b7280', fontWeight:400}}>/ {profileStats.wagerStats.totalLeaderboardParticipants || '?'}</span></div>
                              <div style={{fontSize:'0.75rem', color:'#9ca3af', marginTop:'0.2rem'}}>Monthly Rank</div>
                              {profileStats.wagerStats.periodWagered > 0 && <div style={{fontSize:'0.65rem', color:'#71717a', marginTop:'0.1rem'}}>${profileStats.wagerStats.periodWagered >= 1000 ? (profileStats.wagerStats.periodWagered / 1000).toFixed(1) + 'K' : profileStats.wagerStats.periodWagered.toFixed(0)} wagered</div>}
                            </div>
                          )}
                          {profileStats.wagerStats.weeklyRank && (
                            <div style={{padding:'1rem', background:'rgba(139,92,246,0.08)', border:'1px solid rgba(139,92,246,0.15)', borderRadius:'0.75rem', textAlign:'center'}}>
                              <div style={{fontSize:'1.4rem', fontWeight:800, color:'#a78bfa'}}>#{profileStats.wagerStats.weeklyRank} <span style={{fontSize:'0.7rem', color:'#6b7280', fontWeight:400}}>/ {profileStats.wagerStats.weeklyParticipants || '?'}</span></div>
                              <div style={{fontSize:'0.75rem', color:'#9ca3af', marginTop:'0.2rem'}}>Weekly Rank</div>
                              {profileStats.wagerStats.weeklyWagered > 0 && <div style={{fontSize:'0.65rem', color:'#71717a', marginTop:'0.1rem'}}>${profileStats.wagerStats.weeklyWagered >= 1000 ? (profileStats.wagerStats.weeklyWagered / 1000).toFixed(1) + 'K' : profileStats.wagerStats.weeklyWagered.toFixed(0)} wagered</div>}
                            </div>
                          )}
                          {profileStats.wagerStats.periodWagered > 0 && (
                            <div style={{padding:'1rem', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'0.75rem', textAlign:'center'}}>
                              <div style={{fontSize:'1.4rem', fontWeight:800, color:'#22c55e'}}>${profileStats.wagerStats.periodWagered >= 1000000 ? (profileStats.wagerStats.periodWagered / 1000000).toFixed(2) + 'M' : profileStats.wagerStats.periodWagered >= 1000 ? (profileStats.wagerStats.periodWagered / 1000).toFixed(1) + 'K' : profileStats.wagerStats.periodWagered.toFixed(0)}</div>
                              <div style={{fontSize:'0.75rem', color:'#9ca3af', marginTop:'0.2rem'}}>{profileStats.wagerStats.isWeighted ? 'Weighted Wager' : 'This Period'}</div>
                            </div>
                          )}
                          {profileStats.wagerStats.isWeighted && profileStats.wagerStats.periodWageredRaw > 0 && profileStats.wagerStats.periodWageredRaw !== profileStats.wagerStats.periodWagered && (
                            <div style={{padding:'1rem', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'0.75rem', textAlign:'center'}}>
                              <div style={{fontSize:'1.4rem', fontWeight:800, color:'#a1a1aa'}}>${profileStats.wagerStats.periodWageredRaw >= 1000000 ? (profileStats.wagerStats.periodWageredRaw / 1000000).toFixed(2) + 'M' : profileStats.wagerStats.periodWageredRaw >= 1000 ? (profileStats.wagerStats.periodWageredRaw / 1000).toFixed(1) + 'K' : profileStats.wagerStats.periodWageredRaw.toFixed(0)}</div>
                              <div style={{fontSize:'0.75rem', color:'#71717a', marginTop:'0.2rem'}}>Raw Wager</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Money Won Breakdown (if they've won money) */}
                    {profileStats.moneyWon?.total > 0 && (
                      <div className="profile-section">
                        <h3>💰 Money Won Breakdown</h3>
                        <div className="money-breakdown">
                          {profileStats.moneyWon.fromPredictions > 0 && (
                            <div className="money-breakdown-item predictions">
                              <span className="money-icon">🎯</span>
                              <span className="money-amount">${profileStats.moneyWon.fromPredictions.toLocaleString()}</span>
                              <span className="money-label">From Predictions</span>
                            </div>
                          )}
                          {profileStats.moneyWon.fromGiveaways > 0 && (
                            <div className="money-breakdown-item giveaways">
                              <span className="money-icon">🎁</span>
                              <span className="money-amount">${profileStats.moneyWon.fromGiveaways.toLocaleString()}</span>
                              <span className="money-label">From Giveaways</span>
                            </div>
                          )}
                          {profileStats.moneyWon.fromSlotPicks > 0 && (
                            <div className="money-breakdown-item slotpicks">
                              <span className="money-icon">🎰</span>
                              <span className="money-amount">${profileStats.moneyWon.fromSlotPicks.toLocaleString()}</span>
                              <span className="money-label">From Slot Picks</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pending Money (awaiting payout) */}
                    {profileStats.pendingMoney?.total > 0 && (
                      <div className="profile-section">
                        <h3>⏳ Pending Payouts</h3>
                        <div className="money-breakdown pending">
                          {profileStats.pendingMoney.fromPredictions > 0 && (
                            <div className="money-breakdown-item pending-predictions">
                              <span className="money-icon">🎯</span>
                              <span className="money-amount pending">${profileStats.pendingMoney.fromPredictions.toLocaleString()}</span>
                              <span className="money-label">Predictions</span>
                            </div>
                          )}
                          {profileStats.pendingMoney.fromGiveaways > 0 && (
                            <div className="money-breakdown-item pending-giveaways">
                              <span className="money-icon">🎁</span>
                              <span className="money-amount pending">${profileStats.pendingMoney.fromGiveaways.toLocaleString()}</span>
                              <span className="money-label">Giveaways</span>
                            </div>
                          )}
                          {profileStats.pendingMoney.fromSlotPicks > 0 && (
                            <div className="money-breakdown-item pending-slotpicks">
                              <span className="money-icon">🎰</span>
                              <span className="money-amount pending">${profileStats.pendingMoney.fromSlotPicks.toLocaleString()}</span>
                              <span className="money-label">Slot Picks</span>
                            </div>
                          )}
                        </div>
                        <p className="pending-note">Payouts can take up to 14 days to process.</p>
                      </div>
                    )}

                    {/* Win Breakdown */}
                    {profileStats.wins.total > 0 && (
                      <div className="profile-section">
                        <h3>🏅 Win Breakdown</h3>
                        <div className="win-breakdown">
                          <div className="win-breakdown-item gold">
                            <span className="win-medal">🥇</span>
                            <span className="win-count">{profileStats.wins.firstPlace}</span>
                            <span className="win-label">1st Place</span>
                          </div>
                          <div className="win-breakdown-item silver">
                            <span className="win-medal">🥈</span>
                            <span className="win-count">{profileStats.wins.secondPlace}</span>
                            <span className="win-label">2nd Place</span>
                          </div>
                          <div className="win-breakdown-item bronze">
                            <span className="win-medal">🥉</span>
                            <span className="win-count">{profileStats.wins.thirdPlace}</span>
                            <span className="win-label">3rd Place</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Best Prediction */}
                    {profileStats.bestPrediction && (
                      <div className="profile-section">
                        <h3>🎯 Best Prediction</h3>
                        <div className="best-prediction-card">
                          <div className="best-pred-header">
                            <span className="best-pred-hunt">Hunt #{profileStats.bestPrediction.hunt_id}</span>
                            {profileStats.bestPrediction.rank && (
                              <span className={`best-pred-rank rank-${profileStats.bestPrediction.rank}`}>
                                {profileStats.bestPrediction.rank === 1 ? '🥇' : profileStats.bestPrediction.rank === 2 ? '🥈' : '🥉'} 
                                #{profileStats.bestPrediction.rank}
                              </span>
                            )}
                          </div>
                          <div className="best-pred-stats">
                            <div className="best-pred-stat">
                              <span className="label">Your Guess</span>
                              <span className="value guess">${parseFloat(profileStats.bestPrediction.predicted_balance).toLocaleString()}</span>
                            </div>
                            <div className="best-pred-stat">
                              <span className="label">Actual Result</span>
                              <span className="value actual">${parseFloat(profileStats.bestPrediction.end_balance).toLocaleString()}</span>
                            </div>
                            <div className="best-pred-stat">
                              <span className="label">Difference</span>
                              <span className="value diff">${parseFloat(profileStats.bestPrediction.difference).toLocaleString()}</span>
                            </div>
                          </div>
                          {profileStats.bestPrediction.reward && (
                            <div className="best-pred-reward">Won: {profileStats.bestPrediction.reward}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* No Stats Yet */}
                    {profileStats.predictions.total === 0 && (
                      <div className="profile-empty-state">
                        <div className="empty-icon">🎯</div>
                        <h3>No predictions yet!</h3>
                        <p>Make your first prediction to start tracking your stats.</p>
                        <button className="nav-btn" onClick={() => navigateTo('predictions')}>
                          Make a Prediction →
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="load-error-box" style={{ margin: '1rem 0' }}>
                    <p>Having trouble loading your stats — the server may be under load.</p>
                    <button className="retry-btn" onClick={fetchProfileStats}>↻ Retry</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Admin Panel */}
        {/* ========== POINTS SHOP PAGE ========== */}
        {activePage === 'pointsshop' && (
          <div className="points-shop-container">
            <h1 className="simple-page-title">Points Shop</h1>
            {publicPayoutStats?.pointsShop > 0 && (
              <div style={{display:'flex', justifyContent:'center', margin:'0.5rem 0 1.5rem'}}>
                <OdometerCounter value={publicPayoutStats.pointsShop} label="Total Redemptions Paid" size="small" />
              </div>
            )}
            <p className="points-shop-intro">
              Earn points by wagering, chatting, and watching — then redeem them for exclusive rewards!
            </p>

            {/* Points Balance Card (logged in users) */}
            {user && pointsBalance !== null && (
              <div className="points-balance-card">
                <div className="points-balance-main">
                  <div className="points-balance-icon">💎</div>
                  <div className="points-balance-info">
                    <div className="points-balance-number">{pointsBalance.toLocaleString()}</div>
                    <div className="points-balance-label">Available Points</div>
                  </div>
                </div>
                {/* Discord Link Status */}
                <div className="points-discord-status">
                  {discordLinked ? (
                    <div className="discord-linked-row">
                      <span className="discord-linked-badge">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
                        {discordInfo?.discord_username || 'Connected'}
                      </span>
                      <button className="discord-unlink-btn" onClick={unlinkDiscord}>Unlink</button>
                    </div>
                  ) : discordLinked === false ? (
                    <button className="discord-connect-btn" onClick={connectDiscord}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
                      Connect Discord to earn points from chat activity
                    </button>
                  ) : null}
                </div>
                {/* Kick Link Status */}
                <div className="points-discord-status" style={{ marginTop: '0.4rem' }}>
                  {kickLinked ? (
                    <div className="discord-linked-row">
                      <span className="discord-linked-badge" style={{ background: 'rgba(83,252,24,0.1)', borderColor: 'rgba(83,252,24,0.35)', color: '#53fc18' }}>
                        <span style={{ fontWeight: 900, fontSize: '0.75rem' }}>KICK</span>
                        {kickInfo?.kick_username || 'Connected'}
                      </span>
                      <button className="discord-unlink-btn" onClick={unlinkKick}>Unlink</button>
                    </div>
                  ) : kickLinked === false ? (
                    <button className="discord-connect-btn" onClick={connectKick} style={{ background: 'rgba(83,252,18,0.08)', borderColor: 'rgba(83,252,24,0.35)', color: '#53fc18' }}>
                      <span style={{ fontWeight: 900, fontSize: '0.8rem' }}>KICK</span>
                      Connect Kick — count your Kick chat entries in giveaways
                    </button>
                  ) : null}
                </div>
                {pointsBreakdown.length > 0 && (
                  <div className="points-breakdown-row">
                    {pointsBreakdown.map((b, i) => (
                      <div key={i} className="points-breakdown-item">
                        <span className="breakdown-source">
                          {b.source === 'admin_grant' ? '🎁 Bonus' :
                           b.source === 'wager' ? '🎰 Wagering' :
                           b.source === 'twitch_chat' ? '💬 Chat' :
                           b.source === 'kick_chat' ? '🟢 Kick Chat' :
                           b.source === 'twitch_watch' ? '📺 Watch Time' :
                           b.source === 'discord' ? '🎮 Discord' :
                           b.source === 'shop_redemption' ? '🛒 Spent' :
                           b.source === 'refund' ? '↩️ Refund' :
                           b.source}
                        </span>
                        <span className="breakdown-earned">+{parseInt(b.earned).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Not logged in prompt */}
            {!user && (
              <div className="points-login-prompt">
                <div className="points-login-icon">🔒</div>
                <p>Login with Twitch to view your points balance and redeem rewards!</p>
                <button className="twitch-login-btn" onClick={loginWithTwitch}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="twitch-icon">
                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                  </svg>
                  Login with Twitch
                </button>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="points-tabs">
              <button className={`points-tab ${pointsShopTab === 'shop' ? 'active' : ''}`} onClick={() => setPointsShopTab('shop')}>
                🛒 Shop
              </button>
              {user && (
                <button className={`points-tab ${pointsShopTab === 'history' ? 'active' : ''}`} onClick={() => { setPointsShopTab('history'); fetchPointsHistory(1); fetchMyRedemptions(); }}>
                  📜 My History
                </button>
              )}
              <button className={`points-tab ${pointsShopTab === 'leaderboard' ? 'active' : ''}`} onClick={() => { setPointsShopTab('leaderboard'); fetchPointsLeaderboard(); }}>
                🏆 Leaderboard
              </button>
            </div>

            {/* SHOP TAB */}
            {pointsShopTab === 'shop' && (
              <div>
                {pointsShopItems.length > 0 && (
                  <div className="shop-controls">
                    <input
                      type="text"
                      placeholder="Search rewards..."
                      value={shopSearch}
                      onChange={(e) => setShopSearch(e.target.value)}
                      className="shop-search-input"
                    />
                    <select value={shopSort} onChange={(e) => setShopSort(e.target.value)} className="shop-sort-select">
                      <option value="default">Default</option>
                      <option value="price_asc">Price: Low → High</option>
                      <option value="price_desc">Price: High → Low</option>
                    </select>
                  </div>
                )}
                <div className="points-shop-grid">
                {pointsShopLoading ? (
                  <div className="points-loading">Loading shop items...</div>
                ) : pointsShopItems.length === 0 ? (
                  <div className="points-empty">
                    <div className="points-empty-icon">🏪</div>
                    <p>Shop items coming soon! Stay tuned.</p>
                  </div>
                ) : (
                  [...pointsShopItems]
                    .filter(item => !shopSearch || item.name.toLowerCase().includes(shopSearch.toLowerCase()) || (item.description || '').toLowerCase().includes(shopSearch.toLowerCase()))
                    .sort((a, b) => shopSort === 'price_asc' ? a.cost - b.cost : shopSort === 'price_desc' ? b.cost - a.cost : 0)
                    .length === 0 && shopSearch ? (
                    <div className="points-empty" style={{gridColumn:'1/-1'}}>
                      <p style={{color:'#6b7280'}}>No rewards match "{shopSearch}"</p>
                    </div>
                  ) : (
                  [...pointsShopItems]
                    .filter(item => !shopSearch || item.name.toLowerCase().includes(shopSearch.toLowerCase()) || (item.description || '').toLowerCase().includes(shopSearch.toLowerCase()))
                    .sort((a, b) => shopSort === 'price_asc' ? a.cost - b.cost : shopSort === 'price_desc' ? b.cost - a.cost : 0)
                    .map(item => (
                    <div key={item.id} className={`points-shop-card ${item.stock !== null && item.stock <= 0 ? 'out-of-stock' : ''}`}>
                      {((item.icon_key && item.icon_key !== 'custom') || item.image_url) && (
                        <div className="shop-card-image">
                          <ShopIcon iconKey={item.icon_key} fallbackImageUrl={item.image_url} alt={item.name} />
                        </div>
                      )}
                      <div className="shop-card-body">
                        <h3 className="shop-card-name">{item.name}</h3>
                        {item.description && <p className="shop-card-desc">{item.description}</p>}
                        <div className="shop-card-meta">
                          {item.stock !== null && (
                            <span className="shop-card-stock">{item.stock > 0 ? `${item.stock} left` : 'Sold out'}</span>
                          )}
                          {item.max_per_user && (
                            <span className="shop-card-limit">Limit {item.max_per_user}/person</span>
                          )}
                        </div>
                        <div className="shop-card-footer">
                          <div className="shop-card-cost">
                            <span className="shop-cost-icon">💎</span>
                            <span className="shop-cost-amount">{item.cost.toLocaleString()}</span>
                          </div>
                          {user ? (
                            <button 
                              className="shop-redeem-btn"
                              disabled={pointsBalance < item.cost || (item.stock !== null && item.stock <= 0) || redeemingItemId === item.id}
                              onClick={() => {
                                if (item.requires_discord_username) {
                                  setSelectedShopItem(item);
                                  setShowRedeemModal(true);
                                } else {
                                  redeemShopItem(item.id);
                                }
                              }}
                            >
                              {redeemingItemId === item.id ? 'Redeeming...' : 
                               pointsBalance < item.cost ? `Need ${(item.cost - pointsBalance).toLocaleString()} more` :
                               (item.stock !== null && item.stock <= 0) ? 'Sold out' : 'Redeem'}
                            </button>
                          ) : (
                            <button className="shop-redeem-btn" disabled>Login to redeem</button>
                          )}
                        </div>
                      </div>
                    </div>
                  )))
                )}
              </div>

              {/* Recently Paid Out Rewards */}
              {recentRedemptions.redemptions.length > 0 && (
                <div style={{marginTop:'1.5rem', background:'rgba(14,14,18,0.7)', border:'1px solid rgba(34,197,94,0.12)', borderRadius:'12px', padding:'1rem 1.25rem'}}>
                  <div style={{fontSize:'0.9rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.75rem', display:'flex', alignItems:'center', gap:'0.4rem'}}>
                    ✅ Recently Paid Out Rewards
                  </div>
                  <div style={{display:'flex', flexDirection:'column', gap:'0.4rem'}}>
                    {recentRedemptions.redemptions.map(r => (
                      <div key={r.id} style={{display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 0.65rem', background:'rgba(34,197,94,0.04)', border:'1px solid rgba(34,197,94,0.08)', borderRadius:'8px'}}>
                        {r.profile_image_url && <img src={r.profile_image_url} alt="" style={{width:28, height:28, borderRadius:'50%'}} />}
                        <div style={{flex:1, minWidth:0}}>
                          <span style={{fontSize:'0.82rem', fontWeight:600, color:'#e2e8f0'}}>{censorUsername(r.display_name)}</span>
                          <span style={{fontSize:'0.75rem', color:'#71717a'}}> redeemed </span>
                          <span style={{fontSize:'0.82rem', fontWeight:600, color:'#c084fc'}}>{r.item_name}</span>
                        </div>
                        <div style={{textAlign:'right', flexShrink:0}}>
                          <div style={{fontSize:'0.75rem', fontWeight:600, color:'#22c55e'}}>{r.points_spent.toLocaleString()} 💎</div>
                          <div style={{fontSize:'0.62rem', color:'#71717a'}}>{new Date(r.fulfilled_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {recentRedemptions.totalFulfilled > 5 && (
                    <div style={{textAlign:'center', marginTop:'0.6rem', fontSize:'0.75rem', color:'#71717a'}}>
                      + {(recentRedemptions.totalFulfilled - 5).toLocaleString()} more rewards paid out
                    </div>
                  )}
                </div>
              )}
              </div>
            )}

            {/* HISTORY TAB */}
            {pointsShopTab === 'history' && user && (
              <div className="points-history-container">
                {/* Redemptions Section */}
                {myRedemptions.length > 0 && (
                  <div className="points-history-section">
                    <h3 className="points-history-section-title">🛒 My Redemptions</h3>
                    <div className="points-history-list">
                      {myRedemptions.map(r => (
                        <div key={r.id} className={`points-history-item redemption ${r.status}`}>
                          <div className="history-item-left">
                            <span className="history-item-name">{r.item_name}</span>
                            <span className="history-item-date">{new Date(r.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="history-item-right">
                            <span className="history-item-cost">-{r.points_spent.toLocaleString()} 💎</span>
                            <span className={`history-item-status ${r.status}`}>
                              {r.status === 'pending' ? '⏳ Pending (up to 14 days)' : r.status === 'fulfilled' ? '✅ Fulfilled' : '❌ Cancelled'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transaction History */}
                <div className="points-history-section">
                  <h3 className="points-history-section-title">📊 Points History</h3>
                  {pointsHistory.length === 0 ? (
                    <div className="points-empty">
                      <p>No points history yet. Start earning points!</p>
                    </div>
                  ) : (
                    <>
                      <div className="points-history-list">
                        {pointsHistory.map(tx => (
                          <div key={tx.id} className={`points-history-item ${tx.amount > 0 ? 'earned' : 'spent'}`}>
                            <div className="history-item-left">
                              <span className="history-item-source">
                                {tx.source === 'admin_grant' ? '🎁' :
                                 tx.source === 'admin_deduct' ? '🔻' :
                                 tx.source === 'wager' ? '🎰' :
                                 tx.source === 'twitch_chat' ? '💬' :
                                 tx.source === 'twitch_watch' ? '📺' :
                                 tx.source === 'discord' ? '🎮' :
                                 tx.source === 'shop_redemption' ? '🛒' :
                                 tx.source === 'refund' ? '↩️' : '📌'}
                              </span>
                              <div>
                                <div className="history-item-detail">{tx.source_detail || tx.source.replace(/_/g, ' ')}</div>
                                <div className="history-item-date">{new Date(tx.created_at).toLocaleDateString()} {new Date(tx.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                              </div>
                            </div>
                            <span className={`history-item-amount ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                      {pointsHistoryTotal > 1 && (
                        <div className="points-pagination">
                          <button disabled={pointsHistoryPage <= 1} onClick={() => fetchPointsHistory(pointsHistoryPage - 1)}>← Prev</button>
                          <span>Page {pointsHistoryPage} of {pointsHistoryTotal}</span>
                          <button disabled={pointsHistoryPage >= pointsHistoryTotal} onClick={() => fetchPointsHistory(pointsHistoryPage + 1)}>Next →</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* LEADERBOARD TAB */}
            {pointsShopTab === 'leaderboard' && (
              <div className="points-leaderboard-container">
                {pointsLeaderboard.length === 0 ? (
                  <div className="points-empty">
                    <div className="points-empty-icon">🏆</div>
                    <p>No one has earned points yet. Be the first!</p>
                  </div>
                ) : (
                  <>
                    <div className="points-leaderboard-list">
                      {pointsLeaderboard.map((entry) => (
                        <div key={entry.rank} className={`points-lb-row ${entry.rank <= 3 ? `top-${entry.rank}` : ''}`}>
                          <span className="points-lb-rank">
                            {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                          </span>
                          <div className="points-lb-user">
                            {entry.profile_image_url && <img src={entry.profile_image_url} alt="" className="points-lb-avatar" />}
                            <span className="points-lb-name" style={entry.anonymous ? {color:'#71717a', fontStyle:'italic'} : undefined}>{entry.anonymous ? '🕶️ Anonymous' : censorUsername(entry.display_name)}</span>
                          </div>
                          <span className="points-lb-balance">💎 {parseInt(entry.balance).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    {pointsLbTotalPages > 1 && (
                      <div className="points-pagination">
                        <button disabled={pointsLbPage <= 1} onClick={() => fetchPointsLeaderboard(pointsLbPage - 1)}>← Prev</button>
                        <span>Page {pointsLbPage} of {pointsLbTotalPages}</span>
                        <button disabled={pointsLbPage >= pointsLbTotalPages} onClick={() => fetchPointsLeaderboard(pointsLbPage + 1)}>Next →</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* How to Earn Section */}
            <div className="points-how-to-earn">
              <h3>How to Earn Points</h3>
              <div className="points-earn-grid">
                <div className="points-earn-card">
                  <span className="earn-card-icon">🎰</span>
                  <h4>Wager on Shuffle</h4>
                  <p>Earn 1 point per ${pointsEarnConfig?.wager_dollars_per_point || 50} wagered under guild SNUTZY</p>
                </div>
                <div className="points-earn-card">
                  <span className="earn-card-icon">💬</span>
                  <h4>Twitch Chat</h4>
                  <p>{pointsEarnConfig?.points_per_twitch_chat || 1} {(pointsEarnConfig?.points_per_twitch_chat || 1) === 1 ? 'point' : 'points'} per message in Snutzy's Twitch chat ({(pointsEarnConfig?.twitch_chat_cooldown_seconds || 30) >= 60 ? `${Math.floor((pointsEarnConfig?.twitch_chat_cooldown_seconds || 30) / 60)}min` : `${pointsEarnConfig?.twitch_chat_cooldown_seconds || 30}s`} cooldown)</p>
                </div>
                <div className="points-earn-card">
                  <span className="earn-card-icon">📺</span>
                  <h4>Watch Stream</h4>
                  <p>{pointsEarnConfig?.points_per_watch_hour || 10} points per hour — just be in chat while watching</p>
                </div>
                <div className="points-earn-card">
                  <span className="earn-card-icon">🎮</span>
                  <h4>Discord Activity</h4>
                  <p>{pointsEarnConfig?.points_per_discord_message || 1} {(pointsEarnConfig?.points_per_discord_message || 1) === 1 ? 'point' : 'points'} per message ({(pointsEarnConfig?.discord_chat_cooldown_seconds || 30) >= 60 ? `${Math.floor((pointsEarnConfig?.discord_chat_cooldown_seconds || 30) / 60)}min` : `${pointsEarnConfig?.discord_chat_cooldown_seconds || 30}s`} cooldown) — connect your Discord account above to start earning</p>
                </div>
              </div>
            </div>

            <p style={{textAlign:'center', fontSize:'0.7rem', color:'#6b7280', marginTop:'1.5rem', lineHeight:'1.5', maxWidth:'500px', marginLeft:'auto', marginRight:'auto'}}>
              ⚠️ Shop items, reward values, and point accumulation rates are subject to change at any time. All redemptions are final. Snutzy reserves the right to modify or remove rewards without prior notice.
            </p>

            {/* Redeem Success/Error Message */}
            {redeemMessage && (
              <div className={`points-message ${redeemMessage.type}`}>
                {redeemMessage.type === 'success' ? '✅' : '❌'} {redeemMessage.text}
                <button className="points-message-close" onClick={() => setRedeemMessage(null)}>×</button>
              </div>
            )}
          </div>
        )}

        {/* Redeem Modal (for items requiring extra info) */}
        {showRedeemModal && selectedShopItem && (
          <div className="redeem-modal-overlay" onClick={() => setShowRedeemModal(false)}>
            <div className="redeem-modal" onClick={e => e.stopPropagation()}>
              <button className="redeem-modal-close" onClick={() => setShowRedeemModal(false)}>×</button>
              <h2>Redeem {selectedShopItem.name}</h2>
              <p className="redeem-modal-cost">Cost: 💎 {selectedShopItem.cost.toLocaleString()} points</p>
              {selectedShopItem.requires_discord_username && (
                <div className="redeem-modal-field">
                  <label>Discord Username:</label>
                  <input 
                    type="text" 
                    placeholder="YourName#1234" 
                    value={redeemDiscordUsername}
                    onChange={e => setRedeemDiscordUsername(e.target.value)}
                  />
                </div>
              )}
              {redeemMessage && (
                <div className={`points-message ${redeemMessage.type}`}>{redeemMessage.text}</div>
              )}
              <button 
                className="shop-redeem-btn full-width"
                disabled={redeemingItemId || (selectedShopItem.requires_discord_username && !redeemDiscordUsername.trim())}
                onClick={() => redeemShopItem(selectedShopItem.id, redeemDiscordUsername)}
              >
                {redeemingItemId ? 'Redeeming...' : `Confirm Redemption (💎 ${selectedShopItem.cost.toLocaleString()})`}
              </button>
            </div>
          </div>
        )}

        {/* Claim Giveaway Page */}
        {activePage === 'claim' && (
          <div className="main">
            <div style={{maxWidth:'640px', margin:'0 auto', padding:'2rem 1.5rem'}}>
              
              {!user ? (
                <div style={{textAlign:'center', padding:'3rem 2rem', background:'rgba(14,14,18,0.7)', borderRadius:'16px', border:'1px solid rgba(139,92,246,0.15)'}}>
                  <div style={{fontSize:'2.5rem', marginBottom:'1rem'}}>🎟️</div>
                  <div style={{fontSize:'1.4rem', fontWeight:700, color:'#fff', marginBottom:'0.4rem'}}>Claim Giveaway</div>
                  <p style={{color:'#71717a', fontSize:'0.9rem', marginBottom:'1.5rem', maxWidth:'360px', margin:'0 auto 1.5rem'}}>Won a prize on stream or Discord? Log in with Twitch to check for unclaimed giveaways.</p>
                  <button onClick={() => { window.location.href = `${BACKEND_API_URL}/auth/twitch`; }} style={{padding:'0.7rem 1.75rem', background:'#9146ff', border:'none', borderRadius:'10px', color:'#fff', fontSize:'0.95rem', fontWeight:600, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'0.5rem'}}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
                    Login with Twitch
                  </button>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div style={{marginBottom:'1.5rem'}}>
                    <div style={{fontSize:'1.3rem', fontWeight:700, color:'#fff', marginBottom:'0.3rem'}}>🎟️ Claim Giveaway</div>
                    <p style={{color:'#6b7280', fontSize:'0.85rem', margin:0}}>Won a prize on stream or Discord? Your claimable giveaways will appear below.</p>
                  </div>
                  
                  {publicPayoutStats?.giveaways > 0 && (
                    <div style={{display:'flex', justifyContent:'center', marginBottom:'1.25rem'}}>
                      <OdometerCounter value={publicPayoutStats.giveaways} label="Total Giveaways Paid" size="small" />
                    </div>
                  )}

                  {/* Account card */}
                  <div style={{display:'flex', alignItems:'center', gap:'0.85rem', padding:'1rem 1.25rem', background:'rgba(14,14,18,0.7)', borderRadius:'12px', border:'1px solid rgba(255,255,255,0.08)', marginBottom:'1rem'}}>
                    {user.profileImageUrl && <img src={user.profileImageUrl} alt="" style={{width:44, height:44, borderRadius:'50%', border:'2px solid rgba(139,92,246,0.3)'}} />}
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:'1rem', fontWeight:700, color:'#fff', marginBottom:'0.35rem'}}>{user.displayName}</div>
                      <div style={{display:'flex', gap:'0.4rem', flexWrap:'wrap'}}>
                        <span style={{display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.2rem 0.55rem', borderRadius:'5px', fontSize:'0.72rem', fontWeight:600, background:'rgba(145,70,255,0.12)', border:'1px solid rgba(145,70,255,0.2)', color:'#b794f4'}}>
                          📺 {claimableTwitchName || user.displayName}
                        </span>
                        {claimableDiscordLinked ? (
                          <span style={{display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.2rem 0.55rem', borderRadius:'5px', fontSize:'0.72rem', fontWeight:600, background:'rgba(88,101,242,0.12)', border:'1px solid rgba(88,101,242,0.2)', color:'#7289da'}}>
                            💬 Discord linked ✓
                          </span>
                        ) : (
                          <span style={{display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.2rem 0.55rem', borderRadius:'5px', fontSize:'0.72rem', fontWeight:600, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', color:'#f87171'}}>
                            💬 Discord not linked
                          </span>
                        )}
                        {claimableKickLinked ? (
                          <span style={{display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.2rem 0.55rem', borderRadius:'5px', fontSize:'0.72rem', fontWeight:600, background:'rgba(83,252,24,0.08)', border:'1px solid rgba(83,252,24,0.25)', color:'#53fc18'}}>
                            🟢 Kick linked ✓
                          </span>
                        ) : claimableKickLinked === false ? (
                          <span style={{display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.2rem 0.55rem', borderRadius:'5px', fontSize:'0.72rem', fontWeight:600, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', color:'#f87171'}}>
                            🟢 Kick not linked
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {!claimableDiscordLinked && claimableDiscordLinked !== null && (
                      <button onClick={connectDiscord} style={{padding:'0.45rem 0.85rem', background:'#5865F2', border:'none', borderRadius:'8px', color:'#fff', fontSize:'0.8rem', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap'}}>
                        Link Discord
                      </button>
                    )}
                    {claimableKickLinked === false && (
                      <button onClick={connectKick} style={{padding:'0.45rem 0.85rem', background:'#53fc18', border:'none', borderRadius:'8px', color:'#0a0a0a', fontSize:'0.8rem', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                        Link Kick
                      </button>
                    )}
                  </div>

                  {/* Discord re-link prompt */}
                  {claimableDiscordLinked && !claimableDiscordActual && (
                    <div style={{display:'flex', alignItems:'center', gap:'0.85rem', padding:'0.85rem 1.15rem', background:'rgba(88,101,242,0.06)', borderRadius:'12px', border:'1px solid rgba(88,101,242,0.2)', marginBottom:'1rem'}}>
                      <span style={{fontSize:'1.2rem'}}>⚠️</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:'0.85rem', fontWeight:600, color:'#7289da'}}>Please re-link your Discord</div>
                        <div style={{fontSize:'0.75rem', color:'#9ca3af', marginTop:'0.15rem'}}>We've updated how we match Discord accounts. Re-linking ensures your giveaway claims work correctly.</div>
                      </div>
                      <button onClick={connectDiscord} style={{padding:'0.45rem 0.9rem', background:'#5865F2', border:'none', borderRadius:'8px', color:'#fff', fontSize:'0.8rem', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap'}}>
                        🔄 Re-link
                      </button>
                    </div>
                  )}

                  {/* Pending admin-added giveaways (matched on shuffle username) */}
                  {pendingShuffleGiveaways && pendingShuffleGiveaways.length > 0 && (
                    <div style={{marginBottom:'1rem'}}>
                      {pendingShuffleGiveaways.map(g => (
                        <div key={`p-${g.id}`} style={{
                          background:'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(217,119,6,0.04))',
                          border:'1px solid rgba(245,158,11,0.25)',
                          borderRadius:'12px', padding:'1rem 1.25rem', marginBottom:'0.5rem',
                          display:'flex', alignItems:'center', gap:'0.85rem'
                        }}>
                          <div style={{fontSize:'1.4rem'}}>⏳</div>
                          <div style={{flex:1, minWidth:0}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.25rem', flexWrap:'wrap'}}>
                              <span style={{fontSize:'0.7rem', padding:'0.15rem 0.5rem', borderRadius:'5px', fontWeight:700, background:'rgba(245,158,11,0.15)', color:'#fbbf24', border:'1px solid rgba(245,158,11,0.3)'}}>PENDING PAYOUT</span>
                              <span style={{fontSize:'1.1rem', fontWeight:800, color:'#fbbf24'}}>${parseFloat(g.amount).toLocaleString()}</span>
                            </div>
                            {g.description && <div style={{fontSize:'0.78rem', color:'#a1a1aa', marginBottom:'0.2rem'}}>{g.description}</div>}
                            <div style={{fontSize:'0.7rem', color:'#71717a'}}>
                              Queued for your linked Shuffle account <strong style={{color:'#e2e8f0'}}>{g.shuffle_username}</strong>. An admin will pay this directly to your Shuffle balance — no action needed.
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Giveaway list */}
                  {myClaimableGiveaways.length === 0 ? (
                    <div style={{textAlign:'center', padding:'3.5rem 2rem', background:'rgba(14,14,18,0.5)', borderRadius:'16px', border:'1px dashed rgba(255,255,255,0.08)'}}>
                      <div style={{fontSize:'2.5rem', marginBottom:'0.75rem', opacity:0.7}}>🎁</div>
                      <div style={{color:'#a1a1aa', fontSize:'1rem', fontWeight:600, marginBottom:'0.4rem'}}>No prizes to claim right now</div>
                      <p style={{color:'#71717a', fontSize:'0.82rem', margin:0, maxWidth:'380px', marginLeft:'auto', marginRight:'auto', lineHeight:'1.5'}}>
                        If you won a giveaway on stream or Discord, your prizes will show up here.
                        {!claimableDiscordLinked && ' Make sure to link your Discord for Discord giveaways.'}
                      </p>
                    </div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.75rem'}}>
                      {myClaimableGiveaways.map(g => (
                        <div key={g.id} style={{
                          background: g.status === 'claimed' ? 'rgba(34,197,94,0.04)' : g.status === 'paid' ? 'rgba(34,197,94,0.06)' : g.status === 'denied' ? 'rgba(239,68,68,0.04)' : 'rgba(14,14,18,0.7)',
                          border: `1px solid ${g.status === 'claimed' ? 'rgba(251,191,36,0.2)' : g.status === 'paid' ? 'rgba(34,197,94,0.25)' : g.status === 'denied' ? 'rgba(239,68,68,0.2)' : 'rgba(139,92,246,0.15)'}`,
                          borderRadius: '14px',
                          padding: '1.25rem 1.5rem'
                        }}>
                          <div style={{display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'0.75rem'}}>
                            <span style={{fontSize:'1.2rem'}}>{g.type === 'discord' ? '💬' : g.type === 'kick' ? '🟢' : '📺'}</span>
                            <span style={{fontSize:'0.78rem', padding:'0.2rem 0.6rem', borderRadius:'6px', fontWeight:600,
                              background: g.type === 'discord' ? 'rgba(88,101,242,0.12)' : g.type === 'kick' ? 'rgba(83,252,24,0.1)' : 'rgba(145,70,255,0.12)',
                              border: `1px solid ${g.type === 'discord' ? 'rgba(88,101,242,0.2)' : g.type === 'kick' ? 'rgba(83,252,24,0.25)' : 'rgba(145,70,255,0.2)'}`,
                              color: g.type === 'discord' ? '#7289da' : g.type === 'kick' ? '#53fc18' : '#b794f4'
                            }}>{g.type === 'discord' ? 'Discord' : g.type === 'kick' ? 'Kick' : 'Twitch'} Giveaway</span>
                            <span style={{fontSize:'1.25rem', fontWeight:800, color: g.status === 'denied' ? '#71717a' : '#22c55e', marginLeft:'auto', textDecoration: g.status === 'denied' ? 'line-through' : 'none'}}>${parseFloat(g.amount).toLocaleString()}</span>
                          </div>

                          {g.description && <div style={{fontSize:'0.85rem', color:'#9ca3af', marginBottom:'0.75rem'}}>{g.description}</div>}

                          {g.status === 'unclaimed' ? (() => {
                            const method = claimPayoutMethod[g.id] || 'shuffle';
                            const isCrypto = method === 'crypto';
                            const crypto = claimCryptoInputs[g.id] || { coin: 'BTC', address: '' };
                            const coinMeta = CLAIM_CRYPTO_COINS.find(c => c.key === crypto.coin) || CLAIM_CRYPTO_COINS[0];
                            const addr = crypto.address.trim();
                            const addrInvalid = addr.length > 0 && !coinMeta.pattern.test(addr);
                            const shuffleValue = claimShuffleInputs[g.id] ?? (user?.shuffleUsername || '');
                            const canSubmit = isCrypto ? (addr && !addrInvalid) : !!shuffleValue.trim();
                            const inputStyle = {padding:'0.6rem 0.85rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'10px', color:'#fff', fontSize:'0.9rem'};
                            return (
                            <div>
                              <div style={{fontSize:'0.8rem', color:'#a1a1aa', marginBottom:'0.5rem'}}>How do you want to get paid?</div>
                              <div style={{display:'flex', gap:'0.4rem', marginBottom:'0.75rem'}}>
                                {[['shuffle', '🎰 Shuffle tip'], ['crypto', '🪙 Crypto address']].map(([key, label]) => (
                                  <button key={key} onClick={() => setClaimPayoutMethod(prev => ({...prev, [g.id]: key}))}
                                    style={{flex:1, padding:'0.5rem 0.75rem', background: method === key ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.04)', border:`1px solid ${method === key ? 'rgba(139,92,246,0.45)' : 'rgba(255,255,255,0.08)'}`, borderRadius:'8px', color: method === key ? '#fff' : '#9ca3af', fontSize:'0.82rem', fontWeight:600, cursor:'pointer'}}>
                                    {label}
                                  </button>
                                ))}
                              </div>

                              {isCrypto ? (
                                <div style={{display:'flex', alignItems:'flex-start', gap:'0.4rem', padding:'0.55rem 0.75rem', background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.18)', borderRadius:'8px', marginBottom:'0.6rem'}}>
                                  <span style={{fontSize:'0.85rem', flexShrink:0}}>🚨</span>
                                  <span style={{fontSize:'0.72rem', color:'#fca5a5', lineHeight:'1.4'}}>
                                    Double-check the <strong style={{color:'#fff'}}>coin, network and address</strong>. Crypto sent to a wrong address or network can't be recovered.
                                  </span>
                                </div>
                              ) : (
                                <div style={{display:'flex', alignItems:'flex-start', gap:'0.4rem', padding:'0.55rem 0.75rem', background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.18)', borderRadius:'8px', marginBottom:'0.6rem'}}>
                                  <span style={{fontSize:'0.85rem', flexShrink:0}}>🚨</span>
                                  <span style={{fontSize:'0.72rem', color:'#fca5a5', lineHeight:'1.4'}}>
                                    Must be your <strong style={{color:'#fff'}}>shuffle.com</strong> username, NOT shuffle.us. The .us version is a separate site and cannot receive payouts from us.
                                  </span>
                                </div>
                              )}

                              <div style={{display:'flex', gap:'0.5rem', marginBottom:'0.6rem', flexWrap:'wrap'}}>
                                {isCrypto ? (
                                  <>
                                    <select
                                      value={crypto.coin}
                                      onChange={e => setClaimCryptoInputs(prev => ({...prev, [g.id]: {...crypto, coin: e.target.value}}))}
                                      style={{...inputStyle, flex:'0 0 auto', cursor:'pointer'}}
                                    >
                                      {CLAIM_CRYPTO_COINS.map(c => <option key={c.key} value={c.key} style={{background:'#18181b'}}>{c.label}</option>)}
                                    </select>
                                    <input
                                      type="text"
                                      placeholder={`Your ${coinMeta.key} address (${coinMeta.placeholder})`}
                                      value={crypto.address}
                                      onChange={e => setClaimCryptoInputs(prev => ({...prev, [g.id]: {...crypto, address: e.target.value}}))}
                                      spellCheck={false}
                                      autoComplete="off"
                                      style={{...inputStyle, flex:'1 1 220px', minWidth:0, fontFamily:'monospace', fontSize:'0.82rem', borderColor: addrInvalid ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.12)'}}
                                    />
                                  </>
                                ) : (
                                  <input
                                    type="text"
                                    placeholder="Your shuffle.com username..."
                                    value={shuffleValue}
                                    onChange={e => setClaimShuffleInputs(prev => ({...prev, [g.id]: e.target.value}))}
                                    style={{...inputStyle, flex:1, minWidth:0}}
                                  />
                                )}
                                <button
                                  disabled={claimingId === g.id || !canSubmit}
                                  onClick={async () => {
                                    if (isCrypto && !confirm(`Send this prize as ${coinMeta.label} to:\n\n${addr}\n\nCrypto sent to a wrong address can't be recovered. Is this correct?`)) return;
                                    setClaimingId(g.id);
                                    try {
                                      const token = getAuthToken();
                                      const res = await fetch(`${BACKEND_API_URL}/api/claim-giveaway/${g.id}`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                        body: JSON.stringify(isCrypto
                                          ? { payoutMethod: 'crypto', cryptoCoin: coinMeta.key, cryptoAddress: addr }
                                          : { payoutMethod: 'shuffle', shuffleUsername: shuffleValue.trim() })
                                      });
                                      const data = await res.json();
                                      if (res.ok) {
                                        alert(isCrypto
                                          ? '✅ Prize claimed! It will be sent to your crypto address after admin review. Payouts can take up to 14 days to process.'
                                          : '✅ Prize claimed! It will be sent to your Shuffle account after admin review. Payouts can take up to 14 days to process.');
                                        fetchMyClaimableGiveaways();
                                        collectFingerprint();
                                      } else {
                                        alert(data.error || 'Failed to claim');
                                      }
                                    } catch (e) {
                                      alert('Error claiming prize');
                                    }
                                    setClaimingId(null);
                                  }}
                                  style={{padding:'0.6rem 1.5rem', background: claimingId === g.id || !canSubmit ? '#52525b' : 'linear-gradient(135deg, #8b5cf6, #a855f7)', border:'none', borderRadius:'10px', color:'#fff', fontSize:'0.9rem', fontWeight:700, cursor: claimingId === g.id || !canSubmit ? 'not-allowed' : 'pointer', whiteSpace:'nowrap'}}
                                >
                                  {claimingId === g.id ? '...' : '🎟️ Claim'}
                                </button>
                              </div>
                              {addrInvalid && (
                                <div style={{fontSize:'0.72rem', color:'#f87171', margin:'-0.3rem 0 0.6rem'}}>That doesn't look like a valid {coinMeta.label} address — check you picked the right network.</div>
                              )}
                              {!isCrypto && (
                                <div style={{display:'flex', alignItems:'flex-start', gap:'0.4rem', padding:'0.5rem 0.7rem', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'8px', marginBottom:'0.5rem'}}>
                                  <span style={{fontSize:'0.8rem', flexShrink:0}}>⚠️</span>
                                  <span style={{fontSize:'0.72rem', color:'#d4a017', lineHeight:'1.4'}}>Your Shuffle account must be <strong>KYC Level 1 verified</strong> to receive tips. If your account is not verified, the payout will be rejected. You can verify your account in your Shuffle settings.</span>
                                </div>
                              )}
                              <div style={{textAlign:'center', fontSize:'0.72rem', color:'#71717a'}}>
                                {!isCrypto && <>Don't have a Shuffle account? <button onClick={() => navigateTo('signup')} style={{background:'none', border:'none', color:'#a78bfa', textDecoration:'underline', cursor:'pointer', fontSize:'0.72rem', padding:0, fontWeight:600}}>View the Shuffle Guide →</button><br /></>}
                                Any questions? Reach out to <a href="http://discord.com/users/182121536461864960" target="_blank" rel="noopener noreferrer" style={{color:'#a78bfa', textDecoration:'underline', textUnderlineOffset:'3px', fontWeight:600}}>@pa1n</a> on Discord.
                              </div>
                            </div>
                            );
                          })() : g.status === 'claimed' ? (
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.6rem 0.85rem', background:'rgba(251,191,36,0.06)', borderRadius:'8px', border:'1px solid rgba(251,191,36,0.12)', flexWrap:'wrap'}}>
                              <span style={{color:'#fbbf24', fontSize:'0.85rem', fontWeight:600}}>⏳ Claimed — awaiting payout (up to 14 days)</span>
                              <span style={{color:'#71717a', fontSize:'0.75rem', marginLeft:'auto', wordBreak:'break-all'}}>
                                {g.payout_method === 'crypto' ? `${g.crypto_coin}: ${g.crypto_address}` : `Shuffle: ${g.shuffle_username}`}
                              </span>
                            </div>
                          ) : g.status === 'paid' ? (
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.6rem 0.85rem', background:'rgba(34,197,94,0.06)', borderRadius:'8px', border:'1px solid rgba(34,197,94,0.12)', flexWrap:'wrap'}}>
                              <span style={{color:'#22c55e', fontSize:'0.85rem', fontWeight:600}}>✅ Paid!</span>
                              <span style={{color:'#71717a', fontSize:'0.75rem', marginLeft:'auto', wordBreak:'break-all'}}>
                                {g.payout_method === 'crypto' ? `${g.crypto_coin}: ${g.crypto_address}` : `Shuffle: ${g.shuffle_username}`}
                              </span>
                            </div>
                          ) : g.status === 'denied' ? (
                            <div style={{padding:'0.6rem 0.85rem', background:'rgba(239,68,68,0.06)', borderRadius:'8px', border:'1px solid rgba(239,68,68,0.12)'}}>
                              <span style={{color:'#f87171', fontSize:'0.85rem', fontWeight:600}}>🚫 Denied</span>
                              <p style={{color:'#71717a', fontSize:'0.75rem', margin:'0.3rem 0 0'}}>This giveaway claim has been denied by an administrator. If you believe this is an error, reach out to <a href="http://discord.com/users/182121536461864960" target="_blank" rel="noopener noreferrer" style={{color:'#a78bfa', textDecoration:'underline', textUnderlineOffset:'3px', fontWeight:600}}>@pa1n</a> on Discord.</p>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {activePage === 'admin' && (isAdmin || isModerator) && (
          <div className="admin-container">
            {/* Compact utilitarian header — admin is a workspace, not a marketing page */}
            <h1 style={{display:'flex', alignItems:'center', gap:'0.6rem', fontSize:'1.35rem', fontWeight:800, textAlign:'left', margin:'0 0 1.25rem', color:'#fff'}}>
              ⚙️ {isAdmin ? 'Admin Panel' : 'Mod Panel'}
            </h1>
            
            {/* Admin Tabs */}
            <div className="admin-tabs">
              <button 
                className={`admin-tab ${adminTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setAdminTab('dashboard')}
              >
                📊 Dashboard
              </button>
              <button 
                className={`admin-tab ${adminTab === 'hunts' ? 'active' : ''}`}
                onClick={() => setAdminTab('hunts')}
              >
                🎯 Hunt Management
              </button>
              <button
                className={`admin-tab ${adminTab === 'featuredPosts' ? 'active' : ''}`}
                onClick={() => setAdminTab('featuredPosts')}
              >
                📣 Featured Posts
              </button>
              {/* Everything below is admin-only — moderators get dashboard quick actions, hunts and featured posts */}
              {isAdmin && (<>
              <button
                className={`admin-tab ${adminTab === 'rewardsledger' ? 'active' : ''}`}
                onClick={() => setAdminTab('rewardsledger')}
              >
                🧾 Rewards Ledger
              </button>
              <button
                className={`admin-tab ${adminTab === 'referrals' ? 'active' : ''}`}
                onClick={() => setAdminTab('referrals')}
              >
                🤝 Referrals
                {adminPendingCounts?.referrals > 0 && (
                  <span className="admin-tab-badge">{adminPendingCounts.referrals}</span>
                )}
              </button>
              <button
                className={`admin-tab ${adminTab === 'checklist' ? 'active' : ''}`}
                onClick={() => setAdminTab('checklist')}
              >
                ✅ Checklist
                {adminPendingCounts?.checklist > 0 && (
                  <span className="admin-tab-badge">{adminPendingCounts.checklist}</span>
                )}
              </button>
              <button
                className={`admin-tab ${adminTab === 'clips' ? 'active' : ''}`}
                onClick={() => setAdminTab('clips')}
              >
                ✂️ Clips
                {adminPendingCounts?.clips > 0 && (
                  <span className="admin-tab-badge">{adminPendingCounts.clips}</span>
                )}
              </button>
              <button
                className={`admin-tab ${adminTab === 'lossback' ? 'active' : ''}`}
                onClick={() => setAdminTab('lossback')}
              >
                💸 Lossback
                {adminPendingCounts?.lossback > 0 && (
                  <span className="admin-tab-badge">{adminPendingCounts.lossback}</span>
                )}
              </button>
              <button
                className={`admin-tab ${adminTab === 'rewards' ? 'active' : ''}`}
                onClick={() => setAdminTab('rewards')}
              >
                💰 Pending Rewards
                {adminData.pendingClaims.length > 0 && (
                  <span className="admin-tab-badge">{adminData.pendingClaims.length}</span>
                )}
              </button>
              <button 
                className={`admin-tab ${adminTab === 'paidRewards' ? 'active' : ''}`}
                onClick={() => { setAdminTab('paidRewards'); fetchPayoutSummary(adminPayoutRange); }}
              >
                ✅ Paid Rewards
              </button>
              <button 
                className={`admin-tab ${adminTab === 'giveaways' ? 'active' : ''}`}
                onClick={() => { setAdminTab('giveaways'); fetchAdminClaimableGiveaways(adminClaimableFilter); }}
              >
                🎁 Giveaways
                {(adminData.pendingGiveaways.length + (adminClaimableCounts.claimed || 0)) > 0 && (
                  <span className="admin-tab-badge">{adminData.pendingGiveaways.length + (adminClaimableCounts.claimed || 0)}</span>
                )}
              </button>
              <button 
                className={`admin-tab ${adminTab === 'paidGiveaways' ? 'active' : ''}`}
                onClick={() => { setAdminTab('paidGiveaways'); fetchPayoutSummary(adminPayoutRange); }}
              >
                ✅ Paid Giveaways
              </button>
              <button 
                className={`admin-tab ${adminTab === 'wagerClaims' ? 'active' : ''}`}
                onClick={() => { setAdminTab('wagerClaims'); fetchAdminWagerClaims(); fetchAdminLeaderboardTotal(); fetchAdminWagerBulk(); fetchAdminBulkAdjustments(); }}
              >
                🎫 Wager Claims
              </button>
              <button 
                className={`admin-tab ${adminTab === 'unclaimedWins' ? 'active' : ''}`}
                onClick={() => setAdminTab('unclaimedWins')}
              >
                ⚠️ Unclaimed Wins
                {adminData.unclaimedWins.length > 0 && (
                  <span className="admin-tab-badge">{adminData.unclaimedWins.length}</span>
                )}
              </button>
              <button 
                className={`admin-tab ${adminTab === 'slotPicks' ? 'active' : ''}`}
                onClick={() => {
                  setAdminTab('slotPicks');
                  setAdminSlotPicksFilter('pending');
                  fetchSlotPicks('pending', 1);
                }}
              >
                🎰 Slot Picks
                {adminData.slotPicksStats.pending > 0 && (
                  <span className="admin-tab-badge">{adminData.slotPicksStats.pending}</span>
                )}
              </button>
              <button 
                className={`admin-tab ${adminTab === 'slotPicksRefunds' ? 'active' : ''}`}
                onClick={() => {
                  setAdminTab('slotPicksRefunds');
                  setAdminSlotPicksFilter('refund_pending');
                  fetchSlotPicks('refund_pending', 1);
                }}
              >
                🔄 Refunds
                {adminData.slotPicksStats.refundPending > 0 && (
                  <span className="admin-tab-badge">{adminData.slotPicksStats.refundPending}</span>
                )}
              </button>
              <button 
                className={`admin-tab ${adminTab === 'analytics' ? 'active' : ''}`}
                onClick={() => { setAdminTab('analytics'); setAnalyticsData(null); }}
              >
                📊 Analytics
              </button>
              <button 
                className={`admin-tab ${adminTab === 'users' ? 'active' : ''}`}
                onClick={() => setAdminTab('users')}
              >
                👥 Users
              </button>
              <button 
                className={`admin-tab ${adminTab === 'points' ? 'active' : ''}`}
                onClick={() => { setAdminTab('points'); fetchAdminPointsStats(); fetchAdminShopItems(); fetchAdminPointsRedemptions('pending', 1); }}
              >
                💎 Points
                {adminPointsStats?.pendingRedemptions > 0 && (
                  <span className="admin-tab-badge">{adminPointsStats.pendingRedemptions}</span>
                )}
              </button>
              <button 
                className={`admin-tab ${adminTab === 'fraud' ? 'active' : ''}`}
                onClick={() => { setAdminTab('fraud'); fetchFraudClusters(); fetchShadowbans(); }}
              >
                🔍 Fraud
              </button>
              <button
                className={`admin-tab ${adminTab === 'vip' ? 'active' : ''}`}
                onClick={() => { setAdminTab('vip'); fetchVipMonitor(); }}
              >
                ⭐ VIP
              </button>
              </>)}
            </div>

            {/* Dashboard Tab */}
            {adminTab === 'dashboard' && (
              <>
                {/* Admin Stats (admin-only) */}
                {isAdmin && adminData.stats && (
                  <div style={{marginBottom:'1.5rem'}}>
                    {/* Overview Row */}
                    <div className="admin-stats-section-label">Overview</div>
                    <div className="admin-stats-row">
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminData.stats.totalUsers}</div>
                        <div className="admin-stat-label">Users</div>
                      </div>
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminData.stats.totalPredictions}</div>
                        <div className="admin-stat-label">Predictions</div>
                      </div>
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminData.stats.activeHunts}</div>
                        <div className="admin-stat-label">Active Hunts</div>
                      </div>
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminData.stats.completedHunts}</div>
                        <div className="admin-stat-label">Completed Hunts</div>
                      </div>
                    </div>

                    {/* Pending Row — each tile deep-links to the screen that resolves it */}
                    <div className="admin-stats-section-label">Pending / Action Needed</div>
                    <div className="admin-stats-row" style={{gridTemplateColumns:'repeat(3, 1fr)'}}>
                      <div
                        className="admin-stat-card highlight-unclaimed admin-stat-clickable"
                        role="button" tabIndex={0} title="Open Unclaimed Wins"
                        onClick={() => setAdminTab('unclaimedWins')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAdminTab('unclaimedWins'); } }}
                      >
                        <div className="admin-stat-value">${(adminData.stats.unclaimedWinsAmount || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Unclaimed Wins ({adminData.stats.unclaimedWinsCount || 0}) →</div>
                      </div>
                      <div
                        className="admin-stat-card highlight-pending admin-stat-clickable"
                        role="button" tabIndex={0} title="Open Pending Rewards"
                        onClick={() => setAdminTab('rewards')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAdminTab('rewards'); } }}
                      >
                        <div className="admin-stat-value">${(adminData.stats.pendingPredictionsAmount || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Predictions Pending →</div>
                      </div>
                      <div
                        className="admin-stat-card highlight-pending admin-stat-clickable"
                        role="button" tabIndex={0} title="Open Giveaways"
                        onClick={() => { setAdminTab('giveaways'); fetchAdminClaimableGiveaways(adminClaimableFilter); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAdminTab('giveaways'); fetchAdminClaimableGiveaways(adminClaimableFilter); } }}
                      >
                        <div className="admin-stat-value">${(adminData.stats.pendingGiveawaysAmount || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Giveaways Pending →</div>
                      </div>
                    </div>

                    {/* Paid Out Row — all 7 categories, same canonical total as the site header */}
                    <div className="admin-stats-section-label">Total Paid Out</div>
                    <div className="admin-stats-row" style={{gridTemplateColumns:'repeat(4, 1fr)'}}>
                      <div className="admin-stat-card highlight-paid">
                        <div className="admin-stat-value">${(adminData.stats.totalPredictionsPaidCombined || adminData.stats.totalPredictionsPaid || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Predictions</div>
                      </div>
                      <div className="admin-stat-card highlight-paid">
                        <div className="admin-stat-value">${(adminData.stats.totalGiveawaysPaidCombined || adminData.stats.totalGiveawaysPaid || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Giveaways</div>
                      </div>
                      <div className="admin-stat-card highlight-paid">
                        <div className="admin-stat-value">${(adminData.stats.totalWagerRewardsPaidCombined || adminData.stats.totalWagerRewardsPaid || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Wager Rewards</div>
                      </div>
                      <div className="admin-stat-card highlight-paid">
                        <div className="admin-stat-value">${(adminData.stats.totalLeaderboardPaid || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Leaderboard</div>
                      </div>
                      <div className="admin-stat-card highlight-paid">
                        <div className="admin-stat-value">${(adminData.stats.totalSlotPicksPaidCombined || adminData.stats.totalSlotPicksPaid || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Slot Picks</div>
                      </div>
                      <div className="admin-stat-card highlight-paid">
                        <div className="admin-stat-value">${(adminData.stats.totalPointsShopPaid || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Points Shop</div>
                      </div>
                      <div className="admin-stat-card highlight-paid">
                        <div className="admin-stat-value">${(adminData.stats.totalViewerHuntPaid || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Viewer Hunt</div>
                      </div>
                      <div className="admin-stat-card stat-total">
                        <div className="admin-stat-value">${(adminData.stats.totalPaidOut || 0).toLocaleString()}</div>
                        <div className="admin-stat-label">💰 Total Paid Out (All Categories)</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Feature toggles (admin-only) */}
                {isAdmin && (
                  <div style={{marginBottom:'1.5rem', padding:'1rem 1.25rem', background:'rgba(139,92,246,0.06)', border:'1px solid rgba(139,92,246,0.18)', borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap'}}>
                    <div>
                      <div style={{fontSize:'0.95rem', fontWeight:700, color:'#e2e8f0'}}>📅 Weekly Leaderboard</div>
                      <div style={{fontSize:'0.78rem', color:'#9ca3af', marginTop:'0.2rem', maxWidth:'520px'}}>
                        {weeklyLbAdminEnabled
                          ? 'Currently visible to all users. Turn this off once the current week has ended and been paid out — it only hides the Weekly view and does not touch the monthly leaderboard.'
                          : 'Hidden from the site. The monthly leaderboard is completely unaffected.'}
                      </div>
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:'0.65rem', flexShrink:0}}>
                      <span style={{fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.5px', color: weeklyLbAdminEnabled ? '#22c55e' : '#71717a', minWidth:'46px', textAlign:'right'}}>
                        {weeklyLbAdminBusy ? '…' : (weeklyLbAdminEnabled ? 'Visible' : 'Hidden')}
                      </span>
                      <button
                        role="switch"
                        aria-checked={weeklyLbAdminEnabled}
                        aria-label="Weekly leaderboard visibility"
                        onClick={toggleWeeklyLbAdmin}
                        disabled={weeklyLbAdminBusy}
                        style={{
                          position:'relative', width:'46px', height:'26px', borderRadius:'13px',
                          cursor: weeklyLbAdminBusy ? 'wait' : 'pointer', padding:0,
                          background: weeklyLbAdminEnabled ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.12)',
                          border: weeklyLbAdminEnabled ? '1px solid rgba(34,197,94,0.6)' : '1px solid rgba(255,255,255,0.2)',
                          transition:'background 0.2s ease, border-color 0.2s ease'
                        }}
                      >
                        <span style={{
                          position:'absolute', top:'2px', left: weeklyLbAdminEnabled ? '22px' : '2px',
                          width:'20px', height:'20px', borderRadius:'50%',
                          background:'#fff', transition:'left 0.2s ease',
                          boxShadow:'0 1px 3px rgba(0,0,0,0.4)'
                        }} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick Navigation Cards (admin-only — they link to admin-only tabs) */}
                {isAdmin && (
                <div className="admin-dashboard-cards">
                  <div className="admin-dashboard-card" onClick={() => setAdminTab('hunts')}>
                    <div className="admin-dashboard-card-icon">🎯</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">Hunt Management</div>
                      <div className="admin-dashboard-card-value">{adminData.hunts.length} hunts</div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                  
                  <div className="admin-dashboard-card" onClick={() => setAdminTab('rewards')}>
                    <div className="admin-dashboard-card-icon">💰</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">Pending Rewards</div>
                      <div className={`admin-dashboard-card-value ${adminData.pendingClaims.length > 0 ? 'pending' : ''}`}>
                        {adminData.pendingClaims.length} pending
                      </div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                  
                  <div className="admin-dashboard-card" onClick={() => { setAdminTab('paidRewards'); fetchPayoutSummary(adminPayoutRange); }}>
                    <div className="admin-dashboard-card-icon">✅</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">Paid Rewards</div>
                      <div className="admin-dashboard-card-value success">{adminData.paidClaims.length} paid</div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                  
                  <div className="admin-dashboard-card" onClick={() => { setAdminTab('giveaways'); fetchAdminClaimableGiveaways(adminClaimableFilter); }}>
                    <div className="admin-dashboard-card-icon">🎁</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">Giveaways</div>
                      <div className={`admin-dashboard-card-value ${(adminData.pendingGiveaways.length + (adminClaimableCounts.claimed || 0)) > 0 ? 'pending' : ''}`}>
                        {adminData.pendingGiveaways.length + (adminClaimableCounts.claimed || 0)} pending
                      </div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                  
                  <div className="admin-dashboard-card" onClick={() => { setAdminTab('paidGiveaways'); fetchPayoutSummary(adminPayoutRange); }}>
                    <div className="admin-dashboard-card-icon">✅</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">Paid Giveaways</div>
                      <div className="admin-dashboard-card-value success">{adminData.paidGiveaways.length} paid</div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                  
                  <div className="admin-dashboard-card" onClick={() => setAdminTab('unclaimedWins')}>
                    <div className="admin-dashboard-card-icon">⚠️</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">Unclaimed Wins</div>
                      <div className={`admin-dashboard-card-value ${adminData.unclaimedWins.length > 0 ? 'danger' : ''}`}>
                        {adminData.unclaimedWins.length} unclaimed
                      </div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                  
                  <div className="admin-dashboard-card" onClick={() => setAdminTab('slotPicks')}>
                    <div className="admin-dashboard-card-icon">🎰</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">Slot Picks Queue</div>
                      <div className={`admin-dashboard-card-value ${adminData.slotPicksStats.pending > 0 ? 'pending' : ''}`}>
                        {adminData.slotPicksStats.pending} pending
                      </div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                  
                  <div className="admin-dashboard-card" onClick={() => setAdminTab('users')}>
                    <div className="admin-dashboard-card-icon">👥</div>
                    <div className="admin-dashboard-card-content">
                      <div className="admin-dashboard-card-title">User Database</div>
                      <div className="admin-dashboard-card-value">{adminData.usersTotal} users</div>
                    </div>
                    <span className="admin-dashboard-card-arrow">→</span>
                  </div>
                </div>
                )}

                {/* Quick Actions - Only for opening new predictions */}
                <div className="admin-section">
                  <h2>Quick Actions</h2>
                  <div className="admin-actions">
                    {bonusHuntStats.activeHunt ? (
                      <>
                        {/* Reward Inputs */}
                        <div className="admin-rewards-setup">
                          <h4>🏆 Set Rewards (before opening predictions)</h4>
                          <div className="admin-rewards-inputs">
                            <div className="admin-reward-input">
                              <label>🥇 1st Place</label>
                              <input 
                                type="text"
                                placeholder="e.g., $50 Shuffle Balance"
                                value={adminRewards.first}
                                onChange={(e) => setAdminRewards(prev => ({ ...prev, first: e.target.value }))}
                                className="admin-input"
                              />
                            </div>
                            <div className="admin-reward-input">
                              <label>🥈 2nd Place</label>
                              <input 
                                type="text"
                                placeholder="e.g., $25 Shuffle Balance"
                                value={adminRewards.second}
                                onChange={(e) => setAdminRewards(prev => ({ ...prev, second: e.target.value }))}
                                className="admin-input"
                              />
                            </div>
                            <div className="admin-reward-input">
                              <label>🥉 3rd Place</label>
                              <input 
                                type="text"
                                placeholder="e.g., $10 Shuffle Balance"
                                value={adminRewards.third}
                                onChange={(e) => setAdminRewards(prev => ({ ...prev, third: e.target.value }))}
                                className="admin-input"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="admin-action-buttons">
                          <button 
                            className="admin-action-btn open"
                            onClick={() => adminOpenPredictions(bonusHuntStats.activeHunt.bonusHuntId, bonusHuntStats.activeHunt.infoStartCost)}
                          >
                            🟢 Open Predictions for Current Hunt
                          </button>
                        </div>
                        
                        <p className="admin-hint">Hunt ID: {bonusHuntStats.activeHunt.bonusHuntId} • Start: ${bonusHuntStats.activeHunt.infoStartCost?.toLocaleString()}</p>
                      </>
                    ) : (
                      <p className="admin-no-hunt">No active hunt detected. Start a hunt to enable prediction controls.</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Hunt Management Tab */}
            {adminTab === 'hunts' && (
              <>
                <div className="admin-section">
                  <h2>Hunt Management ({adminData.hunts.length} total)</h2>
                  <div className="admin-hunts-list">
                    {adminData.hunts.length > 0 ? (
                      adminData.hunts
                        .slice((adminHuntsPage - 1) * adminItemsPerPage, adminHuntsPage * adminItemsPerPage)
                        .map((hunt, idx) => {
                        // Check if this hunt matches the current active hunt from Myrtle API
                        const isCurrentActiveHunt = bonusHuntStats.activeHunt && 
                          hunt.hunt_id === bonusHuntStats.activeHunt.bonusHuntId?.toString();
                        // Use live start balance if available, otherwise use database value
                        const displayStartBalance = isCurrentActiveHunt && bonusHuntStats.activeHunt.infoStartCost
                          ? bonusHuntStats.activeHunt.infoStartCost
                          : hunt.start_balance;
                        
                        return (
                        <div key={idx} className={`admin-hunt-item ${hunt.status}`}>
                          <div className="admin-hunt-header">
                            <div className="admin-hunt-info">
                              <span className="admin-hunt-id">Hunt #{hunt.hunt_id}</span>
                              <span className={`admin-hunt-status ${hunt.status}`}>{hunt.status.toUpperCase()}</span>
                              <span className="admin-hunt-predictions">{hunt.prediction_count} predictions</span>
                            </div>
                            <div className="admin-hunt-balances">
                              <span className="start-balance">
                                Start: <strong>${displayStartBalance?.toLocaleString() || 'N/A'}</strong>
                                <button 
                                  className="edit-balance-btn"
                                  onClick={() => adminEditHuntStartBalance(hunt.hunt_id, displayStartBalance || hunt.start_balance)}
                                  title="Edit start balance"
                                >
                                  ✏️
                                </button>
                              </span>
                              {hunt.end_balance && <span className="end-balance">End: <strong>${hunt.end_balance?.toLocaleString()}</strong></span>}
                            </div>
                          </div>
                          
                          {/* Hunt Controls - Different based on status */}
                          <div className="admin-hunt-controls">
                            {/* View buttons - always visible */}
                            <div className="admin-hunt-view-actions">
                              <button 
                                className="admin-small-btn"
                                onClick={() => { adminViewHuntPredictions(hunt.hunt_id); navigateTo('predictions'); }}
                              >
                                📋 Predictions
                              </button>
                              {(hunt.status === 'completed' || hunt.status === 'closed') && (
                                <button 
                                  className="admin-small-btn winners"
                                  onClick={() => { navigateTo('predictionresults'); }}
                                >
                                  🏆 Winners
                                </button>
                              )}
                              {/* Fix Status button - for hunts that were accidentally reopened */}
                              {(hunt.status === 'active' || hunt.status === 'paused') && (
                                <button 
                                  className="admin-small-btn"
                                  style={{ background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' }}
                                  onClick={async () => {
                                    if (window.confirm(`Fix Hunt #${hunt.hunt_id} status?\n\nIf this hunt was accidentally reopened but already has winners, this will set its status back to 'completed' WITHOUT deleting winners or triggering new win popups.`)) {
                                      try {
                                        const token = localStorage.getItem('auth_token');
                                        const response = await fetch(`${BACKEND_API_URL}/api/admin/hunt/fix-status/${hunt.hunt_id}`, {
                                          method: 'POST',
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        const data = await response.json();
                                        if (response.ok) {
                                          alert(`✅ ${data.message}`);
                                          fetchAllHunts();
                                        } else {
                                          alert(data.error || 'Failed to fix status');
                                        }
                                      } catch (e) {
                                        alert('Error: ' + e.message);
                                      }
                                    }
                                  }}
                                  title="Fix status if hunt was accidentally reopened"
                                >
                                  🔧 Fix Status
                                </button>
                              )}
                            </div>
                            
                            {/* Status controls */}
                            {hunt.status === 'active' && (
                              <div className="admin-hunt-status-actions">
                                <button 
                                  className="admin-small-btn pause"
                                  onClick={() => adminPausePredictions(hunt.hunt_id)}
                                >
                                  ⏸️ Pause Predictions
                                </button>
                                <button 
                                  className="admin-small-btn delete"
                                  onClick={() => adminDeleteHunt(hunt.hunt_id, hunt.prediction_count)}
                                >
                                  🗑️ Delete Hunt
                                </button>
                              </div>
                            )}
                            
                            {hunt.status === 'paused' && (
                              <div className="admin-hunt-status-actions">
                                <button 
                                  className="admin-small-btn open"
                                  onClick={() => adminOpenPredictions(hunt.hunt_id)}
                                >
                                  🟢 Reopen Predictions
                                </button>
                                <div className="admin-close-hunt-inline">
                                  <input 
                                    type="number"
                                    placeholder="End balance..."
                                    className="admin-input-small"
                                    id={`end-balance-${hunt.hunt_id}`}
                                  />
                                  <button 
                                    className="admin-small-btn close"
                                    onClick={() => {
                                      const input = document.getElementById(`end-balance-${hunt.hunt_id}`);
                                      if (input && input.value) {
                                        adminCloseHunt(hunt.hunt_id, input.value);
                                      } else {
                                        alert('Please enter the end balance');
                                      }
                                    }}
                                  >
                                    🏁 Close & Calculate Winners
                                  </button>
                                </div>
                                <button 
                                  className="admin-small-btn delete"
                                  onClick={() => adminDeleteHunt(hunt.hunt_id, hunt.prediction_count)}
                                >
                                  🗑️ Delete Hunt
                                </button>
                              </div>
                            )}
                            
                            {hunt.status === 'completed' && (
                              <div className="admin-hunt-status-actions">
                                <span className="hunt-completed-badge">✅ Completed</span>
                                <button 
                                  className="admin-small-btn delete"
                                  onClick={async () => {
                                    if (window.confirm(`⚠️ Reset Hunt #${hunt.hunt_id}?\n\nThis will DELETE all winners and reset the hunt status so you can close it again with a new end balance.\n\nThis action cannot be undone!`)) {
                                      try {
                                        const token = localStorage.getItem('auth_token');
                                        const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/all/${hunt.hunt_id}`, {
                                          method: 'DELETE',
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        const data = await response.json();
                                        if (response.ok) {
                                          alert(`✅ ${data.message}\n\nHunt has been reset to active status.`);
                                          fetchAllHunts();
                                        } else {
                                          alert(data.error || 'Failed to reset hunt');
                                        }
                                      } catch (e) {
                                        alert('Error resetting hunt');
                                      }
                                    }
                                  }}
                                >
                                  🔄 Reset Hunt (Delete Winners)
                                </button>
                              </div>
                            )}

                            {hunt.status === 'closed' && (
                              <div className="admin-hunt-status-actions">
                                <span className="hunt-closed-badge">🔒 Closed</span>
                                <button 
                                  className="admin-small-btn delete"
                                  onClick={async () => {
                                    if (window.confirm(`⚠️ Reset Hunt #${hunt.hunt_id}?\n\nThis will DELETE all winners and reset the hunt status so you can close it again with a new end balance.\n\nThis action cannot be undone!`)) {
                                      try {
                                        const token = localStorage.getItem('auth_token');
                                        const response = await fetch(`${BACKEND_API_URL}/api/admin/winners/all/${hunt.hunt_id}`, {
                                          method: 'DELETE',
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        const data = await response.json();
                                        if (response.ok) {
                                          alert(`✅ ${data.message}\n\nHunt has been reset to active status.`);
                                          fetchAllHunts();
                                        } else {
                                          alert(data.error || 'Failed to reset hunt');
                                        }
                                      } catch (e) {
                                        alert('Error resetting hunt');
                                      }
                                    }
                                  }}
                                >
                                  🔄 Reset Hunt (Delete Winners)
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })
                    ) : (
                      <p>No hunts found.</p>
                    )}
                  </div>
                  
                  {/* Hunts Pagination */}
                  {adminData.hunts.length > adminItemsPerPage && (
                    <div className="admin-pagination">
                      <button 
                        className="pagination-btn"
                        onClick={() => setAdminHuntsPage(p => Math.max(1, p - 1))}
                        disabled={adminHuntsPage === 1}
                      >
                        ← Previous
                      </button>
                      <span className="pagination-info">
                        Page {adminHuntsPage} of {Math.ceil(adminData.hunts.length / adminItemsPerPage)}
                      </span>
                      <button 
                        className="pagination-btn"
                        onClick={() => setAdminHuntsPage(p => Math.min(Math.ceil(adminData.hunts.length / adminItemsPerPage), p + 1))}
                        disabled={adminHuntsPage >= Math.ceil(adminData.hunts.length / adminItemsPerPage)}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>

                {/* Selected Hunt Predictions */}
                {adminData.selectedHunt && (
                  <div className="admin-section">
                    <h2>Predictions for Hunt #{adminData.selectedHunt.hunt_id}</h2>
                    <div className="admin-predictions-table">
                      <div className="admin-table-header">
                        <span>User</span>
                        <span>Prediction</span>
                        <span>Time</span>
                        <span>Actions</span>
                      </div>
                      {adminData.huntPredictions.map((pred, idx) => (
                        <div key={idx} className="admin-table-row">
                          <div className="admin-pred-user">
                            <img src={pred.profile_image_url} alt={pred.display_name} className="admin-pred-avatar" />
                            <span>{pred.display_name || pred.username}</span>
                          </div>
                          <span className="admin-pred-amount">${parseFloat(pred.predicted_balance).toLocaleString()}</span>
                          <span className="admin-pred-time">{new Date(pred.created_at).toLocaleString()}</span>
                          <button 
                            className="admin-delete-btn"
                            onClick={() => adminDeletePrediction(pred.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Winners Management */}
                {adminData.selectedHuntWinners && (
                  <div className="admin-section">
                    <div className="admin-section-header">
                      <h2>🏆 Winners for Hunt #{adminData.selectedHuntWinners}</h2>
                      <button 
                        className="admin-small-btn recalc"
                        onClick={() => adminRecalculateWinners(adminData.selectedHuntWinners)}
                      >
                        🔄 Recalculate Winners
                      </button>
                    </div>
                    
                    {adminData.huntWinners.length > 0 ? (
                      <div className="admin-winners-table">
                        <div className="admin-table-header">
                          <span>Rank</span>
                          <span>User</span>
                          <span>Prediction</span>
                          <span>Difference</span>
                          <span>Reward</span>
                          <span>Actions</span>
                        </div>
                        {adminData.huntWinners.map((winner, idx) => (
                          <div key={idx} className={`admin-table-row winner-rank-${winner.rank}`}>
                            <span className="admin-winner-rank">
                              {winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : '🥉'} #{winner.rank}
                            </span>
                            <div className="admin-pred-user">
                              <img src={winner.profile_image_url} alt={winner.display_name} className="admin-pred-avatar" />
                              <span>{winner.display_name || winner.username}</span>
                            </div>
                            <span className="admin-pred-amount">${parseFloat(winner.predicted_balance).toLocaleString()}</span>
                            <span className="admin-winner-diff">${parseFloat(winner.difference).toLocaleString()}</span>
                            <input 
                              type="text"
                              className="admin-reward-edit"
                              defaultValue={winner.reward}
                              onBlur={(e) => {
                                if (e.target.value !== winner.reward) {
                                  adminUpdateWinnerReward(winner.id, e.target.value, adminData.selectedHuntWinners);
                                }
                              }}
                            />
                            <button 
                              className="admin-delete-btn disqualify"
                              onClick={() => adminDisqualifyWinner(winner.id, adminData.selectedHuntWinners)}
                              title="Disqualify Winner"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="admin-no-winners">No winners recorded for this hunt yet.</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Pending Rewards Tab */}
            {adminTab === 'featuredPosts' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>📣 Featured Social Posts</h2>
                </div>
                <AdminFeaturedPosts apiUrl={BACKEND_API_URL} getToken={getAuthToken} />
              </div>
            )}

            {adminTab === 'rewardsledger' && isAdmin && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>🧾 Rewards Ledger</h2>
                </div>
                <AdminRewardsLedger apiUrl={BACKEND_API_URL} getToken={getAuthToken} />
              </div>
            )}

            {adminTab === 'referrals' && isAdmin && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>🤝 Recruit &amp; Earn</h2>
                </div>
                <AdminReferrals apiUrl={BACKEND_API_URL} getToken={getAuthToken} onChanged={fetchPendingCounts} />
              </div>
            )}

            {adminTab === 'checklist' && isAdmin && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>✅ Newcomer Checklist</h2>
                  <p style={{fontSize:'0.78rem', color:'#71717a', margin:'0.3rem 0 0'}}>Pending social-follow verifications. Users get 500 points once all 7 checklist steps are verified.</p>
                </div>
                <AdminChecklist apiUrl={BACKEND_API_URL} getToken={getAuthToken} onChanged={fetchPendingCounts} />
              </div>
            )}

            {adminTab === 'clips' && isAdmin && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>✂️ Clip &amp; Earn</h2>
                </div>
                <AdminClips apiUrl={BACKEND_API_URL} getToken={getAuthToken} onChanged={fetchPendingCounts} />
              </div>
            )}

            {adminTab === 'lossback' && isAdmin && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>💸 10% Lossback</h2>
                </div>
                <AdminLossback apiUrl={BACKEND_API_URL} getToken={getAuthToken} onChanged={fetchPendingCounts} />
              </div>
            )}

            {adminTab === 'rewards' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>💰 Pending Prediction Rewards ({adminData.pendingClaims.length})</h2>
                </div>
                {adminData.pendingClaims.length > 0 ? (
                  <div className="admin-claims-table">
                    <div className="admin-table-header">
                      <span>User</span>
                      <span>Platform</span>
                      <span>Casino Username</span>
                      <span>Prize</span>
                      <span>Rank</span>
                      <span>Actions</span>
                    </div>
                    {adminData.pendingClaims.map((claim, idx) => (
                      <div key={idx} className="admin-table-row">
                        <div className="admin-pred-user">
                          <img src={claim.profile_image_url} alt={claim.display_name} className="admin-pred-avatar" />
                          <span>{claim.display_name || claim.username}</span>
                        </div>
                        <span className={`admin-platform-badge ${claim.platform || 'rainbet'}`}>
                          {claim.upgraded ? '⬆️ Shuffle' : (claim.platform || 'rainbet') === 'rainbet' ? '🟠 Rainbet' : '🟣 Shuffle'}
                        </span>
                        <span className="admin-shuffle-user">
                          {claim.casino_username || 'N/A'}
                          {claim.upgraded && <span className="upgraded-note" title={`Originally claimed as: ${claim.claimed_username}`}> ✨</span>}
                        </span>
                        <span className="admin-pred-amount">{claim.actual_reward || claim.reward}</span>
                        <span>{claim.rank === 1 ? '🥇' : claim.rank === 2 ? '🥈' : '🥉'}</span>
                        <button
                          className="admin-small-btn paid"
                          onClick={() => adminMarkClaimPaid(claim)}
                        >
                          ✅ Mark Paid
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="admin-no-data">No pending rewards - all caught up! 🎉</p>
                )}
              </div>
            )}

            {/* Wager Claims Tab */}
            {adminTab === 'wagerClaims' && (
              <div className="admin-section">
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem'}}>
                  <h2 style={{margin:0}}>🎫 Wager Reward Claims ({adminWagerClaims.length})</h2>
                  <button onClick={fetchAdminWagerClaims} style={{padding:'0.3rem 0.6rem', background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'0.25rem', color:'#c4b5fd', fontSize:'0.75rem', cursor:'pointer'}}>🔄 Refresh</button>
                </div>
                
                {/* Add claim form */}
                <div style={{background:'rgba(14,14,18,0.6)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'0.5rem', padding:'1rem', marginBottom:'1.5rem'}}>
                  <div style={{fontSize:'0.9rem', fontWeight:600, color:'#e2e8f0', marginBottom:'0.75rem'}}>Mark Tier as Claimed</div>
                  <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center'}}>
                    <input id="wc-username" placeholder="Twitch or Shuffle username" style={{flex:1, minWidth:'150px', padding:'0.4rem 0.7rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.85rem'}} />
                    <select id="wc-tier" style={{padding:'0.4rem 0.7rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.85rem'}}>
                      <option value="">Select tier...</option>
                      {['Wood', 'Bronze', 'Silver', 'Gold', 'Emerald', 'Diamond', 'Eternal'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                      {/* Grind area tiers */}
                      {[...Array(36)].map((_, i) => (
                        <option key={`grind_${i+1}`} value={`Grind_${i+1}`}>Grind #{i+1} (${(1 + (i+1) * 0.25).toFixed(2)}M)</option>
                      ))}
                    </select>
                    <input id="wc-notes" placeholder="Notes (optional)" style={{minWidth:'120px', padding:'0.4rem 0.7rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'0.25rem', color:'#e2e8f0', fontSize:'0.85rem'}} />
                    <button onClick={async () => {
                      const username = document.getElementById('wc-username').value.trim();
                      const tierName = document.getElementById('wc-tier').value;
                      const notes = document.getElementById('wc-notes').value.trim();
                      if (!username || !tierName) return alert('Username and tier required');
                      const token = localStorage.getItem('auth_token');
                      const res = await fetch(`${BACKEND_API_URL}/api/admin/wager-claims`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ username, tierName, notes })
                      });
                      const d = await res.json();
                      if (res.ok) {
                        document.getElementById('wc-username').value = '';
                        document.getElementById('wc-tier').value = '';
                        document.getElementById('wc-notes').value = '';
                        fetchAdminWagerClaims();
                        alert(`✅ Marked ${tierName} as claimed for ${d.user?.display_name || username}`);
                      } else {
                        alert(d.error || 'Failed');
                      }
                    }} style={{padding:'0.4rem 0.9rem', background:'#22c55e', border:'none', borderRadius:'0.25rem', color:'#fff', fontSize:'0.85rem', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap'}}>✅ Mark Claimed</button>
                  </div>
                </div>

                {/* Claims list */}
                {adminWagerClaims.length > 0 ? (
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.85rem'}}>
                      <thead>
                        <tr style={{borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
                          <th style={{textAlign:'left', padding:'0.6rem', color:'#9ca3af', fontWeight:600}}>User</th>
                          <th style={{textAlign:'left', padding:'0.6rem', color:'#9ca3af', fontWeight:600}}>Shuffle</th>
                          <th style={{textAlign:'left', padding:'0.6rem', color:'#9ca3af', fontWeight:600}}>Tier</th>
                          <th style={{textAlign:'left', padding:'0.6rem', color:'#9ca3af', fontWeight:600}}>Notes</th>
                          <th style={{textAlign:'left', padding:'0.6rem', color:'#9ca3af', fontWeight:600}}>Claimed</th>
                          <th style={{textAlign:'left', padding:'0.6rem', color:'#9ca3af', fontWeight:600}}>By</th>
                          <th style={{padding:'0.6rem'}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminWagerClaims.map(claim => (
                          <tr key={claim.id} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                            <td style={{padding:'0.5rem 0.6rem'}}>
                              <div style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                {claim.profile_image_url && <img src={claim.profile_image_url} alt="" style={{width:24, height:24, borderRadius:'50%'}} />}
                                <span style={{color:'#e2e8f0'}}>{claim.display_name || claim.username}</span>
                              </div>
                            </td>
                            <td style={{padding:'0.5rem 0.6rem', color:'#9ca3af'}}>{claim.shuffle_username || '-'}</td>
                            <td style={{padding:'0.5rem 0.6rem'}}>
                              <span style={{padding:'0.2rem 0.5rem', background: claim.tier_name.startsWith('Grind') ? 'rgba(239,68,68,0.15)' : 'rgba(168,85,247,0.15)', border:'1px solid ' + (claim.tier_name.startsWith('Grind') ? 'rgba(239,68,68,0.3)' : 'rgba(168,85,247,0.3)'), borderRadius:'0.25rem', color: claim.tier_name.startsWith('Grind') ? '#f87171' : '#c4b5fd', fontSize:'0.8rem', fontWeight:600}}>{claim.tier_name}</span>
                            </td>
                            <td style={{padding:'0.5rem 0.6rem', color:'#6b7280', fontSize:'0.8rem', maxWidth:'150px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{claim.notes || '-'}</td>
                            <td style={{padding:'0.5rem 0.6rem', color:'#6b7280', fontSize:'0.8rem'}}>{new Date(claim.claimed_at).toLocaleDateString()}</td>
                            <td style={{padding:'0.5rem 0.6rem', color:'#6b7280', fontSize:'0.8rem'}}>{claim.verified_by_username || '-'}</td>
                            <td style={{padding:'0.5rem 0.6rem'}}>
                              <button onClick={async () => {
                                if (!window.confirm(`Remove ${claim.tier_name} claim for ${claim.display_name}?`)) return;
                                const token = localStorage.getItem('auth_token');
                                await fetch(`${BACKEND_API_URL}/api/admin/wager-claims/${claim.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                fetchAdminWagerClaims();
                              }} style={{padding:'0.25rem 0.5rem', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'0.25rem', color:'#f87171', fontSize:'0.75rem', cursor:'pointer'}}>🗑️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{color:'#6b7280', fontSize:'0.9rem', textAlign:'center', padding:'2rem'}}>No wager reward claims yet. Use the form above to mark tiers as claimed.</p>
                )}

                {/* ===== BULK ADJUSTMENTS ===== */}
                <div style={{marginTop:'2rem', borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:'1.5rem'}}>
                  <h2 style={{margin:'0 0 0.5rem 0'}}>📊 Payout Adjustments</h2>
                  <p style={{fontSize:'0.75rem', color:'#6b7280', margin:'0 0 1rem 0'}}>Bulk totals and adjustments for each category. Adjustments get added on top of tracked data. Leaderboard is a standalone total.</p>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(250px, 1fr))', gap:'0.75rem'}}>
                    {[
                      { id: 'wr-bulk', key: 'wager_rewards_bulk_adjustment', label: '🔥 Wager Rewards', desc: 'Extra on top of tracked claims', color: '#f87171', borderColor: 'rgba(239,68,68,0.3)', value: adminWagerBulkAdjustment, setter: setAdminWagerBulkAdjustment },
                      { id: 'lb-total', key: 'leaderboard_total_paid', label: '🏆 Leaderboard', desc: 'Total leaderboard prizes paid', color: '#fbbf24', borderColor: 'rgba(245,158,11,0.3)', value: adminLeaderboardTotal, setter: setAdminLeaderboardTotal },
                      { id: 'gw-bulk', key: 'giveaways_bulk_adjustment', label: '🎁 Giveaways', desc: 'Extra on top of tracked giveaways', color: '#60a5fa', borderColor: 'rgba(96,165,250,0.3)', value: adminGiveawaysBulk, setter: setAdminGiveawaysBulk },
                      { id: 'pr-bulk', key: 'predictions_bulk_adjustment', label: '🎯 Predictions', desc: 'Extra on top of tracked predictions', color: '#a78bfa', borderColor: 'rgba(139,92,246,0.3)', value: adminPredictionsBulk, setter: setAdminPredictionsBulk },
                      { id: 'sp-bulk', key: 'slotpicks_bulk_adjustment', label: '🎰 Slot Calls', desc: 'Extra on top of tracked slot picks', color: '#34d399', borderColor: 'rgba(52,211,153,0.3)', value: adminSlotPicksBulk, setter: setAdminSlotPicksBulk },
                    ].map(item => (
                      <div key={item.id} style={{background:'rgba(14,14,18,0.6)', border:`1px solid ${item.borderColor}`, borderRadius:'0.5rem', padding:'0.75rem'}}>
                        <div style={{fontSize:'0.8rem', fontWeight:600, color: item.color, marginBottom:'0.4rem'}}>{item.label}</div>
                        <div style={{display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.3rem'}}>
                          <span style={{fontWeight:700, color: item.color}}>$</span>
                          <input 
                            id={item.id} 
                            type="number" 
                            defaultValue={item.value} 
                            key={item.value}
                            placeholder="0" 
                            style={{flex:1, padding:'0.35rem 0.5rem', background:'rgba(255,255,255,0.06)', border:`1px solid ${item.borderColor}`, borderRadius:'0.25rem', color: item.color, fontSize:'0.95rem', fontWeight:700}} 
                          />
                          <button onClick={async () => {
                            const value = document.getElementById(item.id).value.trim();
                            if (value === '') return;
                            const token = localStorage.getItem('auth_token');
                            const res = await fetch(`${BACKEND_API_URL}/api/admin/settings`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ key: item.key, value })
                            });
                            if (res.ok) {
                              item.setter(value);
                              alert(`✅ ${item.label} updated to $${parseFloat(value).toLocaleString()}`);
                              fetchPayoutStats();
                            } else {
                              alert('Failed to save');
                            }
                          }} style={{padding:'0.35rem 0.6rem', background: item.color, border:'none', borderRadius:'0.25rem', color:'#000', fontSize:'0.75rem', fontWeight:700, cursor:'pointer'}}>💾</button>
                        </div>
                        <div style={{fontSize:'0.65rem', color:'#71717a'}}>{item.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Paid Rewards Tab */}
            {adminTab === 'paidRewards' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>✅ Paid Prediction Rewards ({adminData.paidClaims.length})</h2>
                </div>

                <PayoutSummaryBar metric="predictions" range={adminPayoutRange} onRange={onPayoutRange} summary={adminPayoutSummary} loading={adminPayoutLoading} />

                {/* Search Bar */}
                <div className="admin-user-search">
                  <input 
                    type="text"
                    placeholder="Search by Twitch, Shuffle, or Discord username..."
                    value={adminPaidRewardsSearch}
                    onChange={(e) => { setAdminPaidRewardsSearch(e.target.value); setAdminPaidRewardsPage(1); }}
                    className="admin-search-input"
                  />
                  {adminPaidRewardsSearch && (
                    <button 
                      className="admin-search-clear"
                      onClick={() => setAdminPaidRewardsSearch('')}
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                {(() => {
                  const filteredClaims = adminData.paidClaims.filter(claim => {
                    if (!adminPaidRewardsSearch) return true;
                    const search = adminPaidRewardsSearch.toLowerCase();
                    return (claim.display_name || '').toLowerCase().includes(search) ||
                           (claim.username || '').toLowerCase().includes(search) ||
                           (claim.shuffle_username || '').toLowerCase().includes(search);
                  });
                  const paginatedClaims = filteredClaims.slice(
                    (adminPaidRewardsPage - 1) * adminItemsPerPage, 
                    adminPaidRewardsPage * adminItemsPerPage
                  );
                  const totalPages = Math.ceil(filteredClaims.length / adminItemsPerPage);
                  
                  return (
                    <>
                      {paginatedClaims.length > 0 ? (
                        <div className="admin-claims-table">
                          <div className="admin-table-header">
                            <span>User</span>
                            <span>Shuffle Username</span>
                            <span>Prize</span>
                            <span>Rank</span>
                            <span>Paid Date</span>
                          </div>
                          {paginatedClaims.map((claim, idx) => (
                            <div key={idx} className="admin-table-row paid-row">
                              <div className="admin-pred-user">
                                <img src={claim.profile_image_url} alt={claim.display_name} className="admin-pred-avatar" />
                                <span>{claim.display_name || claim.username}</span>
                              </div>
                              <span className="admin-shuffle-user">{claim.shuffle_username}</span>
                              <span className="admin-pred-amount">{claim.actual_reward || claim.reward}</span>
                              <span>{claim.rank === 1 ? '🥇' : claim.rank === 2 ? '🥈' : '🥉'}</span>
                              <span className="admin-paid-date">{claim.paid_at ? new Date(claim.paid_at).toLocaleDateString() : '-'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="admin-no-data">{adminPaidRewardsSearch ? 'No matching rewards found' : 'No paid rewards yet'}</p>
                      )}
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="admin-pagination">
                          <button 
                            className="pagination-btn"
                            onClick={() => setAdminPaidRewardsPage(p => Math.max(1, p - 1))}
                            disabled={adminPaidRewardsPage === 1}
                          >
                            ← Previous
                          </button>
                          <span className="pagination-info">
                            Page {adminPaidRewardsPage} of {totalPages}
                          </span>
                          <button 
                            className="pagination-btn"
                            onClick={() => setAdminPaidRewardsPage(p => Math.min(totalPages, p + 1))}
                            disabled={adminPaidRewardsPage >= totalPages}
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Giveaways Tab */}
            {adminTab === 'giveaways' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>🎁 Giveaways</h2>
                </div>
                
                {/* Add Giveaway Form */}
                <div className="admin-giveaway-form">
                  <h4>Add New Giveaway</h4>
                  <div className="giveaway-form-row">
                    <select 
                      value={newGiveaway.recipientType}
                      onChange={(e) => setNewGiveaway(prev => ({ ...prev, recipientType: e.target.value }))}
                      className="admin-select"
                    >
                      <option value="shuffle">Shuffle</option>
                      <option value="crypto">Crypto</option>
                    </select>
                    
                    {newGiveaway.recipientType === 'shuffle' ? (
                      <input 
                        type="text"
                        placeholder="Shuffle username..."
                        value={newGiveaway.shuffleUsername}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, shuffleUsername: e.target.value }))}
                        className="admin-input"
                      />
                    ) : (
                      <input 
                        type="text"
                        placeholder="Crypto address..."
                        value={newGiveaway.cryptoAddress}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, cryptoAddress: e.target.value }))}
                        className="admin-input"
                      />
                    )}
                    
                    <input 
                      type="number"
                      placeholder="Amount ($)..."
                      value={newGiveaway.amount}
                      onChange={(e) => setNewGiveaway(prev => ({ ...prev, amount: e.target.value }))}
                      className="admin-input amount-input"
                    />
                  </div>
                  <div className="giveaway-form-row">
                    <input 
                      type="text"
                      placeholder="Description (optional)..."
                      value={newGiveaway.description}
                      onChange={(e) => setNewGiveaway(prev => ({ ...prev, description: e.target.value }))}
                      className="admin-input description-input"
                    />
                    <button 
                      className="admin-action-btn add-giveaway"
                      onClick={adminAddGiveaway}
                    >
                      ➕ Add Giveaway
                    </button>
                  </div>
                </div>

                {/* Pending Giveaways */}
                <h4>Pending Giveaways ({adminData.pendingGiveaways.length})</h4>
                {adminData.pendingGiveaways.length > 0 ? (
                  <div className="admin-giveaways-table">
                    <div className="admin-table-header">
                      <span>Recipient</span>
                      <span>Amount</span>
                      <span>Description</span>
                      <span>Actions</span>
                    </div>
                    {adminData.pendingGiveaways.map((giveaway, idx) => (
                      <div key={idx} className="admin-table-row">
                        <span className="admin-shuffle-user">
                          {giveaway.recipient_type === 'shuffle' 
                            ? `${giveaway.shuffle_username || 'N/A'}` 
                            : `${giveaway.crypto_address ? giveaway.crypto_address.slice(0, 10) + '...' : 'N/A'}`}
                        </span>
                        <span className="admin-giveaway-amount">${parseFloat(giveaway.amount).toLocaleString()}</span>
                        <span className="admin-giveaway-desc">{giveaway.description || '-'}</span>
                        <div className="admin-giveaway-actions">
                          <button 
                            className="admin-small-btn paid"
                            onClick={() => adminMarkGiveawayPaid(giveaway.id)}
                          >
                            ✅ Paid
                          </button>
                          <button 
                            className="admin-delete-btn"
                            onClick={() => adminDeleteGiveaway(giveaway.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="admin-no-data">No pending giveaways</p>
                )}

                {/* ===== CLAIMABLE GIVEAWAYS (User-Claim System) ===== */}
                <div style={{marginTop:'2rem', borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:'1.5rem'}}>
                  <h2 style={{margin:'0 0 1rem 0'}}>🎟️ Claimable Giveaways</h2>
                  <p style={{fontSize:'0.75rem', color:'#6b7280', margin:'-0.5rem 0 1rem 0'}}>Create giveaways that winners must claim on the site. Collects fingerprints for fraud detection.</p>
                  
                  {/* Create new claimable giveaway */}
                  <div style={{background:'rgba(14,14,18,0.6)', border:'1px solid rgba(139,92,246,0.15)', borderRadius:'8px', padding:'1rem', marginBottom:'1rem'}}>
                    <div style={{fontSize:'0.85rem', fontWeight:600, marginBottom:'0.6rem', color:'#e2e8f0'}}>Add Claimable Giveaway</div>
                    <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'flex-end'}}>
                      <div style={{minWidth:'100px'}}>
                        <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>Platform</div>
                        <select 
                          value={newClaimableGiveaway.type}
                          onChange={e => setNewClaimableGiveaway(p => ({...p, type: e.target.value}))}
                          style={{padding:'0.45rem 0.5rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#fff', fontSize:'0.85rem', width:'100%'}}
                        >
                          <option value="twitch">Twitch</option>
                          <option value="discord">Discord</option>
                          <option value="kick">Kick</option>
                        </select>
                      </div>
                      <div style={{flex:1, minWidth:'140px'}}>
                        <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>{newClaimableGiveaway.type === 'discord' ? 'Discord Username' : 'Twitch Username'}</div>
                        <input 
                          type="text"
                          placeholder={newClaimableGiveaway.type === 'discord' ? 'user#1234 or username' : 'twitchuser'}
                          value={newClaimableGiveaway.identifier}
                          onChange={e => setNewClaimableGiveaway(p => ({...p, identifier: e.target.value}))}
                          style={{padding:'0.45rem 0.6rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#fff', fontSize:'0.85rem', width:'100%', boxSizing:'border-box'}}
                        />
                      </div>
                      <div style={{width:'100px'}}>
                        <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>Amount ($)</div>
                        <input 
                          type="number"
                          placeholder="100"
                          value={newClaimableGiveaway.amount}
                          onChange={e => setNewClaimableGiveaway(p => ({...p, amount: e.target.value}))}
                          style={{padding:'0.45rem 0.6rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#22c55e', fontSize:'0.85rem', fontWeight:700, width:'100%', boxSizing:'border-box'}}
                        />
                      </div>
                      <div style={{flex:1, minWidth:'140px'}}>
                        <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>Description (optional)</div>
                        <input 
                          type="text"
                          placeholder="Stream giveaway, etc."
                          value={newClaimableGiveaway.description}
                          onChange={e => setNewClaimableGiveaway(p => ({...p, description: e.target.value}))}
                          style={{padding:'0.45rem 0.6rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#fff', fontSize:'0.85rem', width:'100%', boxSizing:'border-box'}}
                        />
                      </div>
                      <button 
                        onClick={async () => {
                          if (!newClaimableGiveaway.identifier || !newClaimableGiveaway.amount) return alert('Username and amount required');
                          const token = getAuthToken();
                          const res = await fetch(`${BACKEND_API_URL}/api/admin/claimable-giveaways`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify(newClaimableGiveaway)
                          });
                          if (res.ok) {
                            setNewClaimableGiveaway({ type: 'twitch', identifier: '', amount: '', description: '' });
                            fetchAdminClaimableGiveaways(adminClaimableFilter);
                          } else {
                            const data = await res.json();
                            alert(data.error || 'Failed');
                          }
                        }}
                        style={{padding:'0.45rem 1rem', background:'#8b5cf6', border:'none', borderRadius:'6px', color:'#fff', fontSize:'0.85rem', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap'}}
                      >➕ Add</button>
                    </div>
                  </div>

                  {/* Filter tabs */}
                  <div style={{display:'flex', gap:'0.35rem', marginBottom:'0.75rem', flexWrap:'wrap'}}>
                    {[
                      { label: 'All', value: '', color: '#a1a1aa' },
                      { label: `Unclaimed (${adminClaimableCounts.unclaimed || 0})`, value: 'unclaimed', color: '#a78bfa' },
                      { label: `Claimed (${adminClaimableCounts.claimed || 0})`, value: 'claimed', color: '#fbbf24' },
                      { label: `Paid (${adminClaimableCounts.paid || 0})`, value: 'paid', color: '#22c55e' },
                      { label: `Denied (${adminClaimableCounts.denied || 0})`, value: 'denied', color: '#f87171' },
                    ].map(f => (
                      <button key={f.value} onClick={() => { setAdminClaimableFilter(f.value); fetchAdminClaimableGiveaways(f.value); }}
                        style={{padding:'0.3rem 0.7rem', background: adminClaimableFilter === f.value ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.04)', border:`1px solid ${adminClaimableFilter === f.value ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'6px', color: adminClaimableFilter === f.value ? '#fff' : f.color, fontSize:'0.75rem', fontWeight:600, cursor:'pointer'}}
                      >{f.label}</button>
                    ))}
                  </div>

                  {/* Claimable giveaways list */}
                  {adminClaimableGiveaways.length > 0 ? (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.35rem'}}>
                      {adminClaimableGiveaways.map(g => (
                        <div key={g.id} style={{
                          display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.55rem 0.75rem',
                          background: g.status === 'claimed' ? 'rgba(245,158,11,0.05)' : g.status === 'paid' ? 'rgba(34,197,94,0.05)' : g.status === 'denied' ? 'rgba(239,68,68,0.04)' : 'rgba(14,14,18,0.6)',
                          border: `1px solid ${g.status === 'claimed' ? 'rgba(245,158,11,0.2)' : g.status === 'paid' ? 'rgba(34,197,94,0.15)' : g.status === 'denied' ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)'}`,
                          borderRadius:'8px', flexWrap:'wrap',
                          opacity: g.status === 'denied' ? 0.6 : 1
                        }}>
                          <span style={{fontSize:'0.9rem'}}>{g.type === 'discord' ? '💬' : g.type === 'kick' ? '🟢' : '📺'}</span>
                          <span style={{fontSize:'0.8rem', fontWeight:600, color: g.status === 'denied' ? '#71717a' : '#e2e8f0', minWidth:'100px', textDecoration: g.status === 'denied' ? 'line-through' : 'none'}}>{g.identifier}</span>
                          <span style={{fontSize:'0.85rem', fontWeight:800, color: g.status === 'denied' ? '#71717a' : '#22c55e', textDecoration: g.status === 'denied' ? 'line-through' : 'none'}}>${parseFloat(g.amount).toLocaleString()}</span>
                          {g.description && <span style={{fontSize:'0.7rem', color:'#71717a'}}>— {g.description}</span>}
                          
                          <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:'0.4rem'}}>
                            {g.status === 'unclaimed' && (
                              <span style={{fontSize:'0.7rem', padding:'0.15rem 0.4rem', borderRadius:'4px', background:'rgba(139,92,246,0.1)', color:'#a78bfa', fontWeight:600}}>Unclaimed</span>
                            )}
                            {g.status === 'claimed' && (
                              <>
                                <span style={{fontSize:'0.7rem', color:'#fbbf24', fontWeight:600}}>
                                  Claimed by {g.claimed_by_name} → {g.payout_method === 'crypto' ? (
                                    <span style={{color:'#a78bfa'}}>
                                      🪙 {g.crypto_coin}{' '}
                                      <span
                                        title="Click to copy"
                                        onClick={() => { navigator.clipboard?.writeText(g.crypto_address); }}
                                        style={{fontFamily:'monospace', wordBreak:'break-all', cursor:'copy', textDecoration:'underline dotted'}}
                                      >{g.crypto_address}</span>
                                    </span>
                                  ) : (
                                    <span style={{color:'#a78bfa'}}>{g.shuffle_username}</span>
                                  )}
                                </span>
                                <button onClick={async () => {
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/claimable-giveaways/${g.id}/pay`, {
                                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (res.ok) fetchAdminClaimableGiveaways(adminClaimableFilter);
                                }} style={{padding:'0.2rem 0.5rem', background:'#22c55e', border:'none', borderRadius:'4px', color:'#000', fontSize:'0.7rem', fontWeight:700, cursor:'pointer'}}>💰 Pay</button>
                                <button onClick={async () => {
                                  if (!confirm('Reject this claim? It will go back to unclaimed.')) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/claimable-giveaways/${g.id}/reject`, {
                                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (res.ok) fetchAdminClaimableGiveaways(adminClaimableFilter);
                                }} style={{padding:'0.2rem 0.5rem', background:'rgba(239,68,68,0.2)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'4px', color:'#f87171', fontSize:'0.7rem', fontWeight:600, cursor:'pointer'}}>✕</button>
                                <button onClick={async () => {
                                  if (!confirm(`Deny this giveaway for ${g.identifier}? This will permanently mark it as denied for rule violations. The user will see it as denied.`)) return;
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/claimable-giveaways/${g.id}/deny`, {
                                    method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (res.ok) fetchAdminClaimableGiveaways(adminClaimableFilter);
                                }} style={{padding:'0.2rem 0.5rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', fontWeight:600, cursor:'pointer'}}>🚫 Deny</button>
                              </>
                            )}
                            {g.status === 'paid' && (
                              <span style={{fontSize:'0.7rem', color:'#22c55e', fontWeight:600}}>✅ Paid → {g.payout_method === 'crypto' ? `${g.crypto_coin} ${g.crypto_address}` : g.shuffle_username}</span>
                            )}
                            {g.status === 'denied' && (
                              <span style={{fontSize:'0.7rem', color:'#f87171', fontWeight:600}}>🚫 Denied — {g.claimed_by_name} ({g.payout_method === 'crypto' ? `${g.crypto_coin} ${g.crypto_address}` : g.shuffle_username})</span>
                            )}
                            {g.status === 'unclaimed' && (
                              <button onClick={async () => {
                                if (!confirm('Delete this claimable giveaway?')) return;
                                const token = getAuthToken();
                                await fetch(`${BACKEND_API_URL}/api/admin/claimable-giveaways/${g.id}`, {
                                  method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
                                });
                                fetchAdminClaimableGiveaways(adminClaimableFilter);
                              }} style={{padding:'0.2rem 0.4rem', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.7rem', cursor:'pointer'}}>🗑️</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{color:'#71717a', fontSize:'0.8rem', padding:'1rem 0', textAlign:'center'}}>No claimable giveaways found</div>
                  )}
                </div>
              </div>
            )}
            
            {/* Paid Giveaways Tab */}
            {adminTab === 'paidGiveaways' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>✅ Paid Giveaways ({adminData.paidGiveaways.length})</h2>
                </div>

                <PayoutSummaryBar metric="giveaways" range={adminPayoutRange} onRange={onPayoutRange} summary={adminPayoutSummary} loading={adminPayoutLoading} />

                {/* Search Bar */}
                <div className="admin-user-search">
                  <input 
                    type="text"
                    placeholder="Search by Shuffle username or crypto address..."
                    value={adminGiveawaySearch}
                    onChange={(e) => { setAdminGiveawaySearch(e.target.value); setAdminPaidGiveawaysPage(1); }}
                    onKeyDown={(e) => e.key === 'Enter' && searchPaidGiveaways(adminGiveawaySearch)}
                    className="admin-search-input"
                  />
                  <button 
                    className="admin-search-btn"
                    onClick={() => searchPaidGiveaways(adminGiveawaySearch)}
                  >
                    🔍 Search
                  </button>
                  {adminGiveawaySearch && (
                    <button 
                      className="admin-search-clear"
                      onClick={() => { setAdminGiveawaySearch(''); setAdminPaidGiveawaysPage(1); fetchGiveaways('paid'); }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                {(() => {
                  const paginatedGiveaways = adminData.paidGiveaways.slice(
                    (adminPaidGiveawaysPage - 1) * adminItemsPerPage,
                    adminPaidGiveawaysPage * adminItemsPerPage
                  );
                  const totalPages = Math.ceil(adminData.paidGiveaways.length / adminItemsPerPage);
                  
                  return (
                    <>
                      {paginatedGiveaways.length > 0 ? (
                        <div className="admin-claims-table">
                          <div className="admin-table-header">
                            <span>Recipient</span>
                            <span>Amount</span>
                            <span>Description</span>
                            <span>Paid Date</span>
                          </div>
                          {paginatedGiveaways.map((giveaway, idx) => (
                            <div key={idx} className="admin-table-row paid-row">
                              <span className="admin-giveaway-recipient">
                                {giveaway.shuffle_username 
                                  ? `🎰 ${giveaway.shuffle_username}` 
                                  : `💎 ${giveaway.crypto_address?.substring(0, 12)}...`}
                              </span>
                              <span className="admin-giveaway-amount">${parseFloat(giveaway.amount).toLocaleString()}</span>
                              <span className="admin-giveaway-desc">{giveaway.description || '-'}</span>
                              <span className="admin-paid-date">
                                {giveaway.paid_at ? new Date(giveaway.paid_at).toLocaleDateString() : '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="admin-no-data">No paid giveaways found</p>
                      )}
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="admin-pagination">
                          <button 
                            className="pagination-btn"
                            onClick={() => setAdminPaidGiveawaysPage(p => Math.max(1, p - 1))}
                            disabled={adminPaidGiveawaysPage === 1}
                          >
                            ← Previous
                          </button>
                          <span className="pagination-info">
                            Page {adminPaidGiveawaysPage} of {totalPages}
                          </span>
                          <button 
                            className="pagination-btn"
                            onClick={() => setAdminPaidGiveawaysPage(p => Math.min(totalPages, p + 1))}
                            disabled={adminPaidGiveawaysPage >= totalPages}
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Unclaimed Wins Tab */}
            {adminTab === 'unclaimedWins' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>⚠️ Unclaimed Wins ({adminData.unclaimedWins.length})</h2>
                  <p className="admin-section-desc">Winners who haven't claimed their prize yet. You can manually pay users who have a casino username linked to their profile.</p>
                </div>
                <p className="admin-info-note" style={{ color: '#f59e0b', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  ⚠️ Note: "Casino Username" shows the user's currently linked username (may be Rainbet or Shuffle)
                </p>
                
                {adminData.unclaimedWins.length > 0 ? (
                  <div className="admin-claims-table">
                    <div className="admin-table-header">
                      <span>User</span>
                      <span>Hunt</span>
                      <span>Rank</span>
                      <span>Prediction</span>
                      <span>Reward</span>
                      <span>Casino Username</span>
                      <span>Actions</span>
                    </div>
                    {adminData.unclaimedWins.map((win, idx) => (
                      <div key={idx} className="admin-table-row unclaimed-row">
                        <div className="admin-pred-user">
                          <img src={win.profile_image_url} alt={win.display_name} className="admin-pred-avatar" />
                          <span>{win.display_name || win.username}</span>
                        </div>
                        <span className="admin-hunt-ref">#{win.hunt_id}</span>
                        <span className="admin-rank-badge">
                          {win.rank === 1 ? '🥇' : win.rank === 2 ? '🥈' : '🥉'}
                        </span>
                        <span className="admin-pred-amount">${parseFloat(win.predicted_balance).toLocaleString()}</span>
                        <span className="admin-reward-text">{win.reward || 'N/A'}</span>
                        <span className={`admin-shuffle-status ${win.shuffle_username ? 'linked' : 'not-linked'}`}>
                          {win.shuffle_username ? `🎰 ${win.shuffle_username}` : '❌ Not linked'}
                        </span>
                        <div className="admin-actions-cell">
                          {win.shuffle_username ? (
                            <button
                              className="admin-small-btn pay"
                              onClick={() => payUnclaimedWin(win.winner_id, win.shuffle_username, win.reward)}
                            >
                              💰 Pay
                            </button>
                          ) : (
                            <span className="admin-no-action">No username</span>
                          )}
                          <button
                            className="admin-small-btn delete"
                            title="Void this win — removes it from the queue and blocks claiming"
                            onClick={() => voidUnclaimedWin(win.winner_id, win.display_name || win.username, win.reward)}
                          >
                            🗑️ Void
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="admin-no-data">🎉 All wins have been claimed!</p>
                )}
              </div>
            )}

            {/* Slot Picks Tab */}
            {adminTab === 'slotPicks' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>🎰 Slot Picks Queue</h2>
                  <p className="admin-section-desc">
                    {adminData.slotPicksStats.pending} pending, {adminData.slotPicksStats.completed} to pay, {adminData.slotPicksStats.paid} paid (${adminData.slotPicksStats.totalPaidAmount.toLocaleString()})
                    <button 
                      className="refresh-btn"
                      onClick={() => {
                        fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
                        fetchSlotPicksStats();
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </p>
                </div>
                
                {/* Filter buttons */}
                <div className="admin-filter-buttons">
                  <button 
                    className={`admin-filter-btn ${adminSlotPicksFilter === 'pending' ? 'active' : ''}`}
                    onClick={() => {
                      setAdminSlotPicksFilter('pending');
                      setAdminSlotPicksPage(1);
                      fetchSlotPicks('pending', 1);
                    }}
                  >
                    ⏳ Pending ({adminData.slotPicksStats.pending})
                  </button>
                  <button 
                    className={`admin-filter-btn ${adminSlotPicksFilter === 'completed' ? 'active' : ''}`}
                    onClick={() => {
                      setAdminSlotPicksFilter('completed');
                      setAdminSlotPicksPage(1);
                      fetchSlotPicks('completed', 1);
                    }}
                  >
                    ✅ To Pay ({adminData.slotPicksStats.completed})
                  </button>
                  <button 
                    className={`admin-filter-btn ${adminSlotPicksFilter === 'paid' ? 'active' : ''}`}
                    onClick={() => {
                      setAdminSlotPicksFilter('paid');
                      setAdminSlotPicksPage(1);
                      fetchSlotPicks('paid', 1);
                    }}
                  >
                    💰 Paid ({adminData.slotPicksStats.paid})
                  </button>
                </div>
                
                {adminData.slotPicks.length > 0 ? (
                  <div className="admin-claims-table">
                    <div className="admin-table-header slot-picks-header">
                      <span>User</span>
                      <span>Slot Name</span>
                      <span>Shuffle</span>
                      <span>Time</span>
                      <span>Actions</span>
                    </div>
                    {adminData.slotPicks.map((pick, idx) => (
                      <div key={idx} className={`admin-table-row slot-pick-row ${pick.status}-row`}>
                        <div className="admin-pred-user">
                          {pick.profile_image_url ? (
                            <img src={pick.profile_image_url} alt={pick.twitch_display_name} className="admin-pred-avatar" />
                          ) : (
                            <div className="admin-avatar-placeholder">
                              {(pick.twitch_display_name || pick.twitch_username || '?').substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <span>{pick.twitch_display_name || pick.twitch_username}</span>
                        </div>
                        <span className="slot-pick-name">
                          {pick.slot_name}
                          <button 
                            className="edit-slot-btn"
                            onClick={async () => {
                              const newName = prompt('Edit slot name:', pick.slot_name);
                              if (newName && newName.trim() && newName !== pick.slot_name) {
                                try {
                                  const token = localStorage.getItem('auth_token');
                                  const response = await fetch(`${BACKEND_API_URL}/api/admin/slot-picks/${pick.id}/edit`, {
                                    method: 'PUT',
                                    headers: { 
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ slotName: newName.trim() })
                                  });
                                  if (response.ok) {
                                    fetchSlotPicks(adminSlotPicksFilter, adminSlotPicksPage);
                                  } else {
                                    alert('Failed to edit slot name');
                                  }
                                } catch (e) {
                                  alert('Error editing slot name');
                                }
                              }
                            }}
                            title="Edit slot name"
                            style={{ marginLeft: '0.5rem', padding: '0.15rem 0.4rem', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '4px', color: '#3b82f6', cursor: 'pointer', fontSize: '0.7rem' }}
                          >
                            ✏️
                          </button>
                        </span>
                        <span className={`admin-shuffle-status ${pick.shuffle_username ? 'linked' : 'not-linked'}`}>
                          {pick.shuffle_username ? pick.shuffle_username : '—'}
                        </span>
                        <span className="slot-pick-time">{new Date(pick.created_at).toLocaleDateString()}</span>
                        <div className="admin-actions-cell">
                          {pick.status === 'pending' && (
                            <>
                              <button 
                                className="admin-small-btn complete"
                                onClick={() => completeSlotPick(pick.id)}
                                title="Hit bonus - move to completed"
                              >
                                ✓
                              </button>
                              <button 
                                className="admin-small-btn remove"
                                onClick={() => removeSlotPick(pick.id)}
                                title="No bonus - remove"
                              >
                                ✗
                              </button>
                              <button 
                                className="admin-small-btn refund"
                                onClick={() => refundSlotPick(pick.id)}
                                title="Mark for refund"
                              >
                                ↩️
                              </button>
                            </>
                          )}
                          {pick.status === 'completed' && (
                            <button 
                              className="admin-small-btn pay"
                              onClick={() => openSlotPickPayModal(pick)}
                              title="Pay reward"
                            >
                              💰 Pay
                            </button>
                          )}
                          {pick.status === 'paid' && (
                            <span className="slot-paid-info">
                              ${parseFloat(pick.reward_amount).toLocaleString()} paid
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="admin-no-data">
                    {adminSlotPicksFilter === 'pending' && 'No pending slot picks.'}
                    {adminSlotPicksFilter === 'completed' && 'No completed slot picks to pay.'}
                    {adminSlotPicksFilter === 'paid' && 'No paid slot picks yet.'}
                  </p>
                )}

                {/* Pagination */}
                {adminSlotPicksPagination.totalPages > 1 && (
                  <div className="admin-pagination">
                    <button 
                      className="admin-page-btn"
                      onClick={() => {
                        const newPage = adminSlotPicksPage - 1;
                        setAdminSlotPicksPage(newPage);
                        fetchSlotPicks(adminSlotPicksFilter, newPage);
                      }}
                      disabled={adminSlotPicksPage === 1}
                    >
                      ← Prev
                    </button>
                    <span className="admin-page-info">
                      Page {adminSlotPicksPage} of {adminSlotPicksPagination.totalPages} ({adminSlotPicksPagination.total} total)
                    </span>
                    <button 
                      className="admin-page-btn"
                      onClick={() => {
                        const newPage = adminSlotPicksPage + 1;
                        setAdminSlotPicksPage(newPage);
                        fetchSlotPicks(adminSlotPicksFilter, newPage);
                      }}
                      disabled={adminSlotPicksPage === adminSlotPicksPagination.totalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Slot Picks Refunds Tab */}
            {adminTab === 'slotPicksRefunds' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>🔄 To Be Refunded</h2>
                  <p className="admin-section-desc">
                    Slot picks that need channel points refunded. {adminData.slotPicksStats.refundPending} pending, {adminData.slotPicksStats.refunded} completed.
                  </p>
                </div>
                
                {/* Filter buttons */}
                <div className="admin-filter-buttons">
                  <button 
                    className={`admin-filter-btn ${adminSlotPicksFilter === 'refund_pending' ? 'active' : ''}`}
                    onClick={() => {
                      setAdminSlotPicksFilter('refund_pending');
                      setAdminSlotPicksPage(1);
                      fetchSlotPicks('refund_pending', 1);
                    }}
                  >
                    ⏳ To Refund ({adminData.slotPicksStats.refundPending})
                  </button>
                  <button 
                    className={`admin-filter-btn ${adminSlotPicksFilter === 'refunded' ? 'active' : ''}`}
                    onClick={() => {
                      setAdminSlotPicksFilter('refunded');
                      setAdminSlotPicksPage(1);
                      fetchSlotPicks('refunded', 1);
                    }}
                  >
                    ✅ Refunded ({adminData.slotPicksStats.refunded})
                  </button>
                </div>
                
                {adminData.slotPicks.length > 0 ? (
                  <div className="admin-claims-table">
                    <div className="admin-table-header slot-picks-header">
                      <span>User</span>
                      <span>Slot Name</span>
                      <span>Shuffle</span>
                      <span>Time</span>
                      <span>Actions</span>
                    </div>
                    {adminData.slotPicks.map((pick, idx) => (
                      <div key={idx} className={`admin-table-row slot-pick-row ${pick.status}-row`}>
                        <div className="admin-pred-user">
                          {pick.profile_image_url ? (
                            <img src={pick.profile_image_url} alt={pick.twitch_display_name} className="admin-pred-avatar" />
                          ) : (
                            <div className="admin-avatar-placeholder">
                              {(pick.twitch_display_name || pick.twitch_username || '?').substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <span>{pick.twitch_display_name || pick.twitch_username}</span>
                        </div>
                        <span className="slot-pick-name">{pick.slot_name}</span>
                        <span className={`admin-shuffle-status ${pick.shuffle_username ? 'linked' : 'not-linked'}`}>
                          {pick.shuffle_username ? pick.shuffle_username : '—'}
                        </span>
                        <span className="slot-pick-time">{new Date(pick.created_at).toLocaleDateString()}</span>
                        <div className="admin-actions-cell">
                          {pick.status === 'refund_pending' && (
                            <button 
                              className="admin-small-btn complete"
                              onClick={() => completeRefund(pick.id)}
                              title="Mark as refunded"
                            >
                              ✓ Refunded
                            </button>
                          )}
                          {pick.status === 'refunded' && (
                            <span className="slot-refunded-info">
                              ✅ Refunded {pick.refunded_at && new Date(pick.refunded_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="admin-no-data">
                    {adminSlotPicksFilter === 'refund_pending' && 'No slot picks to refund.'}
                    {adminSlotPicksFilter === 'refunded' && 'No refunded slot picks yet.'}
                  </p>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {adminTab === 'analytics' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>📊 Shuffle Analytics</h2>
                </div>
                
                {/* Quick Period Buttons */}
                <div className="analytics-quick-periods">
                  <span className="period-label">Quick Select:</span>
                  <div className="quick-period-buttons">
                    <button 
                      className={`quick-period-btn ${analyticsMode === 'quick' && analyticsData?.period?.label === 'Today' ? 'active' : ''}`}
                      onClick={() => fetchAnalytics({ quickPeriod: 'today', page: 1 })}
                      disabled={analyticsLoading}
                    >
                      📅 Today
                    </button>
                    <button 
                      className={`quick-period-btn ${analyticsMode === 'quick' && analyticsData?.period?.label === 'Yesterday' ? 'active' : ''}`}
                      onClick={() => fetchAnalytics({ quickPeriod: 'yesterday', page: 1 })}
                      disabled={analyticsLoading}
                    >
                      ⏪ Yesterday
                    </button>
                    <button 
                      className={`quick-period-btn ${analyticsMode === 'quick' && analyticsData?.period?.label === 'This Week' ? 'active' : ''}`}
                      onClick={() => fetchAnalytics({ quickPeriod: 'week', page: 1 })}
                      disabled={analyticsLoading}
                    >
                      📆 This Week
                    </button>
                    <button 
                      className={`quick-period-btn ${analyticsMode === 'quick' && analyticsData?.period?.label === 'All Time Total' ? 'active' : ''}`}
                      onClick={() => fetchAnalytics({ quickPeriod: 'total', page: 1 })}
                      disabled={analyticsLoading}
                    >
                      🌐 All Time
                    </button>
                  </div>
                </div>

                {/* Custom Date Range */}
                <div className="analytics-custom-range">
                  <span className="period-label">Custom Range:</span>
                  <div className="custom-range-inputs">
                    <input 
                      type="date" 
                      className="date-input"
                      value={analyticsCustomStart}
                      onChange={(e) => setAnalyticsCustomStart(e.target.value)}
                    />
                    <span className="date-separator">to</span>
                    <input 
                      type="date" 
                      className="date-input"
                      value={analyticsCustomEnd}
                      onChange={(e) => setAnalyticsCustomEnd(e.target.value)}
                    />
                    <button 
                      className="custom-range-btn"
                      onClick={fetchCustomAnalytics}
                      disabled={analyticsLoading || !analyticsCustomStart || !analyticsCustomEnd}
                    >
                      🔍 Search
                    </button>
                  </div>
                </div>

                {/* Leaderboard Period Selector */}
                <div className="analytics-period-selector">
                  <span className="period-label">Leaderboard Periods:</span>
                  <div className="period-buttons">
                    {(() => {
                      // Calculate how many periods back to Sept 2025
                      const now = new Date();
                      let currentMonth = now.getUTCMonth();
                      let currentYear = now.getUTCFullYear();
                      if (now.getUTCDate() < 23) currentMonth -= 1;
                      if (currentMonth < 0) { currentMonth += 12; currentYear -= 1; }
                      
                      // Sept 2025 is month 8 (0-indexed), year 2025
                      const startYear = 2025;
                      const startMonth = 8; // September
                      
                      // Calculate total periods from Sept 2025 to now
                      const totalPeriods = (currentYear - startYear) * 12 + (currentMonth - startMonth) + 1;
                      
                      return Array.from({ length: Math.max(1, Math.min(totalPeriods, 12)) }, (_, p) => {
                        // Generate period label
                        let month = currentMonth - p;
                        let year = currentYear;
                        while (month < 0) { month += 12; year -= 1; }
                        const startMonthName = new Date(Date.UTC(year, month, 1)).toLocaleString('en-US', { month: 'short' });
                        const endMonthName = new Date(Date.UTC(year, month + 1, 1)).toLocaleString('en-US', { month: 'short' });
                        const label = `${startMonthName}-${endMonthName}`;
                        
                        return (
                          <button 
                            key={p}
                            className={`period-btn ${analyticsMode === 'periods' && analyticsPeriod === p ? 'active' : ''}`}
                            onClick={() => fetchAnalytics({ period: p, page: 1 })}
                            disabled={analyticsLoading}
                          >
                            {p === 0 ? 'Current' : label}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
                
                {analyticsLoading ? (
                  <div className="analytics-loading">Loading analytics...</div>
                ) : analyticsData ? (
                  <>
                    {/* Period Info */}
                    <div className="analytics-period-info">
                      <span className="period-range">{analyticsData.period.label}</span>
                      {analyticsData.period.start && analyticsData.period.end ? (
                        <span className="period-dates">({new Date(analyticsData.period.start * 1000).toLocaleDateString()} to {new Date(analyticsData.period.end * 1000).toLocaleDateString()})</span>
                      ) : (
                        <span className="period-dates">(Lifetime)</span>
                      )}
                    </div>
                    
                    {/* Stats Cards */}
                    <div className="analytics-stats-grid simple">
                      <div className="analytics-stat-card total-wagered">
                        <div className="analytics-stat-value">${analyticsData.stats.totalWagered.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                        <div className="analytics-stat-label">{analyticsData.period?.isWeighted ? 'Total Weighted Wager' : 'Total Wagered (Raw)'}</div>
                      </div>
                      {analyticsData.period?.isWeighted && analyticsData.stats.totalWageredRaw > 0 && (
                        <div className="analytics-stat-card" style={{borderColor:'rgba(161,161,170,0.2)'}}>
                          <div className="analytics-stat-value" style={{color:'#a1a1aa'}}>${analyticsData.stats.totalWageredRaw.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                          <div className="analytics-stat-label">Total Raw Wager</div>
                        </div>
                      )}
                      {!analyticsData.period?.isWeighted && analyticsData.stats.totalWeightedWager > 0 && (
                        <div className="analytics-stat-card" style={{borderColor:'rgba(139,92,246,0.2)'}}>
                          <div className="analytics-stat-value" style={{color:'#a78bfa'}}>${analyticsData.stats.totalWeightedWager.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                          <div className="analytics-stat-label">If Weighted Applied</div>
                        </div>
                      )}
                      {analyticsData.stats.weightingRatio && parseFloat(analyticsData.stats.weightingRatio) > 0 && (
                        <div className="analytics-stat-card" style={{borderColor:'rgba(245,158,11,0.2)'}}>
                          <div className="analytics-stat-value" style={{color:'#fbbf24'}}>{analyticsData.stats.weightingRatio}%</div>
                          <div className="analytics-stat-label">Weighting Ratio</div>
                        </div>
                      )}
                      <div className="analytics-stat-card total-users">
                        <div className="analytics-stat-value">{analyticsData.stats.totalUsers.toLocaleString()}</div>
                        <div className="analytics-stat-label">Total Wagerers</div>
                      </div>
                    </div>
                    
                    {/* Leaderboard Table */}
                    <div className="analytics-table-container">
                      <h3>Top Wagerers</h3>
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Rank</th>
                            <th>Username</th>
                            <th>{analyticsData.period?.isWeighted ? 'Weighted' : 'Wagered'}</th>
                            {analyticsData.period?.isWeighted && <th>Raw</th>}
                            {!analyticsData.period?.isWeighted && <th>Weighted</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.affiliates.map((user) => (
                            <tr key={user.rank} className={user.rank <= 3 ? `top-${user.rank}` : ''}>
                              <td className="rank-cell">
                                {user.rank === 1 && '🥇'}
                                {user.rank === 2 && '🥈'}
                                {user.rank === 3 && '🥉'}
                                {user.rank > 3 && `#${user.rank}`}
                              </td>
                              <td className="username-cell">{user.username}</td>
                              <td className="wager-cell">${user.wagers.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                              {analyticsData.period?.isWeighted && <td className="wager-cell" style={{color:'#a1a1aa'}}>${(user.rawWagers || user.wagers).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>}
                              {!analyticsData.period?.isWeighted && <td className="wager-cell" style={{color:'#a78bfa'}}>${(user.weightedWagers || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Pagination */}
                      {analyticsData.pagination.totalPages > 1 && (
                        <div className="analytics-pagination">
                          <button 
                            className="pagination-btn"
                            onClick={() => {
                              if (analyticsMode === 'quick') {
                                const quickPeriod = analyticsData.period.label === 'Today' ? 'today' : 
                                                   analyticsData.period.label === 'Yesterday' ? 'yesterday' : analyticsData.period.label === 'All Time Total' ? 'total' : 'week';
                                fetchAnalytics({ quickPeriod, page: analyticsPage - 1 });
                              } else if (analyticsMode === 'custom') {
                                fetchAnalytics({ startDate: analyticsCustomStart, endDate: analyticsCustomEnd, page: analyticsPage - 1 });
                              } else {
                                fetchAnalytics({ period: analyticsPeriod, page: analyticsPage - 1 });
                              }
                            }}
                            disabled={analyticsPage === 1 || analyticsLoading}
                          >
                            ← Previous
                          </button>
                          <span className="pagination-info">
                            Page {analyticsPage} of {analyticsData.pagination.totalPages}
                          </span>
                          <button 
                            className="pagination-btn"
                            onClick={() => {
                              if (analyticsMode === 'quick') {
                                const quickPeriod = analyticsData.period.label === 'Today' ? 'today' : 
                                                   analyticsData.period.label === 'Yesterday' ? 'yesterday' : analyticsData.period.label === 'All Time Total' ? 'total' : 'week';
                                fetchAnalytics({ quickPeriod, page: analyticsPage + 1 });
                              } else if (analyticsMode === 'custom') {
                                fetchAnalytics({ startDate: analyticsCustomStart, endDate: analyticsCustomEnd, page: analyticsPage + 1 });
                              } else {
                                fetchAnalytics({ period: analyticsPeriod, page: analyticsPage + 1 });
                              }
                            }}
                            disabled={analyticsPage === analyticsData.pagination.totalPages || analyticsLoading}
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : analyticsError ? (
                  <div className="analytics-empty-state">
                    <div className="analytics-empty-icon">⚠️</div>
                    <h3>Couldn't load analytics</h3>
                    <p style={{color:'#f87171', wordBreak:'break-word'}}>{analyticsError}</p>
                    <p className="analytics-rate-note">Wait a few seconds and pick the period again.</p>
                  </div>
                ) : (
                  <div className="analytics-empty-state">
                    <div className="analytics-empty-icon">📊</div>
                    <h3>Select a Time Period</h3>
                    <p>Choose a time period above to load analytics data from Shuffle.</p>
                    <p className="analytics-rate-note">⚠️ Note: Shuffle API has a rate limit of 1 request per 10 seconds.</p>
                  </div>
                )}

                {/* Period Comparison Tool */}
                <div style={{marginTop:'1.5rem', background:'rgba(14,14,18,0.7)', border:'1px solid rgba(139,92,246,0.15)', borderRadius:'12px', padding:'1.25rem'}}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem'}}>
                    <div>
                      <div style={{fontSize:'1rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.2rem'}}>🔄 Period Comparison</div>
                      <div style={{fontSize:'0.75rem', color:'#71717a'}}>Compare top 200 wagerers between the previous and current leaderboard period. Takes ~12s due to API rate limits.</div>
                    </div>
                    <button
                      onClick={fetchPeriodComparison}
                      disabled={compareLoading}
                      style={{padding:'0.5rem 1rem', background: compareLoading ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.2)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'8px', color: compareLoading ? '#71717a' : '#a78bfa', fontSize:'0.82rem', fontWeight:600, cursor: compareLoading ? 'not-allowed' : 'pointer', whiteSpace:'nowrap'}}
                    >
                      {compareLoading ? '⏳ Fetching... (~12s)' : '🔄 Compare Periods'}
                    </button>
                  </div>

                  {compareLoading && (
                    <div style={{textAlign:'center', padding:'2rem', color:'#71717a'}}>
                      <div style={{fontSize:'1.5rem', marginBottom:'0.5rem'}}>⏳</div>
                      <div style={{fontSize:'0.85rem'}}>Fetching previous period, then waiting 11s for rate limit, then fetching current...</div>
                    </div>
                  )}

                  {compareData && !compareLoading && (
                    <>
                      {/* Summary cards */}
                      <div style={{display:'flex', gap:'0.6rem', marginBottom:'1rem', flexWrap:'wrap'}}>
                        <div style={{flex:1, minWidth:'130px', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', padding:'0.6rem 0.8rem', textAlign:'center'}}>
                          <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>{compareData.prevPeriod.label}</div>
                          <div style={{fontSize:'1rem', fontWeight:800, color:'#a78bfa'}}>${(compareData.prevPeriod.totalWagered || 0).toLocaleString(undefined, {maximumFractionDigits:0})}</div>
                          <div style={{fontSize:'0.65rem', color:'#71717a'}}>{compareData.prevPeriod.totalUsers} users</div>
                        </div>
                        <div style={{flex:1, minWidth:'130px', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', padding:'0.6rem 0.8rem', textAlign:'center'}}>
                          <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>{compareData.currPeriod.label}</div>
                          <div style={{fontSize:'1rem', fontWeight:800, color:'#a78bfa'}}>${(compareData.currPeriod.totalWagered || 0).toLocaleString(undefined, {maximumFractionDigits:0})}</div>
                          <div style={{fontSize:'0.65rem', color:'#71717a'}}>{compareData.currPeriod.totalUsers} users</div>
                        </div>
                        <div style={{flex:1, minWidth:'100px', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'8px', padding:'0.6rem 0.8rem', textAlign:'center'}}>
                          <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>New</div>
                          <div style={{fontSize:'1rem', fontWeight:800, color:'#22c55e'}}>{compareData.summary.newUsers}</div>
                          <div style={{fontSize:'0.65rem', color:'#71717a'}}>${(compareData.summary.newUsersWager || 0).toLocaleString(undefined, {maximumFractionDigits:0})}</div>
                        </div>
                        <div style={{flex:1, minWidth:'100px', background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'8px', padding:'0.6rem 0.8rem', textAlign:'center'}}>
                          <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>Gone</div>
                          <div style={{fontSize:'1rem', fontWeight:800, color:'#f87171'}}>{compareData.summary.goneUsers}</div>
                          <div style={{fontSize:'0.65rem', color:'#71717a'}}>${(compareData.summary.goneUsersWager || 0).toLocaleString(undefined, {maximumFractionDigits:0})}</div>
                        </div>
                        <div style={{flex:1, minWidth:'100px', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', padding:'0.6rem 0.8rem', textAlign:'center'}}>
                          <div style={{fontSize:'0.65rem', color:'#71717a', marginBottom:'0.2rem'}}>Returning</div>
                          <div style={{fontSize:'1rem', fontWeight:800, color:'#fbbf24'}}>{compareData.summary.returningUsers}</div>
                        </div>
                      </div>

                      {/* Filter tabs */}
                      <div style={{display:'flex', gap:'0.35rem', marginBottom:'0.75rem'}}>
                        {[
                          { label: `All (${compareData.comparison.length})`, value: 'all', color: '#a1a1aa' },
                          { label: `New (${compareData.summary.newUsers})`, value: 'new', color: '#22c55e' },
                          { label: `Gone (${compareData.summary.goneUsers})`, value: 'gone', color: '#f87171' },
                          { label: `Returning (${compareData.summary.returningUsers})`, value: 'returning', color: '#fbbf24' },
                        ].map(f => (
                          <button key={f.value} onClick={() => setCompareFilter(f.value)}
                            style={{padding:'0.3rem 0.65rem', background: compareFilter === f.value ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.04)', border:`1px solid ${compareFilter === f.value ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'6px', color: compareFilter === f.value ? '#fff' : f.color, fontSize:'0.72rem', fontWeight:600, cursor:'pointer'}}
                          >{f.label}</button>
                        ))}
                      </div>

                      {/* Comparison table */}
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.78rem'}}>
                          <thead>
                            <tr style={{borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
                              <th style={{textAlign:'left', padding:'0.5rem', color:'#9ca3af', fontWeight:600}}>User</th>
                              <th style={{textAlign:'center', padding:'0.5rem', color:'#9ca3af', fontWeight:600}}>Status</th>
                              <th style={{textAlign:'right', padding:'0.5rem', color:'#9ca3af', fontWeight:600}}>Prev Period</th>
                              <th style={{textAlign:'right', padding:'0.5rem', color:'#9ca3af', fontWeight:600}}>Curr Period</th>
                              <th style={{textAlign:'right', padding:'0.5rem', color:'#9ca3af', fontWeight:600}}>Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {compareData.comparison
                              .filter(c => compareFilter === 'all' || c.status === compareFilter)
                              .map((c, i) => (
                              <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)', background: c.status === 'new' ? 'rgba(34,197,94,0.03)' : c.status === 'gone' ? 'rgba(239,68,68,0.03)' : 'transparent'}}>
                                <td style={{padding:'0.5rem'}}>
                                  <div style={{fontWeight:600, color:'#e2e8f0'}}>{c.username}</div>
                                  <div style={{fontSize:'0.65rem', color:'#71717a'}}>
                                    {c.prevRank ? `#${c.prevRank} prev` : ''}
                                    {c.prevRank && c.currRank ? ' → ' : ''}
                                    {c.currRank ? `#${c.currRank} now` : ''}
                                  </div>
                                </td>
                                <td style={{textAlign:'center', padding:'0.5rem'}}>
                                  <span style={{
                                    fontSize:'0.68rem', fontWeight:700, padding:'0.15rem 0.45rem', borderRadius:'4px',
                                    background: c.status === 'new' ? 'rgba(34,197,94,0.12)' : c.status === 'gone' ? 'rgba(239,68,68,0.12)' : 'rgba(251,191,36,0.12)',
                                    color: c.status === 'new' ? '#22c55e' : c.status === 'gone' ? '#f87171' : '#fbbf24'
                                  }}>
                                    {c.status === 'new' ? '🆕 NEW' : c.status === 'gone' ? '📉 GONE' : '🔁'}
                                  </span>
                                </td>
                                <td style={{textAlign:'right', padding:'0.5rem', fontFamily:'monospace', color: c.status === 'gone' ? '#f87171' : '#a1a1aa'}}>
                                  {c.prevWager > 0 ? `$${c.prevWager.toLocaleString(undefined, {maximumFractionDigits:0})}` : '—'}
                                </td>
                                <td style={{textAlign:'right', padding:'0.5rem', fontFamily:'monospace', color: c.status === 'new' ? '#22c55e' : '#e2e8f0', fontWeight:600}}>
                                  {c.currWager > 0 ? `$${c.currWager.toLocaleString(undefined, {maximumFractionDigits:0})}` : '—'}
                                </td>
                                <td style={{textAlign:'right', padding:'0.5rem', fontFamily:'monospace', fontWeight:700, color: c.wagerChange > 0 ? '#22c55e' : c.wagerChange < 0 ? '#f87171' : '#52525b'}}>
                                  {c.wagerChange !== 0 ? `${c.wagerChange > 0 ? '+' : ''}$${c.wagerChange.toLocaleString(undefined, {maximumFractionDigits:0})}` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>

                {/* Leaderboard Snapshots */}
                <div style={{marginTop:'1.5rem', background:'rgba(14,14,18,0.7)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'12px', padding:'1.25rem'}}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem'}}>
                    <div>
                      <div style={{fontSize:'1rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.2rem'}}>📸 Leaderboard Snapshots</div>
                      <div style={{fontSize:'0.75rem', color:'#71717a'}}>Automatically captured at period end. Shows full usernames and prize amounts for payout reference.</div>
                    </div>
                    <div style={{display:'flex', gap:'0.4rem'}}>
                      <button onClick={fetchLeaderboardSnapshots}
                        style={{padding:'0.4rem 0.8rem', background:'rgba(139,92,246,0.15)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'8px', color:'#a78bfa', fontSize:'0.75rem', fontWeight:600, cursor:'pointer'}}>
                        🔄 Load
                      </button>
                      <button onClick={() => takeLeaderboardSnapshot('monthly')}
                        style={{padding:'0.4rem 0.8rem', background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:'8px', color:'#fbbf24', fontSize:'0.75rem', fontWeight:600, cursor:'pointer'}}>
                        📸 Snapshot Monthly
                      </button>
                      <button onClick={() => takeLeaderboardSnapshot('weekly')}
                        style={{padding:'0.4rem 0.8rem', background:'rgba(139,92,246,0.12)', border:'1px solid rgba(139,92,246,0.25)', borderRadius:'8px', color:'#c084fc', fontSize:'0.75rem', fontWeight:600, cursor:'pointer'}}>
                        📸 Snapshot Weekly
                      </button>
                    </div>
                  </div>

                  {leaderboardSnapshots.length === 0 ? (
                    <div style={{textAlign:'center', padding:'1.5rem', color:'#71717a', fontSize:'0.85rem'}}>
                      No snapshots yet. Click "Load" to fetch existing snapshots, or they will be created automatically at period end.
                    </div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.75rem'}}>
                      {leaderboardSnapshots.map(snap => (
                        <details key={snap.id} style={{background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', overflow:'hidden'}}>
                          <summary style={{padding:'0.7rem 1rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.85rem', color:'#e2e8f0', fontWeight:600}}>
                            <span style={{
                              padding:'0.15rem 0.5rem', borderRadius:'4px', fontSize:'0.68rem', fontWeight:700,
                              background: snap.period_type === 'monthly' ? 'rgba(245,158,11,0.12)' : 'rgba(139,92,246,0.12)',
                              color: snap.period_type === 'monthly' ? '#fbbf24' : '#c084fc',
                              border: `1px solid ${snap.period_type === 'monthly' ? 'rgba(245,158,11,0.25)' : 'rgba(139,92,246,0.25)'}`
                            }}>{snap.period_type.toUpperCase()}</span>
                            <span>{snap.period_label}</span>
                            <span style={{marginLeft:'auto', fontSize:'0.72rem', color:'#71717a'}}>
                              {snap.total_participants} users • ${parseFloat(snap.total_wagered || 0).toLocaleString(undefined, {maximumFractionDigits:0})} wagered • {new Date(snap.created_at).toLocaleString()}
                            </span>
                          </summary>
                          <div style={{padding:'0.5rem 1rem 1rem'}}>
                            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.78rem'}}>
                              <thead>
                                <tr style={{borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
                                  <th style={{textAlign:'left', padding:'0.4rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Rank</th>
                                  <th style={{textAlign:'left', padding:'0.4rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Username</th>
                                  <th style={{textAlign:'right', padding:'0.4rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Wagered</th>
                                  <th style={{textAlign:'right', padding:'0.4rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Prize</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(typeof snap.snapshot_data === 'string' ? JSON.parse(snap.snapshot_data) : snap.snapshot_data).map(entry => (
                                  <tr key={entry.rank} style={{borderBottom:'1px solid rgba(255,255,255,0.03)', background: entry.rank <= 3 ? 'rgba(245,158,11,0.03)' : 'transparent'}}>
                                    <td style={{padding:'0.4rem 0.5rem', fontWeight:700, color: entry.rank === 1 ? '#fbbf24' : entry.rank === 2 ? '#d1d5db' : entry.rank === 3 ? '#cd7f32' : '#71717a'}}>
                                      {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                                    </td>
                                    <td style={{padding:'0.4rem 0.5rem', fontWeight:600, color:'#e2e8f0', fontFamily:'monospace'}}>{entry.username}</td>
                                    <td style={{padding:'0.4rem 0.5rem', textAlign:'right', color:'#a1a1aa', fontFamily:'monospace'}}>${entry.wager.toLocaleString(undefined, {maximumFractionDigits:0})}</td>
                                    <td style={{padding:'0.4rem 0.5rem', textAlign:'right', fontWeight:700, color:'#22c55e'}}>${entry.prize.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div style={{display:'flex', gap:'0.4rem', marginTop:'0.6rem', justifyContent:'flex-end'}}>
                              <button onClick={async () => {
                                if (!confirm(`Retake this snapshot? This will re-fetch data from Shuffle for the period "${snap.period_label}" and overwrite the current snapshot.`)) return;
                                try {
                                  const token = getAuthToken();
                                  const res = await fetch(`${BACKEND_API_URL}/api/admin/leaderboard-snapshots/retake`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ id: snap.id, type: snap.period_type, label: snap.period_label })
                                  });
                                  const data = await res.json();
                                  if (res.ok) { alert(`✅ Snapshot retaken! ${data.participants} participants`); fetchLeaderboardSnapshots(); }
                                  else alert('Error: ' + (data.error || res.status));
                                } catch(e) { alert('Error: ' + e.message); }
                              }} style={{padding:'0.3rem 0.6rem', background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:'6px', color:'#fbbf24', fontSize:'0.7rem', fontWeight:600, cursor:'pointer'}}>
                                🔄 Retake from Shuffle
                              </button>
                              <button onClick={async () => {
                                if (!confirm('Delete this snapshot?')) return;
                                try {
                                  const token = getAuthToken();
                                  await fetch(`${BACKEND_API_URL}/api/admin/leaderboard-snapshots/${snap.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                                  fetchLeaderboardSnapshots();
                                } catch(e) { alert('Error: ' + e.message); }
                              }} style={{padding:'0.3rem 0.6rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'6px', color:'#ef4444', fontSize:'0.7rem', fontWeight:600, cursor:'pointer'}}>
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Users Tab */}
            {adminTab === 'users' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>👥 User Database ({adminData.usersTotal} total)</h2>
                </div>
                
                {/* Search Bar */}
                <div className="admin-user-search">
                  <input 
                    type="text"
                    placeholder="Search by Twitch, Shuffle, or Discord username..."
                    value={adminUserSearch}
                    onChange={(e) => setAdminUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && adminSearchUsers(adminUserSearch)}
                    className="admin-search-input"
                  />
                  <button 
                    className="admin-search-btn"
                    onClick={() => adminSearchUsers(adminUserSearch)}
                  >
                    🔍 Search
                  </button>
                  {adminUserSearch && (
                    <button 
                      className="admin-search-clear"
                      onClick={() => { setAdminUserSearch(''); adminSearchUsers(''); }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                {/* Users List */}
                <div className="admin-users-list">
                  {adminData.users.map((u, idx) => (
                    <div key={idx} className="admin-user-item clickable" onClick={() => adminViewUserDetails(u.id)}>
                      <img src={u.profile_image_url} alt={u.display_name} className="admin-user-avatar" />
                      <div className="admin-user-info">
                        <span className="admin-user-name">{u.display_name || u.username}</span>
                        <div className="admin-user-details-row">
                          <span className="admin-user-twitch">@{u.username}</span>
                          {u.shuffle_username && (
                            <span className="admin-user-shuffle">🎰 {u.shuffle_username}</span>
                          )}
                          {u.discord_username && (
                            <span className="admin-user-discord">🎮 {u.discord_username}</span>
                          )}
                        </div>
                        <span className="admin-user-stats">
                          {u.total_predictions} predictions · {u.total_wins} wins
                          {parseFloat(u.total_prediction_earnings) > 0 && (
                            <span className="admin-user-earnings"> · ${parseFloat(u.total_prediction_earnings).toLocaleString()} earned</span>
                          )}
                        </span>
                      </div>
                      <span className="admin-user-login">Last: {new Date(u.last_login).toLocaleDateString()}</span>
                      <span className="admin-user-view-btn">View →</span>
                    </div>
                  ))}
                  {adminData.users.length === 0 && (
                    <p className="admin-no-data">No users found</p>
                  )}
                </div>
              </div>
            )}
            {/* User Details Modal */}
            {adminData.selectedUserDetails && (
              <div className="admin-user-modal-overlay" onClick={adminCloseUserDetails}>
                <div className="admin-user-modal" onClick={(e) => e.stopPropagation()}>
                  <button className="admin-user-modal-close" onClick={adminCloseUserDetails}>×</button>
                  
                  <div className="admin-user-modal-header">
                    <img 
                      src={adminData.selectedUserDetails.user.profile_image_url} 
                      alt={adminData.selectedUserDetails.user.display_name} 
                      className="admin-user-modal-avatar"
                    />
                    <div className="admin-user-modal-info">
                      <h2>{adminData.selectedUserDetails.user.display_name || adminData.selectedUserDetails.user.username}</h2>
                      <p className="admin-user-modal-usernames">
                        <span className="twitch-username">Twitch: @{adminData.selectedUserDetails.user.username}</span>
                        {adminData.selectedUserDetails.user.shuffle_username && (
                          <span className="shuffle-username">Shuffle: {adminData.selectedUserDetails.user.shuffle_username}</span>
                        )}
                        {adminData.selectedUserDetails.user.discord_username && (
                          <span className="discord-username">Discord: {adminData.selectedUserDetails.user.discord_username}</span>
                        )}
                      </p>
                      <p className="admin-user-modal-dates">
                        Joined: {new Date(adminData.selectedUserDetails.user.created_at).toLocaleDateString()} ·
                        Last login: {new Date(adminData.selectedUserDetails.user.last_login).toLocaleDateString()}
                      </p>
                      {/* Privacy: per-user leaderboard avatar visibility */}
                      <button
                        onClick={async () => {
                          const u = adminData.selectedUserDetails.user;
                          const hidden = !u.hide_lb_avatar;
                          try {
                            const r = await fetch(`${BACKEND_API_URL}/api/admin/users/${u.id}/hide-lb-avatar`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ hidden })
                            });
                            const d = await r.json().catch(() => ({}));
                            if (!r.ok) { alert(d.error || 'Failed to update'); return; }
                            setAdminData(prev => ({
                              ...prev,
                              selectedUserDetails: {
                                ...prev.selectedUserDetails,
                                user: { ...prev.selectedUserDetails.user, hide_lb_avatar: hidden }
                              }
                            }));
                          } catch (e) { alert('Network error'); }
                        }}
                        title="Leaderboard anonymity: hides their avatar on the wager leaderboards AND shows them as a fully anonymous entry (no name, no avatar) on the points leaderboard"
                        style={{
                          marginTop: '0.5rem', padding: '0.35rem 0.75rem', fontSize: '0.72rem', fontWeight: 700,
                          borderRadius: '7px', cursor: 'pointer',
                          background: adminData.selectedUserDetails.user.hide_lb_avatar ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${adminData.selectedUserDetails.user.hide_lb_avatar ? 'rgba(239,68,68,0.35)' : 'rgba(255,255,255,0.12)'}`,
                          color: adminData.selectedUserDetails.user.hide_lb_avatar ? '#f87171' : '#a1a1aa'
                        }}
                      >
                        {adminData.selectedUserDetails.user.hide_lb_avatar
                          ? '🙈 Leaderboard anonymity ON (avatar hidden, anonymous on points LB) — click to disable'
                          : '👁️ Visible on leaderboards — click to make anonymous'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="admin-user-modal-stats">
                    <div className="admin-user-stat-box">
                      <span className="stat-number">{adminData.selectedUserDetails.stats.totalPredictions}</span>
                      <span className="stat-label">Predictions</span>
                    </div>
                    <div className="admin-user-stat-box">
                      <span className="stat-number">{adminData.selectedUserDetails.stats.totalWins}</span>
                      <span className="stat-label">Wins</span>
                    </div>
                    <div className="admin-user-stat-box">
                      <span className="stat-number">{adminData.selectedUserDetails.stats.totalClaims}</span>
                      <span className="stat-label">Claims</span>
                    </div>
                    <div className="admin-user-stat-box highlight">
                      <span className="stat-number">${adminData.selectedUserDetails.stats.totalPredictionEarnings.toLocaleString()}</span>
                      <span className="stat-label">Total Earned</span>
                    </div>
                    <div className="admin-user-stat-box">
                      <span className="stat-number">💎 {(adminData.selectedUserDetails.stats.pointsBalance || 0).toLocaleString()}</span>
                      <span className="stat-label">Points</span>
                    </div>
                    <div className="admin-user-stat-box" title={`Hours watched in the last 2 months (all-time: ${(adminData.selectedUserDetails.stats.watchHoursAllTime || 0).toLocaleString()}h). 40h+ in 2 months = bad luck protection eligible.`}>
                      <span className="stat-number" style={(adminData.selectedUserDetails.stats.watchHours2mo || 0) >= 40 ? { color: '#22c55e' } : undefined}>
                        📺 {(adminData.selectedUserDetails.stats.watchHours2mo || 0).toLocaleString()}h
                      </span>
                      <span className="stat-label">Watchtime (2mo)</span>
                    </div>
                  </div>

                  {/* Wagering & Giveaways (Shuffle) */}
                  <div className="admin-user-modal-section">
                    <h3>💰 Wagering &amp; Giveaways (Shuffle)</h3>
                    {(() => {
                      const ws = adminUserWagerStats;
                      const money = (n) => (n == null ? '—' : '$' + Math.round(n).toLocaleString());
                      if (adminUserWagerLoading && !ws) return <p className="admin-more-note">Loading Shuffle stats…</p>;
                      if (!ws) return <p className="admin-more-note">Couldn't load Shuffle stats.</p>;
                      if (!ws.linked) return <p className="admin-more-note">No Shuffle account linked for this user.</p>;

                      const cell = (label, pair, opts = {}) => {
                        let value = '—', sub = null;
                        if (pair === null) {
                          value = 'unavailable';
                        } else {
                          value = money(pair.weighted);
                          if (ws.isWeighted && opts.showRaw && pair.raw != null && Math.round(pair.raw) !== Math.round(pair.weighted)) {
                            sub = `raw ${money(pair.raw)}`;
                          }
                          if (opts.rank && pair.rank) sub = `rank #${pair.rank}${sub ? ' · ' + sub : ''}`;
                        }
                        return (
                          <div style={{ padding: '0.6rem 0.8rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' }}>
                            <div style={{ fontSize: '0.68rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>{value}</div>
                            {sub && <div style={{ fontSize: '0.7rem', color: '#a1a1aa', marginTop: '0.1rem' }}>{sub}</div>}
                          </div>
                        );
                      };

                      const g = ws.giveaways || {};
                      return (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.6rem', marginBottom: '0.8rem' }}>
                            {cell('Lifetime wagered', ws.lifetime)}
                            {cell('This period', ws.thisPeriod, { showRaw: true, rank: true })}
                            {cell('Last period', ws.lastPeriod, { showRaw: true })}
                            {cell('Last 24 hours', ws.last24h, { showRaw: true })}
                            {cell('Last 7 days', ws.last7d, { showRaw: true })}
                          </div>
                          <div style={{ padding: '0.6rem 0.8rem', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '10px' }}>
                            <div style={{ fontSize: '0.68rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Giveaways won</div>
                            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#22c55e' }}>
                              {money(g.paidTotal)} <span style={{ fontSize: '0.75rem', color: '#a1a1aa', fontWeight: 600 }}>paid ({g.paidCount || 0})</span>
                            </div>
                            {g.pendingCount > 0 && (
                              <div style={{ fontSize: '0.72rem', color: '#fbbf24', marginTop: '0.1rem' }}>
                                {money(g.pendingTotal)} pending ({g.pendingCount})
                              </div>
                            )}
                          </div>
                          {ws.isWeighted && (
                            <p className="admin-more-note" style={{ marginTop: '0.5rem' }}>
                              Weighted = leaderboard-counted wager · raw = unweighted. Rolling windows are cached ~10 min.
                            </p>
                          )}
                          <button
                            onClick={adminWarmWagerRanges}
                            disabled={adminWagerWarming}
                            style={{ marginTop: '0.6rem', padding: '0.45rem 0.9rem', borderRadius: '8px', border: '1px solid rgba(168,85,247,0.35)', background: 'rgba(168,85,247,0.12)', color: '#c4b5fd', fontSize: '0.78rem', fontWeight: 700, cursor: adminWagerWarming ? 'default' : 'pointer', fontFamily: 'Outfit, sans-serif', opacity: adminWagerWarming ? 0.6 : 1 }}
                          >
                            {adminWagerWarming ? 'Refreshing 24h / 7d… (~20s)' : '🔄 Refresh 24h / 7d from Shuffle'}
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  {/* User's Predictions */}
                  {adminData.selectedUserDetails.predictions.length > 0 && (
                    <div className="admin-user-modal-section">
                      <h3>📊 Predictions ({adminData.selectedUserDetails.predictions.length})</h3>
                      <div className="admin-user-predictions-list">
                        {adminData.selectedUserDetails.predictions.slice(0, 10).map((pred, idx) => (
                          <div key={idx} className={`admin-user-pred-item ${pred.hunt_status}`}>
                            <span className="pred-hunt">Hunt #{pred.hunt_id}</span>
                            <span className="pred-guess">${parseFloat(pred.predicted_balance).toLocaleString()}</span>
                            <span className={`pred-status ${pred.hunt_status}`}>
                              {pred.hunt_status === 'completed' ? '✅' : pred.hunt_status === 'active' ? '🟢' : '⏳'}
                            </span>
                            <span className="pred-date">{new Date(pred.created_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                        {adminData.selectedUserDetails.predictions.length > 10 && (
                          <p className="admin-more-note">+ {adminData.selectedUserDetails.predictions.length - 10} more</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* User's Wins */}
                  {adminData.selectedUserDetails.wins.length > 0 && (
                    <div className="admin-user-modal-section">
                      <h3>🏆 Wins ({adminData.selectedUserDetails.wins.length})</h3>
                      <div className="admin-user-wins-list">
                        {adminData.selectedUserDetails.wins.map((win, idx) => (
                          <div key={idx} className={`admin-user-win-item rank-${win.rank}`}>
                            <span className="win-rank">{win.rank === 1 ? '🥇' : win.rank === 2 ? '🥈' : '🥉'}</span>
                            <span className="win-hunt">Hunt #{win.hunt_id}</span>
                            <span className="win-guess">${parseFloat(win.predicted_balance).toLocaleString()}</span>
                            <span className="win-reward">{win.reward}</span>
                            <span className="win-claimed">{win.claimed ? '✅ Claimed' : '⏳ Unclaimed'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* User's Claims */}
                  {adminData.selectedUserDetails.claims.length > 0 && (
                    <div className="admin-user-modal-section">
                      <h3>💰 Claims ({adminData.selectedUserDetails.claims.length})</h3>
                      <div className="admin-user-claims-list">
                        {adminData.selectedUserDetails.claims.map((claim, idx) => (
                          <div key={idx} className={`admin-user-claim-item ${claim.status}`}>
                            <span className="claim-reward">{claim.reward}</span>
                            <span className="claim-shuffle">{claim.shuffle_username}</span>
                            <span className={`claim-status ${claim.status}`}>
                              {claim.status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                            </span>
                            <span className="claim-date">{new Date(claim.claimed_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ========== POINTS ADMIN TAB ========== */}
            {adminTab === 'points' && (
              <div className="admin-points-panel">
                {/* Admin Points Message Toast */}
                {adminPointsMessage && (
                  <div className={`admin-points-msg ${adminPointsMessage.type}`}>
                    {adminPointsMessage.type === 'success' ? '✅' : '❌'} {adminPointsMessage.text}
                    <button onClick={() => setAdminPointsMessage(null)}>×</button>
                  </div>
                )}

                {/* Sub-tabs */}
                <div className="admin-points-subtabs">
                  <button className={adminPointsSubTab === 'overview' ? 'active' : ''} onClick={() => { setAdminPointsSubTab('overview'); fetchAdminPointsStats(); }}>Overview</button>
                  <button className={adminPointsSubTab === 'config' ? 'active' : ''} onClick={() => { setAdminPointsSubTab('config'); fetchAdminPointsConfig(); }}>Config</button>
                  <button className={adminPointsSubTab === 'shop' ? 'active' : ''} onClick={() => { setAdminPointsSubTab('shop'); fetchAdminShopItems(); }}>Shop Items</button>
                  <button className={adminPointsSubTab === 'redemptions' ? 'active' : ''} onClick={() => { setAdminPointsSubTab('redemptions'); fetchAdminPointsRedemptions('pending', 1); }}>
                    Redemptions
                    {adminPointsStats?.pendingRedemptions > 0 && <span className="admin-tab-badge">{adminPointsStats.pendingRedemptions}</span>}
                  </button>
                  <button className={adminPointsSubTab === 'grant' ? 'active' : ''} onClick={() => setAdminPointsSubTab('grant')}>Grant/Deduct</button>
                  <button className={adminPointsSubTab === 'payouts' ? 'active' : ''} onClick={() => { setAdminPointsSubTab('payouts'); fetchRedemptionPayouts(); fetchPayoutSummary(adminPayoutRange); }}>💰 Payouts</button>
                  <button className={adminPointsSubTab === 'reconcile' ? 'active' : ''} onClick={() => { setAdminPointsSubTab('reconcile'); fetchWagerReconciliation(); }}>🔧 Wager Reconcile</button>
                </div>

                {/* OVERVIEW SUB-TAB */}
                {adminPointsSubTab === 'overview' && adminPointsStats && (
                  <div>
                    <div className="admin-stats-row">
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminPointsStats.totalEarned?.toLocaleString() || 0}</div>
                        <div className="admin-stat-label">💎 Total Earned</div>
                      </div>
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminPointsStats.totalSpent?.toLocaleString() || 0}</div>
                        <div className="admin-stat-label">🛒 Total Spent</div>
                      </div>
                      <div className="admin-stat-card">
                        <div className="admin-stat-value" style={{color: '#eab308'}}>{adminPointsStats.totalCirculating?.toLocaleString() || 0}</div>
                        <div className="admin-stat-label">💰 Circulating</div>
                      </div>
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminPointsStats.uniqueUsers || 0}</div>
                        <div className="admin-stat-label">👥 Users w/ Points</div>
                      </div>
                      <div className="admin-stat-card">
                        <div className="admin-stat-value">{adminPointsStats.totalRedemptions || 0}</div>
                        <div className="admin-stat-label">📦 Total Redemptions</div>
                      </div>
                      <div className="admin-stat-card highlight-unclaimed">
                        <div className="admin-stat-value">{adminPointsStats.pendingRedemptions || 0}</div>
                        <div className="admin-stat-label">⏳ Pending</div>
                      </div>
                    </div>

                    {adminPointsStats.sourceBreakdown?.length > 0 && (
                      <div className="ap-section">
                        <h3 className="ap-section-title">Points by Source</h3>
                        <div className="ap-source-list">
                          {adminPointsStats.sourceBreakdown.map((s, i) => (
                            <div key={i} className="ap-source-row">
                              <span className="ap-source-name">
                                {s.source === 'admin_grant' ? '🎁 Admin Grant' :
                                 s.source === 'admin_deduct' ? '🔻 Admin Deduct' :
                                 s.source === 'wager' ? '🎰 Wagering' :
                                 s.source === 'twitch_chat' ? '💬 Twitch Chat' :
                                 s.source === 'twitch_watch' ? '📺 Watch Time' :
                                 s.source === 'discord' ? '🎮 Discord' :
                                 s.source === 'shop_redemption' ? '🛒 Shop' :
                                 s.source === 'refund' ? '↩️ Refund' :
                                 s.source}
                              </span>
                              <span className="ap-source-earned">+{parseInt(s.earned).toLocaleString()}</span>
                              <span className="ap-source-txns">{parseInt(s.transactions).toLocaleString()} txns</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Diagnostics */}
                    <div className="ap-section" style={{marginTop:'2rem'}}>
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem'}}>
                        <h3 className="ap-section-title" style={{margin:0}}>🔍 Diagnostics</h3>
                        <button className="ap-btn-sm" onClick={fetchPointsDiagnostics}>Run Check</button>
                      </div>
                      {pointsDiagnostics && (
                        <div style={{fontSize:'0.8rem', lineHeight:'1.8'}}>
                          <div style={{marginBottom:'1rem'}}>
                            <div style={{fontWeight:600, marginBottom:'0.5rem', color:'#e2e8f0'}}>⚙️ Config Toggles</div>
                            {Object.entries(pointsDiagnostics.config).map(([k, v]) => (
                              <div key={k} style={{display:'flex', justifyContent:'space-between', padding:'2px 8px', background: v === 'true' ? 'rgba(34,197,94,0.08)' : v === 'false' ? 'rgba(248,113,113,0.08)' : 'transparent', borderRadius:'4px'}}>
                                <span style={{color:'#9ca3af'}}>{k}</span>
                                <span style={{color: v === 'true' ? '#22c55e' : v === 'false' ? '#f87171' : '#e2e8f0', fontWeight:600}}>{v}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{marginBottom:'1rem'}}>
                            <div style={{fontWeight:600, marginBottom:'0.5rem', color:'#e2e8f0'}}>🎰 Wager Tracking</div>
                            <div style={{color:'#9ca3af'}}>Users with Shuffle linked: <strong style={{color:'#e2e8f0'}}>{pointsDiagnostics.wager.usersWithShuffle}</strong></div>
                            <div style={{color:'#9ca3af'}}>Wager snapshots saved: <strong style={{color:'#e2e8f0'}}>{pointsDiagnostics.wager.snapshotCount}</strong></div>
                            {pointsDiagnostics.wager.snapshots?.length > 0 && (
                              <div style={{marginTop:'0.4rem'}}>
                                <div style={{fontSize:'0.72rem', color:'#6b7280'}}>Recent snapshots:</div>
                                {pointsDiagnostics.wager.snapshots.map((s, i) => (
                                  <div key={i} style={{fontSize:'0.72rem', color:'#9ca3af'}}>
                                    {s.shuffle_username}: ${parseFloat(s.last_known_wager).toLocaleString()} (updated {new Date(s.updated_at).toLocaleTimeString()})
                                  </div>
                                ))}
                              </div>
                            )}
                            {pointsDiagnostics.wager.recentAwards?.length > 0 && (
                              <div style={{marginTop:'0.4rem'}}>
                                <div style={{fontSize:'0.72rem', color:'#6b7280'}}>Recent wager awards:</div>
                                {pointsDiagnostics.wager.recentAwards.map((a, i) => (
                                  <div key={i} style={{fontSize:'0.72rem', color:'#22c55e'}}>
                                    {a.display_name}: +{a.amount} — {a.source_detail}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{marginBottom:'1rem'}}>
                            <div style={{fontWeight:600, marginBottom:'0.5rem', color:'#e2e8f0'}}>📺 Watch Time</div>
                            <div style={{color:'#9ca3af'}}>Stream: <strong style={{color: pointsDiagnostics.streamLive ? '#22c55e' : '#f87171'}}>{pointsDiagnostics.streamLive ? '🔴 LIVE — earning active' : '⚫ Offline — earning paused'}</strong></div>
                            <div style={{color:'#9ca3af'}}>TMI connected: <strong style={{color: pointsDiagnostics.tmiConnected ? '#22c55e' : '#f87171'}}>{pointsDiagnostics.tmiConnected ? 'Yes' : 'No'}</strong></div>
                            <div style={{color:'#9ca3af'}}>Chatters API: <strong style={{color: pointsDiagnostics.watchTime.broadcasterTokenStatus === 'ready' ? '#22c55e' : '#f87171'}}>
                              {pointsDiagnostics.watchTime.broadcasterTokenStatus === 'ready' ? '✅ Active (tracking all viewers)' :
                               pointsDiagnostics.watchTime.broadcasterTokenStatus === 'missing_chatters_scope' ? '⚠️ Need to re-auth broadcaster with chatters scope' :
                               pointsDiagnostics.watchTime.broadcasterTokenStatus === 'no_token' ? '❌ No broadcaster token' : '❌ Not configured'}
                            </strong></div>
                            <div style={{color:'#9ca3af'}}>Tracked viewers: <strong style={{color:'#e2e8f0'}}>{pointsDiagnostics.watchTime.trackedViewers}</strong></div>
                            {pointsDiagnostics.watchTime.summary && (
                              <div style={{marginTop:'0.4rem', padding:'8px 10px', background:'rgba(139,92,246,0.06)', borderRadius:'6px', border:'1px solid rgba(139,92,246,0.15)'}}>
                                <div style={{fontSize:'0.75rem', color:'#a78bfa', fontWeight:600, marginBottom:'4px'}}>⏱️ Progress to first awards</div>
                                <div style={{fontSize:'0.75rem', color:'#9ca3af'}}>
                                  Closest to award: <strong style={{color:'#e2e8f0'}}>{60 - pointsDiagnostics.watchTime.summary.nextAwardIn}min / 60min</strong>
                                  {pointsDiagnostics.watchTime.summary.nextAwardIn <= 10 && <span style={{color:'#22c55e'}}> — awarding soon!</span>}
                                </div>
                                <div style={{fontSize:'0.75rem', color:'#9ca3af'}}>
                                  Viewers 30+ min: <strong style={{color:'#e2e8f0'}}>{pointsDiagnostics.watchTime.summary.viewersOver30min}</strong> · 
                                  50+ min: <strong style={{color:'#e2e8f0'}}>{pointsDiagnostics.watchTime.summary.viewersOver50min}</strong>
                                </div>
                              </div>
                            )}
                            {pointsDiagnostics.watchTime.viewers.length > 0 && (
                              <div style={{marginTop:'0.4rem'}}>
                                <div style={{fontSize:'0.72rem', color:'#6b7280'}}>Viewers (accumulated / 60min needed):</div>
                                {pointsDiagnostics.watchTime.viewers.slice(0, 10).map((v, i) => (
                                  <div key={i} style={{fontSize:'0.72rem', color: v.secondsAgo < 900 ? '#22c55e' : '#f59e0b', display:'flex', justifyContent:'space-between'}}>
                                    <span>User #{v.userId}</span>
                                    <span><strong>{v.accumulatedMinutes}</strong>/60 min {v.minutesUntilAward <= 10 ? '🔥' : ''} (seen {v.secondsAgo}s ago)</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {pointsDiagnostics.recentWatchAwards?.length > 0 && (
                              <div style={{marginTop:'0.4rem'}}>
                                <div style={{fontSize:'0.72rem', color:'#6b7280'}}>Recent watch awards:</div>
                                {pointsDiagnostics.recentWatchAwards.map((a, i) => (
                                  <div key={i} style={{fontSize:'0.72rem', color:'#22c55e'}}>
                                    {a.display_name}: +{a.amount} ({new Date(a.created_at).toLocaleTimeString()})
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <div style={{fontWeight:600, marginBottom:'0.5rem', color:'#e2e8f0'}}>📊 Leaderboard Cache</div>
                            <div style={{color:'#9ca3af'}}>Last fetched: <strong style={{color:'#e2e8f0'}}>{pointsDiagnostics.leaderboard.lastFetched ? new Date(pointsDiagnostics.leaderboard.lastFetched).toLocaleString() : 'Never'}</strong></div>
                            <div style={{color:'#9ca3af'}}>Affiliates: <strong style={{color:'#e2e8f0'}}>{pointsDiagnostics.leaderboard.affiliateCount}</strong></div>
                            <div style={{color:'#9ca3af'}}>Errors: <strong style={{color: pointsDiagnostics.leaderboard.consecutiveErrors > 0 ? '#f87171' : '#22c55e'}}>{pointsDiagnostics.leaderboard.consecutiveErrors}</strong></div>
                            {pointsDiagnostics.leaderboard.error && (
                              <div style={{fontSize:'0.72rem', color:'#f87171', marginTop:'0.2rem'}}>Last error: {pointsDiagnostics.leaderboard.error}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Danger Zone */}
                    <div className="ap-section" style={{marginTop:'2rem'}}>
                      <h3 className="ap-section-title" style={{color:'#f87171'}}>⚠️ Danger Zone</h3>
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', background:'rgba(248,113,113,0.05)', border:'1px solid rgba(248,113,113,0.15)', borderRadius:'10px'}}>
                        <div>
                          <div style={{fontSize:'0.85rem', fontWeight:600}}>Reset All Points</div>
                          <div style={{fontSize:'0.72rem', color:'#9ca3af'}}>Deletes all transactions, snapshots, redemptions, and cooldowns. Cannot be undone.</div>
                        </div>
                        <button className="ap-btn-sm red" style={{padding:'8px 16px', fontWeight:600}} onClick={resetAllPoints} disabled={adminPointsLoading}>
                          {adminPointsLoading ? 'Resetting...' : '🗑️ Reset All'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* CONFIG SUB-TAB */}
                {adminPointsSubTab === 'config' && (
                  <div className="ap-section">
                    <h3 className="ap-section-title">Points Configuration</h3>
                    <p style={{color:'#9ca3af', fontSize:'0.8rem', marginBottom:'1rem'}}>Configure earning rates and toggle sources. Changes take effect immediately.</p>
                    
                    <div className="ap-config-grid">
                      {Object.entries(adminPointsConfig).map(([key, cfg]) => (
                        <div key={key} className={`ap-config-item ${key.includes('enabled') ? 'toggle' : ''}`}>
                          <div className="ap-config-info">
                            <label className="ap-config-key">{key.replace(/_/g, ' ')}</label>
                            <span className="ap-config-desc">{cfg.description}</span>
                          </div>
                          {key.includes('enabled') ? (
                            <button 
                              className={`ap-toggle-btn ${cfg.value === 'true' ? 'on' : 'off'}`}
                              onClick={() => saveAdminPointsConfig([{ key, value: cfg.value === 'true' ? 'false' : 'true' }])}
                            >
                              {cfg.value === 'true' ? 'ON' : 'OFF'}
                            </button>
                          ) : (
                            <input 
                              className="ap-config-input"
                              type="number"
                              value={cfg.value}
                              onChange={e => setAdminPointsConfig(prev => ({...prev, [key]: {...cfg, value: e.target.value}}))}
                              onBlur={e => saveAdminPointsConfig([{ key, value: e.target.value }])}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* SHOP ITEMS SUB-TAB */}
                {adminPointsSubTab === 'shop' && (
                  <div className="ap-section">
                    <div className="ap-section-header">
                      <h3 className="ap-section-title">Shop Items ({adminShopItems.length})</h3>
                      <button className="ap-add-btn" onClick={() => { setShowAddShopItemModal(true); setEditingShopItem(null); setNewShopItem({ name: '', description: '', cost: '', cashValue: '', imageUrl: '', iconKey: 'custom', stock: '', maxPerUser: '', requiresShuffleUsername: false, requiresDiscordUsername: false, fulfillmentNotes: '', category: 'custom', sortOrder: '0' }); }}>
                        + Add Item
                      </button>
                    </div>
                    
                    {adminShopItems.length === 0 ? (
                      <div className="ap-empty">No shop items yet. Create one!</div>
                    ) : (
                      <div className="ap-shop-list">
                        {adminShopItems.map(item => (
                          <div key={item.id} className={`ap-shop-row ${!item.is_active ? 'inactive' : ''}`}>
                            <div className="ap-shop-row-main">
                              <span className="ap-shop-name">{item.name}</span>
                              <span className="ap-shop-cost">💎 {item.cost.toLocaleString()}</span>
                              {parseFloat(item.cash_value) > 0 && (
                                <span title="Cash value — counts against lossback in the Rewards Ledger" style={{fontSize:'0.72rem', fontWeight:700, color:'#4ade80', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'5px', padding:'0.1rem 0.4rem'}}>
                                  ${parseFloat(item.cash_value).toLocaleString()}
                                </span>
                              )}
                              <span className={`ap-shop-status ${item.is_active ? 'active' : 'inactive'}`}>
                                {item.is_active ? '✅ Active' : '❌ Inactive'}
                              </span>
                              {item.stock !== null && <span className="ap-shop-stock">Stock: {item.stock}</span>}
                              {item.max_per_user && <span className="ap-shop-limit">Limit: {item.max_per_user}/user</span>}
                            </div>
                            <div className="ap-shop-row-actions">
                              {!item.is_active ? (
                                <button className="ap-btn-sm green" onClick={() => updateShopItem(item.id, { isActive: true })}>Reactivate</button>
                              ) : (
                                <>
                                  <button className="ap-btn-sm" onClick={() => {
                                    setEditingShopItem(item);
                                    setNewShopItem({
                                      name: item.name, description: item.description || '', cost: item.cost.toString(),
                                      imageUrl: item.image_url || '', iconKey: item.icon_key || 'custom',
                                      stock: item.stock !== null ? item.stock.toString() : '',
                                      maxPerUser: item.max_per_user ? item.max_per_user.toString() : '',
                                      requiresShuffleUsername: item.requires_shuffle_username,
                                      requiresDiscordUsername: item.requires_discord_username,
                                      fulfillmentNotes: item.fulfillment_notes || '',
                                      category: item.category || 'custom', sortOrder: (item.sort_order || 0).toString(),
                                      cashValue: parseFloat(item.cash_value) > 0 ? String(parseFloat(item.cash_value)) : ''
                                    });
                                    setShowAddShopItemModal(true);
                                  }}>Edit</button>
                                  <button className="ap-btn-sm red" onClick={() => deleteShopItem(item.id)}>Deactivate</button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* REDEMPTIONS SUB-TAB */}
                {adminPointsSubTab === 'redemptions' && (
                  <div className="ap-section">
                    <div className="ap-section-header">
                      <h3 className="ap-section-title">Redemptions</h3>
                      <div className="ap-filter-btns">
                        {['pending', 'fulfilled', 'cancelled'].map(s => (
                          <button key={s} className={`ap-filter-btn ${adminPointsRedemptionsFilter === s ? 'active' : ''}`}
                            onClick={() => { setAdminPointsRedemptionsFilter(s); fetchAdminPointsRedemptions(s, 1); }}>
                            {s === 'pending' ? '⏳' : s === 'fulfilled' ? '✅' : '❌'} {s.charAt(0).toUpperCase() + s.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {adminPointsRedemptions.length === 0 ? (
                      <div className="ap-empty">No {adminPointsRedemptionsFilter} redemptions</div>
                    ) : (
                      <div className="ap-redemptions-list">
                        {adminPointsRedemptions.map(r => (
                          <div key={r.id} className="ap-redemption-row">
                            <div className="ap-redemption-info">
                              <div className="ap-redemption-user">
                                {r.user_avatar && <img src={r.user_avatar} alt="" className="ap-redemption-avatar" />}
                                <div>
                                  <div className="ap-redemption-name">{r.user_display_name}</div>
                                  <div className="ap-redemption-date">{new Date(r.created_at).toLocaleString()}</div>
                                </div>
                              </div>
                              <div className="ap-redemption-item">
                                <div className="ap-redemption-item-name">{r.item_name}</div>
                                <div className="ap-redemption-cost">💎 {r.points_spent.toLocaleString()}</div>
                              </div>
                              {(r.shuffle_username || r.discord_username) && (
                                <div className="ap-redemption-accounts">
                                  {r.shuffle_username && <span>🎰 {r.shuffle_username}</span>}
                                  {r.discord_username && <span>🎮 {r.discord_username}</span>}
                                </div>
                              )}
                              {r.notes && <div className="ap-redemption-notes">📝 {r.notes}</div>}
                            </div>
                            {r.status === 'pending' && (
                              <div className="ap-redemption-actions">
                                <button className="ap-btn-sm green" onClick={() => fulfillRedemption(r.id)}>✅ Fulfill</button>
                                <button className="ap-btn-sm red" onClick={() => cancelRedemption(r.id)}>❌ Cancel</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {adminPointsRedemptionsPagination.totalPages > 1 && (
                      <div className="points-pagination">
                        <button disabled={adminPointsRedemptionsPagination.page <= 1} onClick={() => fetchAdminPointsRedemptions(adminPointsRedemptionsFilter, adminPointsRedemptionsPagination.page - 1)}>← Prev</button>
                        <span>Page {adminPointsRedemptionsPagination.page} of {adminPointsRedemptionsPagination.totalPages}</span>
                        <button disabled={adminPointsRedemptionsPagination.page >= adminPointsRedemptionsPagination.totalPages} onClick={() => fetchAdminPointsRedemptions(adminPointsRedemptionsFilter, adminPointsRedemptionsPagination.page + 1)}>Next →</button>
                      </div>
                    )}
                  </div>
                )}

                {/* PAYOUTS SUB-TAB - Redemption history with parsed dollar values */}
                {adminPointsSubTab === 'payouts' && (
                  <div className="ap-section">
                    <h3 className="ap-section-title">💰 Redemption Payout Tracking</h3>
                    <PayoutSummaryBar metric="redemptions" range={adminPayoutRange} onRange={onPayoutRange} summary={adminPayoutSummary} loading={adminPayoutLoading} />
                    <p style={{color:'#9ca3af', fontSize:'0.8rem', marginBottom:'0.5rem'}}>
                      Dollar values are auto-parsed from item names (e.g. "$50 Shuffle Tip" → $50). Items where parsing failed are flagged in red — for those, set a bulk adjustment in the <strong>points_shop_bulk_adjustment</strong> setting to compensate. The total above reflects the selected time slice; the all-time breakdown is below.
                    </p>
                    {redemptionPayoutsData ? (
                      <>
                        <div style={{display:'flex', alignItems:'center', gap:'1rem', padding:'0.75rem 1rem', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'10px', marginBottom:'1rem'}}>
                          <div style={{fontSize:'0.7rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'1px'}}>Total Auto-Parsed</div>
                          <div style={{fontSize:'1.4rem', fontWeight:900, color:'#22c55e', fontFamily:'monospace'}}>${redemptionPayoutsData.totalParsedDollars.toLocaleString()}</div>
                          <button onClick={fetchRedemptionPayouts} style={{marginLeft:'auto', padding:'0.35rem 0.75rem', background:'rgba(139,92,246,0.15)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'6px', color:'#a78bfa', fontSize:'0.72rem', fontWeight:600, cursor:'pointer'}}>🔄 Refresh</button>
                        </div>
                        <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden'}}>
                          <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.78rem'}}>
                            <thead>
                              <tr style={{borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
                                <th style={{textAlign:'left', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>User</th>
                                <th style={{textAlign:'left', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Item</th>
                                <th style={{textAlign:'right', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Points</th>
                                <th style={{textAlign:'right', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>$ Value</th>
                                <th style={{textAlign:'center', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Status</th>
                                <th style={{textAlign:'right', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {redemptionPayoutsData.redemptions.map(r => (
                                <tr key={r.id} style={{borderBottom:'1px solid rgba(255,255,255,0.03)', background: !r.hasParsedValue && r.status === 'fulfilled' ? 'rgba(239,68,68,0.05)' : 'transparent'}}>
                                  <td style={{padding:'0.5rem 0.75rem'}}>
                                    <div style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                      {r.profile_image_url && <img src={r.profile_image_url} style={{width:'20px', height:'20px', borderRadius:'50%'}} alt="" />}
                                      <span style={{color:'#e2e8f0', fontFamily:'monospace', fontSize:'0.75rem'}}>{r.username}</span>
                                    </div>
                                  </td>
                                  <td style={{padding:'0.5rem 0.75rem', color:'#a1a1aa'}}>{r.item_name}</td>
                                  <td style={{textAlign:'right', padding:'0.5rem 0.75rem', color:'#eab308', fontFamily:'monospace'}}>{r.points_spent.toLocaleString()}</td>
                                  <td style={{textAlign:'right', padding:'0.5rem 0.75rem', fontFamily:'monospace', fontWeight:700, color: r.hasParsedValue ? '#22c55e' : '#ef4444'}}>
                                    {r.hasParsedValue ? `$${r.dollarValue}` : '⚠️ N/A'}
                                  </td>
                                  <td style={{textAlign:'center', padding:'0.5rem 0.75rem'}}>
                                    <span style={{fontSize:'0.65rem', fontWeight:700, padding:'0.15rem 0.5rem', borderRadius:'4px', background: r.status === 'fulfilled' ? 'rgba(34,197,94,0.12)' : r.status === 'pending' ? 'rgba(245,158,11,0.12)' : 'rgba(113,113,122,0.12)', color: r.status === 'fulfilled' ? '#22c55e' : r.status === 'pending' ? '#f59e0b' : '#71717a'}}>
                                      {r.status}
                                    </span>
                                  </td>
                                  <td style={{textAlign:'right', padding:'0.5rem 0.75rem', color:'#71717a', fontSize:'0.7rem'}}>{new Date(r.fulfilled_at || r.created_at).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {redemptionPayoutsData.redemptions.length === 0 && (
                            <div style={{textAlign:'center', padding:'1.5rem', color:'#71717a', fontSize:'0.85rem'}}>No redemptions yet</div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div style={{textAlign:'center', padding:'2rem', color:'#71717a'}}>Loading redemption history...</div>
                    )}
                  </div>
                )}

                {/* GRANT/DEDUCT SUB-TAB */}
                {adminPointsSubTab === 'grant' && (
                  <div className="ap-section">
                    <h3 className="ap-section-title">Grant or Deduct Points</h3>
                    <p style={{color:'#9ca3af', fontSize:'0.8rem', marginBottom:'1rem'}}>Manually add or remove points from a user. Use negative amounts to deduct.</p>
                    
                    <div className="ap-grant-form">
                      <div className="ap-grant-field">
                        <label>Twitch Username</label>
                        <input 
                          type="text" placeholder="e.g. snutzy"
                          value={adminGrantForm.twitchUsername}
                          onChange={e => setAdminGrantForm(f => ({...f, twitchUsername: e.target.value}))}
                        />
                      </div>
                      <div className="ap-grant-field">
                        <label>Amount (negative to deduct)</label>
                        <input 
                          type="number" placeholder="e.g. 500 or -200"
                          value={adminGrantForm.amount}
                          onChange={e => setAdminGrantForm(f => ({...f, amount: e.target.value}))}
                        />
                      </div>
                      <div className="ap-grant-field full">
                        <label>Reason</label>
                        <input 
                          type="text" placeholder="e.g. Bonus for community event"
                          value={adminGrantForm.reason}
                          onChange={e => setAdminGrantForm(f => ({...f, reason: e.target.value}))}
                        />
                      </div>
                      <button 
                        className="ap-grant-submit"
                        disabled={adminPointsLoading || !adminGrantForm.twitchUsername || !adminGrantForm.amount || !adminGrantForm.reason}
                        onClick={grantPoints}
                      >
                        {adminPointsLoading ? 'Processing...' : parseInt(adminGrantForm.amount) > 0 ? `Grant ${parseInt(adminGrantForm.amount || 0).toLocaleString()} 💎` : parseInt(adminGrantForm.amount) < 0 ? `Deduct ${Math.abs(parseInt(adminGrantForm.amount || 0)).toLocaleString()} 💎` : 'Enter Amount'}
                      </button>
                    </div>
                  </div>
                )}

                {/* WAGER RECONCILIATION SUB-TAB */}
                {adminPointsSubTab === 'reconcile' && (
                  <div className="ap-section">
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem'}}>
                      <div>
                        <h3 className="ap-section-title" style={{marginBottom:'0.25rem'}}>Wager Points Reconciliation</h3>
                        <p style={{color:'#9ca3af', fontSize:'0.78rem', margin:0}}>Compares expected wager points (total wager ÷ ${wagerReconciliation?.dollarsPerPoint || '?'}/pt) against actual points awarded. Assumes baseline of $0 — review before bulk awarding.</p>
                      </div>
                      <div style={{display:'flex', gap:'0.5rem', flexShrink:0}}>
                        <button 
                          onClick={fetchWagerReconciliation}
                          style={{padding:'0.45rem 0.9rem', background:'rgba(139,92,246,0.15)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'8px', color:'#a78bfa', fontSize:'0.78rem', fontWeight:600, cursor:'pointer'}}
                        >🔄 Refresh</button>
                        {wagerReconciliation && wagerReconciliation.usersWithDeficit > 0 && (
                          <button 
                            onClick={async () => {
                              if (!window.confirm(`Award missing points to ${wagerReconciliation.usersWithDeficit} users (${wagerReconciliation.totalDeficitPoints.toLocaleString()} total points)? This cannot be undone.`)) return;
                              try {
                                const token = localStorage.getItem('auth_token');
                                const res = await fetch(`${BACKEND_API_URL}/api/admin/points/wager-reconcile-all`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                                });
                                const data = await res.json();
                                if (res.ok) {
                                  setAdminPointsMessage({ type: 'success', text: `Reconciled ${data.usersAwarded} users, awarded ${data.totalPointsAwarded.toLocaleString()} points total` });
                                  fetchWagerReconciliation();
                                } else {
                                  setAdminPointsMessage({ type: 'error', text: data.error || 'Failed' });
                                }
                              } catch (e) { setAdminPointsMessage({ type: 'error', text: 'Error: ' + e.message }); }
                            }}
                            style={{padding:'0.45rem 0.9rem', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'8px', color:'#22c55e', fontSize:'0.78rem', fontWeight:600, cursor:'pointer'}}
                          >⚡ Reconcile All ({wagerReconciliation.totalDeficitPoints.toLocaleString()} pts)</button>
                        )}
                      </div>
                    </div>

                    {!wagerReconciliation ? (
                      <div style={{textAlign:'center', padding:'2rem', color:'#71717a'}}>Loading reconciliation data...</div>
                    ) : (
                      <>
                        {/* Summary stats */}
                        <div style={{display:'flex', gap:'0.6rem', marginBottom:'1.25rem', flexWrap:'wrap'}}>
                          <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', padding:'0.7rem 1rem', flex:1, minWidth:'120px', textAlign:'center'}}>
                            <div style={{fontSize:'1.2rem', fontWeight:800, color:'#a78bfa'}}>{wagerReconciliation.totalUsers}</div>
                            <div style={{fontSize:'0.68rem', color:'#71717a'}}>Tracked Users</div>
                          </div>
                          <div style={{background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'10px', padding:'0.7rem 1rem', flex:1, minWidth:'120px', textAlign:'center'}}>
                            <div style={{fontSize:'1.2rem', fontWeight:800, color:'#f87171'}}>{wagerReconciliation.usersWithDeficit}</div>
                            <div style={{fontSize:'0.68rem', color:'#71717a'}}>Users Missing Pts</div>
                          </div>
                          <div style={{background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'10px', padding:'0.7rem 1rem', flex:1, minWidth:'120px', textAlign:'center'}}>
                            <div style={{fontSize:'1.2rem', fontWeight:800, color:'#fbbf24'}}>{wagerReconciliation.totalDeficitPoints.toLocaleString()}</div>
                            <div style={{fontSize:'0.68rem', color:'#71717a'}}>Total Missing Pts</div>
                          </div>
                          <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', padding:'0.7rem 1rem', flex:1, minWidth:'120px', textAlign:'center'}}>
                            <div style={{fontSize:'1.2rem', fontWeight:800, color:'#34d399'}}>${wagerReconciliation.dollarsPerPoint}</div>
                            <div style={{fontSize:'0.68rem', color:'#71717a'}}>Per Point Rate</div>
                          </div>
                        </div>

                        {/* User table */}
                        <div style={{overflowX:'auto'}}>
                          <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.78rem'}}>
                            <thead>
                              <tr style={{borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
                                <th style={{textAlign:'left', padding:'0.6rem 0.5rem', color:'#9ca3af', fontWeight:600}}>User</th>
                                <th style={{textAlign:'right', padding:'0.6rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Total Wager</th>
                                <th style={{textAlign:'right', padding:'0.6rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Expected</th>
                                <th style={{textAlign:'right', padding:'0.6rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Actual</th>
                                <th style={{textAlign:'right', padding:'0.6rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Deficit</th>
                                <th style={{textAlign:'center', padding:'0.6rem 0.5rem', color:'#9ca3af', fontWeight:600}}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wagerReconciliation.users.map(u => (
                                <tr key={u.userId} style={{borderBottom:'1px solid rgba(255,255,255,0.04)', background: u.deficit > 0 ? 'rgba(239,68,68,0.03)' : 'transparent'}}>
                                  <td style={{padding:'0.55rem 0.5rem'}}>
                                    <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                                      {u.profileImageUrl && <img src={u.profileImageUrl} alt="" style={{width:26, height:26, borderRadius:'50%'}} />}
                                      <div>
                                        <div style={{fontWeight:600, color:'#e2e8f0'}}>{u.displayName}</div>
                                        <div style={{fontSize:'0.68rem', color:'#6b7280'}}>{u.shuffleUsername}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{textAlign:'right', padding:'0.55rem 0.5rem', color:'#a1a1aa', fontFamily:'monospace'}}>
                                    {u.currentWager !== null ? `$${u.currentWager.toLocaleString(undefined, {minimumFractionDigits:0, maximumFractionDigits:0})}` : '—'}
                                  </td>
                                  <td style={{textAlign:'right', padding:'0.55rem 0.5rem', color:'#a78bfa', fontFamily:'monospace', fontWeight:600}}>
                                    {u.expectedPoints !== null ? u.expectedPoints.toLocaleString() : '—'}
                                  </td>
                                  <td style={{textAlign:'right', padding:'0.55rem 0.5rem', color:'#e2e8f0', fontFamily:'monospace'}}>
                                    {u.actualPoints.toLocaleString()}
                                  </td>
                                  <td style={{textAlign:'right', padding:'0.55rem 0.5rem', fontFamily:'monospace', fontWeight:700, color: u.deficit > 0 ? '#f87171' : u.deficit === 0 ? '#22c55e' : '#6b7280'}}>
                                    {u.deficit !== null ? (u.deficit > 0 ? `+${u.deficit.toLocaleString()}` : '✓ OK') : '—'}
                                  </td>
                                  <td style={{textAlign:'center', padding:'0.55rem 0.5rem'}}>
                                    {u.deficit > 0 && (
                                      <button
                                        disabled={reconcilingUserId === u.userId}
                                        onClick={async () => {
                                          setReconcilingUserId(u.userId);
                                          try {
                                            const token = localStorage.getItem('auth_token');
                                            const res = await fetch(`${BACKEND_API_URL}/api/admin/points/wager-reconcile`, {
                                              method: 'POST',
                                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                              body: JSON.stringify({ userId: u.userId, amount: u.deficit, note: `Reconciliation: +${u.deficit} pts (expected ${u.expectedPoints}, had ${u.actualPoints}, wager $${u.currentWager?.toLocaleString()})` })
                                            });
                                            const data = await res.json();
                                            if (res.ok) {
                                              setAdminPointsMessage({ type: 'success', text: `Awarded ${u.deficit} pts to ${u.displayName}` });
                                              fetchWagerReconciliation();
                                            } else {
                                              setAdminPointsMessage({ type: 'error', text: data.error || 'Failed' });
                                            }
                                          } catch (e) { setAdminPointsMessage({ type: 'error', text: 'Error: ' + e.message }); }
                                          setReconcilingUserId(null);
                                        }}
                                        style={{padding:'0.3rem 0.65rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'6px', color:'#22c55e', fontSize:'0.72rem', fontWeight:600, cursor: reconcilingUserId === u.userId ? 'not-allowed' : 'pointer'}}
                                      >
                                        {reconcilingUserId === u.userId ? '...' : `+${u.deficit.toLocaleString()} pts`}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {wagerReconciliation.users.length === 0 && (
                          <div style={{textAlign:'center', padding:'2rem', color:'#71717a'}}>No users with wager snapshots found.</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Add/Edit Shop Item Modal */}

            {/* Fraud Detection Tab */}
            {adminTab === 'fraud' && (
              <div className="admin-section">
                <div className="admin-section-header">
                  <h2>🔍 Fraud Detection — Multi-Account Finder</h2>
                  <button onClick={rescanFraudIps} disabled={fraudRescanning}
                    title="Re-check all stored IPs against the datacenter/VPN lists and refresh timezone countries"
                    style={{padding:'0.35rem 0.75rem', background:'rgba(139,92,246,0.15)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'6px', color:'#a78bfa', fontSize:'0.75rem', fontWeight:700, cursor:'pointer'}}>
                    {fraudRescanning ? 'Rescanning...' : '🌐 Rescan IPs'}
                  </button>
                </div>

                {/* Fraud sub-tabs */}
                <div style={{display:'flex', gap:'0.4rem', flexWrap:'wrap', alignItems:'center', marginBottom:'1rem'}}>
                  {[
                    ['overview', '📊 Overview'],
                    ['user', '🔎 User Lookup'],
                    ['clusters', '🔗 IP & Device Clusters'],
                    ['vpn', '🖥️ VPS & VPN'],
                    ['payout', '💸 Payouts'],
                    ['behavior', '🎯 Behavior'],
                    ...(isAdmin ? [['transfers', '🔁 Transfers']] : []),
                    ...(user?.username?.toLowerCase() === 'notpa1n' ? [['shadowbans', '🚫 Shadowbans']] : [])
                  ].map(([key, label]) => (
                    <button key={key} onClick={() => setFraudView(key)}
                      style={{padding:'0.4rem 0.8rem', background: fraudView === key ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.04)', border: `1px solid ${fraudView === key ? 'rgba(139,92,246,0.45)' : 'rgba(255,255,255,0.08)'}`, borderRadius:'6px', color: fraudView === key ? '#c4b5fd' : '#9ca3af', fontSize:'0.78rem', fontWeight:700, cursor:'pointer'}}>
                      {label}
                    </button>
                  ))}
                  <label style={{display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.75rem', color:'#9ca3af', marginLeft:'auto'}}>
                    <input type="checkbox" checked={fraudHideAcked} onChange={e => setFraudHideAcked(e.target.checked)} style={{accentColor:'#8b5cf6'}} />
                    Hide acknowledged
                  </label>
                </div>

                {/* Overview */}
                {fraudView === 'overview' && fraudData && (
                  <>
                  <div style={{display:'flex', gap:'0.75rem', marginBottom:'0.75rem', flexWrap:'wrap'}}>
                    <div style={{background:'rgba(139,92,246,0.1)', border:'1px solid rgba(139,92,246,0.2)', borderRadius:'8px', padding:'0.6rem 1rem', flex:1, minWidth:'140px', textAlign:'center'}}>
                      <div style={{fontSize:'1.3rem', fontWeight:800, color:'#a78bfa'}}>{fraudData.stats.uniqueUsers}</div>
                      <div style={{fontSize:'0.7rem', color:'#71717a'}}>Users Tracked</div>
                    </div>
                    <div style={{background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'0.6rem 1rem', flex:1, minWidth:'140px', textAlign:'center'}}>
                      <div style={{fontSize:'1.3rem', fontWeight:800, color:'#f87171'}}>
                        {fraudData.sharedIPs.filter(c => !fraudData.acknowledgements?.[`ip:${c.ip_address}`]).length}
                        {fraudData.sharedIPs.some(c => fraudData.acknowledgements?.[`ip:${c.ip_address}`]) && <span style={{fontSize:'0.7rem', color:'#71717a'}}> / {fraudData.sharedIPs.length}</span>}
                      </div>
                      <div style={{fontSize:'0.7rem', color:'#71717a'}}>Shared IP Clusters</div>
                    </div>
                    <div style={{background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'8px', padding:'0.6rem 1rem', flex:1, minWidth:'140px', textAlign:'center'}}>
                      <div style={{fontSize:'1.3rem', fontWeight:800, color:'#fbbf24'}}>
                        {fraudData.sharedFingerprints.filter(c => !fraudData.acknowledgements?.[`fingerprint:${c.fingerprint_hash}`]).length}
                        {fraudData.sharedFingerprints.some(c => fraudData.acknowledgements?.[`fingerprint:${c.fingerprint_hash}`]) && <span style={{fontSize:'0.7rem', color:'#71717a'}}> / {fraudData.sharedFingerprints.length}</span>}
                      </div>
                      <div style={{fontSize:'0.7rem', color:'#71717a'}}>Shared Device Clusters</div>
                    </div>
                    <div style={{background:'rgba(56,189,248,0.1)', border:'1px solid rgba(56,189,248,0.2)', borderRadius:'8px', padding:'0.6rem 1rem', flex:1, minWidth:'140px', textAlign:'center'}}>
                      <div style={{fontSize:'1.3rem', fontWeight:800, color:'#38bdf8'}}>
                        {(fraudData.datacenterUsers?.length || 0) + (fraudData.vpsSuspects?.length || 0)}
                      </div>
                      <div style={{fontSize:'0.7rem', color:'#71717a'}}>VPS / VPN Suspects</div>
                    </div>
                    <div style={{background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.2)', borderRadius:'8px', padding:'0.6rem 1rem', flex:1, minWidth:'140px', textAlign:'center'}}>
                      <div style={{fontSize:'1.3rem', fontWeight:800, color:'#34d399'}}>{fraudData.stats.totalFingerprints}</div>
                      <div style={{fontSize:'0.7rem', color:'#71717a'}}>Total Records</div>
                    </div>
                  </div>
                  {!fraudData.stats.ipIntelLoaded && (
                    <div style={{fontSize:'0.72rem', color:'#fbbf24', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'6px', padding:'0.4rem 0.7rem', marginBottom:'0.75rem'}}>
                      ⏳ Datacenter/VPN IP lists are still loading on the backend (starts ~20s after boot). Rescan in a minute.
                    </div>
                  )}
                  {(() => {
                    const acks = fraudData.acknowledgements || {};
                    const unacked = (list, type, keyFn) => (list || []).filter(c => !acks[`${type}:${keyFn(c)}`]).length;
                    const cards = [
                      { view:'clusters', icon:'🚨', title:'Shared IPs', count: unacked(fraudData.sharedIPs, 'ip', c => c.ip_address), desc:'Multiple accounts seen on the same IP address', color:'#f87171' },
                      { view:'clusters', icon:'⚠️', title:'Shared Devices', count: unacked(fraudData.sharedFingerprints, 'fingerprint', c => c.fingerprint_hash) + unacked(fraudData.sharedCanvas, 'canvas', c => c.canvas_hash), desc:'Same device fingerprint or canvas hash across accounts', color:'#fbbf24' },
                      { view:'vpn', icon:'🖥️', title:'VPS / VM Browsers', count: unacked(fraudData.vpsSuspects, 'vps', c => c.id), desc:'Software or virtualized GPU — check corroboration', color:'#f87171' },
                      { view:'vpn', icon:'🌐', title:'Datacenter / VPN IPs', count: unacked(fraudData.datacenterUsers, 'dcip', c => c.id), desc:'Connections from hosting or known VPN ranges', color:'#fb923c' },
                      { view:'vpn', icon:'🌍', title:'Country Mismatch', count: unacked(fraudData.countryMismatches, 'geo', c => c.id), desc:'IP country differs from device timezone country', color:'#fbbf24' },
                      { view:'payout', icon:'💸', title:'Shared Payouts', count: unacked(fraudData.payoutClusters, 'payout', c => c.shuffle_username) + unacked(fraudData.payoutEntryClusters, 'payoutentry', c => c.shuffle_username), desc:'Different accounts pointing at the same Shuffle username', color:'#a78bfa' },
                      { view:'behavior', icon:'🎯', title:'Behavior Analysis', count: null, desc:'Co-entry, bot timing, never-simultaneous entry pairs', color:'#38bdf8' }
                    ];
                    return (
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(230px, 1fr))', gap:'0.6rem', marginBottom:'1rem'}}>
                        {cards.map((c, i) => (
                          <button key={i} onClick={() => setFraudView(c.view)}
                            style={{textAlign:'left', background:'rgba(14,14,18,0.6)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', padding:'0.8rem 0.9rem', cursor:'pointer'}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.3rem'}}>
                              <span style={{fontSize:'1rem'}}>{c.icon}</span>
                              <span style={{fontSize:'0.85rem', fontWeight:700, color:'#e2e8f0'}}>{c.title}</span>
                              {c.count !== null && (
                                <span style={{marginLeft:'auto', fontSize:'0.9rem', fontWeight:800, color: c.count > 0 ? c.color : '#22c55e'}}>{c.count}</span>
                              )}
                              {c.count === null && <span style={{marginLeft:'auto', fontSize:'0.7rem', color:'#71717a'}}>run →</span>}
                            </div>
                            <div style={{fontSize:'0.68rem', color:'#71717a'}}>{c.desc}</div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <div style={{fontSize:'0.72rem', color:'#71717a'}}>
                    Counts show unacknowledged flags ({Object.keys(fraudData.acknowledgements || {}).length} acknowledged in total). Click a card to open its tab, or look up any user for a full profile.
                  </div>
                  </>
                )}

                {/* User Lookup view */}
                {fraudView === 'user' && (
                <>
                <div style={{background:'rgba(14,14,18,0.6)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'1rem', marginBottom:'1.25rem'}}>
                  <div style={{fontSize:'0.85rem', fontWeight:600, marginBottom:'0.5rem', color:'#e2e8f0'}}>🔎 Search User</div>
                  <div style={{display:'flex', gap:'0.5rem', marginBottom:'0.5rem'}}>
                    <input 
                      type="text" 
                      placeholder="Enter username..." 
                      value={fraudSearch}
                      onChange={e => { setFraudSearch(e.target.value); if (e.target.value.length >= 2) searchFraudUsers(e.target.value); else setFraudSearchResults([]); }}
                      style={{flex:1, padding:'0.45rem 0.7rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#fff', fontSize:'0.85rem'}}
                    />
                  </div>
                  {fraudSearchResults.length > 0 && (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.3rem', maxHeight:'200px', overflowY:'auto'}}>
                      {fraudSearchResults.map(u => (
                        <button key={u.id} onClick={() => { fetchFraudUserDetail(u.id); setFraudSearch(''); setFraudSearchResults([]); }} 
                          style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.4rem 0.6rem', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'6px', cursor:'pointer', color:'#e2e8f0', fontSize:'0.8rem', textAlign:'left'}}>
                          {u.profile_image_url && <img src={u.profile_image_url} alt="" style={{width:24, height:24, borderRadius:'50%'}} />}
                          <span style={{fontWeight:600}}>{u.display_name}</span>
                          {u.shuffle_username && <span style={{fontSize:'0.65rem', color:'#a78bfa'}}>🎰 {u.shuffle_username}</span>}
                          {u.discord_username && <span style={{fontSize:'0.65rem', color:'#5865F2'}}>💬 {u.discord_actual_username || u.discord_username}</span>}
                          <span style={{marginLeft:'auto', color:'#71717a', fontSize:'0.7rem'}}>{u.unique_ips || 0} IPs • {u.unique_fingerprints || 0} FPs</span>
                          {u.last_seen && <span style={{color:'#71717a', fontSize:'0.65rem'}}>{new Date(u.last_seen).toLocaleDateString()}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* User Detail Panel */}
                {fraudUserDetail && (
                  <div style={{background:'rgba(14,14,18,0.6)', border:'1px solid rgba(139,92,246,0.2)', borderRadius:'8px', padding:'1rem', marginBottom:'1.25rem'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1rem'}}>
                      {fraudUserDetail.user.profile_image_url && <img src={fraudUserDetail.user.profile_image_url} alt="" style={{width:40, height:40, borderRadius:'50%'}} />}
                      <div>
                        <div style={{fontSize:'1rem', fontWeight:700, color:'#fff'}}>{fraudUserDetail.user.display_name}</div>
                        <div style={{fontSize:'0.7rem', color:'#71717a'}}>ID: {fraudUserDetail.user.id} • Twitch: {fraudUserDetail.user.twitch_id}</div>
                        {fraudUserDetail.user.twitch_created_at && (() => {
                          const ageDays = Math.floor((Date.now() - new Date(fraudUserDetail.user.twitch_created_at).getTime()) / 86400000);
                          const fresh = ageDays < 30;
                          return (
                            <div style={{fontSize:'0.68rem', color: fresh ? '#f87171' : '#71717a'}}>
                              Twitch account created {new Date(fraudUserDetail.user.twitch_created_at).toLocaleDateString()} ({ageDays} days old){fresh ? ' ⚠️ FRESH' : ''}
                            </div>
                          );
                        })()}
                        <div style={{fontSize:'0.68rem', color:'#71717a', display:'flex', flexWrap:'wrap', gap:'0.4rem', marginTop:'0.15rem'}}>
                          {fraudUserDetail.user.shuffle_username && <span>🎰 {fraudUserDetail.user.shuffle_username}</span>}
                          {fraudUserDetail.user.discord_username && <span>💬 {fraudUserDetail.user.discord_actual_username || fraudUserDetail.user.discord_username}</span>}
                          {!fraudUserDetail.user.shuffle_username && !fraudUserDetail.user.discord_username && <span style={{color:'#71717a'}}>No Shuffle or Discord linked</span>}
                        </div>
                      </div>
                      <button onClick={() => setFraudUserDetail(null)} style={{marginLeft:'auto', background:'none', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#71717a', padding:'0.3rem 0.6rem', cursor:'pointer', fontSize:'0.75rem'}}>✕ Close</button>
                    </div>

                    {/* Risk summary, per-IP intel, device profile */}
                    {(() => {
                      const ips = fraudUserDetail.ipSummary || [];
                      const dev = fraudUserDetail.deviceSummary || {};
                      const vmGpuRegex = /swiftshader|llvmpipe|softpipe|virtualbox|vmware|basic render driver|qxl|virtio|virgl|parallels/i;
                      const vpnCount = ips.filter(ip => ip.is_vpn).length;
                      const dcCount = ips.filter(ip => ip.is_datacenter && !ip.is_vpn).length;
                      const softwareGpus = (dev.renderers || []).filter(r => vmGpuRegex.test(r));
                      const hardwareGpus = (dev.renderers || []).filter(r => !vmGpuRegex.test(r));
                      const hasGeoMismatch = ips.some(ip => ip.geo_mismatch);
                      const chip = (text, color, bg, border, title) => (
                        <span key={text} title={title || ''} style={{fontSize:'0.7rem', fontWeight:700, color, background: bg, border: `1px solid ${border}`, padding:'0.2rem 0.5rem', borderRadius:'4px'}}>{text}</span>
                      );
                      return (
                        <>
                          <div style={{display:'flex', flexWrap:'wrap', gap:'0.4rem', marginBottom:'1rem'}}>
                            {vpnCount > 0 && chip(`🔒 ${vpnCount} VPN IP${vpnCount > 1 ? 's' : ''}`, '#fb923c', 'rgba(251,146,60,0.1)', 'rgba(251,146,60,0.3)', 'Connected from known commercial VPN ranges')}
                            {dcCount > 0 && chip(`🏢 ${dcCount} datacenter IP${dcCount > 1 ? 's' : ''}`, '#f87171', 'rgba(239,68,68,0.1)', 'rgba(239,68,68,0.3)', 'Connected from hosting-provider ranges (VPS territory)')}
                            {softwareGpus.length > 0 && chip('🖥️ VM / software GPU seen', '#f87171', 'rgba(239,68,68,0.1)', 'rgba(239,68,68,0.3)', softwareGpus.join(' • '))}
                            {hasGeoMismatch && chip('🌍 country mismatch', '#fbbf24', 'rgba(245,158,11,0.1)', 'rgba(245,158,11,0.3)', 'IP country differs from device timezone country')}
                            {vpnCount === 0 && dcCount === 0 && softwareGpus.length === 0 && !hasGeoMismatch &&
                              chip('✅ No infrastructure red flags', '#22c55e', 'rgba(34,197,94,0.08)', 'rgba(34,197,94,0.25)')}
                          </div>

                          {ips.length > 0 && (
                            <div style={{marginBottom:'1rem'}}>
                              <div style={{fontSize:'0.8rem', fontWeight:600, color:'#e2e8f0', marginBottom:'0.4rem'}}>🌐 IP Addresses ({ips.length})</div>
                              <div style={{display:'flex', flexDirection:'column', gap:'0.2rem', maxHeight:'220px', overflowY:'auto'}}>
                                {ips.map((ip, i) => (
                                  <div key={i} style={{display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.3rem 0.55rem', background:'rgba(255,255,255,0.02)', border:`1px solid ${ip.is_vpn || ip.is_datacenter ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)'}`, borderRadius:'4px', fontSize:'0.7rem', fontFamily:'monospace', color:'#9ca3af', flexWrap:'wrap'}}>
                                    <span style={{color:'#e2e8f0', minWidth:'110px'}}>{ip.ip_address}</span>
                                    <span style={{minWidth:'28px'}}>{ip.ip_country || '—'}</span>
                                    {ip.is_vpn ? (
                                      <span style={{color:'#fb923c', fontWeight:700}}>VPN</span>
                                    ) : ip.is_datacenter ? (
                                      <span style={{color:'#f87171', fontWeight:700}}>DATACENTER</span>
                                    ) : (
                                      <span style={{color:'#22c55e'}}>residential</span>
                                    )}
                                    {ip.geo_mismatch && <span style={{color:'#fbbf24'}}>geo≠tz</span>}
                                    <span style={{marginLeft:'auto', color:'#71717a'}}>{ip.sightings}× · {new Date(ip.first_seen).toLocaleDateString()} → {new Date(ip.last_seen).toLocaleDateString()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {!!(dev.renderers?.length || dev.resolutions?.length || dev.timezones?.length) && (
                            <div style={{marginBottom:'1rem'}}>
                              <div style={{fontSize:'0.8rem', fontWeight:600, color:'#e2e8f0', marginBottom:'0.4rem'}}>🖥️ Device Profile</div>
                              <div style={{fontSize:'0.7rem', color:'#9ca3af', display:'flex', flexDirection:'column', gap:'0.25rem'}}>
                                {dev.renderers?.length > 0 && (
                                  <div>GPU: {dev.renderers.map((r, i) => (
                                    <span key={i} style={{color: vmGpuRegex.test(r) ? '#f87171' : '#e2e8f0', fontFamily:'monospace'}}>
                                      {r}{vmGpuRegex.test(r) ? ' ⚠ software/VM' : ''}{i < dev.renderers.length - 1 ? ' • ' : ''}
                                    </span>
                                  ))}</div>
                                )}
                                {dev.resolutions?.length > 0 && <div>Screens: <span style={{color:'#e2e8f0', fontFamily:'monospace'}}>{dev.resolutions.join(' • ')}</span></div>}
                                {dev.timezones?.length > 0 && <div>Timezones: <span style={{color:'#e2e8f0', fontFamily:'monospace'}}>{dev.timezones.join(' • ')}</span>{dev.tz_countries?.length > 0 && <span style={{color:'#71717a'}}> (real country: {dev.tz_countries.join(', ')})</span>}</div>}
                                {dev.platforms?.length > 0 && <div>Platforms: <span style={{color:'#e2e8f0', fontFamily:'monospace'}}>{dev.platforms.join(' • ')}</span></div>}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* Linked accounts by IP */}
                    {fraudUserDetail.ipMatches.length > 0 && (
                      <div style={{marginBottom:'1rem'}}>
                        <div style={{fontSize:'0.8rem', fontWeight:600, color:'#f87171', marginBottom:'0.4rem'}}>🚨 Shared IP Addresses ({fraudUserDetail.ipMatches.length} matches)</div>
                        <div style={{display:'flex', flexDirection:'column', gap:'0.25rem'}}>
                          {[...new Map(fraudUserDetail.ipMatches.map(m => [m.id, m])).values()].map(match => (
                            <button key={match.id} onClick={() => fetchFraudUserDetail(match.id)}
                              style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.35rem 0.6rem', background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'6px', cursor:'pointer', color:'#e2e8f0', fontSize:'0.78rem', textAlign:'left'}}>
                              {match.profile_image_url && <img src={match.profile_image_url} alt="" style={{width:22, height:22, borderRadius:'50%'}} />}
                              <span style={{fontWeight:600}}>{match.display_name}</span>
                              <span style={{color:'#f87171', fontSize:'0.68rem', marginLeft:'auto', fontFamily:'monospace'}}>{match.ip_address}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Linked accounts by fingerprint */}
                    {fraudUserDetail.fingerprintMatches.length > 0 && (
                      <div style={{marginBottom:'1rem'}}>
                        <div style={{fontSize:'0.8rem', fontWeight:600, color:'#fbbf24', marginBottom:'0.4rem'}}>⚠️ Shared Device Fingerprint ({fraudUserDetail.fingerprintMatches.length} matches)</div>
                        <div style={{display:'flex', flexDirection:'column', gap:'0.25rem'}}>
                          {[...new Map(fraudUserDetail.fingerprintMatches.map(m => [m.id, m])).values()].map(match => (
                            <button key={match.id} onClick={() => fetchFraudUserDetail(match.id)}
                              style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.35rem 0.6rem', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'6px', cursor:'pointer', color:'#e2e8f0', fontSize:'0.78rem', textAlign:'left'}}>
                              {match.profile_image_url && <img src={match.profile_image_url} alt="" style={{width:22, height:22, borderRadius:'50%'}} />}
                              <span style={{fontWeight:600}}>{match.display_name}</span>
                              <span style={{color:'#fbbf24', fontSize:'0.68rem', marginLeft:'auto', fontFamily:'monospace'}}>{match.fingerprint_hash?.substring(0, 8)}...</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {fraudUserDetail.ipMatches.length === 0 && fraudUserDetail.fingerprintMatches.length === 0 && (
                      <div style={{color:'#22c55e', fontSize:'0.85rem', padding:'0.5rem 0'}}>✅ No shared IPs or fingerprints found — looks clean.</div>
                    )}

                    {/* Giveaway Activity */}
                    {(fraudUserDetail.giveawayEntries?.length > 0 || fraudUserDetail.giveawayWins?.length > 0) && (
                      <div style={{marginTop:'0.75rem', marginBottom:'0.5rem'}}>
                        <div style={{fontSize:'0.8rem', fontWeight:600, color:'#a78bfa', marginBottom:'0.4rem'}}>
                          🎰 Giveaway Activity — {fraudUserDetail.giveawayEntries?.length || 0} sessions entered, {fraudUserDetail.giveawayWins?.filter(w => !w.invalidated_at).length || 0} wins
                        </div>
                        <div style={{display:'flex', gap:'0.3rem', flexWrap:'wrap'}}>
                          {fraudUserDetail.giveawayEntries?.map((e, i) => (
                            <span key={i} style={{padding:'0.15rem 0.4rem', borderRadius:'4px', fontSize:'0.65rem', fontFamily:'monospace',
                              background: e.won ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                              border: e.won ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(255,255,255,0.06)',
                              color: e.won ? '#22c55e' : '#71717a'
                            }}>#{e.session_id}{e.won ? ' ✓' : ''}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Chat Log Stats & Messages */}
                    {fraudUserDetail.chatStats && parseInt(fraudUserDetail.chatStats.total_messages) > 0 && (
                      <div style={{marginTop:'0.75rem', marginBottom:'0.5rem'}}>
                        <div style={{fontSize:'0.8rem', fontWeight:600, color:'#38bdf8', marginBottom:'0.4rem'}}>
                          💬 Giveaway Chat — {fraudUserDetail.chatStats.total_messages} messages across {fraudUserDetail.chatStats.sessions_chatted} sessions
                          {parseInt(fraudUserDetail.chatStats.total_messages) > 0 && (
                            <span style={{marginLeft:'0.5rem', fontSize:'0.7rem', fontWeight:400, color: parseInt(fraudUserDetail.chatStats.keyword_messages) === parseInt(fraudUserDetail.chatStats.total_messages) ? '#f87171' : '#71717a'}}>
                              ({Math.round(100 * fraudUserDetail.chatStats.keyword_messages / fraudUserDetail.chatStats.total_messages)}% keyword-only{parseInt(fraudUserDetail.chatStats.keyword_messages) === parseInt(fraudUserDetail.chatStats.total_messages) ? ' ⚠️' : ''})
                            </span>
                          )}
                        </div>
                        <details>
                          <summary style={{fontSize:'0.72rem', color:'#71717a', cursor:'pointer', marginBottom:'0.3rem'}}>Recent messages ({fraudUserDetail.chatLog?.length || 0})</summary>
                          <div style={{maxHeight:'180px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'0.15rem'}}>
                            {fraudUserDetail.chatLog?.map((msg, i) => (
                              <div key={i} style={{padding:'0.25rem 0.5rem', background: msg.is_keyword ? 'rgba(245,158,11,0.06)' : 'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.04)', borderRadius:'3px', fontSize:'0.68rem', color:'#9ca3af', display:'flex', gap:'0.5rem'}}>
                                <span style={{color:'#71717a', flexShrink:0, fontSize:'0.62rem', minWidth:'55px'}}>{new Date(msg.sent_at).toLocaleTimeString()}</span>
                                <span style={{color: msg.is_keyword ? '#fbbf24' : '#e2e8f0'}}>{msg.message}</span>
                                {msg.is_keyword && <span style={{color:'#fbbf24', fontSize:'0.6rem', flexShrink:0}}>KW</span>}
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}

                    {/* Recent fingerprints */}
                    <details style={{marginTop:'0.5rem'}}>
                      <summary style={{fontSize:'0.75rem', color:'#71717a', cursor:'pointer', marginBottom:'0.4rem'}}>Raw fingerprint history ({fraudUserDetail.fingerprints.length} records)</summary>
                      <div style={{maxHeight:'250px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'0.2rem'}}>
                        {fraudUserDetail.fingerprints.map(fp => {
                          const softwareGpu = /swiftshader|llvmpipe|softpipe|virtualbox|vmware|basic render driver|qxl|virtio|virgl|parallels/i.test(fp.webgl_renderer || '');
                          return (
                          <div key={fp.id} style={{padding:'0.35rem 0.5rem', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.04)', borderRadius:'4px', fontSize:'0.68rem', color:'#9ca3af', fontFamily:'monospace', display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'0.3rem'}}>
                            <span>
                              IP: {fp.ip_address}{fp.ip_country ? ` (${fp.ip_country})` : ''}
                              {fp.is_datacenter && <span style={{color:'#f87171', marginLeft:'0.3rem'}}>DC</span>}
                              {fp.is_vpn && <span style={{color:'#fb923c', marginLeft:'0.3rem'}}>VPN</span>}
                            </span>
                            <span>FP: {fp.fingerprint_hash?.substring(0, 10)}</span>
                            <span>
                              {fp.screen_resolution} • {fp.timezone}
                              {fp.geo_mismatch && <span style={{color:'#fbbf24', marginLeft:'0.3rem'}} title={`IP says ${fp.ip_country}, timezone says ${fp.tz_country}`}>🌍≠</span>}
                              {softwareGpu && <span style={{color:'#f87171', marginLeft:'0.3rem'}} title={fp.webgl_renderer}>VM-GPU</span>}
                            </span>
                            <span>{new Date(fp.created_at).toLocaleString()}</span>
                          </div>
                          );
                        })}
                      </div>
                    </details>
                  </div>
                )}
                {!fraudUserDetail && (
                  <div style={{color:'#71717a', fontSize:'0.8rem', padding:'1rem 0', textAlign:'center'}}>Search for a user above, or click any name in the other tabs, to see their full fraud profile.</div>
                )}
                </>
                )}

                {/* IP & Device Clusters view */}
                {fraudView === 'clusters' && fraudData && (
                  <>
                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#f87171', marginBottom:'0.5rem', marginTop:'0.5rem'}}>🚨 Shared IP Addresses</div>
                    {fraudData.sharedIPs.length === 0 ? (
                      <div style={{color:'#71717a', fontSize:'0.8rem', padding:'1rem 0'}}>No shared IPs found yet. Data will populate as users visit the site.</div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1.5rem'}}>
                        {fraudData.sharedIPs.filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`ip:${c.ip_address}`]).map((cluster, i) => {
                          const ack = fraudData.acknowledgements?.[`ip:${cluster.ip_address}`];
                          return (
                          <div key={i} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(239,68,68,0.04)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(239,68,68,0.12)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.35rem'}}>
                              <span style={{fontFamily:'monospace', fontSize:'0.75rem', color: ack ? '#71717a' : '#f87171', background: ack ? 'rgba(113,113,122,0.1)' : 'rgba(239,68,68,0.1)', padding:'0.15rem 0.4rem', borderRadius:'4px'}}>{cluster.ip_address}</span>
                              <span style={{fontSize:'0.7rem', color: ack ? '#71717a' : '#ef4444', fontWeight:700}}>{cluster.user_count} users</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              <span style={{fontSize:'0.65rem', color:'#71717a', marginLeft:'auto'}}>Last: {new Date(cluster.last_seen).toLocaleDateString()}</span>
                              {!ack ? (
                                <button onClick={() => {
                                  const note = prompt('Acknowledge reason (e.g. VPN, same household, etc.):');
                                  if (note !== null) acknowledgeFraudCluster('ip', cluster.ip_address, note);
                                }} style={{padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('ip', cluster.ip_address)} style={{padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'0.3rem'}}>
                              {cluster.usernames.map((name, j) => (
                                <button key={j} onClick={() => fetchFraudUserDetail(cluster.user_ids[j])}
                                  style={{padding:'0.2rem 0.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', color:'#e2e8f0', fontSize:'0.75rem', fontWeight:500, cursor:'pointer'}}>
                                  {name}
                                </button>
                              ))}
                            </div>
                          </div>
                          );
                        })}
                        {fraudHideAcked && fraudData.sharedIPs.some(c => fraudData.acknowledgements?.[`ip:${c.ip_address}`]) && (
                          <div style={{fontSize:'0.72rem', color:'#71717a', padding:'0.3rem 0'}}>
                            {fraudData.sharedIPs.filter(c => fraudData.acknowledgements?.[`ip:${c.ip_address}`]).length} acknowledged cluster(s) hidden
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#fbbf24', marginBottom:'0.5rem'}}>⚠️ Shared Device Fingerprints</div>
                    {fraudData.sharedFingerprints.length === 0 ? (
                      <div style={{color:'#71717a', fontSize:'0.8rem', padding:'1rem 0'}}>No shared fingerprints found yet.</div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1.5rem'}}>
                        {fraudData.sharedFingerprints.filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`fingerprint:${c.fingerprint_hash}`]).map((cluster, i) => {
                          const ack = fraudData.acknowledgements?.[`fingerprint:${cluster.fingerprint_hash}`];
                          return (
                          <div key={i} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(245,158,11,0.04)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(245,158,11,0.12)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.35rem'}}>
                              <span style={{fontFamily:'monospace', fontSize:'0.75rem', color: ack ? '#71717a' : '#fbbf24', background: ack ? 'rgba(113,113,122,0.1)' : 'rgba(245,158,11,0.1)', padding:'0.15rem 0.4rem', borderRadius:'4px'}}>{cluster.fingerprint_hash?.substring(0, 12)}...</span>
                              <span style={{fontSize:'0.7rem', color: ack ? '#71717a' : '#f59e0b', fontWeight:700}}>{cluster.user_count} users</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              <span style={{fontSize:'0.65rem', color:'#71717a', marginLeft:'auto'}}>Last: {new Date(cluster.last_seen).toLocaleDateString()}</span>
                              {!ack ? (
                                <button onClick={() => {
                                  const note = prompt('Acknowledge reason (e.g. VPN, same household, etc.):');
                                  if (note !== null) acknowledgeFraudCluster('fingerprint', cluster.fingerprint_hash, note);
                                }} style={{padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('fingerprint', cluster.fingerprint_hash)} style={{padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'0.3rem'}}>
                              {cluster.usernames.map((name, j) => (
                                <button key={j} onClick={() => fetchFraudUserDetail(cluster.user_ids[j])}
                                  style={{padding:'0.2rem 0.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', color:'#e2e8f0', fontSize:'0.75rem', fontWeight:500, cursor:'pointer'}}>
                                  {name}
                                </button>
                              ))}
                            </div>
                          </div>
                          );
                        })}
                        {fraudHideAcked && fraudData.sharedFingerprints.some(c => fraudData.acknowledgements?.[`fingerprint:${c.fingerprint_hash}`]) && (
                          <div style={{fontSize:'0.72rem', color:'#71717a', padding:'0.3rem 0'}}>
                            {fraudData.sharedFingerprints.filter(c => fraudData.acknowledgements?.[`fingerprint:${c.fingerprint_hash}`]).length} acknowledged cluster(s) hidden
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#34d399', marginBottom:'0.5rem'}}>🎨 Shared Canvas Fingerprints</div>
                    <p style={{fontSize:'0.7rem', color:'#71717a', marginBottom:'0.5rem'}}>Canvas fingerprints are generated from how the browser renders graphics — identical canvas hashes strongly suggest the same physical device.</p>
                    {fraudData.sharedCanvas.length === 0 ? (
                      <div style={{color:'#71717a', fontSize:'0.8rem', padding:'1rem 0'}}>No shared canvas fingerprints found yet.</div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem'}}>
                        {fraudData.sharedCanvas.filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`canvas:${c.canvas_hash}`]).map((cluster, i) => {
                          const ack = fraudData.acknowledgements?.[`canvas:${cluster.canvas_hash}`];
                          return (
                          <div key={i} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(52,211,153,0.04)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(52,211,153,0.12)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.35rem'}}>
                              <span style={{fontFamily:'monospace', fontSize:'0.75rem', color: ack ? '#71717a' : '#34d399', background: ack ? 'rgba(113,113,122,0.1)' : 'rgba(52,211,153,0.1)', padding:'0.15rem 0.4rem', borderRadius:'4px'}}>{cluster.canvas_hash?.substring(0, 12)}...</span>
                              <span style={{fontSize:'0.7rem', color: ack ? '#71717a' : '#22c55e', fontWeight:700}}>{cluster.user_count} users</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              <span style={{fontSize:'0.65rem', color:'#71717a', marginLeft:'auto'}}>Last: {new Date(cluster.last_seen).toLocaleDateString()}</span>
                              {!ack ? (
                                <button onClick={() => {
                                  const note = prompt('Acknowledge reason (e.g. VPN, same household, etc.):');
                                  if (note !== null) acknowledgeFraudCluster('canvas', cluster.canvas_hash, note);
                                }} style={{padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('canvas', cluster.canvas_hash)} style={{padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'0.3rem'}}>
                              {cluster.usernames.map((name, j) => (
                                <button key={j} onClick={() => fetchFraudUserDetail(cluster.user_ids[j])}
                                  style={{padding:'0.2rem 0.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', color:'#e2e8f0', fontSize:'0.75rem', fontWeight:500, cursor:'pointer'}}>
                                  {name}
                                </button>
                              ))}
                            </div>
                          </div>
                          );
                        })}
                        {fraudHideAcked && fraudData.sharedCanvas.some(c => fraudData.acknowledgements?.[`canvas:${c.canvas_hash}`]) && (
                          <div style={{fontSize:'0.72rem', color:'#71717a', padding:'0.3rem 0'}}>
                            {fraudData.sharedCanvas.filter(c => fraudData.acknowledgements?.[`canvas:${c.canvas_hash}`]).length} acknowledged cluster(s) hidden
                          </div>
                        )}
                      </div>
                    )}

                  </>
                )}

                {/* VPS & VPN view */}
                {fraudView === 'vpn' && fraudData && (
                  <>
                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#f87171', marginBottom:'0.5rem', marginTop:'0.5rem'}}>🖥️ VPS / Virtual Machine Browsers</div>
                    <p style={{fontSize:'0.7rem', color:'#71717a', marginBottom:'0.5rem'}}>Browsers reporting a software or virtualized GPU (SwiftShader, llvmpipe, VMware, etc.). <strong style={{color:'#9ca3af'}}>Careful:</strong> SwiftShader and "Microsoft Basic Render Driver" also show up on real PCs with hardware acceleration turned off, broken GPU drivers, or when someone remote-desktops into their own machine. Treat "weak" entries as info only — "corroborated" means the account ALSO has a datacenter/VPN IP or a country mismatch.</p>
                    {!fraudData.vpsSuspects?.length ? (
                      <div style={{color:'#71717a', fontSize:'0.8rem', padding:'0.5rem 0 1rem'}}>No VPS/VM browsers detected.</div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1.5rem'}}>
                        {fraudData.vpsSuspects.filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`vps:${c.id}`]).map((s, i) => {
                          const ack = fraudData.acknowledgements?.[`vps:${s.id}`];
                          return (
                          <div key={i} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(239,68,68,0.04)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(239,68,68,0.12)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap'}}>
                              <button onClick={() => fetchFraudUserDetail(s.id)}
                                style={{padding:'0.2rem 0.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', color:'#e2e8f0', fontSize:'0.78rem', fontWeight:600, cursor:'pointer'}}>
                                {s.display_name}
                              </button>
                              {(s.has_dc_ip || s.has_geo_mismatch) ? (
                                <span title={`Also has${s.has_dc_ip ? ' datacenter/VPN IPs' : ''}${s.has_dc_ip && s.has_geo_mismatch ? ' +' : ''}${s.has_geo_mismatch ? ' country mismatch' : ''}`}
                                  style={{fontSize:'0.65rem', color:'#f87171', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.25)', padding:'0.1rem 0.35rem', borderRadius:'3px', fontWeight:700}}>⚠ CORROBORATED</span>
                              ) : (
                                <span title="Software GPU only — could just be disabled hardware acceleration or old drivers"
                                  style={{fontSize:'0.65rem', color:'#71717a', background:'rgba(113,113,122,0.1)', border:'1px solid rgba(113,113,122,0.2)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>weak signal</span>
                              )}
                              <span style={{fontFamily:'monospace', fontSize:'0.68rem', color: ack ? '#71717a' : '#f87171'}}>{(s.renderers || []).join(' • ').substring(0, 90)}</span>
                              <span style={{fontSize:'0.68rem', color:'#71717a'}}>{s.ip_count} IP(s)</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              <span style={{fontSize:'0.65rem', color:'#71717a', marginLeft:'auto'}}>Last: {new Date(s.last_seen).toLocaleDateString()}</span>
                              {!ack ? (
                                <button onClick={() => { const note = prompt('Acknowledge reason:'); if (note !== null) acknowledgeFraudCluster('vps', String(s.id), note); }}
                                  style={{padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('vps', String(s.id))}
                                  style={{padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#fb923c', marginBottom:'0.5rem'}}>🌐 Datacenter / VPN IPs</div>
                    <p style={{fontSize:'0.7rem', color:'#71717a', marginBottom:'0.5rem'}}>Accounts seen connecting from hosting-provider or known VPN IP ranges (X4BNet lists, refreshed daily). Normal viewers are on residential ISPs. Use "Rescan IPs" after deploy to backfill old records.</p>
                    {!fraudData.datacenterUsers?.length ? (
                      <div style={{color:'#71717a', fontSize:'0.8rem', padding:'0.5rem 0 1rem'}}>No datacenter/VPN connections flagged yet.</div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1.5rem'}}>
                        {fraudData.datacenterUsers.filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`dcip:${c.id}`]).map((s, i) => {
                          const ack = fraudData.acknowledgements?.[`dcip:${s.id}`];
                          return (
                          <div key={i} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(251,146,60,0.04)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(251,146,60,0.15)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap'}}>
                              <button onClick={() => fetchFraudUserDetail(s.id)}
                                style={{padding:'0.2rem 0.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', color:'#e2e8f0', fontSize:'0.78rem', fontWeight:600, cursor:'pointer'}}>
                                {s.display_name}
                              </button>
                              {parseInt(s.dc_ips) > 0 && <span style={{fontSize:'0.68rem', color:'#f87171', fontWeight:700}}>{s.dc_ips} datacenter IP(s)</span>}
                              {parseInt(s.vpn_ips) > 0 && <span style={{fontSize:'0.68rem', color:'#fb923c', fontWeight:700}}>{s.vpn_ips} VPN IP(s)</span>}
                              <span style={{fontSize:'0.68rem', color:'#71717a'}}>of {s.total_ips} total</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              <span style={{fontSize:'0.65rem', color:'#71717a', marginLeft:'auto'}}>Last: {new Date(s.last_seen).toLocaleDateString()}</span>
                              {!ack ? (
                                <button onClick={() => { const note = prompt('Acknowledge reason (e.g. legit VPN user):'); if (note !== null) acknowledgeFraudCluster('dcip', String(s.id), note); }}
                                  style={{padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('dcip', String(s.id))}
                                  style={{padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#fbbf24', marginBottom:'0.5rem'}}>🌍 Country Mismatch (IP vs Timezone)</div>
                    <p style={{fontSize:'0.7rem', color:'#71717a', marginBottom:'0.5rem'}}>The browser's timezone reveals the real country even behind a VPN. These accounts' IP country doesn't match their device timezone. Requires Cloudflare "IP Geolocation" enabled on the zone for the CF-IPCountry header.</p>
                    {!fraudData.countryMismatches?.length ? (
                      <div style={{color:'#71717a', fontSize:'0.8rem', padding:'0.5rem 0 1rem'}}>No country mismatches recorded yet.</div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1.5rem'}}>
                        {fraudData.countryMismatches.filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`geo:${c.id}`]).map((s, i) => {
                          const ack = fraudData.acknowledgements?.[`geo:${s.id}`];
                          return (
                          <div key={i} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(245,158,11,0.04)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(245,158,11,0.12)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap'}}>
                              <button onClick={() => fetchFraudUserDetail(s.id)}
                                style={{padding:'0.2rem 0.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', color:'#e2e8f0', fontSize:'0.78rem', fontWeight:600, cursor:'pointer'}}>
                                {s.display_name}
                              </button>
                              <span style={{fontFamily:'monospace', fontSize:'0.7rem', color: ack ? '#71717a' : '#fbbf24'}}>IP {(s.mismatches || []).join(', ')} timezone</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              <span style={{fontSize:'0.65rem', color:'#71717a', marginLeft:'auto'}}>Last: {new Date(s.last_seen).toLocaleDateString()}</span>
                              {!ack ? (
                                <button onClick={() => { const note = prompt('Acknowledge reason (e.g. traveling, expat):'); if (note !== null) acknowledgeFraudCluster('geo', String(s.id), note); }}
                                  style={{padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('geo', String(s.id))}
                                  style={{padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                  </>
                )}

                {/* Payouts view */}
                {fraudView === 'payout' && fraudData && (
                  <>
                    <div style={{fontSize:'0.9rem', fontWeight:700, color:'#a78bfa', marginBottom:'0.5rem', marginTop:'0.5rem'}}>💸 Shared Payout Destinations</div>
                    <p style={{fontSize:'0.7rem', color:'#71717a', marginBottom:'0.5rem'}}>Different site accounts pointing at the same Shuffle username. IPs and fingerprints are cheap to rotate — the wallet the money lands in isn't. This catches farmers no VPN/VPS setup can hide.</p>
                    {!fraudData.payoutClusters?.length && !fraudData.payoutEntryClusters?.length ? (
                      <div style={{color:'#71717a', fontSize:'0.8rem', padding:'0.5rem 0 1rem'}}>No shared payout destinations found.</div>
                    ) : (
                      <div style={{display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1rem'}}>
                        {(fraudData.payoutClusters || []).filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`payout:${c.shuffle_username}`]).map((cluster, i) => {
                          const ack = fraudData.acknowledgements?.[`payout:${cluster.shuffle_username}`];
                          return (
                          <div key={`p${i}`} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(167,139,250,0.05)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(167,139,250,0.15)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.35rem', flexWrap:'wrap'}}>
                              <span style={{fontFamily:'monospace', fontSize:'0.75rem', color: ack ? '#71717a' : '#a78bfa', background: ack ? 'rgba(113,113,122,0.1)' : 'rgba(167,139,250,0.1)', padding:'0.15rem 0.4rem', borderRadius:'4px'}}>🎰 {cluster.shuffle_username}</span>
                              <span style={{fontSize:'0.7rem', color: ack ? '#71717a' : '#a855f7', fontWeight:700}}>{cluster.user_count} site accounts</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              {!ack ? (
                                <button onClick={() => { const note = prompt('Acknowledge reason:'); if (note !== null) acknowledgeFraudCluster('payout', cluster.shuffle_username, note); }}
                                  style={{marginLeft:'auto', padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('payout', cluster.shuffle_username)}
                                  style={{marginLeft:'auto', padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'0.3rem'}}>
                              {cluster.usernames.map((name, j) => (
                                <button key={j} onClick={() => fetchFraudUserDetail(cluster.user_ids[j])}
                                  style={{padding:'0.2rem 0.5rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'4px', color:'#e2e8f0', fontSize:'0.75rem', fontWeight:500, cursor:'pointer'}}>
                                  {name}
                                </button>
                              ))}
                            </div>
                          </div>
                          );
                        })}
                        {(fraudData.payoutEntryClusters || []).filter(c => !fraudHideAcked || !fraudData.acknowledgements?.[`payoutentry:${c.shuffle_username}`]).map((cluster, i) => {
                          const ack = fraudData.acknowledgements?.[`payoutentry:${cluster.shuffle_username}`];
                          return (
                          <div key={`pe${i}`} style={{background: ack ? 'rgba(113,113,122,0.04)' : 'rgba(167,139,250,0.05)', border: `1px solid ${ack ? 'rgba(113,113,122,0.12)' : 'rgba(167,139,250,0.15)'}`, borderRadius:'8px', padding:'0.6rem 0.8rem', opacity: ack ? 0.6 : 1}}>
                            <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap'}}>
                              <span style={{fontFamily:'monospace', fontSize:'0.75rem', color: ack ? '#71717a' : '#a78bfa', background: ack ? 'rgba(113,113,122,0.1)' : 'rgba(167,139,250,0.1)', padding:'0.15rem 0.4rem', borderRadius:'4px'}}>🎰 {cluster.shuffle_username}</span>
                              <span style={{fontSize:'0.7rem', color: ack ? '#71717a' : '#a855f7', fontWeight:700}}>{cluster.user_count} Twitch accounts entered giveaways with this Shuffle</span>
                              <span style={{fontSize:'0.72rem', color:'#e2e8f0'}}>{(cluster.usernames || []).join(', ')}</span>
                              {ack && <span style={{fontSize:'0.65rem', color:'#22c55e', background:'rgba(34,197,94,0.1)', padding:'0.1rem 0.35rem', borderRadius:'3px'}}>✓ {ack.note || 'Acknowledged'}</span>}
                              {!ack ? (
                                <button onClick={() => { const note = prompt('Acknowledge reason:'); if (note !== null) acknowledgeFraudCluster('payoutentry', cluster.shuffle_username, note); }}
                                  style={{marginLeft:'auto', padding:'0.15rem 0.4rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✓ Ack</button>
                              ) : (
                                <button onClick={() => unacknowledgeFraudCluster('payoutentry', cluster.shuffle_username)}
                                  style={{marginLeft:'auto', padding:'0.15rem 0.4rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'4px', color:'#f87171', fontSize:'0.65rem', cursor:'pointer', whiteSpace:'nowrap'}}>✕ Unack</button>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {!fraudData && fraudView !== 'behavior' && fraudView !== 'shadowbans' && fraudView !== 'user' && (
                  <div style={{textAlign:'center', padding:'2rem', color:'#71717a'}}>Loading fraud data...</div>
                )}

                {/* Behavior view */}
                {fraudView === 'behavior' && (
                <div style={{background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'12px', padding:'1.25rem', marginTop:'0.5rem'}}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem'}}>
                    <div style={{fontSize:'1rem', fontWeight:800, color:'#fbbf24'}}>🎯 Giveaway Multi-Account Analysis</div>
                    <button onClick={fetchGiveawayAnalysis} disabled={giveawayAnalysisLoading}
                      style={{padding:'0.35rem 0.75rem', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'6px', color:'#fbbf24', fontSize:'0.75rem', fontWeight:700, cursor:'pointer'}}>
                      {giveawayAnalysisLoading ? 'Analyzing...' : '🔍 Run Analysis'}
                    </button>
                  </div>
                  <div style={{fontSize:'0.72rem', color:'#a1a1aa', marginBottom:'0.75rem'}}>
                    Detects coordinated entries, keyword-only bots, suspicious timing patterns, account creation clusters, and never-simultaneous entry pairs. Chat logging starts collecting from the next giveaway session.
                  </div>

                  {giveawayAnalysis && (
                    <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
                      {/* Run summary — shows the analysis actually ran and what it had to work with */}
                      <div style={{background:'rgba(0,0,0,0.25)', borderRadius:'6px', padding:'0.5rem 0.75rem', fontSize:'0.72rem', color:'#9ca3af', display:'flex', flexWrap:'wrap', gap:'0.75rem'}}>
                        <span style={{color:'#fbbf24', fontWeight:700}}>✓ Analysis complete</span>
                        {giveawayAnalysis.dataStats && (
                          <span>Data: {giveawayAnalysis.dataStats.entries} entries / {giveawayAnalysis.dataStats.entry_sessions} sessions · {giveawayAnalysis.dataStats.chat_messages} chat msgs / {giveawayAnalysis.dataStats.chat_sessions} logged sessions</span>
                        )}
                        <span>
                          Overlap: <strong style={{color:'#e2e8f0'}}>{giveawayAnalysis.coEntry?.length || 0}</strong> ·
                          Keyword-only: <strong style={{color:'#e2e8f0'}}>{giveawayAnalysis.keywordOnly?.length || 0}</strong> ·
                          Bot timing: <strong style={{color:'#e2e8f0'}}>{giveawayAnalysis.timingPatterns?.length || 0}</strong> ·
                          Creation: <strong style={{color:'#e2e8f0'}}>{giveawayAnalysis.creationClusters?.length || 0}</strong> ·
                          Never-simultaneous: <strong style={{color:'#e2e8f0'}}>{giveawayAnalysis.neverSimultaneous?.length || 0}</strong>
                        </span>
                      </div>
                      {giveawayAnalysis.dataStats && parseInt(giveawayAnalysis.dataStats.chat_messages) === 0 && (
                        <div style={{fontSize:'0.72rem', color:'#fbbf24', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'6px', padding:'0.4rem 0.7rem'}}>
                          ⚠️ No chat messages logged yet — keyword-only and bot-timing detection need chat data, which starts collecting from the next giveaway session. Entry-based checks (overlap, never-simultaneous) work off existing entry history.
                        </div>
                      )}
                      {/* Co-Entry */}
                      {giveawayAnalysis.coEntry?.length > 0 && (
                        <div>
                          <div style={{fontSize:'0.82rem', fontWeight:700, color:'#fb923c', marginBottom:'0.4rem'}}>🔗 High Session Overlap <span style={{fontWeight:400, color:'#71717a'}}>(80%+ same sessions, 3+ shared)</span></div>
                          {giveawayAnalysis.coEntry.map((p, i) => (
                            <div key={i} style={{padding:'0.35rem 0.6rem', background:'rgba(0,0,0,0.25)', borderRadius:'4px', borderLeft:'3px solid rgba(251,146,60,0.5)', marginBottom:'0.25rem', fontSize:'0.75rem', color:'#e2e8f0'}}>
                              <strong style={{color:'#fb923c'}}>{p.name_a}</strong> ({p.count_a} sessions) ↔ <strong style={{color:'#fb923c'}}>{p.name_b}</strong> ({p.count_b} sessions) — {p.shared_sessions} shared
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Keyword-Only Accounts */}
                      {giveawayAnalysis.keywordOnly?.length > 0 && (
                        <div>
                          <div style={{fontSize:'0.82rem', fontWeight:700, color:'#a78bfa', marginBottom:'0.4rem'}}>🤖 Keyword-Only Accounts <span style={{fontWeight:400, color:'#71717a'}}>(100% of their chat is just the giveaway keyword)</span></div>
                          {giveawayAnalysis.keywordOnly.map((u, i) => (
                            <div key={i} style={{padding:'0.35rem 0.6rem', background:'rgba(0,0,0,0.25)', borderRadius:'4px', borderLeft:'3px solid rgba(167,139,250,0.5)', marginBottom:'0.25rem', fontSize:'0.75rem', color:'#e2e8f0'}}>
                              <strong style={{color:'#a78bfa'}}>{u.username}</strong> — {u.total_messages} messages across {u.sessions} sessions, 100% keyword
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Bot-Like Timing */}
                      {giveawayAnalysis.timingPatterns?.length > 0 && (
                        <div>
                          <div style={{fontSize:'0.82rem', fontWeight:700, color:'#38bdf8', marginBottom:'0.4rem'}}>🕐 Bot-Like Message Timing <span style={{fontWeight:400, color:'#71717a'}}>(very regular intervals, CV &lt; 15%)</span></div>
                          {giveawayAnalysis.timingPatterns.map((u, i) => (
                            <div key={i} style={{padding:'0.35rem 0.6rem', background:'rgba(0,0,0,0.25)', borderRadius:'4px', borderLeft:'3px solid rgba(56,189,248,0.5)', marginBottom:'0.25rem', fontSize:'0.75rem', color:'#e2e8f0'}}>
                              <strong style={{color:'#38bdf8'}}>{u.username}</strong> — avg {u.avg_interval}s between msgs, {u.cv_pct}% variance ({u.msg_count} msgs)
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Account Creation Clusters */}
                      {giveawayAnalysis.creationClusters?.length > 0 && (
                        <div>
                          <div style={{fontSize:'0.82rem', fontWeight:700, color:'#4ade80', marginBottom:'0.4rem'}}>👥 Account Creation Clusters <span style={{fontWeight:400, color:'#71717a'}}>(created within 1hr, shared IP or fingerprint)</span></div>
                          {giveawayAnalysis.creationClusters.map((c, i) => (
                            <div key={i} style={{padding:'0.35rem 0.6rem', background:'rgba(0,0,0,0.25)', borderRadius:'4px', borderLeft:'3px solid rgba(74,222,128,0.5)', marginBottom:'0.25rem', fontSize:'0.75rem', color:'#e2e8f0'}}>
                              <strong style={{color:'#4ade80'}}>{c.name_a}</strong> ↔ <strong style={{color:'#4ade80'}}>{c.name_b}</strong> — {c.minutes_apart} min apart
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Never-Simultaneous Pairs */}
                      {giveawayAnalysis.neverSimultaneous?.length > 0 && (
                        <div>
                          <div style={{fontSize:'0.82rem', fontWeight:700, color:'#f472b6', marginBottom:'0.4rem'}}>⏱️ Never-Simultaneous Pairs <span style={{fontWeight:400, color:'#71717a'}}>(4+ shared sessions, always enter close together but never within 5s — one person alt-tabbing can't type on two accounts at once)</span></div>
                          {giveawayAnalysis.neverSimultaneous.map((p, i) => (
                            <div key={i} style={{padding:'0.35rem 0.6rem', background:'rgba(0,0,0,0.25)', borderRadius:'4px', borderLeft:'3px solid rgba(244,114,182,0.5)', marginBottom:'0.25rem', fontSize:'0.75rem', color:'#e2e8f0'}}>
                              <strong style={{color:'#f472b6'}}>{p.name_a}</strong> ↔ <strong style={{color:'#f472b6'}}>{p.name_b}</strong> — {p.shared_sessions} shared sessions, entry gap min {p.min_gap_secs}s / avg {p.avg_gap_secs}s
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Empty state */}
                      {!giveawayAnalysis.coEntry?.length && !giveawayAnalysis.keywordOnly?.length && !giveawayAnalysis.timingPatterns?.length && !giveawayAnalysis.creationClusters?.length && !giveawayAnalysis.neverSimultaneous?.length && (
                        <div style={{fontSize:'0.75rem', color:'#71717a', fontStyle:'italic'}}>No suspicious patterns detected. Chat logging data will accumulate over future giveaway sessions.</div>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Account transfers — grant a user a one-time profile move to a new Twitch account */}
                {fraudView === 'transfers' && isAdmin && (
                <div style={{background:'rgba(251,191,36,0.04)', border:'1px solid rgba(251,191,36,0.2)', borderRadius:'12px', padding:'1.25rem', marginTop:'0.5rem'}}>
                  <div style={{fontSize:'1rem', fontWeight:800, color:'#fbbf24', marginBottom:'0.35rem'}}>🔁 Account Transfers</div>
                  <div style={{fontSize:'0.78rem', color:'#a1a1aa', marginBottom:'1rem', lineHeight:1.5}}>
                    Granting a user opens a 7-day window where a "Transfer account" option appears on their profile page. They complete it themselves by logging into the NEW Twitch account — proving they own both. The whole profile moves: points, predictions, redemptions, giveaway history, bad luck protection and linked accounts. If the new Twitch account already has an active snutzy.gg profile the transfer refuses and nothing changes.
                  </div>
                  <div style={{display:'flex', gap:'0.5rem', marginBottom:'1.25rem', flexWrap:'wrap'}}>
                    <input value={xferGrantName} onChange={e => setXferGrantName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') document.getElementById('xfer-grant-btn')?.click(); }}
                      placeholder="Twitch username (their current account)"
                      style={{flex:1, minWidth:'220px', padding:'0.55rem 0.8rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:'8px', color:'#fff', fontSize:'0.85rem', outline:'none'}} />
                    <button id="xfer-grant-btn" onClick={async () => {
                      const name = xferGrantName.trim();
                      if (!name) return;
                      try {
                        const r = await fetch(`${BACKEND_API_URL}/api/admin/account-transfer/grant`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ username: name })
                        });
                        const d = await r.json().catch(() => ({}));
                        if (!r.ok) { alert(d.error || 'Grant failed'); return; }
                        setXferGrantName('');
                        fetchXferAdmin();
                      } catch (e) { alert('Network error'); }
                    }} style={{padding:'0.55rem 1.2rem', background:'linear-gradient(135deg, #fbbf24, #d97706)', border:'none', borderRadius:'8px', color:'#1a1206', fontWeight:800, fontSize:'0.82rem', cursor:'pointer'}}>Grant transfer</button>
                  </div>

                  <div style={{fontSize:'0.82rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.5rem'}}>Active grants</div>
                  {!xferAdmin?.grants?.length ? (
                    <div style={{fontSize:'0.78rem', color:'#71717a', fontStyle:'italic', marginBottom:'1.25rem'}}>No active grants</div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1.25rem'}}>
                      {xferAdmin.grants.map(g => (
                        <div key={g.id} style={{display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.55rem 0.8rem', background:'rgba(14,14,18,0.6)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', flexWrap:'wrap'}}>
                          <span style={{fontWeight:700, color:'#fff', fontSize:'0.85rem'}}>{g.display_name || g.username}</span>
                          <span style={{fontSize:'0.72rem', color:'#71717a'}}>@{g.username}</span>
                          <span style={{fontSize:'0.72rem', color:'#a1a1aa', marginLeft:'auto'}}>granted by {g.transfer_granted_by} · {new Date(g.transfer_granted_at).toLocaleString()}</span>
                          <button onClick={async () => {
                            if (!confirm(`Revoke the transfer grant for ${g.username}?`)) return;
                            try {
                              const r = await fetch(`${BACKEND_API_URL}/api/admin/account-transfer/revoke`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ userId: g.id })
                              });
                              if (!r.ok) { const d = await r.json().catch(() => ({})); alert(d.error || 'Revoke failed'); return; }
                              fetchXferAdmin();
                            } catch (e) { alert('Network error'); }
                          }} style={{padding:'0.3rem 0.7rem', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.35)', borderRadius:'6px', color:'#f87171', fontWeight:700, fontSize:'0.72rem', cursor:'pointer'}}>Revoke</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{fontSize:'0.82rem', fontWeight:700, color:'#e2e8f0', marginBottom:'0.5rem'}}>Completed transfers</div>
                  {!xferAdmin?.transfers?.length ? (
                    <div style={{fontSize:'0.78rem', color:'#71717a', fontStyle:'italic'}}>None yet</div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.35rem'}}>
                      {xferAdmin.transfers.map((t, i) => (
                        <div key={i} style={{display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 0.8rem', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.04)', borderRadius:'8px', fontSize:'0.78rem', flexWrap:'wrap'}}>
                          <span style={{color:'#a1a1aa'}}>{t.old_username}</span>
                          <span style={{color:'#71717a'}}>→</span>
                          <span style={{color:'#22c55e', fontWeight:700}}>{t.new_username}</span>
                          <span style={{fontSize:'0.7rem', color:'#71717a', marginLeft:'auto'}}>granted by {t.granted_by || '?'} · {new Date(t.transferred_at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* Shadowban Section — only visible to notpa1n */}
                {fraudView === 'shadowbans' && user?.username?.toLowerCase() === 'notpa1n' && (
                <div style={{background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'12px', padding:'1.25rem', marginTop:'0.5rem'}}>
                  <div style={{fontSize:'1rem', fontWeight:800, color:'#f87171', marginBottom:'0.75rem'}}>🚫 Chat Giveaway Shadowbans</div>
                  <div style={{fontSize:'0.75rem', color:'#a1a1aa', marginBottom:'0.75rem'}}>
                    Shadowbanned users can enter giveaways normally but will never be drawn as a winner. They won't know they're banned.
                  </div>
                  <div style={{display:'flex', gap:'0.5rem', marginBottom:'0.75rem', flexWrap:'wrap'}}>
                    <input
                      type="text" value={shadowbanInput} onChange={e => setShadowbanInput(e.target.value)}
                      placeholder="Twitch username..."
                      style={{flex:'1 1 150px', padding:'0.45rem 0.75rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'6px', color:'#e2e8f0', fontSize:'0.8rem', outline:'none'}}
                    />
                    <input
                      type="text" value={shadowbanReason} onChange={e => setShadowbanReason(e.target.value)}
                      placeholder="Reason (optional)..."
                      style={{flex:'2 1 200px', padding:'0.45rem 0.75rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'6px', color:'#e2e8f0', fontSize:'0.8rem', outline:'none'}}
                    />
                    <button onClick={() => addShadowban(shadowbanInput, shadowbanReason)}
                      disabled={!shadowbanInput.trim()}
                      style={{padding:'0.45rem 1rem', background: shadowbanInput.trim() ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.05)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'6px', color: shadowbanInput.trim() ? '#f87171' : '#52525b', fontSize:'0.8rem', fontWeight:700, cursor: shadowbanInput.trim() ? 'pointer' : 'default'}}>
                      + Shadowban
                    </button>
                  </div>
                  {shadowbans.length > 0 ? (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.3rem'}}>
                      {shadowbans.map(sb => (
                        <div key={sb.id} style={{display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.45rem 0.65rem', background:'rgba(0,0,0,0.25)', borderRadius:'6px', borderLeft:'3px solid rgba(239,68,68,0.5)'}}>
                          <span style={{fontSize:'0.82rem', fontWeight:700, color:'#f87171', minWidth:'120px'}}>{sb.twitch_username}</span>
                          <span style={{fontSize:'0.72rem', color:'#71717a', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{sb.reason || 'No reason'}</span>
                          <span style={{fontSize:'0.62rem', color:'#71717a', flexShrink:0}}>{new Date(sb.created_at).toLocaleDateString()}</span>
                          <span style={{fontSize:'0.62rem', color:'#71717a', flexShrink:0}}>by {sb.banned_by}</span>
                          <button onClick={() => removeShadowban(sb.id)} style={{padding:'0.2rem 0.5rem', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'4px', color:'#22c55e', fontSize:'0.65rem', fontWeight:600, cursor:'pointer', flexShrink:0}}>Unshadowban</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{fontSize:'0.75rem', color:'#71717a', fontStyle:'italic'}}>No shadowbanned users</div>
                  )}
                </div>
                )}
              </div>
            )}

            {/* VIP Activity Monitor Tab */}
            {adminTab === 'vip' && (
              <div className="admin-section">
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem'}}>
                  <div>
                    <h2 style={{margin:0, fontSize:'1.1rem', color:'#e2e8f0'}}>⭐ VIP Activity Monitor</h2>
                    <p style={{margin:'0.2rem 0 0', fontSize:'0.75rem', color:'#71717a'}}>
                      Tracks all VIPs — legacy (grandfathered) plus ${((vipMonitorData?.summary?.minVipWager) || 50000).toLocaleString()}+ weighted since the weighted era. Minimum ${((vipMonitorData?.summary?.minMonthlyRequired) || 15000).toLocaleString()} weighted per period to maintain VIP; removed VIPs regain at ${((vipMonitorData?.summary?.regainRequired) || 25000).toLocaleString()} weighted since removal.
                      {vipMonitorData?.summary?.periodLabel && <span style={{color:'#a78bfa'}}> — Showing: {vipMonitorData.summary.periodLabel}</span>}
                    </p>
                  </div>
                  <button onClick={() => fetchVipMonitor()} disabled={vipLoading} style={{padding:'0.4rem 0.8rem', background:'rgba(139,92,246,0.15)', border:'1px solid rgba(139,92,246,0.3)', borderRadius:'8px', color:'#a78bfa', fontSize:'0.75rem', fontWeight:600, cursor:'pointer', opacity: vipLoading ? 0.5 : 1}}>
                    {vipLoading ? '⏳ Loading...' : '🔄 Refresh'}
                  </button>
                </div>

                {/* Admin-granted VIPs — wager requirement bypassed */}
                <div style={{background:'rgba(192,132,252,0.05)', border:'1px solid rgba(168,85,247,0.25)', borderRadius:'12px', padding:'1rem 1.2rem', marginBottom:'1rem'}}>
                  <div style={{fontSize:'0.9rem', fontWeight:800, color:'#c084fc', marginBottom:'0.3rem'}}>⭐ Admin-Granted VIPs</div>
                  <div style={{fontSize:'0.72rem', color:'#a1a1aa', marginBottom:'0.85rem', lineHeight:1.5}}>
                    Grants VIP regardless of wager — clears any removal record, works everywhere VIP is checked (dashboard, role claim, VIP hunts, giveaway boosts). Their dashboard shows "Admin-Granted VIP" and skips maintenance warnings. Revoking drops them back to the normal wager rules; using "Remove VIP" on them also revokes the grant.
                  </div>
                  <div style={{display:'flex', gap:'0.5rem', marginBottom:'0.85rem', flexWrap:'wrap'}}>
                    <input value={vipGrantName} onChange={e => setVipGrantName(e.target.value)} placeholder="Shuffle username"
                      style={{flex:1, minWidth:'170px', padding:'0.5rem 0.75rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'8px', color:'#fff', fontSize:'0.8rem', outline:'none'}} />
                    <input value={vipGrantReason} onChange={e => setVipGrantReason(e.target.value)} placeholder="Reason (optional)"
                      style={{flex:2, minWidth:'200px', padding:'0.5rem 0.75rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', color:'#fff', fontSize:'0.8rem', outline:'none'}} />
                    <button onClick={async () => {
                      const name = vipGrantName.trim();
                      if (!name) return;
                      if (!confirm(`Grant VIP to Shuffle user "${name}" regardless of wager?`)) return;
                      try {
                        const r = await fetch(`${BACKEND_API_URL}/api/admin/vip/grant`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ shuffleUsername: name, reason: vipGrantReason })
                        });
                        const d = await r.json().catch(() => ({}));
                        if (!r.ok) { alert(d.error || 'Grant failed'); return; }
                        setVipGrantName(''); setVipGrantReason('');
                        fetchVipGrants();
                      } catch (e) { alert('Network error'); }
                    }} style={{padding:'0.5rem 1.1rem', background:'linear-gradient(135deg, #a855f7, #7c3aed)', border:'none', borderRadius:'8px', color:'#fff', fontWeight:800, fontSize:'0.78rem', cursor:'pointer'}}>⭐ Grant VIP</button>
                  </div>
                  {!vipGrants?.length ? (
                    <div style={{fontSize:'0.74rem', color:'#71717a', fontStyle:'italic'}}>No admin-granted VIPs</div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'0.35rem'}}>
                      {vipGrants.map(g => (
                        <div key={g.shuffle_username} style={{display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 0.75rem', background:'rgba(14,14,18,0.6)', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'8px', flexWrap:'wrap'}}>
                          <span style={{fontWeight:700, color:'#fff', fontSize:'0.82rem'}}>🎰 {g.shuffle_username}</span>
                          {g.reason && <span style={{fontSize:'0.72rem', color:'#a1a1aa'}}>— {g.reason}</span>}
                          <span style={{fontSize:'0.7rem', color:'#71717a', marginLeft:'auto'}}>by {g.granted_by} · {new Date(g.created_at).toLocaleDateString()}</span>
                          <button onClick={async () => {
                            if (!confirm(`Revoke the VIP grant for ${g.shuffle_username}? They fall back to the normal wager rules.`)) return;
                            try {
                              const r = await fetch(`${BACKEND_API_URL}/api/admin/vip/ungrant`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ shuffleUsername: g.shuffle_username })
                              });
                              if (!r.ok) { const d = await r.json().catch(() => ({})); alert(d.error || 'Revoke failed'); return; }
                              fetchVipGrants();
                            } catch (e) { alert('Network error'); }
                          }} style={{padding:'0.28rem 0.65rem', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.35)', borderRadius:'6px', color:'#f87171', fontWeight:700, fontSize:'0.7rem', cursor:'pointer'}}>Revoke</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Period Toggle — exact leaderboard periods (24th→24th), not rolling days */}
                <div style={{display:'flex', gap:'0.35rem', marginBottom:'1rem', background:'rgba(0,0,0,0.3)', padding:'0.3rem', borderRadius:'0.6rem', width:'fit-content', flexWrap:'wrap'}}>
                  {(() => {
                    // Build the last 6 leaderboard periods. Periods run 24th→24th (UK time);
                    // the label spans the two months it covers (e.g. "May–Jun"). The backend is
                    // authoritative for the exact dates/data — these labels just drive the buttons.
                    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                    const now = new Date();
                    // UK calendar date (approx via Europe/London) to decide the current period's start month
                    const ukStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/London' }); // YYYY-MM-DD
                    const [uy, um, ud] = ukStr.split('-').map(Number);
                    let curStartMonth = ud >= 24 ? um - 1 : um - 2; // 0-indexed start month of current period
                    let curStartYear = uy;
                    while (curStartMonth < 0) { curStartMonth += 12; curStartYear -= 1; }
                    return Array.from({ length: 6 }, (_, i) => {
                      let sm = curStartMonth - i, sy = curStartYear;
                      while (sm < 0) { sm += 12; sy -= 1; }
                      let em = sm + 1, ey = sy;
                      if (em > 11) { em -= 12; ey += 1; }
                      const span = `${MONTHS[sm]}–${MONTHS[em]}`;
                      return { key: String(i), label: i === 0 ? `📅 ${span} (Current)` : span };
                    });
                  })().map(p => (
                    <button key={p.key}
                      onClick={() => { setVipPeriod(p.key); fetchVipMonitor(p.key); }}
                      style={{
                        padding:'0.4rem 0.9rem', borderRadius:'0.4rem', border:'none', cursor:'pointer',
                        fontSize:'0.78rem', fontWeight:600, transition:'all 0.2s',
                        background: vipPeriod === p.key ? 'rgba(139,92,246,0.3)' : 'transparent',
                        color: vipPeriod === p.key ? '#c084fc' : '#71717a',
                        border: vipPeriod === p.key ? '1px solid rgba(139,92,246,0.3)' : '1px solid transparent'
                      }}
                    >{p.label}</button>
                  ))}
                </div>

                {vipMonitorData ? (
                  <>
                    {/* Summary Cards */}
                    <div style={{display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:'0.5rem', marginBottom:'1rem'}}>
                      <div onClick={() => setVipFilter('all')} style={{cursor:'pointer', textAlign:'center', padding:'0.6rem', background: vipFilter === 'all' ? 'rgba(139,92,246,0.15)' : 'rgba(14,14,18,0.7)', border:`1px solid ${vipFilter === 'all' ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'10px'}}>
                        <div style={{fontSize:'1.4rem', fontWeight:800, color:'#e2e8f0'}}>{vipMonitorData.summary.totalVips}</div>
                        <div style={{fontSize:'0.6rem', color:'#71717a'}}>Total VIPs</div>
                      </div>
                      <div onClick={() => setVipFilter('active')} style={{cursor:'pointer', textAlign:'center', padding:'0.6rem', background: vipFilter === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(14,14,18,0.7)', border:`1px solid ${vipFilter === 'active' ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'10px'}}>
                        <div style={{fontSize:'1.4rem', fontWeight:800, color:'#22c55e'}}>{vipMonitorData.summary.active}</div>
                        <div style={{fontSize:'0.6rem', color:'#71717a'}}>Active ($10k+)</div>
                      </div>
                      <div onClick={() => setVipFilter('at_risk')} style={{cursor:'pointer', textAlign:'center', padding:'0.6rem', background: vipFilter === 'at_risk' ? 'rgba(245,158,11,0.12)' : 'rgba(14,14,18,0.7)', border:`1px solid ${vipFilter === 'at_risk' ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'10px'}}>
                        <div style={{fontSize:'1.4rem', fontWeight:800, color:'#f59e0b'}}>{vipMonitorData.summary.atRisk}</div>
                        <div style={{fontSize:'0.6rem', color:'#71717a'}}>At Risk ($1-10k)</div>
                      </div>
                      <div onClick={() => setVipFilter('low')} style={{cursor:'pointer', textAlign:'center', padding:'0.6rem', background: vipFilter === 'low' ? 'rgba(239,68,68,0.12)' : 'rgba(14,14,18,0.7)', border:`1px solid ${vipFilter === 'low' ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'10px'}}>
                        <div style={{fontSize:'1.4rem', fontWeight:800, color:'#f87171'}}>{vipMonitorData.summary.low}</div>
                        <div style={{fontSize:'0.6rem', color:'#71717a'}}>Low (&lt;$1k)</div>
                      </div>
                      <div onClick={() => setVipFilter('inactive')} style={{cursor:'pointer', textAlign:'center', padding:'0.6rem', background: vipFilter === 'inactive' ? 'rgba(113,113,122,0.15)' : 'rgba(14,14,18,0.7)', border:`1px solid ${vipFilter === 'inactive' ? 'rgba(113,113,122,0.3)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'10px'}}>
                        <div style={{fontSize:'1.4rem', fontWeight:800, color:'#71717a'}}>{vipMonitorData.summary.inactive}</div>
                        <div style={{fontSize:'0.6rem', color:'#71717a'}}>Inactive ($0)</div>
                      </div>
                      <div onClick={() => setVipFilter('removed')} style={{cursor:'pointer', textAlign:'center', padding:'0.6rem', background: vipFilter === 'removed' ? 'rgba(127,29,29,0.25)' : 'rgba(14,14,18,0.7)', border:`1px solid ${vipFilter === 'removed' ? 'rgba(220,38,38,0.4)' : 'rgba(255,255,255,0.06)'}`, borderRadius:'10px'}}>
                        <div style={{fontSize:'1.4rem', fontWeight:800, color:'#dc2626'}}>{vipMonitorData.summary.removed || 0}</div>
                        <div style={{fontSize:'0.6rem', color:'#71717a'}}>🚫 Removed</div>
                      </div>
                    </div>

                    {/* VIP Table */}
                    <div style={{background:'rgba(14,14,18,0.7)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden'}}>
                      <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.78rem'}}>
                        <thead>
                          <tr style={{borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
                            <th style={{textAlign:'left', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>User</th>
                            <th style={{textAlign:'right', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>All-Time</th>
                            <th style={{textAlign:'right', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>{vipPeriod === '0' ? 'This Period' : 'Period Wager'}</th>
                            <th style={{textAlign:'center', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Progress</th>
                            <th style={{textAlign:'center', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Status</th>
                            <th style={{textAlign:'center', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Links</th>
                            <th style={{textAlign:'center', padding:'0.6rem 0.75rem', color:'#9ca3af', fontWeight:600}}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vipMonitorData.vips
                            .filter(v => vipFilter === 'all' || v.status === vipFilter)
                            .map((v, i) => {
                              const isRemoved = v.status === 'removed';
                              const ri = v.removedInfo;
                              return (
                            <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.03)', background: isRemoved ? 'rgba(127,29,29,0.06)' : v.status === 'inactive' ? 'rgba(239,68,68,0.03)' : v.status === 'low' ? 'rgba(239,68,68,0.02)' : 'transparent'}}>
                              <td style={{padding:'0.5rem 0.75rem'}}>
                                <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                                  {v.avatar && <img src={v.avatar} style={{width:'22px', height:'22px', borderRadius:'50%'}} alt="" />}
                                  <div>
                                    <div onClick={() => setVipDetailUser(v)} title="Click for details" style={{fontWeight:600, color:'#c084fc', fontFamily:'monospace', fontSize:'0.8rem', cursor:'pointer', textDecoration:'underline', textDecorationColor:'rgba(192,132,252,0.3)', textUnderlineOffset:'2px'}}>{v.username}</div>
                                    {v.discord && <div style={{fontSize:'0.65rem', color:'#5865F2'}}>@{v.discord}</div>}
                                    {isRemoved && ri && (
                                      <div style={{fontSize:'0.62rem', color:'#dc2626', marginTop:'0.15rem'}}>
                                        Removed {new Date(ri.removedAt).toLocaleDateString()}{ri.removedBy ? ` by ${ri.removedBy}` : ''}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td style={{textAlign:'right', padding:'0.5rem 0.75rem', color:'#a1a1aa', fontFamily:'monospace'}}>
                                ${v.allTimeWager.toLocaleString(undefined, {maximumFractionDigits:0})}
                              </td>
                              <td style={{textAlign:'right', padding:'0.5rem 0.75rem', fontFamily:'monospace', fontWeight:700, color: v.currentPeriodWager >= 5000 ? '#22c55e' : v.currentPeriodWager >= 1000 ? '#f59e0b' : v.currentPeriodWager > 0 ? '#f87171' : '#52525b'}}>
                                ${v.currentPeriodWager.toLocaleString(undefined, {maximumFractionDigits:0})}
                              </td>
                              <td style={{padding:'0.5rem 0.75rem'}}>
                                {isRemoved && ri ? (
                                  <div title={`Regain progress: $${ri.wagerSinceRemoval.toLocaleString()} / $${ri.regainRequired.toLocaleString()}`}>
                                    <div style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                      <div style={{flex:1, height:'6px', background:'rgba(255,255,255,0.06)', borderRadius:'3px', overflow:'hidden'}}>
                                        <div style={{
                                          width:`${ri.regainProgress}%`, height:'100%', borderRadius:'3px',
                                          background: ri.regainEligible ? '#22c55e' : '#f59e0b'
                                        }} />
                                      </div>
                                      <span style={{fontSize:'0.6rem', color:'#71717a', minWidth:'52px', textAlign:'right', fontFamily:'monospace'}}>
                                        ${ri.wagerSinceRemoval >= 1000 ? (ri.wagerSinceRemoval/1000).toFixed(1)+'k' : ri.wagerSinceRemoval.toFixed(0)}/$10k
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                    <div style={{flex:1, height:'6px', background:'rgba(255,255,255,0.06)', borderRadius:'3px', overflow:'hidden'}}>
                                      <div style={{
                                        width:`${Math.min(v.percentOfRequired, 100)}%`, height:'100%', borderRadius:'3px',
                                        background: v.percentOfRequired >= 100 ? '#22c55e' : v.percentOfRequired >= 50 ? '#f59e0b' : '#ef4444'
                                      }} />
                                    </div>
                                    <span style={{fontSize:'0.65rem', color:'#71717a', minWidth:'32px', textAlign:'right'}}>{v.percentOfRequired}%</span>
                                  </div>
                                )}
                              </td>
                              <td style={{textAlign:'center', padding:'0.5rem 0.75rem'}}>
                                {isRemoved ? (
                                  <span style={{
                                    fontSize:'0.65rem', fontWeight:700, padding:'0.15rem 0.5rem', borderRadius:'4px',
                                    background:'rgba(127,29,29,0.2)', color:'#dc2626', border:'1px solid rgba(220,38,38,0.4)'
                                  }}>
                                    {ri?.regainEligible ? '✅ Eligible' : '🚫 Removed'}
                                  </span>
                                ) : (
                                  <span style={{
                                    fontSize:'0.65rem', fontWeight:700, padding:'0.15rem 0.5rem', borderRadius:'4px',
                                    background: v.status === 'active' ? 'rgba(34,197,94,0.12)' : v.status === 'at_risk' ? 'rgba(245,158,11,0.12)' : v.status === 'low' ? 'rgba(239,68,68,0.12)' : 'rgba(113,113,122,0.12)',
                                    color: v.status === 'active' ? '#22c55e' : v.status === 'at_risk' ? '#f59e0b' : v.status === 'low' ? '#f87171' : '#71717a',
                                    border: `1px solid ${v.status === 'active' ? 'rgba(34,197,94,0.25)' : v.status === 'at_risk' ? 'rgba(245,158,11,0.25)' : v.status === 'low' ? 'rgba(239,68,68,0.25)' : 'rgba(113,113,122,0.2)'}`
                                  }}>
                                    {v.status === 'active' ? '✅ Active' : v.status === 'at_risk' ? '⚠️ At Risk' : v.status === 'low' ? '🔻 Low' : '❌ Inactive'}
                                  </span>
                                )}
                              </td>
                              <td style={{textAlign:'center', padding:'0.5rem 0.75rem'}}>
                                <div style={{display:'flex', gap:'0.3rem', justifyContent:'center'}}>
                                  {v.siteLinked && <span title="Site linked" style={{fontSize:'0.7rem'}}>🌐</span>}
                                  {v.discord && <span title={`Discord: @${v.discord}`} style={{fontSize:'0.7rem'}}>💬</span>}
                                  {v.lastLogin && <span title={`Last login: ${new Date(v.lastLogin).toLocaleDateString()}`} style={{fontSize:'0.7rem'}}>👤</span>}
                                  {!v.siteLinked && !v.discord && <span style={{fontSize:'0.65rem', color:'#71717a'}}>—</span>}
                                </div>
                              </td>
                              <td style={{textAlign:'center', padding:'0.5rem 0.75rem'}}>
                                {isRemoved ? (
                                  <button onClick={async () => {
                                    if (!confirm(`Restore VIP status for ${v.username}?\n\nThis will remove them from the removed list. They'll need to claim their VIP role via Discord again.`)) return;
                                    try {
                                      const token = getAuthToken();
                                      const res = await fetch(`${BACKEND_API_URL}/api/admin/vip/restore`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ shuffleUsername: v.username })
                                      });
                                      if (res.ok) { fetchVipMonitor(); }
                                      else { const d = await res.json(); alert(d.error || 'Failed'); }
                                    } catch(e) { alert('Error: ' + e.message); }
                                  }} style={{padding:'0.25rem 0.6rem', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'5px', color:'#22c55e', fontSize:'0.65rem', fontWeight:700, cursor:'pointer'}}>
                                    ↻ Restore
                                  </button>
                                ) : (
                                  <button onClick={async () => {
                                    const regainReq = (vipMonitorData?.summary?.regainRequired) || 25000;
                                    const reason = prompt(`Remove VIP status for ${v.username}?\n\nThis will:\n• Mark them as removed in the database\n• Show them a notice on their VIP dashboard\n• Require them to wager $${regainReq.toLocaleString()} weighted since removal before they can reclaim VIP\n\nOptional reason (shown to user):`);
                                    if (reason === null) return; // cancelled
                                    try {
                                      const token = getAuthToken();
                                      const res = await fetch(`${BACKEND_API_URL}/api/admin/vip/remove`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ shuffleUsername: v.username, reason: reason || null })
                                      });
                                      if (res.ok) { fetchVipMonitor(); }
                                      else { const d = await res.json(); alert(d.error || 'Failed'); }
                                    } catch(e) { alert('Error: ' + e.message); }
                                  }} style={{padding:'0.25rem 0.6rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'5px', color:'#f87171', fontSize:'0.65rem', fontWeight:600, cursor:'pointer'}}>
                                    🚫 Remove
                                  </button>
                                )}
                              </td>
                            </tr>
                              );
                            })}
                        </tbody>
                      </table>
                      {vipMonitorData.vips.filter(v => vipFilter === 'all' || v.status === vipFilter).length === 0 && (
                        <div style={{textAlign:'center', padding:'1.5rem', color:'#71717a', fontSize:'0.85rem'}}>No VIPs in this category</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{textAlign:'center', padding:'2rem', color:'#71717a'}}>Loading VIP data...</div>
                )}
              </div>
            )}

            {/* VIP User Detail Modal */}
            {vipDetailUser && (
              <div onClick={() => setVipDetailUser(null)} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem'}}>
                <div onClick={e => e.stopPropagation()} style={{background:'#0e0e12', border:'1px solid rgba(245,158,11,0.25)', borderRadius:'14px', padding:'1.5rem', maxWidth:'480px', width:'100%', maxHeight:'90vh', overflow:'auto', position:'relative'}}>
                  <button onClick={() => setVipDetailUser(null)} style={{position:'absolute', top:'0.75rem', right:'0.75rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#a1a1aa', width:'28px', height:'28px', cursor:'pointer', fontSize:'1rem'}}>×</button>
                  
                  {/* Header with avatar */}
                  <div style={{display:'flex', alignItems:'center', gap:'0.85rem', marginBottom:'1rem', paddingRight:'2rem'}}>
                    {vipDetailUser.avatar ? (
                      <img src={vipDetailUser.avatar} alt="" style={{width:'52px', height:'52px', borderRadius:'50%', border:'2px solid rgba(245,158,11,0.3)'}} />
                    ) : (
                      <div style={{width:'52px', height:'52px', borderRadius:'50%', background:'rgba(139,92,246,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem'}}>👤</div>
                    )}
                    <div style={{minWidth:0, flex:1}}>
                      <div style={{fontSize:'1rem', fontWeight:800, color:'#fff'}}>{vipDetailUser.displayName || vipDetailUser.username}</div>
                      <div style={{fontSize:'0.75rem', color:'#71717a'}}>VIP Member</div>
                    </div>
                  </div>

                  {/* Account details list */}
                  <div style={{display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1rem'}}>
                    {/* Shuffle */}
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.18)', borderRadius:'8px', gap:'0.5rem'}}>
                      <div style={{display:'flex', alignItems:'center', gap:'0.5rem', minWidth:0}}>
                        <span style={{fontSize:'0.95rem'}}>🎰</span>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:'0.62rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px'}}>Shuffle</div>
                          <div style={{fontSize:'0.85rem', fontWeight:700, color:'#fbbf24', fontFamily:'monospace', wordBreak:'break-all'}}>{vipDetailUser.username}</div>
                        </div>
                      </div>
                      <button onClick={() => navigator.clipboard?.writeText(vipDetailUser.username)} title="Copy" style={{flexShrink:0, background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:'5px', color:'#fbbf24', padding:'0.25rem 0.5rem', fontSize:'0.65rem', cursor:'pointer', fontWeight:600}}>Copy</button>
                    </div>

                    {/* Twitch */}
                    {vipDetailUser.twitchUsername ? (
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background:'rgba(145,70,255,0.06)', border:'1px solid rgba(145,70,255,0.18)', borderRadius:'8px', gap:'0.5rem'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem', minWidth:0}}>
                          <span style={{fontSize:'0.95rem'}}>📺</span>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:'0.62rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px'}}>Twitch</div>
                            <div style={{fontSize:'0.85rem', fontWeight:700, color:'#b794f4', fontFamily:'monospace', wordBreak:'break-all'}}>{vipDetailUser.twitchUsername}</div>
                          </div>
                        </div>
                        <a href={`https://twitch.tv/${vipDetailUser.twitchUsername}`} target="_blank" rel="noopener noreferrer" style={{flexShrink:0, background:'rgba(145,70,255,0.12)', border:'1px solid rgba(145,70,255,0.25)', borderRadius:'5px', color:'#b794f4', padding:'0.25rem 0.5rem', fontSize:'0.65rem', textDecoration:'none', fontWeight:600}}>Open</a>
                      </div>
                    ) : (
                      <div style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.65rem 0.85rem', background:'rgba(113,113,122,0.05)', border:'1px solid rgba(113,113,122,0.15)', borderRadius:'8px'}}>
                        <span style={{fontSize:'0.95rem', opacity:0.4}}>📺</span>
                        <div style={{fontSize:'0.75rem', color:'#71717a'}}>No Twitch account linked on site</div>
                      </div>
                    )}

                    {/* Discord */}
                    {vipDetailUser.discord ? (
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 0.85rem', background:'rgba(88,101,242,0.06)', border:'1px solid rgba(88,101,242,0.2)', borderRadius:'8px', gap:'0.5rem'}}>
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem', minWidth:0}}>
                          <span style={{fontSize:'0.95rem'}}>💬</span>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:'0.62rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px'}}>Discord</div>
                            <div style={{fontSize:'0.85rem', fontWeight:700, color:'#7289da', fontFamily:'monospace', wordBreak:'break-all'}}>@{vipDetailUser.discord}</div>
                            {vipDetailUser.discordLinkedAt && (
                              <div style={{fontSize:'0.6rem', color:'#71717a', marginTop:'0.1rem'}}>Linked {new Date(vipDetailUser.discordLinkedAt).toLocaleDateString()}</div>
                            )}
                          </div>
                        </div>
                        <button onClick={() => navigator.clipboard?.writeText(vipDetailUser.discord)} title="Copy" style={{flexShrink:0, background:'rgba(88,101,242,0.12)', border:'1px solid rgba(88,101,242,0.25)', borderRadius:'5px', color:'#7289da', padding:'0.25rem 0.5rem', fontSize:'0.65rem', cursor:'pointer', fontWeight:600}}>Copy</button>
                      </div>
                    ) : (
                      <div style={{display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.65rem 0.85rem', background:'rgba(113,113,122,0.05)', border:'1px solid rgba(113,113,122,0.15)', borderRadius:'8px'}}>
                        <span style={{fontSize:'0.95rem', opacity:0.4}}>💬</span>
                        <div style={{fontSize:'0.75rem', color:'#71717a'}}>No Discord linked</div>
                      </div>
                    )}
                  </div>

                  {/* Wager Stats */}
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginBottom:'1rem'}}>
                    <div style={{padding:'0.6rem', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', textAlign:'center'}}>
                      <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.2rem'}}>All-Time</div>
                      <div style={{fontSize:'1.05rem', fontWeight:800, color:'#a78bfa', fontFamily:'monospace'}}>${vipDetailUser.allTimeWager.toLocaleString(undefined, {maximumFractionDigits:0})}</div>
                    </div>
                    <div style={{padding:'0.6rem', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', textAlign:'center'}}>
                      <div style={{fontSize:'0.6rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.2rem'}}>This Period</div>
                      <div style={{fontSize:'1.05rem', fontWeight:800, color: vipDetailUser.currentPeriodWager >= 5000 ? '#22c55e' : '#f59e0b', fontFamily:'monospace'}}>${vipDetailUser.currentPeriodWager.toLocaleString(undefined, {maximumFractionDigits:0})}</div>
                    </div>
                  </div>

                  {/* Account history */}
                  <div style={{padding:'0.75rem 0.85rem', background:'rgba(14,14,18,0.5)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', marginBottom:'1rem'}}>
                    <div style={{fontSize:'0.7rem', fontWeight:700, color:'#9ca3af', marginBottom:'0.4rem'}}>Account History</div>
                    {vipDetailUser.siteJoinedAt && (
                      <div style={{display:'flex', justifyContent:'space-between', fontSize:'0.72rem', color:'#a1a1aa', marginBottom:'0.2rem'}}>
                        <span>Site joined</span>
                        <span style={{color:'#e2e8f0'}}>{new Date(vipDetailUser.siteJoinedAt).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}</span>
                      </div>
                    )}
                    {vipDetailUser.lastLogin && (
                      <div style={{display:'flex', justifyContent:'space-between', fontSize:'0.72rem', color:'#a1a1aa'}}>
                        <span>Last login</span>
                        <span style={{color:'#e2e8f0'}}>{new Date(vipDetailUser.lastLogin).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}</span>
                      </div>
                    )}
                    {!vipDetailUser.siteLinked && (
                      <div style={{fontSize:'0.72rem', color:'#f87171'}}>⚠️ Not linked on snutzy.gg</div>
                    )}
                  </div>

                  {/* Removal info if removed */}
                  {vipDetailUser.removedInfo && (
                    <div style={{padding:'0.75rem 0.85rem', background:'rgba(127,29,29,0.1)', border:'1px solid rgba(220,38,38,0.3)', borderRadius:'8px'}}>
                      <div style={{fontSize:'0.7rem', fontWeight:700, color:'#dc2626', marginBottom:'0.4rem'}}>🚫 VIP Removed</div>
                      <div style={{fontSize:'0.72rem', color:'#a1a1aa', marginBottom:'0.2rem'}}>
                        Removed: <strong style={{color:'#e2e8f0'}}>{new Date(vipDetailUser.removedInfo.removedAt).toLocaleDateString()}</strong>
                        {vipDetailUser.removedInfo.removedBy && <span> by <strong style={{color:'#e2e8f0'}}>{vipDetailUser.removedInfo.removedBy}</strong></span>}
                      </div>
                      {vipDetailUser.removedInfo.reason && (
                        <div style={{fontSize:'0.72rem', color:'#a1a1aa', marginBottom:'0.2rem'}}>
                          Reason: <em style={{color:'#e2e8f0'}}>{vipDetailUser.removedInfo.reason}</em>
                        </div>
                      )}
                      <div style={{fontSize:'0.72rem', color:'#a1a1aa'}}>
                        Regain progress: <strong style={{color: vipDetailUser.removedInfo.regainEligible ? '#22c55e' : '#fbbf24'}}>${vipDetailUser.removedInfo.wagerSinceRemoval.toLocaleString()} / ${(vipDetailUser.removedInfo.regainRequired || 25000).toLocaleString()}</strong>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Add/Edit Shop Item Modal */}
            {showAddShopItemModal && (
              <div className="redeem-modal-overlay" onClick={() => setShowAddShopItemModal(false)}>
                <div className="ap-shop-modal" onClick={e => e.stopPropagation()}>
                  <button className="redeem-modal-close" onClick={() => setShowAddShopItemModal(false)}>×</button>
                  <h2>{editingShopItem ? 'Edit Shop Item' : 'Add Shop Item'}</h2>
                  
                  <div className="ap-shop-modal-form">
                    <div className="ap-grant-field">
                      <label>Name *</label>
                      <input type="text" value={newShopItem.name} onChange={e => setNewShopItem(s => ({...s, name: e.target.value}))} placeholder="e.g. Custom Discord Role" />
                    </div>
                    <div className="ap-grant-field full">
                      <label>Description</label>
                      <input type="text" value={newShopItem.description} onChange={e => setNewShopItem(s => ({...s, description: e.target.value}))} placeholder="Brief description of the reward" />
                    </div>
                    <div className="ap-grant-field">
                      <label>Cost (points) *</label>
                      <input type="number" value={newShopItem.cost} onChange={e => setNewShopItem(s => ({...s, cost: e.target.value}))} placeholder="e.g. 5000" />
                    </div>
                    <div className="ap-grant-field">
                      <label>Cash value ($)</label>
                      <input type="number" step="0.01" value={newShopItem.cashValue} onChange={e => setNewShopItem(s => ({...s, cashValue: e.target.value}))} placeholder="e.g. 50 — what it's worth" />
                      <span style={{fontSize:'0.66rem', color:'#71717a', marginTop:'0.2rem', display:'block'}}>Real-money worth of this reward. Used by the Rewards Ledger to offset lossback. Leave 0 for non-cash items (roles, cosmetics).</span>
                    </div>
                    <div className="ap-grant-field">
                      <label>Category</label>
                      <input type="text" value={newShopItem.category} onChange={e => setNewShopItem(s => ({...s, category: e.target.value}))} placeholder="e.g. cosmetic, reward" />
                    </div>
                    <div className="ap-grant-field full">
                      <label>Icon (preset)</label>
                      <select value={newShopItem.iconKey} onChange={e => setNewShopItem(s => ({...s, iconKey: e.target.value}))} style={{width:'100%', padding:'0.5rem', background:'rgba(0,0,0,0.4)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', color:'#e2e8f0', fontSize:'0.85rem'}}>
                        {SHOP_ICON_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                      {newShopItem.iconKey && newShopItem.iconKey !== 'custom' && (
                        <div style={{marginTop:'0.6rem', width:'100%', maxWidth:'280px', height:'180px', borderRadius:'8px', overflow:'hidden', border:'1px solid rgba(168,85,247,0.25)'}}>
                          <ShopIcon iconKey={newShopItem.iconKey} fallbackImageUrl={newShopItem.imageUrl} alt="preview" />
                        </div>
                      )}
                    </div>
                    <div className="ap-grant-field full">
                      <label>Image URL{newShopItem.iconKey !== 'custom' ? ' (ignored — preset icon selected above)' : ''}</label>
                      <input type="text" value={newShopItem.imageUrl} onChange={e => setNewShopItem(s => ({...s, imageUrl: e.target.value}))} placeholder="https://..." />
                    </div>
                    <div className="ap-grant-field">
                      <label>Stock (empty = unlimited)</label>
                      <input type="number" value={newShopItem.stock} onChange={e => setNewShopItem(s => ({...s, stock: e.target.value}))} placeholder="e.g. 10" />
                    </div>
                    <div className="ap-grant-field">
                      <label>Max per user (empty = no limit)</label>
                      <input type="number" value={newShopItem.maxPerUser} onChange={e => setNewShopItem(s => ({...s, maxPerUser: e.target.value}))} placeholder="e.g. 1" />
                    </div>
                    <div className="ap-grant-field">
                      <label>Sort Order</label>
                      <input type="number" value={newShopItem.sortOrder} onChange={e => setNewShopItem(s => ({...s, sortOrder: e.target.value}))} placeholder="0" />
                    </div>
                    <div className="ap-grant-field full">
                      <label>Fulfillment Notes (admin only)</label>
                      <input type="text" value={newShopItem.fulfillmentNotes} onChange={e => setNewShopItem(s => ({...s, fulfillmentNotes: e.target.value}))} placeholder="How to fulfill this item" />
                    </div>
                    <div className="ap-checkbox-row">
                      <label>
                        <input type="checkbox" checked={newShopItem.requiresShuffleUsername} onChange={e => setNewShopItem(s => ({...s, requiresShuffleUsername: e.target.checked}))} />
                        Requires Shuffle Username
                      </label>
                      <label>
                        <input type="checkbox" checked={newShopItem.requiresDiscordUsername} onChange={e => setNewShopItem(s => ({...s, requiresDiscordUsername: e.target.checked}))} />
                        Requires Discord Username
                      </label>
                    </div>
                    <button 
                      className="ap-grant-submit" style={{marginTop:'0.5rem'}}
                      disabled={adminPointsLoading || !newShopItem.name || !newShopItem.cost}
                      onClick={() => {
                        if (editingShopItem) {
                          updateShopItem(editingShopItem.id, {
                            name: newShopItem.name, description: newShopItem.description,
                            cost: parseInt(newShopItem.cost), imageUrl: newShopItem.imageUrl || null,
                            iconKey: newShopItem.iconKey || 'custom',
                            stock: newShopItem.stock ? parseInt(newShopItem.stock) : null,
                            maxPerUser: newShopItem.maxPerUser ? parseInt(newShopItem.maxPerUser) : null,
                            requiresShuffleUsername: newShopItem.requiresShuffleUsername,
                            requiresDiscordUsername: newShopItem.requiresDiscordUsername,
                            fulfillmentNotes: newShopItem.fulfillmentNotes,
                            category: newShopItem.category, sortOrder: parseInt(newShopItem.sortOrder) || 0,
                            cashValue: newShopItem.cashValue === '' ? 0 : parseFloat(newShopItem.cashValue) || 0
                          });
                          setShowAddShopItemModal(false);
                        } else {
                          createShopItem();
                        }
                      }}
                    >
                      {adminPointsLoading ? 'Saving...' : editingShopItem ? 'Save Changes' : 'Create Item'}
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
          </main>
        </div>
      </div>

      {/* "Who referred you?" prompt for fresh accounts */}
      {showReferralPrompt && !showShuffleModal && (
        <div className="shuffle-modal-overlay">
          <div className="shuffle-modal">
            <button className="shuffle-modal-close" onClick={dismissReferralPrompt}>×</button>
            <div className="shuffle-modal-header">
              <div className="shuffle-modal-icon">🤝</div>
              <h2>Who referred you?</h2>
              <p>If a friend told you about SNUTZY, enter their Twitch or Shuffle username — they'll earn rewards as you play, at no cost to you.</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <input
                value={referralPromptInput}
                onChange={(e) => setReferralPromptInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitReferralPrompt(); }}
                placeholder="Friend's username"
                style={{ padding: '0.7rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff', fontSize: '0.95rem', fontFamily: 'Outfit, sans-serif' }}
              />
              {referralPromptError && (
                <p style={{ color: '#fca5a5', fontSize: '0.82rem', margin: 0 }}>{referralPromptError}</p>
              )}
              <button
                onClick={submitReferralPrompt}
                disabled={referralPromptSaving || !referralPromptInput.trim()}
                style={{ padding: '0.75rem', background: 'linear-gradient(135deg, #a855f7, #7c3aed)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Outfit, sans-serif', opacity: referralPromptSaving || !referralPromptInput.trim() ? 0.6 : 1 }}
              >
                {referralPromptSaving ? 'Saving…' : 'Set Referrer'}
              </button>
              <p style={{ color: '#71717a', fontSize: '0.72rem', margin: 0, lineHeight: 1.5 }}>
                Available within 48 hours of signing up. Artificial abuse of the referral system (self-referrals, alt accounts) disqualifies all rewards.
              </p>
            </div>
            <button className="shuffle-skip-btn" onClick={dismissReferralPrompt}>No one referred me</button>
          </div>
        </div>
      )}

      {/* Shuffle Username Linking Modal */}
      {showShuffleModal && (
        <div className="shuffle-modal-overlay">
          <div className="shuffle-modal">
            <button className="shuffle-modal-close" onClick={() => setShowShuffleModal(false)}>×</button>
            
            <div className="shuffle-modal-header">
              <div className="shuffle-modal-icon">🎰</div>
              <h2>Link Your Shuffle Account</h2>
              <p>Enter your Shuffle username to receive prediction prizes and track your rewards!</p>
            </div>
            
            <div className="shuffle-modal-form">
              <div className="shuffle-input-wrapper">
                <span className="shuffle-input-prefix">@</span>
                <input 
                  type="text"
                  placeholder="Your Shuffle username..."
                  value={shuffleUsernameInput}
                  onChange={(e) => setShuffleUsernameInput(e.target.value)}
                  className="shuffle-username-input"
                  onKeyDown={(e) => e.key === 'Enter' && saveShuffleUsername()}
                />
              </div>
              
              <button 
                className="shuffle-save-btn"
                onClick={saveShuffleUsername}
                disabled={savingShuffleUsername || !shuffleUsernameInput.trim()}
              >
                {savingShuffleUsername ? 'Saving...' : '✓ Link Account'}
              </button>
            </div>
            
            <div className="shuffle-modal-benefits">
              <p className="benefits-title">Benefits of linking:</p>
              <ul>
                <li>🏆 Receive rewards hassle-free, tipped directly to your Shuffle account</li>
                <li>📊 Track your rewards history</li>
                <li>🎁 Eligible for exclusive giveaways</li>
              </ul>
            </div>
            
            <button className="shuffle-skip-btn" onClick={() => setShowShuffleModal(false)}>
              Skip for now
            </button>
          </div>
        </div>
      )}

      {/* Slot Pick Pay Modal */}
      {showSlotPickPayModal && selectedSlotPickToPay && (
        <div className="modal-overlay" onClick={() => setShowSlotPickPayModal(false)}>
          <div className="slot-pick-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSlotPickPayModal(false)}>×</button>
            
            <h2>💰 Pay Slot Pick Reward</h2>
            
            <div className="slot-pick-modal-info">
              <div className="slot-pick-modal-user">
                {selectedSlotPickToPay.profile_image_url ? (
                  <img src={selectedSlotPickToPay.profile_image_url} alt="" className="slot-pick-modal-avatar" />
                ) : (
                  <div className="slot-pick-modal-avatar-placeholder">
                    {(selectedSlotPickToPay.twitch_display_name || '?').substring(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="slot-pick-modal-username">{selectedSlotPickToPay.twitch_display_name || selectedSlotPickToPay.twitch_username}</div>
                  {selectedSlotPickToPay.shuffle_username ? (
                    <div className="slot-pick-modal-shuffle">🎰 {selectedSlotPickToPay.shuffle_username}</div>
                  ) : (
                    <div className="slot-pick-modal-no-shuffle">⚠️ No Shuffle username linked</div>
                  )}
                </div>
              </div>
              <div className="slot-pick-modal-slot">
                <span className="label">Picked Slot:</span>
                <span className="slot-name">{selectedSlotPickToPay.slot_name}</span>
              </div>
            </div>

            <div className="slot-pick-modal-form">
              <div className="form-group">
                <label>Reward Amount ($):</label>
                <input 
                  type="number"
                  placeholder="e.g., 25"
                  value={slotPickPayAmount}
                  onChange={(e) => setSlotPickPayAmount(e.target.value)}
                  className="slot-pick-input"
                  autoFocus
                />
              </div>
            </div>

            <div className="slot-pick-modal-actions">
              <button className="btn-cancel" onClick={() => setShowSlotPickPayModal(false)}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={paySlotPick}>
                💰 Pay ${slotPickPayAmount || '0'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Win Notification Modal */}
      {showWinModal && selectedWinToClaim && (
        <div className="win-modal-overlay" onClick={() => setShowWinModal(false)}>
          <div className="win-modal" onClick={(e) => e.stopPropagation()}>
            <button className="win-modal-close" onClick={() => setShowWinModal(false)}>×</button>
            
            <div className="win-modal-header">
              <div className="win-modal-icon">🎉</div>
              <h2>Congratulations!</h2>
              <p>You won a prediction game!</p>
            </div>
            
            <div className="win-modal-details">
              <div className="win-modal-rank">
                {selectedWinToClaim.rank === 1 ? '🥇' : selectedWinToClaim.rank === 2 ? '🥈' : '🥉'}
                <span>{selectedWinToClaim.rank === 1 ? '1st' : selectedWinToClaim.rank === 2 ? '2nd' : '3rd'} Place</span>
              </div>
              
              <div className="win-modal-stats">
                <div className="win-stat">
                  <span className="win-stat-label">Your Prediction</span>
                  <span className="win-stat-value">${parseFloat(selectedWinToClaim.predicted_balance).toLocaleString()}</span>
                </div>
                <div className="win-stat">
                  <span className="win-stat-label">Actual Balance</span>
                  <span className="win-stat-value">${parseFloat(selectedWinToClaim.actual_balance).toLocaleString()}</span>
                </div>
                <div className="win-stat highlight">
                  <span className="win-stat-label">Difference</span>
                  <span className="win-stat-value">${parseFloat(selectedWinToClaim.difference).toLocaleString()}</span>
                </div>
              </div>
              
              <div className="win-modal-prize">
                <span className="prize-label">Your Prize</span>
                <span className="prize-value">{selectedWinToClaim.reward}</span>
              </div>
            </div>
            
            {/* Show different content based on whether user has Shuffle username linked */}
            {user?.shuffleUsername ? (
              // User already has Shuffle username linked - auto-claim
              <div className="win-modal-auto-claim">
                <div className="win-auto-claim-info">
                  <span className="win-auto-icon">✅</span>
                  <p>Your prize will be sent to your linked Shuffle account:</p>
                  <span className="win-shuffle-username">🎰 {user.shuffleUsername}</span>
                </div>
                <button 
                  className="win-claim-btn"
                  onClick={claimWinAuto}
                >
                  🎁 Confirm & Claim Prize
                </button>
                <p style={{fontSize:'0.72rem', color:'#71717a', textAlign:'center', margin:'0.5rem 0 0'}}>Payouts can take up to 14 days to process.</p>
                <p className="win-modal-note">
                  Wrong account? <button className="link-btn" onClick={() => setShowShuffleModal(true)}>Update your Shuffle username</button>
                </p>
              </div>
            ) : (
              // User needs to enter Shuffle username
              <div className="win-modal-claim">
                <p>Enter your Shuffle username to claim your prize:</p>
                <input 
                  type="text"
                  placeholder="Your Shuffle username..."
                  value={claimShuffleUsername}
                  onChange={(e) => setClaimShuffleUsername(e.target.value)}
                  className="win-claim-input"
                />
                <button 
                  className="win-claim-btn"
                  onClick={claimWin}
                  disabled={!claimShuffleUsername.trim()}
                >
                  🎁 Claim Prize
                </button>
              </div>
            )}
            
            <p className="win-modal-note">
              Payouts can take up to 14 days to process.
            </p>
          </div>
        </div>
      )}

      {/* Floating Live Button (sits left of the FAB when a logged-in user has it) */}
      {isStreamerLive && (
        <a
          href="https://twitch.tv/snutzy"
          target="_blank"
          rel="noopener noreferrer"
          className={`floating-live-btn${user ? ' floating-live-btn--with-fab' : ''}`}
        >
          <span className="floating-live-indicator"></span>
          <span className="floating-live-text">🎬 LIVE</span>
        </a>
      )}

      {/* Floating Action Button — quick actions for logged-in users.
          Lossback gets a badge + nudge pill while the claim window is open,
          so nobody misses the only place it can be claimed. */}
      {user && (() => {
        return (
          <Fab
            nudge={lossbackActionable ? {
              id: `lossback_${lossbackStatus.periodLabel}`,
              icon: '💸',
              text: 'Lossback window is OPEN — claim 10% back on your losses',
              onClick: () => navigateTo('lossback')
            } : null}
            options={[
              ...(checklistRow && !checklistRow.points_awarded ? [{
                icon: '🚀',
                label: 'Newcomer Checklist',
                sublabel: `${checklistDoneCount}/${CHECKLIST_STEP_DEFS.length} done — earn 500 points`,
                accent: '#a855f7',
                onClick: () => setShowChecklistPanel(true)
              }] : []),
              ...(featureFlags.lossback ? [{
                icon: '💸',
                label: 'Lossback Claim',
                sublabel: lossbackActionable
                  ? `Window closes on the ${lossbackStatus.windowEndDay}th — claim now!`
                  : lossbackStatus?.claimedThisPeriod
                    ? 'Claimed this period ✓'
                    : 'Claim 10% back on your losses',
                accent: '#22c55e',
                ...(lossbackActionable ? { badge: 'OPEN' } : {}),
                onClick: () => navigateTo('lossback')
              }] : []),
              {
                icon: '🎁',
                label: 'Claim Giveaway',
                sublabel: 'Claim a giveaway you won',
                accent: '#fbbf24',
                onClick: () => navigateTo('claim')
              },
              {
                icon: '👑',
                label: 'VIP Dashboard',
                sublabel: 'Your perks & VIP progress',
                accent: '#fbbf24',
                onClick: () => navigateTo('vipdashboard')
              }
            ]}
          />
        );
      })()}

      {/* First-visit welcome modal — logged-out users, once per browser */}
      {showWelcomeModal && !user && (
        <WelcomeModal
          onClose={() => setShowWelcomeModal(false)}
          onLogin={() => { setShowWelcomeModal(false); loginWithTwitch(); }}
        />
      )}

      {/* Newcomer checklist — full panel */}
      {showChecklistPanel && user && checklistRow && (
        <div onClick={() => setShowChecklistPanel(false)} style={{position:'fixed', inset:0, zIndex:9500, background:'rgba(5,5,10,0.75)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem'}}>
          <div onClick={e => e.stopPropagation()} style={{background:'rgba(12,12,18,0.97)', border:'1px solid rgba(168,85,247,0.3)', borderRadius:'18px', maxWidth:'620px', width:'100%', maxHeight:'88vh', overflowY:'auto', padding:'1.6rem 1.4rem', position:'relative'}}>
            <button onClick={() => setShowChecklistPanel(false)} aria-label="Close" style={{position:'absolute', top:'0.9rem', right:'0.9rem', width:'30px', height:'30px', borderRadius:'50%', border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.05)', color:'#a1a1aa', fontSize:'0.9rem', cursor:'pointer', lineHeight:1}}>✕</button>

            <div style={{textAlign:'center', marginBottom:'1.2rem'}}>
              <div style={{fontSize:'1.35rem', fontWeight:900, color:'#fff'}}>🚀 Newcomer Checklist</div>
              <div style={{fontSize:'0.82rem', color:'#a1a1aa', marginTop:'0.3rem'}}>
                Complete all {CHECKLIST_STEP_DEFS.length} steps and earn <strong style={{color:'#fbbf24'}}>500 points</strong>
              </div>
              <div style={{height:'6px', background:'rgba(255,255,255,0.06)', borderRadius:'3px', margin:'0.9rem auto 0', maxWidth:'320px', overflow:'hidden'}}>
                <div style={{height:'100%', width:`${(checklistDoneCount / CHECKLIST_STEP_DEFS.length) * 100}%`, background:'linear-gradient(90deg, #a855f7, #fbbf24)', borderRadius:'3px', transition:'width 0.6s ease'}} />
              </div>
              <div style={{fontSize:'0.72rem', color:'#71717a', marginTop:'0.35rem'}}>{checklistDoneCount}/{CHECKLIST_STEP_DEFS.length} complete</div>
            </div>

            <div style={{display:'flex', flexDirection:'column', gap:'0.6rem'}}>
              {CHECKLIST_STEP_DEFS.map(def => {
                const verified = !!checklistRow[def.verifiedKey];
                const isPending = (checklistData.pendingSteps || []).includes(def.key);
                const busy = checklistBusyStep === def.key;
                const msg = checklistMsg && checklistMsg.step === def.key ? checklistMsg : null;
                const linkBtnStyle = {padding:'0.4rem 0.9rem', borderRadius:'8px', fontSize:'0.75rem', fontWeight:700, textDecoration:'none', background:'rgba(168,85,247,0.15)', color:'#c084fc', border:'1px solid rgba(168,85,247,0.35)', whiteSpace:'nowrap'};
                const actionBtnStyle = {padding:'0.4rem 0.9rem', borderRadius:'8px', fontSize:'0.75rem', fontWeight:800, cursor:'pointer', background:'rgba(251,191,36,0.12)', color:'#fbbf24', border:'1px solid rgba(251,191,36,0.35)', whiteSpace:'nowrap'};
                return (
                  <div key={def.key} style={{padding:'0.75rem 0.9rem', background: verified ? 'rgba(34,197,94,0.05)' : 'rgba(255,255,255,0.03)', border: verified ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(255,255,255,0.07)', borderRadius:'12px'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'0.7rem'}}>
                      <span style={{fontSize:'1.2rem', flexShrink:0}}>{def.icon}</span>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:'0.85rem', fontWeight:700, color: verified ? '#22c55e' : '#e2e8f0'}}>{def.title}</div>
                        {!verified && <div style={{fontSize:'0.7rem', color:'#8b8b96', marginTop:'0.1rem'}}>{def.desc}</div>}
                      </div>
                      {verified ? (
                        <span style={{fontSize:'1.1rem', color:'#22c55e', flexShrink:0}}>✓</span>
                      ) : isPending ? (
                        <span style={{padding:'0.25rem 0.7rem', borderRadius:'6px', fontSize:'0.68rem', fontWeight:700, background:'rgba(245,158,11,0.12)', color:'#f59e0b', border:'1px solid rgba(245,158,11,0.3)', whiteSpace:'nowrap', flexShrink:0}}>⏳ Pending review</span>
                      ) : null}
                    </div>

                    {/* Action area (only while unverified and not pending) */}
                    {!verified && !isPending && (
                      <div style={{display:'flex', gap:'0.5rem', marginTop:'0.6rem', flexWrap:'wrap', alignItems:'center'}}>
                        {def.kind === 'shuffle' && (
                          !checklistData.shuffleLinked ? (
                            <button onClick={() => { setShowChecklistPanel(false); setShowShuffleModal(true); }} style={actionBtnStyle}>🔗 Link Shuffle account</button>
                          ) : (
                            <>
                              {checklistData.shuffleUnderGuild === false && (
                                <div style={{flexBasis:'100%', fontSize:'0.72rem', color:'#f59e0b', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:'8px', padding:'0.45rem 0.6rem'}}>
                                  ⚠️ {checklistData.shuffleUsername ? <>Your Shuffle account <b>{checklistData.shuffleUsername}</b> isn't</> : "Your Shuffle account isn't"} registered under guild SNUTZY yet.
                                </div>
                              )}
                              <a href="https://shuffle.com/?r=snutzy" target="_blank" rel="noopener noreferrer" style={linkBtnStyle}>Join guild SNUTZY</a>
                              <button onClick={recheckShuffleStep} disabled={busy} style={actionBtnStyle}>{busy ? 'Checking…' : '↻ Check again'}</button>
                            </>
                          )
                        )}
                        {def.kind === 'discord' && (
                          // Server's view of the link wins — the login-time discordLinked state can be stale
                          (checklistData.discordLinked ?? discordLinked) === false ? (
                            <button onClick={connectDiscord} style={actionBtnStyle}>🔗 Connect Discord</button>
                          ) : (
                            <>
                              {checklistData.discordInGuild === false && (
                                <div style={{flexBasis:'100%', fontSize:'0.72rem', color:'#f59e0b', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:'8px', padding:'0.45rem 0.6rem'}}>
                                  ⚠️ {checklistData.discordUsername ? <>Your linked Discord account <b>{checklistData.discordUsername}</b> isn't</> : "Your linked Discord account isn't"} in our server. In the server on a different account? Reconnect with that one.
                                </div>
                              )}
                              <a href={def.link} target="_blank" rel="noopener noreferrer" style={linkBtnStyle}>{def.linkLabel} →</a>
                              <button onClick={() => verifyChecklistStep('discord')} disabled={busy} style={actionBtnStyle}>{busy ? 'Checking…' : '✓ Verify'}</button>
                              {checklistData.discordInGuild === false && (
                                <button onClick={connectDiscord} style={linkBtnStyle}>🔄 Reconnect Discord</button>
                              )}
                            </>
                          )
                        )}
                        {def.kind === 'twitch' && (
                          <>
                            <a href={def.link} target="_blank" rel="noopener noreferrer" style={linkBtnStyle}>{def.linkLabel} →</a>
                            <button onClick={() => verifyChecklistStep('twitch_follow')} disabled={busy} style={actionBtnStyle}>{busy ? 'Checking…' : '✓ Verify'}</button>
                          </>
                        )}
                        {def.kind === 'manual' && (
                          <>
                            <a href={def.link} target="_blank" rel="noopener noreferrer" style={linkBtnStyle}>{def.linkLabel} →</a>
                            <input
                              type="text"
                              placeholder={def.placeholder}
                              value={checklistInputs[def.key] || ''}
                              onChange={e => setChecklistInputs(prev => ({ ...prev, [def.key]: e.target.value }))}
                              maxLength={100}
                              style={{flex:1, minWidth:'140px', padding:'0.4rem 0.6rem', background:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', color:'#fff', fontSize:'0.78rem', outline:'none'}}
                            />
                            <button onClick={() => submitChecklistStep(def.key)} disabled={busy || !(checklistInputs[def.key] || '').trim()} style={{...actionBtnStyle, opacity: (checklistInputs[def.key] || '').trim() ? 1 : 0.5}}>
                              {busy ? 'Sending…' : 'Submit'}
                            </button>
                          </>
                        )}
                        {msg && (
                          <div style={{flexBasis:'100%', fontSize:'0.72rem', color: msg.type === 'info' ? '#a1a1aa' : '#f87171'}}>{msg.text}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{textAlign:'center', fontSize:'0.68rem', color:'#71717a', marginTop:'1rem'}}>
              Social follows are verified by the team — usually within a day.
            </div>
          </div>
        </div>
      )}

      {/* Checklist completed — celebration */}
      {checklistCelebrate && (
        <div onClick={() => setChecklistCelebrate(false)} style={{position:'fixed', inset:0, zIndex:10001, background:'rgba(5,5,10,0.82)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem'}}>
          <style>{`
            @keyframes clPop { 0% { opacity: 0; transform: scale(0.6); } 60% { transform: scale(1.06); } 100% { opacity: 1; transform: scale(1); } }
            @keyframes clWiggle { 0%, 100% { transform: rotate(-10deg); } 50% { transform: rotate(10deg); } }
            @keyframes clBurst { 0%, 100% { box-shadow: 0 0 40px rgba(251,191,36,0.35), 0 0 90px rgba(168,85,247,0.25); } 50% { box-shadow: 0 0 80px rgba(251,191,36,0.65), 0 0 140px rgba(168,85,247,0.45); } }
          `}</style>
          <div onClick={e => e.stopPropagation()} style={{textAlign:'center', background:'rgba(12,12,18,0.97)', border:'1px solid rgba(251,191,36,0.4)', borderRadius:'20px', padding:'2.5rem 2.2rem', maxWidth:'400px', width:'100%', animation:'clPop 0.5s ease both, clBurst 2.2s ease-in-out infinite'}}>
            <div style={{fontSize:'3.4rem', display:'inline-block', animation:'clWiggle 1s ease infinite'}}>🎊</div>
            <div style={{fontSize:'1.5rem', fontWeight:900, marginTop:'0.5rem', background:'linear-gradient(135deg, #a855f7, #fbbf24)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent'}}>
              Checklist Complete!
            </div>
            <div style={{fontSize:'1.6rem', fontWeight:900, color:'#fbbf24', margin:'0.7rem 0'}}>+500 points</div>
            <div style={{fontSize:'0.85rem', color:'#a1a1aa', lineHeight:1.5}}>You've unlocked everything the guild has to offer. Welcome aboard 💜</div>
            <button onClick={() => { setChecklistCelebrate(false); setShowChecklistPanel(false); }} style={{marginTop:'1.4rem', padding:'0.8rem 2.2rem', borderRadius:'12px', border:'none', cursor:'pointer', fontSize:'0.95rem', fontWeight:900, color:'#1a1a2e', background:'linear-gradient(135deg, #fbbf24, #f59e0b)'}}>
              Awesome!
            </button>
          </div>
        </div>
      )}

      {/* Footer with Disclaimer (landing page renders its own footer) */}
      {activePage !== 'home' && (
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img src="https://i.postimg.cc/J0xRJ1dH/Photos-MO1q5GQBt-Q.png" alt="Snutzy Logo" className="footer-logo-img" />
            <span className="footer-logo-text">SNUTZY</span>
            <span className="footer-logo-x">×</span>
            <img src="/shuffle-wordmark.png" alt="Shuffle" className="footer-shuffle-logo" />
          </div>
          <p className="footer-disclaimer">
            We do not take responsibility for any losses from gambling in casinos and betting sites which are linked or promoted on our website(s). As a player, you are responsible for your bets. Gambling can be addictive, please play responsibly.
            {' '}Support is free and confidential at <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" style={{color:'#c4b5fd'}}>BeGambleAware.org</a>.
          </p>
          <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:'0.5rem 1.4rem', flexWrap:'wrap', margin:'0.9rem 0 0.35rem'}}>
            <span style={{padding:'0.15rem 0.55rem', borderRadius:'6px', border:'1.5px solid rgba(251,191,36,0.55)', color:'#fbbf24', fontSize:'0.78rem', fontWeight:800, letterSpacing:'0.05em'}} aria-label="18 plus only">18+</span>
            <a href="/legal#terms" className="footer-link">Terms of Use</a>
            <a href="/legal#rewards" className="footer-link">Rewards Terms</a>
            <a href="/legal#privacy" className="footer-link">Privacy</a>
            <a href="/legal#responsible-gambling" className="footer-link">Responsible Gambling</a>
          </div>
          <div className="footer-links">
            <a href="https://discord.gg/snutzy" target="_blank" rel="noopener noreferrer" className="footer-link">Discord</a>
            <a href="https://www.twitch.tv/snutzy" target="_blank" rel="noopener noreferrer" className="footer-link">Twitch</a>
            <a href="https://x.com/snutztv" target="_blank" rel="noopener noreferrer" className="footer-link">Twitter</a>
            <a href="https://www.youtube.com/@snutztv" target="_blank" rel="noopener noreferrer" className="footer-link">YouTube</a>
            <a href="https://www.tiktok.com/@snutzyclips" target="_blank" rel="noopener noreferrer" className="footer-link">TikTok</a>
          </div>
          <p style={{marginTop:'0.9rem', fontSize:'0.75rem', color:'#8b8b96'}}>
            © {new Date().getFullYear()} SNUTZY <span style={{margin:'0 0.4rem', color:'#71717a'}}>·</span> made by <a href="http://discord.com/users/182121536461864960" target="_blank" rel="noopener noreferrer" style={{color:'#8b8b96', textDecoration:'underline', textUnderlineOffset:'3px'}}>@pa1n</a>
          </p>
        </div>
      </footer>
      )}
    </div>
  );
}
